import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Dna, Music2, Copy, Check, Loader2, FileAudio, Download, 
  RotateCcw, Search, Zap, Info, Filter, Layers, Sparkles,
  Crown, CreditCard, Mail, X, CheckCircle, Mic, MicOff, Radio, Share2, HelpCircle, User, History,
  Sun, Moon, Library, Upload, Trash2, ExternalLink, FileText, Music, Archive,
  Play, Pause, Volume2, VolumeX, SkipBack, SkipForward, ChevronDown, Save, Square,
  Home, Compass, PlusCircle, Mic2, Settings, Activity, Smartphone, Star, Headphones, Waves, ArrowUp, Globe,
  Edit3, Plus, Maximize2, ArrowLeft, Menu, Eye, EyeOff
} from "lucide-react";
import { Toaster, toast } from "sonner";
import SparkMD5 from "spark-md5";
import { Slider } from "./components/ui/slider";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from "axios";
import UserDashboard from "./components/UserDashboard";
import AnalysisHistory from "./components/AnalysisHistory";
import MasteringStudio from "./MasteringStudio";
import VoiceShowcase from "./VoiceShowcase";
import DNAToolkit from "./DNAToolkit";
import MySoundDnaLibrary from "./MySoundDnaLibrary";
import AutonomousPromptLab from "./AutonomousPromptLab";
import "./App.css";

// API base: real users (www or apex) ALWAYS hit the apex domain directly — kills the www redirect/block bugs.
// Preview/dev environments fall back to their own backend URL.
const API = window.location.hostname.endsWith('sounddna.pro')
  ? 'https://sounddna.pro/api'
  : `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;
const API_ORIGIN = API.replace(/\/api$/, '');

// Ticket 269695: track analysis runs as a background job (start + poll) so long
// tracks are not cut off by the hosting proxy's ~100s request limit.
// Resolves like axios ({ data }) and throws axios-shaped errors so callers keep
// their existing error handling.
const runAnalyzeJob = async (formData) => {
  const jobId = (await axios.post(`${API}/analyze/start`, formData, { timeout: 120000 })).data.job_id;
  const t0 = Date.now();
  for (;;) {
    await new Promise((r) => setTimeout(r, 3000));
    const st = (await axios.get(`${API}/analyze/status/${jobId}`, { timeout: 20000 })).data;
    if (st.status === "done") return { data: st.result };
    if (st.status === "error") throw { response: { status: st.code, data: { detail: st.detail } } };
    if (Date.now() - t0 > 300000) throw { response: { data: { detail: "Analysis is taking longer than usual. Please try again." } } };
  }
};

// Ticket 269695: Style Prompt Mini uses the same start + poll pattern so a
// larger dropped file can't exceed the hosting proxy's request window either.
const runStyleMiniJob = async (formData) => {
  const jobId = (await axios.post(`${API}/analyze-audio-style/start`, formData, { timeout: 60000 })).data.job_id;
  const t0 = Date.now();
  for (;;) {
    await new Promise((r) => setTimeout(r, 2000));
    const st = (await axios.get(`${API}/analyze-audio-style/status/${jobId}`, { timeout: 20000 })).data;
    if (st.status === "done") return { data: st.result };
    if (st.status === "error") throw { response: { status: st.code, data: { detail: st.detail } } };
    if (Date.now() - t0 > 180000) throw { response: { data: { detail: "Analysis is taking longer than usual. Please try again." } } };
  }
};

const OWN_LYRICS_LANGS = [
  ["English", "🇺🇸 English"], ["Spanish", "🇪🇸 Spanish"], ["French", "🇫🇷 French"],
  ["Portuguese", "🇧🇷 Portuguese"], ["Italian", "🇮🇹 Italian"], ["German", "🇩🇪 German"],
  ["Japanese", "🇯🇵 Japanese"], ["Korean", "🇰🇷 Korean"], ["Mandarin", "🇨🇳 Mandarin"],
  ["Arabic", "🇸🇦 Arabic"], ["Hindi", "🇮🇳 Hindi"], ["Punjabi", "🇮🇳 Punjabi"], ["Tamil", "🇮🇳 Tamil"],
  ["Bengali", "🇧🇩 Bengali"], ["Urdu", "🇵🇰 Urdu"], ["Russian", "🇷🇺 Russian"], ["Turkish", "🇹🇷 Turkish"],
  ["Dutch", "🇳🇱 Dutch"], ["Polish", "🇵🇱 Polish"], ["Greek", "🇬🇷 Greek"], ["Hebrew", "🇮🇱 Hebrew"],
  ["Thai", "🇹🇭 Thai"], ["Vietnamese", "🇻🇳 Vietnamese"], ["Indonesian", "🇮🇩 Indonesian"], ["Filipino", "🇵🇭 Filipino (Tagalog)"],
  ["Swahili", "🇰🇪 Swahili"], ["Yoruba", "🇳🇬 Yoruba"], ["Pidgin", "🇳🇬 Nigerian Pidgin"], ["Malay", "🇲🇾 Malay"],
  ["Singlish", "🇸🇬 Singlish"], ["Patois", "🇯🇲 Jamaican Patois"], ["Creole", "🇭🇹 Haitian Creole"]
];

// Optimistic UI: creative stage labels per generation type
const GEN_STAGES = {
  beat: [[15, '🎹 Composing melody...'], [40, '🥁 Laying drums & 808s...'], [65, '🎚️ Mixing levels...'], [90, '✨ Mastering...'], [999, '📦 Finalizing...']],
  song: [[15, '✍️ Reading your lyrics...'], [40, '🎹 Composing music...'], [65, '🎤 Recording vocals...'], [90, '🎚️ Mixing & mastering...'], [999, '📦 Finalizing...']],
  vocals: [[20, '🎤 Warming up the singer...'], [50, '🗣️ Recording takes...'], [80, '🎚️ Tuning & timing...'], [999, '✨ Polishing...']]
};
const genStageFor = (kind, progress) => (GEN_STAGES[kind] || GEN_STAGES.beat).find(([t]) => progress < t)[1];

// Safe JSON fetch helper - prevents "body stream already read" error
const safeFetch = async (url, options = {}) => {
  try {
    const res = await fetch(url, options);
    const text = await res.text();
    try {
      const data = JSON.parse(text);
      return { ok: res.ok, status: res.status, data };
    } catch (e) {
      return { ok: false, status: res.status, data: { detail: text || 'Invalid response' } };
    }
  } catch (e) {
    return { ok: false, status: 0, data: { detail: 'Network error' } };
  }
};

// Turn a generation prompt into a clean track title
const titleFromPrompt = (prompt) => {
  if (!prompt) return '';
  if (/^https?:\/\//i.test(prompt.trim())) return ''; // URL prompts (link analyzer) make ugly titles
  // Looks like full song lyrics ([Section] tags or multiple lines) → never use lyrics as a title
  if (/\[[^\]]+\]/.test(prompt) || prompt.trim().split(/\n/).length > 2) return '';
  let t = prompt.trim().split(/\s+/).slice(0, 8).join(' ');
  if (t.length > 48) t = t.slice(0, 48).trim();
  return t.replace(/\b\w/g, (c) => c.toUpperCase());
};

// Per-device guest identity so anonymous users don't share one global free quota
const getGuestEmail = () => {
  let id = localStorage.getItem('sounddna_guest_id');
  if (!id) {
    id = Math.random().toString(36).slice(2, 10);
    localStorage.setItem('sounddna_guest_id', id);
  }
  return `guest-${id}@sounddna.app`;
};

// Green "ON" pill for sidebar sections that are live and working
const SidebarOnBadge = ({ style = {} }) => (
  <span data-testid="sidebar-on-badge" style={{
    marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px',
    padding: '2px 7px', background: 'rgba(34,197,94,0.12)', border: '1px solid rgba(34,197,94,0.4)',
    borderRadius: '10px', fontSize: '0.58rem', fontWeight: 800, color: '#22c55e', letterSpacing: '0.5px',
    ...style
  }}>
    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e' }} />
    ON
  </span>
);

// Debug: Log API endpoint on load
console.log("MANNIS API Endpoint:", API);
console.log("Build timestamp:", Date.now());

// Cross-platform dropdown. The native <select> popover renders unreliably on
// iOS Safari (opens showing only the placeholder / won't expand the list),
// while working fine on desktop and Android. This custom control renders the
// same DOM everywhere and does not depend on the OS native picker.
function CustomSelect({ value, onChange, options, placeholder, style = {}, accent = '#00f0ff', testId }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const label = selected ? selected.label : placeholder;

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          textAlign: 'left',
          cursor: 'pointer',
          ...style
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <ChevronDown
          className="w-4 h-4"
          style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}
        />
      </button>
      {open && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            maxHeight: '260px',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            background: '#12121f',
            border: `2px solid ${accent}`,
            borderRadius: '8px',
            zIndex: 9999,
            boxShadow: '0 12px 34px rgba(0,0,0,0.55)'
          }}
        >
          {options.map((opt) => {
            const isSel = opt.value === value;
            return (
              <div
                key={opt.value || opt.label}
                role="option"
                aria-selected={isSel}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{
                  padding: '12px 14px',
                  cursor: 'pointer',
                  color: isSel ? accent : '#dddddd',
                  fontSize: '15px',
                  fontWeight: isSel ? 700 : 500,
                  background: isSel ? 'rgba(255,255,255,0.06)' : 'transparent',
                  borderBottom: '1px solid rgba(255,255,255,0.05)'
                }}
              >
                {opt.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Universal Style Prompts - Works with ALL A.I. Music Platforms
const PLATFORMS = {
  universal: { name: "MANNIS Style Prompt", color: "#ffd700", icon: "🎵" },
};

// Display-only: show legacy "LeShore" category/prompt branding as "MANNIS"
// (underlying category keys stay unchanged so API calls keep working).
const mannisCat = (n) => (n || '').replace(/Charles\s+LeShore/gi, 'MANNIS').replace(/LeShore/gi, 'MANNIS');

// Packages (trial + paid plans - no free tier)
const PACKAGES = {
  trial: { name: "5-Day Trial", price: 3.99, prompts: "20", platforms: "Universal", badge: "TRIAL", generations: 15, vocals: 15, description: "5 days — then pick a monthly plan" },
  student: { name: "Starter", price: 9, prompts: "Unlimited", platforms: "Universal", badge: "STARTER", generations: 90, vocals: 90 },
  creator: { name: "Creator Pro", price: 16, prompts: "Unlimited", platforms: "Universal", badge: "CREATOR PRO", generations: 160, vocals: 160, subtitle: "Best for most creators" },
  pro: { name: "Pro", price: 28, prompts: "Unlimited", platforms: "Universal", badge: "PRO", generations: 420, vocals: 420 },
};

// DNA Legend
const DNA_LEGEND = {
  G: { name: "Genre", color: "#9F47FF" },
  M: { name: "Mood", color: "#ff0099" },
  I: { name: "Instruments", color: "#00f0ff" },
  V: { name: "Vocals", color: "#a855f7" },
  P: { name: "Production", color: "#7000ff" },
  E: { name: "Energy", color: "#9F47FF" },
  X: { name: "Experimental", color: "#f97316" },
};

const STYLE_MODES = [
  { value: "clone", label: "Sound Clone" },
  { value: "evolve", label: "Evolved Version" },
  { value: "remix", label: "Genre Remix" },
  { value: "contrast", label: "Contrast Flip" },
  { value: "variations", label: "Max Variety" },
];

const GENRES = [
  { value: "auto", label: "Auto-Detect", color: "#9F47FF", icon: "🎯" },
  { value: "all", label: "ALL GENRES", color: "#9F47FF", icon: "🌈", desc: "1M+ combinations across ALL genres!", special: true },
  // Hip Hop & Urban
  { value: "trap", label: "Trap", color: "#9F47FF", bpm: "130-170", desc: "808s, hi-hats, dark melodies", icon: "🔥" },
  { value: "hiphop", label: "Hip Hop", color: "#f97316", bpm: "85-115", desc: "Boom bap, samples, flows", icon: "🎤" },
  { value: "drill", label: "Drill", color: "#ef4444", bpm: "140-150", desc: "Sliding 808s, dark, aggressive", icon: "🔫" },
  { value: "phonk", label: "Phonk", color: "#9333ea", bpm: "130-160", desc: "Memphis rap, cowbells, dark", icon: "👻" },
  { value: "grime", label: "Grime", color: "#6366f1", bpm: "140", desc: "UK rap, aggressive, bass heavy", icon: "🇬🇧" },
  // R&B & Soul
  { value: "rnb", label: "R&B", color: "#a855f7", bpm: "60-90", desc: "Smooth vocals, soul, grooves", icon: "💜" },
  { value: "soul", label: "Soul", color: "#d946ef", bpm: "60-100", desc: "Emotion, gospel roots, warmth", icon: "❤️" },
  { value: "gospel", label: "Gospel", color: "#fcd34d", bpm: "70-120", desc: "Choir harmonies, spiritual, uplifting", icon: "🙏" },
  { value: "funk", label: "Funk", color: "#f472b6", bpm: "100-130", desc: "Groovy bass, rhythmic, party", icon: "🕺" },
  { value: "disco", label: "Disco", color: "#ec4899", bpm: "110-130", desc: "Four-on-the-floor, strings, party", icon: "🪩" },
  // Pop & Mainstream
  { value: "pop", label: "Pop", color: "#ec4899", bpm: "100-130", desc: "Catchy hooks, polished production", icon: "⭐" },
  { value: "kpop", label: "K-Pop", color: "#f43f5e", bpm: "100-140", desc: "Korean pop, dance, polished", icon: "🇰🇷" },
  { value: "jpop", label: "J-Pop", color: "#fb7185", bpm: "120-160", desc: "Japanese pop, anime, energetic", icon: "🇯🇵" },
  // Electronic & Dance
  { value: "edm", label: "EDM", color: "#00f0ff", bpm: "120-150", desc: "Synths, drops, energy", icon: "⚡" },
  { value: "house", label: "House", color: "#9F47FF", bpm: "120-130", desc: "4/4 beat, disco influence", icon: "🏠" },
  { value: "techno", label: "Techno", color: "#0ea5e9", bpm: "130-150", desc: "Industrial, repetitive, dark", icon: "🤖" },
  { value: "trance", label: "Trance", color: "#06b6d4", bpm: "130-150", desc: "Euphoric, melodic, buildups", icon: "🌀" },
  { value: "dubstep", label: "Dubstep", color: "#7c3aed", bpm: "140", desc: "Heavy bass drops, wobble", icon: "💥" },
  { value: "dnb", label: "Drum & Bass", color: "#9F47FF", bpm: "160-180", desc: "Fast breaks, heavy bass", icon: "🥁" },
  { value: "garage", label: "UK Garage", color: "#a78bfa", bpm: "130-140", desc: "2-step, skippy beats, UK", icon: "🎧" },
  // African Music
  { value: "african", label: "African", color: "#f59e0b", bpm: "90-130", desc: "Pan-African rhythms, diverse", icon: "🌍" },
  { value: "afrobeats", label: "Afrobeats", color: "#f97316", bpm: "100-120", desc: "Nigerian/Ghanaian, danceable", icon: "🥁" },
  { value: "amapiano", label: "Amapiano", color: "#fb923c", bpm: "110-120", desc: "South African, deep house, log drums", icon: "🇿🇦" },
  { value: "afropop", label: "Afropop", color: "#fdba74", bpm: "95-120", desc: "African pop fusion, melodic", icon: "🎵" },
  { value: "highlife", label: "Highlife", color: "#fbbf24", bpm: "100-130", desc: "Ghanaian, jazzy, brass", icon: "🎺" },
  { value: "afrohouse", label: "Afro House", color: "#facc15", bpm: "120-125", desc: "African rhythms, house beats", icon: "🔊" },
  // Caribbean & Latin
  { value: "reggae", label: "Reggae", color: "#9F47FF", bpm: "60-90", desc: "Jamaican, offbeat, chill", icon: "🇯🇲" },
  { value: "dancehall", label: "Dancehall", color: "#4ade80", bpm: "90-110", desc: "Jamaican, energetic, bass", icon: "💃" },
  { value: "reggaeton", label: "Reggaeton", color: "#84cc16", bpm: "85-100", desc: "Dembow rhythm, Latin vibes", icon: "🌴" },
  { value: "latin", label: "Latin", color: "#eab308", bpm: "90-130", desc: "Salsa, bachata, tropical", icon: "💃" },
  { value: "soca", label: "Soca", color: "#a3e635", bpm: "130-160", desc: "Caribbean, carnival, party", icon: "🎉" },
  // Rock & Alternative
  { value: "rock", label: "Rock", color: "#f59e0b", bpm: "110-140", desc: "Guitars, drums, raw energy", icon: "🎸" },
  { value: "metal", label: "Metal", color: "#78716c", bpm: "100-200", desc: "Heavy, distorted, aggressive", icon: "🤘" },
  { value: "punk", label: "Punk", color: "#ef4444", bpm: "150-200", desc: "Fast, rebellious, raw", icon: "⚡" },
  { value: "indie", label: "Indie", color: "#67e8f9", bpm: "100-140", desc: "Alternative, DIY sound", icon: "🎹" },
  { value: "alternative", label: "Alternative", color: "#38bdf8", bpm: "100-140", desc: "Non-mainstream, experimental", icon: "🎵" },
  // Chill & Acoustic
  { value: "lofi", label: "Lo-Fi", color: "#9F47FF", bpm: "70-90", desc: "Chill beats, vinyl crackle", icon: "☕" },
  { value: "jazz", label: "Jazz", color: "#06b6d4", bpm: "80-160", desc: "Improvisation, complex chords", icon: "🎷" },
  { value: "blues", label: "Blues", color: "#3b82f6", bpm: "60-120", desc: "12-bar, emotional, guitar", icon: "🎸" },
  { value: "country", label: "Country", color: "#a3e635", bpm: "90-130", desc: "Acoustic, storytelling", icon: "🤠" },
  { value: "folk", label: "Folk", color: "#86efac", bpm: "80-120", desc: "Acoustic, traditional, storytelling", icon: "🪕" },
  { value: "acoustic", label: "Acoustic", color: "#a7f3d0", bpm: "70-120", desc: "Unplugged, intimate, warm", icon: "🎸" },
  // World & Cultural
  { value: "bollywood", label: "Bollywood", color: "#f472b6", bpm: "100-140", desc: "Indian film music, dramatic", icon: "🇮🇳" },
  { value: "arabic", label: "Arabic", color: "#c084fc", bpm: "90-130", desc: "Middle Eastern, quarter tones", icon: "🕌" },
  { value: "brazilian", label: "Brazilian", color: "#4ade80", bpm: "90-140", desc: "Samba, bossa nova, tropical", icon: "🇧🇷" },
  // Atmospheric & Cinematic
  { value: "ambient", label: "Ambient", color: "#5eead4", bpm: "60-100", desc: "Atmospheric, textural", icon: "🌊" },
  { value: "cinematic", label: "Cinematic", color: "#fbbf24", bpm: "60-140", desc: "Orchestral, epic, emotional", icon: "🎬" },
  { value: "classical", label: "Classical", color: "#d4d4d8", bpm: "60-180", desc: "Orchestral, timeless, complex", icon: "🎻" },
  { value: "electronic", label: "Electronic", color: "#c084fc", bpm: "100-180", desc: "Synths, experimental", icon: "🎛️" },
  { value: "experimental", label: "Experimental", color: "#e879f9", bpm: "60-200", desc: "Avant-garde, boundary-pushing", icon: "🔬" },
];

const ARTIST_STYLES = [
  { value: "none", label: "No Style", color: "#666" },
  
  // R&B (3)
  { value: "trapsoul", label: "TrapSoul", color: "#6366f1", genre: "rnb" },
  { value: "dark_rnb", label: "Dark R&B", color: "#ef4444", genre: "rnb" },
  { value: "90s_rnb", label: "90s R&B", color: "#f97316", genre: "rnb" },
  
  // Hip Hop (3)
  { value: "boom_bap", label: "Boom Bap", color: "#f97316", genre: "hiphop" },
  { value: "southern_rap", label: "Southern Rap", color: "#f59e0b", genre: "hiphop" },
  { value: "west_coast", label: "West Coast", color: "#eab308", genre: "hiphop" },
  
  // Trap (4)
  { value: "melodic_trap_prod", label: "Melodic Trap", color: "#f97316", genre: "trap" },
  { value: "hard_trap", label: "Hard Trap", color: "#ef4444", genre: "trap" },
  { value: "dark_melodic", label: "Dark Melodic", color: "#00f0ff", genre: "trap" },
  { value: "rage_trap", label: "Rage Trap", color: "#9F47FF", genre: "trap" },
  
  // Drill (2)
  { value: "uk_drill", label: "UK Drill", color: "#ef4444", genre: "drill" },
  { value: "ny_drill", label: "NY Drill", color: "#f97316", genre: "drill" },
  
  // Pop (3)
  { value: "dance_pop", label: "Dance Pop", color: "#ec4899", genre: "pop" },
  { value: "dark_pop", label: "Dark Pop", color: "#6366f1", genre: "pop" },
  { value: "hyperpop", label: "Hyperpop", color: "#9F47FF", genre: "pop" },
  
  // Electronic (3)
  { value: "deep_house", label: "Deep House", color: "#9F47FF", genre: "house" },
  { value: "future_bass", label: "Future Bass", color: "#f472b6", genre: "edm" },
  { value: "synthwave", label: "Synthwave", color: "#9F47FF", genre: "electronic" },
  
  // African (3)
  { value: "afrobeats_dance", label: "Afrobeats", color: "#f97316", genre: "afrobeats" },
  { value: "amapiano_deep", label: "Amapiano", color: "#fb923c", genre: "amapiano" },
  { value: "afro_fusion", label: "Afro Fusion", color: "#fbbf24", genre: "african" },
  
  // Latin (2)
  { value: "reggaeton_modern", label: "Reggaeton", color: "#84cc16", genre: "reggaeton" },
  { value: "latin_trap", label: "Latin Trap", color: "#ef4444", genre: "latin" },
  
  // Other Essentials (6)
  { value: "jazz_fusion", label: "Jazz Fusion", color: "#06b6d4", genre: "jazz" },
  { value: "neo_soul", label: "Neo Soul", color: "#a855f7", genre: "soul" },
  { value: "lofi_beats", label: "Lo-Fi Beats", color: "#a855f7", genre: "lofi" },
  { value: "epic_cinematic", label: "Cinematic", color: "#fbbf24", genre: "cinematic" },
  { value: "classic_rock", label: "Classic Rock", color: "#f59e0b", genre: "rock" },
  { value: "indie_rock", label: "Indie Rock", color: "#9F47FF", genre: "rock" },
];

// Music Style technical blueprints (mirrors backend STYLE_DESCRIPTIONS) — powers the
// live "Style Preview" line under the Rhyme & Flow Engine Music Style dropdown.
const STYLE_BLUEPRINTS = {
  "modern trap": { rhythm: "Rolling triplet hi-hats over booming 808 slides at 130-150 BPM, syncopated off-beats.", tone: "Confident, flashy, dark-luxury swagger with menacing undertones.", delivery: "Punchy ad-libs, repeated catchy hooks, autotuned melodic flexes and short quotable bars." },
  "southern soul": { rhythm: "Mid-tempo 70-90 BPM shuffle grooves with live drums, warm basslines and organ swells.", tone: "Heartfelt, grown-and-sexy, bluesy Southern warmth and vulnerability.", delivery: "Sung storytelling with melisma, call-and-response and conversational church-raised phrasing." },
  "pain": { rhythm: "Slow-to-mid trap tempo with sparse aching piano loops and heavy reverb-drenched 808s.", tone: "Raw heartbreak, grief and dark introspection — crying-in-the-booth vulnerability.", delivery: "Strained melodic autotune, cracked emotive vocals and confessional first-person bars." },
  "old school": { rhythm: "85-95 BPM boom-bap breakbeats with dusty sampled drums and swing.", tone: "Nostalgic golden-era grit, streetwise confidence and lyricism-first pride.", delivery: "On-beat punchy cadence, multisyllabic rhymes, scratch-hook energy and storytelling verses." },
  "melodic": { rhythm: "Bouncy trap/pop-rap tempo (120-150 BPM) with melodic 808s and airy synth pads.", tone: "Emotional, catchy, bittersweet and radio-friendly.", delivery: "Sing-rap hybrid with autotuned hooks, held notes and hummable toplines." },
  "lyrical": { rhythm: "Flexible boom-bap or trap backdrop that leaves pocket space for words.", tone: "Cerebral, competitive, wordplay-obsessed and metaphor-rich.", delivery: "Dense multisyllabic rhymes, internal rhyme chains and setup-and-punchline structure." },
  "aggressive": { rhythm: "Hard-hitting fast drums with distorted 808s and stabbing brass/synths.", tone: "Confrontational, battle-ready, high-adrenaline intensity.", delivery: "Shouted forceful cadence, rapid-fire bars, chest-out delivery and taunts." },
  "conscious hip-hop": { rhythm: "Soulful jazz/boom-bap grooves with live instrumentation and head-nod mid-tempo.", tone: "Thoughtful, socially aware, uplifting and knowledge-driven.", delivery: "Measured storytelling flow, layered metaphors and message-first verses." },
  "drill": { rhythm: "140 BPM sliding 808s, skittering hi-hats and dark minor-key piano/orchestral loops.", tone: "Menacing, cold, street-hardened and ominous.", delivery: "Sliding off-kilter cadence, clipped line endings and gritty UK/Chicago patois ad-libs." },
  "boom bap": { rhythm: "85-95 BPM hard-swinging sampled drums with thick kicks and crisp snares.", tone: "Classic, gritty, head-nodding golden-era confidence.", delivery: "Locked on-beat pocket, punchy multisyllabic rhymes and scratch hooks." },
  "r&b": { rhythm: "Smooth 60-100 BPM grooves with lush chords, finger-snaps and warm bass.", tone: "Sensual, romantic, emotional and velvety.", delivery: "Layered harmonies, runs and riffs, breathy melodic phrasing and intimate delivery." },
  "rnb": { rhythm: "Smooth 60-100 BPM grooves with lush chords, finger-snaps and warm bass.", tone: "Sensual, romantic, emotional and velvety.", delivery: "Layered harmonies, runs and riffs, breathy melodic phrasing and intimate delivery." },
  "storytelling": { rhythm: "Steady mid-tempo backdrop that gives room for narrative pacing.", tone: "Cinematic, vivid, immersive with an emotional arc.", delivery: "Chronological plot bars, scene-setting detail, character voices and controlled cadence." },
  "west coast": { rhythm: "90-105 BPM G-funk bounce with whiny synth leads, funky bass and live snaps.", tone: "Laid-back, sunny, cocky West Coast swagger and party ease.", delivery: "Smooth pocket flow, drawn-out drawl, gang-vocal hooks and groove-riding phrasing." },
  "gospel": { rhythm: "Uplifting 70-120 BPM with organ, live drums, hand-claps and choir swells.", tone: "Spiritual, joyful, testimonial and reverent.", delivery: "Powerful belted vocals, choir call-and-response, melisma and praise-break dynamics." },
  "trapsoul": { rhythm: "Dark moody trap drums (120-140 BPM) with muted 808s and hazy R&B chords.", tone: "Nocturnal, emotional, toxic-romance melancholy.", delivery: "Hushed autotuned croon, layered ad-libs and blurred sing-rap phrasing (Bryson Tiller lane)." },
  "neo soul": { rhythm: "Loose jazzy 70-95 BPM grooves with warm Rhodes, live bass and pocket drums.", tone: "Soulful, introspective, organic and grown.", delivery: "Conversational sung phrasing, jazz runs, behind-the-beat feel and rich harmonies." },
  "melodic rap": { rhythm: "Melodic trap/pop-rap 120-150 BPM with tuneful 808s and bright synth leads.", tone: "Emotional, catchy, bittersweet.", delivery: "Autotuned sing-rap, hummable hooks and held melodic notes over a rap cadence." },
  "aggressive trap": { rhythm: "Fast rage-adjacent trap with distorted 808s, hard hi-hat rolls and stabs.", tone: "Hyped, confrontational, mosh-pit intensity.", delivery: "Shouted punch-in bars, dense ad-libs and high-energy chant hooks." },
  "conscious": { rhythm: "Soulful mid-tempo boom-bap/jazz grooves with a live feel.", tone: "Aware, uplifting, thought-provoking.", delivery: "Measured storytelling, layered metaphors and message-driven verses." },
  "heartbreak": { rhythm: "Slow-to-mid melodic backdrop with somber piano/guitar and soft 808s.", tone: "Melancholic, longing, late-night regret.", delivery: "Tender melodic vocals, sighing phrasing and confessional first-person bars." },
  "motivational": { rhythm: "Triumphant mid-to-up-tempo with soaring strings/synths and driving drums.", tone: "Inspiring, resilient, victorious grind energy.", delivery: "Anthemic belted hooks, rising-intensity verses and chant-along payoff lines." },
  "party": { rhythm: "High-energy 100-128 BPM club bounce with big drops and hands-up drums.", tone: "Fun, hype, carefree celebration.", delivery: "Chantable call-and-response hooks, simple catchy repetition and crowd-igniting ad-libs." },
  "afrobeats": { rhythm: "100-120 BPM syncopated log-drum/percussion groove with rolling bass and off-beat swing.", tone: "Warm, sunny, danceable Lagos-night joy.", delivery: "Melodic pidgin-flavored phrasing, breezy sing-song hooks and percussive vocal bounce." },
  "amapiano": { rhythm: "110-120 BPM deep-house pulse with signature log-drum basslines, shakers and airy pads.", tone: "Laid-back, hypnotic, groovy South African cool.", delivery: "Chanted repetitive vocal phrases, spacious call-and-response and log-drum-locked cadence." },
  "reggaeton": { rhythm: "Dembow riddim boom-ch-boom-chick at 90-100 BPM with punchy kick-snare bounce.", tone: "Flirtatious, energetic, perreo party heat.", delivery: "Spanish/Spanglish hooks, bouncy syncopated flow and catchy repeated refrains." },
  "dancehall": { rhythm: "Riddim-driven bounce (90-110 BPM) with heavy kick, offbeat skank and sparse space.", tone: "Brash, playful, island swagger.", delivery: "Patois toasting, rhythmic chant-singing and riddim-riding call-and-response." },
  "latin trap": { rhythm: "Trap 130-150 BPM with dark 808s fused with Latin percussion accents.", tone: "Moody, seductive, malianteo street cool.", delivery: "Spanish/Spanglish autotuned bars, half-sung hooks and sliding melodic phrasing." },
  "pop": { rhythm: "Polished 100-125 BPM four-on-the-floor or pop-trap groove with bright production.", tone: "Catchy, clean, universally uplifting crossover appeal.", delivery: "Strong topline melody, a big singable chorus and verse-prechorus-chorus structure." },
  "rock": { rhythm: "Driving 4/4 with distorted guitars, live drums and anthemic builds (100-140 BPM).", tone: "Rebellious, raw, high-energy attitude.", delivery: "Belted shouted hooks, gritty sustained vocals and gang-vocal chants." },
  "country": { rhythm: "Mid-tempo shuffle with acoustic guitar, pedal steel, fiddle and a steady backbeat.", tone: "Honest, heartland, nostalgic storytelling warmth.", delivery: "Twangy conversational vocals, vivid narrative verses and singalong choruses." },
  "baile funk": { rhythm: "130-150 BPM Rio baile funk on the signature tamborzão beat with punchy kicks and syncopated percussion.", tone: "Raw, sweaty, hyper-energetic favela party heat.", delivery: "Chanted Portuguese call-and-response, rapid repetitive hooks and shouted crowd-igniting ad-libs." },
  "uk garage": { rhythm: "130-135 BPM 2-step shuffle with skippy syncopated drums, rolling sub-bass and chopped vocal stabs.", tone: "Slick, nocturnal, London-night dancefloor cool.", delivery: "Bouncy off-beat sung-rap phrasing, clipped MC bars and soulful chopped vocal hooks." },
  "phonk": { rhythm: "130-150 BPM Memphis-rooted phonk with cowbell melodies, distorted 808s and lo-fi drift drums.", tone: "Dark, hazy, menacing retro-underground grit.", delivery: "Pitched-down chopped-and-screwed vocals, aggressive whispered rap cadence and gritty cowbell-locked ad-libs." },
};

// Built-in singing voices for the Voices Library (mirrors backend SINGING_VOICES).
const SONG_VOICES = [
  { voice_id: "CwhRBWXzGAHq8TQ4Fs17", name: "Marcus Cole", style: "Male R&B Tenor", genre: "R&B, Soul" },
  { voice_id: "EXAVITQu4vr4xnSDxMaL", name: "Jasmine Rivers", style: "Female Soul Diva", genre: "Soul, Gospel" },
  { voice_id: "FGY2WhTYpPnrIDTdsKH5", name: "Luna Martinez", style: "Female Pop Soprano", genre: "Pop, Dance" },
  { voice_id: "IKne3meq5aSn9XLyUdCD", name: "Darius Black", style: "Male Trap Baritone", genre: "Trap, Hip-Hop" },
  { voice_id: "JBFqnCBsd6RMkjVDRZzb", name: "George Sterling", style: "Male Crooner", genre: "Jazz, Classic" },
  { voice_id: "N2lVS1w4EtoT3dr4eOWO", name: "Kai Shadow", style: "Male Dark R&B", genre: "Dark R&B, Alternative" },
  { voice_id: "TX3LPaxmHKxFdv7VOQHJ", name: "Liam Cruz", style: "Male Pop Tenor", genre: "Pop, EDM" },
  { voice_id: "Xb7hH8MSUJpSbSDYk0k2", name: "Aria Snow", style: "Female Indie", genre: "Indie, Folk" },
  { voice_id: "XrExE9yKIg1WjnnlVkGX", name: "Matilda Rose", style: "Female Alto", genre: "Alternative, Rock" },
  { voice_id: "bIHbv24MWmeRgasZH58o", name: "Will Thompson", style: "Male Acoustic", genre: "Acoustic, Folk" },
  { voice_id: "cgSgspJ2msm6clMCkdW9", name: "Jessica Blaze", style: "Female Dance Diva", genre: "Dance, EDM" },
  { voice_id: "cjVigY5qzO86Huf0OWal", name: "Eric Velvet", style: "Male Smooth R&B", genre: "R&B, Neo-Soul" },
  { voice_id: "pFZP5JQG7iQjIQuC4Bku", name: "Lily Chen", style: "Female Ethereal", genre: "Ethereal, Dream Pop" },
  { voice_id: "ui0NMIinCTg8KvB4ogeV", name: "Rachel Diamond", style: "Female Powerhouse", genre: "Pop, R&B" },
  { voice_id: "Bg4N75kZgwrthztCKskg", name: "Demi Starr", style: "Female Young Pop", genre: "Pop, Teen" },
  { voice_id: "t3iNwCjYhE9IEQPVBlys", name: "Emi Tanaka", style: "Female J-Pop", genre: "J-Pop, K-Pop" },
];

const SONG_MOODS = ["Happy", "Sad", "Chill", "Energetic", "Romantic", "Dark", "Uplifting", "Aggressive", "Dreamy", "Confident", "Melancholic", "Epic"];

const SONG_INSTRUMENTS = ["808s", "Piano", "Electric Guitar", "Acoustic Guitar", "Synth", "Strings", "Live Drums", "Bass", "Brass", "Flute", "Pads", "Organ"];

// Quick style chips to fill the song description fast
const QUICK_STYLES = ["Trap", "R&B", "Afrobeat", "Pop", "EDM", "Hip-Hop", "Drill", "Amapiano", "Reggaeton", "Lo-Fi", "Gospel", "Rock"];

// Large numbered step badge shown at the top-left of each Create-view section header
const StepBadge = ({ n }) => (
  <span
    data-testid={`step-badge-${n}`}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '40px',
      height: '40px',
      flexShrink: 0,
      borderRadius: '12px',
      marginRight: '6px',
      background: 'linear-gradient(135deg, #00d4ff, #9F47FF)',
      color: '#0a0e1a',
      fontWeight: 900,
      fontSize: '1.45rem',
      lineHeight: 1,
      boxShadow: '0 4px 16px rgba(0,212,255,0.45)',
      border: '2px solid rgba(255,255,255,0.3)'
    }}
    aria-label={`Step ${n}`}
  >
    {n}
  </span>
);

const StyleBlueprintPreview = ({ styleKey, testId = "style-blueprint-preview" }) => {
  const bp = STYLE_BLUEPRINTS[styleKey] || STYLE_BLUEPRINTS["modern trap"];
  const row = (label, text) => (
    <div style={{ color: 'rgba(255,255,255,0.82)', marginBottom: '2px' }}>
      <span style={{ color: '#fb923c', fontWeight: 700 }}>{label}:</span> {text}
    </div>
  );
  return (
    <div
      data-testid={testId}
      style={{ marginTop: '8px', padding: '9px 11px', background: 'rgba(249,115,22,0.07)', border: '1px solid rgba(249,115,22,0.22)', borderRadius: '8px', fontSize: '0.7rem', lineHeight: 1.45 }}
    >
      {row('Rhythm', bp.rhythm)}
      {row('Tone', bp.tone)}
      {row('Delivery', bp.delivery)}
    </div>
  );
};


// DNA Bar
const DNABar = ({ label, value, color }) => (
  <div className="dna-bar-item">
    <span className="dna-bar-label" style={{ color }}>{label}</span>
    <div className="dna-bar-track">
      <motion.div className="dna-bar-fill" initial={{ width: 0 }} animate={{ width: `${(value / 9) * 100}%` }} />
    </div>
    <span className="dna-bar-value">{value}</span>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MO3 SONG STRUCTURE GENERATOR - Professional Song Allocation Formula
// Structure: Intro → Verse → Pre-Chorus → Chorus → Verse → Chorus → Bridge → Outro
// Modulation: Half/Whole step key change after Bridge
// Complexity: 0-9 scale (0=sparse, 9=dense)
// ═══════════════════════════════════════════════════════════════════════════════

const generateMo3Structure = (stylePrompt, songLengthMinutes = 3, tempo = 120) => {
  // Extract key from style prompt
  const keyMatch = stylePrompt.match(/\b([A-G][#b]?)\s*(major|minor|m|maj)?\b/i);
  const baseKey = keyMatch ? keyMatch[1].toUpperCase() + (keyMatch[2]?.toLowerCase().startsWith('m') ? 'm' : '') : 'Cm';
  
  // Calculate total bars based on song length and tempo
  // Formula: (minutes * tempo * 4) / 4 = bars (assuming 4/4 time, 4 beats per bar)
  const totalBars = Math.round((songLengthMinutes * tempo) / 2);
  
  // Define section templates based on song length
  const getSectionTemplate = (minutes) => {
    if (minutes <= 2) {
      // Short song (2 min): Simplified structure
      return [
        { name: 'Intro', bars: 4, complexity: 2 },
        { name: 'Verse', bars: 8, complexity: 4 },
        { name: 'Chorus', bars: 8, complexity: 7 },
        { name: 'Verse', bars: 8, complexity: 5 },
        { name: 'Chorus', bars: 8, complexity: 8 },
        { name: 'Outro', bars: 4, complexity: 3 },
      ];
    } else if (minutes <= 3.5) {
      // Standard song (3-3.5 min): Full Mo3 structure
      return [
        { name: 'Intro', bars: 8, complexity: 3 },
        { name: 'Verse 1', bars: 16, complexity: 5 },
        { name: 'Pre-Chorus', bars: 8, complexity: 6 },
        { name: 'Chorus', bars: 16, complexity: 8 },
        { name: 'Verse 2', bars: 16, complexity: 5 },
        { name: 'Chorus', bars: 16, complexity: 8 },
        { name: 'Bridge', bars: 8, complexity: 7, modulate: true },
        { name: 'Outro', bars: 8, complexity: 4 },
      ];
    } else {
      // Extended song (4+ min): Full structure with breakdown
      return [
        { name: 'Intro', bars: 8, complexity: 2 },
        { name: 'Verse 1', bars: 16, complexity: 4 },
        { name: 'Pre-Chorus', bars: 8, complexity: 6 },
        { name: 'Chorus', bars: 16, complexity: 8 },
        { name: 'Post-Chorus', bars: 8, complexity: 7 },
        { name: 'Verse 2', bars: 16, complexity: 5 },
        { name: 'Pre-Chorus', bars: 8, complexity: 6 },
        { name: 'Chorus', bars: 16, complexity: 9 },
        { name: 'Bridge', bars: 8, complexity: 6, modulate: true },
        { name: 'Breakdown', bars: 8, complexity: 3 },
        { name: 'Final Chorus', bars: 16, complexity: 9 },
        { name: 'Outro', bars: 8, complexity: 4 },
      ];
    }
  };
  
  // Calculate key modulation (half step or whole step up)
  const getModulatedKey = (key) => {
    const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const isMinor = key.includes('m');
    const rootNote = key.replace('m', '');
    const currentIndex = notes.indexOf(rootNote);
    
    // Randomly choose half step (1) or whole step (2)
    const step = Math.random() > 0.5 ? 1 : 2;
    const newIndex = (currentIndex + step) % 12;
    const stepName = step === 1 ? 'half step' : 'whole step';
    
    return {
      newKey: notes[newIndex] + (isMinor ? 'm' : ''),
      step: stepName
    };
  };
  
  const sections = getSectionTemplate(songLengthMinutes);
  const modulation = getModulatedKey(baseKey);
  
  // Generate formatted output
  const sectionStrings = sections.map(s => 
    `[${s.name}: ${s.bars} bars, density ${s.complexity}]`
  ).join(' ');
  
  const compactFormat = sections.map(s => 
    `${s.name.replace(/\s+/g, '')}(${s.complexity})`
  ).join('-');
  
  // Find bridge section and note the key change
  const bridgeIndex = sections.findIndex(s => s.modulate);
  const keyChangeNote = bridgeIndex >= 0 
    ? `Key: ${baseKey} → ${modulation.newKey} (${modulation.step} after Bridge)`
    : `Key: ${baseKey}`;
  
  return {
    // Full detailed format
    detailed: sectionStrings,
    // Compact format
    compact: `Structure: ${compactFormat} | ${keyChangeNote}`,
    // Metadata object
    metadata: {
      baseKey,
      modulatedKey: modulation.newKey,
      modulationStep: modulation.step,
      totalBars: sections.reduce((sum, s) => sum + s.bars, 0),
      sections: sections,
      tempo,
      songLength: songLengthMinutes
    },
    // Quick reference
    quickRef: `${songLengthMinutes}:00 | ${tempo} BPM | ${baseKey} → ${modulation.newKey}`,
    // Section bars summary
    barsSummary: sections.map(s => `${s.name}: ${s.bars}`).join(', ')
  };
};

// Generate complexity based on genre/style keywords
const getGenreComplexity = (stylePrompt) => {
  const complexityMap = {
    // High complexity (7-9)
    'progressive': 8, 'experimental': 9, 'jazz': 8, 'fusion': 8, 'technical': 9,
    'orchestral': 8, 'cinematic': 7, 'epic': 8,
    // Medium-high (6-7)
    'house': 7, 'techno': 7, 'dnb': 8, 'drum and bass': 8, 'metal': 8,
    'funk': 7, 'disco': 6, 'gospel': 7,
    // Medium (4-6)
    'pop': 5, 'rock': 6, 'hip hop': 5, 'trap': 6, 'r&b': 5,
    'afrobeats': 6, 'reggaeton': 5, 'dancehall': 6,
    // Low-medium (3-5)
    'lofi': 3, 'ambient': 2, 'chill': 3, 'acoustic': 4,
    'folk': 4, 'country': 4, 'ballad': 3,
    // Low (1-3)
    'minimal': 2, 'drone': 1, 'meditation': 1
  };
  
  const lowerPrompt = stylePrompt.toLowerCase();
  for (const [keyword, complexity] of Object.entries(complexityMap)) {
    if (lowerPrompt.includes(keyword)) {
      return complexity;
    }
  }
  return 5; // Default medium complexity
};

// Prompt Card
const PromptCard = ({ prompt, index, onGenerateSong, onUseForSong, onGenerateDrumPattern, onGenerateMidi, isGenerating, isGeneratingMidi, preview, onRenderPreview, shareOrDownloadBlob }) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [generatingDrum, setGeneratingDrum] = useState(false);
  const [generatingMidi, setGeneratingMidi] = useState(false);
  const [downloadingStems, setDownloadingStems] = useState(false);
  
  // Render 10 WAV stems from this style prompt and download as zip.
  // Backend starts the render in the background and returns a task_id immediately;
  // we poll status on a short interval so no single request sits open long enough
  // to hit the production edge/proxy timeout (this used to be one long blocking call).
  const handleDownloadStems = async (e) => {
    e.stopPropagation();
    setDownloadingStems(true);
    try {
      toast.info('🎛️ Rendering 10 WAV stems... (~30-60s)');
      const startRes = await axios.post(`${API}/generate-midi/stems-wav`, {
        style_prompt: prompt.prompt,
        bars: 16
      });
      const taskId = startRes.data.task_id;

      let status = startRes.data.status;
      const maxAttempts = 100; // ~5 minutes at 3s intervals, matches backend render timeout
      for (let attempt = 0; attempt < maxAttempts && status === 'processing'; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const statusRes = await axios.get(`${API}/generate-midi/stems-wav/status/${taskId}`);
        status = statusRes.data.status;
        if (status === 'failed') {
          throw new Error(statusRes.data.error || 'Stem rendering failed.');
        }
      }
      if (status !== 'completed') {
        throw new Error('Stem rendering timed out. Try again.');
      }

      const res = await axios.get(`${API}/generate-midi/stems-wav/download/${taskId}`, {
        responseType: 'blob'
      });

      const blob = new Blob([res.data], { type: 'application/zip' });
      const zipName = 'sound_dna_stems.zip';
      await shareOrDownloadBlob(blob, zipName, 'application/zip', {
        successMessage: '🎛️ 10 WAV stems downloaded! Drop the zip into your DAW',
      });
    } catch (err) {
      console.error('Stems render error:', err);
      toast.error(err.message && err.message !== 'Network Error' ? err.message : 'Stem rendering failed. Try again.');
    } finally {
      setDownloadingStems(false);
    }
  };
  const platform = PLATFORMS[prompt.platform];

  const handleCopy = async (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    
    const textToCopy = prompt.prompt;
    
    // Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = textToCopy;
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.width = '2em';
    textArea.style.height = '2em';
    textArea.style.padding = '0';
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';
    textArea.style.background = 'transparent';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    let success = false;
    try {
      success = document.execCommand('copy');
    } catch (err) {
      console.error('execCommand error:', err);
    }
    
    document.body.removeChild(textArea);
    
    if (success) {
      setCopied(true);
      toast.success("✓ COPIED! Tap & hold to paste");
      setTimeout(() => setCopied(false), 2000);
    } else {
      // Try clipboard API as fallback
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        toast.success("✓ COPIED! Tap & hold to paste");
        setTimeout(() => setCopied(false), 2000);
      } catch (clipErr) {
        toast.error("Copy failed - please tap & hold to select, then tap Copy");
      }
    }
  };

  const openInPlatform = async (e) => {
    e?.stopPropagation();
    
    // Copy prompt to clipboard (universal - works with any A.I. music platform)
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(prompt.prompt);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = prompt.prompt;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success("✓ Copied! Tap & hold to paste in " + platform?.name);
    } catch (err) {
      toast.info("Opening " + platform?.name + "... Copy the prompt manually");
    }
    
    // Open the platform URL if available
    if (platform?.url) {
      const newWindow = window.open(platform.url, '_blank');
      if (!newWindow) {
        toast.error("Popup blocked! Allow popups for this site.");
      }
    }
  };

  const handleUseForSong = (e) => {
    e?.stopPropagation();
    if (onUseForSong) {
      onUseForSong(prompt.prompt);
      toast.success("Prompt copied to Create Song! Scroll down to generate.");
    }
  };

  // Generate Drum Pattern from this style prompt
  const handleGenerateDrum = async (e) => {
    e?.stopPropagation();
    if (onGenerateDrumPattern) {
      setGeneratingDrum(true);
      try {
        await onGenerateDrumPattern(prompt.prompt);
        toast.success("🥁 Drum pattern generated from style!");
      } catch (err) {
        toast.error("Failed to generate drum pattern");
      }
      setGeneratingDrum(false);
    }
  };

  // Generate MIDI from this style prompt
  const handleGenerateMidi = async (e) => {
    e?.stopPropagation();
    if (onGenerateMidi) {
      setGeneratingMidi(true);
      try {
        await onGenerateMidi(prompt.prompt);
      } catch (err) {
        toast.error("Failed to generate MIDI");
      }
      setGeneratingMidi(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      transition={{ delay: Math.min(index * 0.05, 0.5) }} 
      className="prompt-card" 
      data-testid={`prompt-card-${index}`}
    >
      <div className="prompt-card-header">
        <span className="prompt-num">{String(index + 1).padStart(3, '0')}</span>
        <span className="prompt-platform" style={{ background: platform?.color + '20', color: platform?.color, border: `1px solid ${platform?.color}40` }}>
          {platform?.icon} {platform?.name}
        </span>
        <button className="dna-toggle-btn" onClick={() => setExpanded(!expanded)}>
          <Dna className="w-3 h-3" />{prompt.dna_code}
        </button>
        <button className="copy-btn" onClick={handleCopy} title="Copy prompt">
          {copied ? <Check className="w-4 h-4" style={{ color: '#9F47FF' }} /> : <Copy className="w-4 h-4" />}
        </button>
        <button className="open-btn" onClick={openInPlatform} title={"Open " + platform?.name}>
          🚀
        </button>
      </div>
      <p className="prompt-text" onClick={handleCopy}>{prompt.prompt}</p>
      
      {/* Auto Audio Preview */}
      {preview?.status === 'rendering' && (
        <div data-testid={`preview-rendering-${index}`} style={{ marginTop: '12px', padding: '10px 12px', background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.3)', borderRadius: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#00d4ff' }} />
            <span style={{ color: '#00d4ff', fontSize: '0.72rem', fontWeight: 700 }}>🎧 Rendering Audio... (~30-60s)</span>
          </div>
          <div style={{ height: '4px', borderRadius: '2px', background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: '40%', borderRadius: '2px', background: 'linear-gradient(90deg, #00d4ff, #a855f7)', animation: 'previewSlide 1.2s ease-in-out infinite' }} />
          </div>
        </div>
      )}
      {preview?.status === 'ready' && preview.url && (
        <div data-testid={`preview-player-${index}`} style={{ marginTop: '12px', padding: '10px 12px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '6px' }}>
          <div style={{ color: '#22c55e', fontSize: '0.68rem', fontWeight: 700, marginBottom: '6px' }}>🎧 10-SECOND AUDIO PREVIEW</div>
          <audio controls preload="metadata" src={preview.url} style={{ width: '100%', height: '34px' }} data-testid={`preview-audio-${index}`} />
        </div>
      )}
      {preview?.status === 'failed' && (
        <div style={{ marginTop: '12px', color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }} data-testid={`preview-failed-${index}`}>
          ⚠️ Preview render failed — <span onClick={(e) => { e.stopPropagation(); onRenderPreview && onRenderPreview(); }} style={{ color: '#00d4ff', cursor: 'pointer', textDecoration: 'underline' }}>retry</span>
        </div>
      )}
      {!preview && onRenderPreview && (
        <button onClick={(e) => { e.stopPropagation(); onRenderPreview(); }} data-testid={`render-preview-btn-${index}`}
          style={{ width: '100%', padding: '8px 16px', marginTop: '12px', background: 'rgba(0,212,255,0.1)', border: '1px dashed rgba(0,212,255,0.4)', borderRadius: '4px', color: '#00d4ff', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}>
          🎧 Render Audio Preview
        </button>
      )}
      
      {/* Copy Prompt Button - Big and Visible */}
      <button 
        onClick={handleCopy}
        style={{
          width: '100%',
          padding: '10px 16px',
          marginTop: '12px',
          background: copied ? '#9F47FF' : '#ffffff',
          border: 'none',
          borderRadius: '4px',
          color: copied ? '#fff' : '#000',
          fontSize: '0.85rem',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s',
          position: 'relative',
          zIndex: 10
        }}
        data-testid={`copy-prompt-btn-${index}`}
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        <span>{copied ? 'COPIED!' : 'COPY PROMPT'}</span>
      </button>
      
      {/* Use for Song Button */}
      <button 
        className="use-for-song-btn"
        onClick={handleUseForSong}
        style={{
          width: '100%',
          padding: '10px 16px',
          marginTop: '8px',
          background: '#E50914',
          border: 'none',
          borderRadius: '4px',
          color: '#fff',
          fontSize: '0.85rem',
          fontWeight: 600,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s',
          position: 'relative',
          zIndex: 10,
          boxShadow: '0 4px 15px rgba(229, 9, 20, 0.4)'
        }}
        data-testid={`use-for-song-btn-${index}`}
      >
        <Music2 className="w-4 h-4" />
        <span>Use for Song Creation</span>
      </button>
      
      {/* Generate Drum Pattern Button */}
      <button 
        onClick={handleGenerateDrum}
        disabled={generatingDrum}
        style={{
          width: '100%',
          padding: '10px 16px',
          marginTop: '8px',
          background: generatingDrum 
            ? 'rgba(139, 92, 246, 0.5)' 
            : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
          border: 'none',
          borderRadius: '4px',
          color: '#fff',
          fontSize: '0.85rem',
          fontWeight: 600,
          cursor: generatingDrum ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s',
          position: 'relative',
          zIndex: 10,
          boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)'
        }}
        data-testid={`generate-drum-btn-${index}`}
      >
        {generatingDrum ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Generating Pattern...</span>
          </>
        ) : (
          <>
            <Music className="w-4 h-4" />
            <span>🥁 Generate Drum Pattern</span>
          </>
        )}
      </button>
      
      {/* Download MIDI Button */}
      <button 
        onClick={handleGenerateMidi}
        disabled={generatingMidi}
        style={{
          width: '100%',
          padding: '10px 16px',
          marginTop: '8px',
          background: generatingMidi 
            ? 'rgba(16, 185, 129, 0.3)' 
            : 'linear-gradient(135deg, #10b981, #059669)',
          border: 'none',
          borderRadius: '4px',
          color: '#fff',
          fontSize: '0.85rem',
          fontWeight: 600,
          cursor: generatingMidi ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s',
          position: 'relative',
          zIndex: 10,
          boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)'
        }}
        data-testid={`download-midi-btn-${index}`}
      >
        {generatingMidi ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Generating MIDI...</span>
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            <span>⬇️ Download MIDI (32 bars)</span>
          </>
        )}
      </button>
      
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="dna-expand">
            {Object.entries(DNA_LEGEND).map(([k, v]) => (
              <DNABar key={k} label={k} value={prompt.dna[{G:'genre',M:'mood',I:'instruments',V:'vocals',P:'production',E:'energy',X:'experimental'}[k]]} color={v.color} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Subscription Management Modal
const SubscriptionManagementModal = ({ isOpen, onClose, currentUser, onPlanChange }) => {
  const [loading, setLoading] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  useEffect(() => {
    if (isOpen && currentUser) {
      fetchSubscriptionDetails();
    }
  }, [isOpen, currentUser]);

  const fetchSubscriptionDetails = async () => {
    try {
      const response = await axios.get(`${API}/subscription/details`, { withCredentials: true });
      setSubscriptionDetails(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      toast.error('Failed to load subscription details');
    }
  };

  const handleCancelSubscription = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/subscription/cancel`, {}, { withCredentials: true });
      toast.success(response.data.message);
      setCancelConfirm(false);
      fetchSubscriptionDetails();
      if (onPlanChange) onPlanChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  const tierColors = {
    free: '#6b7280',
    student: '#3b82f6',
    creator: '#8b5cf6',
    pro: '#f59e0b'
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div 
        className="modal-content" 
        onClick={e => e.stopPropagation()} 
        initial={{ scale: 0.9, opacity: 0 }} 
        animate={{ scale: 1, opacity: 1 }}
        style={{ maxWidth: '500px' }}
      >
        <button className="modal-close" onClick={onClose}><X /></button>
        
        <div className="modal-header" style={{ marginBottom: '24px' }}>
          <Settings className="w-8 h-8" style={{ color: '#9F47FF' }} />
          <h2>Manage Subscription</h2>
          <p>View and manage your MANNIS subscription</p>
        </div>

        {subscriptionDetails ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Current Plan Card */}
            <div style={{
              background: 'rgba(159, 71, 255, 0.1)',
              border: `2px solid ${tierColors[subscriptionDetails.subscription] || '#9F47FF'}`,
              borderRadius: '16px',
              padding: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>Current Plan</span>
                <span style={{
                  background: tierColors[subscriptionDetails.subscription] || '#9F47FF',
                  color: '#fff',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: 600
                }}>
                  {subscriptionDetails.tier_name}
                </span>
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>
                {subscriptionDetails.price === 0 ? 'FREE' : `$${subscriptionDetails.price}/mo`}
              </div>
              {subscriptionDetails.days_remaining !== null && subscriptionDetails.days_remaining >= 0 && (
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
                  {subscriptionDetails.days_remaining} days remaining
                </div>
              )}
            </div>

            {/* Usage Limits */}
            <div style={{ background: 'rgba(0,0,0,0.2)', borderRadius: '12px', padding: '16px' }}>
              <h4 style={{ color: '#fff', marginBottom: '12px', fontSize: '0.9rem' }}>Your Limits</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#9F47FF' }}>
                    {subscriptionDetails.limits.prompts_per_month === -1 ? '∞' : subscriptionDetails.limits.prompts_per_month}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>Prompts/mo</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#00d4ff' }}>
                    {subscriptionDetails.limits.beats_per_month}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>Beats/mo</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f59e0b' }}>
                    {subscriptionDetails.limits.vocals_per_month}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>Vocals/mo</div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
              {subscriptionDetails.subscription === 'free' ? (
                <button 
                  onClick={onClose}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #9F47FF, #6366f1)',
                    color: '#fff',
                    border: 'none',
                    padding: '14px 20px',
                    borderRadius: '12px',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Upgrade Plan
                </button>
              ) : (
                <>
                  <button 
                    onClick={onClose}
                    style={{
                      flex: 1,
                      background: 'linear-gradient(135deg, #9F47FF, #6366f1)',
                      color: '#fff',
                      border: 'none',
                      padding: '14px 20px',
                      borderRadius: '12px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Change Plan
                  </button>
                  {subscriptionDetails.can_cancel && !cancelConfirm && (
                    <button 
                      onClick={() => setCancelConfirm(true)}
                      style={{
                        background: 'transparent',
                        color: '#ef4444',
                        border: '1px solid #ef4444',
                        padding: '14px 20px',
                        borderRadius: '12px',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Cancel Confirmation */}
            {cancelConfirm && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '12px',
                padding: '16px'
              }}>
                <p style={{ color: '#fff', marginBottom: '12px', fontSize: '0.9rem' }}>
                  Are you sure? You'll lose access to premium features at the end of your billing period.
                </p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button 
                    onClick={handleCancelSubscription}
                    disabled={loading}
                    style={{
                      flex: 1,
                      background: '#ef4444',
                      color: '#fff',
                      border: 'none',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: loading ? 'wait' : 'pointer',
                      opacity: loading ? 0.7 : 1
                    }}
                  >
                    {loading ? 'Cancelling...' : 'Yes, Cancel'}
                  </button>
                  <button 
                    onClick={() => setCancelConfirm(false)}
                    style={{
                      flex: 1,
                      background: 'transparent',
                      color: '#fff',
                      border: '1px solid rgba(255,255,255,0.3)',
                      padding: '10px 16px',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Keep Subscription
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
            <Loader2 className="w-8 h-8 animate-spin" style={{ margin: '0 auto 12px' }} />
            Loading subscription details...
          </div>
        )}
      </motion.div>
    </div>
  );
};

// Pricing Modal
const PricingModal = ({ isOpen, onClose, onSelectPlan, currentPlan, userEmail, setUserEmail, currentUser, onLoginRequired }) => {
  // Use logged-in user's email if available
  const effectiveEmail = currentUser?.email || userEmail;
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [loading, setLoading] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountApplied, setDiscountApplied] = useState(null);
  const [discountError, setDiscountError] = useState("");
  const [paypalConfig, setPaypalConfig] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    axios.get(`${API}/paypal/config`)
      .then(res => setPaypalConfig(res.data))
      .catch(() => setPaypalConfig({ configured: false }));
  }, [isOpen]);

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError("Please enter a discount code");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/validate-discount`, {
        code: discountCode.trim().toUpperCase()
      });
      
      if (response.data.valid) {
        setDiscountApplied(response.data);
        setDiscountError("");
        toast.success(`✅ ${response.data.description} - $${response.data.discount_amount} OFF!`);
      } else {
        setDiscountError("Invalid discount code");
        setDiscountApplied(null);
      }
    } catch (error) {
      setDiscountError("Invalid discount code");
      setDiscountApplied(null);
    }
  };

  const handleCheckout = async (packageId) => {
    const checkoutEmail = currentUser?.email || userEmail;
    if (!checkoutEmail) {
      toast.error("Please sign in or enter your email to upgrade");
      if (onLoginRequired) onLoginRequired();
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        package_id: packageId,
        email: checkoutEmail,
        origin_url: window.location.origin,
        payment_method: paymentMethod,
        discount_code: discountApplied ? discountCode.trim().toUpperCase() : null
      });
      
      if (response.data.method === "stripe") {
        window.location.href = response.data.url;
      } else {
        if (!paypalConfig?.configured) {
          toast.error("PayPal is not configured yet - please pay by card instead");
        }
        setSelectedPackage({ ...PACKAGES[packageId], id: packageId, ...response.data });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Checkout failed");
    }
    setLoading(false);
  };

  const getDiscountedPrice = (originalPrice) => {
    if (discountApplied) {
      return Math.max(0, originalPrice - discountApplied.discount_amount);
    }
    return originalPrice;
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div className="modal-content" onClick={e => e.stopPropagation()} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
        <button className="modal-close" onClick={onClose}><X /></button>
        
        <div className="modal-header">
          <Crown className="w-8 h-8 text-yellow-400" />
          <h2>Upgrade to Pro</h2>
          <p>Unlock unlimited analyses & all platforms</p>
        </div>

        {currentUser ? (
          <div className="email-input-section" style={{ background: 'rgba(159, 71, 255, 0.1)', border: '1px solid rgba(159, 71, 255, 0.3)' }}>
            <Mail className="w-4 h-4" style={{ color: '#9F47FF' }} />
            <span style={{ color: '#fff', fontSize: '0.9rem' }}>{currentUser.email}</span>
            <span style={{ color: '#9F47FF', fontSize: '0.75rem', marginLeft: 'auto' }}>Logged in</span>
          </div>
        ) : (
          <div className="email-input-section">
            <Mail className="w-4 h-4" />
            <input 
              type="email" 
              placeholder="Enter your email or sign in first" 
              value={userEmail} 
              onChange={e => setUserEmail(e.target.value)}
              className="email-input"
            />
          </div>
        )}

        <div className="payment-methods">
          <button className={`payment-method-btn ${paymentMethod === 'stripe' ? 'active' : ''}`} onClick={() => setPaymentMethod('stripe')}>
            <CreditCard className="w-4 h-4" /> Card (Stripe)
          </button>
          <button className={`payment-method-btn ${paymentMethod === 'paypal' ? 'active' : ''}`} onClick={() => setPaymentMethod('paypal')}>
            💳 PayPal
          </button>
        </div>

        <div className="packages-grid">
          {Object.entries(PACKAGES).map(([key, pkg]) => (
            <div key={key} className={`package-card ${key === 'pro' ? 'featured' : ''} ${key === 'free' ? 'free-tier' : ''}`}>
              {pkg.badge && <span className="package-badge" style={key === 'free' ? { background: '#9F47FF' } : {}}>{pkg.badge}</span>}
              <h3>{pkg.name}</h3>
              <div className="package-price">
                {pkg.price === 0 ? (
                  <span style={{ color: '#9F47FF', fontWeight: 700 }}>FREE</span>
                ) : discountApplied && getDiscountedPrice(pkg.price) !== pkg.price ? (
                  <>
                    <span style={{ textDecoration: 'line-through', color: 'rgba(255,255,255,0.4)', fontSize: '1rem', marginRight: '8px' }}>${pkg.price}</span>
                    <span style={{ color: '#9F47FF' }}>${getDiscountedPrice(pkg.price)}</span>
                  </>
                ) : (
                  <>${pkg.price}</>
                )}
                {pkg.price > 0 && <span className="price-period">{key === 'trial' ? ' / 5 days' : '/mo'}</span>}
              </div>
              {pkg.subtitle && <p style={{ color: '#9F47FF', fontSize: '0.8rem', fontWeight: 700, marginBottom: '8px' }}>{pkg.subtitle}</p>}
              {pkg.description && <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', marginBottom: '12px' }}>{pkg.description}</p>}
              <ul className="package-features">
                <li><CheckCircle className="w-4 h-4" /> <strong>{pkg.generations} Create generates{key === 'trial' ? ' in 5 days' : ' / month'}</strong></li>
                <li><CheckCircle className="w-4 h-4" /> Genre A &amp; B on Create</li>
                <li><CheckCircle className="w-4 h-4" /> You pay Suno / Lyria on your keys</li>
                <li><CheckCircle className="w-4 h-4" /> Universal platforms · you keep royalties</li>
              </ul>
              {key === 'free' ? (
                <button className="package-btn" style={{ background: '#9F47FF' }} onClick={() => { onClose(); toast.success('You have free access! Sign in to start.'); }}>
                  Start Free
                </button>
              ) : (
                <button className="package-btn" onClick={() => handleCheckout(key)} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : key === 'trial' ? 'Start trial' : key === 'student' ? 'Get Starter' : key === 'creator' ? 'Get Creator Pro' : 'Get Pro'}
                </button>
              )}
            </div>
          ))}
        </div>

        {paymentMethod === 'paypal' && selectedPackage && paypalConfig?.configured && (
          <div style={{ margin: '18px auto 0', maxWidth: '420px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(0, 212, 255, 0.25)', borderRadius: '14px', padding: '18px' }} data-testid="paypal-checkout-panel">
            <div style={{ textAlign: 'center', color: '#fff', fontWeight: 700, marginBottom: '4px' }}>
              Pay with PayPal — {selectedPackage.name || selectedPackage.id} (${selectedPackage.amount ?? getDiscountedPrice(selectedPackage.price)}/mo)
            </div>
            <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', marginBottom: '14px' }}>
              Complete your payment below to activate instantly
            </div>
            <PayPalScriptProvider options={{ clientId: paypalConfig.client_id, currency: 'USD' }}>
              <PayPalButtons
                style={{ layout: 'vertical', color: 'gold', shape: 'pill', label: 'pay' }}
                createOrder={async () => {
                  const res = await axios.post(`${API}/paypal/create-order`, {
                    package_id: selectedPackage.id,
                    email: effectiveEmail,
                    discount_code: discountApplied ? discountCode.trim().toUpperCase() : null
                  });
                  return res.data.order_id;
                }}
                onApprove={async (data) => {
                  try {
                    const res = await axios.post(`${API}/paypal/capture`, { order_id: data.orderID });
                    if (res.data.success) {
                      toast.success('🎉 Payment complete! Your plan is now active.');
                      setSelectedPackage(null);
                      onClose();
                      setTimeout(() => window.location.reload(), 1200);
                    }
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'PayPal payment could not be verified');
                  }
                }}
                onError={() => toast.error('PayPal error - please try again or pay by card')}
                onCancel={() => toast('PayPal payment cancelled')}
              />
            </PayPalScriptProvider>
            <button onClick={() => setSelectedPackage(null)} style={{ display: 'block', margin: '8px auto 0', background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', cursor: 'pointer' }} data-testid="paypal-cancel-btn">
              Cancel
            </button>
          </div>
        )}
        <p style={{ textAlign: 'center', color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', marginTop: '14px' }} data-testid="pricing-token-note">
          ⚡ Subscriptions include monthly tokens (1 token = 1 beat or vocal generation). Extra tokens can be purchased any time — <strong style={{ color: '#f59e0b' }}>$10 per 100 tokens</strong>.
        </p>
      </motion.div>
    </div>
  );
};

// Main App
function App() {
  const [promptCount, setPromptCount] = useState(60);
  const [selectedPlatforms, setSelectedPlatforms] = useState(["all"]);
  const [styleMode, setStyleMode] = useState("clone");
  const [showStyleDropdown, setShowStyleDropdown] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState("");
  const [result, setResult] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [showLegend, setShowLegend] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showSubscriptionManagement, setShowSubscriptionManagement] = useState(false);
  const [showPromptsPopup, setShowPromptsPopup] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [subscription, setSubscription] = useState({ plan: "free", analyses_used: 0, analyses_limit: 20, generations_used: 0, generations_limit: 5, generations_remaining: 5 });
  const [lowCreditDismissed, setLowCreditDismissed] = useState(false);
  const lowCreditWarnedAtRef = useRef(null);
  const CREATE_LOW_THRESHOLD = 5; // warn in advance when this many or fewer Create generates remain

  // Compute remaining "Create generates" (monthly quota left + purchased extra tokens)
  const getCreateGeneratesLeft = (user) => {
    const lim = user?.subscription_limits || {};
    const usage = user?.usage || {};
    const unlimited = lim.beats_per_month === -1 || lim.vocals_per_month === -1;
    if (unlimited) return { left: Infinity, unlimited: true };
    const monthly = (lim.beats_per_month || 0) + (lim.vocals_per_month || 0);
    const used = (usage.beats_used || 0) + (usage.vocals_used || 0);
    const remaining = Math.max(0, monthly - used);
    const extra = user?.token_balance || 0;
    return { left: remaining + extra, remaining, extra, unlimited: false };
  };

  
  // ============ AUTHENTICATION STATE ============
  const [currentUser, setCurrentUser] = useState(null);

  // Advance low-balance warning: proactively nudge subscribers to buy more Create generates
  // BEFORE they fully run out (so renders are never interrupted mid-session).
  useEffect(() => {
    if (!currentUser) { lowCreditWarnedAtRef.current = null; return; }
    const { left, unlimited } = getCreateGeneratesLeft(currentUser);
    if (unlimited) return;
    if (left > CREATE_LOW_THRESHOLD) {
      // Replenished above threshold — reset so a future dip warns again
      lowCreditWarnedAtRef.current = null;
      if (lowCreditDismissed) setLowCreditDismissed(false);
      return;
    }
    // Only toast once per distinct remaining level to avoid spam
    if (lowCreditWarnedAtRef.current === left) return;
    lowCreditWarnedAtRef.current = left;
    if (left <= 0) {
      toast.error("You're out of Create generates. Buy more or upgrade to keep making tracks.", { duration: 8000 });
    } else {
      toast(`⚠️ Only ${left} Create generate${left === 1 ? '' : 's'} left — buy more now so your Suno / Lyria renders don't get interrupted.`, { duration: 8000, icon: '⚡' });
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [cpCurrent, setCpCurrent] = useState('');
  const [cpNew, setCpNew] = useState('');
  const [cpConfirm, setCpConfirm] = useState('');
  const [cpLoading, setCpLoading] = useState(false);
  const [cpError, setCpError] = useState('');
  const [showTokenExhausted, setShowTokenExhausted] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [fpEmail, setFpEmail] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpSent, setFpSent] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [rpNew, setRpNew] = useState('');
  const [rpConfirm, setRpConfirm] = useState('');
  const [rpLoading, setRpLoading] = useState(false);
  const [rpError, setRpError] = useState('');
  const [showAutoWorkflow, setShowAutoWorkflow] = useState(false);
  const [awUrl, setAwUrl] = useState('');
  const [awRunning, setAwRunning] = useState(false);
  const [awTask, setAwTask] = useState(null);
  const awPollRef = useRef(null);
  const [lyricsAssistLoading, setLyricsAssistLoading] = useState(false);
  const [siteGateChecked, setSiteGateChecked] = useState(false);
  const [siteGateOk, setSiteGateOk] = useState(false);
  const [gatePassword, setGatePassword] = useState('');
  const [showGatePassword, setShowGatePassword] = useState(false);
  const [gateError, setGateError] = useState('');
  const [gateLoading, setGateLoading] = useState(false);
  const [showTokenHistory, setShowTokenHistory] = useState(false);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [tokenHistoryData, setTokenHistoryData] = useState(null);
  const [thLoading, setThLoading] = useState(false);

  const openTokenHistory = async () => {
    if (!currentUser) { handleBuyTokens(); return; }
    setShowTokenHistory(true);
    setThLoading(true);
    try {
      const token = localStorage.getItem('sounddna_token');
      const res = await axios.get(`${API}/tokens/history`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setTokenHistoryData(res.data);
    } catch (err) {
      toast.error('Could not load token history');
      setShowTokenHistory(false);
    } finally {
      setThLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('sounddna_gate_token') || '';
    axios.get(`${API}/site-gate/status`, { params: { token } })
      .then(res => {
        setSiteGateOk(!res.data.gate_enabled || res.data.valid);
        setSiteGateChecked(true);
      })
      .catch(() => { setSiteGateOk(true); setSiteGateChecked(true); });
  }, []);

  const handleSiteGateSubmit = async (e) => {
    e?.preventDefault();
    if (!gatePassword) return;
    setGateLoading(true);
    setGateError('');
    try {
      const res = await axios.post(`${API}/site-gate/verify`, { password: gatePassword.trim() });
      if (res.data.gate_token) localStorage.setItem('sounddna_gate_token', res.data.gate_token);
      setSiteGateOk(true);
    } catch (err) {
      setGateError(err.response?.status === 401 ? 'Incorrect access password' : 'Could not verify - try again');
    } finally {
      setGateLoading(false);
    }
  };
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerName, setRegisterName] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationHint, setVerificationHint] = useState('');
  
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [voiceAnalysis, setVoiceAnalysis] = useState(null);
  const [showVoiceMode, setShowVoiceMode] = useState(false);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState(null);
  const [recordedAudioBlob, setRecordedAudioBlob] = useState(null);
  const [selectedGenre, setSelectedGenre] = useState("auto");
  const [selectedArtist, setSelectedArtist] = useState("none");
  const [artistName, setArtistName] = useState("");
  const [styleDescription, setStyleDescription] = useState("");
  const [enable2026Sounds, setEnable2026Sounds] = useState(true);
  const [showGenreGuide, setShowGenreGuide] = useState(false);
  const [presetDetail, setPresetDetail] = useState(null);
  const [dnaStemsLoading, setDnaStemsLoading] = useState(null);
  const previewDnaMixdown = async (dna, key) => {
    if (audioPreviewPlaying === key) { stopAudioPreview(); return; }
    if (audioPreviewLoading) return;
    setAudioPreviewLoading(key);
    try {
      toast.info('🎵 Generating preview... (~2 min)');
      const startRes = await axios.post(`${API}/generate-beat`, {
        prompt: dna.style_prompt || dna.prompt || dna.name,
        duration: 30,
        email: currentUser?.email || userEmail || getGuestEmail(),
        enhance: enhancePrompt
      });
      const taskId = startRes.data.task_id;
      let audioUrl = null;
      for (let attempt = 0; attempt < 100; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const statusRes = await axios.get(`${API}/beat-status/${taskId}`, { timeout: 15000 });
        const s = statusRes.data;
        if (s.status === 'completed' && s.audio_url) {
          audioUrl = s.audio_url.startsWith('/api') ? `${API_ORIGIN}${s.audio_url}` : s.audio_url;
          break;
        }
        if (s.status === 'failed') throw new Error(s.error || 'Preview generation failed.');
      }
      if (!audioUrl) throw new Error('Preview generation timed out. Try again.');
      await playAudioPreview(audioUrl, key);
    } catch (err) {
      toast.error(err.message || 'Preview failed. Try again.');
      setAudioPreviewLoading(null);
    }
  };

  const downloadDnaStems = async (dna, key) => {
    if (dnaStemsLoading) return;
    setDnaStemsLoading(key);
    try {
      toast.info('🎵 Generating song... (~2 min)');
      const startRes = await axios.post(`${API}/generate-beat`, {
        prompt: dna.style_prompt || dna.prompt || dna.name,
        duration: 30,
        email: currentUser?.email || userEmail || getGuestEmail(),
        enhance: enhancePrompt
      });
      const taskId = startRes.data.task_id;
      let audioUrl = null;
      for (let attempt = 0; attempt < 100; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        const statusRes = await axios.get(`${API}/beat-status/${taskId}`, { timeout: 15000 });
        const s = statusRes.data;
        if (s.status === 'completed' && s.audio_url) {
          audioUrl = s.audio_url.startsWith('/api') ? `${API_ORIGIN}${s.audio_url}` : s.audio_url;
          break;
        }
        if (s.status === 'failed') throw new Error(s.error || 'Song generation failed.');
      }
      if (!audioUrl) throw new Error('Generation timed out. Try again.');
      await downloadSong(audioUrl, `${(dna.name || 'preset').replace(/[^a-z0-9]+/gi, '_')}.wav`, 'wav');
    } catch (err) {
      toast.error(err.message && err.message !== 'Network Error' ? err.message : 'Song generation failed. Try again.');
    } finally {
      setDnaStemsLoading(null);
    }
  };
  const [presetFavorites, setPresetFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sounddna_preset_favs') || '[]'); } catch { return []; }
  });
  useEffect(() => {
    localStorage.setItem('sounddna_preset_favs', JSON.stringify(presetFavorites));
  }, [presetFavorites]);
  const isPresetFavorite = (id) => presetFavorites.some(f => f.id === id);
  const togglePresetFavorite = (fav) => {
    setPresetFavorites(prev => prev.some(f => f.id === fav.id)
      ? prev.filter(f => f.id !== fav.id)
      : [...prev, fav]);
  };
  const [showDashboard, setShowDashboard] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [generatedSongs, setGeneratedSongs] = useState([]); // list of generated songs
  const [showSongModal, setShowSongModal] = useState(false);
  const [currentSong, setCurrentSong] = useState(null);
  const [deliveryEmail, setDeliveryEmail] = useState(""); // email to send files to
  const [deliveryMethod, setDeliveryMethod] = useState("desktop"); // 'desktop' or 'email'
  
  // SUNO LAYOUT - Active View State
  const [activeView, setActiveView] = useState("create"); // 'create', 'library', 'voices', 'marketplace', 'analyzer', 'rhyme'
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileNavOpen]);
  const [selectedTool, setSelectedTool] = useState("sounddna"); // Track which sidebar tool is selected
  
  // Onboarding Tutorial State
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem('sounddna_onboarding_complete');
  });
  const [onboardingStep, setOnboardingStep] = useState(0);
  
  // AI Lyrics Generator State
  const [lyricsTheme, setLyricsTheme] = useState("");
  const [lyricsGenre, setLyricsGenre] = useState("pop");
  const [lyricsMood, setLyricsMood] = useState("emotional");
  const [lyricsLanguage, setLyricsLanguage] = useState("English"); // Multi-language support for DNA Lyrics
  const [lyricsModel, setLyricsModel] = useState(() => localStorage.getItem('sounddna_lyrics_model') || 'gpt');
  const [generatedLyrics, setGeneratedLyrics] = useState("");
  const [isGeneratingLyrics, setIsGeneratingLyrics] = useState(false);
  const [isTranslatingLyrics, setIsTranslatingLyrics] = useState(false); // Translation state for DNA Lyrics
  
  // Rhyme/Flow Engine State
  const [rhymeTopic, setRhymeTopic] = useState("");
  const [rhymeStyle, setRhymeStyle] = useState("modern trap");
  const [rhymeScheme, setRhymeScheme] = useState("AABB");
  const [flowPattern, setFlowPattern] = useState("standard");
  const [sectionType, setSectionType] = useState("verse");
  const [barsCount, setBarsCount] = useState(16);
  const [rhymeLanguage, setRhymeLanguage] = useState("English"); // Multi-language support
  const [rhymeDuration, setRhymeDuration] = useState(3); // Target duration in minutes (2-5)
  const [isTranslatingRhyme, setIsTranslatingRhyme] = useState(false); // Translation state for rhyme engine
  const [includeSyllables, setIncludeSyllables] = useState(true);
  const [generatedBars, setGeneratedBars] = useState(null);
  const [isGeneratingBars, setIsGeneratingBars] = useState(false);
  const [rhymeEngineTab, setRhymeEngineTab] = useState("generate"); // "generate" or "custom"
  const [customLyrics, setCustomLyrics] = useState(""); // User's own lyrics
  const [songTitle, setSongTitle] = useState(""); // Song title for both modes
  const [isCreatingSongPrompt, setIsCreatingSongPrompt] = useState(false); // Loading state for Create Song Prompt
  const [formattedSong, setFormattedSong] = useState(null);
  const [isFormattingSong, setIsFormattingSong] = useState(false);
  const [formattedLyrics, setFormattedLyrics] = useState(null);
  const [isFormattingLyrics, setIsFormattingLyrics] = useState(false);
  
  // DRUM MACHINE State
  const [drumGenre, setDrumGenre] = useState("trap");
  const [drumTempo, setDrumTempo] = useState(140);
  const [drumPatternType, setDrumPatternType] = useState("full");
  const [drumBars, setDrumBars] = useState(8);
  const [drumSwing, setDrumSwing] = useState("none");
  const [drumKit, setDrumKit] = useState("808");
  const [drumIncludeVariations, setDrumIncludeVariations] = useState(true);
  const [generatedDrumPattern, setGeneratedDrumPattern] = useState(null);
  const [isGeneratingDrum, setIsGeneratingDrum] = useState(false);
  const [drumAudioUrl, setDrumAudioUrl] = useState(null);
  const drumAudioRenderedFor = useRef(null); // suno_prompt of the pattern that produced drumAudioUrl
  const [drumWavReady, setDrumWavReady] = useState(null); // { url, filename } — WAV rendered, awaiting user tap to save
  const [drumAudioSunoPrompt, setDrumAudioSunoPrompt] = useState(null); // Matching Suno prompt for the WAV
  const [isGeneratingDrumAudio, setIsGeneratingDrumAudio] = useState(false);
  const [isDrumPlaying, setIsDrumPlaying] = useState(false);
  const drumAudioRef = useRef(null);
  const drumAudioRefFullscreen = useRef(null); // Separate ref for fullscreen drum audio
  
  // ADVANCED DRUM MACHINE - Quantum Rhythmic Design
  const [advancedDrumMode, setAdvancedDrumMode] = useState(false);
  const [drumKey, setDrumKey] = useState("C");
  const [drumMode, setDrumMode] = useState("minor");
  const [drumEnergy, setDrumEnergy] = useState("high");
  const [drumComplexity, setDrumComplexity] = useState("professional");
  const [drumSections, setDrumSections] = useState(["intro", "verse", "pre_chorus", "chorus", "bridge", "outro"]);
  const [drumBarsPerSection, setDrumBarsPerSection] = useState(8);
  const [drumIncludeInversions, setDrumIncludeInversions] = useState(true);
  const [drumIncludeFills, setDrumIncludeFills] = useState(true);
  const [advancedDrumResult, setAdvancedDrumResult] = useState(null);
  
  // 40K STYLE PROMPTS FOR DRUM MACHINE
  const [drumStylePrompt, setDrumStylePrompt] = useState("");
  const [showDrumStyleBrowser, setShowDrumStyleBrowser] = useState(false);
  const [drumStyleCategories, setDrumStyleCategories] = useState([]);
  const [selectedDrumCategory, setSelectedDrumCategory] = useState("");
  const [drumCategoryPrompts, setDrumCategoryPrompts] = useState([]);
  
  // 70K Style Prompts PAGINATION
  const [drumPromptPage, setDrumPromptPage] = useState(1);
  const [drumPromptTotalPages, setDrumPromptTotalPages] = useState(1);
  const [drumPromptTotal, setDrumPromptTotal] = useState(0);
  const DRUM_PROMPTS_PER_PAGE = 50;
  
  // MO3 SONG STRUCTURE STATE
  const [songStructure, setSongStructure] = useState(null);
  const [songLengthMinutes, setSongLengthMinutes] = useState(3);
  const [showSongStructure, setShowSongStructure] = useState(true);
  const [mo3Library, setMo3Library] = useState([]);
  const [showMo3Library, setShowMo3Library] = useState(false);
  const [mo3LastMidi, setMo3LastMidi] = useState(null);
  
  // DRUM MACHINE - Editable Prompt Control
  const [editableDrumPrompt, setEditableDrumPrompt] = useState("");
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  
  // DRUM MACHINE LIBRARY - Save & manage patterns
  const [drumLibrary, setDrumLibrary] = useState([]);
  const [showDrumLibrary, setShowDrumLibrary] = useState(false);
  const [libraryAudioPreviewEnabled, setLibraryAudioPreviewEnabled] = useState(true);
  
  // SUNO AI MUSIC GENERATOR STATE
  const [songCreationTab, setSongCreationTab] = useState('song'); // Combined Song Creation box: 'song' | 'vocals'
  const [sunoPrompt, setSunoPrompt] = useState("");
  const [sunoStyle, setSunoStyle] = useState("");
  const [sunoTitle, setSunoTitle] = useState("");
  const [sunoKey, setSunoKey] = useState("C major");  // Musical key for Suno generation
  const [sunoInstrumental, setSunoInstrumental] = useState(false);
  const [sunoDuration, setSunoDuration] = useState(() => {
    try {
      const saved = localStorage.getItem('mannis_default_song_length');
      return saved === null || saved === 'auto' ? null : parseInt(saved, 10);
    } catch { return null; }
  });
  const [isGeneratingSuno, setIsGeneratingSuno] = useState(false);
  const [sunoTaskId, setSunoTaskId] = useState(null);
  const [sunoStatus, setSunoStatus] = useState(null);
  const [sunoTracks, setSunoTracks] = useState([]);
  const [currentSunoTrack, setCurrentSunoTrack] = useState(null);
  const [sunoResultTracks, setSunoResultTracks] = useState([]); // both generated versions (A & B)
  // ── Unified Song Creation flow (Genre+Mood+Instruments+Voice+Style → one Suno call)
  const [selectedMood, setSelectedMood] = useState("");
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [selectedVoiceId, setSelectedVoiceId] = useState("");
  const [voicePreviewId, setVoicePreviewId] = useState(null);
  const [showVoicePicker, setShowVoicePicker] = useState(false);
  const [songLyrics, setSongLyrics] = useState("");
  const [songLyricsLoading, setSongLyricsLoading] = useState(false);
  const voicePreviewAudioRef = useRef(null);
  const [isSunoPlaying, setIsSunoPlaying] = useState(false);
  const sunoAudioRef = useRef(null);
  const [sunoReferenceAudio, setSunoReferenceAudio] = useState(null);  // Reference audio file
  const [sunoShowActions, setSunoShowActions] = useState(false);  // Dropdown toggle
  const sunoFileInputRef = useRef(null);  // File input ref
  const [sunoCelebrityWarning, setSunoCelebrityWarning] = useState(null);  // Celebrity name warning
  
  // MY UPLOADS STATE
  const [myUploads, setMyUploads] = useState([]);
  const [uploadCategory, setUploadCategory] = useState('all');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [storageStatus, setStorageStatus] = useState({ used_mb: 0, max_mb: 1024, percent_used: 0 });
  const [newStylePrompt, setNewStylePrompt] = useState({ title: '', prompt: '', description: '', tags: '' });
  const uploadFileInputRef = useRef(null);
  const uploadMidiInputRef = useRef(null);
  
  // List of celebrity/artist names that Suno API rejects
  const BLOCKED_ARTIST_NAMES = [
    'drake', 'kanye', 'taylor swift', 'beyonce', 'rihanna', 'eminem', 'jay-z', 'jayz',
    'metro boomin', 'travis scott', 'post malone', 'the weeknd', 'weeknd', 'dua lipa',
    'ariana grande', 'billie eilish', 'ed sheeran', 'bruno mars', 'lady gaga', 'adele',
    'justin bieber', 'selena gomez', 'doja cat', 'cardi b', 'megan thee stallion',
    'nicki minaj', 'lil wayne', 'future', 'young thug', 'gunna', 'lil baby', 'dababy',
    'roddy ricch', 'pop smoke', 'juice wrld', 'xxxtentacion', 'lil uzi vert', 'playboi carti',
    'tyler the creator', 'kendrick lamar', 'j cole', 'nas', 'tupac', '2pac', 'biggie',
    'notorious big', 'snoop dogg', 'dr dre', '50 cent', 'ice cube', 'wu tang',
    'michael jackson', 'prince', 'elvis', 'beatles', 'queen', 'led zeppelin', 'pink floyd',
    'nirvana', 'metallica', 'ac dc', 'acdc', 'guns n roses', 'bon jovi', 'aerosmith',
    'coldplay', 'u2', 'radiohead', 'oasis', 'arctic monkeys', 'the 1975', 'imagine dragons',
    'maroon 5', 'onerepublic', 'fall out boy', 'panic at the disco', 'twenty one pilots',
    'bts', 'blackpink', 'twice', 'exo', 'nct', 'stray kids', 'seventeen',
    'bad bunny', 'j balvin', 'daddy yankee', 'ozuna', 'maluma', 'anuel',
    'sza', 'frank ocean', 'the chainsmokers', 'marshmello', 'skrillex', 'deadmau5',
    'david guetta', 'calvin harris', 'tiesto', 'avicii', 'martin garrix', 'zedd',
    'pharrell', 'timbaland', 'max martin', 'rick rubin', 'mike dean'
  ];
  
  // Check for celebrity names in prompt
  const checkForCelebrityNames = (text) => {
    const lowerText = text.toLowerCase();
    for (const artist of BLOCKED_ARTIST_NAMES) {
      if (lowerText.includes(artist)) {
        return artist;
      }
    }
    return null;
  };
  
  // SONG CREATOR - Combined Rhyme & Drum Mode
  const [songCreatorMode, setSongCreatorMode] = useState(false);
  const [syncDrumToRhyme, setSyncDrumToRhyme] = useState(true); // Auto-sync settings
  
  // 1-CLICK SONG GENERATION STATE
  const [isGeneratingSong, setIsGeneratingSong] = useState(false);
  const [generatedSongUrl, setGeneratedSongUrl] = useState(null);
  const [songGenerationProgress, setSongGenerationProgress] = useState(0);
  const [songTaskId, setSongTaskId] = useState(null);
  const [isSongPlaying, setIsSongPlaying] = useState(false);
  const songAudioRef = useRef(null);
  const [songSafety, setSongSafety] = useState(null);
  const [showCommercialTip, setShowCommercialTip] = useState(false);
  const [showCopyrightInsights, setShowCopyrightInsights] = useState(false);
  
  // HIT SONG FORMULA ANALYZER STATE
  const [hitSongInput, setHitSongInput] = useState("");
  const [hitSongAnalysis, setHitSongAnalysis] = useState(null);
  const [isAnalyzingHitSong, setIsAnalyzingHitSong] = useState(false);
  const [showHitSongAnalyzer, setShowHitSongAnalyzer] = useState(false);
  
  // FULLSCREEN WORKFLOW VIEW - Shows individual workflow section
  const [showFullscreenWorkflow, setShowFullscreenWorkflow] = useState(false);
  const [fullscreenWorkflowSection, setFullscreenWorkflowSection] = useState(null); // 'style', 'bpm', 'key', 'mode', 'genre', 'mood', 'voice', 'lyrics'
  
  // FULLSCREEN TOOL VIEW - Shows individual tool in isolation
  const [fullscreenTool, setFullscreenTool] = useState(null); // 'rhyme', 'lyrics', 'voice', 'analyzer', 'stems', etc.
  
  // STYLE PROMPT MINI - Compact style generator widget
  const [showStyleMini, setShowStyleMini] = useState(false);
  const [styleMiniTab, setStyleMiniTab] = useState('generate'); // 'generate', 'audio', 'presets'
  const [isGeneratingStyle, setIsGeneratingStyle] = useState(false);
  const [styleMiniAudio, setStyleMiniAudio] = useState(null);
  const [isAnalyzingAudio, setIsAnalyzingAudio] = useState(false);
  const styleMiniAudioRef = useRef(null);
  
  // Style Prompt Mini - Quick Presets
  const STYLE_PRESETS = [
    { name: '🔥 Trap Banger', style: 'Hard trap, 808 bass, aggressive hi-hats, dark melodies, 140 BPM' },
    { name: '🌊 Chill Lo-Fi', style: 'Lo-fi hip hop, jazzy samples, vinyl crackle, mellow vibes, 85 BPM' },
    { name: '🎬 Epic Cinematic', style: 'Orchestral, dramatic strings, powerful brass, epic percussion, cinematic build' },
    { name: '💃 Dance Pop', style: 'Upbeat pop, catchy synths, four-on-the-floor beat, bright and energetic, 128 BPM' },
    { name: '🎸 Rock Anthem', style: 'Arena rock, powerful guitars, driving drums, anthemic chorus, 120 BPM' },
    { name: '🌙 R&B Slow Jam', style: 'Smooth R&B, silky vocals, sensual groove, 70 BPM, romantic atmosphere' },
    { name: '🎹 Future Bass', style: 'Future bass, lush synths, emotional drops, sidechained chords, 150 BPM' },
    { name: '🏝️ Reggaeton', style: 'Reggaeton, dembow rhythm, Latin percussion, catchy hook, 95 BPM' },
    { name: '🌃 Dark Synthwave', style: 'Synthwave, retro 80s synths, dark atmosphere, driving arpeggios, 118 BPM' },
    { name: '🎺 Jazz Fusion', style: 'Jazz fusion, complex harmonies, improvisation, funky bass, sophisticated groove' },
    { name: '🔊 Dubstep Drop', style: 'Dubstep, heavy wobble bass, aggressive drops, half-time drums, 140 BPM' },
    { name: '🌅 Acoustic Folk', style: 'Acoustic folk, fingerpicked guitar, warm vocals, storytelling, intimate feel' },
  ];
  
  // Style Prompt Mini - Random Generation Data
  const STYLE_COMPONENTS = {
    genres: ['Trap', 'Hip Hop', 'Pop', 'R&B', 'Rock', 'Electronic', 'Jazz', 'Lo-Fi', 'Drill', 'House', 'Dubstep', 'Reggaeton', 'Country', 'Soul', 'Funk', 'Metal', 'Indie', 'Afrobeats'],
    moods: ['Dark', 'Uplifting', 'Melancholic', 'Aggressive', 'Chill', 'Energetic', 'Romantic', 'Mysterious', 'Euphoric', 'Nostalgic', 'Powerful', 'Dreamy'],
    tempos: ['60 BPM (Slow)', '80 BPM (Moderate)', '100 BPM (Medium)', '120 BPM (Upbeat)', '140 BPM (Fast)', '160 BPM (High Energy)'],
    elements: ['heavy 808s', 'crisp hi-hats', 'melodic synths', 'acoustic guitar', 'piano chords', 'orchestral strings', 'vintage samples', 'punchy drums', 'deep bass', 'bright pads', 'vocal chops', 'guitar riffs'],
    vibes: ['club-ready', 'radio-friendly', 'underground', 'mainstream appeal', 'experimental', 'classic feel', 'modern production', 'raw and gritty', 'polished and clean']
  };
  
  // Generate random style prompt
  const generateRandomStyle = () => {
    setIsGeneratingStyle(true);
    const genre = STYLE_COMPONENTS.genres[Math.floor(Math.random() * STYLE_COMPONENTS.genres.length)];
    const mood = STYLE_COMPONENTS.moods[Math.floor(Math.random() * STYLE_COMPONENTS.moods.length)];
    const tempo = STYLE_COMPONENTS.tempos[Math.floor(Math.random() * STYLE_COMPONENTS.tempos.length)];
    const element1 = STYLE_COMPONENTS.elements[Math.floor(Math.random() * STYLE_COMPONENTS.elements.length)];
    const element2 = STYLE_COMPONENTS.elements[Math.floor(Math.random() * STYLE_COMPONENTS.elements.length)];
    const vibe = STYLE_COMPONENTS.vibes[Math.floor(Math.random() * STYLE_COMPONENTS.vibes.length)];
    
    const prompt = `${mood} ${genre}, ${element1}, ${element2}, ${vibe}, ${tempo}`;
    
    setTimeout(() => {
      setBeatPrompt(prompt);
      setSunoStyle(prompt);
      setIsGeneratingStyle(false);
      toast.success('🎲 Random style generated!');
    }, 500);
  };
  
  // Analyze uploaded audio to generate style prompt
  const analyzeAudioForStyle = async (audioFile) => {
    if (!audioFile) {
      toast.error('Please select an audio file');
      return;
    }
    
    setIsAnalyzingAudio(true);
    toast.info('🎵 Analyzing audio...');
    
    try {
      // Create form data with the audio file
      const formData = new FormData();
      formData.append('audio', audioFile);
      
      const response = await runStyleMiniJob(formData);
      
      if (response.data.success && response.data.style_prompt) {
        setBeatPrompt(response.data.style_prompt);
        setSunoStyle(response.data.style_prompt);
        toast.success('🎧 Style extracted from audio!');
        setStyleMiniAudio(null); // Clear the uploaded file
      } else {
        throw new Error(response.data.error || 'Failed to analyze audio');
      }
    } catch (error) {
      console.error('Audio analysis error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze audio. Try a different file.');
    } finally {
      setIsAnalyzingAudio(false);
    }
  };
  
  // Apply preset style
  const applyStylePreset = (preset) => {
    setBeatPrompt(preset.style);
    setSunoStyle(preset.style);
    toast.success(`✨ Applied: ${preset.name}`);
  };
  
  // UNIFIED WORKFLOW STATE - Connects all tools
  const [dnaFusionResult, setDnaFusionResult] = useState(null);
  const [dnaFusionLoading, setDnaFusionLoading] = useState(false);
  const [fusionExporting, setFusionExporting] = useState(null);
  const [dnaBlends, setDnaBlends] = useState([]);
  const [showBlendMenu, setShowBlendMenu] = useState(false);
  const [fusionGenres, setFusionGenres] = useState([]);
  const [fusionGenreA, setFusionGenreA] = useState('');
  const [fusionGenreB, setFusionGenreB] = useState('');
  const [fusionSong, setFusionSong] = useState(null); // {status, url, progress}
  const [enhancePrompt, setEnhancePrompt] = useState(true); // Autonomous Prompt Engine auto-enhance toggle
  const [fusionLibrary, setFusionLibrary] = useState([]);
  const [showFusionLibrary, setShowFusionLibrary] = useState(false);
  const [fusionPlayingId, setFusionPlayingId] = useState(null);
  const fusionAudioRef = useRef(null);
  const toggleFusionPlay = (item) => {
    const audio = fusionAudioRef.current;
    if (!audio || !item.audio_url) return;
    if (fusionPlayingId === item.id) {
      audio.pause();
      setFusionPlayingId(null);
      return;
    }
    const src = item.audio_url.startsWith('/') ? `${API_ORIGIN}${item.audio_url}` : item.audio_url;
    if (audio.src !== src) audio.src = src;
    audio.play().then(() => setFusionPlayingId(item.id)).catch(() => toast.error('Playback failed — try the WAV download'));
  };
  const [fusionLastMidi, setFusionLastMidi] = useState(null);
  useEffect(() => {
    axios.get(`${API}/dna-genres`).then(res => setFusionGenres(res.data.genres || [])).catch(() => {});
  }, []);
  const runDnaFusion = async (params = '') => {
    setDnaFusionLoading(true);
    try {
      const res = await axios.get(`${API}/dna-fusion${params}`);
      setDnaFusionResult(res.data);
      toast.success(`🧬 ${res.data.title} created!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fusion failed. Try again!');
    }
    setDnaFusionLoading(false);
  };
  const pollFusionTask = async (taskId, title) => {
    // Non-blocking background poll (up to ~10 min); user can keep using the app
    for (let attempt = 0; attempt < 200; attempt++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const st = await axios.get(`${API}/beat-status/${taskId}`, { timeout: 15000 });
        const s = st.data;
        setFusionSong(prev => ({ ...(prev || {}), status: 'generating', title, progress: Math.max(s.progress || 0, prev?.progress || 0), note: attempt >= 50 ? '⏳ Still rendering your studio-grade Suno track — please wait, high quality takes a little longer.' : null }));
        if (s.status === 'completed' && s.audio_url) {
          const url = s.audio_url.startsWith('/api') ? `${API_ORIGIN}${s.audio_url}` : s.audio_url;
          setFusionSong({ status: 'completed', url, title, progress: 100 });
          localStorage.removeItem('sounddna_fusion_task');
          toast.success(`🎵 "${title || 'Your fusion track'}" is ready — saved to My Songs!`, { duration: 10000 });
          return;
        }
        if (s.status === 'failed') {
          setFusionSong({ status: 'failed', url: null, progress: 0 });
          localStorage.removeItem('sounddna_fusion_task');
          toast.error(s.error || 'Song generation failed. Try again!');
          return;
        }
      } catch (pollErr) {
        // network hiccup — keep polling
      }
    }
    // Cap reached: backend keeps working and auto-saves to My Songs — don't hard-error
    toast.info('⏳ Still generating — your track will appear in My Songs when it finishes.', { duration: 8000 });
  };

  const generateFusionSong = async () => {
    if (!dnaFusionResult) return;
    setFusionSong({ status: 'generating', url: null, title: dnaFusionResult.title, progress: 5 });
    try {
      const sections = (dnaFusionResult.structure || '').split('\n')
        .map(l => (l.match(/^\[([^\]]+)\]/) || [])[1]).filter(Boolean);
      const prompt = `${dnaFusionResult.style_prompt}${sections.length ? `, arrangement: ${sections.join(' → ').toLowerCase()}` : ''}`;
      const res = await axios.post(`${API}/generate-beat`, {
        prompt,
        duration: 30,
        email: currentUser?.email || userEmail || getGuestEmail(),
        source: 'dna-fusion',
        title: dnaFusionResult.title,
        enhance: enhancePrompt
      });
      const taskId = res.data.task_id;
      localStorage.setItem('sounddna_fusion_task', JSON.stringify({ taskId, title: dnaFusionResult.title, startedAt: Date.now() }));
      toast.success('🎧 Your track is being created — this usually takes a few minutes. Keep creating, we\'ll notify you when it\'s ready!', { duration: 8000 });
      pollFusionTask(taskId, dnaFusionResult.title);
    } catch (e) {
      setFusionSong({ status: 'failed', url: null, progress: 0 });
      toast.error(e.response?.data?.detail || 'Could not start song generation');
    }
  };

  // Resume fusion polling after reload/navigation so long runs always complete cleanly
  useEffect(() => {
    try {
      const pending = JSON.parse(localStorage.getItem('sounddna_fusion_task') || 'null');
      if (pending?.taskId && Date.now() - (pending.startedAt || 0) < 30 * 60 * 1000) {
        setFusionSong({ status: 'generating', url: null, title: pending.title, progress: 10 });
        pollFusionTask(pending.taskId, pending.title);
      } else if (pending) {
        localStorage.removeItem('sounddna_fusion_task');
      }
    } catch { /* ignore corrupt state */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [workflowPrompt, setWorkflowPrompt] = useState({
    stylePrompt: '',
    bpm: 120,
    key: 'C',
    mode: 'minor',
    genre: '',
    mood: '',
    voiceSample: '',
    lyrics: '',
    rhymeScheme: 'AABB',
    flowPattern: 'standard',
    barsCount: 16,
    drumPattern: '',  // Drum pattern for Suno
    drumSunoPrompt: '', // Copy-paste ready Suno prompt for drums
    masteredTrack: null, // {url, filename, presets} from Mastering Studio
    source: '' // 'upload', 'preset', 'analyzer', 'manual', 'drum'
  });
  
  // Function to update workflow from any tool
  const updateWorkflow = (updates) => {
    setWorkflowPrompt(prev => ({ ...prev, ...updates }));
    // Also update individual states for backwards compatibility
    if (updates.stylePrompt) setBeatPrompt(updates.stylePrompt);
    if (updates.bpm) setSelectedBPM(updates.bpm);
    if (updates.key) setSelectedKey(updates.key);
    if (updates.mode) setSelectedMode(updates.mode);
    if (updates.rhymeScheme) setRhymeScheme(updates.rhymeScheme);
    if (updates.flowPattern) setFlowPattern(updates.flowPattern);
    if (updates.barsCount) setBarsCount(updates.barsCount);
    if (updates.lyrics) setGeneratedBars(prev => ({ ...(prev || {}), raw_lyrics: updates.lyrics }));
  };
  
  // World Voice DNA State
  const [worldVoiceData, setWorldVoiceData] = useState(null);
  const [worldVoiceRegion, setWorldVoiceRegion] = useState('pop');
  const [worldVoiceGenerating, setWorldVoiceGenerating] = useState(null);
  const [voiceStylePresets, setVoiceStylePresets] = useState([]);
  const [selectedVoiceStyle, setSelectedVoiceStyle] = useState('');
  useEffect(() => {
    axios.get(`${API}/voice-styles`).then(r => setVoiceStylePresets(r.data.styles || [])).catch(() => {});
  }, []);
  const [worldVoiceAudio, setWorldVoiceAudio] = useState(null);
  const [worldVoiceWav, setWorldVoiceWav] = useState(null); // WAV file for Suno upload
  const [lastGeneratedVoice, setLastGeneratedVoice] = useState(null); // For workflow integration
  const worldVoiceAudioRef = useRef(null);
  
  // Stems Separator State
  const [stemsAudioUrl, setStemsAudioUrl] = useState("");
  const [stemsFile, setStemsFile] = useState(null);
  const [stemsDragActive, setStemsDragActive] = useState(false);
  const [isSeparatingStems, setIsSeparatingStems] = useState(false);
  const [separatedStems, setSeparatedStems] = useState(null);
  const [stemsTaskId, setStemsTaskId] = useState(null);
  const [copiedVoiceSample, setCopiedVoiceSample] = useState(null);
  
  // ElevenLabs Voice Generator State
  const [elevenVoices, setElevenVoices] = useState([]);
  const [singingVoices, setSingingVoices] = useState([]);  // Professional singing voices
  const [elevenSelectedVoice, setElevenSelectedVoice] = useState("");
  const [selectedVoiceSample, setSelectedVoiceSample] = useState("");  // For workflow hub
  const [elevenText, setElevenText] = useState("");
  const [elevenGenerating, setElevenGenerating] = useState(false);
  const [elevenAudioUrl, setElevenAudioUrl] = useState(null);
  const [elevenError, setElevenError] = useState(null);
  const [elevenSingingMode, setElevenSingingMode] = useState(false);  // Singing mode toggle
  
  // VOCAL GENERATOR State (MiniMax singing vocals)
  const [vocalLyrics, setVocalLyrics] = useState("");
  const [vocalStyle, setVocalStyle] = useState("R&B");
  const [vocalGender, setVocalGender] = useState("female");
  const [vocalTempo, setVocalTempo] = useState("medium");
  const [vocalGenerating, setVocalGenerating] = useState(false);
  // Global generation status widget (Optimistic UI)
  const [genWidget, setGenWidget] = useState(null); // {kind, label, progress, eta, status: 'working'|'ready', resultUrl}
  const genWidgetAudioRef = useRef(null);
  const genWidgetAutoPlay = (url) => {
    setTimeout(() => {
      const a = genWidgetAudioRef.current;
      if (a && url) { a.src = url; a.play().catch(() => {}); }
    }, 300);
  };
  
  const [vocalTaskId, setVocalTaskId] = useState(null);
  const [vocalProgress, setVocalProgress] = useState(0);
  const [vocalAudioUrl, setVocalAudioUrl] = useState(null);
  const [vocalError, setVocalError] = useState(null);
  const [vocalLibrary, setVocalLibrary] = useState([]);  // My Vocals Library
  const [vocalLibraryExpanded, setVocalLibraryExpanded] = useState(false);
  const [playingVocal, setPlayingVocal] = useState(null);
  const vocalAudioRef = useRef(null);
  
  // Helper function to download files with "Save As" dialog (File System Access API)
  // ── UNIFIED DOWNLOAD HELPER ─────────────────────────────────────────────
  // Single entry point for every file download in the app.
  // Priority: (1) navigator.share with a files payload when navigator.canShare
  // allows it (mobile / PWA — gives the user a real "save to / share" chooser),
  // (2) optional desktop Save-As picker for the handful of call sites that
  // already used it (opt-in via desktopPicker, keeps DESKTOP behaviour unchanged),
  // (3) iOS audio-via-proxy for reliable Files-app saves, then
  // (4) the existing anchor + download attribute + createObjectURL fallback.
  const isIOSDevice = () =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const shareOrDownloadBlob = async (blob, filename, mimeType = null, options = {}) => {
    const { successMessage = null, sourceUrl = null, desktopPicker = false, forceBlob = false } = options;
    const type = (blob && blob.type) || mimeType || 'application/octet-stream';
    const isIOS = isIOSDevice();

    // (1) Web Share with files — the reliable "Save to Files" path on iOS (Safari AND
    // installed PWA). Sharing the in-memory file keeps it a real audio file and returns
    // to the app afterward.
    try {
      const file = new File([blob], filename, { type });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
        toast.success(successMessage || '✅ Saved / shared');
        return true;
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return false; // user cancelled the share sheet
      // any other share error → fall through to the download fallbacks
    }

    // (2) Desktop "Save As" picker — only for sites that historically used it (keeps desktop unchanged).
    if (desktopPicker && 'showSaveFilePicker' in window && !isIOS) {
      try {
        const extension = (filename.split('.').pop() || 'bin').toLowerCase();
        const handle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{ description: `${extension.toUpperCase()} file`, accept: { [type]: [`.${extension}`] } }],
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
        toast.success(successMessage || '✅ Saved to your chosen location!');
        return true;
      } catch (err) {
        if (err && err.name === 'AbortError') return false; // user cancelled
        // otherwise fall through to anchor download
      }
    }

    // (3) Blob-URL anchor download (real file — never opens the remote URL, which iOS
    // would render as a fullscreen video/QuickTime player with no way to save the audio).
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.rel = 'noopener noreferrer';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      if (link.parentNode) document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, 100);
    if (successMessage) toast.success(successMessage);
    else if (isIOS) toast.success('📥 Downloading. Check Downloads in Safari or the Files app.', { duration: 5000 });
    else toast.success('📥 Downloaded to your Downloads folder.');
    return true;
  };

  // URL convenience wrapper: fetch a (possibly relative /api) URL, then share/download it.
  const shareOrDownloadUrl = async (url, filename, mimeType = null, options = {}) => {
    let fullUrl = url;
    if (fullUrl && fullUrl.startsWith('/')) fullUrl = `${API_ORIGIN}${fullUrl}`;
    const response = await fetch(fullUrl);
    if (!response.ok) throw new Error(`Download failed (${response.status})`);
    const blob = await response.blob();
    return shareOrDownloadBlob(blob, filename, mimeType, { sourceUrl: fullUrl, ...options });
  };

  // Text/JSON convenience wrapper (lyrics, prompts, structure exports).
  const shareOrDownloadText = async (text, filename, mimeType = 'text/plain', options = {}) => {
    const blob = new Blob([text], { type: mimeType });
    return shareOrDownloadBlob(blob, filename, mimeType, options);
  };


  const downloadFileWithPicker = async (blob, suggestedName, mimeType, successMessage, sourceUrl = null) => {
    // Delegates to the unified helper. desktopPicker:true preserves this call site's
    // historical desktop "Save As" behaviour; mobile now gets the native share sheet.
    try {
      return await shareOrDownloadBlob(blob, suggestedName, mimeType, {
        successMessage, sourceUrl, desktopPicker: true,
      });
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
      return false;
    }
  };

  // Helper function to copy text to clipboard (works on iOS/mobile)
  const copyTextToClipboard = async (text, label) => {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        throw new Error('Clipboard not available');
      }
    } catch (err) {
      // Fallback: Create temp textarea and copy
      try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        textarea.setAttribute('readonly', '');
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const success = document.execCommand('copy');
        document.body.removeChild(textarea);
        return success;
      } catch (e) {
        return false;
      }
    }
  };
  
  // Charles M. LeShore Prompt Generator State
  const [leshoreTitle, setLeshoreTitle] = useState("");
  const [leshoreLyrics, setLeshoreLyrics] = useState("");
  const [leshoreStyle, setLeshoreStyle] = useState("");
  const [leshoreStyleTags, setLeshoreStyleTags] = useState([]);
  const [leshoreInstrumental, setLeshoreInstrumental] = useState(false);
  const [leshoreWorkspace, setLeshoreWorkspace] = useState([]);
  const [leshoreActiveTab, setLeshoreActiveTab] = useState('create'); // 'create' or 'workspace'
  const [leshorePlaying, setLeshorePlaying] = useState(null);
  const [leshoreCopied, setLeshoreCopied] = useState(null);
  
  // ============================================
  // MANNIS DEMO CREATOR - For quick beat demos (use Suno for studio quality)
  // ============================================
  const [beatPrompt, setBeatPrompt] = useState("");
  const [beatLyrics, setBeatLyrics] = useState(""); // Lyrics for the beat
  const [beatDuration, setBeatDuration] = useState(30);
  const [beatQuickMode, setBeatQuickMode] = useState(true); // Quick mode by default
  const [beatGenerating, setBeatGenerating] = useState(false);
  const [beatAudioUrl, setBeatAudioUrl] = useState(null);
  const [beatAudioUrlB, setBeatAudioUrlB] = useState(null); // Version B
  const [beatError, setBeatError] = useState(null);
  const [beatProgress, setBeatProgress] = useState(0);
  const [beatStatus, setBeatStatus] = useState("idle"); // idle, starting, processing, completed, failed
  const [showBeatLibrary, setShowBeatLibrary] = useState(false);
  const [activeBeatVersion, setActiveBeatVersion] = useState("A"); // "A" or "B"
  const beatAudioRef = useRef(null);
  const beatAudioRefB = useRef(null);
  
  // ============ NEGATIVE DNA ENGINE ============
  const [negativeDNA, setNegativeDNA] = useState(""); // What to exclude
  const [selectedBPM, setSelectedBPM] = useState(""); // Specific BPM
  const [selectedKey, setSelectedKey] = useState(""); // Musical key
  const [selectedMode, setSelectedMode] = useState("minor"); // Major/Minor
  
  // ============ AI SINGING GENERATOR (MiniMax) ============
  const [showSingingGenerator, setShowSingingGenerator] = useState(false);
  const [singingLyrics, setSingingLyrics] = useState("");
  const [singingGender, setSingingGender] = useState("female");
  const [singingStyle, setSingingStyle] = useState("pop ballad");
  const [singingTempo, setSingingTempo] = useState("medium");
  const [singingKey, setSingingKey] = useState("C");  // Musical key for singing
  const [singingLanguage, setSingingLanguage] = useState("English"); // Multi-language support
  const [isTranslating, setIsTranslating] = useState(false); // Translation loading state
  const [singingTaskId, setSingingTaskId] = useState(null);
  const [singingStatus, setSingingStatus] = useState("idle"); // idle, generating, completed, failed
  const [singingAudioUrl, setSingingAudioUrl] = useState(null);
  const [singingProgress, setSingingProgress] = useState(0);
  const singingAudioRef = useRef(null);
  
  // ============ LANGUAGE DEMO AUDIO ============
  const [demoLanguage, setDemoLanguage] = useState(null); // Currently playing demo language
  const [demoAudioUrl, setDemoAudioUrl] = useState(null);
  const [demoGenerating, setDemoGenerating] = useState(false);
  const demoAudioRef = useRef(null);
  
  // ============ DNA LYRICS ============
  const [showDNALyrics, setShowDNALyrics] = useState(false);
  const [showWorldVoiceDNA, setShowWorldVoiceDNA] = useState(false); // World Voice DNA panel toggle
  const [dnaLyricsTopic, setDnaLyricsTopic] = useState("");
  const [dnaLyricsGenre, setDnaLyricsGenre] = useState("Hip-Hop");
  const [dnaLyricsMood, setDnaLyricsMood] = useState("Emotional");
  const [dnaLyricsGenerating, setDnaLyricsGenerating] = useState(false);
  const [dnaGeneratedLyrics, setDnaGeneratedLyrics] = useState("");
  const [dnaLyricsTab, setDnaLyricsTab] = useState("generate"); // generate | library | rhyme
  const [lyricsPresets, setLyricsPresets] = useState([
    { id: 1, title: "Heartbreak Hotel", genre: "R&B", mood: "Sad", lyrics: "[Verse 1]\nEmpty rooms and memories\nYour perfume still lingers here\nI keep reaching for your hand\nBut you're no longer near\n\n[Chorus]\nHeartbreak hotel, I'm checking in tonight\nRoom for one, no love in sight\nHeartbreak hotel, these walls know my pain\nI'll be here until I'm whole again" },
    { id: 2, title: "City Lights", genre: "Pop", mood: "Upbeat", lyrics: "[Verse 1]\nNeon signs are calling me\nThe city never sleeps tonight\nI'm chasing dreams on empty streets\nUnder the electric lights\n\n[Chorus]\nCity lights, they shine so bright\nMaking everything feel right\nCity lights, guide me home\nIn this concrete jungle I roam" },
    { id: 3, title: "Trap Star", genre: "Hip-Hop", mood: "Confident", lyrics: "[Verse 1]\nStarted from the bottom now I'm here\nHaters couldn't see me, vision clear\nStacking paper, making moves all year\nTrap star living, no more fear\n\n[Chorus]\nI'm a trap star, watch me shine\nEvery hustle, every grind\nTrap star, it's my time\nSuccess is mine by design" },
    { id: 4, title: "Gospel Glory", genre: "Gospel", mood: "Spiritual", lyrics: "[Verse 1]\nIn my darkest hour, You were there\nLifted me up with tender care\nYour love surrounds me everywhere\nGrateful for Your answered prayer\n\n[Chorus]\nGlory, glory, hallelujah\nYour grace is more than enough\nGlory, glory, I praise You\nForever grateful for Your love" },
    { id: 5, title: "Summer Vibes", genre: "Afrobeat", mood: "Happy", lyrics: "[Verse 1]\nSun is shining, feeling alive\nRhythm got me moving side to side\nDancing with the waves and tide\nThis summer love I can't hide\n\n[Chorus]\nSummer vibes, we feeling right\nDancing under moonlight\nSummer vibes, hold me tight\nThis moment feels so right" },
    { id: 6, title: "Midnight Drive", genre: "R&B", mood: "Chill", lyrics: "[Verse 1]\nCruising down the highway slow\nCity lights put on a show\nJust you and me, nowhere to go\nLetting all our worries flow\n\n[Chorus]\nMidnight drive, just us tonight\nEverything feels so right\nMidnight drive, stars so bright\nHold my hand and hold on tight" }
  ]);
  
  // ============ AUDIO TUTORIAL ============
  const [tutorialAudioUrl, setTutorialAudioUrl] = useState(null);
  const [tutorialLoading, setTutorialLoading] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialLanguage, setTutorialLanguage] = useState("English");
  const [showWhySoundDNA, setShowWhySoundDNA] = useState(false);  // Why MANNIS comparison modal
  const tutorialAudioRef = useRef(null);
  
  // Tutorial text in English (base)
  const tutorialTextEnglish = `Welcome to MANNIS! Here are all the powerful features available for music creators. 

NEW! The MANNIS Fusion Workbench - create music no one has heard before! Pick any two of 166 genres: Genre A supplies the chords, Genre B supplies the rhythm and vocal harmonies. Try Salsa crossed with Neuro Drum and Bass, or Bossa Nova crossed with UK Drill. That's over 27,000 possible fusions, plus 30 hand-curated experimental blends like Monastery Phonk and Glitter Delta, ready to play from the Curated Blends menu.

NEW! 1-Click Create on the Suno version 5.5 engine - generate complete songs, beats, and full auto-pipeline productions with a single click. Every WAV export is automatically mastered with our broadcast-ready club preset, and every generation receives a Harmonic Uniqueness Safety Badge - an originality score with copyright insights so your music is verifiably yours. Finished songs auto-save to My Library.

First, DNA Lyrics and the Rhyme & Flow Engine - click DNA Lyrics for AI-powered lyrics generation. Choose your genre, mood, language, and enter a theme to create custom lyrics instantly. The Rhyme and Flow Engine now supports 32 world languages - from Spanish, Hindi and Punjabi to Russian, Thai, Bengali, Filipino, Amharic, Jamaican Patois and Haitian Creole! Browse the Lyrics Library with thousands of pre-written templates, or use the Rhyme Engine for structured bars with over 15 rhyme styles and 16 flow patterns, and choose anywhere from 8 to 64 bars.

Second, the 70K Style Prompts Library gives you instant access to over 70,000 professional style prompts across 60 plus genres. Click the audio preview button to hear how each style sounds.

Audio Preview - Every style prompt has an Audio Preview button! Click the purple headphone icon next to any prompt to hear a 10-second AI-generated preview of how that style sounds. This helps you choose the perfect style before creating your full song.

MIDI Files - Convert any style prompt into a full 32-bar MIDI arrangement! Click the green Download MIDI button on any prompt to generate a professional MIDI file with 10 pro stems: split Drums (Kick & Snare / Hats & Perc), 808 Bass, Sub Bass, Chords, Pads, Lead Melody, Counter Arp, Strings & Brass, and FX & Cymbals. The MIDI file matches your style prompt's BPM, key, and genre. Import directly into your DAW for further production.

Third, the Music Link Analyzer - paste any Spotify, YouTube, or SoundCloud link to analyze the song's BPM, key, mood, and style. Get AI-generated style prompts based on the analysis.

My MANNIS Library - everything you create auto-saves here, organised into Prompts, Songs, Drums, and Stems tabs. Search by genre, star your favorites, and on every row use four buttons: Copy Prompt, Download, Audio Play to listen inline, and MIDI to build a 32-bar arrangement. Hit Erase to remove any item you no longer want.

Everything integrates with ALL major AI music applications - Suno, Udio, Loudly, Boomy, Beatoven, and Soundraw. The Export to Suno section shows your style prompt and lyrics ready to copy. Download broadcast-ready mastered WAV files and upload to any platform. Download MIDI files to import into any DAW.

The Unified Workflow connects all tools seamlessly. Fuse two genres in the MANNIS Fusion Workbench, generate lyrics in 32 world languages, preview audio styles, download MIDI arrangements, and create your full mastered song with one click. Everything lands in My MANNIS Library. Start creating professional music today!`;
  
  // Generate audio tutorial
  const generateAudioTutorial = async () => {
    setTutorialLoading(true);
    try {
      let tutorialText = tutorialTextEnglish;
      
      // Translate if not English
      if (tutorialLanguage !== "English") {
        toast.info(`🌍 Translating tutorial to ${tutorialLanguage}...`);
        const translateRes = await fetch(`${API}/translate-lyrics`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: tutorialTextEnglish,
            target_language: tutorialLanguage,
            keep_song_structure: false
          })
        });
        if (!translateRes.ok) {
          console.error('Translation failed:', translateRes.status);
          toast.error('Translation failed, using English');
        } else {
          const translateData = await translateRes.json();
          if (translateData.translated_text) {
            tutorialText = translateData.translated_text;
          }
        }
      }
      
      toast.info('🎙️ Generating audio narration...');
      
      const res = await fetch(`${API}/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: tutorialText,
          voice_id: "21m00Tcm4TlvDq8ikWAM",
          model_id: "eleven_multilingual_v2",
          stability: 0.5,
          similarity_boost: 0.75,
          style: 0.0,
          use_speaker_boost: true,
          singing_mode: false
        })
      });
      
      if (res.status === 429) {
        toast.error('Too many requests, please wait a moment');
        return;
      }
      
      if (res.status === 402) {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'ElevenLabs quota exceeded - please add credits');
        return;
      }
      
      if (res.status === 401) {
        toast.error('ElevenLabs API key is invalid or expired');
        return;
      }
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error: ${res.status}`);
      }
      
      const data = await res.json();
      if (data.audio_url) {
        setTutorialAudioUrl(data.audio_url);
        toast.success(`🎧 ${tutorialLanguage} audio tutorial ready!`);
      } else {
        throw new Error(data.detail || 'No audio URL received');
      }
    } catch (err) {
      console.error('Tutorial audio error:', err);
      toast.error(err.message || 'Could not generate audio tutorial');
    } finally {
      setTutorialLoading(false);
    }
  };
  
  // ============ DNA MARKETPLACE ============
  const [showDNAMarketplace, setShowDNAMarketplace] = useState(false);
  const [marketplaceDNAs, setMarketplaceDNAs] = useState([]);
  const [myDNAs, setMyDNAs] = useState([]);
  const [dnaMarketplaceTab, setDnaMarketplaceTab] = useState("browse"); // browse | my-library
  const [dnaCurrentPage, setDnaCurrentPage] = useState(1); // Pagination: Pages 1-10
  const [dnaFilterGenre, setDnaFilterGenre] = useState("All"); // Genre filter
  const PRESETS_PER_PAGE = 60; // 60 presets per page
  const [savingDNA, setSavingDNA] = useState(false);
  const [dnaName, setDnaName] = useState("");
  const [dnaDescription, setDnaDescription] = useState("");
  const [dnaTags, setDnaTags] = useState("");
  
  // Fetch marketplace DNAs - get ALL presets for pagination
  const fetchMarketplaceDNAs = async () => {
    try {
      console.log("Fetching DNA presets from:", `${API}/dna/marketplace?limit=700`);
      const response = await fetch(`${API}/dna/marketplace?limit=700`);
      if (!response.ok) {
        console.error("API response not OK:", response.status);
        return;
      }
      const data = await response.json();
      console.log("Fetched presets:", data.dnas?.length || 0);
      if (data.success && data.dnas) {
        setMarketplaceDNAs(data.dnas);
        setDnaCurrentPage(1);
      } else {
        console.error("API returned success=false or no dnas");
      }
    } catch (error) {
      console.error("Failed to fetch marketplace:", error);
    }
  };
  
  // Auto-fetch presets when switching to marketplace view
  useEffect(() => {
    if (activeView === 'marketplace' && marketplaceDNAs.length === 0) {
      fetchMarketplaceDNAs();
    }
  }, [activeView]);
  
  // Also fetch on initial load so presets are ready
  useEffect(() => {
    fetchMarketplaceDNAs();
  }, []);
  
  // Get paginated and filtered presets
  const getPaginatedPresets = () => {
    let filtered = marketplaceDNAs;
    
    // Apply genre filter with flexible matching
    if (dnaFilterGenre !== "All") {
      const searchTerm = dnaFilterGenre.toLowerCase().replace(/\s+/g, '');
      filtered = marketplaceDNAs.filter(dna => {
        // Check categories array
        if (Array.isArray(dna.categories)) {
          const hasMatch = dna.categories.some(cat => {
            if (!cat) return false;
            const catLower = cat.toLowerCase().replace(/\s+/g, '');
            // Exact match or partial match
            return catLower === searchTerm || 
                   catLower.includes(searchTerm) || 
                   searchTerm.includes(catLower) ||
                   cat.toLowerCase().includes(dnaFilterGenre.toLowerCase());
          });
          if (hasMatch) return true;
        } else if (typeof dna.categories === 'string') {
          if (dna.categories.toLowerCase().includes(dnaFilterGenre.toLowerCase())) return true;
        }
        // Also check name and prompt for genre keywords
        const searchText = `${dna.name || ''} ${dna.prompt || ''}`.toLowerCase();
        return searchText.includes(dnaFilterGenre.toLowerCase());
      });
    }
    
    // Calculate pagination
    const startIndex = (dnaCurrentPage - 1) * PRESETS_PER_PAGE;
    const endIndex = startIndex + PRESETS_PER_PAGE;
    const paginated = filtered.slice(startIndex, endIndex);
    const totalPages = Math.ceil(filtered.length / PRESETS_PER_PAGE);
    
    return { presets: paginated, totalPages, totalCount: filtered.length };
  };
  
  // Fetch my DNAs
  const fetchMyDNAs = async () => {
    if (!userEmail) return;
    try {
      const response = await fetch(`${API}/dna/my-library?email=${encodeURIComponent(userEmail)}`);
      const data = await response.json();
      if (data.success) {
        setMyDNAs(data.dnas);
      }
    } catch (error) {
      console.error("Failed to fetch my DNAs:", error);
    }
  };
  
  // Translate lyrics to selected language
  const translateLyrics = async () => {
    if (!singingLyrics.trim()) {
      toast.error('Please enter lyrics to translate');
      return;
    }
    
    if (singingLanguage === 'English') {
      toast.info('Lyrics are already in English');
      return;
    }
    
    setIsTranslating(true);
    toast.info(`🌍 Translating to ${singingLanguage}...`);
    
    try {
      const response = await fetch(`${API}/translate-lyrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: singingLyrics,
          target_language: singingLanguage,
          keep_song_structure: true
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.translated_text) {
        setSingingLyrics(data.translated_text);
        toast.success(`✅ Lyrics translated to ${singingLanguage}!`);
      } else {
        throw new Error(data.detail || 'Translation failed');
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error(error.message || 'Failed to translate lyrics');
    } finally {
      setIsTranslating(false);
    }
  };
  
  // Generate AI Singing Vocals with MiniMax
  const generateSingingVocals = async () => {
    if (!singingLyrics.trim()) {
      toast.error('Please enter lyrics for the singing vocals');
      return;
    }
    
    setSingingStatus('generating');
    setSingingProgress(10);
    
    try {
      const response = await fetch(`${API}/generate-vocals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lyrics: singingLyrics,
          gender: singingGender,
          vocal_style: singingStyle,
          tempo: singingTempo,
          language: singingLanguage,
          email: userEmail || ''
        })
      });
      
      const data = await response.json();
      
      if (data.task_id) {
        setSingingTaskId(data.task_id);
        toast.info('🎤 Generating singing vocals... This takes ~2 minutes');
        pollSingingStatus(data.task_id);
      } else {
        throw new Error(data.detail || 'Failed to start generation');
      }
    } catch (error) {
      console.error('Singing generation error:', error);
      setSingingStatus('failed');
      toast.error(error.message || 'Failed to generate singing vocals');
    }
  };
  
  // Poll singing generation status
  const pollSingingStatus = async (taskId) => {
    const maxAttempts = 60; // 2 minutes max
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await fetch(`${API}/vocal-status/${taskId}`);
        const data = await response.json();
        
        setSingingProgress(data.progress || 50);
        
        if (data.status === 'completed' && data.audio_url) {
          setSingingStatus('completed');
          setSingingAudioUrl(data.audio_url);
          setSingingProgress(100);
          toast.success('🎤 Singing vocals generated successfully!');
          return;
        } else if (data.status === 'failed') {
          setSingingStatus('failed');
          toast.error(data.error || 'Singing generation failed');
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setSingingStatus('failed');
          toast.error('Generation timed out. Please try again.');
        }
      } catch (error) {
        console.error('Poll error:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        }
      }
    };
    
    poll();
  };
  
  // Save DNA to marketplace
  const saveDNAToMarketplace = async () => {
    if (!beatPrompt || !beatPrompt.trim() || !dnaName || !dnaName.trim()) {
      toast.error("Enter a style prompt and name first!");
      return;
    }
    
    setSavingDNA(true);
    try {
      const response = await fetch(`${API}/dna/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userEmail || getGuestEmail(),
          name: dnaName,
          description: dnaDescription,
          style_prompt: beatPrompt,
          negative_dna: negativeDNA,
          bpm: selectedBPM || "auto",
          key: selectedKey || "auto",
          mode: selectedMode,
          categories: {
            rhythm: extractCategory(beatPrompt, "rhythm"),
            melody: extractCategory(beatPrompt, "melody"),
            timbre: extractCategory(beatPrompt, "timbre")
          },
          tags: dnaTags.split(",").map(t => t.trim()).filter(t => t),
          is_public: true
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`✅ "${dnaName}" saved to marketplace!`);
        setDnaName("");
        setDnaDescription("");
        setDnaTags("");
        fetchMyDNAs();
      } else {
        throw new Error(data.detail || "Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save DNA: " + error.message);
    } finally {
      setSavingDNA(false);
    }
  };
  
  // Load DNA from marketplace
  const loadDNA = async (dna) => {
    setBeatPrompt(dna.style_prompt || "");
    setNegativeDNA(dna.negative_dna || "");
    setSelectedBPM(dna.bpm || "");
    setSelectedKey(dna.key || "");
    setSelectedMode(dna.mode || "minor");
    toast.success(`Loaded: ${dna.name}`);
    setShowDNAMarketplace(false);
    
    // Increment download count
    try {
      await fetch(`${API}/dna/download/${dna._id}`, { method: 'POST' });
    } catch (e) {}
  };
  
  // DNA Export Function
  const exportDNA = async () => {
    const dnaData = {
      version: "1.0",
      created: new Date().toISOString(),
      platform: "SoundDNA.pro",
      dna: {
        style: beatPrompt || "",
        negative: negativeDNA || "",
        bpm: selectedBPM || "auto",
        key: selectedKey || "auto",
        mode: selectedMode || "minor",
        lyrics: beatLyrics || "",
        duration: beatDuration,
        // 9 Category DNA Breakdown
        categories: {
          rhythm: extractCategory(beatPrompt, "rhythm"),
          meter: extractCategory(beatPrompt, "meter"),
          melody: extractCategory(beatPrompt, "melody"),
          harmony: extractCategory(beatPrompt, "harmony"),
          timbre: extractCategory(beatPrompt, "timbre"),
          texture: extractCategory(beatPrompt, "texture"),
          dynamics: extractCategory(beatPrompt, "dynamics"),
          form: extractCategory(beatPrompt, "form"),
          expression: extractCategory(beatPrompt, "expression")
        }
      },
      sunoPrompt: buildFullPrompt(),
      metadata: {
        userEmail: userEmail || "guest",
        exportedAt: new Date().toISOString()
      }
    };
    
    const filename = `sounddna-${Date.now()}.sdna`;
    const blob = new Blob([JSON.stringify(dnaData, null, 2)], { type: 'application/json' });

    await shareOrDownloadBlob(blob, filename, 'application/json', {
      desktopPicker: true,
      successMessage: '✅ DNA exported! Use this file anywhere.',
    });
  };
  
  // Extract category hints from prompt
  const extractCategory = (prompt, category) => {
    if (!prompt) return "auto";
    const lower = prompt.toLowerCase();
    const categoryHints = {
      rhythm: lower.includes("808") ? "808-heavy" : lower.includes("boom bap") ? "boom-bap" : "standard",
      meter: lower.match(/\d+\s*bpm/i) ? lower.match(/\d+\s*bpm/i)[0] : "4/4",
      melody: lower.includes("melodic") ? "melodic" : lower.includes("dark") ? "dark" : "neutral",
      harmony: lower.includes("minor") ? "minor" : lower.includes("major") ? "major" : "minor",
      timbre: lower.includes("synth") ? "synthetic" : lower.includes("acoustic") ? "acoustic" : "mixed",
      texture: lower.includes("sparse") ? "sparse" : lower.includes("full") ? "full" : "layered",
      dynamics: lower.includes("aggressive") ? "aggressive" : lower.includes("soft") ? "soft" : "dynamic",
      form: lower.includes("verse") ? "verse-chorus" : "freeform",
      expression: lower.includes("emotional") ? "emotional" : lower.includes("hype") ? "energetic" : "expressive"
    };
    return categoryHints[category] || "auto";
  };
  
  // Build full prompt with BPM, Key, and Negative DNA
  const buildFullPrompt = () => {
    let prompt = beatPrompt || "";
    if (selectedBPM) prompt += `, ${selectedBPM} BPM`;
    if (selectedKey) prompt += `, ${selectedKey} ${selectedMode}`;
    if (negativeDNA) prompt += ` [EXCLUDE: ${negativeDNA}]`;
    return prompt;
  };
  
  // Song Structure Builder State
  const [showStructureBuilder, setShowStructureBuilder] = useState(false);
  const [structureBaseStyle, setStructureBaseStyle] = useState("dark trap, 808 bass, emotional vocals, 140 BPM");
  const [structureLibraryOpen, setStructureLibraryOpen] = useState(false);
  const [structureLibraryCategory, setStructureLibraryCategory] = useState('');
  const [structureLibraryPrompts, setStructureLibraryPrompts] = useState([]);
  const [structureLibraryLoading, setStructureLibraryLoading] = useState(false);
  const [structureVocals, setStructureVocals] = useState({
    soprano: false,
    alto: false,
    tenor: false,
    bass: false,
    twoOctavesDown: false,
    harmony: false
  });
  const [structureSections, setStructureSections] = useState({
    intro: { enabled: true, instruction: "atmospheric build, sparse instrumentation" },
    verse: { enabled: true, instruction: "rhythmic groove, storytelling" },
    preChorus: { enabled: false, instruction: "building tension, rising energy" },
    chorus: { enabled: true, instruction: "explosive, full harmony, exciting, powerful" },
    bridge: { enabled: false, instruction: "stripped down, emotional, key change" },
    outro: { enabled: true, instruction: "fade out, callback to intro" }
  });
  
  // Chord Progression State
  const [structureChords, setStructureChords] = useState({
    enabled: false,
    key: "C",
    mode: "minor",
    intro: "i - iv",
    verse: "i - VI - III - VII",
    preChorus: "iv - v",
    chorus: "VI - III - VII - i",
    bridge: "iv - v - i",
    outro: "i - iv - i"
  });
  
  // Load beat history from localStorage
  const [beatHistory, setBeatHistory] = useState(() => {
    try {
      const saved = localStorage.getItem('sounddna_beat_history');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  
  // MY BEAT LIBRARY - Cloud-based library
  const [beatLibrary, setBeatLibrary] = useState([]);
  const [beatLibraryLoading, setBeatLibraryLoading] = useState(false);
  const [beatLibraryOpen, setBeatLibraryOpen] = useState(false);
  const [libraryTab, setLibraryTab] = useState('beats');
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [savedAnalyses, setSavedAnalyses] = useState([]);

  const fetchSavedPrompts = async () => {
    if (!userEmail) return;
    try {
      const res = await axios.get(`${API}/library/style-prompts?email=${encodeURIComponent(userEmail)}`);
      setSavedPrompts(res.data.prompts || []);
    } catch (e) { console.error('fetch prompts lib failed', e); }
  };

  const fetchSavedAnalyses = async () => {
    if (!userEmail) return;
    try {
      const res = await axios.get(`${API}/library/analyses?email=${encodeURIComponent(userEmail)}`);
      setSavedAnalyses(res.data.analyses || []);
    } catch (e) { console.error('fetch analyses lib failed', e); }
  };

  const deleteSavedPrompt = async (id) => {
    try {
      await axios.delete(`${API}/library/style-prompts/${id}?email=${encodeURIComponent(userEmail)}`);
      setSavedPrompts(prev => prev.filter(p => p.id !== id));
      toast.success('Prompt removed');
    } catch (e) { toast.error('Could not delete'); }
  };

  const deleteSavedAnalysis = async (id) => {
    try {
      await axios.delete(`${API}/library/analyses/${id}?email=${encodeURIComponent(userEmail)}`);
      setSavedAnalyses(prev => prev.filter(a => a.id !== id));
      toast.success('Analysis removed');
    } catch (e) { toast.error('Could not delete'); }
  };
  const [playingLibraryBeat, setPlayingLibraryBeat] = useState(null);
  const [libraryProgress, setLibraryProgress] = useState(0);
  const [libraryDuration, setLibraryDuration] = useState(0);
  const [libraryCurrentTime, setLibraryCurrentTime] = useState(0);
  const libraryAudioRef = useRef(null);
  
  // Fetch beat library from cloud
  const fetchBeatLibrary = async () => {
    if (!userEmail) return;
    setBeatLibraryLoading(true);
    try {
      const response = await fetch(`${API}/beat-library/${encodeURIComponent(userEmail)}`);
      if (response.ok) {
        const data = await response.json();
        setBeatLibrary(data.beats || []);
      }
    } catch (error) {
      console.error('Error fetching beat library:', error);
    } finally {
      setBeatLibraryLoading(false);
    }
  };
  
  // Delete beat from library
  const deleteBeatFromLibrary = async (taskId) => {
    if (!userEmail) {
      toast.error('Please sign in to delete beats');
      return;
    }
    try {
      const response = await fetch(`${API}/beat-library/${encodeURIComponent(userEmail)}/${taskId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setBeatLibrary(prev => prev.filter(b => b.task_id !== taskId));
        toast.success('Beat deleted from library');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete beat');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete beat');
    }
  };
  
  // Play/pause library beat
  // State for currently playing library beat URL
  const [libraryBeatUrl, setLibraryBeatUrl] = useState(null);
  
  const toggleLibraryBeat = (beat) => {
    if (playingLibraryBeat === beat.task_id) {
      // Stop playing
      if (libraryAudioRef.current) {
        libraryAudioRef.current.pause();
        libraryAudioRef.current.currentTime = 0;
      }
      setPlayingLibraryBeat(null);
      setLibraryBeatUrl(null);
      setLibraryProgress(0);
      setLibraryCurrentTime(0);
    } else {
      // Stop any currently playing audio first
      if (libraryAudioRef.current) {
        libraryAudioRef.current.pause();
      }
      
      let audioUrl = beat.audio_url;
      if (!audioUrl) {
        toast.error('No audio URL available');
        return;
      }
      
      // If it's a permanent URL (relative path), prepend the API base
      if (audioUrl.startsWith('/api/')) {
        audioUrl = `${API_ORIGIN}${audioUrl}`;
      }
      
      // Set state
      setPlayingLibraryBeat(beat.task_id);
      setLibraryBeatUrl(audioUrl);
      setLibraryProgress(0);
      setLibraryCurrentTime(0);
      
      // iOS FIX: Play directly in the click handler (no setTimeout)
      // iOS requires audio play to be triggered directly from user gesture
      if (libraryAudioRef.current) {
        libraryAudioRef.current.src = audioUrl;
        libraryAudioRef.current.load();
        
        // For iOS: Create a new Audio object if needed
        const playAudio = () => {
          const playPromise = libraryAudioRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch(err => {
              console.error('Play failed:', err);
              // iOS fallback: try with user gesture unlock
              toast.error('Tap again to play audio', { duration: 2000 });
              setPlayingLibraryBeat(null);
              setLibraryBeatUrl(null);
            });
          }
        };
        
        // Try to play immediately
        playAudio();
      }
    }
  };
  
  // Remove the useEffect for libraryBeatUrl - we handle play directly now
  
  // ============ MUSIC LINK ANALYZER ============
  const [spotifyUrl, setSpotifyUrl] = useState("");
  const [spotifyAnalyzing, setSpotifyAnalyzing] = useState(false);
  const [spotifyResult, setSpotifyResult] = useState(null);
  const [showSpotifyModal, setShowSpotifyModal] = useState(false);
  const [inlinePrompts, setInlinePrompts] = useState(null); // For inline dropdown
  const [musicAnalyzerFile, setMusicAnalyzerFile] = useState(null); // For drag-and-drop audio file
  const [musicAnalyzerDragActive, setMusicAnalyzerDragActive] = useState(false);
  const [analyzerAudioUrl, setAnalyzerAudioUrl] = useState(null); // Preview player source (object URL or remote MP3 URL)
  const [analyzerAudioSource, setAnalyzerAudioSource] = useState(null); // 'upload' | 'url'
  const [isConvertingWav, setIsConvertingWav] = useState(false);
  
  // Load a file into the analyzer (preview first, analyze on demand)
  const loadMusicAnalyzerFile = (file) => {
    if (!file) return;
    
    const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/x-m4a', 'audio/flac', 'audio/mp3'];
    const validExtensions = ['.mp3', '.wav', '.m4a', '.flac'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExt)) {
      toast.error("Please upload MP3, WAV, M4A, or FLAC files only");
      return;
    }
    
    if (analyzerAudioUrl && analyzerAudioSource === 'upload') {
      URL.revokeObjectURL(analyzerAudioUrl);
    }
    setMusicAnalyzerFile(file);
    setAnalyzerAudioUrl(URL.createObjectURL(file));
    setAnalyzerAudioSource('upload');
    setInlinePrompts(null);
    setSpotifyResult(null);
    toast.success(`🎧 "${file.name}" loaded - preview below, then hit Analyze`);
  };
  
  // Download the currently loaded track as WAV (converted server-side via ffmpeg)
  const downloadAnalyzerWav = async () => {
    if (!analyzerAudioSource) {
      toast.error('Load an MP3 first (upload or URL)');
      return;
    }
    
    setIsConvertingWav(true);
    try {
      toast.info('🎛️ Converting to WAV...');
      const formData = new FormData();
      if (analyzerAudioSource === 'upload' && musicAnalyzerFile) {
        formData.append('file', musicAnalyzerFile);
      } else {
        formData.append('url', analyzerAudioUrl);
      }
      
      const response = await axios.post(`${API}/music-analyzer/convert-to-wav`, formData, {
        responseType: 'blob',
        timeout: 120000
      });
      
      const blob = new Blob([response.data], { type: 'audio/wav' });
      const baseName = (musicAnalyzerFile?.name || analyzerAudioUrl.split('/').pop() || 'track').replace(/\.[^/.]+$/, '');
      await shareOrDownloadBlob(blob, `${baseName}.wav`, 'audio/wav', {
        desktopPicker: true, successMessage: '📥 WAV downloaded!',
      });
    } catch (error) {
      console.error('WAV conversion error:', error);
      let detail = error.response?.data?.detail;
      if (error.response?.data instanceof Blob) {
        try { detail = JSON.parse(await error.response.data.text()).detail; } catch (e) {}
      }
      toast.error(detail || 'WAV conversion failed. Try again.');
    } finally {
      setIsConvertingWav(false);
    }
  };
  
  // Handle audio file analysis for Music Link Analyzer
  const analyzeAudioFile = async (file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['audio/mpeg', 'audio/wav', 'audio/mp4', 'audio/x-m4a', 'audio/flac', 'audio/mp3'];
    const validExtensions = ['.mp3', '.wav', '.m4a', '.flac'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExt)) {
      toast.error("Please upload MP3, WAV, M4A, or FLAC files only");
      return;
    }
    
    setMusicAnalyzerFile(file);
    setSpotifyAnalyzing(true);
    setInlinePrompts(null);
    
    try {
      toast.info(`🎵 Analyzing "${file.name}"...`);
      
      const formData = new FormData();
      formData.append("file", file);
      formData.append("prompt_count", "10");
      formData.append("platforms", "all");
      formData.append("style_mode", "professional");
      formData.append("email", userEmail || getGuestEmail());
      formData.append("enable_2026_sounds", "true");
      
      const response = await runAnalyzeJob(formData);
      
      if (response.data) {
        // Extract BPM, Key, Mood from analysis
        const analysis = response.data;
        
        // Create result structure similar to Spotify analysis
        const analysisResult = {
          track: {
            name: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
            artists: ["Uploaded Audio"],
            album: "Your File",
            album_art: null
          },
          analysis_summary: {
            bpm: analysis.bpm || analysis.analysis?.bpm || "N/A",
            key: analysis.key || analysis.analysis?.key || "N/A",
            energy: analysis.energy || analysis.analysis?.energy || "N/A",
            mood: analysis.mood || analysis.analysis?.mood || "N/A",
            genre: analysis.genre || analysis.analysis?.genre || "N/A",
            happiness: analysis.happiness || analysis.analysis?.happiness || "N/A"
          },
          generated_prompts: (analysis.prompts || []).slice(0, 10).map((p, i) => ({
            prompt: p.prompt || p,
            dna_code: p.dna_code || `DNA-${i + 1}`
          })),
          matching_styles: []
        };
        
        // Also show inline prompts for quick access
        const inlineVariations = (analysis.prompts || []).slice(0, 10).map((p, i) => ({
          prompt: p.prompt || p,
          dna_code: p.dna_code || `DNA-${i + 1}`
        }));
        
        setInlinePrompts(inlineVariations);
        setSpotifyResult(analysisResult);
        
        toast.success(`✅ Analyzed! Found ${analysis.prompts?.length || 0} style prompts`);
        
        // Fetch structured producer report (non-blocking enrichment)
        try {
          const reportRes = await axios.post(`${API}/music-analyzer/producer-report`, {
            track_name: analysisResult.track.name,
            artists: analysisResult.track.artists,
            bpm: String(analysisResult.analysis_summary.bpm || ''),
            key: String(analysisResult.analysis_summary.key || ''),
            mood: String(analysisResult.analysis_summary.mood || ''),
            genre: String(analysisResult.analysis_summary.genre || ''),
            energy: String(analysisResult.analysis_summary.energy || '')
          }, { timeout: 60000 });
          if (reportRes.data?.producer_report) {
            setSpotifyResult(prev => ({ ...prev, producer_report: reportRes.data.producer_report }));
          }
        } catch (reportErr) {
          console.error('Producer report failed:', reportErr);
        }
      }
    } catch (error) {
      console.error("Audio analysis error:", error);
      const errorDetail = error.response?.data?.detail || error.message;
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("Analysis timed out. Try a shorter audio file.");
      } else if (error.response?.status === 402) {
        if (currentUser) { setShowTokenExhausted(true); }
        else { toast.error(errorDetail || "Free limit reached! Upgrade to Pro."); setShowPricing(true); }
      } else if (error.response?.status === 400) {
        toast.error(errorDetail || "Could not read audio file. Try MP3 or WAV format.");
      } else {
        toast.error(errorDetail || "Failed to analyze audio file");
      }
    } finally {
      setSpotifyAnalyzing(false);
    }
  };
  
  // Handle drag-and-drop for Music Analyzer
  const handleMusicAnalyzerDrop = (e) => {
    e.preventDefault();
    setMusicAnalyzerDragActive(false);
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      loadMusicAnalyzerFile(files[0]);
    }
  };
  
  const handleMusicAnalyzerDragOver = (e) => {
    e.preventDefault();
    setMusicAnalyzerDragActive(true);
  };
  
  const handleMusicAnalyzerDragLeave = (e) => {
    e.preventDefault();
    setMusicAnalyzerDragActive(false);
  };
  
  const handleMusicAnalyzerFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      loadMusicAnalyzerFile(file);
    }
  };
  
  const analyzeSpotifyTrack = async () => {
    if (!spotifyUrl.trim()) {
      toast.error("Please enter a style prompt or music link");
      return;
    }
    
    // Spotify temporarily unavailable until API approval is finalized
    if (spotifyUrl.includes('spotify.com') || spotifyUrl.includes('spotify:')) {
      toast.warning("🎧 Spotify analysis is under maintenance while we finalize API approval. Please paste a YouTube link instead — it works great!", { duration: 6000 });
      return;
    }
    
    setSpotifyAnalyzing(true);
    try {
      // Direct MP3 URL - load into preview player instead of link analysis
      if (/\.mp3(\?|$)/i.test(spotifyUrl.trim())) {
        if (analyzerAudioUrl && analyzerAudioSource === 'upload') {
          URL.revokeObjectURL(analyzerAudioUrl);
        }
        setMusicAnalyzerFile(null);
        setAnalyzerAudioUrl(spotifyUrl.trim());
        setAnalyzerAudioSource('url');
        setSpotifyAnalyzing(false);
        toast.success('🎧 MP3 loaded - preview below or download as WAV');
        return;
      }
      
      // Check if it's a URL or a style prompt text
      const isUrl = spotifyUrl.includes('http') || spotifyUrl.includes('spotify.com') || spotifyUrl.includes('youtube.com') || spotifyUrl.includes('youtu.be');
      
      if (isUrl) {
        // Use unified music link analyzer for both Spotify and YouTube
        const response = await fetch(`${API}/music/analyze`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            url: spotifyUrl,
            email: userEmail || getGuestEmail(),
            generate_prompts: true,
            prompt_count: 10
          })
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || "Failed to analyze track");
        }
        
        const data = await response.json();
        setSpotifyResult(data);
        setShowSpotifyModal(true);
        if (data.warning) {
          toast.warning(data.warning, { duration: 9000 });
        }
        // Note: audio preview is user-initiated only (click the 🎧 button) — no auto-generation
        const source = data.source === 'youtube' ? 'YouTube' : 'Spotify';
        toast.success(`Analyzed from ${source}: ${data.track?.name || 'Unknown track'} by ${(data.track?.artists || []).join(', ') || 'Unknown artist'}`);
      } else {
        // Style prompt text - generate 10 variations locally
        const basePrompt = spotifyUrl.trim();
        const variations = [
          { prompt: basePrompt, dna_code: "ORIGINAL" },
          { prompt: `${basePrompt}, male vocals, deep voice`, dna_code: "MALE-V1" },
          { prompt: `${basePrompt}, female vocals, soulful`, dna_code: "FEMALE-V1" },
          { prompt: `${basePrompt}, instrumental only, no vocals`, dna_code: "INST-V1" },
          { prompt: `${basePrompt}, faster tempo, high energy`, dna_code: "FAST-V1" },
          { prompt: `${basePrompt}, slower tempo, emotional`, dna_code: "SLOW-V1" },
          { prompt: `${basePrompt}, live instruments, organic feel`, dna_code: "LIVE-V1" },
          { prompt: `${basePrompt}, heavy bass, club ready`, dna_code: "BASS-V1" },
          { prompt: `${basePrompt}, atmospheric, spacey reverb`, dna_code: "ATMO-V1" },
          { prompt: `${basePrompt}, raw energy, aggressive`, dna_code: "RAW-V1" },
        ];
        
        // Show prompts inline (not in modal)
        setInlinePrompts(variations);
        // Auto-save to the user's Style Prompt Library (fire & forget)
        if (userEmail) {
          axios.post(`${API}/library/style-prompts`, {
            email: userEmail,
            prompts: variations,
            source: 'Prompt Variations'
          }).catch(() => {});
        }
        toast.success("Generated 10 style prompt variations!");
      }
    } catch (error) {
      console.error("Analysis error:", error);
      if (error.message.includes("not configured")) {
        toast.error("API not configured. Contact admin.");
      } else {
        toast.error(error.message || "Failed to analyze");
      }
    } finally {
      setSpotifyAnalyzing(false);
    }
  };
  
  const applySpotifyPromptToBeat = (prompt) => {
    setBeatPrompt(prompt);
    setShowSpotifyModal(false);
    toast.success("Prompt loaded! Now generate your beat.");
    // Scroll to beat generator
    document.querySelector('[data-testid="beat-generator"]')?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // HIT SONG FORMULA ANALYZER
  const analyzeHitSong = async () => {
    if (!hitSongInput.trim()) {
      toast.error("Please enter a song name or describe a hit song");
      return;
    }
    
    setIsAnalyzingHitSong(true);
    try {
      const response = await fetch(`${API}/analyze-hit-song`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          song_input: hitSongInput.trim(),
          email: userEmail || getGuestEmail()
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to analyze song");
      }
      
      const data = await response.json();
      setHitSongAnalysis(data);
      toast.success("Hit song formula analyzed!");
    } catch (error) {
      console.error("Hit song analysis error:", error);
      toast.error(error.message || "Failed to analyze hit song");
    } finally {
      setIsAnalyzingHitSong(false);
    }
  };
  
  // Copy fallback state (when clipboard is blocked)
  const [showCopyFallback, setShowCopyFallback] = useState(false);
  const [copyFallbackText, setCopyFallbackText] = useState("");
  
  // iOS Audio Unlock - needed for audio to play on iOS
  const [iosAudioUnlocked, setIosAudioUnlocked] = useState(false);
  
  useEffect(() => {
    // iOS requires user interaction to unlock audio
    const unlockAudio = () => {
      if (!iosAudioUnlocked) {
        // Create a silent audio context to unlock audio
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
          const audioCtx = new AudioContext();
          const buffer = audioCtx.createBuffer(1, 1, 22050);
          const source = audioCtx.createBufferSource();
          source.buffer = buffer;
          source.connect(audioCtx.destination);
          source.start(0);
          setIosAudioUnlocked(true);
          console.log('iOS audio unlocked');
        }
        // Remove listeners after unlock
        document.removeEventListener('touchstart', unlockAudio);
        document.removeEventListener('click', unlockAudio);
      }
    };
    
    document.addEventListener('touchstart', unlockAudio, { once: true });
    document.addEventListener('click', unlockAudio, { once: true });
    
    return () => {
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('click', unlockAudio);
    };
  }, [iosAudioUnlocked]);
  
  // iPhone Tutorial state - shows only once for mobile users
  const [showIphoneTutorial, setShowIphoneTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(1);
  const [tutorialAudioPlaying, setTutorialAudioPlaying] = useState(false);
  // tutorialAudioRef is declared earlier in the file
  
  // Tutorial narration scripts - EACH LANGUAGE WITH AUDIO
  const tutorialNarrations = {
    1: "Welcome to MANNIS. Create music in any language with AI-powered vocals.",
    2: "Bienvenido a MANNIS. Crea música en cualquier idioma.",
    3: "Bienvenue sur MANNIS. Créez de la musique dans n'importe quelle langue.",
    4: "欢迎来到MANNIS。用任何语言创作音乐。",
    5: "MANNISへようこそ。どんな言語でも音楽を作成できます。",
    6: "MANNIS에 오신 것을 환영합니다. 어떤 언어로든 음악을 만드세요.",
    7: "Bem-vindo ao MANNIS. Crie música em qualquer idioma.",
    8: "Willkommen bei MANNIS. Erstellen Sie Musik in jeder Sprache.",
    9: "Benvenuto su MANNIS. Crea musica in qualsiasi lingua.",
    10: "مرحبًا بك في MANNIS. أنشئ موسيقى بأي لغة.",
    11: "MANNIS में आपका स्वागत है। किसी भी भाषा में संगीत बनाएं।",
    12: "Karibu MANNIS. Tengeneza muziki kwa lugha yoyote.",
    13: "Kaabo si MANNIS. Ṣẹda orin ni ede eyikeyi.",
    14: "Your music. Any language. MANNIS."
  };
  
  const tutorialLanguages = {
    1: { flag: '🇺🇸', name: 'English' },
    2: { flag: '🇪🇸', name: 'Español' },
    3: { flag: '🇫🇷', name: 'Français' },
    4: { flag: '🇨🇳', name: '中文' },
    5: { flag: '🇯🇵', name: '日本語' },
    6: { flag: '🇰🇷', name: '한국어' },
    7: { flag: '🇧🇷', name: 'Português' },
    8: { flag: '🇩🇪', name: 'Deutsch' },
    9: { flag: '🇮🇹', name: 'Italiano' },
    10: { flag: '🇸🇦', name: 'العربية' },
    11: { flag: '🇮🇳', name: 'हिन्दी' },
    12: { flag: '🇰🇪', name: 'Kiswahili' },
    13: { flag: '🇳🇬', name: 'Yoruba' },
    14: { flag: '🌍', name: 'All Languages' }
  };
  
  // Play tutorial narration when step changes - auto-advance through languages
  useEffect(() => {
    if (showIphoneTutorial && tutorialNarrations[tutorialStep]) {
      playTutorialNarration(tutorialStep);
    }
    return () => {
      if (tutorialAudioRef.current) {
        tutorialAudioRef.current.pause();
      }
    };
  }, [tutorialStep, showIphoneTutorial]);
  
  const playTutorialNarration = async (step) => {
    const text = tutorialNarrations[step];
    if (!text) return;
    
    setTutorialAudioPlaying(true);
    
    // Fallback timer - 4 seconds per language
    const fallbackTimer = setTimeout(() => {
      setTutorialAudioPlaying(false);
      if (step < 14 && showIphoneTutorial) {
        setTutorialStep(prev => prev + 1);
      }
    }, 4000);
    
    try {
      const res = await fetch(`${API}/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: text,
          voice_id: 'CwhRBWXzGAHq8TQ4Fs17',
          model_id: 'eleven_multilingual_v2'
        })
      });
      
      if (res.status === 429) {
        toast.error('Too many requests, please wait a moment');
        clearTimeout(fallbackTimer);
        setTutorialAudioPlaying(false);
        return;
      }
      
      if (res.ok) {
        const data = await res.json();
        
        if (!tutorialAudioRef.current) {
          tutorialAudioRef.current = new Audio();
        }
        
        tutorialAudioRef.current.src = data.audio_url;
        tutorialAudioRef.current.onended = () => {
          clearTimeout(fallbackTimer);
          setTutorialAudioPlaying(false);
          if (step < 14) {
            setTimeout(() => setTutorialStep(prev => prev + 1), 300);
          }
        };
        await tutorialAudioRef.current.play();
        clearTimeout(fallbackTimer);
      }
    } catch (err) {
      console.error('Tutorial narration error:', err);
    }
  };
  
  // Check if user is on mobile and hasn't seen tutorial
  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const hasSeenTutorial = localStorage.getItem('sounddna_tutorial_seen');
    if (isMobile && !hasSeenTutorial) {
      // Show tutorial after a short delay
      setTimeout(() => setShowIphoneTutorial(true), 1500);
    }
  }, []);
  
  // Fetch ElevenLabs voices on mount
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        // Fetch regular speech voices
        const res = await fetch(`${API}/voices`);
        if (res.ok) {
          const data = await res.json();
          setElevenVoices(data.voices || []);
          if (data.voices?.length > 0) {
            setElevenSelectedVoice(data.voices[0].voice_id);
          }
        }
        
        // Fetch professional singing voices
        const singingRes = await fetch(`${API}/singing-voices`);
        if (singingRes.ok) {
          const singingData = await singingRes.json();
          setSingingVoices(singingData.voices || []);
        }
      } catch (err) {
        console.log("ElevenLabs voices not available");
      }
    };
    fetchVoices();
  }, []);
  
  // Fetch Vocal Library on mount and when userEmail changes
  useEffect(() => {
    const fetchVocalLibrary = async () => {
      if (!userEmail) return;
      try {
        const res = await fetch(`${API}/vocal-library/${encodeURIComponent(userEmail)}`);
        if (res.ok) {
          const data = await res.json();
          setVocalLibrary(data.vocals || []);
        }
      } catch (err) {
        console.log("Could not fetch vocal library");
      }
    };
    fetchVocalLibrary();
  }, [userEmail]);
  
  // Fetch uploads when My Uploads panel is opened
  useEffect(() => {
    if (fullscreenTool === 'my-uploads') {
      fetchMyUploads(uploadCategory);
      fetchStorageStatus();
    }
  }, [fullscreenTool]);
  
  const completeTutorial = () => {
    // Stop any playing audio
    if (tutorialAudioRef.current) {
      tutorialAudioRef.current.pause();
    }
    setTutorialAudioPlaying(false);
    localStorage.setItem('sounddna_tutorial_seen', 'true');
    setShowIphoneTutorial(false);
    setTutorialStep(1);
  };
  
  // Suno Export Modal state (shows Style + Lyrics separately)
  const [showSunoExport, setShowSunoExport] = useState(false);
  const [sunoExportStyle, setSunoExportStyle] = useState("");
  const [sunoExportLyrics, setSunoExportLyrics] = useState("");
  const [sunoStyleCopied, setSunoStyleCopied] = useState(false);
  const [sunoLyricsCopied, setSunoLyricsCopied] = useState(false);
  
  // ============================================
  // MANNIS CREATOR - Original Music Creation Interface
  // ============================================
  const [creatorTab, setCreatorTab] = useState('create'); // 'create' or 'workspace'
  const [creatorLyrics, setCreatorLyrics] = useState('');
  const [creatorStyles, setCreatorStyles] = useState([]);
  const [creatorTitle, setCreatorTitle] = useState('');
  const [creatorInstrumental, setCreatorInstrumental] = useState(false);
  const [creatorWorkspace, setCreatorWorkspace] = useState(() => {
    try {
      const saved = localStorage.getItem('sounddna_creator_workspace');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  
  // ============================================
  // MANNIS PLAYER - Custom Audio Player
  // ============================================
  const [playerCurrentSong, setPlayerCurrentSong] = useState(null);
  const [playerIsPlaying, setPlayerIsPlaying] = useState(false);
  const [playerProgress, setPlayerProgress] = useState(0);
  const [playerDuration, setPlayerDuration] = useState(0);
  const [playerVolume, setPlayerVolume] = useState(0.8);
  const [playerMuted, setPlayerMuted] = useState(false);
  const [playerShuffle, setPlayerShuffle] = useState(false);
  const [playerRepeat, setPlayerRepeat] = useState(false);
  const playerAudioRef = useRef(null);
  
  // Player controls
  const playerTogglePlay = () => {
    if (playerAudioRef.current) {
      if (playerIsPlaying) {
        playerAudioRef.current.pause();
      } else {
        playerAudioRef.current.play();
      }
      setPlayerIsPlaying(!playerIsPlaying);
    }
  };
  
  const playerSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * playerDuration;
    if (playerAudioRef.current) {
      playerAudioRef.current.currentTime = newTime;
      setPlayerProgress(newTime);
    }
  };
  
  const playerSetVolume = (value) => {
    setPlayerVolume(value);
    if (playerAudioRef.current) {
      playerAudioRef.current.volume = value;
    }
    if (value === 0) {
      setPlayerMuted(true);
    } else {
      setPlayerMuted(false);
    }
  };
  
  const playerToggleMute = () => {
    if (playerAudioRef.current) {
      if (playerMuted) {
        playerAudioRef.current.volume = playerVolume || 0.8;
        setPlayerMuted(false);
      } else {
        playerAudioRef.current.volume = 0;
        setPlayerMuted(true);
      }
    }
  };
  
  const playerPlaySong = (song) => {
    setPlayerCurrentSong(song);
    setPlayerIsPlaying(true);
    setPlayerProgress(0);
    // Simulate duration for demo (3:30 = 210 seconds)
    setPlayerDuration(210);
  };
  
  const playerNextSong = () => {
    if (creatorWorkspace.length > 0 && playerCurrentSong) {
      const currentIndex = creatorWorkspace.findIndex(s => s.id === playerCurrentSong.id);
      const nextIndex = playerShuffle 
        ? Math.floor(Math.random() * creatorWorkspace.length)
        : (currentIndex + 1) % creatorWorkspace.length;
      playerPlaySong(creatorWorkspace[nextIndex]);
    }
  };
  
  const playerPrevSong = () => {
    if (creatorWorkspace.length > 0 && playerCurrentSong) {
      const currentIndex = creatorWorkspace.findIndex(s => s.id === playerCurrentSong.id);
      const prevIndex = currentIndex === 0 ? creatorWorkspace.length - 1 : currentIndex - 1;
      playerPlaySong(creatorWorkspace[prevIndex]);
    }
  };
  
  const formatPlayerTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Save workspace to localStorage
  useEffect(() => {
    localStorage.setItem('sounddna_creator_workspace', JSON.stringify(creatorWorkspace));
  }, [creatorWorkspace]);
  
  // Creator style options
  const creatorStyleOptions = [
    'honky tonk', 'nachdenklich', 'poétique', 'symphonic black metal', 'tambores',
    'dark trap', 'melodic R&B', 'gospel soul', 'drill beat', 'lo-fi chill',
    'afrobeats', 'jazz fusion', 'pop anthem', 'cinematic orchestral', 'reggaeton',
    'neo soul', 'trap soul', 'boom bap', 'UK garage', 'house'
  ];
  
  // Add song to workspace
  const addToWorkspace = () => {
    if (!creatorStyles.length && !creatorLyrics.trim()) {
      toast.error("Add some styles or lyrics first!");
      return;
    }
    
    const newSong = {
      id: Date.now(),
      title: creatorTitle || `Untitled ${creatorWorkspace.length + 1}`,
      styles: [...creatorStyles],
      lyrics: creatorLyrics,
      instrumental: creatorInstrumental,
      createdAt: new Date().toISOString(),
      color: ['#ec4899', '#9F47FF', '#3b82f6', '#9F47FF', '#f59e0b', '#ef4444'][Math.floor(Math.random() * 6)]
    };
    
    setCreatorWorkspace(prev => [newSong, ...prev]);
    toast.success(`"${newSong.title}" saved to workspace!`);
    
    // Clear form
    setCreatorTitle('');
    setCreatorLyrics('');
    setCreatorStyles([]);
    setCreatorInstrumental(false);
  };
  
  // Export song to Suno
  const exportCreatorToSuno = (song = null) => {
    const styles = song ? song.styles : creatorStyles;
    const lyrics = song ? song.lyrics : creatorLyrics;
    const instrumental = song ? song.instrumental : creatorInstrumental;
    
    if (!styles.length && !lyrics.trim()) {
      toast.error("Add some styles or lyrics first!");
      return;
    }
    
    const styleText = styles.join(', ');
    let fullContent;
    
    if (instrumental || !lyrics.trim()) {
      fullContent = `STYLE:\n${styleText}\n\n[Instrumental]`;
    } else {
      fullContent = `STYLE:\n${styleText}\n\nLYRICS:\n${lyrics}`;
    }
    
    universalCopy(fullContent, '✅ COPIED! Paste into Suno');
  };
  
  // Delete from workspace
  const deleteFromWorkspace = (id) => {
    setCreatorWorkspace(prev => prev.filter(s => s.id !== id));
    toast.success("Removed from workspace");
  };
  
  // Load song into editor
  const loadToEditor = (song) => {
    setCreatorTitle(song.title);
    setCreatorStyles(song.styles);
    setCreatorLyrics(song.lyrics);
    setCreatorInstrumental(song.instrumental);
    setCreatorTab('create');
    toast.success(`Loaded "${song.title}" into editor`);
  };
  
  // Universal Copy Utility - works even when Clipboard API is blocked
  const universalCopy = async (text, successMessage = "Copied to clipboard!") => {
    let copied = false;
    
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // Try modern Clipboard API first (works on iOS Safari 13.4+)
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        toast.success(successMessage);
        copied = true;
        return true;
      } catch (e) {
        console.log("Clipboard API failed, trying fallback...", e);
      }
    }
    
    // Fallback method using execCommand (may work on older browsers)
    if (!copied) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.top = '-9999px';
      textarea.style.opacity = '0';
      textarea.setAttribute('readonly', '');
      document.body.appendChild(textarea);
      
      // For iOS, we need to use a different selection method
      if (isIOS) {
        const range = document.createRange();
        range.selectNodeContents(textarea);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        textarea.setSelectionRange(0, 999999);
      } else {
        textarea.focus();
        textarea.select();
        textarea.setSelectionRange(0, text.length);
      }
      
      try {
        const success = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (success) {
          toast.success(successMessage);
          return true;
        }
      } catch (err) {
        document.body.removeChild(textarea);
        console.log("execCommand failed:", err);
      }
    }
    
    // If all else fails, show the text so user can copy manually
    toast.info("📋 Tap & hold the text to copy manually", { duration: 5000 });
    setCopyFallbackText(text);
    setShowCopyFallback(true);
    return false;
  };

  // Format content for Suno with Style Prompt + Lyrics
  const createSunoFormat = (lyrics, styleHint = "") => {
    // Create a style prompt based on rhymeStyle or a default
    const stylePrompt = styleHint || rhymeStyle || "Modern trap, 130 BPM, hard-hitting 808s, dark piano, male vocals, confident delivery";
    
    return `[STYLE]
${stylePrompt}

[LYRICS]
${lyrics}`;
  };

  // Copy to Suno with Style + Lyrics (generic)
  const copyToSuno = (lyrics, styleHint = "") => {
    if (!lyrics) {
      toast.error("Generate content first!");
      return;
    }
    const sunoContent = createSunoFormat(lyrics, styleHint);
    universalCopy(sunoContent, "✅ COPIED! Tap & hold in Suno to paste");
  };

  // Parse combined input to extract Style and Lyrics separately
  const parseCombinedInput = (combinedText) => {
    let style = "";
    let lyrics = "(Instrumental)";
    
    // Try to find [Style of Music] and [Lyrics] sections
    const styleMatch = combinedText.match(/\[Style of Music\]([\s\S]*?)(?=\[Lyrics\]|$)/i);
    const lyricsMatch = combinedText.match(/\[Lyrics\]([\s\S]*?)$/i);
    
    if (styleMatch) {
      style = styleMatch[1].trim();
    }
    if (lyricsMatch) {
      lyrics = lyricsMatch[1].trim() || "(Instrumental)";
    }
    
    // If no sections found, treat entire text as style
    if (!styleMatch && !lyricsMatch) {
      style = combinedText.trim();
    }
    
    return { style, lyrics };
  };

  // Generate structured Suno prompt with vocals and sections
  const generateStructuredPrompt = () => {
    // Build vocal harmony string
    const vocalParts = [];
    if (structureVocals.soprano) vocalParts.push("soprano");
    if (structureVocals.alto) vocalParts.push("alto");
    if (structureVocals.tenor) vocalParts.push("tenor");
    if (structureVocals.bass) vocalParts.push("bass");
    if (structureVocals.twoOctavesDown) vocalParts.push("two octaves down");
    if (structureVocals.harmony) vocalParts.push("six-part vocal harmony");
    
    const vocalString = vocalParts.length > 0 
      ? `Vocals: ${vocalParts.join(", ")}` 
      : "";
    
    // Build style with vocals
    let stylePrompt = structureBaseStyle;
    if (vocalString) {
      stylePrompt += `, ${vocalString}`;
    }
    
    // Add chord/key info to style if enabled
    if (structureChords.enabled) {
      stylePrompt += `, Key of ${structureChords.key} ${structureChords.mode}`;
    }
    
    // Build lyrics with sections and chord changes
    let lyrics = "";
    
    if (structureSections.intro.enabled) {
      let introText = `[Intro]\n(${structureSections.intro.instruction})`;
      if (structureChords.enabled && structureChords.intro) {
        introText += `\n(Chords: ${structureChords.intro})`;
      }
      lyrics += introText + "\n\n";
    }
    
    if (structureSections.verse.enabled) {
      let verseText = `[Verse 1]\n(${structureSections.verse.instruction})`;
      if (structureChords.enabled && structureChords.verse) {
        verseText += `\n(Chords: ${structureChords.verse})`;
      }
      lyrics += verseText + "\n\n";
    }
    
    if (structureSections.preChorus.enabled) {
      let preChorusText = `[Pre-Chorus]\n(${structureSections.preChorus.instruction})`;
      if (structureChords.enabled && structureChords.preChorus) {
        preChorusText += `\n(Chords: ${structureChords.preChorus})`;
      }
      lyrics += preChorusText + "\n\n";
    }
    
    if (structureSections.chorus.enabled) {
      let chorusText = `[Chorus]\n(${structureSections.chorus.instruction})`;
      if (structureChords.enabled && structureChords.chorus) {
        chorusText += `\n(Chords: ${structureChords.chorus})`;
      }
      lyrics += chorusText + "\n\n";
    }
    
    if (structureSections.verse.enabled) {
      let verse2Text = `[Verse 2]\n(${structureSections.verse.instruction})`;
      if (structureChords.enabled && structureChords.verse) {
        verse2Text += `\n(Chords: ${structureChords.verse})`;
      }
      lyrics += verse2Text + "\n\n";
    }
    
    if (structureSections.preChorus.enabled) {
      let preChorus2Text = `[Pre-Chorus]\n(${structureSections.preChorus.instruction})`;
      if (structureChords.enabled && structureChords.preChorus) {
        preChorus2Text += `\n(Chords: ${structureChords.preChorus})`;
      }
      lyrics += preChorus2Text + "\n\n";
    }
    
    if (structureSections.chorus.enabled) {
      let chorus2Text = `[Chorus]\n(${structureSections.chorus.instruction}, exciting, powerful)`;
      if (structureChords.enabled && structureChords.chorus) {
        chorus2Text += `\n(Chords: ${structureChords.chorus})`;
      }
      lyrics += chorus2Text + "\n\n";
    }
    
    if (structureSections.bridge.enabled) {
      let bridgeText = `[Bridge]\n(${structureSections.bridge.instruction})`;
      if (structureChords.enabled && structureChords.bridge) {
        bridgeText += `\n(Chords: ${structureChords.bridge})`;
      }
      lyrics += bridgeText + "\n\n";
    }
    
    if (structureSections.chorus.enabled) {
      let finalChorusText = `[Final Chorus]\n(${structureSections.chorus.instruction}, climactic, full harmony)`;
      if (structureChords.enabled && structureChords.chorus) {
        finalChorusText += `\n(Chords: ${structureChords.chorus})`;
      }
      lyrics += finalChorusText + "\n\n";
    }
    
    if (structureSections.outro.enabled) {
      let outroText = `[Outro]\n(${structureSections.outro.instruction})`;
      if (structureChords.enabled && structureChords.outro) {
        outroText += `\n(Chords: ${structureChords.outro})`;
      }
      lyrics += outroText + "\n";
    }
    
    return { style: stylePrompt, lyrics: lyrics.trim() };
  };

  // Open Music AI Export Modal with parsed Style and Lyrics
  const openSunoExportFromCombined = (combinedText) => {
    if (!combinedText || !combinedText.trim()) {
      toast.error("Enter Style + Lyrics first!");
      return;
    }
    const { style, lyrics } = parseCombinedInput(combinedText);
    setSunoExportStyle(style);
    setSunoExportLyrics(lyrics);
    setSunoStyleCopied(false);
    setSunoLyricsCopied(false);
    setShowSunoExport(true);
  };

  // Open Music AI Export Modal (shows Style + Lyrics separately for Suno's two-field interface)
  const openSunoExport = (style, lyrics) => {
    setSunoExportStyle(style || "");
    setSunoExportLyrics(lyrics || "(Instrumental)");
    setSunoStyleCopied(false);
    setSunoLyricsCopied(false);
    setShowSunoExport(true);
  };

  // Copy ONLY Style Prompt to Suno (for Demo Creator - instrumentals)
  const copyStyleToSuno = (stylePrompt) => {
    if (!stylePrompt || !stylePrompt.trim()) {
      toast.error("Enter a style prompt first!");
      return;
    }
    // Open the export modal with style and instrumental lyrics
    openSunoExport(stylePrompt, "(Instrumental)");
  };

  // Copy STYLE + LYRICS together to Suno (for Rhyme/Flow Engine & DNA Lyrics)
  const copyLyricsToSuno = (lyrics, style = "") => {
    if (!lyrics || !lyrics.trim()) {
      toast.error("Generate lyrics first!");
      return;
    }
    // Use provided style or default based on context
    const stylePrompt = style || rhymeStyle || lyricsGenre || "Modern trap, 130 BPM, hard-hitting 808s, emotional vocals";
    
    // Open the export modal with both style and lyrics
    openSunoExport(stylePrompt, lyrics);
  };

  // Save beat history to localStorage
  const saveBeatToHistory = (beat) => {
    const newHistory = [beat, ...beatHistory.slice(0, 9)];
    setBeatHistory(newHistory);
    try {
      localStorage.setItem('sounddna_beat_history', JSON.stringify(newHistory));
    } catch (e) {
      // Storage full or unavailable
    }
  };
  
  // BULLETPROOF Beat Generation
  const generateBeat = async () => {
    const prompt = beatPrompt?.trim();
    if (!prompt) {
      toast.error("Enter a beat description first!");
      return;
    }
    
    // Check generation limits (purchased tokens can cover overage)
    if (subscription.generations_remaining <= 0 && (currentUser?.token_balance || 0) <= 0) {
      if (currentUser) {
        setShowTokenExhausted(true);
      } else {
        toast.error(`Generation limit reached (${subscription.generations_limit}/month). Upgrade your plan!`);
        setShowPricing(true);
      }
      return;
    }
    
    // Reset state
    setBeatGenerating(true);
    setBeatError(null);
    setBeatAudioUrl(null);
    setBeatAudioUrlB(null);
    setBeatProgress(0);
    setBeatStatus("starting");
    setActiveBeatVersion("A");
    setGenWidget({ kind: 'beat', label: 'Creating your beat', progress: 5, eta: 120, status: 'working' });
    
    const hasLyrics = beatLyrics?.trim().length > 0;
    
    try {
      // Step 1: Start generation
      const loadingMsg = hasLyrics ? "Starting vocal generation..." : "Starting beat generation...";
      toast.loading(loadingMsg, { id: "beat-toast" });
      
      const requestBody = {
        prompt: prompt,
        duration: beatDuration,
        email: userEmail || "guest"
      };
      
      // Add lyrics if provided
      if (hasLyrics) {
        requestBody.lyrics = beatLyrics.trim();
      }
      
      const startResponse = await fetch(`${API}/generate-beat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody)
      });
      
      const startData = await startResponse.json();
      
      if (!startResponse.ok) {
        throw new Error(startData.detail || "Failed to start. Try again.");
      }
      
      if (!startData.task_id) {
        throw new Error("No task ID returned. Try again.");
      }
      
      const taskId = startData.task_id;
      setBeatStatus("processing");
      const processingMsg = hasLyrics 
        ? "🎤 Creating song with vocals (~2 min)..." 
        : "🎵 Creating your beat (~2 min)...";
      toast.loading(processingMsg, { id: "beat-toast" });
      
      // Step 2: Poll using setInterval
      // MiniMax takes ~2 minutes
      let attempts = 0;
      const maxAttempts = 300; // 5 min max timeout
      
      const pollInterval = setInterval(async () => {
        attempts++;
        // Realistic progress: crawl to 95% over ~2 minutes (120 attempts)
        const progress = Math.min(95, Math.floor((attempts / 120) * 95));
        setBeatProgress(progress);
        setGenWidget(prev => prev && prev.kind === 'beat' && prev.status === 'working'
          ? { ...prev, progress, eta: Math.max(5, 120 - attempts) } : prev);
        
        try {
          const statusResponse = await fetch(`${API}/beat-status/${taskId}`);
          const statusText = await statusResponse.text();
          
          if (!statusText) return;
          
          let statusData;
          try {
            statusData = JSON.parse(statusText);
          } catch (e) {
            return; // Keep polling
          }
          
          if (statusData.detail === "Task not found") return;
          
          // SUCCESS - Handle completed beat (A and/or B versions)
          if (statusData.status === "completed" && (statusData.audio_url || statusData.audio_url_a)) {
            clearInterval(pollInterval);
            
            const audioUrlA = statusData.audio_url_a || statusData.audio_url;
            const audioUrlB = statusData.audio_url_b || null;
            
            setBeatAudioUrl(audioUrlA);
            setBeatAudioUrlB(audioUrlB);
            setBeatProgress(100);
            setBeatStatus("completed");
            setBeatGenerating(false);
            setActiveBeatVersion("A");
            
            const absBeatUrl = audioUrlA && audioUrlA.startsWith('/api/')
              ? `${API_ORIGIN}${audioUrlA}` : audioUrlA;
            setGenWidget({ kind: 'beat', label: 'Your beat', progress: 100, eta: 0, status: 'ready', resultUrl: absBeatUrl });
            genWidgetAutoPlay(absBeatUrl);
            
            saveBeatToHistory({
              id: taskId,
              prompt: prompt,
              duration: beatDuration,
              audioUrl: audioUrlA,
              audioUrlB: audioUrlB,
              createdAt: new Date().toISOString()
            });
            
            // Refresh beat library to show the new song and auto-open it
            if (userEmail) {
              fetchBeatLibrary();
              setBeatLibraryOpen(true);
            }
            
            const successMsg = hasLyrics 
              ? "🎤 Song ready! Saved to MY BEAT LIBRARY below ⬇️" 
              : "🎵 Beat ready! Saved to MY BEAT LIBRARY below ⬇️";
            toast.success(successMsg, { id: "beat-toast", duration: 5000 });
            return;
          }
          
          // FAILED
          if (statusData.status === "failed") {
            clearInterval(pollInterval);
            setBeatError(statusData.error || "Generation failed");
            setBeatStatus("failed");
            setBeatGenerating(false);
            setGenWidget(null);
            toast.error("Generation failed. Try again.", { id: "beat-toast" });
            return;
          }
          
          // TIMEOUT
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setBeatError("Taking too long. Please try again.");
            setBeatStatus("failed");
            setBeatGenerating(false);
            setGenWidget(null);
            toast.error("Timed out. Try again.", { id: "beat-toast" });
          }
          
        } catch (pollError) {
          // Network error - keep trying unless max attempts
          if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setBeatError("Connection error");
            setBeatStatus("failed");
            setBeatGenerating(false);
          }
        }
      }, 1000);
      
    } catch (error) {
      console.error("Beat generation error:", error);
      setBeatError(error.message || "Something went wrong");
      setBeatStatus("failed");
      setBeatGenerating(false);
      setGenWidget(null);
      toast.error(error.message || "Generation failed", { id: "beat-toast" });
    }
  };
  
  // Retry generation
  const retryBeat = () => {
    setBeatError(null);
    setBeatStatus("idle");
    generateBeat();
  };
  
  // Play beat from history
  const playFromHistory = (beat) => {
    setBeatAudioUrl(beat.audioUrl);
    setBeatAudioUrlB(beat.audioUrlB || null);
    setBeatPrompt(beat.prompt);
    setBeatStatus("completed");
    setActiveBeatVersion("A");
    toast.success("Loaded from history!");
  };
  
  // Download beat - handles cross-origin URLs with Save As dialog
  const downloadBeat = async (url, filename) => {
    try {
      toast.loading("Preparing download...", { id: "download-toast" });
      const finalFilename = filename || `sounddna-beat-${Date.now()}.mp3`;
      let fullUrl = url;
      if (fullUrl.startsWith('/api/')) fullUrl = `${API_ORIGIN}${fullUrl}`;
      const response = await fetch(fullUrl);
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      toast.dismiss("download-toast");
      await shareOrDownloadBlob(blob, finalFilename, blob.type || 'audio/mpeg', {
        sourceUrl: fullUrl, desktopPicker: true, successMessage: "Download started!",
      });
    } catch (error) {
      console.error("Download error:", error);
      window.open(url, '_blank');
      toast.success("Opening in new tab - right-click to save", { id: "download-toast" });
    }
  };

  // Save a WAV directly to the device — NEVER opens the iOS Share Sheet.
  // iOS: server proxy with Content-Disposition attachment → lands in the Files app.
  // Desktop: fetch to Blob → silent download. Error toast on failure.
  const saveWavFromUrl = async (audioUrl, filename) => {
    if (!audioUrl) { toast.error('No audio to download'); return; }
    const src = audioUrl.startsWith('/') ? `${API_ORIGIN}${audioUrl}` : audioUrl;
    const proxyUrl = `${API}/download-audio-proxy?url=${encodeURIComponent(src)}&filename=${encodeURIComponent(filename)}`;
    try {
      if (isIOSDevice()) {
        // iOS: navigate a same-origin link whose response is Content-Disposition: attachment.
        // Safari downloads it to Files ▸ Downloads and STAYS in the app (no Share Sheet
        // activation race, no fullscreen player). Done synchronously in the tap gesture.
        const link = document.createElement('a');
        link.href = proxyUrl;
        link.download = filename;
        link.rel = 'noopener';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => { if (link.parentNode) document.body.removeChild(link); }, 200);
        toast.success('📥 Saving to Files ▸ Downloads…', { duration: 6000 });
        return;
      }
      // Desktop: fetch into a Blob and download silently.
      toast.loading('📥 Saving WAV…', { id: 'wav-dl' });
      const resp = await fetch(proxyUrl);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { if (a.parentNode) document.body.removeChild(a); URL.revokeObjectURL(blobUrl); }, 100);
      toast.dismiss('wav-dl');
      toast.success('✅ WAV saved');
    } catch (error) {
      toast.dismiss('wav-dl');
      console.error('WAV download failed:', error);
      toast.error('Download failed — please try again');
    }
  };

  // Custom Audio Player Controls (for beat player)
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const togglePlayPause = () => {
    const audio = beatAudioRef.current;
    if (!audio) return;
    
    if (playerIsPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setPlayerIsPlaying(!playerIsPlaying);
  };

  const handleSeek = (e) => {
    const audio = beatAudioRef.current;
    if (!audio) return;
    
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const percent = clickX / width;
    const newTime = percent * audio.duration;
    
    audio.currentTime = newTime;
    setPlayerProgress(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    playerSetVolume(newVolume);
  };

  const toggleMute = () => {
    playerToggleMute();
  };

  const handleTimeUpdate = () => {
    const audio = beatAudioRef.current;
    if (audio) {
      setPlayerProgress(audio.currentTime);
      setPlayerDuration(audio.duration || 0);
    }
  };

  const handleLoadedMetadata = () => {
    const audio = beatAudioRef.current;
    if (audio) {
      setPlayerDuration(audio.duration);
    }
  };

  const handleAudioEnded = () => {
    setPlayerIsPlaying(false);
    setPlayerProgress(0);
  };
  
  // Generate ElevenLabs TTS
  const generateElevenLabsTTS = async () => {
    if (!elevenText.trim()) {
      toast.error("Enter text to generate voice!");
      return;
    }
    if (!elevenSelectedVoice) {
      toast.error("Select a voice first!");
      return;
    }
    
    setElevenGenerating(true);
    setElevenError(null);
    setElevenAudioUrl(null);
    
    try {
      const res = await fetch(`${API}/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: elevenText,
          voice_id: elevenSelectedVoice,
          stability: 0.5,
          similarity_boost: 0.75,
          singing_mode: elevenSingingMode  // Pass singing mode
        })
      });
      
      if (res.status === 429) {
        toast.error('Too many requests, please wait a moment');
        setElevenGenerating(false);
        return;
      }
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || "Failed to generate voice");
      }
      
      setElevenAudioUrl(data.audio_url);
      toast.success(elevenSingingMode ? "🎤 Singing vocals generated!" : "Voice generated!");
    } catch (err) {
      console.error("TTS Error:", err);
      const errorMsg = err.message || "Failed to generate voice";
      if (errorMsg.includes("unusual_activity") || errorMsg.includes("Free Tier")) {
        setElevenError("ElevenLabs requires a paid plan for this feature. Upgrade at elevenlabs.io");
      } else {
        setElevenError(errorMsg);
      }
      toast.error("Voice generation failed");
    } finally {
      setElevenGenerating(false);
    }
  };
  
  // Generate singing vocals using MiniMax
  const generateVocals = async () => {
    if (!vocalLyrics.trim()) {
      toast.error("Enter lyrics to generate vocals!");
      return;
    }
    
    setVocalGenerating(true);
    setVocalError(null);
    setVocalAudioUrl(null);
    setVocalProgress(0);
    setGenWidget({ kind: 'vocals', label: 'Generating vocals', progress: 5, eta: 45, status: 'working' });
    
    // Auto-add vocal style and lyrics to workflow for Suno export
    const vocalStyleDescriptions = {
      "R&B": "smooth R&B vocals, silky melismatic runs, sensual neo-soul delivery",
      "Pop": "polished pop vocals, bright catchy melodic delivery",
      "Trap": "melodic trap vocals with autotune, emotional hip-hop style",
      "Rock": "powerful rock vocals, raw belting",
      "Country": "country vocals with twang, heartfelt",
      "Jazz": "smooth jazz vocals, sophisticated phrasing",
      "Gospel": "powerful gospel vocals, spiritual belting",
      "Afrobeat": "energetic Afrobeats vocals, rhythmic African style",
      "Afropop": "melodic Afropop vocals, vibrant hooks",
      "Amapiano": "laid-back Amapiano vocals, South African groove",
      "Reggae": "reggae vocals, Caribbean style",
      "Latin": "passionate Latin vocals, reggaeton style",
      "Metal": "aggressive metal vocals",
      "Blues": "soulful blues vocals",
      "Soul": "warm neo-soul vocals",
      "Funk": "groovy funk vocals",
      "Drill": "aggressive UK drill vocals",
      "Ballad": "emotional ballad vocals"
    };
    const styleDesc = vocalStyleDescriptions[vocalStyle] || `${vocalStyle} vocals`;
    const vocalPromptForWorkflow = `${vocalGender} singer, ${styleDesc}`;
    
    updateWorkflow({ 
      voiceSample: vocalPromptForWorkflow,
      genre: vocalStyle,
      lyrics: vocalLyrics,
      source: 'vocal-generator'
    });
    
    try {
      const res = await fetch(`${API}/generate-vocals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lyrics: vocalLyrics,
          vocal_style: vocalStyle,
          gender: vocalGender,
          tempo: vocalTempo,
          email: userEmail || getGuestEmail()
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || "Failed to start vocal generation");
      }
      
      setVocalTaskId(data.task_id);
      toast.success("🎤 Generating singing vocals...");
      
      // Poll for status
      let vocalAttempts = 0;
      const pollInterval = setInterval(async () => {
        try {
          vocalAttempts++;
          const statusRes = await fetch(`${API}/vocal-status/${data.task_id}`);
          const statusData = await statusRes.json();
          
          const vp = statusData.progress || Math.min(90, vocalAttempts * 8);
          setVocalProgress(statusData.progress || 0);
          setGenWidget(prev => prev && prev.kind === 'vocals' && prev.status === 'working'
            ? { ...prev, progress: vp, eta: Math.max(3, 45 - vocalAttempts * 3) } : prev);
          
          if (statusData.status === 'completed') {
            clearInterval(pollInterval);
            let audioUrl = statusData.audio_url;
            if (audioUrl && audioUrl.startsWith('/api/')) {
              audioUrl = `${API_ORIGIN}${audioUrl}`;
            }
            setVocalAudioUrl(audioUrl);
            setVocalGenerating(false);
            setGenWidget({ kind: 'vocals', label: 'Vocals', progress: 100, eta: 0, status: 'ready', resultUrl: audioUrl });
            genWidgetAutoPlay(audioUrl);
            toast.success("🎤 Singing vocals ready!");
            
            // Refresh vocal library and auto-expand to show the new entry
            try {
              const libRes = await fetch(`${API}/vocal-library/${encodeURIComponent(userEmail)}`);
              if (libRes.ok) {
                const libData = await libRes.json();
                setVocalLibrary(libData.vocals || []);
                setVocalLibraryExpanded(true);
              }
            } catch (e) {
              console.log("Could not refresh vocal library");
            }
          } else if (statusData.status === 'failed') {
            clearInterval(pollInterval);
            setVocalError(statusData.error || "Generation failed");
            setVocalGenerating(false);
            toast.error("Vocal generation failed");
          }
        } catch (err) {
          console.error("Poll error:", err);
        }
      }, 3000);
      
      // Store interval ID for cleanup
      const timeoutId = setTimeout(() => {
        clearInterval(pollInterval);
        setVocalGenerating(prev => {
          if (prev) {
            setVocalError("Generation timed out. Please try again.");
            setGenWidget(null);
            return false;
          }
          return prev;
        });
      }, 300000);
      
      // Store for cleanup
      return () => {
        clearInterval(pollInterval);
        clearTimeout(timeoutId);
      };
      
    } catch (err) {
      console.error("Vocal gen error:", err);
      setVocalError(err.message);
      setVocalGenerating(false);
      setGenWidget(null);
      toast.error("Failed to generate vocals");
    }
  };
  
  // Style presets for LeShore generator
  const LESHORE_STYLES = [
    "dark trap", "melodic R&B", "gospel soul", "drill beat", "lo-fi chill",
    "hard rock", "smooth jazz", "country ballad", "EDM festival", "acoustic folk",
    "hip hop classic", "pop anthem", "afrobeat groove", "reggaeton", "indie alternative",
    "synth wave", "neo soul", "trap metal", "bedroom pop", "orchestral epic"
  ];
  
  // Generate LeShore prompt
  const generateLeshorePrompt = async () => {
    // If user has detailed input, use it
    if (leshoreStyle && leshoreStyle.length > 20) {
      // User has custom detailed input - save it to workspace
      const prompt = {
        id: Date.now(),
        title: leshoreTitle || "Untitled Track",
        lyrics: leshoreLyrics,
        style: leshoreStyle,
        styleTags: leshoreStyleTags,
        instrumental: leshoreInstrumental,
        createdAt: new Date().toISOString(),
        coverColor: `hsl(${Math.random() * 360}, 70%, 40%)`
      };
      
      setLeshoreWorkspace(prev => [prompt, ...prev]);
      toast.success(`Created: ${prompt.title}`);
      
      // Clear form
      setLeshoreTitle("");
      setLeshoreLyrics("");
      setLeshoreStyle("");
      setLeshoreStyleTags([]);
      setLeshoreInstrumental(false);
      setLeshoreActiveTab('workspace');
      return;
    }
    
    // Generate a REAL detailed AI prompt from library
    try {
      toast.loading("Generating detailed AI prompt...");
      
      // Get random prompts from library - prioritize detailed categories
      const categories = ['ATL Gospel Trap', 'African 808', 'Boom Bap', 'Experimental R&B 2026', 'Hip Hop', 'R&B', 'Electronic', 'Jazz'];
      const randomCategory = categories[Math.floor(Math.random() * categories.length)];
      const randomPage = Math.floor(Math.random() * 50) + 1;
      
      const { ok, data } = await safeFetch(`${API}/suno-prompts/${encodeURIComponent(randomCategory)}?page=${randomPage}&limit=10`);
      
      if (ok && data.prompts && data.prompts.length > 0) {
        // Find the longest, most detailed prompt
        const sortedPrompts = data.prompts.sort((a, b) => b.length - a.length);
        const detailedPrompt = sortedPrompts[0]; // Get the most detailed one
        
        // Generate a title based on the style
        const titleWords = ['Midnight', 'Golden', 'Electric', 'Velvet', 'Neon', 'Crystal', 'Shadow', 'Divine', 'Eternal', 'Sacred'];
        const titleNouns = ['Dreams', 'Waves', 'Soul', 'Fire', 'Rain', 'Glory', 'Vibes', 'Flow', 'Grace', 'Light'];
        const randomTitle = `${titleWords[Math.floor(Math.random() * titleWords.length)]} ${titleNouns[Math.floor(Math.random() * titleNouns.length)]}`;
        
        const prompt = {
          id: Date.now(),
          title: leshoreTitle || randomTitle,
          lyrics: leshoreInstrumental ? "" : leshoreLyrics,
          style: detailedPrompt, // Full detailed prompt from library
          styleTags: [randomCategory],
          instrumental: leshoreInstrumental,
          createdAt: new Date().toISOString(),
          coverColor: `hsl(${Math.random() * 360}, 70%, 40%)`,
          aiGenerated: true
        };
        
        setLeshoreWorkspace(prev => [prompt, ...prev]);
        toast.dismiss();
        toast.success(`AI Prompt Generated: ${randomCategory} style!`);
        
        // Clear form
        setLeshoreTitle("");
        setLeshoreActiveTab('workspace');
      } else {
        toast.dismiss();
        toast.error("Failed to generate. Please try again.");
      }
    } catch (err) {
      toast.dismiss();
      toast.error("Failed to generate prompt. Try again.");
      console.error(err);
    }
  };
  
  // Format prompt for Suno (clean, optimized format)
  const formatLeshorePrompt = (prompt) => {
    // Build style string
    const styleStr = [prompt.style, ...prompt.styleTags].filter(Boolean).join(', ');
    
    // Suno-optimized format
    let formatted = '';
    
    // Style of Music (most important for Suno)
    if (styleStr) {
      formatted += `${styleStr}\n\n`;
    }
    
    // Add instrumental marker or lyrics
    if (prompt.instrumental) {
      formatted += `[Instrumental]\n`;
    } else if (prompt.lyrics) {
      formatted += prompt.lyrics;
    }
    
    return formatted.trim();
  };
  
  // Copy prompt for Suno
  const copyLeshorePrompt = async (prompt) => {
    const fullPrompt = formatLeshorePrompt(prompt);
    const ok = await universalCopy(fullPrompt, "✓ Prompt copied! Paste into MANNIS");
    if (ok) {
      setLeshoreCopied(prompt.id);
      setTimeout(() => setLeshoreCopied(null), 2000);
    }
  };
  
  // Download prompt as .txt file
  const downloadLeshorePrompt = (prompt) => {
    const fullPrompt = formatLeshorePrompt(prompt);
    const titleSlug = prompt.title.toLowerCase().replace(/[^a-z0-9]/g, '-').slice(0, 30);
    shareOrDownloadText(fullPrompt, `${titleSlug}-style-prompt.txt`, 'text/plain', {
      successMessage: `Downloaded: ${prompt.title}`,
    });
  };
  
  // Delete from workspace
  const deleteLeshorePrompt = (id) => {
    setLeshoreWorkspace(prev => prev.filter(p => p.id !== id));
    toast.success("Deleted");
  };
  
  // Add style tag
  const addLeshoreTag = (tag) => {
    if (!leshoreStyleTags.includes(tag)) {
      setLeshoreStyleTags(prev => [...prev, tag]);
    }
  };
  
  // Remove style tag
  const removeLeshoreTag = (tag) => {
    setLeshoreStyleTags(prev => prev.filter(t => t !== tag));
  };
  
  // FREE MP3 Catalog State
  const [showMp3Catalog, setShowMp3Catalog] = useState(false);
  const [mp3Playing, setMp3Playing] = useState(null);
  const [mp3Filter, setMp3Filter] = useState('all');
  const [uploadedMp3s, setUploadedMp3s] = useState([]);
  const [mp3Uploading, setMp3Uploading] = useState(false);
  const mp3InputRef = useRef(null);
  
  // Only user uploads - no samples
  const allMp3Tracks = uploadedMp3s;
  
  // Load uploaded MP3s from server on mount
  useEffect(() => {
    const loadUploadedMp3s = async () => {
      try {
        const res = await fetch(`${API}/mp3/list`);
        const data = await res.json();
        if (data.tracks) {
          // Add full URL to uploaded tracks (track.url already starts with /api)
          const baseUrl = API_ORIGIN;
          const tracksWithUrls = data.tracks.map(track => ({
            ...track,
            url: `${baseUrl}${track.url}`,
            isUploaded: true
          }));
          setUploadedMp3s(tracksWithUrls);
        }
      } catch (err) {
        console.error('Failed to load uploaded MP3s:', err);
      }
    };
    loadUploadedMp3s();
  }, []);
  
  // Upload MP3 to server
  const handleMp3Upload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setMp3Uploading(true);
    
    for (const file of files) {
      if (!file.type.includes('audio')) {
        toast.error(`${file.name} is not an audio file`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await fetch(`${API}/mp3/upload`, {
          method: 'POST',
          body: formData
        });
        
        const data = await res.json();
        
        if (data.success && data.track) {
          // Add the new track with full URL (track.url already starts with /api)
          const baseUrl = API_ORIGIN;
          const newTrack = {
            ...data.track,
            url: `${baseUrl}${data.track.url}`,
            isUploaded: true
          };
          setUploadedMp3s(prev => [...prev, newTrack]);
          toast.success(`Uploaded: ${newTrack.title}`);
        } else {
          toast.error(`Failed to upload ${file.name}`);
        }
      } catch (err) {
        console.error('Upload error:', err);
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    
    setMp3Uploading(false);
    if (mp3InputRef.current) mp3InputRef.current.value = '';
  };
  
  // Delete uploaded MP3
  const handleDeleteMp3 = async (trackId) => {
    try {
      const res = await fetch(`${API}/mp3/${trackId}`, { method: 'DELETE' });
      const data = await res.json();
      
      if (data.success) {
        setUploadedMp3s(prev => prev.filter(t => t.id !== trackId));
        if (mp3Playing === trackId) setMp3Playing(null);
        toast.success('Track deleted');
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete track');
    }
  };
  
  // Mashup Music Prompt State
  const [showMashup, setShowMashup] = useState(false);
  const [mashupStyle1, setMashupStyle1] = useState('');
  const [mashupStyle2, setMashupStyle2] = useState('');
  const [mashupResult, setMashupResult] = useState('');
  const [mashupGenerating, setMashupGenerating] = useState(false);
  
  // Suno Prompts Library State (10,000 Prompts)
  const [showSunoLibrary, setShowSunoLibrary] = useState(false);
  const [sunoLibraryCategories, setSunoLibraryCategories] = useState([]);
  const [sunoLibraryCategory, setSunoLibraryCategory] = useState('');
  const [sunoLibraryPrompts, setSunoLibraryPrompts] = useState([]);
  const [sunoLibraryStructures, setSunoLibraryStructures] = useState({});
  const [sunoLibraryPage, setSunoLibraryPage] = useState(1);
  const [sunoLibraryTotalPages, setSunoLibraryTotalPages] = useState(1);
  const [sunoLibrarySearch, setSunoLibrarySearch] = useState('');
  const [sunoLibraryLoading, setSunoLibraryLoading] = useState(false);
  const [sunoLibraryTotal, setSunoLibraryTotal] = useState(0);
  const [speakingPromptIdx, setSpeakingPromptIdx] = useState(null); // For audio playback
  const [audioPreviewLoading, setAudioPreviewLoading] = useState(null);
  const [cardPreviews, setCardPreviews] = useState({}); // cardKey -> {status: 'rendering'|'ready'|'failed', url}

  // Auto-preview: trigger MiniMax render for a prompt and track it per card
  const startCardPreview = async (promptText, cardKey) => {
    if (!promptText) return;
    setCardPreviews(prev => ({ ...prev, [cardKey]: { status: 'rendering' } }));
    const resolve = (u) => (u && u.startsWith('/') ? `${API_ORIGIN}${u}` : u);
    try {
      const res = await fetch(`${API}/generate-audio-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: promptText.slice(0, 300), category: 'auto-preview', email: currentUser?.email || userEmail || getGuestEmail() })
      });
      if (res.status === 402) {
        setCardPreviews(prev => ({ ...prev, [cardKey]: { status: 'failed' } }));
        if (currentUser) { setShowTokenExhausted(true); }
        else { toast.error('Audio previews are a paid feature — upgrade to unlock!'); setShowPricing(true); }
        return;
      }
      const data = await res.json();
      if (data.status === 'completed' && data.audio_url) {
        setCardPreviews(prev => ({ ...prev, [cardKey]: { status: 'ready', url: resolve(data.audio_url) } }));
        return;
      }
      if (data.status !== 'processing' || !data.task_id) throw new Error(data.error || 'preview failed');
      let attempts = 0;
      const poll = async () => {
        if (attempts++ >= 80) {
          setCardPreviews(prev => ({ ...prev, [cardKey]: { status: 'failed' } }));
          return;
        }
        try {
          const sr = await fetch(`${API}/audio-preview-status/${data.task_id}`);
          const sd = await sr.json();
          if (sd.status === 'completed' && sd.audio_url) {
            setCardPreviews(prev => ({ ...prev, [cardKey]: { status: 'ready', url: resolve(sd.audio_url) } }));
          } else if (sd.status === 'failed') {
            setCardPreviews(prev => ({ ...prev, [cardKey]: { status: 'failed' } }));
          } else {
            setTimeout(poll, 3000);
          }
        } catch (e) { setTimeout(poll, 3000); }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setCardPreviews(prev => ({ ...prev, [cardKey]: { status: 'failed' } }));
    }
  }; // Loading state for audio preview
  const [audioPreviewUrl, setAudioPreviewUrl] = useState(null); // Current audio preview URL
  const [audioPreviewPlaying, setAudioPreviewPlaying] = useState(null); // Currently playing preview index
  const audioPreviewRef = useRef(null); // Audio element reference
  const [libStemsLoadingIdx, setLibStemsLoadingIdx] = useState(null); // 70K library: WAV stems render in progress

  // 70K library: render 10 WAV stems from a style prompt and download as a zip
  // (same background render + poll flow as the style-prompt cards).
  const downloadLibStems = async (promptText, idx) => {
    if (!promptText) return;
    setLibStemsLoadingIdx(idx);
    try {
      toast.info('🎛️ Rendering 10 WAV stems... (~30-60s)');
      const startRes = await axios.post(`${API}/generate-midi/stems-wav`, { style_prompt: promptText, bars: 16 });
      const taskId = startRes.data.task_id;
      let status = startRes.data.status;
      for (let attempt = 0; attempt < 100 && status === 'processing'; attempt++) {
        await new Promise((r) => setTimeout(r, 3000));
        const sr = await axios.get(`${API}/generate-midi/stems-wav/status/${taskId}`);
        status = sr.data.status;
        if (status === 'failed') throw new Error(sr.data.error || 'Stem rendering failed.');
      }
      if (status !== 'completed') throw new Error('Stem rendering timed out. Try again.');
      const res = await axios.get(`${API}/generate-midi/stems-wav/download/${taskId}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      await shareOrDownloadBlob(blob, 'mannis_stems.zip', 'application/zip', {
        successMessage: '🎛️ 10 WAV stems downloaded! Drop the zip into your DAW',
      });
    } catch (err) {
      toast.error(err.message && err.message !== 'Network Error' ? err.message : 'Stem rendering failed. Try again.');
    } finally {
      setLibStemsLoadingIdx(null);
    }
  };

  // Generate audio preview for a style prompt (async with polling)
  // Uses Replicate to generate actual style-matched audio previews
  const generateAudioPreview = async (prompt, category, idx) => {
    // If already playing this one, stop it
    if (audioPreviewPlaying === idx && audioPreviewRef.current) {
      audioPreviewRef.current.pause();
      setAudioPreviewPlaying(null);
      return;
    }
    
    // If already loading, don't start again
    if (audioPreviewLoading !== null) {
      toast.info('Please wait for current preview to finish');
      return;
    }
    
    setAudioPreviewLoading(idx);
    
    // Check if we have a cached preview for this exact prompt first
    // (must match the backend key: md5 of the exact prompt string sent, first 16 hex chars)
    const cacheKey = SparkMD5.hash(prompt.slice(0, 300)).slice(0, 16);
    
    try {
      // Check for existing cached preview
      const cacheCheck = await fetch(`${API}/audio-preview-status/${cacheKey}`);
      if (cacheCheck.ok) {
        const cacheData = await cacheCheck.json();
        if (cacheData.status === 'completed' && cacheData.audio_url) {
          await playAudioPreview(cacheData.audio_url, idx);
          return;
        }
      }
    } catch (e) {
      // Cache check failed, continue to generate
    }
    
    // Generate new audio preview
    toast.info('🎵 Generating audio preview... (~60-90 seconds)');
    
    try {
      const response = await fetch(`${API}/generate-audio-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: prompt.slice(0, 300), category, email: currentUser?.email || userEmail || getGuestEmail() }),
      });
      
      // Handle rate limiting
      if (response.status === 429) {
        toast.error('Too many requests, please wait a moment');
        setAudioPreviewLoading(null);
        return;
      }

      // Paid feature - show upgrade path
      if (response.status === 402) {
        setAudioPreviewLoading(null);
        if (currentUser) { setShowTokenExhausted(true); }
        else { toast.error('Audio previews are a paid feature — upgrade to unlock!'); setShowPricing(true); }
        return;
      }
      
      const text = await response.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (jsonError) {
        throw new Error(text || 'Invalid response from server');
      }
      
      // Check if already completed (cached)
      if (data.status === 'completed' && data.audio_url) {
        await playAudioPreview(data.audio_url, idx);
        return;
      }
      
      // If processing, poll for status
      if (data.status === 'processing' && data.task_id) {
        const taskId = data.task_id;
        let attempts = 0;
        const maxAttempts = 100; // ~5 minutes with 3-second intervals (full Suno generation)
        
        const pollStatus = async () => {
          if (attempts >= maxAttempts) {
            toast.error('Audio generation timed out. Please try again.');
            setAudioPreviewLoading(null);
            return;
          }
          
          attempts++;
          
          try {
            const statusResponse = await fetch(`${API}/audio-preview-status/${taskId}`);
            const statusData = await statusResponse.json();
            
            if (statusData.status === 'completed' && statusData.audio_url) {
              await playAudioPreview(statusData.audio_url, idx);
            } else if (statusData.status === 'failed') {
              toast.error(statusData.error || 'Audio generation failed');
              setAudioPreviewLoading(null);
            } else if (statusData.status === 'processing') {
              // Continue polling
              setTimeout(pollStatus, 3000);
            } else {
              toast.error('Audio generation failed');
              setAudioPreviewLoading(null);
            }
          } catch (pollError) {
            console.error('Poll error:', pollError);
            setTimeout(pollStatus, 3000);
          }
        };
        
        // Start polling
        setTimeout(pollStatus, 3000);
      } else {
        throw new Error(data.error || data.detail || 'Failed to start audio generation');
      }
    } catch (error) {
      console.error('Audio preview error:', error);
      toast.error(error.message || 'Failed to generate audio preview');
      setAudioPreviewLoading(null);
    }
  };
  
  // Helper function to play audio preview
  const stopAudioPreview = () => {
    if (audioPreviewRef.current) audioPreviewRef.current.pause();
    setAudioPreviewPlaying(null);
  };

  const playAudioPreview = async (audioUrl, idx) => {
    try {
      // Resolve relative /api URLs against the apex backend (www-safe)
      audioUrl = audioUrl && audioUrl.startsWith('/') ? `${API_ORIGIN}${audioUrl}` : audioUrl;
      setAudioPreviewUrl(audioUrl);
      
      // Create or update audio element
      if (!audioPreviewRef.current) {
        audioPreviewRef.current = new Audio();
      }
      
      audioPreviewRef.current.onended = () => setAudioPreviewPlaying(null);
      audioPreviewRef.current.onerror = (e) => {
        console.error('Audio playback error:', e);
        setAudioPreviewPlaying(null);
        toast.error('Audio failed - try again');
      };
      
      audioPreviewRef.current.src = audioUrl;
      audioPreviewRef.current.preload = 'metadata';
      
      // Start playback as soon as the first chunk is buffered (canplay)
      await new Promise((resolve) => {
        const a = audioPreviewRef.current;
        a.addEventListener('canplay', resolve, { once: true });
        a.load();
      });
      await audioPreviewRef.current.play();
      setAudioPreviewPlaying(idx);
      setAudioPreviewLoading(null);
      toast.success('🎵 Playing preview!');
    } catch (playError) {
      console.error('Play error:', playError);
      // Autoplay blocked (play happened minutes after the tap) — the track IS ready & cached
      toast.success('🎵 Preview ready! Tap the button once more to play it.', { duration: 7000 });
      setAudioPreviewPlaying(null);
      setAudioPreviewLoading(null);
    }
  };
  
  // Text-to-Speech for Style Prompts
  const speakPrompt = (text, idx) => {
    // Stop any current speech
    window.speechSynthesis.cancel();
    
    if (speakingPromptIdx === idx) {
      // If clicking on same prompt, just stop
      setSpeakingPromptIdx(null);
      return;
    }
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    // Try to find a good voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => v.name.includes('Google') || v.name.includes('Premium')) || voices[0];
    if (preferredVoice) utterance.voice = preferredVoice;
    
    utterance.onstart = () => setSpeakingPromptIdx(idx);
    utterance.onend = () => setSpeakingPromptIdx(null);
    utterance.onerror = () => setSpeakingPromptIdx(null);
    
    window.speechSynthesis.speak(utterance);
  };
  
  // Generate Language Demo Audio (ElevenLabs TTS)
  const playLanguageDemo = async (language) => {
    // Sample phrases for each language
    const demoPhrases = {
      'English': 'Welcome to MANNIS. Create music in any language with AI-powered vocals.',
      'Spanish': 'Bienvenido a MANNIS. Crea música en cualquier idioma con voces impulsadas por IA.',
      'French': 'Bienvenue sur MANNIS. Créez de la musique dans n\'importe quelle langue avec des voix alimentées par l\'IA.',
      'Portuguese': 'Bem-vindo ao MANNIS. Crie música em qualquer idioma com vocais alimentados por IA.',
      'German': 'Willkommen bei MANNIS. Erstellen Sie Musik in jeder Sprache mit KI-gestützten Stimmen.',
      'Italian': 'Benvenuto su MANNIS. Crea musica in qualsiasi lingua con voci alimentate dall\'IA.',
      'Japanese': 'MANNISへようこそ。AIパワードボーカルであらゆる言語で音楽を作成できます。',
      'Korean': 'MANNIS에 오신 것을 환영합니다. AI 보컬로 어떤 언어로든 음악을 만드세요.',
      'Chinese': '欢迎来到MANNIS。使用AI驱动的人声，用任何语言创作音乐。',
      'Arabic': 'مرحبًا بك في MANNIS. أنشئ موسيقى بأي لغة مع أصوات مدعومة بالذكاء الاصطناعي.',
      'Hindi': 'MANNIS में आपका स्वागत है। AI-संचालित वोकल्स के साथ किसी भी भाषा में संगीत बनाएं।',
      'Swahili': 'Karibu MANNIS. Tengeneza muziki kwa lugha yoyote na sauti za AI.',
      'Yoruba': 'Kaabo si MANNIS. Ṣẹda orin ni ede eyikeyi pẹlu ohun AI.',
      'Pidgin': 'Welcome to MANNIS. Make music for any language with AI voice wey dey sound correct.'
    };
    
    const phrase = demoPhrases[language] || demoPhrases['English'];
    
    // If clicking same language and audio is playing, stop it
    if (demoLanguage === language && demoAudioRef.current && !demoAudioRef.current.paused) {
      demoAudioRef.current.pause();
      setDemoLanguage(null);
      return;
    }
    
    setDemoLanguage(language);
    setDemoGenerating(true);
    toast.info(`🎙️ Generating ${language} demo...`);
    
    try {
      const res = await fetch(`${API}/tts/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: phrase,
          voice_id: language === 'Chinese' || language === 'Japanese' || language === 'Korean' 
            ? 'XB0fDUnXU5powFXDhCwa' // Asian-friendly voice
            : 'CwhRBWXzGAHq8TQ4Fs17', // Default multilingual voice
          model_id: 'eleven_multilingual_v2'
        })
      });
      
      if (res.status === 429) {
        toast.error('Too many requests, please wait a moment');
        setDemoGenerating(false);
        setDemoLanguage(null);
        return;
      }
      
      // Read response body only once to avoid "body stream already read" error
      const text = await res.text();
      
      if (res.ok) {
        const data = JSON.parse(text);
        const audioUrl = data.audio_url; // base64 data URL
        
        setDemoAudioUrl(audioUrl);
        
        if (!demoAudioRef.current) {
          demoAudioRef.current = new Audio();
          demoAudioRef.current.onended = () => setDemoLanguage(null);
        }
        
        demoAudioRef.current.src = audioUrl;
        demoAudioRef.current.preload = 'metadata';
        await new Promise((resolve) => {
          const a = demoAudioRef.current;
          a.addEventListener('canplay', resolve, { once: true });
          a.load();
        });
        await demoAudioRef.current.play();
        toast.success(`🔊 Playing ${language} demo`);
      } else {
        let errorMsg = 'Failed to generate demo';
        try {
          const errorData = JSON.parse(text);
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {}
        throw new Error(errorMsg);
      }
    } catch (err) {
      console.error('Demo audio error:', err);
      toast.error(`Demo failed: ${err.message || 'Unknown error'}`);
      setDemoLanguage(null);
    } finally {
      setDemoGenerating(false);
    }
  };
  
  // ============ AUTHENTICATION FUNCTIONS ============
  const checkAuth = async () => {
    // First try to restore from localStorage
    const savedUser = localStorage.getItem('sounddna_user');
    const savedToken = localStorage.getItem('sounddna_token');
    
    // Always verify with server first - don't trust localStorage alone
    try {
      const headers = {};
      if (savedToken) {
        headers['Authorization'] = `Bearer ${savedToken}`;
      }
      
      const res = await fetch(`${API}/auth/me`, { 
        credentials: 'include',
        headers 
      });
      
      if (res.ok) {
        const user = await res.json();
        setCurrentUser(user);
        setUserEmail(user.email);
        setSubscription({
          plan: user.subscription,
          ...user.subscription_limits,
          ...user.usage
        });
        // Update localStorage with fresh data
        localStorage.setItem('sounddna_user', JSON.stringify(user));
        return user;
      } else {
        // Server says not authenticated - clear everything
        localStorage.removeItem('sounddna_user');
        localStorage.removeItem('sounddna_token');
        setCurrentUser(null);
        setUserEmail('');
      }
    } catch (err) {
      console.log('Auth check failed');
      // On network error, don't trust cached data
      localStorage.removeItem('sounddna_user');
      localStorage.removeItem('sounddna_token');
      setCurrentUser(null);
    }
    return null;
  };
  
  const handleLogin = async (e) => {
    e?.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: loginEmail.trim(), password: loginPassword.trim() })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // Store token in localStorage as backup for cookie issues
        if (data.access_token) {
          localStorage.setItem('sounddna_token', data.access_token);
        }
        localStorage.setItem('sounddna_user', JSON.stringify(data));
        
        setCurrentUser(data);
        setUserEmail(data.email);
        setSubscription({
          plan: data.subscription,
          ...data.subscription_limits,
          ...data.usage
        });
        setShowAuthModal(false);
        setLoginEmail('');
        setLoginPassword('');
        toast.success(`Welcome back, ${data.name || data.email}!`);
      } else {
        setAuthError(data.detail || 'Login failed');
      }
    } catch (err) {
      setAuthError('Network error. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };
  
  // Step 1: Send verification code
  const handleSendVerification = async (e) => {
    e?.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    
    try {
      const res = await fetch(`${API}/auth/send-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: loginEmail, 
          password: loginPassword, 
          name: registerName 
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setVerificationSent(true);
        setVerificationHint(data.code_hint || ''); // Remove in production
        toast.success('Verification code sent! Check your email.');
      } else {
        setAuthError(data.detail || 'Failed to send verification code');
      }
    } catch (err) {
      setAuthError('Network error. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };
  
  // Step 2: Verify code and complete registration
  const handleVerifyAndRegister = async (e) => {
    e?.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    
    try {
      const res = await fetch(`${API}/auth/verify-and-register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          email: loginEmail, 
          code: verificationCode 
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // Store token in localStorage
        if (data.access_token) {
          localStorage.setItem('sounddna_token', data.access_token);
        }
        localStorage.setItem('sounddna_user', JSON.stringify(data));
        
        setCurrentUser(data);
        setUserEmail(data.email);
        setSubscription({
          plan: data.subscription,
          ...data.subscription_limits,
          ...data.usage
        });
        setShowAuthModal(false);
        setLoginEmail('');
        setLoginPassword('');
        setRegisterName('');
        setVerificationCode('');
        setVerificationSent(false);
        toast.success(`Welcome to MANNIS, ${data.name}! 🎵`);
      } else {
        setAuthError(data.detail || 'Verification failed');
      }
    } catch (err) {
      setAuthError('Network error. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e?.preventDefault();
    // Now uses 2-step verification
    if (!verificationSent) {
      await handleSendVerification(e);
    } else {
      await handleVerifyAndRegister(e);
    }
  };
  
  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (err) {}
    // Clear localStorage
    localStorage.removeItem('sounddna_user');
    localStorage.removeItem('sounddna_token');
    setCurrentUser(null);
    setUserEmail('');
    setSubscription({ plan: "free", analyses_used: 0, analyses_limit: 20, generations_used: 0, generations_limit: 5, generations_remaining: 5 });
    toast.info('Logged out successfully');
  };

  const handleChangePassword = async (e) => {
    e?.preventDefault();
    setCpError('');
    if (cpNew.length < 8) { setCpError('New password must be at least 8 characters'); return; }
    if (cpNew !== cpConfirm) { setCpError('New passwords do not match'); return; }
    setCpLoading(true);
    try {
      const token = localStorage.getItem('sounddna_token');
      const res = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        body: JSON.stringify({ current_password: cpCurrent, new_password: cpNew })
      });
      const data = await res.json();
      if (!res.ok) {
        const d = data?.detail;
        setCpError(typeof d === 'string' ? d : 'Failed to change password');
      } else {
        setShowChangePassword(false);
        toast.success('Password changed successfully!');
      }
    } catch (err) {
      setCpError('Network error. Please try again.');
    } finally {
      setCpLoading(false);
    }
  };
  
  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, []);
  
  // World Voice DNA Functions
  const loadWorldVoiceData = async () => {
    try {
      const res = await fetch(`${API}/world-voice-dna`);
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      setWorldVoiceData(data);
    } catch (err) {
      console.error('Failed to load world voice data:', err);
    }
  };
  
  // forceFastMode defaults to FALSE because ElevenLabs quota is exhausted - use Replicate by default
  const generateWorldVoice = async (voice, region, forceFastMode = false) => {
    if (worldVoiceGenerating) {
      toast.info('Already generating a voice sample, please wait...');
      return;
    }
    
    setWorldVoiceGenerating(voice.voice_id);
    setWorldVoiceWav(null);
    setWorldVoiceAudio(null);
    setLastGeneratedVoice(null);
    
    const useFastMode = forceFastMode;
    toast.info(useFastMode ? `⚡ Generating ${voice.name} (~5 sec)...` : `🎵 Generating ${voice.name} (~60 sec)...`, { duration: useFastMode ? 3000 : 8000 });
    
    // Retry logic with exponential backoff
    const fetchWithRetry = async (url, options, maxRetries = 2) => {
      let lastError;
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const res = await fetch(url, options);
          return res;
        } catch (err) {
          lastError = err;
          if (attempt < maxRetries) {
            // Exponential backoff: 1s, 2s
            const delay = Math.pow(2, attempt) * 1000;
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }
      }
      throw lastError;
    };
    
    try {
      
      const res = await fetchWithRetry(`${API}/world-voice-generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_name: voice.name,
          text: voice.sample_text || '',
          lyrics: voice.sample_text || '',
          region: region || worldVoiceRegion,
          genre: voice.genre,
          style: voice.style,
          gender: voice.name.toLowerCase().includes('female') || 
                  voice.name.toLowerCase().includes('queen') || 
                  voice.name.toLowerCase().includes('diva') ? 'female' : 'male',
          fast: useFastMode
        })
      });
      
      if (res.status === 429) {
        toast.error('Too many requests. Please wait a moment and try again.');
        setWorldVoiceGenerating(null);
        return;
      }
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || `Server error: ${res.status}`);
      }
      
      const responseText = await res.text();
      if (!responseText) {
        throw new Error('Empty response from server');
      }
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid response format');
      }
      
      if (data.error || data.detail) {
        throw new Error(data.error || data.detail);
      }
      
      if (!data.task_id) {
        throw new Error('No task ID received');
      }
      
      // Show appropriate message based on mode
      const isFastMode = data.mode === 'fast';
      toast.info(isFastMode ? '⚡ Fast generation (~5 sec)...' : '🎵 Generating studio vocal sample on Suno + isolating with Demucs (~2-4 min)...', { duration: isFastMode ? 3000 : 8000 });
      
      // Poll for completion - faster polling for fast mode
      let attempts = 0;
      const maxAttempts = isFastMode ? 15 : 100;  // STANDARD: 100 × 3s ≈ 300s (Suno ~200s + Demucs isolation)
      const pollInterval = isFastMode ? 1000 : 3000;  // 1 sec for fast, 3 sec for slow
      
      const poll = async () => {
        if (attempts >= maxAttempts) {
          // Don't hard-error: the backend task keeps running and the sample can still complete
          toast.info('⏳ Still generating — your voice sample will appear in the collection when ready. Check back shortly.', { duration: 8000 });
          setWorldVoiceGenerating(null);
          return;
        }
        
        attempts++;
        
        try {
          const statusRes = await fetchWithRetry(`${API}/world-voice-status/${data.task_id}`, {}, 1);
          if (!statusRes.ok) {
            setTimeout(poll, 3000);
            return;
          }
          
          const statusText = await statusRes.text();
          if (!statusText) {
            setTimeout(poll, 3000);
            return;
          }
          
          let status;
          try {
            status = JSON.parse(statusText);
          } catch (e) {
            setTimeout(poll, 3000);
            return;
          }
          
          if (status.status === 'completed' && status.audio_url) {
            setWorldVoiceAudio(status.audio_url);
            setWorldVoiceWav(status.wav_url || status.audio_url);
            setLastGeneratedVoice({
              name: voice.name,
              genre: voice.genre,
              region: region || worldVoiceRegion,
              audioUrl: status.audio_url,
              wavUrl: status.wav_url
            });
            setWorldVoiceGenerating(null);
            toast.success(`✅ Voice sample ready! Download WAV for Suno.`);
          } else if (status.status === 'failed') {
            const errorMsg = status.error || 'Generation failed';
            
            // If quota error and we were using fast mode, retry with slow mode
            if ((errorMsg.includes('quota') || errorMsg.includes('Quota')) && isFastMode) {
              toast.info('⚡ Fast mode unavailable. Trying standard mode (~60 sec)...', { duration: 4000 });
              // Retry with slow mode
              setWorldVoiceGenerating(null);
              setTimeout(() => generateWorldVoice(voice, region, false), 500);  // Pass false for slow mode
              return;
            }
            
            setWorldVoiceGenerating(null);
            // User-friendly error messages
            if (errorMsg.includes('quota') || errorMsg.includes('Quota')) {
              toast.error('Voice generation quota exceeded. Please try again later.');
            } else if (errorMsg.includes('rate') || errorMsg.includes('limit')) {
              toast.error('Service is busy. Please try again in a moment.');
            } else if (errorMsg.includes('timeout') || errorMsg.includes('Timeout')) {
              toast.error('Request timed out. Please try again.');
            } else {
              toast.error('Generation failed. Please try again.');
            }
          } else {
            // Continue polling with appropriate interval
            setTimeout(poll, pollInterval);
          }
        } catch (pollErr) {
          console.error('Poll error:', pollErr);
          setTimeout(poll, pollInterval);
        }
      };
      
      setTimeout(poll, 3000);
      
    } catch (err) {
      console.error('World voice error:', err);
      setWorldVoiceGenerating(null);
      // User-friendly error messages
      if (err.message?.includes('fetch') || err.message?.includes('network') || err.name === 'TypeError') {
        toast.error('Connection interrupted. Please check your internet and try again.');
      } else if (err.message?.includes('rate') || err.message?.includes('429')) {
        toast.error('Too many requests. Please wait a moment and try again.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    }
  };
  
  // Download World Voice WAV file for Suno upload
  const downloadWorldVoiceWav = async (voiceName) => {
    if (!worldVoiceWav) {
      toast.error('No voice sample to download. Generate one first!');
      return;
    }
    try {
      const fileName = `${voiceName || 'voice_sample'}_for_suno.wav`;
      let audioUrl = worldVoiceWav;
      if (audioUrl.startsWith('/api/')) {
        audioUrl = `${API}${audioUrl.replace('/api', '')}`;
      }
      toast.info('⏳ Preparing file...', { duration: 2000 });
      const response = await fetch(audioUrl);
      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
      const blob = await response.blob();
      await shareOrDownloadBlob(blob, fileName, 'audio/wav', {
        sourceUrl: audioUrl, desktopPicker: true, successMessage: '📥 WAV saved!',
      });
    } catch (err) {
      console.error('Download error:', err);
      toast.error(`Download failed: ${err.message}`);
    }
  };
  
  // Helper function to trigger download with device-specific handling
  
  // Add World Voice to Unified Workflow - VOICE STYLE ONLY (no lyrics)
  const addWorldVoiceToWorkflow = (voice, region) => {
    const voiceStyle = `${voice.genre} vocals, ${voice.style}, ${voice.name} style`;
    
    // Update workflow with voice style ONLY - no lyrics
    updateWorkflow({
      voiceSample: voice.name,
      stylePrompt: voiceStyle,
      genre: voice.genre
    });
    
    // Also update beatPrompt directly for Suno export
    setBeatPrompt(prev => {
      if (prev && prev.trim()) {
        return `${prev}, ${voiceStyle}`;
      }
      return voiceStyle;
    });
    
    toast.success(`🎤 Added "${voice.name}" voice style to Workflow!`);
  };
  
  // INSTANT download - get a pre-generated sample immediately (no waiting!)
  const downloadInstantSample = async (voice) => {
    try {
      toast.info('📥 Getting instant sample...', { duration: 2000 });
      
      // Call the instant sample endpoint with voice_id for exact matching
      const res = await fetch(`${API}/world-voice-instant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_id: voice.voice_id,
          voice_name: voice.name,
          region: worldVoiceRegion,
          genre: voice.genre,
          gender: voice.name.toLowerCase().includes('female') || 
                  voice.name.toLowerCase().includes('queen') || 
                  voice.name.toLowerCase().includes('diva') ? 'female' : 'male'
        })
      });
      
      const data = await res.json();
      
      if (data.status === 'ready' && data.wav_url) {
        // Set the audio for playback (cache-busted so browsers fetch the new music samples)
        setWorldVoiceWav(`${data.wav_url}?v=mx2`);
        setWorldVoiceAudio(`${data.audio_url || data.wav_url}?v=mx2`);
        setLastGeneratedVoice({
          name: voice.name,
          genre: voice.genre,
          region: worldVoiceRegion,
          audioUrl: data.audio_url,
          wavUrl: data.wav_url
        });
        
        // AUTO-DOWNLOAD via unified helper (mobile share sheet → desktop picker → anchor fallback)
        const fileName = `${voice.name || 'voice_sample'}_for_suno.mp3`;
        let audioUrl = data.audio_url || data.wav_url;
        if (audioUrl.startsWith('/api/')) {
          audioUrl = `${API}${audioUrl.replace('/api', '')}`;
        }
        toast.info('⏳ Downloading voice sample...', { duration: 2000 });
        const fileResponse = await fetch(audioUrl);
        if (!fileResponse.ok) throw new Error(`Failed to fetch: ${fileResponse.status}`);
        const blob = await fileResponse.blob();
        await shareOrDownloadBlob(blob, fileName, 'audio/mpeg', {
          sourceUrl: audioUrl, desktopPicker: true, successMessage: '📥 Voice sample saved!',
        });
        
      } else {
        toast.info('No cached samples. Generating new one (~60 sec)...', { duration: 3000 });
        // Fall back to generation
        generateWorldVoice(voice, worldVoiceRegion, false);
      }
    } catch (err) {
      console.error('Instant sample error:', err);
      toast.error('Could not get instant sample. Try generating.');
    }
  };
  
  // Copy World Voice style to Suno clipboard - VOICE STYLE ONLY
  const copyWorldVoiceToSuno = async (voice) => {
    try {
      // Get optimized Suno prompt from backend
      const res = await fetch(`${API}/world-voice-prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voice_name: voice.name,
          region: worldVoiceRegion,
          genre: voice.genre,
          style: voice.style,
          language: voice.language || 'English'
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        universalCopy(data.suno_prompt, `📋 Suno voice prompt copied! Paste in Suno's voice description.`);
      } else {
        // Fallback to local generation
        const sunoStyle = `${voice.genre} vocals, ${voice.style}, ${voice.name} style singing, professional studio quality`;
        universalCopy(sunoStyle, `📋 Copied "${voice.name}" voice style for Suno!`);
      }
    } catch (err) {
      // Fallback to local generation
      const sunoStyle = `${voice.genre} vocals, ${voice.style}, ${voice.name} style singing, professional studio quality`;
      universalCopy(sunoStyle, `📋 Copied "${voice.name}" voice style for Suno!`);
    }
  };
  
  // Suno Prompts Library Functions
  const loadSunoLibraryCategories = async () => {
    try {
      console.log("Loading 40K library categories...");
      const res = await fetch(`${API}/suno-prompts/categories`);
      const data = await res.json();
      console.log("Loaded categories:", data.categories?.length, "Total:", data.total_prompts);
      setSunoLibraryCategories(data.categories || []);
      setSunoLibraryTotal(data.total_prompts || 0);
    } catch (err) {
      console.error('Failed to load Suno library categories:', err);
    }
  };
  
  // Auto-load 40K library when modal opens
  useEffect(() => {
    if (showSunoLibrary && sunoLibraryCategories.length === 0) {
      loadSunoLibraryCategories();
    }
  }, [showSunoLibrary]);
  
  const loadSunoLibraryPrompts = async (category, page = 1, search = '') => {
    setSunoLibraryLoading(true);
    try {
      let url = `${API}/suno-prompts/${encodeURIComponent(category)}?page=${page}&limit=20`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url);
      const data = await res.json();
      setSunoLibraryPrompts(data.prompts || []);
      setSunoLibraryStructures(data.structures || {});
      setSunoLibraryTotalPages(data.total_pages || 1);
      setSunoLibraryPage(page);
    } catch (err) {
      console.error('Failed to load Suno prompts:', err);
      setSunoLibraryPrompts([]);
    }
    setSunoLibraryLoading(false);
  };
  
  const searchSunoLibrary = async (query) => {
    if (!query || query.length < 2) return;
    setSunoLibraryLoading(true);
    try {
      const res = await fetch(`${API}/suno-prompts/search?q=${encodeURIComponent(query)}&limit=50`);
      const data = await res.json();
      setSunoLibraryPrompts(data.results?.map(r => r.prompt) || []);
      const structMap = {};
      (data.results || []).forEach(r => { if (r.structure) structMap[r.prompt] = r.structure; });
      setSunoLibraryStructures(structMap);
      setSunoLibraryTotalPages(1);
      setSunoLibraryPage(1);
      setSunoLibraryCategory('');
    } catch (err) {
      console.error('Failed to search Suno prompts:', err);
    }
    setSunoLibraryLoading(false);
  };
  
  const copySunoPrompt = (prompt) => {
    universalCopy(prompt, '✓ Prompt copied! Tap & hold to paste');
  };
  
  // Download prompts as TXT file WITH SONG STRUCTURE
  const downloadCategoryPrompts = async (category) => {
    try {
      toast.loading(`Downloading ${category} prompts with song structure...`);
      const res = await fetch(`${API}/suno-prompts/${encodeURIComponent(category)}?page=1&limit=10000&with_structure=true`);
      const data = await res.json();
      
      if (data.prompts && data.prompts.length > 0) {
        const content = `MANNIS STYLE PROMPTS - ${category.toUpperCase()}\n` +
          `Total: ${data.prompts.length} Prompts\n` +
          `Downloaded: ${new Date().toLocaleDateString()}\n` +
          `Includes: Chord Progressions, Key Changes, Song Structure\n` +
          `Format: Intro → Verse → Pre-Chorus → Chorus → Bridge → Outro\n` +
          `${'='.repeat(60)}\n\n` +
          data.prompts.map((p, i) => `${'='.repeat(60)}\n--- Prompt ${i + 1} ---\n${'='.repeat(60)}\n${p}\n`).join('\n');
        
        const blob = new Blob([content], { type: 'text/plain' });
        toast.dismiss();
        await shareOrDownloadBlob(blob, `MANNIS_${category.replace(/\s+/g, '_')}_Prompts_With_Structure.txt`, 'text/plain', {
          successMessage: `Downloaded ${data.prompts.length} ${category} prompts with song structure!`,
        });
      }
    } catch (err) {
      toast.dismiss();
      toast.error('Download failed. Try again.');
    }
  };

  // Theme state
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sounddna-theme') || 'dark';
    }
    return 'dark';
  });
  
  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('sounddna-theme', theme);
  }, [theme]);
  
  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };
  
  // Voice Samples Library
  const VOICE_SAMPLES = {
    male: [
      { name: "Smooth R&B", prompt: "smooth male R&B vocals, warm rich baritone, silky melodic delivery, soulful runs, intimate bedroom tone, sensual phrasing" },
      { name: "Deep Trap", prompt: "deep male trap vocals, heavy auto-tune, dark emotional melodic, low chest voice, moody atmospheric, minor key feeling" },
      { name: "Raspy Rock", prompt: "raspy male rock vocals, gritty powerful belt, raw emotional intensity, slight vocal fry, energetic aggressive delivery" },
      { name: "Soft Acoustic", prompt: "soft male acoustic vocals, gentle breathy tone, intimate folk style, delicate phrasing, warm natural reverb" },
      { name: "Hip Hop Flow", prompt: "male hip hop rap vocals, confident rhythmic flow, punchy precise diction, energetic delivery, hard-hitting bars" },
      { name: "Gospel Soul", prompt: "male gospel vocals, powerful soulful belt, rich harmonies, uplifting spiritual energy, call-and-response, melismatic runs" },
      { name: "Pop Tenor", prompt: "male pop tenor vocals, bright catchy hooks, radio-ready polished tone, youthful energetic, clean clear delivery" },
      { name: "Lo-Fi Chill", prompt: "male lo-fi vocals, laid-back dreamy delivery, mellow relaxed tone, slightly filtered nostalgic sound, gentle vibes" },
    ],
    female: [
      { name: "Ethereal Pop", prompt: "ethereal female pop vocals, angelic airy high register, dreamy layered harmonies, soft reverb-soaked, celestial floating tone" },
      { name: "Soulful R&B", prompt: "soulful female R&B vocals, powerful emotional belt, impressive melismatic runs, rich warm tone, passionate delivery" },
      { name: "Soft Indie", prompt: "soft female indie vocals, intimate whispery delicate, vulnerable emotional phrasing, gentle breathy folk-pop tone" },
      { name: "Dance Diva", prompt: "female dance diva vocals, powerful soaring belts, EDM festival energy, bright commanding hooks, club-ready anthemic" },
      { name: "Trap Queen", prompt: "female trap vocals, auto-tuned melodic confident, modern urban attitude, catchy hook delivery, fierce sassy tone" },
      { name: "Jazz Smooth", prompt: "female jazz vocals, smooth sophisticated sultry, classic torch song phrasing, intimate smoky tone, elegant restraint" },
      { name: "Pop Princess", prompt: "female pop princess vocals, bright youthful catchy, radio-friendly hooks, energetic fun delivery, polished sweet tone" },
      { name: "Dark Alternative", prompt: "female dark alternative vocals, moody haunting atmospheric, minor key emotional, slightly breathy mysterious, gothic undertones" },
    ],
    styles: [
      { name: "Auto-Tune Heavy", prompt: "heavy auto-tune vocals, robotic pitch-corrected melodic, futuristic T-Pain style, obvious tuning effect, electronic feel" },
      { name: "Choir Harmonies", prompt: "choir vocal harmonies, layered stacked voices, powerful gospel-influenced, uplifting anthemic, rich full sound" },
      { name: "Whisper ASMR", prompt: "whispered ASMR vocals, intimate extremely soft, close-mic breathy, tingling sensory, delicate barely-there delivery" },
      { name: "Distorted Scream", prompt: "distorted screaming vocals, intense metal aggressive, harsh powerful yelling, raw primal energy, extreme emotional" },
      { name: "Falsetto", prompt: "falsetto high-pitched vocals, ethereal smooth head voice, silky smooth tone, airy floating delicate, romantic gentle" },
      { name: "Talk-Sing", prompt: "talk-singing vocals, spoken word rhythmic, conversational melodic, indie bedroom pop style, casual intimate phrasing" },
      { name: "Vocal Chops", prompt: "chopped vocal samples, glitchy stuttered electronic, pitched manipulated EDM, rhythmic sliced fragments, modern production" },
      { name: "Acapella Stack", prompt: "stacked acapella vocals, no instruments vocal-only, rich layered harmonies, barbershop to modern, pure voice arrangement" },
    ],
    suno: [
      { name: "Soft Melodic Male", prompt: "soft melodic male vocals, light airy tone, conversational delivery, smooth R&B singing mixed with rap, intimate close-mic sound, emotional restraint, moody atmosphere, subtle pitch movement, confessional feel" },
      { name: "Dark Falsetto Male", prompt: "light-lyric male tenor, airy falsetto-heavy singing, breathy intimate tone, dark husky lower register, melancholic pained delivery, controlled vibrato, nocturnal cinematic R&B, smooth emotional vulnerability" },
      { name: "Power Diva Female", prompt: "powerful mezzo-soprano female vocals, rich flexible tone, strong belt with breath support, advanced melisma and runs, precise rhythmic diction, controlled vibrato, dynamic range from smoky low to bright high, run-heavy R&B diva style" },
      { name: "Auto-Tune Rage Male", prompt: "male auto-tuned melodic trap vocals, heavy pitch correction locked to key, layered ad-libs with reverb delay, psychedelic spacious effects, dark atmospheric floaty sound, energetic raspy delivery, rage style" },
      { name: "Whisper Pop Female", prompt: "soft breathy female vocals, whisper-like intimate ASMR delivery, controlled airflow, dark pop tone, low volume close-mic technique, conspiratorial half-spoken phrasing, minimal bright relaxed throat, delicate emotional" },
      { name: "Raspy Rock-Rap Male", prompt: "male melodic singing, emotional raspy tone, rock-influenced vocals, heartfelt vulnerable delivery, gentle vibrato, country-tinged pop-rap crossover, raw authentic feeling, sing-rap hybrid style" },
      { name: "Playful Versatile Female", prompt: "versatile female vocals, playful energetic tone, rap-singing hybrid, catchy melodic hooks, confident sassy attitude, genre-fluid from soft R&B to hard rap, bright dynamic delivery" },
      { name: "Dreamy Falsetto Male", prompt: "male soft airy falsetto, breathy intimate experimental R&B, emotional conversational phrasing, layered dreamy harmonies, vulnerable restrained delivery, unconventional melodic patterns, soulful texture and atmosphere" },
      { name: "Whistle Register Female", prompt: "female light-lyric soprano, impressive whistle register, powerful yet controlled belt, R&B pop runs and riffs, bright youthful tone, light vibrato, seamless register transitions, high agile melismatic style" },
      { name: "Melodic Trap Male", prompt: "male auto-tuned melodic trap, slurred murky delivery, laid-back behind-beat phrasing, emotional melancholic tone, trap sound, heavy vocal effects, sing-rap mumble style, dark atmospheric" },
      { name: "Airy Neo-Soul Female", prompt: "female breathy airy neo-soul R&B, intimate conversational tone, cursive blended phrasing, relaxed behind-beat delivery, soft head-voice leaning, dreamy vulnerable emotional, smooth laid-back style" },
      { name: "Emo-Rap Male", prompt: "male emotional emo-rap vocals, melodic freestyled feel, heartfelt vulnerable singing, auto-tuned with raw emotion, rock-influenced punk-rap energy, sad introspective lyrics, youthful passionate delivery" },
      { name: "Disco Pop Female", prompt: "female disco-pop vocals, confident sultry tone, dance-ready powerful chorus, retro 80s influence, slightly husky lower register, catchy hook delivery, polished radio-friendly, rhythmic precise phrasing" },
      { name: "Deadpan Trap Male", prompt: "male deadpan monotone rap delivery, dark menacing trap, minimal melodic variation, cold aggressive tone, street sound, hard-hitting sparse flow, intimidating presence" },
      { name: "Caribbean Pop Female", prompt: "female Caribbean-influenced pop R&B, versatile sultry commanding vocals, confident attitude, island rhythmic feel, slightly nasal bright tone, powerful hooks, dancehall to ballad range" },
      { name: "Psychedelic Hum Male", prompt: "male melodic humming vocals, dreamy psychedelic atmosphere, emotional vulnerable singing, auto-tuned with soul, spacey reverb-heavy sound, introspective mood, alternative hip-hop experimental" },
    ]
  };
  
  const perPage = 10;

  // Check for payment success
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, []);

  const translateCustomLyrics = async () => {
    if (!(customLyrics || '').trim() || customLyrics.trim().length < 10) {
      toast.error('Write or paste your lyrics first');
      return;
    }
    setLyricsAssistLoading(true);
    toast.info(`🌍 Translating your lyrics to ${rhymeLanguage}...`);
    try {
      const response = await fetch(`${API}/translate-lyrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: customLyrics,
          target_language: rhymeLanguage,
          keep_song_structure: true
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Translation failed');
      const translated = data.translated_text || data.lyrics || data.text;
      setCustomLyrics(translated);
      setGeneratedBars({ bars: translated, raw_lyrics: translated, total_bars: translated.split('\n').filter(l => l.trim() && !l.trim().startsWith('[')).length });
      toast.success(`✅ Lyrics translated to ${rhymeLanguage} & synced!`);
    } catch (err) {
      toast.error(err.message || 'Translation failed - try again');
    } finally {
      setLyricsAssistLoading(false);
    }
  };

  const runLyricsAssistant = async (action) => {
    if (!(customLyrics || '').trim() || customLyrics.trim().length < 10) {
      toast.error('Paste a few lines of your lyrics first');
      return;
    }
    setLyricsAssistLoading(true);
    const labels = { expand: 'Expanding your lyrics...', polish: 'Applying sonic polish...', rewrite: 'Rewriting your song...' };
    toast.info(labels[action]);
    try {
      const res = await axios.post(`${API}/lyrics-assistant`, {
        lyrics: customLyrics,
        action,
        genre: rhymeStyle || 'pop',
        language: rhymeLanguage || 'English'
      });
      const newLyrics = res.data.lyrics;
      setCustomLyrics && setCustomLyrics(newLyrics);
      // Auto-sync into Song Creator generation
      setGeneratedBars({ bars: newLyrics, raw_lyrics: newLyrics, total_bars: newLyrics.split('\n').filter(l => l.trim() && !l.trim().startsWith('[')).length });
      toast.success(action === 'expand' ? '4 new lines added & synced to Song Creator!' : action === 'polish' ? 'Sonic polish applied & synced!' : 'Professional rewrite complete & synced!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI assistant failed - try again');
    } finally {
      setLyricsAssistLoading(false);
    }
  };

  const startAutoWorkflow = async (overrideStyle = null) => {
    const stylePrompt = ((typeof overrideStyle === 'string' ? overrideStyle : '') || workflowPrompt.stylePrompt || '').trim();
    if (!awUrl.trim() && !stylePrompt) {
      toast.error('Paste a YouTube link or load a style prompt first');
      return;
    }
    setAwRunning(true);
    setAwTask(null);
    try {
      const res = await axios.post(`${API}/workflow/run`, {
        email: currentUser?.email || userEmail || getGuestEmail(),
        source_url: awUrl.trim() || null,
        style_prompt: stylePrompt || null,
        bars: 8
      });
      const taskId = res.data.task_id;
      awPollRef.current = setInterval(async () => {
        try {
          const st = await axios.get(`${API}/workflow/status/${taskId}`);
          setAwTask(st.data);
          if (st.data.status === 'completed' || st.data.status === 'failed') {
            clearInterval(awPollRef.current);
            setAwRunning(false);
            if (st.data.status === 'completed') {
              toast.success('🎉 Ready to Download! Your track was analyzed, generated, mastered and saved to your library.');
              if (st.data.result?.style_prompt) {
                setWorkflowPrompt(prev => ({ ...prev, stylePrompt: st.data.result.style_prompt, source: 'auto-workflow' }));
              }
            } else {
              toast.error(st.data.error || 'Workflow failed');
            }
          }
        } catch (e) { /* keep polling */ }
      }, 3000);
    } catch (err) {
      setAwRunning(false);
      toast.error(err.response?.data?.detail || 'Could not start workflow');
    }
  };

  const closeAutoWorkflow = () => {
    if (awPollRef.current) clearInterval(awPollRef.current);
    setShowAutoWorkflow(false);
    setAwRunning(false);
  };

  const handleForgotPassword = async (e) => {
    e?.preventDefault();
    if (!fpEmail.trim()) return;
    setFpLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: fpEmail.trim(), origin_url: window.location.origin });
      setFpSent(true);
    } catch (err) {
      toast.error('Could not send reset email. Please try again.');
    } finally {
      setFpLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e?.preventDefault();
    setRpError('');
    if (rpNew.length < 8) { setRpError('Password must be at least 8 characters'); return; }
    if (rpNew !== rpConfirm) { setRpError('Passwords do not match'); return; }
    setRpLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: resetToken, new_password: rpNew });
      setShowResetPassword(false);
      toast.success('Password reset! Please log in with your new password.');
      window.history.replaceState({}, '', window.location.pathname);
      setAuthMode('login');
      setShowAuthModal(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setRpError(typeof d === 'string' ? d : 'Reset failed. Please request a new link.');
    } finally {
      setRpLoading(false);
    }
  };

  // Detect password reset link on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('reset_token');
    if (token) {
      setResetToken(token);
      setRpNew(''); setRpConfirm(''); setRpError('');
      setShowResetPassword(true);
    }
  }, []);

  const handleBuyTokens = async () => {
    const email = currentUser?.email || userEmail;
    if (!email) {
      toast.error("Please sign in to buy tokens");
      setAuthMode('login');
      setShowAuthModal(true);
      return;
    }
    try {
      toast.info("Opening secure checkout...");
      const response = await axios.post(`${API}/checkout/create`, {
        package_id: 'tokens_100',
        email,
        origin_url: window.location.origin,
        payment_method: 'stripe'
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Checkout failed");
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    for (let i = 0; i < 15; i++) {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          if (response.data.tokens_credited) {
            toast.success(`Payment successful! ${response.data.tokens_credited} tokens added to your balance.`);
          } else {
            toast.success("Payment successful! Your subscription is now active.");
          }
          window.history.replaceState({}, '', window.location.pathname);
          // Refresh user data if logged in
          if (currentUser) {
            try {
              const meResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
              if (meResponse.data) {
                setCurrentUser(meResponse.data);
                setSubscription({
                  plan: meResponse.data.subscription,
                  ...meResponse.data.subscription_limits,
                });
              }
            } catch (e) {
              console.error("Failed to refresh user data:", e);
            }
          } else if (userEmail) {
            fetchSubscription(userEmail);
          }
          return;
        }
      } catch (error) {
        console.error("Payment check failed:", error); // transient — keep polling
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    toast.info("Payment is still confirming — your purchase will be credited shortly.");
  };

  const fetchSubscription = async (email) => {
    try {
      const response = await axios.get(`${API}/subscription/${email}`);
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    }
  };

  // Load saved email on startup
  useEffect(() => {
    const savedEmail = localStorage.getItem('bailey_email');
    if (savedEmail) {
      setUserEmail(savedEmail);
      fetchSubscription(savedEmail);
    }
  }, []);

  useEffect(() => {
    // Only fetch subscription when email looks complete (has @ and .)
    if (userEmail && userEmail.includes('@') && userEmail.includes('.') && userEmail.length > 5) {
      const timer = setTimeout(() => {
        fetchSubscription(userEmail);
      }, 500); // Debounce 500ms
      return () => clearTimeout(timer);
    }
  }, [userEmail]);

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      // Clear previous recording
      if (recordedAudioUrl) {
        URL.revokeObjectURL(recordedAudioUrl);
        setRecordedAudioUrl(null);
        setRecordedAudioBlob(null);
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];

      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        // Create URL for playback
        const audioUrl = URL.createObjectURL(blob);
        setRecordedAudioUrl(audioUrl);
        setRecordedAudioBlob(blob);
        toast.success("Recording saved! Play it back or download.");
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
      toast.info("Recording... Sing, hum, or whistle!");

      // Recording timer (30 second max)
      const timer = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 30) {
            clearInterval(timer);
            stopRecording();
            return 30;
          }
          return prev + 1;
        });
      }, 1000);
      recorder.timerInterval = timer;
    } catch (error) {
      console.error("Recording error:", error);
      if (error.name === 'NotAllowedError' || error.message.includes('denied')) {
        toast.error("🎤 Microphone blocked! Click the 🔒 lock icon in your browser's address bar → Allow Microphone → Refresh page", { duration: 8000 });
      } else if (error.name === 'NotFoundError') {
        toast.error("No microphone found. Please connect a microphone.", { duration: 5000 });
      } else {
        toast.error("Microphone error: " + error.message, { duration: 5000 });
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      clearInterval(mediaRecorder.timerInterval);
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
    }
  };

  const downloadRecording = () => {
    if (recordedAudioBlob) {
      shareOrDownloadBlob(recordedAudioBlob, `vocal-sample-${Date.now()}.webm`, 'audio/webm', { successMessage: 'Vocal sample downloaded!' });
    }
  };

  const clearRecording = () => {
    if (recordedAudioUrl) {
      URL.revokeObjectURL(recordedAudioUrl);
    }
    setRecordedAudioUrl(null);
    setRecordedAudioBlob(null);
    setRecordingTime(0);
  };

  const analyzeVoice = async (audioBlob) => {
    setIsAnalyzing(true);
    setProgress(0);
    setProgressText("Analyzing your melody...");

    let apiCompleted = false;
    let currentProgress = 0;
    const steps = ["Capturing audio...", "Detecting pitch...", "Analyzing melody...", "Finding key...", "Detecting chords...", "Identifying genre...", "Building DNA..."];
    
    const interval = setInterval(() => {
      if (apiCompleted) return;
      
      if (currentProgress < 70) {
        currentProgress += 4;
      } else if (currentProgress < 98) {
        currentProgress += 0.5;
      }
      currentProgress = Math.min(currentProgress, 98);
      setProgress(Math.round(currentProgress));
      
      const stepIndex = Math.min(Math.floor(currentProgress / 15), steps.length - 1);
      setProgressText(steps[stepIndex]);
    }, 100);

    try {
      const formData = new FormData();
      formData.append("file", audioBlob, "voice-melody.webm");
      formData.append("email", userEmail || getGuestEmail());

      const response = await axios.post(`${API}/analyze-voice`, formData);
      apiCompleted = true;
      clearInterval(interval);
      setProgress(100);
      setProgressText("Analysis complete!");
      
      setVoiceAnalysis(response.data);
      setShowVoiceMode(true);
      toast.success("Melody analyzed! Key, chords, and genre detected.");
      
      setTimeout(() => setIsAnalyzing(false), 500);
    } catch (error) {
      apiCompleted = true;
      clearInterval(interval);
      setIsAnalyzing(false);
      toast.error(error.response?.data?.detail || "Voice analysis failed");
    }
  };

  const generateFromVoice = async () => {
    if (!voiceAnalysis) return;
    
    setIsAnalyzing(true);
    setProgress(0);
    setResult(null);
    setCurrentPage(1);

    let apiCompleted = false;
    let currentProgress = 0;
    const steps = ["Using voice DNA...", "Generating prompts...", "Applying style...", "Almost done..."];
    
    const interval = setInterval(() => {
      if (apiCompleted) return;
      
      if (currentProgress < 70) {
        currentProgress += 4;
      } else if (currentProgress < 98) {
        currentProgress += 0.5;
      }
      currentProgress = Math.min(currentProgress, 98);
      setProgress(Math.round(currentProgress));
      
      const stepIndex = Math.min(Math.floor(currentProgress / 25), steps.length - 1);
      setProgressText(steps[stepIndex]);
    }, 100);

    try {
      const response = await axios.post(`${API}/generate-from-voice`, {
        voice_dna: voiceAnalysis,
        prompt_count: promptCount,
        platforms: selectedPlatforms.includes("all") ? "all" : selectedPlatforms.join(","),
        style_mode: styleMode,
        email: userEmail || getGuestEmail()
      }, { timeout: 60000 });

      apiCompleted = true;
      clearInterval(interval);
      setProgress(100);
      setProgressText("Complete!");
      setTimeout(() => { setResult(response.data); setIsAnalyzing(false); }, 200);
      toast.success(`Generated ${response.data.total_prompts} prompts from your melody!`);
      if (userEmail) fetchSubscription(userEmail);
    } catch (error) {
      apiCompleted = true;
      clearInterval(interval);
      setIsAnalyzing(false);
      setProgress(0);
      if (error.response?.status === 402) {
        if (currentUser) { setShowTokenExhausted(true); }
        else { toast.error("Free limit reached! Upgrade to Pro."); setShowPricing(true); }
      } else {
        toast.error(error.response?.data?.detail || "Generation failed");
      }
    }
  };

  const filteredPrompts = useMemo(() => {
    if (!result?.prompts) return [];
    let filtered = result.prompts;
    if (platformFilter !== "all") filtered = filtered.filter(p => p.platform === platformFilter);
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p => p.prompt.toLowerCase().includes(q) || p.dna_code.toLowerCase().includes(q));
    }
    return filtered;
  }, [result, platformFilter, searchQuery]);

  const totalPages = Math.ceil(filteredPrompts.length / perPage);
  const paginatedPrompts = filteredPrompts.slice((currentPage - 1) * perPage, currentPage * perPage);

  const onDrop = useCallback((files) => {
    if (files[0]) {
      setUploadedFile(files[0]);
      toast.success(`Loaded: ${files[0].name}`);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 
      "audio/*": [".mp3", ".wav", ".m4a", ".flac", ".ogg", ".aac", ".wma"],
      "audio/mpeg": [".mp3"],
      "audio/wav": [".wav"],
      "audio/x-m4a": [".m4a"],
      "audio/mp4": [".m4a"]
    },
    maxFiles: 1,
    disabled: isAnalyzing,
    noClick: false,
    noKeyboard: false,
    noDrag: false,
    useFsAccessApi: false, // Better iOS compatibility
  });

  const handleGenerate = async () => {
    // Allow generation if: file uploaded, OR style description, OR genre selected, OR artist selected, OR artist name typed
    const hasInput = uploadedFile || styleDescription.trim() || selectedGenre !== 'auto' || selectedArtist !== 'none' || artistName.trim();
    
    if (!hasInput) {
      return toast.error("Drop a track, describe your style, or select a genre/artist first!");
    }

    console.log("=== GENERATE STARTED ===");
    console.log("API URL:", API);
    console.log("Style Description:", styleDescription);
    console.log("Selected Genre:", selectedGenre);
    console.log("Selected Artist:", selectedArtist);
    console.log("Artist Name:", artistName);
    console.log("User Email:", userEmail);
    
    setIsAnalyzing(true);
    setResult(null);
    setCurrentPage(1);

    try {
      let response;
      
      if (uploadedFile) {
        const formData = new FormData();
        formData.append("file", uploadedFile);
        formData.append("prompt_count", promptCount.toString());
        formData.append("platforms", selectedPlatforms.includes("all") ? "all" : selectedPlatforms.join(","));
        formData.append("style_mode", styleMode);
        formData.append("email", userEmail || getGuestEmail());
        formData.append("target_genre", selectedGenre);
        formData.append("artist_style", selectedArtist);
        formData.append("artist_name", artistName);
        formData.append("style_description", styleDescription);
        formData.append("enable_2026_sounds", enable2026Sounds.toString());
      
        response = await runAnalyzeJob(formData);
      } else {
        response = await axios.post(`${API}/generate-from-style`, {
          prompt_count: promptCount,
          platforms: selectedPlatforms.includes("all") ? "all" : selectedPlatforms.join(","),
          style_mode: styleMode,
          email: userEmail || getGuestEmail(),
          target_genre: selectedGenre,
          artist_style: selectedArtist,
          artist_name: artistName,
          style_description: styleDescription,
          enable_2026_sounds: enable2026Sounds
        }, { timeout: 120000 });
      }
      
      // Done - show results
      console.log("API Success - showing results:", response.data);
      setIsAnalyzing(false);
      setResult(response.data);
      toast.success(`Generated ${response.data.total_prompts} prompts!`);
      if (userEmail) fetchSubscription(userEmail);
      
    } catch (error) {
      console.error("=== API ERROR ===");
      console.error("Error object:", error);
      console.error("Error message:", error.message);
      console.error("Error response:", error.response);
      console.error("Error response data:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      setIsAnalyzing(false);
      
      const errorDetail = error.response?.data?.detail || "";
      
      // Show detailed error to user
      if (error.message === "Network Error") {
        toast.error("Network error - check your internet connection", { duration: 5000 });
      } else if (error.response?.status === 402) {
        if (currentUser) { setShowTokenExhausted(true); }
        else { toast.error("Free limit reached! Upgrade to Pro for unlimited."); setShowPricing(true); }
      } else if (error.response?.status === 400) {
        toast.error(errorDetail || "Could not read audio file. Try MP3 or WAV format.", { duration: 5000 });
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error("Analysis timed out. Try a shorter audio file.");
      } else {
        toast.error(errorDetail || `Error: ${error.message || "Analysis failed"}`, { duration: 5000 });
      }
    }
  };

  const copyAllPrompts = async () => {
    if (!filteredPrompts.length) {
      toast.error("No prompts to copy!");
      return;
    }
    const text = filteredPrompts.map(p => `[${p.platform.toUpperCase()}] ${p.dna_code}\n${p.prompt}`).join("\n\n");
    await universalCopy(text, `Copied ${filteredPrompts.length} prompts!`);
  };

  const exportTxt = async () => {
    if (!filteredPrompts || !filteredPrompts.length) {
      toast.error("No prompts! Generate some first.");
      return;
    }
    
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `sound-dna-prompts-${timestamp}.txt`;
    const text = `MANNIS - Style Prompts\nGenerated: ${new Date().toLocaleString()}\nTotal Prompts: ${filteredPrompts.length}\n${'='.repeat(50)}\n\n` + 
      filteredPrompts.map(p => `=== ${PLATFORMS[p.platform]?.name || 'MANNIS'} ===\nDNA: ${p.dna_code}\n${p.prompt}`).join("\n\n---\n\n");
    
    await shareOrDownloadText(text, filename, 'text/plain', {
      desktopPicker: true,
      successMessage: `Downloaded: ${filename}`,
    });
  };
  
  const sharePrompts = async () => {
    if (!filteredPrompts || !filteredPrompts.length) {
      toast.error("No prompts to share!");
      return;
    }
    
    const text = filteredPrompts.map(p => `=== ${PLATFORMS[p.platform]?.name || 'MANNIS'} ===\nDNA: ${p.dna_code}\n${p.prompt}`).join("\n\n---\n\n");
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'MANNIS Prompts',
          text: text,
        });
        toast.success("Shared!");
      } catch (err) {
        if (err.name !== 'AbortError') {
          toast.error("Share failed");
        }
      }
    } else {
      // Copy to clipboard as fallback
      await universalCopy(text, "Copied! Paste anywhere");
    }
  };

  // Social Media Share Functions
  const shareToTwitter = (text) => {
    const tweetText = encodeURIComponent(text || "Check out MANNIS - AI Music Style Prompt Generator! 🎵🧬 Create professional music prompts for Suno, Udio & more!\n\n#SoundDNA #AIMusic #MusicProduction");
    const url = encodeURIComponent("https://sounddna.pro");
    window.open(`https://twitter.com/intent/tweet?text=${tweetText}&url=${url}`, '_blank', 'width=550,height=420');
  };

  const shareToFacebook = () => {
    const url = encodeURIComponent("https://sounddna.pro");
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=550,height=420');
  };

  const shareToLinkedIn = () => {
    const url = encodeURIComponent("https://sounddna.pro");
    const title = encodeURIComponent("MANNIS - AI Music Style Prompt Generator");
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank', 'width=550,height=420');
  };

  const copyShareLink = () => {
    const shareText = "🎵🧬 MANNIS - AI Music Style Prompt Generator\n\nCreate professional music prompts for Suno, Udio & all music AI platforms!\n\n70,000+ Style Prompts • iPhone Ready • One-Tap Copy\n\n👉 https://sounddna.pro\n\n#SoundDNA #AIMusic #MusicProduction #Suno #Udio";
    universalCopy(shareText, "📋 Share text copied! Paste on any social media");
  };
  
  const getPromptsText = () => {
    if (!filteredPrompts || !filteredPrompts.length) return "";
    return filteredPrompts.map(p => `=== ${PLATFORMS[p.platform]?.name || 'MANNIS'} ===\nDNA: ${p.dna_code}\n${p.prompt}`).join("\n\n---\n\n");
  };

  const reset = () => { setResult(null); setUploadedFile(null); setSearchQuery(""); setPlatformFilter("all"); setCurrentPage(1); };

  const togglePlatform = (platform) => {
    if (platform === "all") setSelectedPlatforms(["all"]);
    else {
      let updated = selectedPlatforms.filter(p => p !== "all");
      updated = updated.includes(platform) ? updated.filter(p => p !== platform) : [...updated, platform];
      setSelectedPlatforms(updated.length ? updated : ["all"]);
    }
  };


  // Download song to Desktop with Save As dialog
  const downloadSong = async (url, filename, format = 'wav') => {
    const finalName = filename || `sound-dna-song.${format}`;
    const mime = format === 'wav' ? 'audio/wav' : 'audio/mpeg';
    // For WAV, transcode server-side so the bytes are real WAV, not relabelled MP3
    const fetchUrl = format === 'wav'
      ? `/api/download-song-wav?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(finalName)}`
      : url;
    let fullFetchUrl = fetchUrl;
    if (fullFetchUrl.startsWith('/')) fullFetchUrl = `${API_ORIGIN}${fullFetchUrl}`;

    const blobToFile = (blob) => {
      // Save a REAL audio file (never open the remote URL — iOS renders that as a
      // fullscreen video/QuickTime player with no way to save the audio).
      const burl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = burl;
      a.download = finalName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { if (a.parentNode) document.body.removeChild(a); URL.revokeObjectURL(burl); }, 150);
    };

    try {
      toast.loading(`⬇️ Preparing ${format.toUpperCase()}…`, { id: 'song-dl' });
      const response = await fetch(fullFetchUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      toast.dismiss('song-dl');

      if (isIOSDevice()) {
        // iOS: share the actual audio file → "Save to Files" and back to the app.
        const file = new File([blob], finalName, { type: mime });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file] });
            toast.success(`✅ ${format.toUpperCase()} saved — find it in Files`);
            return;
          } catch (err) {
            if (err && err.name === 'AbortError') return; // user cancelled
            // otherwise fall through to a real blob download (still an audio file)
          }
        }
        blobToFile(blob);
        toast.success(`📥 ${format.toUpperCase()} downloading — check Files / Downloads`, { duration: 5000 });
        return;
      }

      // Desktop: silent blob download
      blobToFile(blob);
      toast.success(`✅ ${format.toUpperCase()} saved!`);
    } catch (error) {
      toast.dismiss('song-dl');
      console.error("Download error:", error);
      toast.error(`Couldn't prepare the ${format.toUpperCase()} file — please try again`);
    }
  };

  // Download Drum MIDI from Song Creator
  const downloadDrumMidi = async () => {
    if (!generatedDrumPattern) {
      toast.error("Generate a drum pattern first!");
      return;
    }
    
    const sunoPrompt = generatedDrumPattern?.suno_prompt || `${drumGenre} drums, ${drumKit} kit, ${drumTempo} BPM`;
    toast.info("🎹 Generating MIDI file...");
    
    try {
      const response = await axios.post(`${API}/generate-midi`, {
        style_prompt: sunoPrompt,
        bars: 32,
        preset_id: null
      });
      
      if (response.data.success) {
        const { filename, params } = response.data;
        setMo3LastMidi(filename);
        const downloadUrl = `${API}/download-midi/${filename}`;
        const fetchResponse = await fetch(downloadUrl);
        if (!fetchResponse.ok) throw new Error("Download failed");
        const blob = await fetchResponse.blob();
        await downloadFileWithPicker(
          blob, 
          filename, 
          'audio/midi', 
          `🎹 MIDI saved! ${params?.genre || drumGenre} @ ${params?.tempo || drumTempo} BPM`,
          downloadUrl
        );
      }
    } catch (error) {
      console.error("MIDI generation error:", error);
      toast.error("Failed to generate MIDI");
    }
  };

  // Download Drum WAV from Song Creator
  const downloadDrumWav = async () => {
    if (!generatedDrumPattern && !drumStylePrompt) {
      toast.error("Select a style prompt or generate a drum pattern first!");
      return;
    }
    
    const sunoPrompt = generatedDrumPattern?.suno_prompt || drumStylePrompt || `${drumGenre} drums, ${drumKit} kit, ${drumTempo} BPM`;
    
    // REUSE GUARD: if the previewed track already matches this exact prompt,
    // save that file — don't burn credits on a second Suno render of a different take.
    if (drumAudioUrl && drumAudioRenderedFor.current && drumAudioRenderedFor.current === sunoPrompt) {
      setDrumWavReady({
        url: drumAudioUrl,
        filename: `SoundDNA_${generatedDrumPattern?.genre || drumGenre}_${drumTempo}BPM.wav`
      });
      toast.success('🎵 Your previewed track is ready — tap "SAVE WAV" to download it (no re-render needed).', { duration: 7000 });
      return;
    }
    
    setDrumWavReady(null);
    toast.info("🎵 Generating WAV file (30-90 seconds)...");
    await warnIfArtistNames(sunoPrompt);
    
    try {
      const response = await axios.post(`${API}/generate-drum-preview`, {
        genre: generatedDrumPattern?.genre || drumGenre,
        tempo: generatedDrumPattern?.tempo || drumTempo,
        kit: generatedDrumPattern?.kit || drumKit,
        swing: generatedDrumPattern?.swing || drumSwing,
        key: drumKey,
        mode: drumMode,
        energy: drumEnergy,
        complexity: drumComplexity,
        sections: drumSections,
        style_prompt: sunoPrompt,
        prompt: sunoPrompt,
        email: currentUser?.email || userEmail || getGuestEmail()
      });
      
      if (response.data.task_id) {
        const taskId = response.data.task_id;
        
        // Poll for completion. Backend generation (MiniMax via Replicate) has been
        // observed taking ~2 minutes; the old 50 x 3s = 150s budget left almost no
        // margin and produced real "timed out" failures on completely healthy
        // generations. 100 x 3s = 300s gives real headroom, with an interim toast
        // so a long wait doesn't look stuck.
        let attempts = 0;
        const maxAttempts = 100;
        let shownSlowNotice = false;
        let wavConsecutiveErrors = 0;
        
        const pollInterval = setInterval(async () => {
          attempts++;
          if (!shownSlowNotice && attempts === 23) { // ~90s in
            shownSlowNotice = true;
            toast.info("Still rendering your drums — this one's taking a bit longer, hang tight...");
          }
          try {
            const statusResponse = await axios.get(`${API}/drum-preview-status/${taskId}`);
            wavConsecutiveErrors = 0;
            
            if (statusResponse.data.status === 'completed' && statusResponse.data.audio_url) {
              clearInterval(pollInterval);

              // RENDER PHASE done — resolve the URL and arm the SAVE button.
              // The actual save happens on the user's next tap (a real anchor navigation),
              // because a download fired from inside this poll is not a user gesture and
              // iOS Safari silently blocks it.
              const resolvedUrl = statusResponse.data.audio_url.startsWith('/api')
                ? `${API_ORIGIN}${statusResponse.data.audio_url}`
                : statusResponse.data.audio_url;
              setDrumAudioUrl(resolvedUrl);
              drumAudioRenderedFor.current = sunoPrompt;
              setDrumWavReady({
                url: resolvedUrl,
                filename: `SoundDNA_${generatedDrumPattern?.genre || drumGenre}_${drumTempo}BPM.wav`
              });
              toast.success('🎵 WAV rendered! Tap "SAVE WAV" to download it.', { duration: 8000 });
            } else if (statusResponse.data.status === 'failed' || attempts >= maxAttempts) {
              clearInterval(pollInterval);
              toast.error(statusResponse.data.error || "WAV generation failed or timed out", { duration: 9000 });
            }
          } catch (err) {
            console.error("Poll error:", err);
            wavConsecutiveErrors++;
            if (wavConsecutiveErrors >= 5 || attempts >= maxAttempts) {
              clearInterval(pollInterval);
              toast.error("Lost connection while rendering the WAV. Please check your network and try again.");
            }
          }
        }, 3000);
      }
    } catch (error) {
      console.error("WAV generation error:", error);
      if (error.response?.status === 402) {
        toast.error(error.response?.data?.detail || "WAV downloads require an active plan or tokens — start your $3.99 trial or choose a plan!", { duration: 8000 });
        if (currentUser) { setShowTokenExhausted(true); }
        else { setShowPricing(true); }
      } else {
        toast.error("Failed to generate WAV");
      }
    }
  };

  // Generate AI Lyrics
  const generateLyrics = async () => {
    if (!lyricsTheme.trim()) {
      toast.error("Please enter a theme or topic for your lyrics");
      return;
    }
    
    setIsGeneratingLyrics(true);
    setGeneratedLyrics("");
    
    try {
      const response = await axios.post(`${API}/generate-lyrics`, {
        theme: lyricsTheme,
        genre: lyricsGenre,
        mood: lyricsMood,
        language: lyricsLanguage,
        model: lyricsModel,
        email: userEmail || getGuestEmail()
      });
      
      if (response.data.lyrics) {
        setGeneratedLyrics(response.data.lyrics);
        toast.success(`Lyrics generated in ${lyricsLanguage} with ${response.data.model_used || 'AI'}!`);
      } else {
        toast.error("Failed to generate lyrics");
      }
    } catch (error) {
      console.error("Lyrics generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate lyrics. Try again!");
    } finally {
      setIsGeneratingLyrics(false);
    }
  };
  
  // Translate DNA Lyrics to selected language
  const translateDNALyrics = async () => {
    if (!generatedLyrics.trim()) {
      toast.error('Generate lyrics first, then translate');
      return;
    }
    
    if (lyricsLanguage === 'English') {
      toast.info('Lyrics are already in English');
      return;
    }
    
    setIsTranslatingLyrics(true);
    toast.info(`🌍 Translating to ${lyricsLanguage}...`);
    
    try {
      const response = await fetch(`${API}/translate-lyrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: generatedLyrics,
          target_language: lyricsLanguage,
          keep_song_structure: true
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.translated_text) {
        setGeneratedLyrics(data.translated_text);
        toast.success(`✅ Lyrics translated to ${lyricsLanguage}!`);
      } else {
        throw new Error(data.detail || 'Translation failed');
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error(error.message || 'Failed to translate lyrics');
    } finally {
      setIsTranslatingLyrics(false);
    }
  };

  // Translate Rhyme/Flow lyrics to selected language
  const translateRhymeLyrics = async () => {
    if (!generatedBars?.raw_lyrics?.trim()) {
      toast.error('Generate lyrics first, then translate');
      return;
    }
    
    if (rhymeLanguage === 'English') {
      toast.info('Lyrics are already in English');
      return;
    }
    
    setIsTranslatingRhyme(true);
    toast.info(`🌍 Translating to ${rhymeLanguage}...`);
    
    try {
      const response = await fetch(`${API}/translate-lyrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: generatedBars.raw_lyrics,
          target_language: rhymeLanguage,
          keep_song_structure: true
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.translated_text) {
        setGeneratedBars(prev => ({ ...prev, raw_lyrics: data.translated_text }));
        toast.success(`✅ Lyrics translated to ${rhymeLanguage}!`);
      } else {
        throw new Error(data.detail || 'Translation failed');
      }
    } catch (error) {
      console.error('Translation error:', error);
      toast.error(error.message || 'Failed to translate lyrics');
    } finally {
      setIsTranslatingRhyme(false);
    }
  };

  // Generate Rhyme/Flow Bars
  const generateBars = async () => {
    const topic = (rhymeTopic || "").trim();
    if (!topic) {
      toast.error("Please enter a topic for your bars");
      return;
    }
    
    if (rhymeDuration < 2 || rhymeDuration > 5) {
      toast.error("Duration must be between 2 and 5 minutes");
      return;
    }
    
    setIsGeneratingBars(true);
    setGeneratedBars(null);
    
    try {
      const payload = {
        topic: topic,
        style: rhymeStyle,
        rhyme_scheme: rhymeScheme,
        flow_pattern: flowPattern,
        bars: barsCount,
        language: rhymeLanguage,
        include_syllables: includeSyllables,
        duration_minutes: rhymeDuration,
        email: userEmail || getGuestEmail()
      };
      let data;
      if (barsCount >= 32) {
        // Ticket 261435: long songs outlast the proxy's request limit, so run
        // them as a background job and poll until the lyrics are ready.
        const start = await axios.post(`${API}/generate-rhyme-flow/start`, payload, { timeout: 30000 });
        const jobId = start.data.job_id;
        const t0 = Date.now();
        for (;;) {
          await new Promise((r) => setTimeout(r, 3000));
          const st = (await axios.get(`${API}/generate-rhyme-flow/status/${jobId}`, { timeout: 20000 })).data;
          if (st.status === "done") { data = st.result; break; }
          if (st.status === "error") throw { response: { data: { detail: st.detail } } };
          if (Date.now() - t0 > 300000) throw { response: { data: { detail: "Generation is taking longer than usual. Please try again." } } };
        }
      } else {
        data = (await axios.post(`${API}/generate-rhyme-flow`, payload, { timeout: 60000 })).data;
      }
      
      if (data && data.bars) {
        setGeneratedBars(data);
        toast.success(`Generated ${data.total_bars} bars! 🔥`);
      } else {
        toast.error("Failed to generate bars");
      }
    } catch (error) {
      console.error("Bars generation error:", error);
      if (error.request && !error.response) {
        toast.error("Can't reach the server — check your internet connection and try again");
      } else {
        toast.error(error.response?.data?.detail || "Failed to generate bars. Try again!");
      }
    } finally {
      setIsGeneratingBars(false);
    }
  };

  // Format user's own lyrics into structured bars
  const formatBarsFromLyrics = async () => {
    if (!customLyrics.trim() || customLyrics.trim().length < 10) {
      toast.error("Please enter your lyrics first (at least 10 characters)");
      return;
    }
    
    setIsGeneratingBars(true);
    setGeneratedBars(null);
    
    try {
      const response = await axios.post(`${API}/generate-bars-from-lyrics`, {
        lyrics: customLyrics,
        language: rhymeLanguage,
        style: rhymeStyle,
        rhyme_scheme: rhymeScheme,
        flow_pattern: flowPattern,
        include_syllables: includeSyllables,
        email: userEmail || getGuestEmail()
      }, { timeout: 60000 });
      
      if (response.data.bars) {
        setGeneratedBars(response.data);
        toast.success(`Formatted ${response.data.total_bars} bars in ${rhymeLanguage}! 🔥`);
      } else {
        toast.error("Failed to format bars");
      }
    } catch (error) {
      console.error("Bars formatting error:", error);
      toast.error(error.response?.data?.detail || "Failed to format bars. Try again!");
    } finally {
      setIsGeneratingBars(false);
    }
  };

  // Generate Drum Pattern
  const generateDrumPattern = async () => {
    setIsGeneratingDrum(true);
    setGeneratedDrumPattern(null);
    setDrumWavReady(null);
    
    try {
      const response = await axios.post(`${API}/generate-drum-pattern`, {
        genre: drumGenre,
        tempo: drumTempo,
        pattern_type: drumPatternType,
        bars: drumBars,
        swing: drumSwing,
        kit: drumKit,
        include_variations: drumIncludeVariations,
        email: currentUser?.email || userEmail || getGuestEmail(),
        quick_mode: true,  // Fast instant generation
        style_context: drumStylePrompt || null  // 40K Style Prompt integration
      }, { timeout: 15000 });
      
      if (response.data.pattern) {
        setGeneratedDrumPattern(response.data);
        // Auto-save to library
        autoSavePattern(response.data);
        toast.success(`🥁 Generated ${drumGenre} drum pattern @ ${drumTempo} BPM!`);
      } else {
        toast.error("Failed to generate drum pattern");
      }
    } catch (error) {
      console.error("Drum pattern generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate drum pattern. Try again!");
    } finally {
      setIsGeneratingDrum(false);
    }
  };

  // ADVANCED DRUM PATTERN - Quantum Rhythmic Design with Full Song Structure
  const generateAdvancedDrumPattern = async () => {
    setIsGeneratingDrum(true);
    setAdvancedDrumResult(null);
    setGeneratedDrumPattern(null);
    setDrumWavReady(null);
    
    try {
      toast.info('🧬 Generating quantum rhythmic architecture...', { duration: 3000 });
      
      const response = await axios.post(`${API}/generate-advanced-drum-pattern`, {
        genre: drumGenre,
        tempo: drumTempo,
        kit: drumKit,
        swing: drumSwing,
        key: drumKey,
        mode: drumMode,
        energy: drumEnergy,
        complexity: drumComplexity,
        sections: drumSections,
        bars_per_section: drumBarsPerSection,
        include_inversions: drumIncludeInversions,
        include_fills: drumIncludeFills,
        include_variations: drumIncludeVariations,
        style_prompt: drumStylePrompt || null,  // 40K Style Prompt integration
        email: currentUser?.email || userEmail || getGuestEmail()
      }, { timeout: 120000 });
      
      if (response.data.success) {
        setAdvancedDrumResult(response.data);
        const patternData = { 
          pattern: response.data.pattern, 
          suno_prompt: response.data.master_suno_prompt,
          genre: response.data.genre,
          tempo: response.data.tempo,
          kit: response.data.kit,
          key: response.data.key,
          swing: response.data.swing,
          isQuantum: true,
          sections: response.data.sections,
          total_bars: response.data.total_bars
        };
        setGeneratedDrumPattern(patternData);
        
        // Auto-save Quantum patterns to Library
        autoSavePattern({
          ...patternData,
          style_prompt: drumStylePrompt,
          name: `Quantum ${drumGenre} - ${drumTempo} BPM (${response.data.total_bars} bars)`
        });
        
        toast.success(`🧬 Quantum drum pattern ready! ${response.data.total_bars} bars across ${response.data.sections.length} sections`, { duration: 5000 });
      } else {
        toast.error("Failed to generate advanced drum pattern");
      }
    } catch (error) {
      console.error("Advanced drum pattern generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate advanced pattern. Try again!");
    } finally {
      setIsGeneratingDrum(false);
    }
  };

  // Toggle section in advanced drum mode
  const toggleDrumSection = (section) => {
    setDrumSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Load 40K Style Prompt categories for Drum Machine
  const loadDrumStyleCategories = async () => {
    try {
      const response = await axios.get(`${API}/suno-prompts/categories`);
      if (response.data.categories) {
        setDrumStyleCategories(response.data.categories);
      }
    } catch (error) {
      console.error("Failed to load style categories:", error);
    }
  };

  // Auto-load 40K categories on mount for mobile compatibility
  useEffect(() => {
    loadDrumStyleCategories();
  }, []);

  // Load prompts for selected category in Drum Machine with PAGINATION
  const loadDrumCategoryPrompts = async (category, page = 1) => {
    // Guard against empty category - just reset state and return
    if (!category) {
      setSelectedDrumCategory("");
      setDrumCategoryPrompts([]);
      setDrumPromptTotal(0);
      setDrumPromptTotalPages(1);
      return;
    }
    try {
      setSelectedDrumCategory(category);
      setDrumPromptPage(page);
      // Request prompts with pagination
      const response = await axios.get(`${API}/suno-prompts/${encodeURIComponent(category)}?page=${page}&limit=${DRUM_PROMPTS_PER_PAGE}`);
      if (response.data.prompts) {
        setDrumCategoryPrompts(response.data.prompts);
        setDrumPromptTotal(response.data.total || response.data.prompts.length);
        setDrumPromptTotalPages(response.data.total_pages || 1);
      }
    } catch (error) {
      console.error("Failed to load prompts:", error);
      setDrumCategoryPrompts([]);
      setDrumPromptTotal(0);
      setDrumPromptTotalPages(1);
    }
  };

  // Apply style prompt to drum machine
  const applyStyleToDrum = (prompt) => {
    setDrumStylePrompt(prompt);
    setShowDrumStyleBrowser(false);
    toast.success(`🎨 Style applied to Drum Machine: ${prompt.substring(0, 50)}...`);
  };
  
  // Apply style prompt to Suno AI
  const applyStyleToSuno = (prompt) => {
    setSunoStyle(prompt);
    toast.success(`🎤 Style applied to Suno AI: ${prompt.substring(0, 50)}...`);
  };
  
  // Apply style prompt to BOTH drum machine AND Suno
  const applyStyleToBoth = (prompt) => {
    setDrumStylePrompt(prompt);
    setSunoStyle(prompt);
    setShowDrumStyleBrowser(false);
    
    // Extract genre, tempo, kit from style prompt to sync Quantum Rhythmic Design
    const genrePatterns = [
      { pattern: /trap|808/i, genre: 'trap', kit: '808', tempo: 140 },
      { pattern: /drill/i, genre: 'drill', kit: '808', tempo: 145 },
      { pattern: /boom.?bap|90s|golden.?era/i, genre: 'boom bap', kit: 'mpc', tempo: 95 },
      { pattern: /r&b|rnb|slow.?jam/i, genre: 'r&b', kit: '808', tempo: 85 },
      { pattern: /house|edm|dance/i, genre: 'house', kit: '909', tempo: 128 },
      { pattern: /techno/i, genre: 'techno', kit: '909', tempo: 135 },
      { pattern: /lo.?fi|lofi|chill/i, genre: 'lo-fi', kit: 'sp1200', tempo: 80 },
      { pattern: /afro|afrobeat|amapiano/i, genre: 'afrobeats', kit: 'acoustic', tempo: 113 },
      { pattern: /reggaeton|latin/i, genre: 'reggaeton', kit: '808', tempo: 95 },
      { pattern: /gospel/i, genre: 'gospel', kit: 'acoustic', tempo: 110 },
      { pattern: /jazz/i, genre: 'jazz', kit: 'acoustic', tempo: 120 },
      { pattern: /rock|metal/i, genre: 'rock', kit: 'acoustic', tempo: 130 },
      { pattern: /pop/i, genre: 'pop', kit: 'linn', tempo: 115 },
      { pattern: /soul|neo.?soul/i, genre: 'neo soul', kit: 'sp1200', tempo: 90 },
      { pattern: /funk/i, genre: 'funk', kit: 'linn', tempo: 110 },
      { pattern: /disco/i, genre: 'disco', kit: '909', tempo: 120 },
      { pattern: /hip.?hop/i, genre: 'hip hop', kit: 'mpc', tempo: 95 },
      { pattern: /phonk/i, genre: 'phonk', kit: '808', tempo: 130 },
      { pattern: /jersey.?club/i, genre: 'jersey club', kit: '808', tempo: 140 },
      { pattern: /electronic/i, genre: 'electronic', kit: '909', tempo: 128 },
    ];
    
    // Extract BPM if present in prompt
    const bpmMatch = prompt.match(/(\d{2,3})\s*bpm/i);
    let extractedTempo = bpmMatch ? parseInt(bpmMatch[1]) : null;
    
    // Find matching genre from style prompt
    let extractedGenre = null;
    let extractedKit = null;
    for (const { pattern, genre, kit, tempo } of genrePatterns) {
      if (pattern.test(prompt)) {
        extractedGenre = genre;
        extractedKit = kit;
        if (!extractedTempo) extractedTempo = tempo;
        break;
      }
    }
    
    // Apply extracted settings to Drum Machine controls
    if (extractedGenre) setDrumGenre(extractedGenre);
    if (extractedKit) setDrumKit(extractedKit);
    if (extractedTempo) setDrumTempo(extractedTempo);
    
    // Generate Mo3 Song Structure based on style prompt
    const structure = generateMo3Structure(prompt, songLengthMinutes, extractedTempo || 120);
    setSongStructure(structure);
    
    toast.success(`🎵 Style synced! ${extractedGenre ? extractedGenre.toUpperCase() : 'Custom'} @ ${extractedTempo || 120} BPM`);
  };

  // Generate drum pattern from ANY style prompt (for 40K library integration)
  const generateDrumFromStylePrompt = async (stylePrompt) => {
    setIsGeneratingDrum(true);
    setGeneratedDrumPattern(null);
    
    // Extract genre from style prompt
    const genrePatterns = [
      { pattern: /trap|808/i, genre: 'trap', kit: '808', tempo: 140 },
      { pattern: /drill/i, genre: 'drill', kit: '808', tempo: 145 },
      { pattern: /boom.?bap|90s|golden.?era/i, genre: 'boom bap', kit: 'mpc', tempo: 95 },
      { pattern: /r&b|rnb|slow.?jam/i, genre: 'r&b', kit: '808', tempo: 85 },
      { pattern: /house|edm|dance/i, genre: 'house', kit: '909', tempo: 128 },
      { pattern: /techno/i, genre: 'techno', kit: '909', tempo: 135 },
      { pattern: /lo.?fi|lofi|chill/i, genre: 'lo-fi', kit: 'sp1200', tempo: 80 },
      { pattern: /afro|afrobeat/i, genre: 'afrobeats', kit: 'acoustic', tempo: 105 },
      { pattern: /reggaeton|latin/i, genre: 'reggaeton', kit: '808', tempo: 95 },
      { pattern: /gospel/i, genre: 'gospel', kit: 'acoustic', tempo: 110 },
      { pattern: /jazz/i, genre: 'jazz', kit: 'acoustic', tempo: 120 },
      { pattern: /rock|metal/i, genre: 'rock', kit: 'acoustic', tempo: 130 },
      { pattern: /pop/i, genre: 'pop', kit: 'linn', tempo: 115 },
      { pattern: /soul|neo.?soul/i, genre: 'neo soul', kit: 'sp1200', tempo: 90 },
      { pattern: /funk/i, genre: 'funk', kit: 'linn', tempo: 110 },
      { pattern: /disco/i, genre: 'disco', kit: '909', tempo: 120 },
      { pattern: /hip.?hop/i, genre: 'hip hop', kit: 'mpc', tempo: 95 },
      { pattern: /phonk/i, genre: 'phonk', kit: '808', tempo: 130 },
      { pattern: /jersey.?club/i, genre: 'jersey club', kit: '808', tempo: 140 },
      { pattern: /amapiano/i, genre: 'amapiano', kit: 'log drums', tempo: 113 },
    ];
    
    // Extract BPM if present
    const bpmMatch = stylePrompt.match(/(\d{2,3})\s*bpm/i);
    let extractedTempo = bpmMatch ? parseInt(bpmMatch[1]) : null;
    
    // Find matching genre
    let extractedGenre = 'trap';
    let extractedKit = '808';
    for (const { pattern, genre, kit, tempo } of genrePatterns) {
      if (pattern.test(stylePrompt)) {
        extractedGenre = genre;
        extractedKit = kit;
        if (!extractedTempo) extractedTempo = tempo;
        break;
      }
    }
    
    // Default tempo if not found
    if (!extractedTempo) extractedTempo = 120;
    
    try {
      const response = await axios.post(`${API}/generate-drum-pattern`, {
        genre: extractedGenre,
        tempo: extractedTempo,
        pattern_type: 'syncopated',
        bars: 8,
        swing: 'light',
        kit: extractedKit,
        include_variations: true,
        style_context: stylePrompt, // Pass the full style prompt for context
        email: currentUser?.email || userEmail || getGuestEmail(),
        quick_mode: true  // Fast instant generation
      }, { timeout: 15000 });
      
      if (response.data.pattern) {
        setGeneratedDrumPattern(response.data);
        setDrumGenre(extractedGenre);
        setDrumTempo(extractedTempo);
        setDrumKit(extractedKit);
        
        // Auto-save to library
        autoSavePattern({...response.data, genre: extractedGenre, tempo: extractedTempo, kit: extractedKit});
        
        // Open Song Creator to show the pattern
        setFullscreenTool('song-creator');
        
        toast.success(`🥁 Generated ${extractedGenre} drum @ ${extractedTempo} BPM from style!`);
      } else {
        toast.error("Failed to generate drum pattern");
      }
    } catch (error) {
      console.error("Drum pattern generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate drum pattern. Try again!");
    } finally {
      setIsGeneratingDrum(false);
    }
  };

  const copyDrumPattern = () => {
    if (generatedDrumPattern?.pattern) {
      universalCopy(generatedDrumPattern.pattern, "🥁 Drum pattern copied!");
    }
  };

  // Extract Suno prompt from drum pattern
  const extractSunoPromptFromDrum = (pattern) => {
    if (!pattern) return '';
    // Look for the SUNO/UDIO PROMPT section
    const sunoMatch = pattern.match(/\*\*🎹 SUNO\/UDIO PROMPT\*\*\s*\n?([\s\S]*?)(?=\n\*\*|$)/i);
    if (sunoMatch && sunoMatch[1]) {
      return sunoMatch[1].trim();
    }
    // Fallback - try to find any paragraph description
    const lines = pattern.split('\n').filter(l => l.trim() && !l.startsWith('**') && !l.startsWith('```') && !l.includes('|'));
    return lines.slice(0, 3).join(' ').trim();
  };

  // Copy just the Suno-ready prompt
  const copySunoPromptFromDrum = () => {
    // First priority: Use the suno_prompt returned from the API (full 2026 hit record prompt)
    if (generatedDrumPattern?.suno_prompt) {
      universalCopy(generatedDrumPattern.suno_prompt, "🎵 Full Suno style prompt copied! Paste into Suno's style field.");
      return;
    }
    
    // Second priority: Use the audio prompt if available
    if (drumAudioSunoPrompt) {
      universalCopy(drumAudioSunoPrompt, "🎵 Full Suno style prompt copied! Paste into Suno's style field.");
      return;
    }
    
    // Fallback: Extract from pattern text
    if (generatedDrumPattern?.pattern) {
      const sunoPrompt = extractSunoPromptFromDrum(generatedDrumPattern.pattern);
      if (sunoPrompt) {
        universalCopy(sunoPrompt, "🎵 Suno drum prompt copied! Paste into Suno's style field.");
      } else {
        toast.error("Couldn't extract Suno prompt. Copy full pattern instead.");
      }
    }
  };

  // ============================================
  // DRUM MACHINE LIBRARY - Save & Manage Patterns
  // ============================================
  
  // Load drum library from localStorage on mount
  useEffect(() => {
    try {
      const savedLibrary = localStorage.getItem('sounddna_drum_library');
      if (savedLibrary) {
        setDrumLibrary(JSON.parse(savedLibrary));
      }
    } catch (e) {
      console.error('Failed to load drum library:', e);
    }
  }, []);

  // Save drum library to localStorage whenever it changes
  useEffect(() => {
    if (drumLibrary.length > 0) {
      try {
        localStorage.setItem('sounddna_drum_library', JSON.stringify(drumLibrary));
      } catch (e) {
        console.error('Failed to save drum library:', e);
      }
    }
  }, [drumLibrary]);

  // Save pattern to library
  const saveToLibrary = (patternData, audioUrl = null, midiData = null) => {
    const newEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      genre: patternData?.genre || drumGenre,
      tempo: patternData?.tempo || drumTempo,
      kit: patternData?.kit || drumKit,
      swing: patternData?.swing || drumSwing,
      pattern: patternData?.pattern || '',
      suno_prompt: patternData?.suno_prompt || '',
      style_prompt: drumStylePrompt || '',
      audioUrl: audioUrl || drumAudioUrl,
      midiData: midiData,
      name: `${patternData?.genre || drumGenre} - ${patternData?.tempo || drumTempo} BPM`
    };
    
    setDrumLibrary(prev => [newEntry, ...prev]);
    toast.success('🥁 Pattern saved to library!');
    return newEntry.id;
  };

  // Auto-save when pattern is generated
  const autoSavePattern = (patternData) => {
    if (patternData && patternData.pattern) {
      saveToLibrary(patternData);
    }
  };

  // Delete pattern from library
  const deleteFromLibrary = (id) => {
    setDrumLibrary(prev => prev.filter(p => p.id !== id));
    toast.success('Pattern removed from library');
  };

  // Erase a single row from the My MANNIS unified library (any source)
  const eraseLibraryItem = (row) => {
    const label = row.title || 'this item';
    toast(`Erase "${label}"?`, {
      description: 'This removes it from your library and cannot be undone.',
      duration: 8000,
      action: {
        label: '🗑️ Erase',
        onClick: () => {
          if (row.kind === 'suno' && row.raw) { performSunoDelete(row.raw); return; }
          if (row.kind === 'beat' && row.rawId) { deleteBeatFromLibrary(row.rawId); return; }
          if (row.kind === 'drum' && row.rawId != null) {
            setDrumLibrary(prev => prev.filter(p => String(p.id) !== String(row.rawId)));
            toast.success('Removed from library');
            return;
          }
          if (row.kind === 'stem' && row.rawId) {
            setSeparatedStems(prev => {
              if (!prev) return prev;
              const next = { ...prev };
              delete next[row.rawId];
              return next;
            });
            toast.success('Stem removed');
            return;
          }
          toast.error('Could not erase this item');
        }
      },
      cancel: { label: 'Cancel', onClick: () => {} }
    });
  };

  // Clear entire library
  const clearLibrary = () => {
    if (window.confirm('Are you sure you want to clear all saved patterns?')) {
      setDrumLibrary([]);
      localStorage.removeItem('sounddna_drum_library');
      toast.success('Library cleared');
    }
  };

  // Load pattern from library
  const loadFromLibrary = (entry) => {
    // Set drum machine settings
    setDrumGenre(entry.genre || 'trap');
    setDrumTempo(entry.tempo || 140);
    setDrumKit(entry.kit || '808');
    setDrumSwing(entry.swing || 'none');
    
    // Set the generated pattern
    setGeneratedDrumPattern({
      pattern: entry.pattern,
      suno_prompt: entry.suno_prompt,
      genre: entry.genre,
      tempo: entry.tempo,
      kit: entry.kit,
      swing: entry.swing
    });
    
    // Set audio if available
    if (entry.audioUrl) {
      setDrumAudioUrl(entry.audioUrl);
      drumAudioRenderedFor.current = entry.suno_prompt || null;
      setDrumAudioSunoPrompt(entry.suno_prompt);
    }
    
    // Set style prompt if available
    if (entry.style_prompt) {
      setDrumStylePrompt(entry.style_prompt);
    }
    
    toast.success(`🎵 Loaded: ${entry.name}`);
    setShowDrumLibrary(false);
  };

  // Download pattern as JSON
  const exportPattern = (entry) => {
    const dataStr = JSON.stringify(entry, null, 2);
    shareOrDownloadText(dataStr, `SoundDNA_${entry.genre}_${entry.tempo}BPM.json`, 'application/json', { successMessage: 'Pattern exported!' });
  };

  // ============================================
  // SUNO AI MUSIC GENERATION
  // ============================================
  
  // Load user's Suno tracks on mount
  useEffect(() => {
    loadSunoTracks();
  }, []);
  
  const loadSunoTracks = async () => {
    try {
      // Include user email to filter tracks
      const userEmail = currentUser?.email || localStorage.getItem('sounddna_user_email') || '';
      const response = await axios.get(`${API}/music/list?limit=20${userEmail ? `&email=${encodeURIComponent(userEmail)}` : ''}`);
      if (response.data.success) {
        const rawTracks = response.data.tracks || [];
        
        // Keep only the single best version per generation (longest duration), named from the prompt
        const expandedTracks = rawTracks.map(track => {
          const niceTitle = titleFromPrompt(track.prompt) || track.title;
          if (track.all_tracks && track.all_tracks.length > 1) {
            const best = [...track.all_tracks].sort((a, b) => (b.duration || 0) - (a.duration || 0))[0];
            return {
              ...track,
              title: niceTitle || best.title,
              audio_url: best.audio_url || track.audio_url,
              image_url: best.image_url || track.image_url,
              duration: best.duration || track.duration,
              tags: best.tags || track.tags
            };
          }
          return { ...track, title: niceTitle || track.title };
        });
        
        setSunoTracks(expandedTracks);
      }
    } catch (error) {
      console.error("Failed to load Suno tracks:", error);
    }
  };
  
  // ============================================
  // MY UPLOADS FUNCTIONS
  // ============================================
  
  const fetchMyUploads = async (category = null) => {
    try {
      const params = new URLSearchParams();
      if (category && category !== 'all') {
        params.append('upload_type', category);
      }
      params.append('limit', '100');
      // Filter by user email
      const emailToUse = currentUser?.email || localStorage.getItem('sounddna_user_email') || '';
      if (emailToUse) {
        params.append('email', emailToUse);
      }
      
      const response = await axios.get(`${API}/uploads/list?${params}`);
      if (response.data.success) {
        setMyUploads(response.data.uploads || []);
      }
    } catch (error) {
      console.error("Failed to fetch uploads:", error);
    }
  };
  
  const fetchStorageStatus = async () => {
    try {
      const response = await axios.get(`${API}/uploads/storage-status`);
      setStorageStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch storage status:", error);
    }
  };
  
  const handleAudioUpload = async (file) => {
    if (!file) return;
    
    setIsUploading(true);
    setUploadProgress('Uploading audio...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
      formData.append('category', 'audio_preview');
      formData.append('email', userEmail || getGuestEmail());
      
      const response = await axios.post(`${API}/uploads/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        toast.success(`Uploaded: ${response.data.title} (${response.data.size_mb}MB)`);
        fetchMyUploads(uploadCategory);
        fetchStorageStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };
  
  const handleMidiUpload = async (file) => {
    if (!file) return;
    
    setIsUploading(true);
    setUploadProgress('Uploading MIDI...');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', file.name.replace(/\.[^/.]+$/, ''));
      formData.append('email', userEmail || getGuestEmail());
      
      const response = await axios.post(`${API}/uploads/midi`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        toast.success(`Uploaded: ${response.data.title}`);
        fetchMyUploads(uploadCategory);
        fetchStorageStatus();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };
  
  const handleSaveStylePrompt = async () => {
    if (!newStylePrompt.title.trim() || !newStylePrompt.prompt.trim()) {
      toast.error('Title and prompt are required');
      return;
    }
    
    setIsUploading(true);
    
    try {
      const response = await axios.post(`${API}/uploads/style-prompt`, {
        title: newStylePrompt.title,
        prompt: newStylePrompt.prompt,
        description: newStylePrompt.description,
        tags: newStylePrompt.tags.split(',').map(t => t.trim()).filter(t => t),
        email: userEmail || getGuestEmail()
      });
      
      if (response.data.success) {
        toast.success('Style prompt saved!');
        setNewStylePrompt({ title: '', prompt: '', description: '', tags: '' });
        fetchMyUploads(uploadCategory);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    } finally {
      setIsUploading(false);
    }
  };
  
  const handleAnalyzeAudio = async (fileId) => {
    try {
      toast.info('Analyzing audio...');
      const response = await axios.post(`${API}/uploads/analyze-audio`, {
        file_id: fileId,
        email: userEmail || getGuestEmail()
      });
      
      if (response.data.success) {
        toast.success('Style prompt generated!');
        setNewStylePrompt(prev => ({
          ...prev,
          prompt: response.data.generated_prompt,
          title: `Style from: ${response.data.source_file}`
        }));
        fetchMyUploads(uploadCategory);
      }
    } catch (error) {
      toast.error('Analysis failed');
    }
  };
  
  const handleDeleteUpload = async (uploadId) => {
    if (!window.confirm('Delete this upload?')) return;
    
    try {
      await axios.delete(`${API}/uploads/${uploadId}`);
      toast.success('Deleted');
      fetchMyUploads(uploadCategory);
      fetchStorageStatus();
    } catch (error) {
      toast.error('Delete failed');
    }
  };
  
  const generateSongLyrics = async () => {
    const seedTitle = (sunoTitle || '').trim();
    const seedDesc = (sunoPrompt || '').trim();
    if (!seedTitle && !seedDesc) { toast.error("Enter a Song Title (or description) first"); return; }
    if (checkForCelebrityNames(`${seedTitle} ${seedDesc}`)) { toast.error("Remove artist names first"); return; }
    setSongLyricsLoading(true);
    try {
      const res = await axios.post(`${API}/generate-song-lyrics`, {
        title: seedTitle,
        description: seedDesc,
        email: currentUser?.email || userEmail || getGuestEmail()
      });
      setSongLyrics(res.data.lyrics || "");
      toast.success("✍️ Clean lyrics ready — review & edit, then generate your track");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate lyrics");
    } finally {
      setSongLyricsLoading(false);
    }
  };

  const generateSunoMusic = async (promptOverride = null) => {
    const chosenVoice = SONG_VOICES.find(v => v.voice_id === selectedVoiceId);
    const voiceDesc = (chosenVoice && !sunoInstrumental) ? `${chosenVoice.style} lead vocals` : '';
    // If AI lyrics were generated & reviewed, send the LYRICS to Suno instead of the raw description
    const useLyrics = !!(songLyrics.trim() && !sunoInstrumental && !promptOverride);
    let effectivePrompt = (typeof promptOverride === 'string' && promptOverride.trim())
      ? promptOverride
      : (useLyrics ? songLyrics.trim() : sunoPrompt);
    if (!effectivePrompt.trim()) {
      toast.error("Please enter a prompt for your song");
      return;
    }
    
    setIsGeneratingSuno(true);
    setSunoResultTracks([]);
    setSunoStatus({ status: "starting", message: "Submitting to Suno AI..." });
    
    try {
      // Include key at the FRONT of the style so it's never truncated
      let styleWithKey = sunoKey && sunoKey !== 'auto' 
        ? `Key of ${sunoKey}${sunoStyle ? ', ' + sunoStyle : ''}` 
        : sunoStyle;
      // Put the chosen voice FIRST in the style so Suno drives the vocal character (gender/tone) from it
      if (voiceDesc) {
        styleWithKey = styleWithKey ? `${voiceDesc}, ${styleWithKey}` : voiceDesc;
      }
      // With lyrics, the prompt box (where the Quick Style genre chips go) is not sent to Suno,
      // so carry its genre/style text into `style`, ahead of the voice, or the voice's own genre wins.
      const genreText = useLyrics ? sunoPrompt.trim().slice(0, 300) : '';
      if (genreText) {
        styleWithKey = styleWithKey ? `${genreText}, ${styleWithKey}` : genreText;
      }
      
      const response = await axios.post(`${API}/music/generate`, {
        prompt: effectivePrompt,
        style: styleWithKey,
        lyrics: useLyrics ? songLyrics.trim() : undefined,
        voice_id: (!sunoInstrumental && selectedVoiceId) ? selectedVoiceId : undefined,
        title: (sunoTitle && sunoTitle.trim()) ? sunoTitle.trim() : (titleFromPrompt(sunoPrompt) || "MANNIS Song"),
        instrumental: sunoInstrumental,
        duration: sunoDuration || undefined,
        email: currentUser?.email || userEmail || getGuestEmail()
      });
      
      // Save email to localStorage for track filtering
      const emailUsed = currentUser?.email || userEmail || getGuestEmail();
      localStorage.setItem('sounddna_user_email', emailUsed);
      
      if (response.data.success) {
        const taskId = response.data.task_id;
        setSunoTaskId(taskId);
        setSunoStatus({ status: "processing", message: "🎵 Generating your song..." });
        toast.success("🎵 Song generation started!");
        
        // Start polling with exponential backoff
        pollSunoStatus(taskId, 0);
      } else {
        throw new Error(response.data.message || "Failed to start generation");
      }
    } catch (error) {
      console.error("Suno generation error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Generation failed";
      setSunoStatus({ status: "failed", message: errorMsg });
      toast.error(errorMsg);
      setIsGeneratingSuno(false);
    }
  };
  
  // ── Unified Song Creation helpers ──────────────────────────────
  const buildSongRecipePrompt = () => {
    const parts = [];
    if (fusionGenreA && fusionGenreB) parts.push(`${fusionGenreA} x ${fusionGenreB} fusion`);
    else if (fusionGenreA) parts.push(fusionGenreA);
    else if (fusionGenreB) parts.push(fusionGenreB);
    if (selectedMood) parts.push(`${selectedMood.toLowerCase()} mood`);
    if (selectedInstruments.length) parts.push(selectedInstruments.join(', '));
    const voice = SONG_VOICES.find(v => v.voice_id === selectedVoiceId);
    if (voice) parts.push(`${voice.style.toLowerCase()} vocals`);
    else if (vocalStyle) parts.push(`${vocalGender} ${vocalStyle} vocals`);
    if (sunoPrompt.trim()) parts.push(sunoPrompt.trim());
    return parts.join(', ');
  };

  const isRecipeComplete = () =>
    !!(fusionGenreA && fusionGenreB && selectedMood && selectedInstruments.length > 0 && sunoPrompt.trim() && selectedVoiceId);

  const createUnifiedSong = async () => {
    if (!isRecipeComplete()) {
      toast.error("Complete all sections first: pick two genres, a mood, instruments, a style prompt and a voice.");
      return;
    }
    if (checkForCelebrityNames(sunoPrompt)) {
      toast.error("Remove artist names from your style prompt — describe the style instead.");
      return;
    }
    await generateSunoMusic(buildSongRecipePrompt());
  };

  const toggleInstrument = (inst) => {
    setSelectedInstruments(prev => prev.includes(inst) ? prev.filter(i => i !== inst) : [...prev, inst]);
  };

  const playVoicePreview = (voiceId) => {
    // Toggle: clicking the playing voice stops it
    if (voicePreviewAudioRef.current) {
      voicePreviewAudioRef.current.pause();
      voicePreviewAudioRef.current = null;
    }
    if (voicePreviewId === voiceId) {
      setVoicePreviewId(null);
      return;
    }
    const audio = new Audio(`${API}/voice-preview/${voiceId}`);
    voicePreviewAudioRef.current = audio;
    setVoicePreviewId(voiceId);
    audio.onended = () => setVoicePreviewId(null);
    audio.onerror = () => { toast.error("Could not load voice preview"); setVoicePreviewId(null); };
    audio.play().catch(() => { toast.error("Could not play voice preview"); setVoicePreviewId(null); });
  };

  const pollSunoStatus = async (taskId, attempt) => {
    // Max 150 attempts x 2s = 300 seconds (5 minutes) for high-quality songs
    if (attempt >= 150) {
      setSunoStatus({ status: "failed", message: "Generation timed out after 5 minutes. Please try again." });
      setIsGeneratingSuno(false);
      toast.error("Generation timed out");
      // Auto-refresh library in case song completed via webhook
      loadSunoTracks();
      return;
    }
    
    try {
      const response = await axios.get(`${API}/music/status/${taskId}`);
      const data = response.data;
      
      if (data.status === "completed") {
        setSunoStatus({ 
          status: "completed", 
          message: "✅ Song ready!",
          audioUrl: data.audio_url,
          title: data.title,
          duration: data.duration,
          imageUrl: data.image_url,
          tracks: data.tracks,  // Store all tracks
          trackCount: data.track_count
        });
        
        // Suno returns 2 versions per generation — keep BOTH so the user gets two WAV downloads.
        const allTracks = data.tracks && data.tracks.length ? data.tracks : null;
        const baseTitle = titleFromPrompt(sunoPrompt) || data.title || 'Generated Song';
        const versions = (allTracks || [{ audio_url: data.audio_url, duration: data.duration, image_url: data.image_url }])
          .map((t, i) => ({
            task_id: taskId,
            audio_url: t.audio_url,
            title: (allTracks && allTracks.length > 1) ? `${baseTitle} (Version ${i + 1})` : baseTitle,
            duration: t.duration,
            image_url: t.image_url,
          }));
        setSunoResultTracks(versions);
        // Auto-play the longest version first
        const best = [...versions].sort((a, b) => (b.duration || 0) - (a.duration || 0))[0];
        setCurrentSunoTrack(best);
        toast.success(versions.length > 1 ? `🎵 2 versions of "${baseTitle}" are ready!` : `🎵 "${baseTitle}" is ready!`);
        
        setIsGeneratingSuno(false);
        
        // Refresh tracks list
        loadSunoTracks();
        
        // Clear form
        setSunoPrompt("");
        setSunoStyle("");
        setSunoTitle("");
        setSunoKey("C major");
        
      } else if (data.status === "failed") {
        // Parse error message for better user feedback
        let errorMessage = data.error || "Generation failed";
        if (errorMessage.includes("artist name")) {
          errorMessage = "❌ Artist names not allowed. Please describe the style instead of naming artists.";
        } else if (errorMessage.includes("copyright")) {
          errorMessage = "❌ Copyrighted content detected. Please use original lyrics.";
        }
        setSunoStatus({ status: "failed", message: errorMessage });
        setIsGeneratingSuno(false);
        toast.error(errorMessage);
        
      } else {
        // Still processing - poll faster now that webhook handles completion
        // Show progress messages based on attempt count
        const progressMessages = [
          "🎵 Submitting to Suno AI...",
          "🧠 AI is composing your song...",
          "🎹 Generating melodies...",
          "🎸 Adding instruments...",
          "🎤 Creating vocals...",
          "🔊 Mixing audio...",
          "✨ Finalizing your tracks...",
          "📦 Packaging 2 songs...",
          "⏳ Almost ready...",
          "🎵 Just a few more seconds..."
        ];
        const messageIndex = Math.min(Math.floor(attempt / 3), progressMessages.length - 1);
        
        setSunoStatus({ 
          status: "processing", 
          message: progressMessages[messageIndex],
          attempt: attempt + 1,
          progress: Math.min(95, 10 + (attempt * 3))  // Progress bar from 10% to 95%
        });
        
        // Fast polling: 2s intervals (webhook will complete before this usually)
        // Max 150 attempts = 5 minutes timeout
        if (attempt < 150) {
          setTimeout(() => pollSunoStatus(taskId, attempt + 1), 2000);
        } else {
          setSunoStatus({ status: "failed", message: "Generation timed out after 5 minutes. Please try again." });
          setIsGeneratingSuno(false);
          // Auto-refresh library in case song completed via webhook
          loadSunoTracks();
        }
      }
    } catch (error) {
      console.error("Poll error:", error);
      // Slow connection is NOT a failure - keep retrying (35 x 8s on top of normal polling)
      if (attempt < 35) {
        // Longer wait between retries: 8 seconds instead of 5
        setTimeout(() => pollSunoStatus(taskId, attempt + 1), 8000);
      } else {
        setSunoStatus({ status: "failed", message: "Connection lost. Please refresh." });
        setIsGeneratingSuno(false);
        // Auto-refresh library even on failure in case song completed via webhook
        loadSunoTracks();
      }
    }
  };
  
  const playSunoTrack = (track) => {
    const sameTrack = currentSunoTrack?.task_id === track.task_id && currentSunoTrack?.audio_url === track.audio_url;
    setCurrentSunoTrack(track);
    if (sameTrack && sunoAudioRef.current) {
      sunoAudioRef.current.play().catch(e => {
        console.error("Audio play error:", e);
        toast.error("Failed to play audio");
      });
    }
    setIsSunoPlaying(true);
  };
  
  const toggleSunoPlayback = () => {
    if (sunoAudioRef.current) {
      if (isSunoPlaying) {
        sunoAudioRef.current.pause();
      } else {
        sunoAudioRef.current.play();
      }
      setIsSunoPlaying(!isSunoPlaying);
    }
  };
  
  const downloadSunoTrack = async (track) => {
    if (!track.audio_url) {
      toast.error("❌ No audio URL available for this track");
      return;
    }
    const fileName = `${(track.title || 'MANNIS_Track').replace(/[^a-z0-9]/gi, '_')}.wav`;
    // Transcode the provider's MP3 into a genuine full-song WAV (44.1kHz) server-side
    // via /api/download-song-wav, then save it in-app. One file, no stems.
    await downloadSong(track.audio_url, fileName, 'wav');
  };

  const deleteSunoTrack = (track) => {
    // In-app confirmation (window.confirm is blocked inside embedded/iframe contexts)
    toast(`Delete "${track.title || 'this track'}"?`, {
      description: 'This cannot be undone.',
      duration: 8000,
      action: { label: '🗑️ Delete', onClick: () => performSunoDelete(track) },
      cancel: { label: 'Cancel', onClick: () => {} }
    });
  };

  const performSunoDelete = async (track) => {
    try {
      // Use parent_task_id if this is an expanded track, otherwise use task_id
      const taskId = track.parent_task_id || track.task_id;
      const trackIndex = track.track_index;
      
      const response = await axios.delete(`${API}/music/delete/${taskId}`, {
        params: trackIndex !== undefined ? { track_index: trackIndex } : {}
      });
      
      if (response.data.success) {
        toast.success(`🗑️ "${track.title}" deleted`);
        
        // Clear current track if it was deleted
        if (currentSunoTrack?.task_id === track.task_id) {
          setCurrentSunoTrack(null);
          setIsSunoPlaying(false);
          if (sunoAudioRef.current) {
            sunoAudioRef.current.pause();
          }
        }
        
        // Refresh the library
        loadSunoTracks();
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete track");
    }
  };

  // ============================================
  // MIDI GENERATION FROM STYLE PROMPTS
  // ============================================
  const [isGeneratingMidi, setIsGeneratingMidi] = useState(false);
  
  const generateMidiFromPrompt = async (stylePrompt, presetId = null) => {
    setIsGeneratingMidi(true);
    try {
      toast.info("🎹 Generating MIDI file (32 bars, full arrangement)...");
      
      const response = await axios.post(`${API}/generate-midi`, {
        style_prompt: stylePrompt,
        bars: 32,
        preset_id: presetId
      });
      
      if (response.data.success) {
        const { filename, params } = response.data;
        setMo3LastMidi(filename);
        
        // Download using fetch-and-blob pattern (cross-origin safe)
        const downloadUrl = `${API}/download-midi/${filename}`;
        
        try {
          // Fetch the MIDI file as blob
          const fetchResponse = await fetch(downloadUrl);
          if (!fetchResponse.ok) throw new Error("Download failed");
          
          const blob = await fetchResponse.blob();
          await downloadFileWithPicker(
            blob, 
            filename, 
            'audio/midi', 
            `🎹 MIDI saved! ${params.genre} @ ${params.tempo} BPM`,
            downloadUrl
          );
        } catch (fetchError) {
          console.error("MIDI fetch error:", fetchError);
          // Fallback: open in new tab
          window.open(downloadUrl, '_blank');
          toast.info("Opening MIDI in new tab - right-click to save");
        }
      } else {
        toast.error("Failed to generate MIDI");
      }
    } catch (error) {
      console.error("MIDI generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate MIDI file");
    } finally {
      setIsGeneratingMidi(false);
    }
  };

  // Add drum pattern to unified workflow
  const addDrumToWorkflow = () => {
    if (generatedDrumPattern?.pattern) {
      const sunoPrompt = extractSunoPromptFromDrum(generatedDrumPattern.pattern);
      
      // Update workflow with drum info
      updateWorkflow({
        drumPattern: generatedDrumPattern.pattern,
        drumSunoPrompt: sunoPrompt,
        bpm: generatedDrumPattern.tempo || drumTempo,
        genre: generatedDrumPattern.genre || drumGenre,
        source: 'drum'
      });
      
      // Also update the style prompt to include drum info if not already present
      const currentStyle = workflowPrompt.stylePrompt || '';
      const drumStyle = `${generatedDrumPattern.genre} drums, ${generatedDrumPattern.tempo} BPM, ${drumKit} kit`;
      
      if (!currentStyle.toLowerCase().includes('drum')) {
        updateWorkflow({
          stylePrompt: currentStyle ? `${currentStyle}, ${drumStyle}` : drumStyle
        });
      }
      
      toast.success("🥁 Drum pattern added to workflow! BPM and genre synced.");
    }
  };

  // Copy combined workflow with drum pattern for Suno
  const copyCombinedSunoPrompt = () => {
    let combined = '';
    
    if (workflowPrompt.stylePrompt) {
      combined += workflowPrompt.stylePrompt;
    }
    
    if (workflowPrompt.drumSunoPrompt) {
      combined += combined ? ', ' + workflowPrompt.drumSunoPrompt : workflowPrompt.drumSunoPrompt;
    }
    
    if (workflowPrompt.voiceSample) {
      combined += combined ? ', ' + workflowPrompt.voiceSample : workflowPrompt.voiceSample;
    }
    
    if (combined) {
      universalCopy(combined, "🎵 Combined Suno prompt copied!");
    } else {
      toast.error("No prompts in workflow to copy");
    }
  };

  // Generate complete song (lyrics + drums together)
  const generateCompleteSong = async () => {
    // First generate lyrics if topic is provided
    if (rhymeTopic.trim()) {
      await generateBars();
    }
    
    // Then generate drum pattern with synced settings
    if (syncDrumToRhyme) {
      // Sync drum bars to rhyme bars
      setDrumBars(barsCount);
    }
    await generateDrumPattern();
    
    toast.success("🎵 Complete song package generated!");
  };

  // ============== 1-CLICK FULL SONG GENERATION ==============
  // Creates actual audio from style prompt + lyrics using AI
  const generateFullSong = async () => {
    setIsGeneratingSong(true);
    setSongGenerationProgress(0);
    setGeneratedSongUrl(null);
    
    try {
      // Build the ultimate prompt from all available sources
      let stylePrompt = '';
      
      // Add drum pattern style
      if (generatedDrumPattern?.pattern) {
        const drumSunoPrompt = extractSunoPromptFromDrum(generatedDrumPattern.pattern);
        if (drumSunoPrompt) {
          stylePrompt += drumSunoPrompt;
        }
      }
      
      // Add workflow style
      if (workflowPrompt.stylePrompt) {
        stylePrompt += stylePrompt ? ', ' + workflowPrompt.stylePrompt : workflowPrompt.stylePrompt;
      }
      
      // Add genre/mood context
      if (drumGenre) {
        stylePrompt += `, ${drumGenre} beat`;
      }
      if (rhymeStyle) {
        stylePrompt += `, ${rhymeStyle} style`;
      }
      
      // Add tempo
      if (drumTempo) {
        stylePrompt += `, ${drumTempo} BPM`;
      }
      
      // Fallback if no style generated
      if (!stylePrompt) {
        stylePrompt = rhymeTopic 
          ? `A song about ${rhymeTopic}, modern production, professional quality`
          : 'Modern trap beat, hard-hitting 808s, professional studio quality';
      }
      
      toast.info("🎵 Creating your song... This takes about 2 minutes");
      setSongGenerationProgress(10);
      setGenWidget({ kind: 'song', label: 'Creating your song', progress: 10, eta: 120, status: 'working' });
      
      // Get lyrics if available
      const lyricsText = generatedBars?.raw_lyrics || generatedBars?.bars || customLyrics || null;
      
      // Call the generate-beat API
      const response = await axios.post(`${API}/generate-beat`, {
        prompt: stylePrompt,
        duration: 30, // 30 seconds for now
        lyrics: lyricsText,
        email: userEmail || getGuestEmail(),
        quick_mode: false,
        enhance: enhancePrompt
      });
      
      const taskId = response.data.task_id;
      setSongTaskId(taskId);
      setSongGenerationProgress(20);
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 120; // 2 minutes max
      
      const pollStatus = async () => {
        while (attempts < maxAttempts) {
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 2000)); // Poll every 2 seconds
          
          try {
            const statusRes = await axios.get(`${API}/beat-status/${taskId}`);
            const status = statusRes.data;
            
            // Update progress
            let songP;
            if (status.progress) {
              songP = Math.max(20, Math.min(90, status.progress));
            } else {
              songP = 20 + (attempts / maxAttempts) * 70;
            }
            setSongGenerationProgress(songP);
            setGenWidget(prev => prev && prev.kind === 'song' && prev.status === 'working'
              ? { ...prev, progress: Math.round(songP), eta: Math.max(5, 120 - attempts * 2) } : prev);
            
            if (status.status === 'completed') {
              setSongGenerationProgress(100);
              setGeneratedSongUrl(status.audio_url);
              setSongSafety(status.safety || null);
              const absSongUrl = status.audio_url && status.audio_url.startsWith('/api/')
                ? `${API_ORIGIN}${status.audio_url}` : status.audio_url;
              setGenWidget({ kind: 'song', label: 'Your song', progress: 100, eta: 0, status: 'ready', resultUrl: absSongUrl });
              genWidgetAutoPlay(absSongUrl);
              toast.success("🎉 Your song is ready!");
              setIsGeneratingSong(false);
              return;
            }
            
            if (status.status === 'failed') {
              throw new Error(status.error || 'Song generation failed');
            }
          } catch (pollError) {
            console.error('Poll error:', pollError);
          }
        }
        
        throw new Error('Generation timed out');
      };
      
      await pollStatus();
      
    } catch (error) {
      console.error('Song generation error:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to generate song');
      setIsGeneratingSong(false);
      setSongGenerationProgress(0);
      setGenWidget(null);
    }
  };

  // Play/Pause generated song
  const toggleSongPlayback = () => {
    if (songAudioRef.current) {
      if (isSongPlaying) {
        songAudioRef.current.pause();
      } else {
        songAudioRef.current.play();
      }
      setIsSongPlaying(!isSongPlaying);
    }
  };

  // Download generated song
  const downloadGeneratedSong = async () => {
    if (!generatedSongUrl) return;

    try {
      toast.info("Preparing download...");
      const wavFilename = `sound-dna-song-${Date.now()}.wav`;
      const proxyUrl = `/api/download-song-wav?url=${encodeURIComponent(generatedSongUrl)}&filename=${encodeURIComponent(wavFilename)}`;
      const fullProxyUrl = `${API_ORIGIN}${proxyUrl}`;
      const response = await fetch(fullProxyUrl);
      const blob = await response.blob();
      await shareOrDownloadBlob(blob, wavFilename, 'audio/wav', {
        sourceUrl: fullProxyUrl, desktopPicker: true, successMessage: '🎵 Song downloaded!',
      });
    } catch (error) {
      toast.error("Download failed");
    }
  };

  // Copy complete song package for Suno
  const copyCompleteSongForSuno = () => {
    let songPackage = '';
    
    // Add drum style prompt
    if (generatedDrumPattern?.pattern) {
      const drumSunoPrompt = extractSunoPromptFromDrum(generatedDrumPattern.pattern);
      if (drumSunoPrompt) {
        songPackage += drumSunoPrompt;
      }
    }
    
    // Add any existing style prompt
    if (workflowPrompt.stylePrompt) {
      songPackage += songPackage ? ', ' + workflowPrompt.stylePrompt : workflowPrompt.stylePrompt;
    }
    
    if (songPackage) {
      universalCopy(songPackage, "🎵 Song style copied for Suno!");
    }
    
    // Also prepare lyrics for separate copy
    if (generatedBars?.bars) {
      toast.info("💡 Tip: Copy your lyrics separately for Suno's lyrics field");
    }
  };

  // Copy both style and lyrics formatted for Suno
  const copyFullSongPackage = () => {
    let output = "=== SUNO SONG PACKAGE ===\n\n";
    
    // Style section
    output += "📋 STYLE PROMPT (paste in Style field):\n";
    output += "─".repeat(40) + "\n";
    
    if (generatedDrumPattern?.pattern) {
      const drumSunoPrompt = extractSunoPromptFromDrum(generatedDrumPattern.pattern);
      if (drumSunoPrompt) {
        output += drumSunoPrompt + "\n";
      }
    }
    
    if (workflowPrompt.stylePrompt) {
      output += workflowPrompt.stylePrompt + "\n";
    }
    
    output += "\n";
    
    // Lyrics section
    output += "📝 LYRICS (paste in Lyrics field):\n";
    output += "─".repeat(40) + "\n";
    
    if (generatedBars?.raw_lyrics) {
      output += generatedBars.raw_lyrics + "\n";
    } else if (generatedBars?.bars && typeof generatedBars.bars === 'string') {
      output += generatedBars.bars + "\n";
    } else if (customLyrics) {
      output += customLyrics + "\n";
    } else {
      output += "(No lyrics generated yet)\n";
    }
    
    output += "\n";
    
    // Settings
    output += "⚙️ RECOMMENDED SETTINGS:\n";
    output += "─".repeat(40) + "\n";
    output += `Tempo: ${drumTempo} BPM\n`;
    output += `Genre: ${drumGenre}\n`;
    output += `Bars: ${drumBars}\n`;
    
    universalCopy(output, "📦 Complete song package copied!");
  };

  // Generate Drum Audio Preview - Uses AI to create audio matching the exact drum settings
  // Warn (non-blocking) if a drum prompt mentions a real artist/producer name
  const loadMo3Library = async () => {
    const email = currentUser?.email || userEmail;
    if (!email) return;
    try {
      const res = await axios.get(`${API}/library/mo3?email=${encodeURIComponent(email)}&source=mo3`);
      setMo3Library(res.data.items || []);
    } catch (e) { /* silent */ }
  };

  const loadFusionLibrary = async () => {
    const email = currentUser?.email || userEmail;
    if (!email) return;
    try {
      const res = await axios.get(`${API}/library/mo3?email=${encodeURIComponent(email)}&source=fusion`);
      setFusionLibrary(res.data.items || []);
    } catch (e) { /* silent */ }
  };

  const saveFusionToLibrary = async () => {
    const email = currentUser?.email || userEmail;
    if (!email) { toast.error('Sign in to save to your library'); return; }
    if (!dnaFusionResult) { toast.error('Fuse two genres first!'); return; }
    try {
      // Ensure a MIDI file exists for this fusion — auto-generate from the blueprint if needed
      let midiFile = fusionLastMidi;
      if (!midiFile && dnaFusionResult.style_prompt) {
        try {
          const midiRes = await axios.post(`${API}/generate-midi`, {
            style_prompt: dnaFusionResult.style_prompt,
            bars: 32,
            audio_url: fusionSong?.status === 'completed' && fusionSong.url ? fusionSong.url : null
          });
          midiFile = midiRes.data.filename || null;
          if (midiFile) setFusionLastMidi(midiFile);
        } catch (mErr) { /* save proceeds without MIDI */ }
      }
      const res = await axios.post(`${API}/library/mo3`, {
        email,
        source: 'fusion',
        name: `${dnaFusionResult.title || 'DNA Fusion'}${dnaFusionResult.bpm ? ` — ${dnaFusionResult.bpm} BPM` : ''}`.slice(0, 120),
        style_prompt: dnaFusionResult.style_prompt || '',
        structure: (dnaFusionResult.structure || '').slice(0, 1400),
        audio_url: (fusionSong?.status === 'completed' && fusionSong.url) ? fusionSong.url : null,
        midi_filename: midiFile || null,
        bpm: dnaFusionResult.bpm
      });
      if (res.data.saved) {
        toast.success('💾 Saved to your Fusion Library!');
        loadFusionLibrary();
        setShowFusionLibrary(true);
      }
    } catch (e) { toast.error('Failed to save — please try again'); }
  };

  const renderLibraryAudio = async (item, source) => {
    const email = currentUser?.email || userEmail;
    if (!item.style_prompt) { toast.error('No style prompt saved on this entry'); return; }
    toast.info('🎧 Rendering audio from the saved prompt (2-3 min)... keep this tab open');
    try {
      const res = await fetch(`${API}/generate-audio-preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: item.style_prompt.slice(0, 900), category: 'library', email })
      });
      if (res.status === 402) {
        if (currentUser) { setShowTokenExhausted(true); } else { setShowPricing(true); }
        return;
      }
      const data = await res.json();
      const finish = async (audioUrl) => {
        await axios.patch(`${API}/library/mo3/${item.id}`, { email, audio_url: audioUrl });
        toast.success('🎧 Audio ready — the ⬇ WAV button is now on your library entry!', { duration: 8000 });
        if (source === 'fusion') loadFusionLibrary(); else loadMo3Library();
      };
      if (data.status === 'completed' && data.audio_url) { await finish(data.audio_url); return; }
      if (!data.task_id) throw new Error(data.error || 'failed');
      let n = 0;
      const poll = async () => {
        if (n++ >= 100) { toast.error('Audio render timed out — try again'); return; }
        try {
          const sr = await fetch(`${API}/audio-preview-status/${data.task_id}`);
          const sd = await sr.json();
          if (sd.status === 'completed' && sd.audio_url) return finish(sd.audio_url);
          if (sd.status === 'failed') { toast.error(sd.error || 'Audio render failed', { duration: 8000 }); return; }
        } catch (e) { /* transient */ }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) { toast.error('Audio render failed — try again'); }
  };

  const deleteFusionItem = async (id) => {
    const email = currentUser?.email || userEmail;
    try {
      await axios.delete(`${API}/library/mo3/${id}?email=${encodeURIComponent(email)}`);
      setFusionLibrary(prev => prev.filter(i => i.id !== id));
      toast.success('Removed from library');
    } catch (e) { toast.error('Delete failed'); }
  };

  const saveMo3ToLibrary = async () => {
    const email = currentUser?.email || userEmail;
    if (!email) { toast.error('Sign in to save to your library'); return; }
    if (!drumStylePrompt && !songStructure) { toast.error('Select a style prompt or generate a structure first'); return; }
    try {
      const promptForSave = editableDrumPrompt || drumStylePrompt || generatedDrumPattern?.suno_prompt || '';
      // Ensure a MIDI file exists — auto-generate from the style prompt if none was exported
      let midiFile = mo3LastMidi;
      if (!midiFile && promptForSave) {
        try {
          const midiRes = await axios.post(`${API}/generate-midi`, { style_prompt: promptForSave, bars: 16 });
          midiFile = midiRes.data.filename || null;
          if (midiFile) setMo3LastMidi(midiFile);
        } catch (mErr) { /* save proceeds without MIDI */ }
      }
      const res = await axios.post(`${API}/library/mo3`, {
        email,
        name: `${(drumStylePrompt || 'MO3').split('.')[0].slice(0, 60)} — ${drumTempo} BPM`,
        style_prompt: promptForSave,
        structure: songStructure?.compact || '',
        audio_url: drumWavReady?.url || drumAudioUrl || null,
        midi_filename: midiFile || null,
        bpm: drumTempo
      });
      if (res.data.saved) {
        toast.success('💾 Saved to your MO3 Library!');
        loadMo3Library();
        setShowMo3Library(true);
      }
    } catch (e) {
      toast.error('Failed to save — please try again');
    }
  };

  const deleteMo3Item = async (id) => {
    const email = currentUser?.email || userEmail;
    try {
      await axios.delete(`${API}/library/mo3/${id}?email=${encodeURIComponent(email)}`);
      setMo3Library(prev => prev.filter(i => i.id !== id));
      toast.success('Removed from library');
    } catch (e) { toast.error('Delete failed'); }
  };

  const warnIfArtistNames = async (promptText) => {
    if (!promptText) return;
    try {
      const res = await axios.post(`${API}/check-artist-names`, { prompt: promptText }, { timeout: 8000 });
      const found = res.data?.found || [];
      if (found.length > 0) {
        toast.warning(`⚠️ Your prompt mentions ${found.slice(0, 3).join(', ')} — Suno rejects real artist names, so we'll auto-replace them with sound descriptors before generating.`, { duration: 9000 });
      }
    } catch (e) { /* non-blocking */ }
  };

  const generateDrumAudio = async () => {
    setIsGeneratingDrumAudio(true);
    setDrumAudioUrl(null);
    
    toast.info(`🎵 Generating ${drumGenre.toUpperCase()} drums @ ${drumTempo} BPM... (30-90 seconds)`);
    await warnIfArtistNames(generatedDrumPattern?.suno_prompt);
    
    try {
      // Start the generation - returns immediately with task_id
      const response = await axios.post(`${API}/generate-drum-preview`, {
        genre: drumGenre,
        tempo: drumTempo,
        kit: drumKit,
        swing: drumSwing,
        key: drumKey,
        mode: drumMode,
        energy: drumEnergy,
        complexity: drumComplexity,
        sections: drumSections,
        style_prompt: generatedDrumPattern?.suno_prompt || null,
        duration: 10,
        email: currentUser?.email || userEmail || getGuestEmail()
      }, { timeout: 30000 }); // Short timeout since this returns immediately now
      
      const taskId = response.data.task_id;
      
      if (!taskId) {
        toast.error("Failed to start drum generation");
        setIsGeneratingDrumAudio(false);
        return;
      }
      
      // Poll for completion
      let attempts = 0;
      let consecutiveErrors = 0;
      const maxAttempts = 100; // 100 attempts * 3 seconds = 300 seconds max (full Suno generation)
      const maxConsecutiveErrors = 5;
      const pollInterval = 3000; // Poll every 3 seconds
      
      const pollStatus = async () => {
        try {
          const statusResponse = await axios.get(`${API}/drum-preview-status/${taskId}`, { timeout: 10000 });
          consecutiveErrors = 0;
          const status = statusResponse.data;
          
          if (status.status === 'completed' && status.audio_url) {
            // Drum audio is now rehosted internally (relative /api/drum-audio/...);
            // resolve it to an absolute backend URL so players/downloads work.
            const resolvedDrumUrl = status.audio_url.startsWith('/api')
              ? `${API_ORIGIN}${status.audio_url}`
              : status.audio_url;
            setDrumAudioUrl(resolvedDrumUrl);
            drumAudioRenderedFor.current = generatedDrumPattern?.suno_prompt || null;
            // Store the matching Suno prompt so style and WAV are synced
            if (status.suno_prompt) {
              setDrumAudioSunoPrompt(status.suno_prompt);
            }
            toast.success(`🥁 ${drumGenre.toUpperCase()} @ ${drumTempo} BPM ready! Matches your Suno style.`);
            setIsGeneratingDrumAudio(false);
            // The Download WAV button can sit far below the fold on long layouts
            // (e.g. the Full Page / all-tools view) - now that it's enabled, bring
            // it into view instead of leaving the user to hunt for it.
            setTimeout(() => {
              const wavBtn = document.querySelector('[data-testid="drum-download-wav-btn"]')
                || document.querySelector('[data-testid="drum-fullscreen-download-wav-btn"]');
              wavBtn?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 200);
            return true; // Done
          } else if (status.status === 'failed') {
            toast.error(status.error || "Drum generation failed. Try again!", { duration: 9000 });
            setIsGeneratingDrumAudio(false);
            return true; // Done (with error)
          }
          
          // Still processing
          return false;
        } catch (pollError) {
          console.error("Poll error:", pollError);
          consecutiveErrors++;
          if (consecutiveErrors >= maxConsecutiveErrors) {
            toast.error("Lost connection while checking drum status. Please check your network and try again.");
            setIsGeneratingDrumAudio(false);
            return true; // Stop polling - don't leave the button stuck
          }
          return false; // Transient error - keep polling
        }
      };
      
      // Start polling
      const poll = async () => {
        while (attempts < maxAttempts) {
          attempts++;
          const done = await pollStatus();
          if (done) return;
          
          // Update toast with progress every 10 attempts
          if (attempts % 10 === 0) {
            toast.info(`Still generating... (${attempts * 3}s elapsed)`);
          }
          
          await new Promise(resolve => setTimeout(resolve, pollInterval));
        }
        
        // Timeout after max attempts
        toast.error("Generation timed out. The AI service may be busy - try again later.");
        setIsGeneratingDrumAudio(false);
      };
      
      poll();
      
    } catch (error) {
      console.error("Drum audio generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to start drum generation. Try again!");
      setIsGeneratingDrumAudio(false);
    }
  };

  // Play/Pause Drum Audio
  const toggleDrumAudio = async () => {
    // Use whichever audio element is currently mounted
    const audioElement = drumAudioRef.current || drumAudioRefFullscreen.current;
    
    if (audioElement) {
      if (isDrumPlaying) {
        audioElement.pause();
        setIsDrumPlaying(false);
      } else {
        try {
          await audioElement.play();
          setIsDrumPlaying(true);
        } catch (error) {
          console.error("Audio playback error:", error);
          setIsDrumPlaying(false);
          toast.error("Failed to play audio. Try clicking Preview again.");
        }
      }
    }
  };

  // Handle drum audio ended
  const handleDrumAudioEnded = () => {
    setIsDrumPlaying(false);
  };

  const copyBars = () => {
    if (generatedBars?.raw_lyrics) {
      universalCopy(generatedBars.raw_lyrics, "Bars copied! Tap & hold to paste 🎤");
    }
  };

  const downloadBars = () => {
    if (generatedBars?.raw_lyrics) {
      shareOrDownloadText(
        generatedBars.raw_lyrics,
        `SoundDNA_Bars_${rhymeStyle.replace(/ /g, '_')}_${Date.now()}.txt`,
        'text/plain;charset=utf-8',
        { successMessage: 'Bars downloading!' }
      ).catch((err) => { console.error('Download error:', err); toast.error('Download failed - try copying instead'); });
    }
  };

  // Format bars into full song structure for Suno
  const formatForSuno = async () => {
    if (!generatedBars?.bars || generatedBars.bars.length < 8) {
      toast.error("Need at least 8 bars to format a full song");
      return;
    }
    
    setIsFormattingSong(true);
    setFormattedSong(null);
    
    try {
      const barTexts = generatedBars.bars.map(b => b.text);
      
      const response = await axios.post(`${API}/format-full-song`, {
        bars: barTexts,
        topic: rhymeTopic,
        style: rhymeStyle,
        email: userEmail || getGuestEmail()
      }, { timeout: 60000 });
      
      if (response.data.full_song) {
        setFormattedSong(response.data);
        toast.success("Full song structure created! 🎵 Ready to generate!");
      } else {
        toast.error("Failed to format song");
      }
    } catch (error) {
      console.error("Song formatting error:", error);
      toast.error(error.response?.data?.detail || "Failed to format song. Try again!");
    } finally {
      setIsFormattingSong(false);
    }
  };

  const copyFullSong = () => {
    if (formattedSong?.full_song) {
      universalCopy(formattedSong.full_song, "Full song copied! Tap & hold to paste 🎤");
    }
  };

  const downloadFullSong = () => {
    if (formattedSong?.full_song) {
      shareOrDownloadText(
        formattedSong.full_song,
        `SoundDNA_FullSong_${rhymeStyle.replace(/ /g, '_')}_${Date.now()}.txt`,
        'text/plain',
        { successMessage: 'Full song downloaded!' }
      );
    }
  };

  // Format AI Lyrics into full song structure
  const formatLyricsForSuno = async () => {
    if (!generatedLyrics || generatedLyrics.trim().length < 50) {
      toast.error("Generate lyrics first before formatting");
      return;
    }
    
    setIsFormattingLyrics(true);
    setFormattedLyrics(null);
    
    try {
      // Split lyrics into lines and use as bars
      const lines = generatedLyrics.split('\n').filter(l => l.trim() && !l.startsWith('['));
      
      const response = await axios.post(`${API}/format-full-song`, {
        bars: lines.slice(0, 16), // Take first 16 lines as bars
        topic: lyricsTheme,
        style: lyricsGenre,
        email: userEmail || getGuestEmail()
      }, { timeout: 60000 });
      
      if (response.data.full_song) {
        setFormattedLyrics(response.data);
        toast.success("Lyrics formatted into full song! 🎵 Ready to generate!");
      } else {
        toast.error("Failed to format lyrics");
      }
    } catch (error) {
      console.error("Lyrics formatting error:", error);
      toast.error(error.response?.data?.detail || "Failed to format lyrics. Try again!");
    } finally {
      setIsFormattingLyrics(false);
    }
  };

  const copyFormattedLyrics = () => {
    if (formattedLyrics?.full_song) {
      universalCopy(formattedLyrics.full_song, "Full song copied! Tap & hold to paste 🎤");
    }
  };

  const downloadFormattedLyrics = () => {
    if (formattedLyrics?.full_song) {
      shareOrDownloadText(
        formattedLyrics.full_song,
        `SoundDNA_Lyrics_FullSong_${lyricsGenre}_${Date.now()}.txt`,
        'text/plain',
        { successMessage: 'Full song downloaded!' }
      );
    }
  };


  // Download STEMS as ZIP to Desktop
  const downloadStems = async (audioUrl, title) => {
    toast.info("🎵 Generating STEMS (Vocals, Drums, Bass, Melody)... Please wait ~30 seconds.");
    
    try {
      // Request stems generation
      const response = await axios.post(`${API}/generate-stems`, {
        audio_url: audioUrl,
        title: title || 'song',
        email: userEmail || getGuestEmail()
      });
      
      const taskId = response.data.task_id;
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 60; // 2 minutes max
      
      const pollInterval = setInterval(async () => {
        attempts++;
        
        try {
          const statusResponse = await axios.get(`${API}/stems-status/${taskId}`);
          const status = statusResponse.data;
          
          if (status.status === 'completed' && status.has_download) {
            clearInterval(pollInterval);
            
            // Download the ZIP file
            toast.info("⬇️ Preparing STEMS ZIP...");
            
            const downloadResponse = await fetch(`${API}/stems-download/${taskId}`);
            const blob = await downloadResponse.blob();
            const filename = status.filename || `${title}_stems.zip`;
            await downloadFileWithPicker(blob, filename, 'application/zip', '✅ STEMS ZIP saved!', `${API}/stems-download/${taskId}`);
            
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            toast.error(`Stems failed: ${status.error || 'Unknown error'}`);
            
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            toast.error("Stems generation timed out. Try again.");
          }
        } catch (e) {
          console.error("Poll error:", e);
        }
      }, 2000);
      
    } catch (error) {
      console.error("Stems error:", error);
      toast.error("Failed to generate stems. Try again.");
    }
  };

  // Separate Stems using Demucs AI
  const separateStems = async () => {
    console.log("separateStems called", { stemsFile, stemsAudioUrl });
    
    // Check if we have either a file or URL
    if (!stemsFile && !stemsAudioUrl.trim()) {
      toast.error("Please upload an audio file or enter a URL!");
      return;
    }
    
    setIsSeparatingStems(true);
    setSeparatedStems(null);
    
    try {
      let response;
      
      if (stemsFile) {
        // Upload file
        console.log("Uploading file:", stemsFile.name, stemsFile.size);
        toast.info(`Uploading ${stemsFile.name}...`);
        
        const formData = new FormData();
        formData.append('file', stemsFile);
        formData.append('email', userEmail || getGuestEmail());
        
        console.log("Sending to /separate-stems-upload...");
        response = await axios.post(`${API}/separate-stems-upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 60000, // 60 second timeout for upload
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Upload progress: ${percent}%`);
          }
        });
        console.log("Upload response:", response.data);
      } else {
        // Use URL
        response = await axios.post(`${API}/separate-stems`, {
          audio_url: stemsAudioUrl,
          email: userEmail || getGuestEmail()
        });
      }
      
      const taskId = response.data.task_id;
      setStemsTaskId(taskId);
      toast.success("Stem separation started! This may take 2-5 minutes...");
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 150; // 5 minutes
      
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await axios.get(`${API}/stems-ai-status/${taskId}`);
          const status = statusRes.data;
          
          if (status.status === 'completed') {
            clearInterval(pollInterval);
            setSeparatedStems(status.stems);
            setIsSeparatingStems(false);
            setStemsFile(null); // Clear file after success
            toast.success("Stems separated successfully!");
          } else if (status.status === 'failed') {
            clearInterval(pollInterval);
            setIsSeparatingStems(false);
            toast.error(status.error || "Stem separation failed. Try again!");
          } else if (!status.status) {
            // Task not found or invalid - server may have restarted
            clearInterval(pollInterval);
            setIsSeparatingStems(false);
            toast.error("Task lost. Please try again.");
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setIsSeparatingStems(false);
            toast.error("Stem separation timed out. Try again!");
          }
        } catch (e) {
          console.error("Poll error:", e);
          // Handle 404 - task not found
          if (e.response?.status === 404) {
            clearInterval(pollInterval);
            setIsSeparatingStems(false);
            toast.error("Task not found. Please try again.");
          }
        }
      }, 2000);
      
    } catch (error) {
      console.error("Stems separation error:", error);
      setIsSeparatingStems(false);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to separate stems. Try again!";
      toast.error(errorMsg);
    }
  };

  // Handle stems file drop
  const handleStemsDrop = (e) => {
    e.preventDefault();
    setStemsDragActive(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/m4a', 'audio/x-m4a', 'audio/flac', 'audio/ogg', 'audio/aac', 'video/mp4'];
      const allowedExts = ['.mp3', '.wav', '.m4a', '.flac', '.ogg', '.aac', '.mp4'];
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (allowedTypes.includes(file.type) || allowedExts.includes(ext)) {
        if (file.size > 50 * 1024 * 1024) {
          toast.error("File too large! Maximum size is 50MB");
          return;
        }
        setStemsFile(file);
        setStemsAudioUrl(""); // Clear URL when file is dropped
        toast.success(`File loaded: ${file.name}`);
      } else {
        toast.error("Please drop an audio file (MP3, WAV, M4A, FLAC, OGG)");
      }
    }
  };

  // Site-wide password gate: rendered before anything else
  if (!siteGateChecked || !siteGateOk) {
    return (
      <div className="quantum-bg" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a14' }} data-testid="site-gate-screen">
        <Toaster position="top-center" theme="dark" />
        {siteGateChecked && (
          <div style={{ background: '#12121e', border: '1px solid rgba(0, 212, 255, 0.3)', borderRadius: '20px', padding: '40px', width: '92%', maxWidth: '420px', textAlign: 'center' }}>
            <div style={{ fontSize: '2.4rem', marginBottom: '8px' }}>🧬</div>
            <h1 style={{ color: '#fff', fontSize: '1.5rem', margin: '0 0 6px 0', fontWeight: 800, letterSpacing: '1px' }}>MANNIS <span style={{ color: '#00d4ff' }}>PRO</span></h1>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.9rem', marginBottom: '24px' }}>This site is private. Enter the access password to continue.</p>
            <form onSubmit={handleSiteGateSubmit}>
              <div style={{ position: 'relative', marginBottom: '12px' }}>
                <input
                  type={showGatePassword ? 'text' : 'password'}
                  placeholder="Access password"
                  value={gatePassword}
                  onChange={(e) => setGatePassword(e.target.value)}
                  autoFocus
                  autoCapitalize="none"
                  autoCorrect="off"
                  autoComplete="off"
                  spellCheck={false}
                  style={{ width: '100%', padding: '14px 44px 14px 44px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '10px', color: '#fff', fontSize: '0.95rem', boxSizing: 'border-box', textAlign: 'center' }}
                  data-testid="site-gate-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowGatePassword(v => !v)}
                  aria-label={showGatePassword ? 'Hide password' : 'Show password'}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'rgba(255,255,255,0.55)', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center' }}
                  data-testid="site-gate-toggle-password-btn"
                >
                  {showGatePassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {gateError && <div style={{ color: '#ef4444', fontSize: '0.8rem', marginBottom: '12px' }} data-testid="site-gate-error">{gateError}</div>}
              <button
                type="submit"
                disabled={gateLoading}
                style={{ width: '100%', padding: '14px', background: gateLoading ? 'rgba(0,212,255,0.3)' : 'linear-gradient(135deg, #00d4ff, #8b5cf6)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: gateLoading ? 'wait' : 'pointer' }}
                data-testid="site-gate-submit-btn"
              >
                {gateLoading ? 'Verifying...' : 'Enter MANNIS'}
              </button>
            </form>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="suno-app quantum-bg quantum-grid">
      <Toaster position="top-center" theme="dark" />

      {/* GLOBAL FUSION GENERATION PROGRESS PILL - visible anywhere in the app */}
      {fusionSong?.status === 'generating' && (
        <div data-testid="fusion-progress-pill" style={{
          position: 'fixed', bottom: '24px', right: '96px', zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: '12px',
          background: 'rgba(10, 10, 20, 0.95)', border: '1px solid rgba(236, 72, 153, 0.5)',
          borderRadius: '999px', padding: '12px 20px', maxWidth: '380px',
          boxShadow: '0 8px 32px rgba(236, 72, 153, 0.3)', backdropFilter: 'blur(12px)'
        }}>
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#ec4899', flexShrink: 0 }} />
          <div>
            <p style={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700, margin: 0 }}>
              {fusionSong.title ? `Creating "${fusionSong.title}"` : 'Creating your track'} — {Math.min(fusionSong.progress || 5, 95)}%
            </p>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', margin: 0 }}>
              {fusionSong.note || 'Your track is being created, this usually takes a few minutes. Keep browsing — it saves to My Songs automatically.'}
            </p>
          </div>
        </div>
      )}
      {fusionSong?.status === 'completed' && fusionSong.url && !fusionSong.dismissed && (
        <div data-testid="fusion-ready-pill" style={{
          position: 'fixed', bottom: '24px', right: '96px', zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: '12px',
          background: 'rgba(6, 30, 15, 0.95)', border: '1px solid rgba(16, 185, 129, 0.6)',
          borderRadius: '999px', padding: '12px 20px', maxWidth: '380px',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)', backdropFilter: 'blur(12px)'
        }}>
          <CheckCircle className="w-5 h-5" style={{ color: '#10b981', flexShrink: 0 }} />
          <p style={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700, margin: 0 }}>
            🎵 {fusionSong.title ? `"${fusionSong.title}"` : 'Your track'} is ready — saved to My Songs!
          </p>
          <button data-testid="fusion-view-in-songs" onClick={() => { setActiveView('library'); setFusionSong(prev => ({ ...prev, dismissed: true })); }}
            style={{ background: 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.5)', color: '#10b981', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, padding: '4px 10px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
            View in My Songs
          </button>
          <button data-testid="fusion-ready-dismiss" onClick={() => setFusionSong(prev => ({ ...prev, dismissed: true }))}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: '1rem', padding: '0 4px' }}>×</button>
        </div>
      )}
      
      {/* FULLSCREEN INDIVIDUAL WORKFLOW SECTION VIEW */}
      {fullscreenWorkflowSection && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--bg-base)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(0, 212, 255, 0.2)',
            background: 'rgba(0, 0, 0, 0.3)'
          }}>
            <h1 style={{ 
              color: fullscreenWorkflowSection === 'style' ? '#00d4ff' :
                     fullscreenWorkflowSection === 'bpm' ? '#8b5cf6' :
                     fullscreenWorkflowSection === 'key' ? '#ec4899' :
                     fullscreenWorkflowSection === 'mode' ? '#f59e0b' :
                     fullscreenWorkflowSection === 'genre' ? '#10b981' :
                     fullscreenWorkflowSection === 'mood' ? '#6366f1' :
                     fullscreenWorkflowSection === 'voice' ? '#f97316' : '#a855f7',
              fontSize: '1.5rem', 
              fontWeight: 700, 
              margin: 0 
            }}>
              {fullscreenWorkflowSection === 'style' && '1. STYLE PROMPT'}
              {fullscreenWorkflowSection === 'bpm' && '2. BPM (TEMPO)'}
              {fullscreenWorkflowSection === 'key' && '3. KEY'}
              {fullscreenWorkflowSection === 'mode' && '4. MODE'}
              {fullscreenWorkflowSection === 'genre' && '5. GENRE'}
              {fullscreenWorkflowSection === 'mood' && '6. MOOD'}
              {fullscreenWorkflowSection === 'voice' && '7. VOICE STYLE'}
              {fullscreenWorkflowSection === 'lyrics' && '8. LYRICS'}
            </h1>
            <button
              onClick={() => setFullscreenWorkflowSection(null)}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to App
            </button>
          </div>
          
          {/* Section Content */}
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 24px'
          }}>
            <div style={{ width: '100%', maxWidth: '700px' }}>
              
              {/* 1. STYLE PROMPT */}
              {fullscreenWorkflowSection === 'style' && (
                <div style={{
                  background: 'rgba(0, 212, 255, 0.08)',
                  border: '2px solid rgba(0, 212, 255, 0.4)',
                  borderRadius: '20px',
                  padding: '32px'
                }}>
                  <label style={{ color: '#00d4ff', fontSize: '1rem', fontWeight: 700, marginBottom: '16px', display: 'block' }}>
                    Enter your complete style description
                  </label>
                  <textarea
                    value={beatPrompt || workflowPrompt.stylePrompt || ''}
                    onChange={(e) => {
                      setBeatPrompt(e.target.value);
                      updateWorkflow({ stylePrompt: e.target.value, source: 'manual' });
                    }}
                    placeholder="Describe your music style in detail... (e.g., Modern trap beat, 140 BPM, hard 808s, dark melody, minor key, atmospheric pads)"
                    style={{
                      width: '100%',
                      minHeight: '250px',
                      padding: '20px',
                      background: 'rgba(0, 0, 0, 0.5)',
                      border: '1px solid rgba(0, 212, 255, 0.3)',
                      borderRadius: '14px',
                      color: '#fff',
                      fontSize: '1.1rem',
                      lineHeight: 1.7,
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      universalCopy(beatPrompt || workflowPrompt.stylePrompt || '', 'Style prompt copied!');
                    }}
                    style={{
                      marginTop: '16px',
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #00d4ff, #0891b2)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    <Copy className="w-4 h-4" style={{ marginRight: '8px' }} />
                    Copy Style Prompt
                  </button>
                </div>
              )}
              
              {/* 2. BPM */}
              {fullscreenWorkflowSection === 'bpm' && (
                <div style={{
                  background: 'rgba(139, 92, 246, 0.08)',
                  border: '2px solid rgba(139, 92, 246, 0.4)',
                  borderRadius: '20px',
                  padding: '32px',
                  textAlign: 'center'
                }}>
                  <div style={{ 
                    fontSize: '6rem', 
                    fontWeight: 800, 
                    color: '#8b5cf6',
                    marginBottom: '24px'
                  }}>
                    {workflowPrompt.bpm || selectedBPM || 120}
                  </div>
                  <label style={{ color: '#8b5cf6', fontSize: '1rem', fontWeight: 600, display: 'block', marginBottom: '16px' }}>
                    Beats Per Minute
                  </label>
                  <input
                    type="range"
                    min="60"
                    max="200"
                    value={workflowPrompt.bpm || selectedBPM || 120}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      setSelectedBPM(val);
                      updateWorkflow({ bpm: val });
                    }}
                    style={{ width: '100%', height: '8px', marginBottom: '24px' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>
                    <span>60 (Slow)</span>
                    <span>130 (Mid)</span>
                    <span>200 (Fast)</span>
                  </div>
                </div>
              )}
              
              {/* 3. KEY */}
              {fullscreenWorkflowSection === 'key' && (
                <div style={{
                  background: 'rgba(236, 72, 153, 0.08)',
                  border: '2px solid rgba(236, 72, 153, 0.4)',
                  borderRadius: '20px',
                  padding: '32px'
                }}>
                  <label style={{ color: '#ec4899', fontSize: '1rem', fontWeight: 700, display: 'block', marginBottom: '20px', textAlign: 'center' }}>
                    Select Musical Key
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px' }}>
                    {/* Natural keys */}
                    {['C', 'D', 'E', 'F', 'G', 'A', 'B'].map(k => (
                      <button
                        key={k}
                        onClick={() => {
                          setSelectedKey(k);
                          updateWorkflow({ key: k });
                        }}
                        style={{
                          padding: '16px 8px',
                          background: (workflowPrompt.key || selectedKey) === k 
                            ? 'linear-gradient(135deg, #ec4899, #db2777)' 
                            : 'rgba(0,0,0,0.4)',
                          border: `2px solid ${(workflowPrompt.key || selectedKey) === k ? '#ec4899' : 'rgba(236, 72, 153, 0.3)'}`,
                          borderRadius: '10px',
                          color: '#fff',
                          fontSize: '1.2rem',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginTop: '12px' }}>
                    {/* Sharp keys */}
                    {['C#', 'D#', 'F#', 'G#', 'A#'].map(k => (
                      <button
                        key={k}
                        onClick={() => {
                          setSelectedKey(k);
                          updateWorkflow({ key: k });
                        }}
                        style={{
                          padding: '14px 6px',
                          background: (workflowPrompt.key || selectedKey) === k 
                            ? 'linear-gradient(135deg, #8b5cf6, #6d28d9)' 
                            : 'rgba(0,0,0,0.4)',
                          border: `2px solid ${(workflowPrompt.key || selectedKey) === k ? '#8b5cf6' : 'rgba(139, 92, 246, 0.3)'}`,
                          borderRadius: '10px',
                          color: '#fff',
                          fontSize: '1rem',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginTop: '10px' }}>
                    {/* Flat keys */}
                    {['Db', 'Eb', 'Gb', 'Ab', 'Bb'].map(k => (
                      <button
                        key={k}
                        onClick={() => {
                          setSelectedKey(k);
                          updateWorkflow({ key: k });
                        }}
                        style={{
                          padding: '14px 6px',
                          background: (workflowPrompt.key || selectedKey) === k 
                            ? 'linear-gradient(135deg, #00d4ff, #0099cc)' 
                            : 'rgba(0,0,0,0.4)',
                          border: `2px solid ${(workflowPrompt.key || selectedKey) === k ? '#00d4ff' : 'rgba(0, 212, 255, 0.3)'}`,
                          borderRadius: '10px',
                          color: '#fff',
                          fontSize: '1rem',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        {k}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 4. MODE */}
              {fullscreenWorkflowSection === 'mode' && (
                <div style={{
                  background: 'rgba(245, 158, 11, 0.08)',
                  border: '2px solid rgba(245, 158, 11, 0.4)',
                  borderRadius: '20px',
                  padding: '32px'
                }}>
                  <label style={{ color: '#f59e0b', fontSize: '1rem', fontWeight: 700, display: 'block', marginBottom: '24px', textAlign: 'center' }}>
                    Select Mode
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    {['major', 'minor'].map(m => (
                      <button
                        key={m}
                        onClick={() => {
                          setSelectedMode(m);
                          updateWorkflow({ mode: m });
                        }}
                        style={{
                          padding: '40px',
                          background: (workflowPrompt.mode || selectedMode) === m 
                            ? 'linear-gradient(135deg, #f59e0b, #d97706)' 
                            : 'rgba(0,0,0,0.4)',
                          border: `2px solid ${(workflowPrompt.mode || selectedMode) === m ? '#f59e0b' : 'rgba(245, 158, 11, 0.3)'}`,
                          borderRadius: '16px',
                          color: '#fff',
                          fontSize: '1.8rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          textTransform: 'uppercase'
                        }}
                      >
                        {m}
                        <div style={{ fontSize: '0.9rem', fontWeight: 400, marginTop: '8px', opacity: 0.7 }}>
                          {m === 'major' ? '(Happy, Bright)' : '(Sad, Dark)'}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 5. GENRE */}
              {fullscreenWorkflowSection === 'genre' && (
                <div style={{
                  background: 'rgba(16, 185, 129, 0.08)',
                  border: '2px solid rgba(16, 185, 129, 0.4)',
                  borderRadius: '20px',
                  padding: '32px'
                }}>
                  <label style={{ color: '#10b981', fontSize: '1rem', fontWeight: 700, display: 'block', marginBottom: '16px' }}>
                    Enter Genre
                  </label>
                  <input
                    type="text"
                    value={workflowPrompt.genre || ''}
                    onChange={(e) => updateWorkflow({ genre: e.target.value })}
                    placeholder="e.g., Trap, R&B, Pop, Rock, Hip-Hop, EDM..."
                    style={{
                      width: '100%',
                      padding: '20px',
                      background: 'rgba(0,0,0,0.5)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: '14px',
                      color: '#fff',
                      fontSize: '1.3rem',
                      textAlign: 'center'
                    }}
                    autoFocus
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '20px', justifyContent: 'center' }}>
                    {['Trap', 'R&B', 'Pop', 'Hip-Hop', 'Rock', 'EDM', 'Jazz', 'Country', 'Latin'].map(g => (
                      <button
                        key={g}
                        onClick={() => updateWorkflow({ genre: g })}
                        style={{
                          padding: '10px 20px',
                          background: workflowPrompt.genre === g ? 'rgba(16, 185, 129, 0.4)' : 'rgba(0,0,0,0.3)',
                          border: '1px solid rgba(16, 185, 129, 0.3)',
                          borderRadius: '20px',
                          color: '#fff',
                          fontSize: '0.9rem',
                          cursor: 'pointer'
                        }}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 6. MOOD */}
              {fullscreenWorkflowSection === 'mood' && (
                <div style={{
                  background: 'rgba(99, 102, 241, 0.08)',
                  border: '2px solid rgba(99, 102, 241, 0.4)',
                  borderRadius: '20px',
                  padding: '32px'
                }}>
                  <label style={{ color: '#6366f1', fontSize: '1rem', fontWeight: 700, display: 'block', marginBottom: '16px' }}>
                    Enter Mood
                  </label>
                  <input
                    type="text"
                    value={workflowPrompt.mood || ''}
                    onChange={(e) => updateWorkflow({ mood: e.target.value })}
                    placeholder="e.g., Dark, Uplifting, Chill, Aggressive, Emotional..."
                    style={{
                      width: '100%',
                      padding: '20px',
                      background: 'rgba(0,0,0,0.5)',
                      border: '1px solid rgba(99, 102, 241, 0.3)',
                      borderRadius: '14px',
                      color: '#fff',
                      fontSize: '1.3rem',
                      textAlign: 'center'
                    }}
                    autoFocus
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '20px', justifyContent: 'center' }}>
                    {['Dark', 'Uplifting', 'Chill', 'Aggressive', 'Emotional', 'Energetic', 'Melancholic', 'Party'].map(m => (
                      <button
                        key={m}
                        onClick={() => updateWorkflow({ mood: m })}
                        style={{
                          padding: '10px 20px',
                          background: workflowPrompt.mood === m ? 'rgba(99, 102, 241, 0.4)' : 'rgba(0,0,0,0.3)',
                          border: '1px solid rgba(99, 102, 241, 0.3)',
                          borderRadius: '20px',
                          color: '#fff',
                          fontSize: '0.9rem',
                          cursor: 'pointer'
                        }}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 7. VOICE STYLE */}
              {fullscreenWorkflowSection === 'voice' && (
                <div style={{
                  background: 'rgba(249, 115, 22, 0.08)',
                  border: '2px solid rgba(249, 115, 22, 0.4)',
                  borderRadius: '20px',
                  padding: '32px'
                }}>
                  <label style={{ color: '#f97316', fontSize: '1rem', fontWeight: 700, display: 'block', marginBottom: '16px' }}>
                    Describe Voice Style
                  </label>
                  <textarea
                    value={workflowPrompt.voiceSample || ''}
                    onChange={(e) => updateWorkflow({ voiceSample: e.target.value })}
                    placeholder="Describe the vocal style... (e.g., Smooth R&B male vocals, Raspy rock female, Soft breathy pop, Deep baritone rap)"
                    style={{
                      width: '100%',
                      minHeight: '150px',
                      padding: '20px',
                      background: 'rgba(0,0,0,0.5)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '14px',
                      color: '#fff',
                      fontSize: '1.1rem',
                      lineHeight: 1.6,
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                    autoFocus
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '20px', justifyContent: 'center' }}>
                    {['Smooth R&B', 'Raspy Rock', 'Soft Pop', 'Deep Rap', 'Powerful Belter', 'Whisper Tone'].map(v => (
                      <button
                        key={v}
                        onClick={() => updateWorkflow({ voiceSample: v })}
                        style={{
                          padding: '10px 20px',
                          background: 'rgba(0,0,0,0.3)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '20px',
                          color: '#fff',
                          fontSize: '0.9rem',
                          cursor: 'pointer'
                        }}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 8. LYRICS */}
              {fullscreenWorkflowSection === 'lyrics' && (
                <div style={{
                  background: 'rgba(168, 85, 247, 0.08)',
                  border: '2px solid rgba(168, 85, 247, 0.4)',
                  borderRadius: '20px',
                  padding: '32px'
                }}>
                  <label style={{ color: '#a855f7', fontSize: '1rem', fontWeight: 700, display: 'block', marginBottom: '16px' }}>
                    Enter Lyrics
                  </label>
                  <textarea
                    value={workflowPrompt.lyrics || generatedBars?.raw_lyrics || ''}
                    onChange={(e) => updateWorkflow({ lyrics: e.target.value })}
                    placeholder="Write or paste your lyrics here..."
                    style={{
                      width: '100%',
                      minHeight: '350px',
                      padding: '20px',
                      background: 'rgba(0,0,0,0.5)',
                      border: '1px solid rgba(168, 85, 247, 0.3)',
                      borderRadius: '14px',
                      color: '#fff',
                      fontSize: '1.1rem',
                      lineHeight: 1.8,
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                    autoFocus
                  />
                  <button
                    onClick={() => {
                      universalCopy(workflowPrompt.lyrics || generatedBars?.raw_lyrics || '', 'Lyrics copied!');
                    }}
                    style={{
                      marginTop: '16px',
                      padding: '12px 24px',
                      background: 'linear-gradient(135deg, #a855f7, #9333ea)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    <Copy className="w-4 h-4" style={{ marginRight: '8px' }} />
                    Copy Lyrics
                  </button>
                </div>
              )}
              
            </div>
          </div>
        </div>
      )}
      
      {/* FULLSCREEN INDIVIDUAL TOOL VIEW */}
      {fullscreenTool === 'all-tools' && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(135deg, #0a0f1a 0%, #1a1a2e 50%, #0d1526 100%)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto'
        }}>
          {/* Header with Back Button */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(0, 0, 0, 0.3)',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <h1 style={{ 
              color: '#00d4ff',
              fontSize: '1.3rem', 
              fontWeight: 700, 
              margin: 0,
              textTransform: 'uppercase'
            }}>
              🎹 MANNIS - ALL TOOLS
            </h1>
            <button
              onClick={() => setFullscreenTool(null)}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              ← Back to App
            </button>
          </div>
          
          {/* All 7 Tools Stacked */}
          <div style={{ padding: '24px 24px 96px', maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
            {/* Bottom action bar: turn the combined selections into an actual track */}
            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', padding: '14px 24px', background: 'rgba(5,8,16,0.72)', backdropFilter: 'blur(12px)', borderTop: '1px solid rgba(0,212,255,0.25)' }}>
              <button
                onClick={() => {
                  const base = (workflowPrompt.stylePrompt || beatPrompt || '').trim();
                  const parts = [];
                  if (base) {
                    parts.push(base);
                    if (workflowPrompt.bpm || selectedBPM) parts.push(`${workflowPrompt.bpm || selectedBPM} BPM`);
                    const k = workflowPrompt.key || selectedKey;
                    if (k) parts.push(`${k} ${workflowPrompt.mode || selectedMode || ''}`.trim());
                    if (workflowPrompt.voiceSample) parts.push(`vocals: ${workflowPrompt.voiceSample}`);
                  }
                  const bundle = parts.join(', ');
                  setFullscreenTool(null);
                  setAwTask(null);
                  setShowAutoWorkflow(true);
                  if (bundle) {
                    // Seed the combined selections into the workflow so the Auto Pipeline generates from them
                    setWorkflowPrompt(prev => ({ ...prev, stylePrompt: bundle, source: 'all-tools' }));
                    toast.success('🎵 Sending your combined selections to the Auto Pipeline…');
                    startAutoWorkflow(bundle);
                  } else {
                    toast('Pick a style prompt first (70K Style Prompts), then run the pipeline.', { icon: '💡' });
                  }
                }}
                data-testid="all-tools-generate-song-btn"
                style={{ padding: '12px 30px', background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)', border: 'none', borderRadius: '12px', color: '#fff', fontSize: '0.95rem', fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 20px rgba(0,212,255,0.35)' }}
              >🎵 GENERATE SONG</button>
              <button
                onClick={() => { setFullscreenTool(null); setAwTask(null); setShowAutoWorkflow(true); }}
                data-testid="all-tools-auto-pipeline-btn"
                style={{ padding: '12px 20px', background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.5)', borderRadius: '12px', color: '#c4b5fd', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
              >⚡ Auto Pipeline</button>
            </div>
            
            {/* 1. ANALYZE MP3 */}
            <div style={{
              background: 'rgba(0, 212, 255, 0.08)',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '20px'
            }}>
              <h2 style={{ color: '#00d4ff', fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Upload className="w-5 h-5" /> 1. ANALYZE MP3
              </h2>
              <div
                {...getRootProps()}
                style={{
                  border: `2px dashed ${isDragActive ? '#00d4ff' : 'rgba(0, 212, 255, 0.4)'}`,
                  borderRadius: '12px',
                  padding: '30px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: isDragActive ? 'rgba(0, 212, 255, 0.15)' : 'rgba(0, 212, 255, 0.05)'
                }}
              >
                <input {...getInputProps()} />
                <p style={{ color: '#00d4ff', fontWeight: 600 }}>
                  {uploadedFile ? `📁 ${uploadedFile.name}` : 'Click or Drag & Drop MP3'}
                </p>
              </div>
              {uploadedFile && (
                <button
                  onClick={handleGenerate}
                  disabled={isAnalyzing}
                  style={{
                    marginTop: '12px',
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #00d4ff, #0ea5e9)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {isAnalyzing ? '⏳ Analyzing...' : '🎵 Analyze & Generate Style'}
                </button>
              )}
            </div>
            
            {/* 2. 40K PROMPTS */}
            <div style={{
              background: 'rgba(139, 92, 246, 0.08)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '20px'
            }}>
              <h2 style={{ color: '#8b5cf6', fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Star className="w-5 h-5" /> 2. 70K STYLE PROMPTS
              </h2>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
                <input
                  type="text"
                  placeholder="Search prompts..."
                  value={sunoLibrarySearch}
                  onChange={(e) => setSunoLibrarySearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchSunoLibrary(sunoLibrarySearch)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '10px',
                    color: '#fff'
                  }}
                />
                <button
                  onClick={() => searchSunoLibrary(sunoLibrarySearch)}
                  style={{
                    padding: '12px 20px',
                    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  <Search className="w-4 h-4" />
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {sunoLibraryCategories.slice(0, 8).map(cat => (
                  <button
                    key={cat.name}
                    onClick={() => { setSunoLibraryCategory(cat.name); loadSunoLibraryPrompts(cat.name, 1); }}
                    style={{
                      padding: '6px 12px',
                      background: sunoLibraryCategory === cat.name ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' : 'rgba(139, 92, 246, 0.15)',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '0.75rem',
                      cursor: 'pointer'
                    }}
                  >
                    {mannisCat(cat.name)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* 3. ADD VOICE */}
            <div style={{
              background: 'rgba(236, 72, 153, 0.08)',
              border: '1px solid rgba(236, 72, 153, 0.3)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '20px'
            }}>
              <h2 style={{ color: '#ec4899', fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Mic2 className="w-5 h-5" /> 3. ADD VOICE
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                {['Male Deep', 'Male Tenor', 'Female Alto', 'Female Soprano', 'Choir', 'Rap/Spoken', 'Whisper', 'Falsetto'].map(voice => (
                  <button
                    key={voice}
                    onClick={() => { setSelectedVoiceSample(voice); updateWorkflow({ voiceSample: voice }); toast.success(`${voice} added!`); }}
                    style={{
                      padding: '12px',
                      background: selectedVoiceSample === voice ? 'linear-gradient(135deg, #ec4899, #be185d)' : 'rgba(236, 72, 153, 0.15)',
                      border: '1px solid rgba(236, 72, 153, 0.3)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '0.8rem',
                      cursor: 'pointer'
                    }}
                  >
                    {voice}
                  </button>
                ))}
              </div>
            </div>
            
            {/* 4. ADD LYRICS */}
            <div style={{
              background: 'rgba(249, 115, 22, 0.08)',
              border: '1px solid rgba(249, 115, 22, 0.3)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '20px'
            }}>
              <h2 style={{ color: '#f97316', fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FileText className="w-5 h-5" /> 4. ADD LYRICS
              </h2>
              <textarea
                value={workflowPrompt.lyrics}
                onChange={(e) => updateWorkflow({ lyrics: e.target.value })}
                placeholder="[Verse 1]&#10;Your lyrics here..."
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '16px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(249, 115, 22, 0.3)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  resize: 'vertical'
                }}
              />
            </div>
            
            {/* 5. AI SINGING */}
            <div style={{
              background: 'rgba(236, 72, 153, 0.08)',
              border: '1px solid rgba(236, 72, 153, 0.3)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '20px'
            }}>
              <h2 style={{ color: '#ec4899', fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Music2 className="w-5 h-5" /> 5. AI SINGING
              </h2>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <select value={singingGender} onChange={(e) => setSingingGender(e.target.value)} style={{ padding: '10px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(236, 72, 153, 0.3)', borderRadius: '8px', color: '#fff' }}>
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
                <select value={singingStyle} onChange={(e) => setSingingStyle(e.target.value)} style={{ flex: 1, padding: '10px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(236, 72, 153, 0.3)', borderRadius: '8px', color: '#fff' }}>
                  <option value="pop ballad">Pop Ballad</option>
                  <option value="rnb soul">R&B Soul</option>
                  <option value="rock anthem">Rock Anthem</option>
                </select>
              </div>
              <textarea
                value={singingLyrics}
                onChange={(e) => setSingingLyrics(e.target.value)}
                placeholder="Enter lyrics to sing..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '12px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(236, 72, 153, 0.3)',
                  borderRadius: '8px',
                  color: '#fff',
                  marginBottom: '12px'
                }}
              />
              <button
                onClick={generateSingingVocals}
                disabled={singingStatus === 'generating'}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #ec4899, #be185d)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {singingStatus === 'generating' ? `⏳ ${singingProgress}%` : '🎤 Generate Vocals'}
              </button>
            </div>
            
            {/* 6. WORLD VOICES */}
            <div style={{
              background: 'rgba(6, 182, 212, 0.08)',
              border: '1px solid rgba(6, 182, 212, 0.3)',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '20px'
            }}>
              <h2 style={{ color: '#06b6d4', fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Globe className="w-5 h-5" /> 6. WORLD VOICES
              </h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {['pop', 'rnb', 'rock', 'hiphop', 'country', 'gospel', 'jazz', 'electronic'].map(region => (
                  <button
                    key={region}
                    onClick={() => { setWorldVoiceRegion(region); if (!worldVoiceData) loadWorldVoiceData(); }}
                    style={{
                      padding: '8px 14px',
                      background: worldVoiceRegion === region ? 'linear-gradient(135deg, #06b6d4, #a855f7)' : 'rgba(6, 182, 212, 0.15)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      textTransform: 'capitalize'
                    }}
                  >
                    {region}
                  </button>
                ))}
              </div>
            </div>
            
            {/* 7. 650 PRESETS */}
            <div style={{
              background: 'rgba(16, 185, 129, 0.08)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <h2 style={{ color: '#10b981', fontSize: '1.1rem', fontWeight: 700, marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Compass className="w-5 h-5" /> 7. 650 PRESETS
              </h2>
              <button
                onClick={() => { setActiveView('marketplace'); fetchMarketplaceDNAs(); setFullscreenTool(null); }}
                style={{
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Browse {marketplaceDNAs.length || 650}+ Presets →
              </button>
            </div>
            
          </div>
        </div>
      )}

      {/* RHYME & FLOW ENGINE FULLSCREEN */}
      {fullscreenTool === 'rhyme' && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(135deg, #0a0f1a 0%, #1a1a2e 50%, #0d1526 100%)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid rgba(249, 115, 22, 0.3)',
            background: 'rgba(0, 0, 0, 0.3)',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <h1 style={{ color: '#f97316', fontSize: '1.3rem', fontWeight: 700, margin: 0 }}>
              🎤 RHYME & FLOW ENGINE
            </h1>
            <button
              onClick={() => setFullscreenTool(null)}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #f97316, #ea580c)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              ← Back to App
            </button>
          </div>
          <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto', width: '100%' }}>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <button
                onClick={() => setRhymeEngineTab("generate")}
                style={{
                  padding: '10px 20px',
                  background: rhymeEngineTab === 'generate' ? 'linear-gradient(135deg, #f97316, #ea580c)' : 'rgba(249, 115, 22, 0.15)',
                  border: rhymeEngineTab === 'generate' ? 'none' : '1px solid rgba(249, 115, 22, 0.3)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <Zap className="w-4 h-4" style={{ marginRight: '8px', display: 'inline' }} />
                AI Generated
              </button>
              <button
                onClick={() => setRhymeEngineTab("custom")}
                style={{
                  padding: '10px 20px',
                  background: rhymeEngineTab === 'custom' ? 'linear-gradient(135deg, #f97316, #ea580c)' : 'rgba(249, 115, 22, 0.15)',
                  border: rhymeEngineTab === 'custom' ? 'none' : '1px solid rgba(249, 115, 22, 0.3)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <Edit3 className="w-4 h-4" style={{ marginRight: '8px', display: 'inline' }} />
                My Own Lyrics (Original)
              </button>
            </div>

            {/* Song Title */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#f97316', fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '8px' }}>SONG TITLE</label>
              <input
                type="text"
                value={songTitle}
                onChange={(e) => setSongTitle(e.target.value)}
                placeholder="Enter your song title..."
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(249, 115, 22, 0.3)',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* AI Generate Tab */}
            {rhymeEngineTab === 'generate' && (
              <>
                {/* Controls Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>STYLE</label>
                    <select value={rhymeStyle} onChange={(e) => setRhymeStyle(e.target.value)} style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(249, 115, 22, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.85rem' }}>
                      <option value="modern trap">Modern Trap (Atlanta)</option>
                      <option value="southern soul">Southern Soul</option>
                      <option value="pain">Pain / Emotional</option>
                      <option value="boom bap">Boom Bap (90s NY)</option>
                      <option value="drill">UK Drill</option>
                      <option value="west coast">West Coast G-Funk</option>
                      <option value="melodic">Melodic Rap</option>
                      <option value="conscious">Conscious/Lyrical</option>
                      <option value="party">Party/Club</option>
                      <option value="rnb">R&B / Soul</option>
                      <option value="afrobeats">Afrobeats</option>
                      <option value="gospel">Gospel</option>
                      <option value="reggaeton">Reggaeton</option>
                      <option value="dancehall">Dancehall</option>
                      <option value="baile funk">Baile Funk</option>
                      <option value="uk garage">UK Garage</option>
                      <option value="phonk">Phonk</option>
                    </select>
                    <StyleBlueprintPreview styleKey={rhymeStyle} testId="style-blueprint-preview-main" />
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>RHYME SCHEME</label>
                    <select value={rhymeScheme} onChange={(e) => setRhymeScheme(e.target.value)} style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(249, 115, 22, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.85rem' }}>
                      <option value="AABB">AABB (Couplets)</option>
                      <option value="ABAB">ABAB (Alternating)</option>
                      <option value="ABBA">ABBA (Enclosed)</option>
                      <option value="AAAA">AAAA (Mono-rhyme)</option>
                      <option value="AAAB">AAAB (Triple + Twist)</option>
                      <option value="ABCB">ABCB (Ballad Stanza)</option>
                      <option value="ABCABC">ABCABC (Triple Rhyme)</option>
                      <option value="AABBA">AABBA (Limerick)</option>
                      <option value="AABCCB">AABCCB (Sestet)</option>
                      <option value="XAXA">XAXA (Alternate Blank)</option>
                      <option value="chain">Chain Rhyme (ABA BCB)</option>
                      <option value="internal">Internal Rhymes</option>
                      <option value="multisyllabic">Multi-Syllabic</option>
                      <option value="compound">Compound Rhymes</option>
                      <option value="slant">Slant / Near Rhymes</option>
                      <option value="assonance">Assonance (Vowel Rhymes)</option>
                      <option value="alliteration">Alliteration Driven</option>
                      <option value="internal-multi">Internal Multi-Syllabic</option>
                      <option value="cross">Cross Rhyme</option>
                      <option value="free">Free Form</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>FLOW PATTERN</label>
                    <select value={flowPattern} onChange={(e) => setFlowPattern(e.target.value)} style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(249, 115, 22, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.85rem' }}>
                      <option value="standard">Standard (4/4 Flow)</option>
                      <option value="boom-bap-cadence">Boom Bap Cadence (90s Pocket)</option>
                      <option value="triplet">Triplet Flow (Migos)</option>
                      <option value="double-time">Double-Time (Fast)</option>
                      <option value="chopper">Chopper (Tech N9ne Speed)</option>
                      <option value="slow-flow">Slow Flow (Deliberate)</option>
                      <option value="lazy-drawl">Lazy Drawl (Behind the Beat)</option>
                      <option value="syncopated">Syncopated (Off-Beat)</option>
                      <option value="halftime">Half-Time (Trap Beat)</option>
                      <option value="drill-slide">Drill Slide (UK Cadence)</option>
                      <option value="grime">Grime (140 Skippy)</option>
                      <option value="melodic-flow">Melodic Flow</option>
                      <option value="sing-song">Sing-Song (Playground Cadence)</option>
                      <option value="stutter">Stutter Flow (Word Repeats)</option>
                      <option value="start-stop">Start-Stop (Dramatic Pauses)</option>
                      <option value="pocket">Pocket Flow (Locked to Drums)</option>
                      <option value="bounce">Bounce (New Orleans)</option>
                      <option value="afro-bounce">Afro Bounce</option>
                      <option value="reggaeton-flow">Reggaeton Dembow</option>
                      <option value="syncopated-offbeat">Syncopated Off-Beat</option>
                      <option value="waterfall">Waterfall</option>
                      <option value="spoken-word">Spoken Word / Poetry</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>LANGUAGE</label>
                    <select value={rhymeLanguage} onChange={(e) => setRhymeLanguage(e.target.value)} style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(249, 115, 22, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.85rem' }}>
                      {OWN_LYRICS_LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>BARS COUNT</label>
                    <select value={barsCount} onChange={(e) => setBarsCount(e.target.value)} style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(249, 115, 22, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.85rem' }}>
                      <option value="8">8 Bars</option>
                      <option value="12">12 Bars</option>
                      <option value="16">16 Bars</option>
                      <option value="24">24 Bars</option>
                      <option value="32">32 Bars</option>
                      <option value="48">48 Bars</option>
                      <option value="64">64 Bars</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>DURATION (2-5 MIN)</label>
                    <input
                      type="number"
                      min={2}
                      max={5}
                      step={0.5}
                      value={rhymeDuration}
                      onChange={(e) => setRhymeDuration(e.target.value === '' ? '' : parseFloat(e.target.value))}
                      onBlur={() => setRhymeDuration(Math.min(5, Math.max(2, parseFloat(rhymeDuration) || 3)))}
                      data-testid="rhyme-duration-song-creator"
                      style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(249, 115, 22, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.85rem' }}
                    />
                  </div>
                </div>

                {/* Topic Input */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ color: '#f97316', fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '8px' }}>TOPIC</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <input
                      type="text"
                      value={rhymeTopic}
                      onChange={(e) => setRhymeTopic(e.target.value)}
                      placeholder="Enter topic (e.g., hustle, love, dreams, success...)"
                      style={{
                        flex: 1,
                        padding: '14px 18px',
                        background: 'rgba(0,0,0,0.4)',
                        border: '1px solid rgba(249, 115, 22, 0.3)',
                        borderRadius: '12px',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && generateBars()}
                    />
                    <button
                      onClick={generateBars}
                      disabled={isGeneratingBars || !rhymeTopic.trim()}
                      style={{
                        padding: '14px 32px',
                        background: isGeneratingBars ? 'rgba(249, 115, 22, 0.3)' : 'linear-gradient(135deg, #f97316, #ea580c)',
                        border: 'none',
                        borderRadius: '12px',
                        color: '#fff',
                        fontWeight: 700,
                        cursor: isGeneratingBars ? 'wait' : 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      {isGeneratingBars ? '⏳ Generating...' : `🎤 Generate ${barsCount} Bars`}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Custom Lyrics Tab */}
            {rhymeEngineTab === 'custom' && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#f97316', fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '8px' }}>YOUR LYRICS</label>
                <textarea
                  value={customLyrics}
                  onChange={(e) => setCustomLyrics(e.target.value)}
                  placeholder="Write or paste your own lyrics here..."
                  style={{
                    width: '100%',
                    minHeight: '200px',
                    padding: '16px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '12px',
                    color: '#fff',
                    fontSize: '1rem',
                    lineHeight: 1.6,
                    resize: 'vertical'
                  }}
                  data-testid="rhyme-fullscreen-lyrics-textarea"
                />
                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginTop: '10px' }} data-testid="own-lyrics-language-row-fullscreen">
                  <div style={{ flex: 1 }}>
                    <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>🌍 LANGUAGE</label>
                    <select value={rhymeLanguage} onChange={(e) => setRhymeLanguage(e.target.value)} style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(249, 115, 22, 0.3)', borderRadius: '10px', color: '#fff', fontSize: '0.85rem' }} data-testid="own-lyrics-language-select-fullscreen">
                      {OWN_LYRICS_LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <button onClick={translateCustomLyrics} disabled={lyricsAssistLoading} style={{ padding: '12px 18px', background: 'rgba(0, 212, 255, 0.12)', border: '1px solid rgba(0, 212, 255, 0.5)', borderRadius: '10px', color: '#00d4ff', fontSize: '0.85rem', fontWeight: 700, cursor: lyricsAssistLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap' }} data-testid="own-lyrics-translate-btn-fullscreen">
                    {lyricsAssistLoading ? '⏳' : '🌍 Translate'}
                  </button>
                </div>
                <select
                  value=""
                  disabled={lyricsAssistLoading}
                  onChange={(e) => { if (e.target.value) runLyricsAssistant(e.target.value); }}
                  style={{
                    width: '100%',
                    marginTop: '10px',
                    padding: '12px',
                    background: 'rgba(139, 92, 246, 0.12)',
                    border: '1px solid rgba(139, 92, 246, 0.5)',
                    borderRadius: '10px',
                    color: '#8b5cf6',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: lyricsAssistLoading ? 'wait' : 'pointer'
                  }}
                  data-testid="lyric-assistant-dropdown-fullscreen"
                >
                  <option value="">{lyricsAssistLoading ? '🪄 AI working on your lyrics...' : '🪄 AI Lyric Assistant...'}</option>
                  <option value="expand">➕ Expand — add the next 4 lines in your rhyme scheme</option>
                  <option value="polish">✨ Sonic Polish — better synonyms, flow & sound</option>
                  <option value="rewrite">🏆 Full Rewrite — professional finish of your ideas</option>
                </select>
              </div>
            )}

            {/* Generated Bars Display */}
            {generatedBars && (
              <div style={{
                background: 'rgba(249, 115, 22, 0.1)',
                border: '1px solid rgba(249, 115, 22, 0.3)',
                borderRadius: '16px',
                padding: '24px'
              }}>
                <h3 style={{ color: '#f97316', marginBottom: '16px', fontWeight: 700 }}>Generated Bars</h3>
                <pre style={{
                  color: '#fff',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  margin: 0
                }}>
                  {typeof generatedBars === 'string' ? generatedBars : (generatedBars.raw_lyrics || generatedBars.bars || '')}
                </pre>
                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <button
                    onClick={() => { universalCopy(typeof generatedBars === 'string' ? generatedBars : (generatedBars.raw_lyrics || generatedBars.bars || ''), 'Copied!'); }}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'rgba(249, 115, 22, 0.3)',
                      border: '1px solid #f97316',
                      borderRadius: '10px',
                      color: '#f97316',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    <Copy className="w-4 h-4" style={{ display: 'inline', marginRight: '8px' }} />
                    Copy Bars
                  </button>
                  <button
                    onClick={() => { updateWorkflow({ lyrics: generatedBars }); toast.success('Added to workflow!'); }}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'linear-gradient(135deg, #f97316, #ea580c)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    + Add to Workflow
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SINGING VOCAL GENERATOR FULLSCREEN */}
      {fullscreenTool === 'singing' && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(135deg, #0a0f1a 0%, #1a1a2e 50%, #0d1526 100%)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid rgba(236, 72, 153, 0.3)',
            background: 'rgba(0, 0, 0, 0.3)',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <h1 style={{ color: '#ec4899', fontSize: '1.3rem', fontWeight: 700, margin: 0 }}>
              🎵 SINGING VOCAL GENERATOR
            </h1>
            <button
              onClick={() => setFullscreenTool(null)}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              ← Back to App
            </button>
          </div>
          <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto', width: '100%' }}>
            {/* Voice Settings */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ color: '#ec4899', fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Gender</label>
                <select
                  value={singingGender}
                  onChange={(e) => setSingingGender(e.target.value)}
                  style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(236, 72, 153, 0.3)', borderRadius: '10px', color: '#fff' }}
                >
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
              </div>
              <div>
                <label style={{ color: '#ec4899', fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Style</label>
                <select
                  value={singingStyle}
                  onChange={(e) => setSingingStyle(e.target.value)}
                  style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(236, 72, 153, 0.3)', borderRadius: '10px', color: '#fff' }}
                >
                  <option value="pop ballad">Pop Ballad</option>
                  <option value="rnb soul">R&B Soul</option>
                  <option value="rock anthem">Rock Anthem</option>
                  <option value="hip hop">Hip Hop</option>
                  <option value="electronic">Electronic</option>
                </select>
              </div>
              <div>
                <label style={{ color: '#ec4899', fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Language</label>
                <select
                  value={singingLanguage}
                  onChange={(e) => setSingingLanguage(e.target.value)}
                  style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(236, 72, 153, 0.3)', borderRadius: '10px', color: '#fff' }}
                >
                  <option value="English">English</option>
                  <option value="Chinese">Chinese</option>
                  <option value="Japanese">Japanese</option>
                  <option value="Korean">Korean</option>
                  <option value="Spanish">Spanish</option>
                  <option value="Punjabi">Punjabi</option>
                </select>
              </div>
            </div>
            
            {/* Lyrics Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#ec4899', fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '8px' }}>
                Lyrics to Sing
              </label>
              <textarea
                value={singingLyrics}
                onChange={(e) => setSingingLyrics(e.target.value)}
                placeholder="Enter lyrics you want to turn into singing vocals..."
                style={{
                  width: '100%',
                  minHeight: '180px',
                  padding: '16px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(236, 72, 153, 0.3)',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  resize: 'vertical'
                }}
              />
            </div>
            
            {/* Generate Button */}
            <button
              onClick={generateSingingVocals}
              disabled={singingStatus === 'generating' || !singingLyrics.trim()}
              style={{
                width: '100%',
                padding: '16px',
                background: singingStatus === 'generating' ? 'rgba(236, 72, 153, 0.3)' : 'linear-gradient(135deg, #ec4899, #a855f7)',
                border: 'none',
                borderRadius: '12px',
                color: '#fff',
                fontSize: '1.1rem',
                fontWeight: 700,
                cursor: singingStatus === 'generating' ? 'wait' : 'pointer',
                marginBottom: '20px'
              }}
            >
              {singingStatus === 'generating' ? `⏳ Generating... ${singingProgress}%` : '🎤 GENERATE SINGING VOCALS'}
            </button>
            
            {/* Audio Player */}
            {singingAudioUrl && (
              <div style={{
                background: 'rgba(236, 72, 153, 0.1)',
                border: '1px solid rgba(236, 72, 153, 0.3)',
                borderRadius: '16px',
                padding: '20px'
              }}>
                <audio preload="metadata" controls src={singingAudioUrl} style={{ width: '100%', marginBottom: '16px' }} />
                <button
                  onClick={() => saveWavFromUrl(singingAudioUrl, 'singing-vocals.wav')}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '14px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: 600,
                    textAlign: 'center',
                    cursor: 'pointer'
                  }}
                >
                  <Download className="w-4 h-4" style={{ display: 'inline', marginRight: '8px' }} />
                  Download WAV
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* DNA LYRICS FULLSCREEN */}
      {fullscreenTool === 'dna-lyrics' && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(135deg, #0a0f1a 0%, #1a1a2e 50%, #0d1526 100%)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid rgba(245, 158, 11, 0.3)',
            background: 'rgba(0, 0, 0, 0.3)',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}>
            <h1 style={{ color: '#f59e0b', fontSize: '1.3rem', fontWeight: 700, margin: 0 }}>
              ✨ DNA LYRICS GENERATOR
            </h1>
            <button
              onClick={() => setFullscreenTool(null)}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              ← Back to App
            </button>
          </div>
          <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto', width: '100%' }}>
            {/* Settings Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
              <div>
                <label style={{ color: '#f59e0b', fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Genre</label>
                <select value={lyricsGenre} onChange={(e) => setLyricsGenre(e.target.value)} style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '10px', color: '#fff' }}>
                  <option value="Hip Hop">Hip Hop</option>
                  <option value="R&B">R&B</option>
                  <option value="Pop">Pop</option>
                  <option value="Rock">Rock</option>
                  <option value="Country">Country</option>
                  <option value="Gospel">Gospel</option>
                </select>
              </div>
              <div>
                <label style={{ color: '#f59e0b', fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Mood</label>
                <select value={lyricsMood} onChange={(e) => setLyricsMood(e.target.value)} style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '10px', color: '#fff' }}>
                  <option value="Confident">Confident</option>
                  <option value="Emotional">Emotional</option>
                  <option value="Aggressive">Aggressive</option>
                  <option value="Romantic">Romantic</option>
                  <option value="Melancholic">Melancholic</option>
                  <option value="Uplifting">Uplifting</option>
                </select>
              </div>
              <div>
                <label style={{ color: '#f59e0b', fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Language</label>
                <select value={lyricsLanguage} onChange={(e) => setLyricsLanguage(e.target.value)} style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '10px', color: '#fff' }}>
                  <option value="English">🇺🇸 English</option>
                  <option value="Spanish">🇪🇸 Spanish</option>
                  <option value="French">🇫🇷 French</option>
                  <option value="Portuguese">🇧🇷 Portuguese</option>
                  <option value="Japanese">🇯🇵 Japanese</option>
                  <option value="Korean">🇰🇷 Korean</option>
                  <option value="Punjabi">🇮🇳 Punjabi</option>
                </select>
              </div>
              <div>
                <label style={{ color: '#f59e0b', fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '8px' }}>Theme</label>
                <input type="text" value={lyricsTheme} onChange={(e) => setLyricsTheme(e.target.value)} placeholder="e.g., success, love..." style={{ width: '100%', padding: '12px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(245, 158, 11, 0.3)', borderRadius: '10px', color: '#fff' }} />
              </div>
            </div>
            
            {/* Generate Button */}
            <button
              onClick={generateLyrics}
              disabled={isGeneratingLyrics}
              style={{
                width: '100%',
                padding: '16px',
                background: isGeneratingLyrics ? 'rgba(245, 158, 11, 0.3)' : 'linear-gradient(135deg, #f59e0b, #f97316)',
                border: 'none',
                borderRadius: '12px',
                color: '#fff',
                fontSize: '1.1rem',
                fontWeight: 700,
                cursor: isGeneratingLyrics ? 'wait' : 'pointer',
                marginBottom: '20px'
              }}
            >
              {isGeneratingLyrics ? '⏳ Generating...' : '✨ GENERATE AI LYRICS'}
            </button>
            
            {/* Generated Lyrics */}
            {generatedLyrics && (
              <div style={{
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                borderRadius: '16px',
                padding: '24px'
              }}>
                <pre style={{
                  color: '#fff',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  fontSize: '1rem',
                  lineHeight: 1.8,
                  margin: 0
                }}>
                  {generatedLyrics}
                </pre>
                <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                  <button
                    onClick={() => { universalCopy(generatedLyrics, 'Copied!'); }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'rgba(245, 158, 11, 0.3)',
                      border: '1px solid #f59e0b',
                      borderRadius: '10px',
                      color: '#f59e0b',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    <Copy className="w-4 h-4" style={{ display: 'inline', marginRight: '8px' }} />
                    Copy
                  </button>
                  <button
                    onClick={() => { updateWorkflow({ lyrics: generatedLyrics }); toast.success('Added to workflow!'); }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    + Add to Workflow
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* DRUM MACHINE FULLSCREEN */}
      {fullscreenTool === 'drum' && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(135deg, #0a0f1a 0%, #1a1a2e 50%, #0d1526 100%)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid rgba(139, 92, 246, 0.3)',
            background: 'rgba(0, 0, 0, 0.3)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            flexWrap: 'wrap',
            gap: '8px'
          }}>
            <h1 style={{ color: '#8b5cf6', fontSize: 'clamp(0.9rem, 4vw, 1.3rem)', fontWeight: 700, margin: 0 }}>
              🥁 DRUM MACHINE
            </h1>
            <button
              onClick={() => setFullscreenTool(null)}
              style={{
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '0.85rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              ← Back
            </button>
          </div>
          
          <div style={{ padding: 'clamp(12px, 3vw, 24px)', flex: 1 }}>
            {/* Controls Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
              gap: 'clamp(8px, 2vw, 16px)', 
              marginBottom: '16px' 
            }}>
              <div>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>GENRE</label>
                <select
                  value={drumGenre}
                  onChange={(e) => setDrumGenre(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem'
                  }}
                >
                  <optgroup label="🔥 Trap">
                    <option value="trap">Trap</option>
                    <option value="rnb trap">R&B Trap</option>
                    <option value="melodic trap">Melodic Trap</option>
                    <option value="hard trap">Hard Trap</option>
                    <option value="dark trap">Dark Trap</option>
                    <option value="sad trap">Sad Trap</option>
                    <option value="emotional trap">Emotional Trap</option>
                    <option value="hybrid trap">Hybrid Trap</option>
                    <option value="festival trap">Festival Trap</option>
                    <option value="trap soul">Trap Soul</option>
                    <option value="cloud trap">Cloud Trap</option>
                    <option value="space trap">Space Trap</option>
                  </optgroup>
                  <optgroup label="🎤 Hip-Hop">
                    <option value="boom bap">Boom Bap (90s)</option>
                    <option value="east coast">East Coast</option>
                    <option value="west coast">West Coast (G-Funk)</option>
                    <option value="dirty south">Dirty South</option>
                    <option value="southern hip hop">Southern Hip Hop</option>
                    <option value="memphis rap">Memphis Rap</option>
                    <option value="chopped and screwed">Chopped & Screwed</option>
                    <option value="crunk">Crunk</option>
                    <option value="snap music">Snap Music</option>
                    <option value="hyphy">Hyphy (Bay Area)</option>
                    <option value="conscious hip hop">Conscious Hip Hop</option>
                    <option value="abstract hip hop">Abstract Hip Hop</option>
                    <option value="jazz rap">Jazz Rap</option>
                  </optgroup>
                  <optgroup label="🔫 Drill">
                    <option value="drill">Drill</option>
                    <option value="uk drill">UK Drill</option>
                    <option value="ny drill">NY Drill</option>
                    <option value="brooklyn drill">Brooklyn Drill</option>
                    <option value="chicago drill">Chicago Drill</option>
                    <option value="detroit drill">Detroit Drill</option>
                    <option value="melodic drill">Melodic Drill</option>
                    <option value="afro drill">Afro Drill</option>
                  </optgroup>
                  <optgroup label="💜 R&B / Soul">
                    <option value="rnb">R&B</option>
                    <option value="modern rnb">Modern R&B</option>
                    <option value="90s rnb">90s R&B</option>
                    <option value="neo soul">Neo Soul</option>
                    <option value="alternative rnb">Alternative R&B</option>
                    <option value="quiet storm">Quiet Storm</option>
                    <option value="new jack swing">New Jack Swing</option>
                    <option value="funk rnb">Funk R&B</option>
                    <option value="soul">Classic Soul</option>
                    <option value="motown">Motown</option>
                  </optgroup>
                  <optgroup label="🏠 House">
                    <option value="house">House</option>
                    <option value="deep house">Deep House</option>
                    <option value="tech house">Tech House</option>
                    <option value="progressive house">Progressive House</option>
                    <option value="future house">Future House</option>
                    <option value="bass house">Bass House</option>
                    <option value="electro house">Electro House</option>
                    <option value="tropical house">Tropical House</option>
                    <option value="afro house">Afro House</option>
                    <option value="chicago house">Chicago House</option>
                    <option value="acid house">Acid House</option>
                    <option value="minimal house">Minimal House</option>
                    <option value="soulful house">Soulful House</option>
                  </optgroup>
                  <optgroup label="⚡ Techno / Electronic">
                    <option value="techno">Techno</option>
                    <option value="detroit techno">Detroit Techno</option>
                    <option value="industrial techno">Industrial Techno</option>
                    <option value="hard techno">Hard Techno</option>
                    <option value="minimal techno">Minimal Techno</option>
                    <option value="acid techno">Acid Techno</option>
                    <option value="melodic techno">Melodic Techno</option>
                    <option value="peak time techno">Peak Time Techno</option>
                    <option value="trance">Trance</option>
                    <option value="psytrance">Psytrance</option>
                    <option value="progressive trance">Progressive Trance</option>
                  </optgroup>
                  <optgroup label="🌍 Afro / World">
                    <option value="afrobeats">Afrobeats</option>
                    <option value="afroswing">Afroswing</option>
                    <option value="amapiano">Amapiano</option>
                    <option value="gqom">Gqom</option>
                    <option value="kwaito">Kwaito</option>
                    <option value="reggaeton">Reggaeton</option>
                    <option value="latin trap">Latin Trap</option>
                    <option value="dembow">Dembow</option>
                    <option value="dancehall">Dancehall</option>
                    <option value="reggae">Reggae</option>
                    <option value="dub">Dub</option>
                    <option value="soca">Soca</option>
                    <option value="cumbia">Cumbia</option>
                    <option value="bachata">Bachata</option>
                    <option value="salsa">Salsa</option>
                    <option value="bossa nova">Bossa Nova</option>
                    <option value="baile funk">Baile Funk</option>
                    <option value="k-pop">K-Pop</option>
                    <option value="j-pop">J-Pop</option>
                    <option value="bollywood">Bollywood</option>
                  </optgroup>
                  <optgroup label="🎵 UK Sounds">
                    <option value="uk garage">UK Garage</option>
                    <option value="2-step">2-Step</option>
                    <option value="grime">Grime</option>
                    <option value="dubstep">Dubstep</option>
                    <option value="uk bass">UK Bass</option>
                    <option value="jungle">Jungle</option>
                    <option value="breakbeat">Breakbeat</option>
                  </optgroup>
                  <optgroup label="🥁 Drum & Bass">
                    <option value="dnb">Drum & Bass</option>
                    <option value="liquid dnb">Liquid DnB</option>
                    <option value="neuro dnb">Neuro DnB</option>
                    <option value="jump up dnb">Jump Up DnB</option>
                    <option value="halftime dnb">Halftime DnB</option>
                    <option value="rollers dnb">Rollers</option>
                  </optgroup>
                  <optgroup label="🎭 Club / Dance">
                    <option value="jersey club">Jersey Club</option>
                    <option value="baltimore club">Baltimore Club</option>
                    <option value="footwork">Footwork/Juke</option>
                    <option value="ghetto house">Ghetto House</option>
                    <option value="bounce">New Orleans Bounce</option>
                    <option value="vogue">Vogue/Ballroom</option>
                  </optgroup>
                  <optgroup label="🎸 Rock / Metal">
                    <option value="rock">Rock</option>
                    <option value="indie rock">Indie Rock</option>
                    <option value="punk rock">Punk Rock</option>
                    <option value="metal">Metal</option>
                    <option value="hard rock">Hard Rock</option>
                    <option value="alternative">Alternative</option>
                    <option value="grunge">Grunge</option>
                    <option value="post punk">Post-Punk</option>
                  </optgroup>
                  <optgroup label="📼 Phonk / Drift">
                    <option value="phonk">Phonk</option>
                    <option value="drift phonk">Drift Phonk</option>
                    <option value="house phonk">House Phonk</option>
                    <option value="brazilian phonk">Brazilian Phonk</option>
                    <option value="memphis phonk">Memphis Phonk</option>
                    <option value="cowbell phonk">Cowbell Phonk</option>
                  </optgroup>
                  <optgroup label="🔊 Bass Music">
                    <option value="bass">Bass Music</option>
                    <option value="future bass">Future Bass</option>
                    <option value="wave">Wave</option>
                    <option value="riddim">Riddim</option>
                    <option value="brostep">Brostep</option>
                    <option value="color bass">Color Bass</option>
                    <option value="tearout">Tearout</option>
                  </optgroup>
                  <optgroup label="🌙 Chill / Ambient">
                    <option value="lo-fi">Lo-Fi</option>
                    <option value="lo-fi hip hop">Lo-Fi Hip Hop</option>
                    <option value="chillhop">Chillhop</option>
                    <option value="chillwave">Chillwave</option>
                    <option value="ambient">Ambient</option>
                    <option value="downtempo">Downtempo</option>
                    <option value="trip hop">Trip Hop</option>
                  </optgroup>
                  <optgroup label="🎹 Other Genres">
                    <option value="plugg">Plugg</option>
                    <option value="rage">Rage</option>
                    <option value="hyperpop">Hyperpop</option>
                    <option value="funk">Funk</option>
                    <option value="disco">Disco</option>
                    <option value="nu disco">Nu Disco</option>
                    <option value="gospel">Gospel</option>
                    <option value="pop">Pop</option>
                    <option value="synthpop">Synthpop</option>
                    <option value="edm">EDM</option>
                    <option value="future">Future</option>
                    <option value="synthwave">Synthwave</option>
                    <option value="retrowave">Retrowave</option>
                    <option value="vaporwave">Vaporwave</option>
                    <option value="experimental">Experimental</option>
                    <option value="glitch">Glitch</option>
                    <option value="idm">IDM</option>
                    <option value="jazz">Jazz</option>
                    <option value="country">Country</option>
                    <option value="blues">Blues</option>
                    <option value="cinematic">Cinematic</option>
                    <option value="orchestral">Orchestral</option>
                  </optgroup>
                </select>
              </div>
              
              <div>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>TEMPO (BPM)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    onClick={() => setDrumTempo(prev => Math.max(60, prev - 5))}
                    style={{
                      width: '36px',
                      height: '42px',
                      background: 'rgba(139, 92, 246, 0.2)',
                      border: '1px solid rgba(139, 92, 246, 0.4)',
                      borderRadius: '8px',
                      color: '#a855f7',
                      fontSize: '1.3rem',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >−</button>
                  <input
                    type="number"
                    value={drumTempo}
                    onChange={(e) => setDrumTempo(Math.max(60, Math.min(200, parseInt(e.target.value) || 120)))}
                    min="60"
                    max="200"
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.9rem',
                      textAlign: 'center'
                    }}
                  />
                  <button
                    onClick={() => setDrumTempo(prev => Math.min(200, prev + 5))}
                    style={{
                      width: '36px',
                      height: '42px',
                      background: 'rgba(139, 92, 246, 0.2)',
                      border: '1px solid rgba(139, 92, 246, 0.4)',
                      borderRadius: '8px',
                      color: '#a855f7',
                      fontSize: '1.3rem',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >+</button>
                </div>
              </div>
              
              <div>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>DRUM KIT</label>
                <select
                  value={drumKit}
                  onChange={(e) => setDrumKit(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem'
                  }}
                >
                  <optgroup label="🎛️ Roland Classics">
                    <option value="808">TR-808</option>
                    <option value="909">TR-909</option>
                    <option value="707">TR-707</option>
                    <option value="606">TR-606</option>
                    <option value="727">TR-727 (Latin)</option>
                    <option value="505">TR-505</option>
                    <option value="626">TR-626</option>
                    <option value="r8">R-8</option>
                  </optgroup>
                  <optgroup label="🎚️ Other Drum Machines">
                    <option value="linn drum">LinnDrum</option>
                    <option value="lm-1">Linn LM-1</option>
                    <option value="dmx">Oberheim DMX</option>
                    <option value="cr-78">CR-78</option>
                    <option value="alesis sr-16">Alesis SR-16</option>
                    <option value="korg kr-55">Korg KR-55</option>
                    <option value="korg minipops">Korg Minipops</option>
                    <option value="boss dr-110">Boss DR-110</option>
                    <option value="simmons">Simmons SDS-V</option>
                    <option value="emu drumulator">E-mu Drumulator</option>
                  </optgroup>
                  <optgroup label="💻 Samplers">
                    <option value="sp1200">E-mu SP-1200</option>
                    <option value="mpc60">Akai MPC60</option>
                    <option value="mpc2000">MPC2000</option>
                    <option value="mpc3000">MPC3000</option>
                    <option value="mpc live">MPC Live</option>
                    <option value="maschine">Native Instruments Maschine</option>
                    <option value="emu sp-12">E-mu SP-12</option>
                    <option value="s950">Akai S950</option>
                    <option value="s1000">Akai S1000</option>
                    <option value="electribe">Korg Electribe</option>
                  </optgroup>
                  <optgroup label="🥁 Acoustic Kits">
                    <option value="acoustic">Standard Acoustic</option>
                    <option value="acoustic jazz">Jazz Kit</option>
                    <option value="acoustic rock">Rock Kit</option>
                    <option value="acoustic metal">Metal Kit</option>
                    <option value="acoustic funk">Funk Kit</option>
                    <option value="acoustic soul">Soul Kit</option>
                    <option value="acoustic brush">Brush Kit</option>
                    <option value="acoustic vintage">Vintage 60s Kit</option>
                    <option value="acoustic 70s">70s Kit</option>
                    <option value="acoustic 80s">80s Gated Kit</option>
                    <option value="marching">Marching Drums</option>
                    <option value="orchestral drums">Orchestral Drums</option>
                  </optgroup>
                  <optgroup label="🔥 Trap / Hip-Hop Kits">
                    <option value="trap kit">Trap Kit</option>
                    <option value="hard trap kit">Hard Trap Kit</option>
                    <option value="melodic trap kit">Melodic Trap Kit</option>
                    <option value="dark trap kit">Dark Trap Kit</option>
                    <option value="south side">South Side Kit</option>
                    <option value="atlanta">Atlanta Kit</option>
                    <option value="zaytoven">Zaytoven Style</option>
                    <option value="metro">Metro Boomin Style</option>
                    <option value="pierre bourne">Pi'erre Bourne Style</option>
                  </optgroup>
                  <optgroup label="🔫 Drill Kits">
                    <option value="drill kit">Drill Kit</option>
                    <option value="uk drill kit">UK Drill Kit</option>
                    <option value="ny drill kit">NY Drill Kit</option>
                    <option value="chicago drill kit">Chicago Drill Kit</option>
                    <option value="dark drill kit">Dark Drill Kit</option>
                  </optgroup>
                  <optgroup label="📼 Phonk Kits">
                    <option value="phonk kit">Phonk Kit</option>
                    <option value="drift phonk kit">Drift Phonk Kit</option>
                    <option value="memphis kit">Memphis Kit</option>
                    <option value="cowbell kit">Cowbell Heavy Kit</option>
                    <option value="brazilian phonk kit">Brazilian Phonk Kit</option>
                  </optgroup>
                  <optgroup label="🎭 Club Kits">
                    <option value="jersey kit">Jersey Club Kit</option>
                    <option value="baltimore kit">Baltimore Kit</option>
                    <option value="footwork kit">Footwork Kit</option>
                    <option value="bounce kit">Bounce Kit</option>
                    <option value="vogue kit">Vogue Kit</option>
                  </optgroup>
                  <optgroup label="🏠 House / Techno Kits">
                    <option value="house kit">House Kit</option>
                    <option value="deep house kit">Deep House Kit</option>
                    <option value="tech house kit">Tech House Kit</option>
                    <option value="techno kit">Techno Kit</option>
                    <option value="minimal kit">Minimal Kit</option>
                    <option value="acid kit">Acid Kit</option>
                    <option value="garage kit">Garage Kit</option>
                  </optgroup>
                  <optgroup label="🌍 World Kits">
                    <option value="afro kit">Afro Kit</option>
                    <option value="amapiano kit">Amapiano Kit</option>
                    <option value="latin kit">Latin Kit</option>
                    <option value="reggaeton kit">Reggaeton Kit</option>
                    <option value="dancehall kit">Dancehall Kit</option>
                    <option value="reggae kit">Reggae Kit</option>
                    <option value="bossa kit">Bossa Nova Kit</option>
                    <option value="samba kit">Samba Kit</option>
                    <option value="indian kit">Indian Kit</option>
                    <option value="arabic kit">Arabic Kit</option>
                    <option value="asian kit">Asian Kit</option>
                  </optgroup>
                  <optgroup label="🔊 Electronic Kits">
                    <option value="electronic">Electronic</option>
                    <option value="edm kit">EDM Kit</option>
                    <option value="future bass kit">Future Bass Kit</option>
                    <option value="dubstep kit">Dubstep Kit</option>
                    <option value="riddim kit">Riddim Kit</option>
                    <option value="dnb kit">DnB Kit</option>
                    <option value="jungle kit">Jungle Kit</option>
                    <option value="breakbeat kit">Breakbeat Kit</option>
                    <option value="glitch kit">Glitch Kit</option>
                    <option value="idm kit">IDM Kit</option>
                  </optgroup>
                  <optgroup label="🌙 Lo-Fi / Chill Kits">
                    <option value="lo-fi kit">Lo-Fi Kit</option>
                    <option value="vinyl kit">Vinyl Breaks Kit</option>
                    <option value="dusty kit">Dusty Samples Kit</option>
                    <option value="tape kit">Tape Saturated Kit</option>
                    <option value="chillhop kit">Chillhop Kit</option>
                  </optgroup>
                  <optgroup label="🎸 Rock / Metal Kits">
                    <option value="rock kit">Rock Kit</option>
                    <option value="punk kit">Punk Kit</option>
                    <option value="metal kit">Metal Kit</option>
                    <option value="indie kit">Indie Kit</option>
                    <option value="grunge kit">Grunge Kit</option>
                  </optgroup>
                  <optgroup label="🎹 Synth / Retro Kits">
                    <option value="synthwave kit">Synthwave Kit</option>
                    <option value="retrowave kit">Retrowave Kit</option>
                    <option value="80s kit">80s Kit</option>
                    <option value="vaporwave kit">Vaporwave Kit</option>
                    <option value="new wave kit">New Wave Kit</option>
                  </optgroup>
                  <optgroup label="🔧 Special Kits">
                    <option value="industrial kit">Industrial Kit</option>
                    <option value="noise kit">Noise Kit</option>
                    <option value="cinematic kit">Cinematic Kit</option>
                    <option value="foley kit">Foley Kit</option>
                    <option value="found sound kit">Found Sound Kit</option>
                    <option value="hybrid kit">Hybrid Kit</option>
                    <option value="layered kit">Layered Kit</option>
                  </optgroup>
                </select>
              </div>
              
              <div>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>BARS</label>
                <select
                  value={drumBars}
                  onChange={(e) => setDrumBars(parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem'
                  }}
                >
                  <option value={4}>4 Bars</option>
                  <option value={8}>8 Bars</option>
                  <option value={16}>16 Bars</option>
                </select>
              </div>
              
              <div>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>SWING</label>
                <select
                  value={drumSwing}
                  onChange={(e) => setDrumSwing(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem'
                  }}
                >
                  <optgroup label="🎯 Basic Timing">
                    <option value="none">None (Straight)</option>
                    <option value="light">Light Swing (5%)</option>
                    <option value="medium-light">Medium-Light (10%)</option>
                    <option value="medium">Medium Swing (15%)</option>
                    <option value="medium-heavy">Medium-Heavy (20%)</option>
                    <option value="heavy">Heavy Swing (25%)</option>
                    <option value="extreme">Extreme Swing (30%)</option>
                  </optgroup>
                  <optgroup label="🎵 Classic Feels">
                    <option value="shuffle">Shuffle</option>
                    <option value="half-shuffle">Half Shuffle</option>
                    <option value="triplet">Triplet Feel</option>
                    <option value="dotted">Dotted Feel</option>
                    <option value="bounce">Bounce</option>
                    <option value="pocket">Deep Pocket</option>
                    <option value="tight">Tight/Quantized</option>
                  </optgroup>
                  <optgroup label="🎹 Sampler Grooves">
                    <option value="mpc60-54">MPC60 54%</option>
                    <option value="mpc60-58">MPC60 58%</option>
                    <option value="mpc60-62">MPC60 62%</option>
                    <option value="mpc60-66">MPC60 66%</option>
                    <option value="mpc60-70">MPC60 70%</option>
                    <option value="mpc3000">MPC3000</option>
                    <option value="sp1200">SP-1200 Swing</option>
                    <option value="maschine">Maschine Swing</option>
                  </optgroup>
                  <optgroup label="🎤 Producer Styles">
                    <option value="j dilla">J Dilla Style</option>
                    <option value="madlib">Madlib Style</option>
                    <option value="pete rock">Pete Rock Style</option>
                    <option value="dj premier">DJ Premier Style</option>
                    <option value="9th wonder">9th Wonder Style</option>
                    <option value="kanye">Kanye Style</option>
                    <option value="timbaland">Timbaland Style</option>
                    <option value="neptunes">Neptunes Style</option>
                    <option value="metro">Metro Boomin Style</option>
                  </optgroup>
                  <optgroup label="⏱️ Timing Offsets">
                    <option value="lazy">Lazy/Behind Beat</option>
                    <option value="slightly-late">Slightly Late</option>
                    <option value="on-grid">On Grid</option>
                    <option value="slightly-early">Slightly Early</option>
                    <option value="pushed">Pushed/Ahead</option>
                    <option value="drunk">Drunk/Loose</option>
                    <option value="humanize-light">Humanize Light</option>
                    <option value="humanize-heavy">Humanize Heavy</option>
                  </optgroup>
                  <optgroup label="🎸 Genre Grooves">
                    <option value="funk">Funk Pocket</option>
                    <option value="soul">Soul Groove</option>
                    <option value="motown">Motown Swing</option>
                    <option value="disco">Disco Swing</option>
                    <option value="jazz">Jazz Swing</option>
                    <option value="blues">Blues Shuffle</option>
                    <option value="rock">Rock Swing</option>
                    <option value="country">Country Shuffle</option>
                  </optgroup>
                  <optgroup label="🌍 World Grooves">
                    <option value="reggae">Reggae/One Drop</option>
                    <option value="dub">Dub Groove</option>
                    <option value="latin">Latin Clave</option>
                    <option value="bossa">Bossa Nova</option>
                    <option value="samba">Samba</option>
                    <option value="cumbia">Cumbia</option>
                    <option value="afro">Afro Poly</option>
                    <option value="afrobeat">Afrobeat</option>
                    <option value="highlife">Highlife</option>
                    <option value="gospel">Gospel Shuffle</option>
                    <option value="new orleans">New Orleans</option>
                  </optgroup>
                  <optgroup label="🏠 Electronic Grooves">
                    <option value="house">House Groove</option>
                    <option value="garage">Garage Swing</option>
                    <option value="breakbeat">Breakbeat</option>
                    <option value="jungle">Jungle Swing</option>
                    <option value="dnb">DnB Groove</option>
                    <option value="techno">Techno Grid</option>
                  </optgroup>
                  <optgroup label="💜 R&B Feels">
                    <option value="neo soul">Neo-Soul</option>
                    <option value="slow jam">Slow Jam</option>
                    <option value="quiet storm">Quiet Storm</option>
                    <option value="new jack">New Jack Swing</option>
                    <option value="90s rnb">90s R&B</option>
                    <option value="modern rnb">Modern R&B</option>
                  </optgroup>
                </select>
              </div>
              
              <div>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>PATTERN TYPE</label>
                <select
                  value={drumPatternType}
                  onChange={(e) => setDrumPatternType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem'
                  }}
                >
                  <optgroup label="🎼 Main Patterns">
                    <option value="full">Full Groove</option>
                    <option value="minimal">Minimal</option>
                    <option value="complex">Complex</option>
                    <option value="syncopated">Syncopated</option>
                  </optgroup>
                  <optgroup label="📍 Song Sections">
                    <option value="intro">Intro</option>
                    <option value="verse">Verse</option>
                    <option value="chorus">Chorus</option>
                    <option value="bridge">Bridge</option>
                    <option value="outro">Outro</option>
                  </optgroup>
                  <optgroup label="⚡ Energy Patterns">
                    <option value="buildup">Buildup</option>
                    <option value="drop">Drop</option>
                    <option value="breakdown">Breakdown</option>
                    <option value="fill">Fill/Transition</option>
                  </optgroup>
                  <optgroup label="🎚️ Time Feels">
                    <option value="half-time">Half-Time</option>
                    <option value="double-time">Double-Time</option>
                    <option value="four-on-floor">Four on Floor</option>
                    <option value="boom-bap">Boom-Bap</option>
                    <option value="trap-roll">Trap Roll</option>
                  </optgroup>
                  <optgroup label="🌊 Vibes">
                    <option value="ambient">Ambient</option>
                    <option value="transition">Transition</option>
                  </optgroup>
                </select>
              </div>
            </div>
            
            {/* Include Variations Checkbox */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={drumIncludeVariations}
                  onChange={(e) => setDrumIncludeVariations(e.target.checked)}
                  style={{ width: '18px', height: '18px', accentColor: '#8b5cf6' }}
                />
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                  Include variations (Intro, Main, Build, Drop patterns)
                </span>
              </label>
            </div>
            
            {/* Generate Buttons */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
              <button
                onClick={generateDrumPattern}
                disabled={isGeneratingDrum}
                style={{
                  flex: '1 1 250px',
                  maxWidth: '400px',
                  padding: '16px 24px',
                  background: isGeneratingDrum 
                    ? 'rgba(139, 92, 246, 0.3)' 
                    : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: isGeneratingDrum ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                {isGeneratingDrum ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating Pattern...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    Generate Pattern
                  </>
                )}
              </button>
              
              <button
                onClick={generateDrumAudio}
                disabled={isGeneratingDrumAudio}
                style={{
                  flex: '1 1 200px',
                  maxWidth: '300px',
                  padding: '16px 24px',
                  background: isGeneratingDrumAudio 
                    ? 'rgba(16, 185, 129, 0.3)' 
                    : 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: isGeneratingDrumAudio ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px'
                }}
              >
                {isGeneratingDrumAudio ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    AI Generating... (30-90s)
                  </>
                ) : (
                  <>
                    <Volume2 className="w-5 h-5" />
                    🎧 Preview
                  </>
                )}
              </button>
            </div>
            
            {/* Audio Player in Fullscreen */}
            {drumAudioUrl && (
              <div style={{
                marginBottom: '24px',
                padding: '16px 20px',
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <button
                  onClick={toggleDrumAudio}
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  {isDrumPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </button>
                <button
                  onClick={async () => {
                    if (!drumAudioUrl) return;
                    try {
                      const response = await fetch(drumAudioUrl);
                      const blob = await response.blob();
                      const filename = `SoundDNA_${drumGenre}_${drumTempo}BPM.wav`;
                      await downloadFileWithPicker(blob, filename, 'audio/wav', `📥 WAV saved!`, drumAudioUrl);
                    } catch (err) {
                      console.error('Drum download error:', err);
                      toast.error('Download failed');
                    }
                  }}
                  data-testid="drum-fullscreen-download-wav-btn"
                  title="Download WAV"
                  style={{
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                    border: 'none',
                    color: '#fff',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <Download className="w-5 h-5" />
                </button>
                <div style={{ flex: 1 }}>
                  <p style={{ color: '#10b981', fontWeight: 700, fontSize: '1rem', margin: 0 }}>
                    🎧 {drumGenre.toUpperCase()} @ {drumTempo} BPM
                  </p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '4px 0 0' }}>
                    {drumKit.toUpperCase()} kit • {drumSwing === 'none' ? 'Straight' : drumSwing + ' swing'} • Matches Suno Style
                  </p>
                </div>
                <audio 
                  ref={drumAudioRef} 
                  src={drumAudioUrl} 
                  preload="metadata"
                  onEnded={handleDrumAudioEnded}
                  style={{ display: 'none' }}
                />
              </div>
            )}
            
            {/* Generated Pattern Display */}
            {generatedDrumPattern && (
              <div style={{
                padding: '24px',
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '16px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '16px',
                  flexWrap: 'wrap',
                  gap: '12px'
                }}>
                  <div>
                    <h3 style={{ color: '#8b5cf6', fontWeight: 700, fontSize: '1.1rem', margin: 0 }}>
                      🥁 {generatedDrumPattern.genre.toUpperCase()} PATTERN
                    </h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '4px 0 0' }}>
                      {generatedDrumPattern.tempo} BPM • {generatedDrumPattern.kit} kit • {generatedDrumPattern.bars} bars • {generatedDrumPattern.swing} swing
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      onClick={copySunoPromptFromDrum}
                      style={{
                        padding: '10px 16px',
                        background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      title="Copy Suno-ready prompt"
                    >
                      <Sparkles className="w-4 h-4" /> Copy for Suno
                    </button>
                    <button
                      onClick={addDrumToWorkflow}
                      style={{
                        padding: '10px 16px',
                        background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      title="Add to Unified Workflow"
                    >
                      <Plus className="w-4 h-4" /> Add to Workflow
                    </button>
                    <button
                      onClick={copyDrumPattern}
                      style={{
                        padding: '10px 16px',
                        background: 'rgba(139, 92, 246, 0.2)',
                        border: '1px solid rgba(139, 92, 246, 0.4)',
                        borderRadius: '8px',
                        color: '#8b5cf6',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Copy className="w-4 h-4" /> Copy All
                    </button>
                  </div>
                </div>
                <pre style={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: '0.85rem',
                  lineHeight: 1.7,
                  whiteSpace: 'pre-wrap',
                  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                  background: 'rgba(0,0,0,0.3)',
                  padding: '20px',
                  borderRadius: '12px',
                  maxHeight: '500px',
                  overflowY: 'auto'
                }}>
                  {generatedDrumPattern.pattern}
                </pre>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* SONG CREATOR FULLSCREEN - Combined Rhyme & Drum */}
      {fullscreenTool === 'song-creator' && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(135deg, #0a0f1a 0%, #1a1a2e 50%, #0d1526 100%)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid rgba(0, 212, 255, 0.3)',
            background: 'rgba(0, 0, 0, 0.3)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h1 style={{ 
              background: 'linear-gradient(135deg, #f97316, #8b5cf6, #00d4ff)', 
              WebkitBackgroundClip: 'text', 
              WebkitTextFillColor: 'transparent',
              fontSize: '1.3rem', 
              fontWeight: 700, 
              margin: 0 
            }}>
              🎵 SONG CREATOR - Lyrics + Drums Combined
            </h1>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={copyFullSongPackage}
                data-testid="song-creator-export-suno-btn"
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                <Sparkles className="w-4 h-4" /> Export for Suno
              </button>
              <button
                onClick={() => setFullscreenTool(null)}
                data-testid="song-creator-back-btn"
                style={{
                  padding: '10px 20px',
                  background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                ← Back to App
              </button>
            </div>
          </div>
          
          <div style={{ padding: '24px', flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: '24px' }}>
            {/* LEFT PANEL - Rhyme & Flow Engine (FULL FEATURES) */}
            <div style={{
              background: 'rgba(249, 115, 22, 0.08)',
              border: '1px solid rgba(249, 115, 22, 0.3)',
              borderRadius: '16px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <h2 style={{ color: '#f97316', fontWeight: 700, fontSize: '1.1rem', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Mic className="w-5 h-5" /> RHYME & FLOW ENGINE
              </h2>
              
              {/* Tabs: AI Generate / Your Own Lyrics */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
                <button
                  onClick={() => setRhymeEngineTab && setRhymeEngineTab("generate")}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: (!rhymeEngineTab || rhymeEngineTab === 'generate') ? 'linear-gradient(135deg, #f97316, #ea580c)' : 'rgba(249, 115, 22, 0.15)',
                    border: (!rhymeEngineTab || rhymeEngineTab === 'generate') ? 'none' : '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Zap className="w-3 h-3" /> AI Generated
                </button>
                <button
                  onClick={() => setRhymeEngineTab && setRhymeEngineTab("custom")}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: rhymeEngineTab === 'custom' ? 'linear-gradient(135deg, #f97316, #ea580c)' : 'rgba(249, 115, 22, 0.15)',
                    border: rhymeEngineTab === 'custom' ? 'none' : '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Edit3 className="w-3 h-3" /> My Own Lyrics
                </button>
              </div>

              {/* Song Title */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '4px', display: 'block' }}>SONG TITLE</label>
                <input
                  type="text"
                  value={songTitle || ''}
                  onChange={(e) => setSongTitle && setSongTitle(e.target.value)}
                  placeholder="Enter song title..."
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.85rem'
                  }}
                />
              </div>

              {/* AI GENERATE TAB */}
              {(!rhymeEngineTab || rhymeEngineTab === 'generate') && (
                <>
                  {/* Topic Input */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '4px', display: 'block' }}>TOPIC / THEME</label>
                    <input
                      type="text"
                      value={rhymeTopic}
                      onChange={(e) => setRhymeTopic(e.target.value)}
                      placeholder="e.g., hustle, love, dreams, success..."
                      data-testid="song-creator-topic-input"
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: 'rgba(0,0,0,0.4)',
                        border: '1px solid rgba(249, 115, 22, 0.3)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.85rem'
                      }}
                    />
                  </div>
                  
                  {/* Style & Rhyme Scheme Row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>STYLE</label>
                      <select
                        value={rhymeStyle}
                        onChange={(e) => setRhymeStyle(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          background: 'rgba(0,0,0,0.4)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.75rem'
                        }}
                      >
                        <optgroup label="🔥 TRAP / HIP-HOP">
                          <option value="modern trap">Modern Trap (Atlanta)</option>
                          <option value="melodic rap">Melodic Rap</option>
                          <option value="aggressive trap">Aggressive</option>
                          <option value="drill">UK Drill</option>
                          <option value="boom bap">Boom Bap (90s NY)</option>
                          <option value="west coast">West Coast G-Funk</option>
                          <option value="conscious">Conscious/Lyrical</option>
                        </optgroup>
                        <optgroup label="💜 R&B / SOUL">
                          <option value="rnb">R&B / Soul</option>
                          <option value="southern soul">Southern Soul</option>
                          <option value="neo soul">Neo Soul</option>
                        </optgroup>
                        <optgroup label="😢 EMOTIONAL">
                          <option value="pain">Pain / Emotional</option>
                          <option value="heartbreak">Heartbreak</option>
                          <option value="motivational">Motivational</option>
                        </optgroup>
                        <optgroup label="🎵 OTHER">
                          <option value="party">Party/Club</option>
                          <option value="storytelling">Storytelling</option>
                          <option value="afrobeats">Afrobeats</option>
                          <option value="gospel">Gospel</option>
                          <option value="reggaeton">Reggaeton</option>
                          <option value="dancehall">Dancehall</option>
                          <option value="baile funk">Baile Funk</option>
                          <option value="uk garage">UK Garage</option>
                          <option value="phonk">Phonk</option>
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>RHYME SCHEME</label>
                      <select
                        value={rhymeScheme}
                        onChange={(e) => setRhymeScheme(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          background: 'rgba(0,0,0,0.4)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.75rem'
                        }}
                      >
                        <option value="AABB">AABB (Couplets)</option>
                        <option value="ABAB">ABAB (Alternating)</option>
                        <option value="ABBA">ABBA (Enclosed)</option>
                        <option value="AAAA">AAAA (Mono-rhyme)</option>
                        <option value="AAAB">AAAB (Triple + Twist)</option>
                        <option value="ABCB">ABCB (Ballad Stanza)</option>
                        <option value="ABCABC">ABCABC (Triple Rhyme)</option>
                        <option value="AABBA">AABBA (Limerick)</option>
                        <option value="AABCCB">AABCCB (Sestet)</option>
                        <option value="XAXA">XAXA (Alternate Blank)</option>
                        <option value="chain">Chain Rhyme (ABA BCB)</option>
                        <option value="internal">Internal Rhymes</option>
                        <option value="multisyllabic">Multi-Syllabic</option>
                        <option value="compound">Compound Rhymes</option>
                        <option value="slant">Slant / Near Rhymes</option>
                        <option value="assonance">Assonance (Vowel Rhymes)</option>
                        <option value="alliteration">Alliteration Driven</option>
                        <option value="internal-multi">Internal Multi-Syllabic</option>
                        <option value="cross">Cross Rhyme</option>
                        <option value="free">Free Form</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Flow Pattern & Language Row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>FLOW PATTERN</label>
                      <select
                        value={flowPattern}
                        onChange={(e) => setFlowPattern(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          background: 'rgba(0,0,0,0.4)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.75rem'
                        }}
                      >
                        <option value="standard">Standard (4/4)</option>
                        <option value="boom-bap-cadence">Boom Bap Cadence (90s Pocket)</option>
                        <option value="triplet">Triplet Flow (Migos)</option>
                        <option value="double-time">Double-Time (Fast)</option>
                        <option value="chopper">Chopper (Tech N9ne Speed)</option>
                        <option value="slow-flow">Slow Flow (Deliberate)</option>
                        <option value="lazy-drawl">Lazy Drawl (Behind the Beat)</option>
                        <option value="syncopated">Syncopated (Off-Beat)</option>
                        <option value="halftime">Half-Time (Trap Beat)</option>
                        <option value="drill-slide">Drill Slide (UK Cadence)</option>
                        <option value="grime">Grime (140 Skippy)</option>
                        <option value="melodic-flow">Melodic Flow</option>
                        <option value="sing-song">Sing-Song (Playground Cadence)</option>
                        <option value="stutter">Stutter Flow (Word Repeats)</option>
                        <option value="start-stop">Start-Stop (Dramatic Pauses)</option>
                        <option value="pocket">Pocket Flow (Locked to Drums)</option>
                        <option value="bounce">Bounce (New Orleans)</option>
                        <option value="afro-bounce">Afro Bounce</option>
                        <option value="reggaeton-flow">Reggaeton Dembow</option>
                        <option value="syncopated-offbeat">Syncopated Off-Beat</option>
                        <option value="waterfall">Waterfall</option>
                        <option value="spoken-word">Spoken Word / Poetry</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>LANGUAGE</label>
                      <select
                        value={rhymeLanguage || 'English'}
                        onChange={(e) => setRhymeLanguage && setRhymeLanguage(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          background: 'rgba(0,0,0,0.4)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.75rem'
                        }}
                      >
                        <option value="English">🇺🇸 English</option>
                        {OWN_LYRICS_LANGS.filter(([v]) => v !== 'English').map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                  </div>
                  
                  {/* Bars Count & Section Type Row */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                    <div>
                      <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>BARS: {barsCount}</label>
                      <select
                        value={barsCount}
                        onChange={(e) => {
                          setBarsCount(parseInt(e.target.value));
                          if (syncDrumToRhyme) setDrumBars(parseInt(e.target.value));
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          background: 'rgba(0,0,0,0.4)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.75rem'
                        }}
                      >
                        <option value="8">8 Bars</option>
                        <option value="12">12 Bars</option>
                        <option value="16">16 Bars</option>
                        <option value="24">24 Bars</option>
                        <option value="32">32 Bars</option>
                        <option value="48">48 Bars</option>
                        <option value="64">64 Bars</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>SECTION</label>
                      <select
                        value={sectionType}
                        onChange={(e) => setSectionType(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          background: 'rgba(0,0,0,0.4)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.75rem'
                        }}
                      >
                        <option value="intro">Intro</option>
                        <option value="verse">Verse</option>
                        <option value="pre-chorus">Pre-Chorus</option>
                        <option value="chorus">Chorus/Hook</option>
                        <option value="bridge">Bridge</option>
                        <option value="outro">Outro</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Duration Row */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>DURATION (2-5 MIN): {rhymeDuration} min</label>
                    <input
                      type="number"
                      min={2}
                      max={5}
                      step={0.5}
                      value={rhymeDuration}
                      onChange={(e) => setRhymeDuration(e.target.value === '' ? '' : parseFloat(e.target.value))}
                      onBlur={() => setRhymeDuration(Math.min(5, Math.max(2, parseFloat(rhymeDuration) || 3)))}
                      data-testid="rhyme-duration-nested"
                      style={{
                        width: '100%',
                        padding: '8px',
                        background: 'rgba(0,0,0,0.4)',
                        border: '1px solid rgba(249, 115, 22, 0.3)',
                        borderRadius: '6px',
                        color: '#fff',
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                  
                  {/* Generate Lyrics Button */}
                  <button
                    onClick={generateBars}
                    disabled={isGeneratingBars || !rhymeTopic.trim()}
                    data-testid="song-creator-generate-lyrics-btn"
                    style={{
                      padding: '12px',
                      background: isGeneratingBars ? 'rgba(249, 115, 22, 0.3)' : 'linear-gradient(135deg, #f97316, #f59e0b)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      cursor: isGeneratingBars ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      marginBottom: '12px'
                    }}
                  >
                    {isGeneratingBars ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Generating...</>
                    ) : (
                      <><Zap className="w-4 h-4" /> Generate {barsCount} Bars</>
                    )}
                  </button>
                </>
              )}

              {/* CUSTOM LYRICS TAB */}
              {rhymeEngineTab === 'custom' && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '4px', display: 'block' }}>YOUR LYRICS</label>
                  <textarea
                    value={customLyrics || ''}
                    onChange={(e) => setCustomLyrics && setCustomLyrics(e.target.value)}
                    placeholder="Write or paste your own lyrics here..."
                    style={{
                      width: '100%',
                      minHeight: '150px',
                      padding: '12px',
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.85rem',
                      lineHeight: 1.6,
                      resize: 'vertical'
                    }}
                    data-testid="my-own-lyrics-textarea"
                  />
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginTop: '10px' }} data-testid="own-lyrics-language-row-main">
                    <div style={{ flex: 1 }}>
                      <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '4px', display: 'block' }}>🌍 LANGUAGE</label>
                      <select value={rhymeLanguage} onChange={(e) => setRhymeLanguage(e.target.value)} style={{ width: '100%', padding: '10px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(249, 115, 22, 0.3)', borderRadius: '8px', color: '#fff', fontSize: '0.8rem' }} data-testid="own-lyrics-language-select-main">
                        {OWN_LYRICS_LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    <button onClick={translateCustomLyrics} disabled={lyricsAssistLoading} style={{ padding: '10px 14px', background: 'rgba(0, 212, 255, 0.12)', border: '1px solid rgba(0, 212, 255, 0.5)', borderRadius: '8px', color: '#00d4ff', fontSize: '0.8rem', fontWeight: 700, cursor: lyricsAssistLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap' }} data-testid="own-lyrics-translate-btn-main">
                      {lyricsAssistLoading ? '⏳' : '🌍 Translate'}
                    </button>
                  </div>
                  {/* AI Lyric Assistant Dropdown */}
                  <div style={{ display: 'flex', gap: '8px', marginTop: '10px', alignItems: 'center' }}>
                    <select
                      value=""
                      disabled={lyricsAssistLoading}
                      onChange={(e) => { if (e.target.value) runLyricsAssistant(e.target.value); }}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: 'rgba(139, 92, 246, 0.12)',
                        border: '1px solid rgba(139, 92, 246, 0.5)',
                        borderRadius: '8px',
                        color: '#8b5cf6',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        cursor: lyricsAssistLoading ? 'wait' : 'pointer'
                      }}
                      data-testid="lyric-assistant-dropdown"
                    >
                      <option value="">{lyricsAssistLoading ? '🪄 AI working on your lyrics...' : '🪄 AI Lyric Assistant...'}</option>
                      <option value="expand">➕ Expand — add the next 4 lines in your rhyme scheme</option>
                      <option value="polish">✨ Sonic Polish — better synonyms, flow & sound</option>
                      <option value="rewrite">🏆 Full Rewrite — professional finish of your ideas</option>
                    </select>
                  </div>
                  <button
                    onClick={() => {
                      if (customLyrics) {
                        setGeneratedBars({ bars: customLyrics, raw_lyrics: customLyrics, total_bars: customLyrics.split('\n').length });
                        toast.success('Lyrics synced to Song Creator!');
                      }
                    }}
                    data-testid="use-my-lyrics-btn"
                    style={{
                      width: '100%',
                      padding: '12px',
                      marginTop: '10px',
                      background: 'linear-gradient(135deg, #f97316, #f59e0b)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Use These Lyrics
                  </button>
                </div>
              )}
              
              {/* Generated Lyrics Display */}
              {generatedBars && (
                <div style={{
                  flex: 1,
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: '10px',
                  padding: '12px',
                  overflow: 'auto'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span style={{ color: '#f97316', fontWeight: 600, fontSize: '0.75rem' }}>
                      🎤 {generatedBars.total_bars || barsCount} Bars • {sectionType.toUpperCase()}
                    </span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => universalCopy(generatedBars.raw_lyrics || generatedBars.bars, "📝 Lyrics copied!")}
                        style={{
                          padding: '4px 8px',
                          background: 'rgba(249, 115, 22, 0.2)',
                          border: '1px solid rgba(249, 115, 22, 0.3)',
                          borderRadius: '6px',
                          color: '#f97316',
                          fontSize: '0.65rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        data-testid="song-creator-copy-lyrics-btn"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                      <button
                        onClick={() => { updateWorkflow({ lyrics: generatedBars.raw_lyrics || generatedBars.bars }); toast.success('Added to workflow!'); }}
                        style={{
                          padding: '4px 8px',
                          background: 'linear-gradient(135deg, #f97316, #ea580c)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.65rem',
                          cursor: 'pointer'
                        }}
                      >
                        + Workflow
                      </button>
                    </div>
                  </div>
                  <pre style={{
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '0.7rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    maxHeight: '200px',
                    overflowY: 'auto',
                    margin: 0
                  }}>
                    {generatedBars.raw_lyrics || generatedBars.bars}
                  </pre>
                  
                  {/* Rhyme Analysis */}
                  {generatedBars.rhyme_analysis && (
                    <div style={{ marginTop: '8px', padding: '6px', background: 'rgba(249, 115, 22, 0.1)', borderRadius: '6px' }}>
                      <span style={{ color: '#f97316', fontSize: '0.65rem', fontWeight: 600 }}>📊 FLOW ANALYSIS</span>
                      <div style={{ color: '#888', fontSize: '0.6rem', marginTop: '3px' }}>
                        Scheme: {generatedBars.rhyme_analysis.scheme || rhymeScheme} • 
                        Syllables: {generatedBars.rhyme_analysis.avg_syllables || '~'}/line •
                        Flow: {flowPattern}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* RIGHT PANEL - Drum Machine (Full Features) */}
            <div style={{
              background: 'rgba(139, 92, 246, 0.08)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '16px',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <h2 style={{ color: '#8b5cf6', fontWeight: 700, fontSize: '1.1rem', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Music className="w-5 h-5" /> DRUM MACHINE
              </h2>
              
              {/* Sync Toggle */}
              <div style={{ 
                marginBottom: '16px', 
                padding: '10px', 
                background: 'rgba(0,0,0,0.2)', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <input
                  type="checkbox"
                  checked={syncDrumToRhyme}
                  onChange={(e) => setSyncDrumToRhyme(e.target.checked)}
                  style={{ width: '18px', height: '18px', accentColor: '#8b5cf6' }}
                />
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.85rem' }}>
                  🔗 Sync bars with lyrics ({barsCount} bars)
                </span>
              </div>
              
              {/* Genre Selection - Full 147 genres */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>GENRE (147 options)</label>
                <select
                  value={drumGenre}
                  onChange={(e) => setDrumGenre(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.85rem'
                  }}
                >
                  <optgroup label="🔥 TRAP">
                    <option value="trap">Trap</option>
                    <option value="rnb trap">R&B Trap</option>
                    <option value="melodic trap">Melodic Trap</option>
                    <option value="hard trap">Hard Trap</option>
                    <option value="dark trap">Dark Trap</option>
                    <option value="sad trap">Sad Trap</option>
                    <option value="hybrid trap">Hybrid Trap</option>
                  </optgroup>
                  <optgroup label="🔫 DRILL">
                    <option value="drill">Drill</option>
                    <option value="uk drill">UK Drill</option>
                    <option value="ny drill">NY Drill</option>
                    <option value="chicago drill">Chicago Drill</option>
                    <option value="melodic drill">Melodic Drill</option>
                  </optgroup>
                  <optgroup label="🎤 HIP-HOP">
                    <option value="boom bap">Boom Bap</option>
                    <option value="east coast">East Coast</option>
                    <option value="west coast">West Coast</option>
                    <option value="dirty south">Dirty South</option>
                    <option value="crunk">Crunk</option>
                  </optgroup>
                  <optgroup label="💜 R&B / SOUL">
                    <option value="rnb">R&B</option>
                    <option value="modern rnb">Modern R&B</option>
                    <option value="neo soul">Neo Soul</option>
                    <option value="soul">Soul</option>
                  </optgroup>
                  <optgroup label="🏠 HOUSE">
                    <option value="house">House</option>
                    <option value="deep house">Deep House</option>
                    <option value="tech house">Tech House</option>
                    <option value="afro house">Afro House</option>
                  </optgroup>
                  <optgroup label="🤖 TECHNO">
                    <option value="techno">Techno</option>
                    <option value="melodic techno">Melodic Techno</option>
                    <option value="hard techno">Hard Techno</option>
                  </optgroup>
                  <optgroup label="⚡ EDM">
                    <option value="edm">EDM</option>
                    <option value="future bass">Future Bass</option>
                    <option value="dubstep">Dubstep</option>
                    <option value="dnb">Drum & Bass</option>
                  </optgroup>
                  <optgroup label="🌍 AFRICAN">
                    <option value="afrobeats">Afrobeats</option>
                    <option value="amapiano">Amapiano</option>
                    <option value="afroswing">Afroswing</option>
                  </optgroup>
                  <optgroup label="🌴 CARIBBEAN">
                    <option value="reggaeton">Reggaeton</option>
                    <option value="dancehall">Dancehall</option>
                    <option value="reggae">Reggae</option>
                  </optgroup>
                  <optgroup label="☕ LO-FI">
                    <option value="lo-fi">Lo-Fi</option>
                    <option value="chillhop">Chillhop</option>
                  </optgroup>
                  <optgroup label="👻 PHONK">
                    <option value="phonk">Phonk</option>
                    <option value="drift phonk">Drift Phonk</option>
                  </optgroup>
                  <optgroup label="🎸 ROCK">
                    <option value="rock">Rock</option>
                    <option value="indie">Indie</option>
                    <option value="pop">Pop</option>
                  </optgroup>
                </select>
              </div>
              
              {/* Tempo */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', marginBottom: '6px', display: 'block' }}>TEMPO: {drumTempo} BPM</label>
                <input
                  type="range"
                  min="60"
                  max="200"
                  value={drumTempo}
                  onChange={(e) => setDrumTempo(parseInt(e.target.value))}
                  style={{ width: '100%', accentColor: '#8b5cf6' }}
                />
              </div>
              
              {/* Kit & Swing Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '4px', display: 'block' }}>KIT (119)</label>
                  <select
                    value={drumKit}
                    onChange={(e) => setDrumKit(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.8rem'
                    }}
                  >
                    <optgroup label="🎛️ ROLAND">
                      <option value="808">TR-808</option>
                      <option value="909">TR-909</option>
                      <option value="707">TR-707</option>
                      <option value="606">TR-606</option>
                    </optgroup>
                    <optgroup label="📼 SAMPLERS">
                      <option value="sp1200">SP-1200</option>
                      <option value="mpc60">MPC60</option>
                      <option value="mpc3000">MPC3000</option>
                      <option value="maschine">Maschine</option>
                    </optgroup>
                    <optgroup label="🥁 ACOUSTIC">
                      <option value="acoustic">Acoustic</option>
                      <option value="acoustic jazz">Jazz Kit</option>
                      <option value="acoustic rock">Rock Kit</option>
                    </optgroup>
                    <optgroup label="🔥 TRAP/DRILL">
                      <option value="trap kit">Trap Kit</option>
                      <option value="drill kit">Drill Kit</option>
                      <option value="uk drill kit">UK Drill Kit</option>
                    </optgroup>
                    <optgroup label="🌍 WORLD">
                      <option value="afro kit">Afro Kit</option>
                      <option value="amapiano kit">Amapiano Kit</option>
                      <option value="latin kit">Latin Kit</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '4px', display: 'block' }}>SWING (70)</label>
                  <select
                    value={drumSwing}
                    onChange={(e) => setDrumSwing(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px',
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.8rem'
                    }}
                  >
                    <optgroup label="📊 BASIC">
                      <option value="none">None (Straight)</option>
                      <option value="light">Light</option>
                      <option value="medium">Medium</option>
                      <option value="heavy">Heavy</option>
                    </optgroup>
                    <optgroup label="📼 MPC">
                      <option value="mpc60-54">MPC60 54%</option>
                      <option value="mpc60-62">MPC60 62%</option>
                      <option value="mpc3000">MPC3000</option>
                    </optgroup>
                    <optgroup label="🎤 PRODUCER">
                      <option value="j dilla">J Dilla</option>
                      <option value="madlib">Madlib</option>
                      <option value="timbaland">Timbaland</option>
                      <option value="metro">Metro Boomin</option>
                    </optgroup>
                    <optgroup label="🎸 GROOVE">
                      <option value="funk">Funk Pocket</option>
                      <option value="neo soul">Neo Soul</option>
                      <option value="reggae">Reggae</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* Advanced Mode Toggle */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 12px',
                background: advancedDrumMode ? 'rgba(139, 92, 246, 0.2)' : 'rgba(0,0,0,0.2)',
                borderRadius: '8px',
                marginBottom: '12px',
                cursor: 'pointer'
              }}
              onClick={() => setAdvancedDrumMode(!advancedDrumMode)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🧬</span>
                  <span style={{ color: advancedDrumMode ? '#a78bfa' : '#888', fontSize: '0.8rem', fontWeight: 600 }}>
                    QUANTUM MODE
                  </span>
                </div>
                <div style={{
                  width: '36px', height: '20px',
                  background: advancedDrumMode ? '#8b5cf6' : '#333',
                  borderRadius: '10px', position: 'relative'
                }}>
                  <div style={{
                    width: '16px', height: '16px',
                    background: '#fff', borderRadius: '50%',
                    position: 'absolute', top: '2px',
                    left: advancedDrumMode ? '18px' : '2px',
                    transition: 'all 0.2s'
                  }} />
                </div>
              </div>

              {/* Advanced Settings (when enabled) */}
              {advancedDrumMode && (
                <div style={{ 
                  background: 'rgba(139, 92, 246, 0.1)', 
                  borderRadius: '8px', 
                  padding: '12px', 
                  marginBottom: '12px' 
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', marginBottom: '10px' }}>
                    <div>
                      <label style={{ color: '#888', fontSize: '0.65rem' }}>KEY</label>
                      <select value={drumKey} onChange={(e) => setDrumKey(e.target.value)}
                        style={{ width: '100%', padding: '6px', background: '#111', border: '1px solid #333', borderRadius: '4px', color: '#a78bfa', fontSize: '0.75rem' }}>
                        <optgroup label="Natural">
                          {['C', 'D', 'E', 'F', 'G', 'A', 'B'].map(k => <option key={k} value={k}>{k}</option>)}
                        </optgroup>
                        <optgroup label="Sharps">
                          {['C#', 'D#', 'F#', 'G#', 'A#'].map(k => <option key={k} value={k}>{k}</option>)}
                        </optgroup>
                        <optgroup label="Flats">
                          {['Db', 'Eb', 'Gb', 'Ab', 'Bb'].map(k => <option key={k} value={k}>{k}</option>)}
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label style={{ color: '#888', fontSize: '0.65rem' }}>ENERGY</label>
                      <select value={drumEnergy} onChange={(e) => setDrumEnergy(e.target.value)}
                        style={{ width: '100%', padding: '6px', background: '#111', border: '1px solid #333', borderRadius: '4px', color: '#f59e0b', fontSize: '0.75rem' }}>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="extreme">Extreme</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {['intro', 'verse', 'pre_chorus', 'chorus', 'bridge', 'outro'].map(s => (
                      <button key={s} onClick={() => toggleDrumSection(s)}
                        style={{
                          padding: '4px 10px', fontSize: '0.65rem', fontWeight: 600,
                          background: drumSections.includes(s) ? '#8b5cf6' : 'transparent',
                          border: drumSections.includes(s) ? 'none' : '1px solid #444',
                          borderRadius: '12px', color: drumSections.includes(s) ? '#fff' : '#888',
                          cursor: 'pointer', textTransform: 'uppercase'
                        }}>
                        {s.replace('_', '-')}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* 40K STYLE PROMPTS - SONG CREATOR INTEGRATION */}
              <div style={{
                padding: '14px',
                background: drumStylePrompt ? 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(139, 92, 246, 0.1))' : 'rgba(0,0,0,0.2)',
                border: drumStylePrompt ? '2px solid rgba(0, 240, 255, 0.4)' : '1px solid rgba(255,255,255,0.1)',
                borderRadius: '10px',
                marginBottom: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                  <Star className="w-4 h-4" style={{ color: '#00f0ff' }} />
                  <span style={{ color: '#00f0ff', fontWeight: 700, fontSize: '0.8rem' }}>70K STYLE PROMPTS</span>
                  <span style={{ color: '#666', fontSize: '0.65rem' }}>(70,796 prompts)</span>
                </div>
                
                {/* Two-tier dropdown */}
                <div style={{ display: 'grid', gridTemplateColumns: selectedDrumCategory ? '1fr 1fr' : '1fr', gap: '8px', marginBottom: drumStylePrompt ? '12px' : '0' }}>
                  <select
                    value={selectedDrumCategory}
                    onChange={(e) => loadDrumCategoryPrompts(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: '#111',
                      border: '1px solid rgba(0, 240, 255, 0.3)',
                      borderRadius: '6px',
                      color: selectedDrumCategory ? '#00f0ff' : '#666',
                      fontSize: '0.8rem',
                      cursor: 'pointer'
                    }}
                    data-testid="song-creator-40k-category"
                  >
                    <option value="">SELECT GENRE ({drumStyleCategories.length || 46})</option>
                    {drumStyleCategories.map(cat => (
                      <option key={cat.name || cat.genre} value={cat.name || cat.genre}>
                        {cat.name || cat.genre} ({(cat.count || 0).toLocaleString()})
                      </option>
                    ))}
                  </select>
                  
                  {selectedDrumCategory && (
                    <select
                      value={drumStylePrompt}
                      onChange={(e) => e.target.value && applyStyleToDrum(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        background: '#111',
                        border: '1px solid rgba(139, 92, 246, 0.3)',
                        borderRadius: '6px',
                        color: drumStylePrompt ? '#a78bfa' : '#666',
                        fontSize: '0.75rem',
                        cursor: 'pointer'
                      }}
                      data-testid="song-creator-40k-prompt"
                    >
                      <option value="">SELECT PROMPT ({drumCategoryPrompts.length})</option>
                      {drumCategoryPrompts.map((prompt, idx) => (
                        <option key={idx} value={prompt}>
                          {prompt.length > 50 ? prompt.substring(0, 50) + '...' : prompt}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                
                {/* Selected Style with Action Buttons */}
                {drumStylePrompt && (
                  <div style={{
                    background: 'rgba(0, 240, 255, 0.05)',
                    border: '1px solid rgba(0, 240, 255, 0.2)',
                    borderRadius: '8px',
                    padding: '10px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <span style={{ color: '#00f0ff', fontSize: '0.65rem', fontWeight: 600 }}>🎵 SELECTED STYLE</span>
                      <button
                        onClick={() => {
                          setDrumStylePrompt("");
                          setSelectedDrumCategory("");
                          setDrumCategoryPrompts([]);
                        }}
                        style={{
                          background: 'rgba(239, 68, 68, 0.2)',
                          border: 'none',
                          borderRadius: '4px',
                          color: '#ef4444',
                          padding: '2px 6px',
                          fontSize: '0.55rem',
                          cursor: 'pointer'
                        }}
                      >
                        ✕ CLEAR
                      </button>
                    </div>
                    <p style={{ 
                      color: '#ccc', 
                      fontSize: '0.7rem', 
                      margin: '0 0 10px 0',
                      fontStyle: 'italic',
                      lineHeight: 1.4
                    }}>
                      "{drumStylePrompt.length > 100 ? drumStylePrompt.substring(0, 100) + '...' : drumStylePrompt}"
                    </p>
                    
                    {/* Action Buttons */}
                    <div style={{ display: 'grid', gridTemplateColumns: advancedDrumMode ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)', gap: '6px' }}>
                      {advancedDrumMode && (
                        <button
                          onClick={() => generateAdvancedDrumPattern()}
                          disabled={isGeneratingDrum}
                          style={{
                            padding: '8px 4px',
                            background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#fff',
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            cursor: isGeneratingDrum ? 'not-allowed' : 'pointer',
                            opacity: isGeneratingDrum ? 0.5 : 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '2px'
                          }}
                          data-testid="song-creator-40k-fullsong"
                        >
                          <span>🧬</span>
                          FULL SONG
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          await generateDrumFromStylePrompt(drumStylePrompt);
                          setTimeout(() => generateDrumAudio(), 1000);
                        }}
                        disabled={isGeneratingDrum || isGeneratingDrumAudio}
                        style={{
                          padding: '8px 4px',
                          background: 'linear-gradient(135deg, #10b981, #059669)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          cursor: (isGeneratingDrum || isGeneratingDrumAudio) ? 'not-allowed' : 'pointer',
                          opacity: (isGeneratingDrum || isGeneratingDrumAudio) ? 0.5 : 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '2px'
                        }}
                        data-testid="song-creator-40k-preview"
                      >
                        <Headphones className="w-3 h-3" />
                        PREVIEW
                      </button>
                      <button
                        onClick={() => universalCopy(drumStylePrompt, '📋 Copied!')}
                        style={{
                          padding: '8px 4px',
                          background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '2px'
                        }}
                        data-testid="song-creator-40k-copy"
                      >
                        <Copy className="w-3 h-3" />
                        COPY
                      </button>
                      <button
                        onClick={() => generateDrumFromStylePrompt(drumStylePrompt)}
                        disabled={isGeneratingDrum}
                        style={{
                          padding: '8px 4px',
                          background: 'linear-gradient(135deg, #00f0ff, #0ea5e9)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#000',
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          cursor: isGeneratingDrum ? 'not-allowed' : 'pointer',
                          opacity: isGeneratingDrum ? 0.5 : 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '2px'
                        }}
                        data-testid="song-creator-40k-drums"
                      >
                        <Zap className="w-3 h-3" />
                        DRUMS
                      </button>
                      <button
                        onClick={() => generateMidiFromPrompt(drumStylePrompt)}
                        disabled={isGeneratingMidi}
                        style={{
                          padding: '8px 4px',
                          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.6rem',
                          fontWeight: 700,
                          cursor: isGeneratingMidi ? 'not-allowed' : 'pointer',
                          opacity: isGeneratingMidi ? 0.5 : 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '2px'
                        }}
                        data-testid="song-creator-40k-midi"
                      >
                        <Music className="w-3 h-3" />
                        MIDI
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Generate Drum Button */}
              <button
                onClick={advancedDrumMode ? generateAdvancedDrumPattern : generateDrumPattern}
                disabled={isGeneratingDrum}
                data-testid="song-creator-generate-drums-btn"
                style={{
                  padding: '14px',
                  background: isGeneratingDrum ? 'rgba(139, 92, 246, 0.3)' : advancedDrumMode ? 'linear-gradient(135deg, #8b5cf6, #ec4899)' : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: isGeneratingDrum ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '16px'
                }}
              >
                {isGeneratingDrum ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating Pattern...</>
                ) : (
                  <><Zap className="w-4 h-4" /> Generate Drums</>
                )}
              </button>
              
              {/* Generated Drum Display */}
              {generatedDrumPattern && (
                <div style={{
                  flex: 1,
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: '10px',
                  padding: '16px',
                  overflow: 'auto'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ color: '#8b5cf6', fontWeight: 600, fontSize: '0.85rem' }}>
                      🥁 {(generatedDrumPattern.genre || drumGenre).toUpperCase()} @ {generatedDrumPattern.tempo || drumTempo} BPM
                      {generatedDrumPattern.key && <span style={{ color: '#a78bfa', marginLeft: '6px' }}>• {generatedDrumPattern.key}</span>}
                    </span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={copySunoPromptFromDrum}
                        style={{
                          padding: '4px 10px',
                          background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.7rem',
                          cursor: 'pointer'
                        }}
                      >
                        Suno
                      </button>
                      <button
                        onClick={copyDrumPattern}
                        style={{
                          padding: '4px 10px',
                          background: 'rgba(139, 92, 246, 0.2)',
                          border: '1px solid rgba(139, 92, 246, 0.3)',
                          borderRadius: '6px',
                          color: '#8b5cf6',
                          fontSize: '0.7rem',
                          cursor: 'pointer'
                        }}
                      >
                        Copy All
                      </button>
                    </div>
                  </div>
                  <pre style={{
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '0.75rem',
                    lineHeight: 1.6,
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    {generatedDrumPattern.pattern}
                  </pre>
                </div>
              )}
            </div>
          </div>
          
          {/* Bottom Action Bar */}
          <div style={{
            padding: '16px 24px',
            borderTop: '1px solid rgba(0, 212, 255, 0.2)',
            background: 'rgba(0, 0, 0, 0.4)',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {/* Row 1: Generate Prompts & Export */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <button
                onClick={generateCompleteSong}
                disabled={isGeneratingBars || isGeneratingDrum || !rhymeTopic.trim()}
                data-testid="song-creator-generate-complete-btn"
                style={{
                  padding: '14px 28px',
                  background: (isGeneratingBars || isGeneratingDrum) 
                    ? 'rgba(0, 212, 255, 0.3)' 
                    : 'linear-gradient(135deg, #f97316, #8b5cf6, #00d4ff)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: (isGeneratingBars || isGeneratingDrum) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                {(isGeneratingBars || isGeneratingDrum) ? (
                  <><Loader2 className="w-5 h-5 animate-spin" /> Creating Song...</>
                ) : (
                  <><Sparkles className="w-5 h-5" /> 🎵 Generate Complete Song</>
                )}
              </button>
              
              <button
                onClick={copyFullSongPackage}
                disabled={!generatedBars && !generatedDrumPattern}
                data-testid="song-creator-export-package-btn"
                style={{
                  padding: '14px 28px',
                  background: (!generatedBars && !generatedDrumPattern) 
                    ? 'rgba(249, 115, 22, 0.3)' 
                    : 'linear-gradient(135deg, #f59e0b, #f97316)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: (!generatedBars && !generatedDrumPattern) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <Copy className="w-5 h-5" /> 📦 Export Song Package
              </button>
              
              {/* Download MIDI Button - For Song Creator */}
              <button
                onClick={downloadDrumMidi}
                disabled={!generatedDrumPattern}
                data-testid="song-creator-download-midi-btn"
                style={{
                  padding: '14px 28px',
                  background: !generatedDrumPattern 
                    ? 'rgba(16, 185, 129, 0.3)' 
                    : 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: !generatedDrumPattern ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <Download className="w-5 h-5" /> 🎹 Download MIDI
              </button>
              
              {/* Download WAV Button - For Song Creator */}
              <button
                onClick={downloadDrumWav}
                disabled={!generatedDrumPattern}
                data-testid="song-creator-download-wav-btn"
                style={{
                  padding: '14px 28px',
                  background: !generatedDrumPattern 
                    ? 'rgba(139, 92, 246, 0.3)' 
                    : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: !generatedDrumPattern ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <Download className="w-5 h-5" /> 🎵 Download WAV
              </button>
              
              {/* Library Button - For Song Creator */}
              <button
                onClick={() => setShowDrumLibrary(!showDrumLibrary)}
                data-testid="song-creator-library-btn"
                style={{
                  padding: '14px 28px',
                  background: showDrumLibrary 
                    ? 'linear-gradient(135deg, #a78bfa, #8b5cf6)' 
                    : 'rgba(167, 139, 250, 0.2)',
                  border: showDrumLibrary ? 'none' : '2px solid rgba(167, 139, 250, 0.5)',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <Archive className="w-5 h-5" /> 📚 Library ({drumLibrary.length})
              </button>
            </div>
            
            {/* Library Panel - For Song Creator */}
            {showDrumLibrary && (
              <div style={{
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.1))',
                border: '2px solid rgba(139, 92, 246, 0.4)',
                borderRadius: '12px',
                padding: '16px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h3 style={{ color: '#a78bfa', fontSize: '1rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Archive className="w-5 h-5" /> PATTERN LIBRARY ({drumLibrary.length})
                  </h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {drumLibrary.length > 0 && (
                      <button onClick={clearLibrary} style={{ padding: '6px 12px', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '6px', color: '#ef4444', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                        CLEAR ALL
                      </button>
                    )}
                    <button onClick={() => setShowDrumLibrary(false)} style={{ padding: '6px 12px', background: 'rgba(100,100,100,0.3)', border: 'none', borderRadius: '6px', color: '#888', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}>
                      CLOSE
                    </button>
                  </div>
                </div>
                
                {drumLibrary.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '30px', color: '#666' }}>
                    <Archive className="w-10 h-10" style={{ margin: '0 auto 10px', opacity: 0.3 }} />
                    <p style={{ margin: 0 }}>No patterns saved yet</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', maxHeight: '300px', overflowY: 'auto' }}>
                    {drumLibrary.slice(0, 12).map((entry, idx) => (
                      <div key={entry.id} style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(139, 92, 246, 0.2)', borderRadius: '10px', padding: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#00f0ff', fontWeight: 700, fontSize: '0.85rem' }}>{entry.genre?.toUpperCase()} - {entry.tempo} BPM</span>
                          <button onClick={() => deleteFromLibrary(entry.id)} style={{ background: 'transparent', border: 'none', color: '#666', cursor: 'pointer' }}><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <p style={{ color: '#666', fontSize: '0.7rem', margin: '0 0 10px' }}>{entry.kit?.toUpperCase()} Kit • {new Date(entry.timestamp).toLocaleDateString()}</p>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button onClick={() => loadFromLibrary(entry)} style={{ flex: '1 1 60px', padding: '6px 10px', background: 'linear-gradient(135deg, #00f0ff, #0ea5e9)', border: 'none', borderRadius: '6px', color: '#000', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><Upload className="w-3 h-3" /> LOAD</button>
                          {entry.audioUrl && <button onClick={() => { const a = new Audio(); a.preload = 'metadata'; a.addEventListener('canplay', () => a.play().catch(() => {}), { once: true }); a.src = entry.audioUrl; a.load(); }} style={{ flex: '1 1 60px', padding: '6px 10px', background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><Play className="w-3 h-3" /> PLAY</button>}
                          <button onClick={() => generateMidiFromPrompt(entry.suno_prompt || `${entry.genre} drums`)} style={{ flex: '1 1 60px', padding: '6px 10px', background: 'linear-gradient(135deg, #22c55e, #16a34a)', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}><Music className="w-3 h-3" /> MIDI</button>
                          {entry.audioUrl && <button onClick={async () => { const filename = `SoundDNA_${entry.genre}_${entry.tempo}BPM.wav`; try { let src = entry.audioUrl; if (src.startsWith('/')) src = `${API_ORIGIN}${src}`; toast.info('⏳ Preparing WAV...', { duration: 1500 }); const r = await fetch(src); const blob = await r.blob(); await shareOrDownloadBlob(blob, filename, 'audio/wav', { sourceUrl: src, desktopPicker: true, successMessage: '📥 WAV saved!' }); } catch (e) { toast.error('Download failed — try again'); } }} style={{ flex: '1 1 60px', padding: '6px 10px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }} data-testid={`lib-wav-${entry.id}`}><Download className="w-3 h-3" /> WAV</button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Row 2: 1-CLICK CREATE SONG - The Killer Feature */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(139, 92, 246, 0.1))',
              border: '2px solid rgba(0, 212, 255, 0.4)',
              borderRadius: '16px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', textAlign: 'center' }}>
                <strong style={{ color: '#00d4ff' }}>🚀 1-CLICK CREATE</strong> - Generate actual audio from your prompts
              </div>
              
              {/* Progress Bar */}
              {isGeneratingSong && (
                <div style={{ width: '100%', maxWidth: '400px' }}>
                  <div style={{
                    background: 'rgba(0,0,0,0.4)',
                    borderRadius: '10px',
                    height: '8px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      background: 'linear-gradient(90deg, #00d4ff, #8b5cf6, #f97316)',
                      height: '100%',
                      width: `${songGenerationProgress}%`,
                      transition: 'width 0.3s ease',
                      borderRadius: '10px'
                    }} />
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', textAlign: 'center', marginTop: '4px' }}>
                    {songGenerationProgress < 30 ? 'Starting AI generation...' :
                     songGenerationProgress < 70 ? 'Creating your music...' :
                     songGenerationProgress < 100 ? 'Finalizing audio...' : 'Complete!'}
                  </div>
                </div>
              )}
              
              {/* Generated Song Player */}
              {generatedSongUrl && !isGeneratingSong && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 20px',
                  background: 'rgba(0, 212, 255, 0.15)',
                  borderRadius: '12px',
                  border: '1px solid rgba(0, 212, 255, 0.3)'
                }}>
                  <button
                    onClick={toggleSongPlayback}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                      border: 'none',
                      color: '#fff',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    data-testid="song-play-btn"
                  >
                    {isSongPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                  </button>
                  <div style={{ color: '#fff', flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>🎵 Your Song is Ready!</div>
                    <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>AI-generated from your prompts</div>
                  </div>
                  <button
                    onClick={downloadGeneratedSong}
                    style={{
                      padding: '10px 16px',
                      background: 'linear-gradient(135deg, #10b981, #059669)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    data-testid="song-download-btn"
                  >
                    <Download className="w-4 h-4" /> Download
                  </button>
                  <audio preload="metadata"
                    ref={songAudioRef}
                    src={generatedSongUrl}
                    onEnded={() => setIsSongPlaying(false)}
                    style={{ display: 'none' }}
                  />
                </div>
              )}
              
              {/* COPYRIGHT & SAFETY PANEL */}
              {generatedSongUrl && !isGeneratingSong && songSafety && (
                <div data-testid="copyright-safety-panel" style={{
                  padding: '16px 20px',
                  background: 'rgba(16,185,129,0.08)',
                  border: '1px solid rgba(16,185,129,0.3)',
                  borderRadius: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '1.1rem' }}>🛡️</span>
                    <span style={{ color: '#10b981', fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.05em' }}>COPYRIGHT & SAFETY</span>
                    <button
                      data-testid="commercial-use-tooltip-btn"
                      onClick={() => setShowCommercialTip(v => !v)}
                      style={{ marginLeft: 'auto', padding: '4px 10px', background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.35)', borderRadius: '20px', color: '#10b981', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Commercial Use ⓘ
                    </button>
                  </div>
                  {showCommercialTip && (
                    <div data-testid="commercial-use-tooltip" style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', color: 'rgba(255,255,255,0.75)', fontSize: '0.78rem', lineHeight: 1.5 }}>
                      <strong style={{ color: '#10b981' }}>Who owns this track?</strong> Ownership applies to the <strong>human-authored elements</strong> — your lyrics, your prompt choices, and your arrangement decisions. Under current U.S. Copyright Office guidance, purely AI-generated audio is not copyrightable on its own, so your creative input is what carries the rights.
                    </div>
                  )}
                  {songSafety.uniqueness_score != null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '10px' }}>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Uniqueness Score</div>
                        <div data-testid="uniqueness-score" style={{ color: '#10b981', fontWeight: 800, fontSize: '1.6rem' }}>{songSafety.uniqueness_score}%</div>
                      </div>
                      <div style={{ flex: 1, minWidth: '140px' }}>
                        <div style={{ height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${songSafety.uniqueness_score}%`, height: '100%', background: 'linear-gradient(90deg, #10b981, #34d399)', borderRadius: '4px' }} />
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.7rem', marginTop: '4px' }}>
                          Compared against {(songSafety.library_size || 71000).toLocaleString()}+ internal style prompts — no meaningful overlap detected
                        </div>
                      </div>
                      <div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Hybrid DNA</div>
                        <div data-testid="hybrid-dna-ratio" style={{ color: '#34d399', fontWeight: 700, fontSize: '0.95rem' }}>{songSafety.hybrid_dna?.label || 'Original blend'}</div>
                      </div>
                    </div>
                  )}
                  <button
                    data-testid="copyright-insights-toggle"
                    onClick={() => setShowCopyrightInsights(v => !v)}
                    style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.55)', fontSize: '0.75rem', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                  >
                    {showCopyrightInsights ? 'Hide' : 'Show'} Copyright Insights — U.S. Copyright Office rules
                  </button>
                  {showCopyrightInsights && (
                    <div data-testid="copyright-insights" style={{ marginTop: '10px', background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '12px 14px', color: 'rgba(255,255,255,0.7)', fontSize: '0.76rem', lineHeight: 1.6 }}>
                      <div style={{ color: '#fff', fontWeight: 700, marginBottom: '6px' }}>📜 Copyright Insights (U.S. Copyright Office guidance)</div>
                      <ul style={{ margin: 0, paddingLeft: '18px' }}>
                        <li>Copyright protects <strong>human authorship</strong> — purely AI-generated material is not copyrightable by itself.</li>
                        <li>Your <strong>lyrics, prompt selections, arrangement and editing choices</strong> are human contributions that can be protected.</li>
                        <li>When registering a work, you must <strong>disclose AI-generated content</strong> and claim only the human-authored parts.</li>
                        <li>Uniqueness scores are an internal comparison against our prompt library — they are <strong>not a legal clearance</strong> or audio fingerprint check.</li>
                      </ul>
                      <div style={{ marginTop: '8px', color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>This is educational information, not legal advice.</div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Create Song Button */}
              {!generatedSongUrl && !isGeneratingSong && (
                <button
                  onClick={generateFullSong}
                  disabled={!generatedBars && !generatedDrumPattern && !rhymeTopic.trim()}
                  data-testid="create-song-btn"
                  style={{
                    padding: '18px 40px',
                    background: (!generatedBars && !generatedDrumPattern && !rhymeTopic.trim())
                      ? 'rgba(0, 212, 255, 0.3)'
                      : 'linear-gradient(135deg, #00d4ff, #8b5cf6, #f97316)',
                    border: 'none',
                    borderRadius: '14px',
                    color: '#fff',
                    fontSize: '1.1rem',
                    fontWeight: 800,
                    cursor: (!generatedBars && !generatedDrumPattern && !rhymeTopic.trim()) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    boxShadow: '0 4px 20px rgba(0, 212, 255, 0.3)'
                  }}
                >
                  <Music className="w-6 h-6" /> 🚀 CREATE SONG
                </button>
              )}
              
              {/* Regenerate Button */}
              {generatedSongUrl && !isGeneratingSong && (
                <button
                  onClick={() => {
                    setGeneratedSongUrl(null);
                    setIsSongPlaying(false);
                  }}
                  style={{
                    padding: '8px 16px',
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  Create New Song
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* ============================================ */}
      {/* SUNO AI MUSIC GENERATOR - FULLSCREEN VIEW */}
      {/* ============================================ */}
      {fullscreenTool === 'suno-music' && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 50%, #16213e 100%)',
          zIndex: 100,
          overflow: 'auto',
          padding: 'clamp(12px, 3vw, 20px)'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            paddingBottom: '12px',
            borderBottom: '1px solid rgba(236, 72, 153, 0.2)',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: 'clamp(36px, 8vw, 50px)',
                height: 'clamp(36px, 8vw, 50px)',
                background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(236, 72, 153, 0.4)',
                flexShrink: 0
              }}>
                <Music className="w-5 h-5" style={{ color: '#fff' }} />
              </div>
              <div>
                <h1 style={{ 
                  fontSize: 'clamp(1rem, 4vw, 1.5rem)', 
                  fontWeight: 800, 
                  margin: 0,
                  background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  MANNIS AI Music Generator
                </h1>
                <p style={{ color: '#888', fontSize: 'clamp(0.7rem, 2vw, 0.85rem)', margin: '2px 0 0', display: 'none' }}>
                  Create professional songs with AI
                </p>
              </div>
            </div>
            <button
              onClick={() => setFullscreenTool(null)}
              style={{
                padding: '8px 16px',
                background: 'rgba(100,100,100,0.3)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>

          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
            gap: 'clamp(12px, 3vw, 24px)',
            maxWidth: '1400px',
            margin: '0 auto'
          }}>
            {/* LEFT PANEL - GENERATION FORM */}
            <div style={{
              background: 'rgba(0,0,0,0.3)',
              border: '1px solid rgba(236, 72, 153, 0.2)',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <h2 style={{ 
                color: '#ec4899', 
                fontSize: '1.1rem', 
                fontWeight: 700, 
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <Zap className="w-5 h-5" /> Generate New Song
              </h2>

              {/* Title Input — moved to top; lyrics are written from this */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  color: '#00d4ff', 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Song Title
                </label>
                <input
                  type="text"
                  value={sunoTitle}
                  onChange={(e) => setSunoTitle(e.target.value)}
                  placeholder="e.g., Summer Nights, Chasing Dreams — lyrics are written from this"
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: '#111',
                    border: '2px solid rgba(0, 212, 255, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    outline: 'none'
                  }}
                  data-testid="suno-title-input"
                />
              </div>

              {/* Prompt Input */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  color: '#ec4899', 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Song Description / Lyrics *
                </label>
                <textarea
                  value={sunoPrompt}
                  onChange={(e) => {
                    setSunoPrompt(e.target.value);
                    const celebrity = checkForCelebrityNames(e.target.value);
                    setSunoCelebrityWarning(celebrity);
                  }}
                  placeholder="Describe your song or paste lyrics here...&#10;&#10;Example: A dreamy synth-pop song about summer nights, with ethereal vocals and a catchy chorus"
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '14px',
                    background: '#111',
                    border: sunoCelebrityWarning ? '2px solid #ef4444' : '2px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    resize: 'vertical',
                    outline: 'none'
                  }}
                  data-testid="suno-prompt-input"
                />
                {/* Celebrity Name Warning */}
                {sunoCelebrityWarning && (
                  <div style={{
                    marginTop: '8px',
                    padding: '10px 14px',
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.4)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                    <div>
                      <p style={{ color: '#ef4444', fontSize: '0.85rem', fontWeight: 600, margin: 0 }}>
                        Artist Name Detected: "{sunoCelebrityWarning}"
                      </p>
                      <p style={{ color: '#fca5a5', fontSize: '0.75rem', margin: '4px 0 0 0' }}>
                        Suno AI doesn't allow artist references. Describe the STYLE instead (e.g., "dark trap with heavy 808s" instead of artist names).
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* QUICK STYLE CHIPS + OPTIONAL VOICES LIBRARY (fullscreen generator) */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'8px' }}>
                  <Sparkles className="w-3 h-3" style={{ color:'#ec4899' }} />
                  <span style={{ color:'rgba(255,255,255,0.6)', fontSize:'0.72rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.05em' }}>Quick Styles</span>
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'12px' }}>
                  {QUICK_STYLES.map(st => (
                    <button
                      key={st}
                      data-testid={`fs-quick-style-${st.replace(/\s+/g,'-').toLowerCase()}`}
                      onClick={() => {
                        setSunoPrompt(prev => {
                          const p = (prev || '').trim();
                          if (!p) return st;
                          if (new RegExp(`\\b${st.replace(/[-\/\\^$*+?.()|[\]{}]/g,'\\$&')}\\b`, 'i').test(p)) return p;
                          return `${p}, ${st}`;
                        });
                      }}
                      style={{ padding:'7px 14px', borderRadius:'999px', fontSize:'0.78rem', fontWeight:700, cursor:'pointer', background:'rgba(236,72,153,0.12)', border:'1px solid rgba(236,72,153,0.35)', color:'#f9a8d4' }}
                    >
                      + {st}
                    </button>
                  ))}
                </div>
                <button
                  data-testid="fs-toggle-voice-picker"
                  onClick={() => setShowVoicePicker(v => !v)}
                  style={{ display:'flex', alignItems:'center', gap:'8px', width:'100%', padding:'12px 14px', borderRadius:'10px', cursor:'pointer', background: showVoicePicker ? 'rgba(159,71,255,0.15)' : 'rgba(0,0,0,0.3)', border:'1px solid rgba(159,71,255,0.35)', color:'#fff', fontSize:'0.82rem', fontWeight:700 }}
                >
                  <span>🎙️ Add a Voice <span style={{ color:'rgba(255,255,255,0.45)', fontWeight:500 }}>(optional)</span></span>
                  {selectedVoiceId && (
                    <span style={{ marginLeft:'auto', marginRight:'8px', color:'#c4b5fd', fontWeight:600 }}>
                      {SONG_VOICES.find(v=>v.voice_id===selectedVoiceId)?.name}
                    </span>
                  )}
                  <span style={{ marginLeft: selectedVoiceId ? '0' : 'auto', padding:'4px 12px', borderRadius:'999px', background: showVoicePicker ? 'rgba(239,68,68,0.25)' : 'rgba(159,71,255,0.3)', border:`1px solid ${showVoicePicker ? 'rgba(239,68,68,0.5)' : 'rgba(159,71,255,0.55)'}`, color:'#fff', fontSize:'0.7rem', fontWeight:800, letterSpacing:'0.04em' }}>{showVoicePicker ? '✕ CLOSE' : '＋ OPEN'}</span>
                </button>
                {showVoicePicker && (
                  <div data-testid="fs-voices-library" style={{ marginTop:'10px', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px, 1fr))', gap:'10px' }}>
                    {selectedVoiceId && (
                      <button
                        data-testid="fs-voice-clear"
                        onClick={() => setSelectedVoiceId('')}
                        style={{ gridColumn:'1 / -1', padding:'8px', borderRadius:'8px', cursor:'pointer', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', color:'#ef4444', fontSize:'0.75rem', fontWeight:700 }}
                      >
                        ✕ Clear selected voice
                      </button>
                    )}
                    {SONG_VOICES.map(v => {
                      const selected = selectedVoiceId === v.voice_id;
                      const playing = voicePreviewId === v.voice_id;
                      return (
                        <div
                          key={v.voice_id}
                          data-testid={`fs-voice-card-${v.voice_id}`}
                          onClick={() => setSelectedVoiceId(selected ? '' : v.voice_id)}
                          style={{ background: selected ? 'linear-gradient(135deg, rgba(236,72,153,0.25), rgba(159,71,255,0.2))' : 'rgba(0,0,0,0.35)', border: selected ? '2px solid #ec4899' : '1px solid rgba(255,255,255,0.14)', borderRadius:'12px', padding:'12px', cursor:'pointer', position:'relative' }}
                        >
                          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'6px' }}>
                            <span style={{ color:'#fff', fontWeight:700, fontSize:'0.85rem' }}>{v.name}</span>
                            <button
                              data-testid={`fs-voice-preview-btn-${v.voice_id}`}
                              onClick={(e) => { e.stopPropagation(); playVoicePreview(v.voice_id); }}
                              title="Preview voice"
                              style={{ width:'30px', height:'30px', flexShrink:0, borderRadius:'50%', background: playing ? '#ec4899' : 'rgba(236,72,153,0.2)', border:'1px solid rgba(236,72,153,0.5)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                            >
                              {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                            </button>
                          </div>
                          <div style={{ color:'rgba(255,255,255,0.65)', fontSize:'0.72rem', marginTop:'4px' }}>{v.style}</div>
                          <div style={{ color:'rgba(255,255,255,0.4)', fontSize:'0.66rem', marginTop:'2px' }}>{v.genre}</div>
                          {selected && <div style={{ position:'absolute', top:'6px', left:'8px', color:'#ec4899', fontSize:'0.72rem', fontWeight:900 }}>✓</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* AI LYRICS — auto-written from the description, review & edit before generating */}
              <div style={{ marginBottom: '16px' }}>
                <button
                  data-testid="generate-lyrics-btn"
                  onClick={generateSongLyrics}
                  disabled={songLyricsLoading || (!sunoTitle.trim() && !sunoPrompt.trim())}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', width:'100%', padding:'13px', borderRadius:'10px', cursor: (songLyricsLoading || (!sunoTitle.trim() && !sunoPrompt.trim())) ? 'not-allowed':'pointer', background: (songLyricsLoading || (!sunoTitle.trim() && !sunoPrompt.trim())) ? 'rgba(255,255,255,0.08)' : 'linear-gradient(135deg, #f59e0b, #ec4899)', border:'none', color: (songLyricsLoading || (!sunoTitle.trim() && !sunoPrompt.trim())) ? 'rgba(255,255,255,0.4)':'#fff', fontSize:'0.88rem', fontWeight:800 }}
                >
                  {songLyricsLoading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Writing clean, Suno-safe lyrics...</>) : (<>✍️ Write Lyrics with AI from Title</>)}
                </button>
                {songLyrics && (
                  <div style={{ marginTop:'10px' }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'6px' }}>
                      <span style={{ color:'#f59e0b', fontSize:'0.72rem', fontWeight:800, letterSpacing:'0.05em', textTransform:'uppercase' }}>✅ Generated Lyrics — review &amp; edit</span>
                      <button data-testid="clear-lyrics-btn" onClick={() => setSongLyrics("")} style={{ background:'none', border:'none', color:'#ef4444', fontSize:'0.72rem', fontWeight:700, cursor:'pointer' }}>✕ Clear</button>
                    </div>
                    <textarea
                      data-testid="song-lyrics-textarea"
                      value={songLyrics}
                      onChange={(e) => setSongLyrics(e.target.value)}
                      style={{ width:'100%', minHeight:'180px', padding:'12px', background:'#111', border:'2px solid rgba(245,158,11,0.4)', borderRadius:'10px', color:'#fff', fontSize:'0.85rem', resize:'vertical', outline:'none', fontFamily:'inherit', lineHeight:1.5 }}
                    />
                    <p style={{ color:'rgba(255,255,255,0.45)', fontSize:'0.68rem', marginTop:'4px' }}>These lyrics (not the raw description) are sent to MANNIS AI. Clear them to generate straight from the description instead.</p>
                  </div>
                )}
              </div>

              {/* Style Input */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  color: '#a78bfa', 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Style / Genre (Optional)
                </label>
                <input
                  type="text"
                  value={sunoStyle}
                  onChange={(e) => setSunoStyle(e.target.value)}
                  placeholder="e.g., Pop, Hip-Hop, R&B, Electronic, Rock, Jazz..."
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: '#111',
                    border: '2px solid rgba(167, 139, 250, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    outline: 'none'
                  }}
                  data-testid="suno-style-input"
                />
              </div>

              {/* Musical Key Selector */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  display: 'block', 
                  color: '#ec4899', 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  Musical Key
                </label>
                <select
                  value={sunoKey}
                  onChange={(e) => setSunoKey(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: '#111',
                    border: '2px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                  data-testid="suno-key-input"
                >
                  <optgroup label="Major Keys (Natural)">
                    {['C', 'D', 'E', 'F', 'G', 'A', 'B'].map(k => (
                      <option key={`${k}-major`} value={`${k} major`}>{k} Major</option>
                    ))}
                  </optgroup>
                  <optgroup label="Minor Keys (Natural)">
                    {['C', 'D', 'E', 'F', 'G', 'A', 'B'].map(k => (
                      <option key={`${k}-minor`} value={`${k} minor`}>{k} Minor</option>
                    ))}
                  </optgroup>
                  <optgroup label="Major Keys (Sharps)">
                    {['C#', 'D#', 'F#', 'G#', 'A#'].map(k => (
                      <option key={`${k}-major`} value={`${k} major`}>{k} Major</option>
                    ))}
                  </optgroup>
                  <optgroup label="Minor Keys (Sharps)">
                    {['C#', 'D#', 'F#', 'G#', 'A#'].map(k => (
                      <option key={`${k}-minor`} value={`${k} minor`}>{k} Minor</option>
                    ))}
                  </optgroup>
                  <optgroup label="Major Keys (Flats)">
                    {['Db', 'Eb', 'Gb', 'Ab', 'Bb'].map(k => (
                      <option key={`${k}-major`} value={`${k} major`}>{k} Major</option>
                    ))}
                  </optgroup>
                  <optgroup label="Minor Keys (Flats)">
                    {['Db', 'Eb', 'Gb', 'Ab', 'Bb'].map(k => (
                      <option key={`${k}-minor`} value={`${k} minor`}>{k} Minor</option>
                    ))}
                  </optgroup>
                </select>
                <p style={{
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '0.7rem',
                  marginTop: '6px',
                  fontStyle: 'italic'
                }}>
                  Hint for Suno AI — PREVIEW & MIDI produce exact key locally
                </p>
              </div>

              {/* Instrumental Toggle */}
              <div style={{ 
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '14px',
                background: 'rgba(139, 92, 246, 0.1)',
                borderRadius: '10px',
                border: '1px solid rgba(139, 92, 246, 0.2)'
              }}>
                <input
                  type="checkbox"
                  id="suno-instrumental"
                  checked={sunoInstrumental}
                  onChange={(e) => setSunoInstrumental(e.target.checked)}
                  style={{ 
                    width: '20px', 
                    height: '20px',
                    cursor: 'pointer'
                  }}
                  data-testid="suno-instrumental-toggle"
                />
                <label htmlFor="suno-instrumental" style={{ 
                  color: '#a78bfa', 
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}>
                  🎹 Instrumental Only (No vocals)
                </label>
              </div>

              {/* Song Duration Selector */}
              <div style={{ marginBottom: '24px' }} data-testid="suno-duration-section">
                <label style={{ 
                  display: 'block', 
                  color: '#f59e0b', 
                  fontSize: '0.75rem', 
                  fontWeight: 600,
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}>
                  ⏱️ Song Length <span style={{ textTransform: 'none', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>· your pick is saved as the default</span>
                </label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {[
                    { label: 'Auto', value: null },
                    { label: '30 sec', value: 30 },
                    { label: '1 min', value: 60 },
                    { label: '2 min', value: 120 },
                    { label: '3 min', value: 180 },
                    { label: '4 min', value: 240 },
                    { label: '5 min', value: 300 },
                  ].map((opt) => {
                    const active = sunoDuration === opt.value;
                    return (
                      <button
                        key={opt.label}
                        type="button"
                        onClick={() => {
                          setSunoDuration(opt.value);
                          try { localStorage.setItem('mannis_default_song_length', opt.value === null ? 'auto' : String(opt.value)); } catch (e) {}
                        }}
                        data-testid={`suno-duration-${opt.value || 'auto'}`}
                        style={{
                          flex: '1 1 56px',
                          padding: '10px 12px',
                          background: active ? 'linear-gradient(135deg,#f59e0b,#d97706)' : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${active ? '#f59e0b' : 'rgba(255,255,255,0.15)'}`,
                          borderRadius: '10px',
                          color: active ? '#111' : '#fff',
                          fontSize: '0.8rem',
                          fontWeight: 800,
                          cursor: 'pointer'
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', marginTop: '6px', fontStyle: 'italic' }}>
                  Auto lets Suno pick a natural length. Pick a time to build a complete, full-length song (uses the latest V6 engine). Your choice is remembered as your default next time.
                </p>
              </div>

              {/* Reference Audio Upload Display */}
              {sunoReferenceAudio && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px',
                  background: 'rgba(34, 197, 94, 0.1)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <Music className="w-5 h-5" style={{ color: '#22c55e' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#22c55e', fontSize: '0.85rem', fontWeight: 600 }}>
                      Reference Audio Attached
                    </div>
                    <div style={{ color: '#888', fontSize: '0.75rem' }}>
                      {sunoReferenceAudio.name} ({(sunoReferenceAudio.size / 1024 / 1024).toFixed(2)} MB)
                    </div>
                  </div>
                  <button
                    onClick={() => setSunoReferenceAudio(null)}
                    style={{
                      padding: '6px 10px',
                      background: 'rgba(239, 68, 68, 0.2)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '6px',
                      color: '#ef4444',
                      fontSize: '0.75rem',
                      cursor: 'pointer'
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}

              {/* Hidden File Input */}
              <input
                type="file"
                ref={sunoFileInputRef}
                accept="audio/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > 50 * 1024 * 1024) {
                      toast.error('File too large. Max 50MB.');
                      return;
                    }
                    setSunoReferenceAudio(file);
                    toast.success(`📎 Reference audio: ${file.name}`);
                  }
                }}
              />

              {/* Three Button Action Bar */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(3, 1fr)', 
                gap: '10px',
                marginBottom: '16px'
              }}>
                {/* Upload Audio Button */}
                <button
                  onClick={() => sunoFileInputRef.current?.click()}
                  style={{
                    padding: '14px 12px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                  }}
                  data-testid="suno-upload-btn"
                >
                  <Upload className="w-5 h-5" />
                  Upload Audio
                </button>

                {/* Browse Library Button */}
                <button
                  onClick={() => {
                    // Scroll to library section
                    const library = document.querySelector('[data-testid="suno-library"]');
                    if (library) {
                      library.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                    toast.info('📚 Scroll down to browse your music library');
                  }}
                  style={{
                    padding: '14px 12px',
                    background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)'
                  }}
                  data-testid="suno-browse-btn"
                >
                  <Library className="w-5 h-5" />
                  Browse Library
                </button>

                {/* Quick Generate Button */}
                <button
                  onClick={() => {
                    if (!sunoPrompt.trim()) {
                      toast.error('Enter a song description first');
                      return;
                    }
                    generateSunoMusic();
                  }}
                  disabled={isGeneratingSuno || !sunoPrompt.trim()}
                  style={{
                    padding: '14px 12px',
                    background: isGeneratingSuno || !sunoPrompt.trim()
                      ? 'rgba(100,100,100,0.3)'
                      : 'linear-gradient(135deg, #ec4899, #f43f5e)',
                    border: 'none',
                    borderRadius: '10px',
                    color: isGeneratingSuno || !sunoPrompt.trim() ? '#666' : '#fff',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: isGeneratingSuno || !sunoPrompt.trim() ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: isGeneratingSuno || !sunoPrompt.trim() ? 'none' : '0 4px 15px rgba(236, 72, 153, 0.3)'
                  }}
                  data-testid="suno-quick-generate-btn"
                >
                  <Zap className="w-5 h-5" />
                  {isGeneratingSuno ? 'Generating...' : 'Quick Generate'}
                </button>
              </div>

              {/* Generate Button */}
              <button
                onClick={() => {
                  if (sunoCelebrityWarning) {
                    toast.error(`Remove artist name "${sunoCelebrityWarning}" - describe the style instead`);
                    return;
                  }
                  generateSunoMusic();
                }}
                disabled={isGeneratingSuno || !sunoPrompt.trim() || sunoCelebrityWarning}
                style={{
                  width: '100%',
                  padding: '18px',
                  background: isGeneratingSuno || sunoCelebrityWarning
                    ? 'rgba(100,100,100,0.5)' 
                    : 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  cursor: isGeneratingSuno || !sunoPrompt.trim() || sunoCelebrityWarning ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  boxShadow: isGeneratingSuno || sunoCelebrityWarning ? 'none' : '0 4px 20px rgba(236, 72, 153, 0.4)'
                }}
                data-testid="suno-generate-btn"
              >
                {isGeneratingSuno ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {sunoStatus?.message || 'Generating...'}
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    🎤 Generate Song with MANNIS AI
                  </>
                )}
              </button>

              {/* Status Display with Progress Bar */}
              {sunoStatus && sunoStatus.status !== 'completed' && (
                <div style={{
                  marginTop: '16px',
                  padding: '14px',
                  background: sunoStatus.status === 'failed' 
                    ? 'rgba(239, 68, 68, 0.1)' 
                    : 'rgba(236, 72, 153, 0.1)',
                  border: `1px solid ${sunoStatus.status === 'failed' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(236, 72, 153, 0.3)'}`,
                  borderRadius: '10px'
                }}>
                  <p style={{ 
                    color: sunoStatus.status === 'failed' ? '#ef4444' : '#ec4899',
                    fontSize: '0.85rem',
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    {sunoStatus.status === 'processing' && <Loader2 className="w-4 h-4 animate-spin" />}
                    {sunoStatus.status === 'failed' && '❌'}
                    {sunoStatus.message}
                  </p>
                  
                  {/* Progress Bar */}
                  {sunoStatus.status === 'processing' && sunoStatus.progress && (
                    <div style={{
                      marginTop: '10px',
                      height: '6px',
                      background: 'rgba(0,0,0,0.3)',
                      borderRadius: '3px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        height: '100%',
                        width: `${sunoStatus.progress}%`,
                        background: 'linear-gradient(90deg, #ec4899, #8b5cf6)',
                        borderRadius: '3px',
                        transition: 'width 0.5s ease-out'
                      }} />
                    </div>
                  )}
                  
                  {/* Estimated Time */}
                  {sunoStatus.status === 'processing' && (
                    <p style={{ 
                      color: '#888', 
                      fontSize: '0.7rem', 
                      margin: '8px 0 0 0',
                      textAlign: 'center'
                    }}>
                      ⚡ 2 songs generating • Usually takes 20-30 seconds
                    </p>
                  )}
                </div>
              )}

              {/* Current Track Player */}
              {currentSunoTrack && (
                <div style={{
                  marginTop: '24px',
                  padding: '20px',
                  background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15), rgba(139, 92, 246, 0.15))',
                  border: '2px solid rgba(236, 72, 153, 0.4)',
                  borderRadius: '14px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    marginBottom: '16px'
                  }}>
                    <div>
                      <h3 style={{ 
                        color: '#ec4899', 
                        fontSize: '1rem', 
                        fontWeight: 700,
                        margin: '0 0 4px 0'
                      }}>
                        🎵 {currentSunoTrack.title || 'Generated Song'}
                      </h3>
                      {currentSunoTrack.duration && (
                        <p style={{ color: '#888', fontSize: '0.8rem', margin: 0 }}>
                          Duration: {Math.floor(currentSunoTrack.duration / 60)}:{String(Math.floor(currentSunoTrack.duration % 60)).padStart(2, '0')}
                        </p>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={() => downloadSunoTrack(currentSunoTrack)}
                        onTouchEnd={(e) => { e.preventDefault(); downloadSunoTrack(currentSunoTrack); }}
                        style={{
                          padding: '10px 16px',
                          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          WebkitTapHighlightColor: 'transparent',
                          touchAction: 'manipulation'
                        }}
                        data-testid="suno-download-btn"
                      >
                        <Download className="w-4 h-4" /> Download
                      </button>
                      {/* STOP/CLOSE Button */}
                      <button
                        onClick={() => {
                          if (sunoAudioRef.current) {
                            sunoAudioRef.current.pause();
                            sunoAudioRef.current.currentTime = 0;
                          }
                          setCurrentSunoTrack(null);
                          setIsSunoPlaying(false);
                          setSunoResultTracks([]);
                        }}
                        style={{
                          padding: '10px 16px',
                          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        data-testid="suno-close-player-btn"
                      >
                        <X className="w-4 h-4" /> Close
                      </button>
                    </div>
                  </div>
                  
                  {/* BOTH generated versions — each with its own WAV download */}
                  {sunoResultTracks.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }} data-testid="suno-versions-list">
                      {sunoResultTracks.map((t, vi) => {
                        const isActive = currentSunoTrack?.audio_url === t.audio_url;
                        return (
                          <div key={vi} style={{
                            display: 'flex', alignItems: 'center', gap: '8px',
                            padding: '8px 10px', borderRadius: '10px',
                            background: isActive ? 'rgba(236,72,153,0.18)' : 'rgba(255,255,255,0.05)',
                            border: `1px solid ${isActive ? 'rgba(236,72,153,0.5)' : 'rgba(255,255,255,0.12)'}`
                          }}>
                            <button
                              onClick={() => setCurrentSunoTrack(t)}
                              data-testid={`suno-version-play-${vi}`}
                              style={{
                                flex: 1, display: 'flex', alignItems: 'center', gap: '8px',
                                background: 'transparent', border: 'none', color: '#fff',
                                fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', textAlign: 'left'
                              }}
                            >
                              {isActive && isSunoPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                              <span>{t.title}{t.duration ? ` · ${Math.floor(t.duration/60)}:${String(Math.floor(t.duration%60)).padStart(2,'0')}` : ''}</span>
                            </button>
                            <button
                              onClick={() => downloadSunoTrack(t)}
                              onTouchEnd={(e) => { e.preventDefault(); downloadSunoTrack(t); }}
                              data-testid={`suno-version-download-${vi}`}
                              style={{
                                padding: '8px 14px', background: 'linear-gradient(135deg,#22c55e,#16a34a)',
                                border: 'none', borderRadius: '8px', color: '#fff', fontSize: '0.78rem',
                                fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px',
                                flexShrink: 0, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation'
                              }}
                            >
                              <Download className="w-4 h-4" /> WAV
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Audio Player - single source of truth for Suno playback */}
                  <audio preload="metadata"
                    ref={sunoAudioRef}
                    src={currentSunoTrack.audio_url?.startsWith('/') 
                      ? `${API}${currentSunoTrack.audio_url.replace('/api', '')}` 
                      : currentSunoTrack.audio_url}
                    controls
                    autoPlay
                    style={{
                      width: '100%',
                      borderRadius: '8px',
                      outline: 'none'
                    }}
                    onPlay={() => setIsSunoPlaying(true)}
                    onPause={() => setIsSunoPlaying(false)}
                    onEnded={() => setIsSunoPlaying(false)}
                    data-testid="suno-audio-player"
                  />
                </div>
              )}
            </div>

            {/* RIGHT PANEL - LIBRARY */}
            <div 
              data-testid="suno-library"
              style={{
                background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(139, 92, 246, 0.2)',
                borderRadius: '16px',
                padding: '24px',
                maxHeight: 'calc(100vh - 150px)',
                overflow: 'auto'
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '20px'
              }}>
                <h2 style={{ 
                  color: '#a78bfa', 
                  fontSize: '1.1rem', 
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  margin: 0
                }}>
                  <Archive className="w-5 h-5" /> Your Music Library
                </h2>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {sunoTracks.length > 0 && (
                    <button
                      onClick={() => {
                        toast(`Delete ALL ${sunoTracks.length} tracks?`, {
                          description: 'This cannot be undone.',
                          duration: 8000,
                          cancel: { label: 'Cancel', onClick: () => {} },
                          action: { label: '🗑️ Delete All', onClick: async () => {
                            toast.info('🗑️ Clearing library...');
                            try {
                              // Get unique parent task IDs
                              const uniqueTaskIds = [...new Set(sunoTracks.map(t => t.parent_task_id || t.task_id))];
                              for (const taskId of uniqueTaskIds) {
                                await axios.delete(`${API}/music/delete/${taskId}`);
                              }
                              toast.success('✅ Library cleared!');
                              loadSunoTracks();
                            } catch (error) {
                              console.error('Clear error:', error);
                              toast.error('Failed to clear some tracks');
                              loadSunoTracks();
                            }
                          }}
                        });
                      }}
                      style={{
                        padding: '8px 12px',
                        background: 'rgba(239, 68, 68, 0.2)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '6px',
                        color: '#ef4444',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                      data-testid="suno-clear-all-btn"
                    >
                      🗑️ Clear All
                    </button>
                  )}
                  <button
                    onClick={loadSunoTracks}
                    style={{
                      padding: '8px 12px',
                      background: 'rgba(167, 139, 250, 0.2)',
                      border: '1px solid rgba(167, 139, 250, 0.3)',
                      borderRadius: '6px',
                      color: '#a78bfa',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Refresh
                  </button>
                </div>
              </div>

              {sunoTracks.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px',
                  color: '#666'
                }}>
                  <Music className="w-12 h-12" style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                  <p style={{ margin: 0, fontSize: '0.95rem' }}>No tracks yet</p>
                  <p style={{ margin: '8px 0 0', fontSize: '0.8rem' }}>
                    Generate your first song using the form!
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {sunoTracks.map((track, idx) => (
                    <div 
                      key={track.task_id || idx}
                      style={{
                        background: currentSunoTrack?.task_id === track.task_id 
                          ? 'rgba(236, 72, 153, 0.15)' 
                          : 'rgba(255,255,255,0.03)',
                        border: currentSunoTrack?.task_id === track.task_id 
                          ? '1px solid rgba(236, 72, 153, 0.4)' 
                          : '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '12px',
                        padding: '14px',
                        transition: 'all 0.15s'
                      }}
                      data-testid={`suno-track-${idx}`}
                    >
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '10px'
                      }}>
                        <div style={{ flex: 1 }}>
                          <h4 style={{ 
                            color: '#ec4899', 
                            fontSize: '0.9rem', 
                            fontWeight: 600,
                            margin: '0 0 4px 0'
                          }}>
                            {track.title || 'Untitled'}
                            {track.track_label && (
                              <span style={{ 
                                marginLeft: '8px', 
                                fontSize: '0.65rem', 
                                background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                color: '#fff',
                                fontWeight: 500
                              }}>
                                {track.track_label}
                              </span>
                            )}
                          </h4>
                          <p style={{ 
                            color: '#666', 
                            fontSize: '0.7rem', 
                            margin: 0,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            {track.style && <span style={{ color: '#a78bfa' }}>{track.style}</span>}
                            {track.instrumental && <span style={{ color: '#00d4ff' }}>🎹 Instrumental</span>}
                            {track.status === 'completed' && track.audio_url ? '✅' : <span style={{ color: '#ef4444' }}>⚠️ FAILED</span>}
                          </p>
                        </div>
                        {/* Action buttons - Delete ALWAYS visible, Play/Download only for completed tracks */}
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {track.status === 'completed' && track.audio_url ? (
                            <>
                              {/* Show STOP if this track is playing, otherwise show PLAY */}
                              {currentSunoTrack?.task_id === track.task_id && isSunoPlaying ? (
                                <button
                                  onClick={() => {
                                    if (sunoAudioRef.current) {
                                      sunoAudioRef.current.pause();
                                      sunoAudioRef.current.currentTime = 0;
                                    }
                                    setIsSunoPlaying(false);
                                    setCurrentSunoTrack(null);
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    background: 'rgba(239, 68, 68, 0.3)',
                                    border: '1px solid rgba(239, 68, 68, 0.5)',
                                    borderRadius: '6px',
                                    color: '#ef4444',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                  data-testid={`suno-stop-${idx}`}
                                >
                                  <Square className="w-3 h-3" /> OFF
                                </button>
                              ) : (
                                <button
                                  onClick={() => playSunoTrack(track)}
                                  onTouchEnd={(e) => { e.preventDefault(); playSunoTrack(track); }}
                                  style={{
                                    padding: '8px 12px',
                                    background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
                                    border: 'none',
                                    borderRadius: '6px',
                                    color: '#fff',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    WebkitTapHighlightColor: 'transparent',
                                    touchAction: 'manipulation'
                                  }}
                                  data-testid={`suno-play-${idx}`}
                                >
                                  <Play className="w-3 h-3" /> ON
                                </button>
                              )}
                              <button
                                onClick={() => downloadSunoTrack(track)}
                                onTouchEnd={(e) => { e.preventDefault(); downloadSunoTrack(track); }}
                                style={{
                                  padding: '8px 12px',
                                  background: 'rgba(34, 197, 94, 0.2)',
                                  border: '1px solid rgba(34, 197, 94, 0.3)',
                                  borderRadius: '6px',
                                  color: '#22c55e',
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  WebkitTapHighlightColor: 'transparent',
                                  touchAction: 'manipulation'
                                }}
                                data-testid={`suno-download-${idx}`}
                              >
                                <Download className="w-3 h-3" />
                              </button>
                              {/* DOWNLOAD PROMPT BUTTON */}
                              <button
                                onClick={() => {
                                  const promptContent = `MANNIS - SUNO AI PROMPT\n${'='.repeat(40)}\n\nTITLE: ${track.title || 'Untitled'}\n\nSTYLE:\n${track.style || 'N/A'}\n\nPROMPT/LYRICS:\n${track.prompt || 'N/A'}\n\nKEY: ${track.key || 'N/A'}\nINSTRUMENTAL: ${track.instrumental ? 'Yes' : 'No'}\n\nGenerated: ${new Date(track.created_at || Date.now()).toLocaleString()}`;
                                  shareOrDownloadText(promptContent, `${(track.title || 'SoundDNA').replace(/[^a-z0-9]/gi, '_')}-prompt.txt`, 'text/plain', { successMessage: '📋 Prompt downloaded!' });
                                }}
                                onTouchEnd={(e) => {
                                  e.preventDefault();
                                  const promptContent = `MANNIS - SUNO AI PROMPT\n${'='.repeat(40)}\n\nTITLE: ${track.title || 'Untitled'}\n\nSTYLE:\n${track.style || 'N/A'}\n\nPROMPT/LYRICS:\n${track.prompt || 'N/A'}\n\nKEY: ${track.key || 'N/A'}\nINSTRUMENTAL: ${track.instrumental ? 'Yes' : 'No'}\n\nGenerated: ${new Date(track.created_at || Date.now()).toLocaleString()}`;
                                  shareOrDownloadText(promptContent, `${(track.title || 'SoundDNA').replace(/[^a-z0-9]/gi, '_')}-prompt.txt`, 'text/plain', { successMessage: '📋 Prompt downloaded!' });
                                }}
                                style={{
                                  padding: '8px 12px',
                                  background: 'rgba(249, 115, 22, 0.2)',
                                  border: '1px solid rgba(249, 115, 22, 0.3)',
                                  borderRadius: '6px',
                                  color: '#f97316',
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  WebkitTapHighlightColor: 'transparent',
                                  touchAction: 'manipulation'
                                }}
                                data-testid={`suno-prompt-${idx}`}
                                title="Download prompt as text file"
                              >
                                <FileText className="w-3 h-3" />
                              </button>
                            </>
                          ) : (
                            <span style={{ 
                              color: '#ef4444', 
                              fontSize: '0.7rem', 
                              padding: '8px 12px',
                              background: 'rgba(239, 68, 68, 0.1)',
                              borderRadius: '6px'
                            }}>
                              Generation failed
                            </span>
                          )}
                          {/* DELETE BUTTON - Always visible */}
                          <button
                            onClick={() => deleteSunoTrack(track)}
                            onTouchEnd={(e) => {
                              e.preventDefault();
                              deleteSunoTrack(track);
                            }}
                            style={{
                              padding: '8px 12px',
                              background: 'rgba(239, 68, 68, 0.15)',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              borderRadius: '6px',
                              color: '#ef4444',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              WebkitTapHighlightColor: 'transparent',
                              touchAction: 'manipulation'
                            }}
                            data-testid={`suno-delete-${idx}`}
                            title="Delete track"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Show truncated prompt */}
                      <p style={{ 
                        color: '#555', 
                        fontSize: '0.7rem', 
                        margin: 0,
                        fontStyle: 'italic',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        "{track.prompt?.substring(0, 80)}{track.prompt?.length > 80 ? '...' : ''}"
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* ============================================ */}
      {/* MY UPLOADS - FULLSCREEN VIEW */}
      {/* ============================================ */}
      {fullscreenTool === 'my-uploads' && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(135deg, #0d0d1a 0%, #1a2e1a 50%, #162e16 100%)',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }} data-testid="my-uploads-fullscreen">
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: window.innerWidth < 768 ? '12px 16px' : '16px 24px',
            borderBottom: '1px solid rgba(16, 185, 129, 0.2)',
            background: 'rgba(0, 0, 0, 0.3)',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                onClick={() => setFullscreenTool(null)}
                style={{
                  padding: window.innerWidth < 768 ? '8px 12px' : '10px 16px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: window.innerWidth < 768 ? '0.8rem' : '0.9rem'
                }}
              >
                <X className="w-4 h-4" /> Back
              </button>
              <h2 style={{ 
                color: '#10b981', 
                fontSize: window.innerWidth < 768 ? '1.1rem' : '1.5rem', 
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Upload className={window.innerWidth < 768 ? "w-5 h-5" : "w-6 h-6"} />
                My Uploads
              </h2>
            </div>
            
            {/* Storage Status */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: window.innerWidth < 768 ? '8px' : '16px',
              color: 'rgba(255,255,255,0.7)',
              fontSize: window.innerWidth < 768 ? '0.75rem' : '0.85rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: window.innerWidth < 768 ? '60px' : '100px',
                  height: '6px',
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${storageStatus.percent_used}%`,
                    height: '100%',
                    background: storageStatus.percent_used > 80 ? '#ef4444' : '#10b981',
                    borderRadius: '3px'
                  }} />
                </div>
                <span>{storageStatus.used_mb.toFixed(1)}MB</span>
              </div>
              <button
                onClick={() => { fetchMyUploads(uploadCategory); fetchStorageStatus(); }}
                style={{
                  padding: window.innerWidth < 768 ? '6px 10px' : '8px 12px',
                  background: 'rgba(16, 185, 129, 0.2)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '6px',
                  color: '#10b981',
                  cursor: 'pointer',
                  fontSize: window.innerWidth < 768 ? '0.7rem' : '0.8rem'
                }}
              >
                Refresh
              </button>
            </div>
          </div>
          
          {/* Main Content - Stack vertically on mobile */}
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: window.innerWidth < 768 ? 'column' : 'row',
            overflow: 'hidden'
          }}>
            {/* Upload Panel */}
            <div style={{
              padding: window.innerWidth < 768 ? '16px' : '20px',
              borderRight: window.innerWidth < 768 ? 'none' : '1px solid rgba(16, 185, 129, 0.2)',
              borderBottom: window.innerWidth < 768 ? '1px solid rgba(16, 185, 129, 0.2)' : 'none',
              overflow: 'auto',
              background: 'rgba(0, 0, 0, 0.2)',
              width: window.innerWidth < 768 ? '100%' : '350px',
              minWidth: window.innerWidth < 768 ? 'auto' : '350px',
              maxHeight: window.innerWidth < 768 ? '45vh' : 'none'
            }}>
              <h3 style={{ color: '#10b981', marginBottom: '12px', fontSize: '0.95rem', fontWeight: 600 }}>
                Upload Content
              </h3>
              
              {/* Upload buttons row on mobile */}
              <div style={{ 
                display: window.innerWidth < 768 ? 'grid' : 'block',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '16px'
              }}>
                {/* Upload Audio */}
                <div style={{ marginBottom: window.innerWidth < 768 ? '0' : '16px' }}>
                  <input
                    type="file"
                    ref={uploadFileInputRef}
                    accept=".mp3,.wav,.flac,.ogg,.m4a"
                    style={{ display: 'none' }}
                    onChange={(e) => e.target.files[0] && handleAudioUpload(e.target.files[0])}
                  />
                  <button
                    onClick={() => uploadFileInputRef.current?.click()}
                    disabled={isUploading}
                    style={{
                      width: '100%',
                      padding: window.innerWidth < 768 ? '12px 8px' : '16px',
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(5, 150, 105, 0.2))',
                      border: '2px dashed rgba(16, 185, 129, 0.4)',
                      borderRadius: '10px',
                      color: '#10b981',
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      fontSize: window.innerWidth < 768 ? '0.75rem' : '0.9rem',
                      fontWeight: 600,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    data-testid="upload-audio-btn"
                  >
                    <Music className={window.innerWidth < 768 ? "w-5 h-5" : "w-6 h-6"} />
                    {isUploading ? uploadProgress : (window.innerWidth < 768 ? 'Audio' : 'Upload Audio (MP3, WAV, FLAC)')}
                  </button>
                </div>
                
                {/* Upload MIDI */}
                <div style={{ marginBottom: window.innerWidth < 768 ? '0' : '16px' }}>
                  <input
                    type="file"
                    ref={uploadMidiInputRef}
                    accept=".mid,.midi"
                    style={{ display: 'none' }}
                    onChange={(e) => e.target.files[0] && handleMidiUpload(e.target.files[0])}
                  />
                  <button
                    onClick={() => uploadMidiInputRef.current?.click()}
                    disabled={isUploading}
                    style={{
                      width: '100%',
                      padding: window.innerWidth < 768 ? '12px 8px' : '16px',
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(109, 40, 217, 0.2))',
                      border: '2px dashed rgba(139, 92, 246, 0.4)',
                      borderRadius: '10px',
                      color: '#8b5cf6',
                      cursor: isUploading ? 'not-allowed' : 'pointer',
                      fontSize: window.innerWidth < 768 ? '0.75rem' : '0.9rem',
                      fontWeight: 600,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    data-testid="upload-midi-btn"
                  >
                    <FileText className={window.innerWidth < 768 ? "w-5 h-5" : "w-6 h-6"} />
                    {window.innerWidth < 768 ? 'MIDI' : 'Upload MIDI Files'}
                  </button>
                </div>
              </div>
              
              {/* Save Style Prompt */}
              <div style={{
                padding: window.innerWidth < 768 ? '12px' : '16px',
                background: 'rgba(236, 72, 153, 0.1)',
                border: '1px solid rgba(236, 72, 153, 0.3)',
                borderRadius: '10px'
              }}>
                <h4 style={{ color: '#ec4899', marginBottom: '10px', fontSize: window.innerWidth < 768 ? '0.8rem' : '0.9rem' }}>
                  Save Style Prompt
                </h4>
                <input
                  type="text"
                  placeholder="Title"
                  value={newStylePrompt.title}
                  onChange={(e) => setNewStylePrompt(p => ({ ...p, title: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: window.innerWidth < 768 ? '8px' : '10px',
                    marginBottom: '6px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: window.innerWidth < 768 ? '0.8rem' : '0.85rem'
                  }}
                  data-testid="style-prompt-title"
                />
                <textarea
                  placeholder="Your style prompt..."
                  value={newStylePrompt.prompt}
                  onChange={(e) => setNewStylePrompt(p => ({ ...p, prompt: e.target.value }))}
                  rows={window.innerWidth < 768 ? 2 : 3}
                  style={{
                    width: '100%',
                    padding: window.innerWidth < 768 ? '8px' : '10px',
                    marginBottom: '6px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: window.innerWidth < 768 ? '0.8rem' : '0.85rem',
                    resize: 'vertical'
                  }}
                  data-testid="style-prompt-text"
                />
                <input
                  type="text"
                  placeholder="Tags (comma separated)"
                  value={newStylePrompt.tags}
                  onChange={(e) => setNewStylePrompt(p => ({ ...p, tags: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: window.innerWidth < 768 ? '8px' : '10px',
                    marginBottom: '10px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: window.innerWidth < 768 ? '0.8rem' : '0.85rem'
                  }}
                  data-testid="style-prompt-tags"
                />
                <button
                  onClick={handleSaveStylePrompt}
                  disabled={isUploading || !newStylePrompt.title || !newStylePrompt.prompt}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: newStylePrompt.title && newStylePrompt.prompt 
                      ? 'linear-gradient(135deg, #ec4899, #8b5cf6)' 
                      : 'rgba(100, 100, 100, 0.3)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: newStylePrompt.title && newStylePrompt.prompt ? 'pointer' : 'not-allowed',
                    fontWeight: 600
                  }}
                  data-testid="save-style-prompt-btn"
                >
                  Save Style Prompt
                </button>
              </div>
            </div>
            
            {/* Library Panel */}
            <div style={{ 
              padding: window.innerWidth < 768 ? '16px' : '20px', 
              overflow: 'auto',
              background: 'rgba(0, 0, 0, 0.1)',
              flex: 1
            }}>
              {/* Category Filter */}
              <div style={{ 
                display: 'flex', 
                gap: window.innerWidth < 768 ? '6px' : '8px', 
                marginBottom: window.innerWidth < 768 ? '12px' : '20px',
                flexWrap: 'wrap'
              }}>
                {['all', 'audio', 'midi', 'style_prompt'].map(cat => (
                  <button
                    key={cat}
                    onClick={() => { setUploadCategory(cat); fetchMyUploads(cat); }}
                    style={{
                      padding: window.innerWidth < 768 ? '6px 12px' : '8px 16px',
                      background: uploadCategory === cat 
                        ? 'linear-gradient(135deg, #10b981, #059669)' 
                        : 'rgba(255, 255, 255, 0.1)',
                      border: uploadCategory === cat 
                        ? 'none' 
                        : '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '16px',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: window.innerWidth < 768 ? '0.7rem' : '0.8rem',
                      fontWeight: uploadCategory === cat ? 600 : 400
                    }}
                  >
                    {cat === 'all' ? 'All' : cat === 'style_prompt' ? 'Prompts' : cat.toUpperCase()}
                  </button>
                ))}
              </div>
              
              {/* Uploads Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: window.innerWidth < 768 ? '12px' : '16px'
              }}>
                {myUploads.length === 0 ? (
                  <div style={{
                    gridColumn: '1 / -1',
                    textAlign: 'center',
                    padding: window.innerWidth < 768 ? '30px 16px' : '60px 20px',
                    color: 'rgba(255,255,255,0.5)'
                  }}>
                    <Upload className={window.innerWidth < 768 ? "w-8 h-8 mx-auto mb-3 opacity-30" : "w-12 h-12 mx-auto mb-4 opacity-30"} />
                    <p style={{ fontSize: window.innerWidth < 768 ? '0.95rem' : '1.1rem', marginBottom: '6px' }}>No uploads yet</p>
                    <p style={{ fontSize: window.innerWidth < 768 ? '0.75rem' : '0.85rem' }}>Upload audio, MIDI, or save style prompts</p>
                  </div>
                ) : (
                  myUploads.map((upload, idx) => (
                    <div
                      key={upload.file_id || idx}
                      style={{
                        padding: '16px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px'
                      }}
                      data-testid={`upload-item-${idx}`}
                    >
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        marginBottom: '8px'
                      }}>
                        <h4 style={{ 
                          color: '#fff', 
                          fontSize: '0.95rem', 
                          fontWeight: 600,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {upload.title || upload.filename || 'Untitled'}
                        </h4>
                        <span style={{
                          padding: '2px 8px',
                          background: upload.upload_type === 'audio' ? 'rgba(16, 185, 129, 0.2)' :
                                     upload.upload_type === 'midi' ? 'rgba(139, 92, 246, 0.2)' :
                                     'rgba(236, 72, 153, 0.2)',
                          color: upload.upload_type === 'audio' ? '#10b981' :
                                upload.upload_type === 'midi' ? '#8b5cf6' :
                                '#ec4899',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 600
                        }}>
                          {upload.upload_type?.toUpperCase() || 'FILE'}
                        </span>
                      </div>
                      
                      {/* Show prompt for style_prompt type */}
                      {upload.upload_type === 'style_prompt' && upload.prompt && (
                        <p style={{
                          color: 'rgba(255,255,255,0.6)',
                          fontSize: '0.8rem',
                          marginBottom: '12px',
                          lineHeight: 1.4,
                          maxHeight: '60px',
                          overflow: 'hidden'
                        }}>
                          {upload.prompt}
                        </p>
                      )}
                      
                      {/* Audio player for audio type */}
                      {upload.upload_type === 'audio' && upload.file_url && (
                        <audio preload="metadata"
                          controls
                          src={`${API.replace('/api', '')}${upload.file_url}`}
                          style={{ width: '100%', height: '32px', marginBottom: '12px' }}
                        />
                      )}
                      
                      {/* File info */}
                      {upload.file_size && (
                        <p style={{ 
                          color: 'rgba(255,255,255,0.4)', 
                          fontSize: '0.75rem',
                          marginBottom: '12px'
                        }}>
                          {(upload.file_size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                      )}
                      
                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {upload.upload_type === 'audio' && (
                          <button
                            onClick={() => handleAnalyzeAudio(upload.file_id)}
                            style={{
                              padding: '6px 12px',
                              background: 'rgba(0, 212, 255, 0.2)',
                              border: '1px solid rgba(0, 212, 255, 0.3)',
                              borderRadius: '6px',
                              color: '#00d4ff',
                              cursor: 'pointer',
                              fontSize: '0.75rem'
                            }}
                          >
                            Analyze → Prompt
                          </button>
                        )}
                        
                        {upload.upload_type === 'style_prompt' && (
                          <button
                            onClick={() => {
                              setSunoStyle(upload.prompt);
                              setFullscreenTool('suno-music');
                              toast.success('Style loaded in Suno!');
                            }}
                            style={{
                              padding: '6px 12px',
                              background: 'rgba(236, 72, 153, 0.2)',
                              border: '1px solid rgba(236, 72, 153, 0.3)',
                              borderRadius: '6px',
                              color: '#ec4899',
                              cursor: 'pointer',
                              fontSize: '0.75rem'
                            }}
                          >
                            Use in Suno
                          </button>
                        )}
                        
                        {upload.file_url && (
                          <a
                            href={`${API.replace('/api', '')}${upload.file_url}${upload.file_url.includes('?') ? '&' : '?'}download=1`}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="upload-download-link"
                            style={{
                              padding: '6px 12px',
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '6px',
                              color: '#fff',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              textDecoration: 'none'
                            }}
                          >
                            Download
                          </a>
                        )}
                        
                        <button
                          onClick={() => handleDeleteUpload(upload.file_id || upload._id)}
                          style={{
                            padding: '6px 12px',
                            background: 'rgba(239, 68, 68, 0.2)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '6px',
                            color: '#ef4444',
                            cursor: 'pointer',
                            fontSize: '0.75rem'
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Onboarding Walkthrough Modal */}
      {showOnboarding && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #0d1220 0%, #1a2332 100%)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '24px',
            padding: '40px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            {/* Close Button */}
            <button
              onClick={() => {
                setShowOnboarding(false);
                localStorage.setItem('sounddna_onboarding_complete', 'true');
              }}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: '1.5rem',
                cursor: 'pointer'
              }}
            >
              ×
            </button>

            {/* Step Content */}
            {onboardingStep === 0 && (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 24px',
                  background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 40px rgba(0, 212, 255, 0.4)'
                }}>
                  <Dna className="w-10 h-10" style={{ color: '#fff' }} />
                </div>
                <h2 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '16px', background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Welcome to MANNIS PRO
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', lineHeight: 1.6, marginBottom: '32px' }}>
                  The AI-powered music production suite for Suno, Udio, and other music AI platforms. Create complete songs with one click!
                </p>
              </div>
            )}

            {onboardingStep === 1 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #00d4ff, #0099cc)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Upload className="w-6 h-6" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>1. Drop Your Tracks</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Analyze any audio file</p>
                  </div>
                </div>
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '20px' }}>
                  Upload MP3, WAV, FLAC, or OGG files to extract BPM, key, and mood. MANNIS will analyze your track and generate matching style prompts you can use with Suno or Udio.
                </p>
                <div style={{ background: 'rgba(0, 212, 255, 0.1)', border: '1px solid rgba(0, 212, 255, 0.2)', borderRadius: '12px', padding: '16px' }}>
                  <p style={{ color: '#00d4ff', fontSize: '0.85rem', fontWeight: 600 }}>💡 Pro Tip:</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Upload up to 10 files at once for batch analysis!</p>
                </div>
              </div>
            )}

            {onboardingStep === 2 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Star className="w-6 h-6" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>2. Discover Presets</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>500+ ready-to-use style prompts</p>
                  </div>
                </div>
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '20px' }}>
                  Browse our library of 500+ ATL Trap style prompts, professionally crafted for immediate use. Click any preset to load it into your clipboard, then paste directly into Suno or Udio.
                </p>
                <div style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.2)', borderRadius: '12px', padding: '16px' }}>
                  <p style={{ color: '#8b5cf6', fontSize: '0.85rem', fontWeight: 600 }}>🎵 Featured:</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>70,000+ Style Prompts Library available!</p>
                </div>
              </div>
            )}

            {onboardingStep === 3 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #06b6d4, #a855f7)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Library className="w-6 h-6" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>3. My MANNIS Library</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Every prompt, song, drum & stem in one place</p>
                  </div>
                </div>
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '20px' }}>
                  Everything you make auto-saves to My MANNIS Library — organised into Prompts, Songs, Drums, and Stems tabs. Search by genre, star your keepers with Favorites, and act on any item with four buttons: Copy Prompt, Download, Audio Play (listen inline), and MIDI (build a 32-bar arrangement). Done with something? Hit Erase to remove it.
                </p>
                <div style={{ background: 'rgba(6, 182, 212, 0.1)', border: '1px solid rgba(6, 182, 212, 0.2)', borderRadius: '12px', padding: '16px' }}>
                  <p style={{ color: '#06b6d4', fontSize: '0.85rem', fontWeight: 600 }}>📚 How to use:</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Open My MANNIS from the sidebar • Pick a tab • Copy Prompt · Download · Audio Play · MIDI · Erase on every row</p>
                </div>
              </div>
            )}

            {onboardingStep === 4 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #ec4899, #be185d)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Mic2 className="w-6 h-6" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>4. Rhyme & Flow Engine</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>AI-powered lyrics generation</p>
                  </div>
                </div>
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '20px' }}>
                  Generate lyrics with our AI Rhyme & Flow Engine — customizable flow patterns, rhyme schemes, syllable counts, and song length. Browse Voice Collections for reference vocal styles to pair with your track.
                </p>
                <div style={{ background: 'rgba(236, 72, 153, 0.1)', border: '1px solid rgba(236, 72, 153, 0.2)', borderRadius: '12px', padding: '16px' }}>
                  <p style={{ color: '#ec4899', fontSize: '0.85rem', fontWeight: 600 }}>✨ Features:</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>15+ rhyme styles • 16 flow patterns • 8-32 bars • 32 world languages</p>
                </div>
              </div>
            )}

            {onboardingStep === 5 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #f97316, #ea580c)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Radio className="w-6 h-6" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>5. Music Link Analyzer</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Analyze any track by URL</p>
                  </div>
                </div>
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '20px' }}>
                  Paste any music link to analyze the track's characteristics. Get BPM, key, mood detection, and AI-generated "Sound Like" prompts to recreate similar vibes.
                </p>
                <div style={{ background: 'rgba(249, 115, 22, 0.1)', border: '1px solid rgba(249, 115, 22, 0.2)', borderRadius: '12px', padding: '16px' }}>
                  <p style={{ color: '#f97316', fontSize: '0.85rem', fontWeight: 600 }}>🔗 Supported:</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Works with most music streaming links!</p>
                </div>
              </div>
            )}

            {onboardingStep === 6 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #a855f7, #7c3aed)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Headphones className="w-6 h-6" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>6. Audio Preview</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Hear styles before creating</p>
                  </div>
                </div>
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '20px' }}>
                  Every style prompt now has an Audio Preview button! Click the purple headphone icon next to any prompt to hear a 10-second AI-generated preview of how that style sounds. This helps you choose the perfect style before creating your full song.
                </p>
                <div style={{ background: 'rgba(168, 85, 247, 0.1)', border: '1px solid rgba(168, 85, 247, 0.2)', borderRadius: '12px', padding: '16px' }}>
                  <p style={{ color: '#a855f7', fontSize: '0.85rem', fontWeight: 600 }}>🎧 How to use:</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Click the purple 🎧 button on any prompt in 70K Library, DNA Presets, or My Library!</p>
                </div>
              </div>
            )}

            {onboardingStep === 7 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #10b981, #059669)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Music className="w-6 h-6" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>7. MIDI Files</h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>32-bar arrangements for your DAW</p>
                  </div>
                </div>
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '20px' }}>
                  Convert any style prompt into a full 32-bar MIDI arrangement! Click the green Download MIDI button on any prompt to generate a professional MIDI file with 10 pro stems: split Drums, 808 Bass, Sub Bass, Chords, Pads, Lead Melody, Counter Arp, Strings & Brass, and FX & Cymbals. Import directly into your DAW for further production.
                </p>
                <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '12px', padding: '16px' }}>
                  <p style={{ color: '#10b981', fontSize: '0.85rem', fontWeight: 600 }}>🎹 Features:</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>4 tracks (Drums, Bass, Chords, Melody) • 32 bars • Matches BPM/Key • Works with any DAW!</p>
                </div>
              </div>
            )}

            {onboardingStep === 8 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #ec4899, #00d4ff)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Dna className="w-6 h-6" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>8. MANNIS Fusion Workbench <span style={{ fontSize: '0.7rem', background: '#ec4899', padding: '2px 8px', borderRadius: '10px', verticalAlign: 'middle' }}>NEW</span></h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Blend any two of 166 genres</p>
                  </div>
                </div>
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '20px' }}>
                  Create music no one has heard before! Pick Genre A for the chords and Genre B for the rhythm & vocals — like Salsa × Neuro DnB or Bossa Nova × UK Drill. 166 genre genomes give you 27,000+ possible fusions, plus 30 hand-curated experimental blends ready to play.
                </p>
                <div style={{ background: 'rgba(236, 72, 153, 0.1)', border: '1px solid rgba(236, 72, 153, 0.2)', borderRadius: '12px', padding: '16px' }}>
                  <p style={{ color: '#ec4899', fontSize: '0.85rem', fontWeight: 600 }}>🧬 How to use:</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Find the glowing MANNIS FUSION WORKBENCH at the top of Create • Pick two genres • Hit FUSE DNA — or tap Surprise Me!</p>
                </div>
              </div>
            )}

            {onboardingStep === 9 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                  <div style={{ width: '50px', height: '50px', background: 'linear-gradient(135deg, #f59e0b, #ef4444)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Zap className="w-6 h-6" style={{ color: '#fff' }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#fff' }}>9. 1-Click Create & Studio Mastering <span style={{ fontSize: '0.7rem', background: '#f59e0b', padding: '2px 8px', borderRadius: '10px', verticalAlign: 'middle' }}>NEW</span></h3>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Full songs on Suno v5.5, broadcast-ready</p>
                  </div>
                </div>
                <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '20px' }}>
                  Generate complete songs with one click on the Suno v5.5 engine. Every WAV export is automatically mastered with our broadcast-ready club preset, and every generation gets a Harmonic Uniqueness Safety Badge — an originality score with copyright insights so your music is verifiably yours. Now with lyrics in 32 world languages including Hindi and Punjabi!
                </p>
                <div style={{ background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)', borderRadius: '12px', padding: '16px' }}>
                  <p style={{ color: '#f59e0b', fontSize: '0.85rem', fontWeight: 600 }}>⚡ Included:</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Suno v5.5 engine • Broadcast-ready WAV mastering • Safety Badge originality score • Auto-save to My Library</p>
                </div>
              </div>
            )}

            {onboardingStep === 10 && (
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 24px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 0 40px rgba(16, 185, 129, 0.4)'
                }}>
                  <CheckCircle className="w-10 h-10" style={{ color: '#fff' }} />
                </div>
                <h2 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '16px', color: '#10b981' }}>
                  You're Ready!
                </h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '1rem', lineHeight: 1.6, marginBottom: '24px' }}>
                  Start creating amazing AI music with genre fusions, professional style prompts, audio previews, and MIDI files!
                </p>
                <div style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                  <p style={{ color: '#10b981', fontSize: '0.9rem', fontWeight: 600, marginBottom: '12px' }}>Quick Start:</p>
                  <ol style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', textAlign: 'left', paddingLeft: '20px', lineHeight: 1.8 }}>
                    <li>Fuse two genres in the MANNIS Fusion Workbench</li>
                    <li>Browse 70K Style Prompts or DNA Presets</li>
                    <li>Click 🎧 to preview how a style sounds</li>
                    <li>1-Click Create your song — mastered & saved to My Library!</li>
                  </ol>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '32px' }}>
              <button
                onClick={() => setOnboardingStep(Math.max(0, onboardingStep - 1))}
                disabled={onboardingStep === 0}
                style={{
                  padding: '10px 20px',
                  background: 'transparent',
                  border: '1px solid var(--border)',
                  borderRadius: '10px',
                  color: onboardingStep === 0 ? 'var(--text-muted)' : 'var(--text-primary)',
                  cursor: onboardingStep === 0 ? 'not-allowed' : 'pointer',
                  opacity: onboardingStep === 0 ? 0.5 : 1
                }}
              >
                Back
              </button>
              
              {/* Step Indicators */}
              <div style={{ display: 'flex', gap: '8px' }}>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(step => (
                  <div
                    key={step}
                    style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: step === onboardingStep ? '#00d4ff' : 'var(--border)',
                      transition: 'all 0.2s'
                    }}
                  />
                ))}
              </div>
              
              <button
                onClick={() => {
                  if (onboardingStep === 10) {
                    setShowOnboarding(false);
                    localStorage.setItem('sounddna_onboarding_complete', 'true');
                  } else {
                    setOnboardingStep(onboardingStep + 1);
                  }
                }}
                style={{
                  padding: '10px 24px',
                  background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)'
                }}
              >
                {onboardingStep === 10 ? "Get Started!" : "Next"}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modals */}
      <PricingModal isOpen={showPricing} onClose={() => setShowPricing(false)} currentPlan={subscription.plan} userEmail={userEmail} setUserEmail={setUserEmail} currentUser={currentUser} onLoginRequired={() => { setShowPricing(false); setShowAuthModal(true); }} />
      
      <SubscriptionManagementModal 
        isOpen={showSubscriptionManagement} 
        onClose={() => setShowSubscriptionManagement(false)} 
        currentUser={currentUser}
        onPlanChange={() => {
          // Refresh user data after plan change
          if (currentUser) {
            axios.get(`${API}/auth/me`, { withCredentials: true })
              .then(res => {
                setCurrentUser(res.data);
                setSubscription({ plan: res.data.subscription, ...res.data.subscription_limits });
              })
              .catch(err => console.error('Failed to refresh user data:', err));
          }
        }}
      />
      
      {/* Token Exhausted Modal */}
      {showTokenExhausted && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setShowTokenExhausted(false)} data-testid="token-exhausted-modal">
          <div style={{ background: '#12121e', border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: '16px', padding: '32px', width: '92%', maxWidth: '420px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>⚡</div>
            <h3 style={{ margin: '0 0 10px 0', color: '#fff', fontSize: '1.25rem' }}>Monthly tokens exhausted</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.9rem', marginBottom: '22px', lineHeight: 1.5 }}>
              Upgrade your plan or buy a top-up pack to continue. A top-up of <strong style={{ color: '#f59e0b' }}>100 tokens is $10</strong> — they never expire and are used automatically.
            </p>
            <button
              onClick={() => { setShowTokenExhausted(false); handleBuyTokens(); }}
              style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #f59e0b, #f97316)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', marginBottom: '10px' }}
              data-testid="token-exhausted-buy-btn"
            >
              ⚡ Buy 100 Tokens — $10
            </button>
            <button
              onClick={() => { setShowTokenExhausted(false); setShowPricing(true); }}
              style={{ width: '100%', padding: '12px', background: 'transparent', border: '1px solid rgba(139, 92, 246, 0.5)', borderRadius: '10px', color: '#8b5cf6', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', marginBottom: '10px' }}
              data-testid="token-exhausted-upgrade-btn"
            >
              Upgrade my plan instead
            </button>
            <button
              onClick={() => setShowTokenExhausted(false)}
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem', cursor: 'pointer' }}
              data-testid="token-exhausted-close-btn"
            >
              Maybe later
            </button>
          </div>
        </div>
      )}

      {/* Chrome Extension Modal */}
      {showExtensionModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setShowExtensionModal(false)} data-testid="extension-modal">
          <div style={{ background: '#12121e', border: '1px solid rgba(0, 212, 255, 0.35)', borderRadius: '16px', padding: '28px', width: '92%', maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.2rem' }}>⚡ MANNIS Chrome Extension</h3>
              <button onClick={() => setShowExtensionModal(false)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '1.3rem', cursor: 'pointer' }} data-testid="extension-modal-close">×</button>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.88rem', lineHeight: 1.5, marginBottom: '16px' }}>
              Auto-fill Suno and Udio style fields with your saved MANNIS prompts — plus search the full 70K library right from your browser toolbar.
            </p>
            <a
              href={`${API}/extension/download`}
              download
              style={{ display: 'block', textAlign: 'center', padding: '12px', background: 'linear-gradient(135deg, #00d4ff, #0099cc)', borderRadius: '10px', color: '#fff', fontWeight: 700, textDecoration: 'none', marginBottom: '18px' }}
              data-testid="extension-download-btn"
            >
              ⬇ Download Extension (.zip)
            </a>
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '14px 16px' }}>
              <div style={{ color: '#00d4ff', fontWeight: 700, fontSize: '0.8rem', marginBottom: '8px' }}>HOW TO INSTALL (Chrome / Edge / Brave)</div>
              {['Unzip the downloaded file', 'Open chrome://extensions in your browser', 'Turn ON "Developer mode" (top-right)', 'Click "Load unpacked" and select the unzipped folder', 'Pin the MANNIS icon, enter your email, and start filling!'].map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem', marginBottom: '6px' }}>
                  <span style={{ color: '#00d4ff', fontWeight: 700 }}>{i + 1}.</span> {step}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Token History Modal */}
      {showTokenHistory && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setShowTokenHistory(false)} data-testid="token-history-modal">
          <div style={{ background: '#12121e', border: '1px solid rgba(0, 212, 255, 0.35)', borderRadius: '16px', padding: '28px', width: '92%', maxWidth: '540px', maxHeight: '82vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.2rem' }}>🧾 Token History</h3>
              <button onClick={() => setShowTokenHistory(false)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '1.3rem', cursor: 'pointer' }} data-testid="token-history-close-btn">×</button>
            </div>
            {thLoading ? (
              <div style={{ padding: '40px 0', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }} data-testid="token-history-loading">Loading your history...</div>
            ) : tokenHistoryData ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <span style={{ padding: '4px 12px', background: 'rgba(245, 158, 11, 0.15)', border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: '10px', fontSize: '0.8rem', fontWeight: 700, color: '#f59e0b' }} data-testid="token-history-balance">
                    ⚡ {tokenHistoryData.token_balance || 0} extra tokens available
                  </span>
                  <button
                    onClick={() => { setShowTokenHistory(false); handleBuyTokens(); }}
                    style={{ padding: '4px 12px', background: 'linear-gradient(135deg, #f59e0b, #f97316)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}
                    data-testid="token-history-buy-btn"
                  >
                    + Buy 100 for $10
                  </button>
                </div>
                <div style={{ overflowY: 'auto', flex: 1 }}>
                  {(tokenHistoryData.purchases || []).length > 0 && (
                    <>
                      <div style={{ color: '#00d4ff', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Purchases</div>
                      {tokenHistoryData.purchases.map((p, i) => (
                        <div key={`p-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: 'rgba(0, 212, 255, 0.06)', border: '1px solid rgba(0, 212, 255, 0.15)', borderRadius: '10px', marginBottom: '6px' }} data-testid={`token-purchase-row-${i}`}>
                          <div>
                            <div style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}>
                              {p.purchase_type === 'tokens' ? `${p.tokens || 100} Token Top-Up` : `${(p.package_id || 'Plan').charAt(0).toUpperCase()}${(p.package_id || 'plan').slice(1)} Plan`}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem' }}>{p.paid_at ? new Date(p.paid_at).toLocaleString() : ''}</div>
                          </div>
                          <span style={{ color: '#22c55e', fontWeight: 700, fontSize: '0.85rem' }}>${Number(p.amount || 0).toFixed(2)}</span>
                        </div>
                      ))}
                    </>
                  )}
                  <div style={{ color: '#8b5cf6', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '12px 0 8px' }}>Token Activity</div>
                  {(tokenHistoryData.ledger || []).length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem' }} data-testid="token-history-empty">
                      No token activity yet. Extra tokens are used automatically when your monthly allowance runs out.
                    </div>
                  ) : (
                    tokenHistoryData.ledger.map((e, i) => (
                      <div key={`l-${i}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', marginBottom: '5px' }} data-testid={`token-ledger-row-${i}`}>
                        <div>
                          <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.82rem' }}>{e.description}</div>
                          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</div>
                        </div>
                        <span style={{ color: e.amount > 0 ? '#22c55e' : '#ef4444', fontWeight: 700, fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                          {e.amount > 0 ? `+${e.amount}` : e.amount} ⚡
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Auto Pipeline (Unified Workflow) Modal */}
      {showAutoWorkflow && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }} onClick={() => !awRunning && closeAutoWorkflow()} data-testid="auto-workflow-modal">
          <div style={{ background: '#12121e', border: '1px solid rgba(245, 158, 11, 0.4)', borderRadius: '16px', padding: '28px', width: '92%', maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.15rem' }}>⚡ Auto Pipeline</h3>
              <button onClick={closeAutoWorkflow} style={{ background: 'none', border: 'none', color: '#888', fontSize: '1.3rem', cursor: 'pointer' }} data-testid="auto-workflow-close-btn">×</button>
            </div>

            {!awTask && !awRunning && (
              <>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', marginBottom: '14px', lineHeight: 1.5 }}>
                  One click runs the full chain: <strong style={{ color: '#f59e0b' }}>Analyze → Generate → Master → Save to Library</strong>. Paste a YouTube link, or leave it empty to use your current style prompt.
                </p>
                <input
                  type="text"
                  placeholder="YouTube link (optional)"
                  value={awUrl}
                  onChange={(e) => setAwUrl(e.target.value)}
                  style={{ width: '100%', padding: '12px', marginBottom: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  data-testid="auto-workflow-url-input"
                />
                {(workflowPrompt.stylePrompt || '').trim() && !awUrl.trim() && (
                  <div style={{ fontSize: '0.75rem', color: '#22c55e', marginBottom: '10px' }}>
                    Using current style prompt: "{workflowPrompt.stylePrompt.slice(0, 70)}..."
                  </div>
                )}
                <button
                  onClick={startAutoWorkflow}
                  style={{ width: '100%', padding: '14px', background: 'linear-gradient(135deg, #f59e0b, #ef4444)', border: 'none', borderRadius: '10px', color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: 'pointer' }}
                  data-testid="auto-workflow-run-btn"
                >
                  ▶ Run Full Workflow
                </button>
              </>
            )}

            {(awRunning || awTask) && (
              <div>
                {[
                  { key: 'analyze', label: '1. Analyze — extract the MANNIS' },
                  { key: 'generate', label: '2. Generate — style prompts + song preview' },
                  { key: 'master', label: '3. Master — pro mastering chain' },
                  { key: 'library', label: '4. Library — save everything' }
                ].map(step => {
                  const state = awTask?.steps?.[step.key] || 'pending';
                  return (
                    <div key={step.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', marginBottom: '6px', background: state === 'done' ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.03)', borderRadius: '8px', border: `1px solid ${state === 'running' ? 'rgba(245,158,11,0.5)' : 'rgba(255,255,255,0.06)'}` }} data-testid={`workflow-step-${step.key}`}>
                      <span style={{ fontSize: '1rem' }}>
                        {state === 'done' ? '✅' : state === 'running' ? '⏳' : '○'}
                      </span>
                      <span style={{ color: state === 'done' ? '#22c55e' : state === 'running' ? '#f59e0b' : '#888', fontSize: '0.85rem', fontWeight: state === 'running' ? 700 : 500 }}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}

                {awTask?.status === 'completed' && (
                  <div style={{ marginTop: '14px', textAlign: 'center' }} data-testid="workflow-success">
                    {awTask.result?.generation_warning && (
                      <div data-testid="workflow-generation-warning" style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.45)', borderRadius: '8px', padding: '10px 12px', marginBottom: '10px', color: '#f87171', fontSize: '0.78rem', lineHeight: 1.5, textAlign: 'left' }}>
                        ⚠️ {awTask.result.generation_warning}
                      </div>
                    )}
                    <div style={{ color: '#22c55e', fontWeight: 700, fontSize: '1.05rem', marginBottom: '12px' }}>🎉 Ready to Download!</div>
                    <button
                      onClick={() => saveWavFromUrl(awTask.result.mastered_url, `${(awTask.result.library_title || 'sound-dna').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 40)}-mastered.wav`)}
                      style={{ display: 'block', width: '100%', padding: '12px', background: 'linear-gradient(135deg, #22c55e, #059669)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', marginBottom: '8px' }}
                      data-testid="workflow-download-mastered"
                    >
                      ⬇ Download Mastered WAV
                    </button>
                    <button
                      onClick={() => saveWavFromUrl(awTask.result.preview_url, `${(awTask.result.library_title || 'sound-dna').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 40)}-preview.wav`)}
                      style={{ display: 'block', width: '100%', padding: '10px', background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.4)', borderRadius: '8px', color: '#00d4ff', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', marginBottom: '8px' }}
                      data-testid="workflow-download-preview"
                    >
                      ⬇ Download Preview WAV
                    </button>
                    <button
                      onClick={() => { navigator.clipboard.writeText(awTask.result.style_prompt || ''); toast.success('Style prompt copied!'); }}
                      style={{ width: '100%', padding: '10px', background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.4)', borderRadius: '8px', color: '#8b5cf6', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                      data-testid="workflow-copy-prompt"
                    >
                      📋 Copy Style Prompt
                    </button>
                    <div style={{ marginTop: '10px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                      Saved to your library as "{awTask.result.library_title}"
                    </div>
                  </div>
                )}

                {awTask?.status === 'failed' && (
                  <div style={{ marginTop: '12px', color: '#ef4444', fontSize: '0.85rem', textAlign: 'center' }} data-testid="workflow-error">
                    {awTask.error || 'Workflow failed'} — <button onClick={() => { setAwTask(null); }} style={{ background: 'none', border: 'none', color: '#00d4ff', cursor: 'pointer', textDecoration: 'underline' }}>try again</button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }} onClick={() => setShowForgotPassword(false)} data-testid="forgot-password-modal">
          <div style={{ background: '#12121e', border: '1px solid rgba(0, 212, 255, 0.3)', borderRadius: '16px', padding: '28px', width: '92%', maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.15rem' }}>🔑 Reset Password</h3>
              <button onClick={() => setShowForgotPassword(false)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '1.3rem', cursor: 'pointer' }} data-testid="forgot-password-close-btn">×</button>
            </div>
            {fpSent ? (
              <div style={{ textAlign: 'center' }} data-testid="forgot-password-sent">
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>📬</div>
                <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                  If an account exists for <strong style={{ color: '#00d4ff' }}>{fpEmail}</strong>, a reset link is on its way. Check your inbox (and spam folder) — the link expires in 1 hour.
                </p>
                <button onClick={() => setShowForgotPassword(false)} style={{ marginTop: '14px', padding: '10px 24px', background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 600, cursor: 'pointer' }} data-testid="forgot-password-done-btn">Got it</button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword}>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', marginBottom: '14px' }}>
                  Enter your account email and we'll send you a link to reset your password.
                </p>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={fpEmail}
                  onChange={(e) => setFpEmail(e.target.value)}
                  required
                  style={{ width: '100%', padding: '12px', marginBottom: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', boxSizing: 'border-box' }}
                  data-testid="forgot-password-email-input"
                />
                <button
                  type="submit"
                  disabled={fpLoading}
                  style={{ width: '100%', padding: '12px', background: fpLoading ? 'rgba(0,212,255,0.3)' : 'linear-gradient(135deg, #00d4ff, #8b5cf6)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '0.95rem', fontWeight: 600, cursor: fpLoading ? 'wait' : 'pointer' }}
                  data-testid="forgot-password-submit-btn"
                >
                  {fpLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Reset Password Modal (opened via emailed link) */}
      {showResetPassword && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10001 }} data-testid="reset-password-modal">
          <div style={{ background: '#12121e', border: '1px solid rgba(0, 212, 255, 0.3)', borderRadius: '16px', padding: '28px', width: '92%', maxWidth: '400px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.15rem' }}>🔐 Choose a New Password</h3>
              <button onClick={() => { setShowResetPassword(false); window.history.replaceState({}, '', window.location.pathname); }} style={{ background: 'none', border: 'none', color: '#888', fontSize: '1.3rem', cursor: 'pointer' }} data-testid="reset-password-close-btn">×</button>
            </div>
            <form onSubmit={handleResetPassword}>
              <input
                type="password"
                placeholder="New password (min 8 characters)"
                value={rpNew}
                onChange={(e) => setRpNew(e.target.value)}
                required
                autoComplete="new-password"
                style={{ width: '100%', padding: '12px', marginBottom: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', boxSizing: 'border-box' }}
                data-testid="reset-password-new-input"
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={rpConfirm}
                onChange={(e) => setRpConfirm(e.target.value)}
                required
                autoComplete="new-password"
                style={{ width: '100%', padding: '12px', marginBottom: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', boxSizing: 'border-box' }}
                data-testid="reset-password-confirm-input"
              />
              {rpError && (
                <div style={{ color: '#ef4444', fontSize: '0.8rem', marginBottom: '12px' }} data-testid="reset-password-error">{rpError}</div>
              )}
              <button
                type="submit"
                disabled={rpLoading}
                style={{ width: '100%', padding: '12px', background: rpLoading ? 'rgba(0,212,255,0.3)' : 'linear-gradient(135deg, #00d4ff, #8b5cf6)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '0.95rem', fontWeight: 600, cursor: rpLoading ? 'wait' : 'pointer' }}
                data-testid="reset-password-submit-btn"
              >
                {rpLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePassword && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }} onClick={() => setShowChangePassword(false)} data-testid="change-password-modal">
          <div style={{ background: '#12121e', border: '1px solid rgba(0, 212, 255, 0.3)', borderRadius: '16px', padding: '28px', width: '92%', maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.15rem' }}>🔑 Change Password</h3>
              <button onClick={() => setShowChangePassword(false)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '1.3rem', cursor: 'pointer' }} data-testid="change-password-close-btn">×</button>
            </div>
            <form onSubmit={handleChangePassword}>
              <input
                type="password"
                placeholder="Current password"
                value={cpCurrent}
                onChange={(e) => setCpCurrent(e.target.value)}
                required
                autoComplete="current-password"
                style={{ width: '100%', padding: '12px', marginBottom: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', boxSizing: 'border-box' }}
                data-testid="current-password-input"
              />
              <input
                type="password"
                placeholder="New password (min 8 characters)"
                value={cpNew}
                onChange={(e) => setCpNew(e.target.value)}
                required
                autoComplete="new-password"
                style={{ width: '100%', padding: '12px', marginBottom: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', boxSizing: 'border-box' }}
                data-testid="new-password-input"
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={cpConfirm}
                onChange={(e) => setCpConfirm(e.target.value)}
                required
                autoComplete="new-password"
                style={{ width: '100%', padding: '12px', marginBottom: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', color: '#fff', fontSize: '0.9rem', boxSizing: 'border-box' }}
                data-testid="confirm-password-input"
              />
              {cpError && (
                <div style={{ color: '#ef4444', fontSize: '0.8rem', marginBottom: '12px' }} data-testid="change-password-error">{cpError}</div>
              )}
              <button
                type="submit"
                disabled={cpLoading}
                style={{ width: '100%', padding: '12px', background: cpLoading ? 'rgba(0,212,255,0.3)' : 'linear-gradient(135deg, #00d4ff, #8b5cf6)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '0.95rem', fontWeight: 600, cursor: cpLoading ? 'wait' : 'pointer' }}
                data-testid="change-password-submit-btn"
              >
                {cpLoading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Auth Modal - Login/Register */}
      {showAuthModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '420px',
            width: '90%',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            boxShadow: '0 0 50px rgba(0, 212, 255, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
                {authMode === 'login' ? '🔐 Login' : '🎵 Create Account'}
              </h2>
              <button onClick={() => setShowAuthModal(false)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
            </div>
            
            {authError && (
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#ef4444', fontSize: '0.85rem' }}>
                {authError}
              </div>
            )}
            
            <form onSubmit={authMode === 'login' ? handleLogin : handleRegister}>
              {authMode === 'register' && !verificationSent && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', color: '#888', fontSize: '0.8rem', marginBottom: '6px' }}>Name</label>
                  <input
                    type="text"
                    value={registerName}
                    onChange={(e) => setRegisterName(e.target.value)}
                    placeholder="Your name"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              )}
              
              {/* Show verification code input when code has been sent */}
              {authMode === 'register' && verificationSent && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ 
                    padding: '12px', 
                    background: 'rgba(16, 185, 129, 0.15)', 
                    borderRadius: '10px', 
                    marginBottom: '16px',
                    border: '1px solid rgba(16, 185, 129, 0.3)'
                  }}>
                    <p style={{ color: '#10b981', fontSize: '0.85rem', margin: 0 }}>
                      ✓ Verification code sent to <strong>{loginEmail}</strong>
                    </p>
                    {verificationHint && (
                      <p style={{ color: '#f59e0b', fontSize: '0.8rem', margin: '8px 0 0 0' }}>
                        Test Code: <strong>{verificationHint}</strong>
                      </p>
                    )}
                  </div>
                  <label style={{ display: 'block', color: '#888', fontSize: '0.8rem', marginBottom: '6px' }}>
                    Enter 6-digit verification code
                  </label>
                  <input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    style={{
                      width: '100%',
                      padding: '16px',
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      textAlign: 'center',
                      letterSpacing: '8px'
                    }}
                  />
                </div>
              )}
              
              {(!verificationSent || authMode === 'login') && (
                <>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', color: '#888', fontSize: '0.8rem', marginBottom: '6px' }}>Email</label>
                    <input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '10px',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', color: '#888', fontSize: '0.8rem', marginBottom: '6px' }}>Password</label>
                    <input
                      type="password"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '10px',
                        color: '#fff',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </>
              )}
              
              <button
                type="submit"
                disabled={authLoading}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: authLoading ? 'not-allowed' : 'pointer',
                  opacity: authLoading ? 0.7 : 1
                }}
              >
                {authLoading ? 'Please wait...' : (
                  authMode === 'login' ? 'Login' : 
                  verificationSent ? 'Verify & Create Account' : 'Send Verification Code'
                )}
              </button>
              
              {/* Back button when in verification step */}
              {authMode === 'register' && verificationSent && (
                <button
                  type="button"
                  onClick={() => {
                    setVerificationSent(false);
                    setVerificationCode('');
                    setVerificationHint('');
                  }}
                  style={{
                    width: '100%',
                    marginTop: '10px',
                    padding: '12px',
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    color: '#888',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  ← Back to change email
                </button>
              )}
            </form>
            
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              {authMode === 'login' && (
                <div style={{ marginBottom: '10px' }}>
                  <button
                    onClick={() => { setFpEmail(loginEmail); setFpSent(false); setShowForgotPassword(true); }}
                    style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
              )}
              <span style={{ color: '#888', fontSize: '0.85rem' }}>
                {authMode === 'login' ? "Don't have an account? " : "Already have an account? "}
              </span>
              <button
                onClick={() => {
                  setAuthMode(authMode === 'login' ? 'register' : 'login');
                  setAuthError('');
                }}
                style={{ background: 'none', border: 'none', color: '#00d4ff', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}
              >
                {authMode === 'login' ? 'Sign Up' : 'Login'}
              </button>
            </div>
            
            {authMode === 'register' && (
              <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', fontSize: '0.8rem', color: '#10b981' }}>
                💎 Start your $3.99 5-day trial, or choose a plan — generations require an active plan or tokens
              </div>
            )}
          </div>
        </div>
      )}
      
      <UserDashboard 
        isOpen={showDashboard} 
        onClose={() => setShowDashboard(false)} 
        userEmail={userEmail}
        onUpgrade={() => setShowPricing(true)}
      />
      <AnalysisHistory
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        userEmail={userEmail}
      />
      
      {/* LEFT SIDEBAR - QUANTUM CONTROL ROOM */}
      {mobileNavOpen && (
        <div
          className="mobile-nav-backdrop"
          data-testid="mobile-nav-backdrop"
          onClick={() => setMobileNavOpen(false)}
        />
      )}
      <aside className={`suno-sidebar quantum-sidebar ${mobileNavOpen ? 'mobile-open' : ''}`} style={{ background: 'var(--bg-surface)', borderRight: '1px solid var(--border)' }}>
        <button
          className="mobile-nav-close"
          data-testid="mobile-nav-close"
          aria-label="Close menu"
          onClick={() => setMobileNavOpen(false)}
        >
          <X className="w-5 h-5" />
        </button>
        {/* Logo - MANNIS PRO Quantum Style */}
        <div className="sidebar-logo" style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px',
          padding: '20px 16px',
          borderBottom: '1px solid var(--border)',
          marginBottom: '8px',
          background: 'linear-gradient(180deg, rgba(0, 240, 255, 0.03) 0%, transparent 100%)'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', lineHeight: 1 }}>
            <span style={{
              fontSize: '2rem',
              fontWeight: 900,
              fontFamily: 'Unbounded, sans-serif',
              letterSpacing: '-0.01em',
              textTransform: 'uppercase',
              background: 'linear-gradient(95deg, var(--quantum-cyan) 0%, #7c5cff 55%, var(--quantum-purple) 100%)',
              WebkitBackgroundClip: 'text',
              backgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 0 14px rgba(0, 212, 255, 0.45))'
            }}>MANNIS</span>
            <span style={{
              fontSize: '0.62rem',
              fontWeight: 700,
              fontFamily: 'JetBrains Mono, monospace',
              letterSpacing: '0.42em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.5)',
              paddingLeft: '2px'
            }}>QUANTUM PRO</span>
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="sidebar-nav" onClick={() => { if (mobileNavOpen) setMobileNavOpen(false); }}>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Create</div>
            <button
              className={`sidebar-item ${activeView === 'create' ? 'active' : ''}`}
              onClick={() => setActiveView('create')}
            >
              <PlusCircle className="w-5 h-5" />
              <span>MANNIS</span>
              <SidebarOnBadge />
            </button>
            <button
              className={`sidebar-item ${activeView === 'library' ? 'active' : ''}`}
              onClick={() => setActiveView('library')}
            >
              <Library className="w-5 h-5" />
              <span>My Songs</span>
              {beatLibrary.length > 0 && (
                <span className="sidebar-item-badge">{beatLibrary.length}</span>
              )}
            </button>
            <button
              className={`sidebar-item ${activeView === 'mysounddna' ? 'active' : ''}`}
              onClick={() => { setActiveView('mysounddna'); loadSunoTracks(); fetchBeatLibrary(); }}
              data-testid="sidebar-mysounddna-btn"
            >
              <Library className="w-5 h-5" />
              <span>My MANNIS</span>
            </button>
            <button
              className={`sidebar-item ${activeView === 'promptlab' ? 'active' : ''}`}
              onClick={() => setActiveView('promptlab')}
              data-testid="sidebar-promptlab-btn"
            >
              <Sparkles className="w-5 h-5" />
              <span>Prompt Lab</span>
              <SidebarOnBadge style={{ marginLeft: '8px' }} />
            </button>
            <button
              className="sidebar-item"
              onClick={() => setShowExtensionModal(true)}
              data-testid="sidebar-extension-btn"
            >
              <Zap className="w-5 h-5" />
              <span>Suno Extension</span>
              <span className="sidebar-item-badge" style={{ background: 'rgba(0, 212, 255, 0.2)', color: '#00d4ff' }}>NEW</span>
            </button>
          </div>

          <div className="sidebar-section">
            <div className="sidebar-section-title">Tools</div>
            <button
              className={`sidebar-item ${activeView === 'create' ? 'active' : ''}`}
              onClick={() => {
                setActiveView('create');
                setTimeout(() => {
                  const el = document.querySelector('[data-testid="dropzone"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 200);
              }}
              style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Upload className="w-5 h-5" />
                <span>Drop Your Tracks</span>
                <SidebarOnBadge style={{ marginLeft: '8px' }} />
              </div>
              <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginLeft: '32px' }}>MP3, WAV, FLAC, OGG • Up to 10 files</span>
            </button>
            <button
              className={`sidebar-item ${activeView === 'create' ? 'active' : ''}`}
              onClick={() => {
                setActiveView('create');
                setShowGenreGuide(true);
              }}
            >
              <Star className="w-5 h-5" />
              <span>Discover Presets</span>
              <SidebarOnBadge />
            </button>
            <button
              className={`sidebar-item ${activeView === 'create' ? 'active' : ''}`}
              onClick={async () => {
                setActiveView('create');
                await loadSunoLibraryCategories();
                setShowSunoLibrary(true);
              }}
            >
              <Library className="w-5 h-5" />
              <span>70K Style Prompts</span>
              <SidebarOnBadge />
            </button>
            <button
              className={`sidebar-item ${activeView === 'create' ? 'active' : ''}`}
              onClick={() => {
                setActiveView('create');
                setTimeout(() => {
                  const el = document.querySelector('[data-testid="music-analyzer"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 100);
              }}
            >
              <Radio className="w-5 h-5" />
              <span>Music Link Analyzer</span>
              <SidebarOnBadge />
            </button>
            <button
              className={`sidebar-item ${activeView === 'mastering' ? 'active' : ''}`}
              onClick={() => setActiveView('mastering')}
              data-testid="sidebar-mastering-studio"
            >
              <Headphones className="w-5 h-5" />
              <span>Mixing & Mastering</span>
              <SidebarOnBadge />
            </button>
            {/* World Voice DNA Toggle - Dedicated Sidebar Button */}
            <button
              className="sidebar-item"
              onClick={() => {
                const newState = !showWorldVoiceDNA;
                setShowWorldVoiceDNA(newState);
                if (newState && !worldVoiceData) loadWorldVoiceData();
                // Scroll to panel if on main view
                if (newState && activeView === 'create') {
                  setTimeout(() => {
                    const panel = document.querySelector('[data-testid="world-voice-dna-panel"]');
                    if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }
              }}
              style={{
                background: showWorldVoiceDNA 
                  ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.3), rgba(168, 85, 247, 0.3))'
                  : 'transparent',
                borderLeft: showWorldVoiceDNA ? '3px solid #06b6d4' : '3px solid transparent'
              }}
              data-testid="world-voice-sidebar-toggle"
            >
              <Globe className="w-5 h-5" style={{ color: showWorldVoiceDNA ? '#06b6d4' : 'inherit' }} />
              <span style={{ color: showWorldVoiceDNA ? '#06b6d4' : 'inherit' }}>World Voice DNA</span>
              <span style={{
                marginLeft: 'auto',
                padding: '2px 8px',
                background: showWorldVoiceDNA ? '#06b6d4' : 'rgba(255,255,255,0.1)',
                borderRadius: '10px',
                fontSize: '0.6rem',
                fontWeight: 700,
                color: showWorldVoiceDNA ? '#000' : 'rgba(255,255,255,0.5)'
              }}>
                {showWorldVoiceDNA ? 'ON' : 'OFF'}
              </span>
            </button>
            <button
              className={`sidebar-item ${activeView === 'voiceshowcase' ? 'active' : ''}`}
              onClick={() => setActiveView('voiceshowcase')}
              data-testid="sidebar-voice-collections"
            >
              <Crown className="w-5 h-5" />
              <span>Voice Collections</span>
              <SidebarOnBadge />
            </button>
            <button
              className={`sidebar-item ${activeView === 'create' ? 'active' : ''}`}
              onClick={() => setActiveView('analyzer')}
            >
              <Music2 className="w-5 h-5" />
              <span>Music Analyzer</span>
              <SidebarOnBadge />
            </button>
            <button
              className={`sidebar-item ${activeView === 'create' ? 'active' : ''}`}
              onClick={() => setActiveView('rhyme')}
            >
              <FileText className="w-5 h-5" />
              <span>Rhyme Engine</span>
              <SidebarOnBadge />
            </button>
            <button
              className={`sidebar-item ${activeView === 'create' ? 'active' : ''}`}
              onClick={() => { setActiveView('marketplace'); fetchMarketplaceDNAs(); }}
            >
              <Compass className="w-5 h-5" />
              <span>DNA Presets</span>
              <SidebarOnBadge />
            </button>
            <button
              className={`sidebar-item ${activeView === 'dna-toolkit' ? 'active' : ''}`}
              onClick={() => setActiveView('dna-toolkit')}
              data-testid="sidebar-dna-toolkit"
            >
              <Layers className="w-5 h-5" />
              <span>DNA Toolkit</span>
              <SidebarOnBadge />
            </button>
          </div>
        </nav>

        {/* User Section */}
        <div className="sidebar-user">
          <div style={{ marginBottom: '8px', fontSize: '0.65rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Account
          </div>
          {currentUser ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div className="sidebar-user-info" style={{ background: 'rgba(0, 212, 255, 0.1)', padding: '8px', borderRadius: '8px' }}>
                <div className="sidebar-avatar" style={{ background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)' }}>
                  <User className="w-5 h-5" style={{ color: '#fff' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div className="sidebar-user-name" style={{ fontWeight: 600 }}>{currentUser.name || currentUser.email.split('@')[0]}</div>
                  <div className="sidebar-user-plan" style={{ color: '#00d4ff', fontSize: '0.7rem' }}>
                    {currentUser.subscription === 'free' ? 'No Plan' : 
                     currentUser.subscription === 'student' ? 'Starter' :
                     currentUser.subscription === 'creator' ? 'Creator Pro' : 'Pro'}
                    {currentUser.role === 'admin' && ' 👑'}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  onClick={() => setShowPricing(true)}
                  style={{
                    flex: 1,
                    padding: '8px',
                    background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {currentUser.subscription === 'free' ? 'Upgrade' : 'Plans'}
                </button>
                {currentUser.subscription !== 'free' && (
                  <button
                    onClick={() => setShowSubscriptionManagement(true)}
                    style={{
                      padding: '8px 12px',
                      background: 'transparent',
                      border: '1px solid #9F47FF',
                      borderRadius: '6px',
                      color: '#9F47FF',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                    data-testid="manage-subscription-btn"
                  >
                    Manage
                  </button>
                )}
                <button
                  onClick={handleBuyTokens}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(245, 158, 11, 0.12)',
                    border: '1px solid rgba(245, 158, 11, 0.5)',
                    borderRadius: '6px',
                    color: '#f59e0b',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                  title="Buy 100 tokens for $10 - use them when your monthly limit runs out"
                  data-testid="buy-tokens-btn"
                >
                  ⚡ Buy Tokens
                </button>
                <button
                  onClick={() => { setShowChangePassword(true); setCpCurrent(''); setCpNew(''); setCpConfirm(''); setCpError(''); }}
                  style={{
                    padding: '8px 12px',
                    background: 'transparent',
                    border: '1px solid rgba(0, 212, 255, 0.5)',
                    borderRadius: '6px',
                    color: '#00d4ff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                  data-testid="change-password-btn"
                >
                  Password
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.5)',
                    borderRadius: '6px',
                    color: '#ef4444',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                  data-testid="sidebar-signout-btn"
                >
                  Sign Out
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button
                onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
                data-testid="sidebar-login-btn"
              >
                🔐 Login / Sign Up
              </button>
              <p style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center', margin: 0 }}>
                Create account & choose your plan
              </p>
            </div>
          )}
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <main className="suno-main">
        {/* Header */}
        <header className="suno-header quantum-nav" style={{ background: 'rgba(3, 3, 5, 0.85)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button
              className="mobile-nav-toggle"
              data-testid="mobile-nav-toggle"
              aria-label="Open menu"
              onClick={() => setMobileNavOpen(true)}
            >
              <Menu className="mobile-nav-toggle-icon" strokeWidth={2.75} />
              <span className="mobile-nav-toggle-label">MENU</span>
            </button>
            <h1 className="suno-header-title" style={{ 
              background: 'linear-gradient(135deg, var(--quantum-cyan), var(--quantum-purple), var(--quantum-crimson))', 
              WebkitBackgroundClip: 'text', 
              WebkitTextFillColor: 'transparent',
              fontSize: '1.75rem',
              fontWeight: 900,
              fontFamily: 'Unbounded, sans-serif',
              letterSpacing: '-0.02em',
              textShadow: '0 0 40px rgba(0, 240, 255, 0.3)'
            }}>
              STYLE PROMPTS
            </h1>
          </div>
          <div className="suno-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Mobile Account Button - Shows login/user status */}
            <div className="mobile-account-btn" style={{ display: 'none' }}>
              {currentUser ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    onClick={() => setShowPricing(true)}
                    style={{
                      padding: '8px 12px',
                      background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    data-testid="mobile-plans-btn"
                  >
                    <Crown className="w-3 h-3" />
                    {currentUser.subscription === 'free' ? 'Upgrade' : 'Plans'}
                  </button>
                  <button
                    onClick={handleLogout}
                    style={{
                      padding: '8px 12px',
                      background: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid rgba(239, 68, 68, 0.4)',
                      borderRadius: '8px',
                      color: '#ef4444',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                    data-testid="mobile-logout-btn"
                  >
                    Sign Out
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  data-testid="mobile-login-btn"
                >
                  <User className="w-4 h-4" />
                  Login
                </button>
              )}
            </div>
            
            {/* Desktop Auth Actions */}
            <div className="desktop-header-actions">
            {!currentUser ? (
              <button
                onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
                style={{
                  padding: '8px 16px',
                  background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                data-testid="header-login-btn"
              >
                <User className="w-4 h-4" />
                Login / Sign Up
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                  <User className="w-4 h-4" style={{ color: 'var(--accent)' }} />
                  <span style={{ color: '#00d4ff', fontWeight: 600 }}>{currentUser.name || currentUser.email?.split('@')[0]}</span>
                  <span style={{ 
                    padding: '2px 8px', 
                    background: 'rgba(139, 92, 246, 0.2)', 
                    borderRadius: '10px', 
                    fontSize: '0.7rem',
                    color: '#8b5cf6',
                    fontWeight: 700,
                    whiteSpace: 'nowrap'
                  }} data-testid="header-tier-badge">
                    {({ free: 'No Plan', student: 'Starter Plan', creator: 'Creator Plan', pro: 'Pro Plan', lifetime: 'Lifetime' }[currentUser.subscription]) || `${currentUser.subscription} Plan`}
                  </span>
                  <span
                    onClick={handleBuyTokens}
                    title="Monthly token usage - click to buy more"
                    style={{ 
                      padding: '3px 10px', 
                      background: 'rgba(245, 158, 11, 0.15)', 
                      border: '1px solid rgba(245, 158, 11, 0.4)',
                      borderRadius: '10px', 
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: '#f59e0b',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      flexDirection: 'column',
                      gap: '3px',
                      minWidth: '130px'
                    }}
                    data-testid="header-token-balance"
                  >
                    {(() => {
                      const lim = currentUser.subscription_limits || {};
                      const usage = currentUser.usage || {};
                      const unlimited = lim.beats_per_month === -1 || lim.vocals_per_month === -1;
                      const monthly = (lim.beats_per_month || 0) + (lim.vocals_per_month || 0);
                      const used = (usage.beats_used || 0) + (usage.vocals_used || 0);
                      const remaining = unlimited ? -1 : Math.max(0, monthly - used);
                      const extra = currentUser.token_balance || 0;
                      const total = unlimited ? -1 : remaining + extra;
                      const low = !unlimited && total <= CREATE_LOW_THRESHOLD;
                      const pct = unlimited || monthly === 0 ? 0 : Math.min(100, (used / monthly) * 100);
                      return (
                        <>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', color: low ? '#f87171' : undefined, fontWeight: low ? 800 : 700 }}>
                            {low ? '⚠️' : '⚡'} {unlimited ? 'Unlimited tokens' : (low ? `${total} left — Buy more` : `${remaining} tokens left`)}{!low && extra > 0 ? ` +${extra} extra` : ''}
                          </span>
                          {!unlimited && (
                            <span data-testid="token-progress-bar" style={{ display: 'block', width: '100%', height: '4px', background: 'rgba(255,255,255,0.12)', borderRadius: '2px', overflow: 'hidden' }}>
                              <span style={{ display: 'block', height: '100%', width: `${100 - pct}%`, background: (low || pct > 85) ? '#ef4444' : 'linear-gradient(90deg, #f59e0b, #f97316)', borderRadius: '2px', transition: 'width 0.4s' }} />
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </span>
                  <span
                    onClick={openTokenHistory}
                    title="View token purchase & spending history"
                    style={{
                      padding: '5px 8px',
                      background: 'rgba(0, 212, 255, 0.12)',
                      border: '1px solid rgba(0, 212, 255, 0.35)',
                      borderRadius: '10px',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      lineHeight: 1
                    }}
                    data-testid="header-token-history-btn"
                  >
                    🧾
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  style={{
                    padding: '6px 12px',
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.4)',
                    borderRadius: '6px',
                    color: '#ef4444',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                  data-testid="header-logout-btn"
                >
                  Sign Out
                </button>
              </div>
            )}
            </div>
            <button
              onClick={() => setShowHistory(true)}
              className="btn btn-ghost"
            >
              <History className="w-4 h-4" />
              History
            </button>
            <button
              onClick={() => { setOnboardingStep(0); setShowOnboarding(true); }}
              className="btn btn-ghost"
              title="App Tutorial"
            >
              <HelpCircle className="w-4 h-4" />
              Help
            </button>
            <button
              onClick={() => setShowWhySoundDNA(true)}
              className="btn btn-ghost"
              title="Why MANNIS?"
              style={{ background: 'linear-gradient(135deg, rgba(159,71,255,0.2), rgba(0,212,255,0.2))', border: '1px solid rgba(159,71,255,0.4)' }}
            >
              <Sparkles className="w-4 h-4" style={{ color: '#9F47FF' }} />
              Why Us?
            </button>
            {subscription.plan === 'free' && (
              <button onClick={() => setShowPricing(true)} className="btn btn-primary">
                <Crown className="w-4 h-4" />
                Upgrade
              </button>
            )}
          </div>
        </header>

        {/* Content Area */}
        <div className="suno-content">
      
      {/* Genre Guide Modal */}
      {showGenreGuide && (
        <div className="modal-overlay" onClick={() => setShowGenreGuide(false)}>
          <motion.div 
            className="genre-guide-modal" 
            onClick={e => e.stopPropagation()} 
            initial={{ scale: 0.9, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }}
            data-testid="genre-guide-modal"
          >
            <button className="modal-close" onClick={() => setShowGenreGuide(false)}><X /></button>
            <div className="genre-guide-content">
              <h2><Music2 className="w-6 h-6" /> Genre Reference Guide</h2>
              <p className="genre-guide-subtitle">Tap a genre to learn about its sound and tempo</p>
              
              <div className="genre-guide-grid">
                {GENRES.filter(g => g.value !== 'auto').map(g => (
                  <div 
                    key={g.value} 
                    className="genre-guide-card"
                    style={{ '--genre-color': g.color, borderColor: g.color }}
                    onClick={() => setPresetDetail({
                      id: `genre:${g.value}`,
                      name: g.label,
                      prompt: `${g.label.toLowerCase()}, ${g.desc || ''}${g.bpm ? `, ${g.bpm} BPM` : ''}`,
                      bpm: g.bpm || '—',
                      key: '—',
                      genre: g.label,
                      desc: g.desc,
                      apply: () => { setSelectedGenre(g.value); setShowGenreGuide(false); }
                    })}
                  >
                    <div className="genre-guide-icon">{g.icon}</div>
                    <div className="genre-guide-info">
                      <div className="genre-guide-header">
                        <span className="genre-guide-name" style={{ color: g.color }}>{g.label}</span>
                        <span className="genre-guide-bpm">{g.bpm}</span>
                      </div>
                      <p className="genre-guide-desc">{g.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <button className="download-got-it-btn" onClick={() => setShowGenreGuide(false)}>
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Shared Preset Detail Modal */}
      {presetDetail && (
        <div className="modal-overlay" onClick={() => setPresetDetail(null)} style={{ zIndex: 10000 }}>
          <motion.div
            onClick={e => e.stopPropagation()}
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            data-testid="preset-detail-modal"
            style={{
              background: 'linear-gradient(135deg, rgba(15,15,26,0.98) 0%, rgba(26,26,46,0.98) 100%)',
              border: '1px solid rgba(0,212,255,0.4)',
              borderRadius: '16px',
              padding: '26px',
              maxWidth: '520px',
              width: '92%',
              boxShadow: '0 0 50px rgba(0,212,255,0.2)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, color: '#fff', fontSize: '1.15rem', fontWeight: 800 }}>{presetDetail.name}</h3>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={() => togglePresetFavorite({
                    id: presetDetail.id, name: presetDetail.name, prompt: presetDetail.prompt,
                    bpm: presetDetail.bpm, key: presetDetail.key, genre: presetDetail.genre, desc: presetDetail.desc
                  })}
                  data-testid="preset-detail-fav-btn"
                  title={isPresetFavorite(presetDetail.id) ? 'Remove from favorites' : 'Add to favorites'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  <Star className="w-5 h-5" fill={isPresetFavorite(presetDetail.id) ? '#fbbf24' : 'none'} style={{ color: '#fbbf24' }} />
                </button>
                <button
                  onClick={() => setPresetDetail(null)}
                  data-testid="preset-detail-close"
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 0 }}
                ><X className="w-5 h-5" /></button>
              </div>
            </div>
            {presetDetail.desc && (
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', margin: '0 0 14px 0' }}>{presetDetail.desc}</p>
            )}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
              {[
                { label: 'BPM', value: presetDetail.bpm },
                { label: 'KEY', value: presetDetail.key },
                { label: 'GENRE', value: presetDetail.genre }
              ].map((item, i) => (
                <div key={i} style={{
                  padding: '10px 16px', background: 'rgba(0,212,255,0.08)',
                  border: '1px solid rgba(0,212,255,0.25)', borderRadius: '10px', textAlign: 'center', minWidth: '80px'
                }}>
                  <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em' }}>{item.label}</div>
                  <div style={{ color: '#00d4ff', fontSize: '0.95rem', fontWeight: 800 }}>{item.value}</div>
                </div>
              ))}
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)',
              borderRadius: '10px', padding: '14px', marginBottom: '18px'
            }}>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', marginBottom: '6px' }}>STYLE PROMPT</div>
              <p data-testid="preset-detail-prompt" style={{ margin: 0, color: '#f4f4f5', fontSize: '0.9rem', lineHeight: 1.5 }}>{presetDetail.prompt}</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { const apply = presetDetail.apply; setPresetDetail(null); apply(); }}
                data-testid="preset-detail-use-btn"
                style={{
                  flex: 1, padding: '12px', background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                  border: 'none', borderRadius: '10px', color: '#fff', fontSize: '0.9rem', fontWeight: 800, cursor: 'pointer'
                }}
              >Use This Preset</button>
              <button
                onClick={() => setPresetDetail(null)}
                data-testid="preset-detail-cancel-btn"
                style={{
                  padding: '12px 18px', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)',
                  borderRadius: '10px', color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer'
                }}
              >Cancel</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Why MANNIS? Comparison Modal */}
      {showWhySoundDNA && (
        <div className="modal-overlay" onClick={() => setShowWhySoundDNA(false)}>
          <motion.div 
            onClick={e => e.stopPropagation()} 
            initial={{ scale: 0.9, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }}
            data-testid="why-sounddna-modal"
            style={{
              background: 'linear-gradient(135deg, rgba(15,15,26,0.98) 0%, rgba(26,26,46,0.98) 100%)',
              border: '2px solid rgba(159,71,255,0.5)',
              borderRadius: '20px',
              padding: '30px',
              maxWidth: '900px',
              width: '95%',
              maxHeight: '85vh',
              overflow: 'auto',
              boxShadow: '0 0 60px rgba(159,71,255,0.3)'
            }}
          >
            <button 
              onClick={() => setShowWhySoundDNA(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: '#fff'
              }}
            ><X className="w-5 h-5" /></button>
            
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
              <h2 style={{ 
                fontSize: '2rem', 
                fontWeight: 800, 
                background: 'linear-gradient(135deg, #9F47FF, #00d4ff)', 
                WebkitBackgroundClip: 'text', 
                WebkitTextFillColor: 'transparent',
                marginBottom: '10px'
              }}>
                Why MANNIS?
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem' }}>
                Features no other AI music platform offers
              </p>
            </div>
            
            {/* Comparison Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
              gap: '16px',
              marginBottom: '30px'
            }}>
              {[
                { 
                  icon: '🎹', 
                  title: 'AI Drum Machine + MIDI Export',
                  desc: 'Real AI-generated drum patterns with downloadable MIDI files for your DAW. No other platform does this.',
                  others: '❌ Others: No MIDI export'
                },
                { 
                  icon: '🎯', 
                  title: '70,000+ Curated Style Prompts',
                  desc: 'The largest library of professionally curated style prompts across 46 genres.',
                  others: '❌ Others: Generic templates'
                },
                { 
                  icon: '🔊', 
                  title: 'Audio Preview Before Generation',
                  desc: 'Hear how your prompt will sound BEFORE generating on Suno/Udio. Save credits!',
                  others: '❌ Others: No preview'
                },
                { 
                  icon: '🧬', 
                  title: '650 DNA-Coded Presets',
                  desc: 'Unique DNA code system for instant style matching and saving your favorites.',
                  others: '❌ Others: No preset system'
                },
                { 
                  icon: '🎤', 
                  title: 'World Voice DNA Vocals',
                  desc: 'AI singing vocals in 10+ styles: Pop, R&B, Hip-Hop, Gospel, Jazz, K-Pop & more.',
                  others: '❌ Others: No vocal generation'
                },
                { 
                  icon: '🔗', 
                  title: 'YouTube Link Analysis',
                  desc: 'Paste any YouTube music link and get matching style prompts instantly. (Spotify returning soon)',
                  others: '❌ Others: Manual input only'
                },
                { 
                  icon: '🎛️', 
                  title: 'Maschine-Style Interface',
                  desc: 'Professional hardware-inspired UI designed for serious music producers.',
                  others: '❌ Others: Basic web forms'
                },
                { 
                  icon: '🌍', 
                  title: 'Multi-Language Support',
                  desc: 'Generate prompts and vocals in multiple languages including Spanish, French, Japanese & more.',
                  others: '❌ Others: English only'
                },
                { 
                  icon: '📱', 
                  title: 'iOS Files App Integration',
                  desc: 'Save directly to your iPhone Files app. Works seamlessly on mobile.',
                  others: '❌ Others: Desktop only'
                },
                { 
                  icon: '🎼', 
                  title: 'Rhyme & Flow Engine',
                  desc: '15+ rhyme styles and 16 flow patterns for lyrics that actually work.',
                  others: '❌ Others: Generic lyrics'
                },
                { 
                  icon: '🔀', 
                  title: 'Stem Separation',
                  desc: 'Split any song into vocals, drums, bass, and other stems.',
                  others: '❌ Others: Not included'
                },
                { 
                  icon: '⚡', 
                  title: 'One-Click Workflow',
                  desc: 'Analyze → Generate → Preview → Export in a seamless unified workflow.',
                  others: '❌ Others: Fragmented tools'
                }
              ].map((feature, i) => (
                <div key={i} style={{
                  background: 'rgba(159,71,255,0.08)',
                  border: '1px solid rgba(159,71,255,0.2)',
                  borderRadius: '12px',
                  padding: '16px',
                  transition: 'all 0.2s',
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '8px' }}>{feature.icon}</div>
                  <h3 style={{ color: '#fff', fontSize: '0.95rem', fontWeight: 700, marginBottom: '6px' }}>
                    {feature.title}
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', marginBottom: '8px', lineHeight: 1.4 }}>
                    {feature.desc}
                  </p>
                  <p style={{ color: 'rgba(255,100,100,0.8)', fontSize: '0.7rem', fontStyle: 'italic' }}>
                    {feature.others}
                  </p>
                </div>
              ))}
            </div>
            
            {/* CTA */}
            <div style={{ 
              textAlign: 'center', 
              padding: '20px',
              background: 'linear-gradient(135deg, rgba(159,71,255,0.15), rgba(0,212,255,0.15))',
              borderRadius: '12px',
              border: '1px solid rgba(159,71,255,0.3)'
            }}>
              <p style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 600, marginBottom: '8px' }}>
                🚀 The Ultimate AI Music Companion
              </p>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: '16px' }}>
                Built by producers, for producers. Join thousands creating better music with MANNIS.
              </p>
              <button
                onClick={() => setShowWhySoundDNA(false)}
                style={{
                  padding: '12px 40px',
                  background: 'linear-gradient(135deg, #9F47FF, #00d4ff)',
                  border: 'none',
                  borderRadius: '30px',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '1rem',
                  cursor: 'pointer'
                }}
              >
                Start Creating
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Mashup Music Prompt Modal */}
      {showMashup && (
        <div className="modal-overlay" onClick={() => setShowMashup(false)}>
          <motion.div 
            onClick={e => e.stopPropagation()} 
            initial={{ scale: 0.9, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }}
            data-testid="mashup-modal"
            style={{
              background: 'linear-gradient(135deg, rgba(15,15,26,0.98) 0%, rgba(26,26,46,0.98) 100%)',
              border: '2px solid rgba(255,153,0,0.4)',
              borderRadius: '20px',
              padding: '30px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative'
            }}
          >
            <button 
              className="modal-close" 
              onClick={() => setShowMashup(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
              }}
            >
              <X />
            </button>
            
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '10px',
                background: 'linear-gradient(135deg, rgba(255,153,0,0.2), rgba(255,0,102,0.1))',
                padding: '12px 24px',
                borderRadius: '30px',
                marginBottom: '12px'
              }}>
                <Layers className="w-6 h-6" style={{ color: '#ff9500' }} />
                <span style={{ color: '#ff9500', fontWeight: 700, fontSize: '1.3rem' }}>MASHUP PROMPT CREATOR</span>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                Combine two style prompts into one unique hybrid sound
              </p>
            </div>
            
            {/* Style 1 Input */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                color: '#9F47FF', 
                fontSize: '0.85rem', 
                fontWeight: 600, 
                marginBottom: '8px', 
                display: 'block' 
              }}>
                STYLE 1 - Base Sound
              </label>
              <textarea
                placeholder="e.g. dark trap with heavy 808s, minor key melodies, aggressive hi-hats"
                value={mashupStyle1}
                onChange={e => setMashupStyle1(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'rgba(0,0,0,0.4)',
                  border: mashupStyle1 ? '2px solid #9F47FF' : '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '0.95rem',
                  minHeight: '80px',
                  resize: 'vertical',
                  outline: 'none'
                }}
                data-testid="mashup-style1"
              />
            </div>
            
            {/* Merge Icon */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              margin: '16px 0' 
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #ff9500, #9F47FF)',
                borderRadius: '50%',
                width: '50px',
                height: '50px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 20px rgba(255,153,0,0.4)'
              }}>
                <span style={{ fontSize: '1.5rem' }}>+</span>
              </div>
            </div>
            
            {/* Style 2 Input */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: '#9F47FF', 
                fontSize: '0.85rem', 
                fontWeight: 600, 
                marginBottom: '8px', 
                display: 'block' 
              }}>
                STYLE 2 - Blend Sound
              </label>
              <textarea
                placeholder="e.g. smooth R&B vocals, emotional melodies, silky synths"
                value={mashupStyle2}
                onChange={e => setMashupStyle2(e.target.value)}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'rgba(0,0,0,0.4)',
                  border: mashupStyle2 ? '2px solid #9F47FF' : '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '0.95rem',
                  minHeight: '80px',
                  resize: 'vertical',
                  outline: 'none'
                }}
                data-testid="mashup-style2"
              />
            </div>
            
            {/* Generate Mashup Button */}
            <button
              onClick={async () => {
                if (!mashupStyle1.trim() || !mashupStyle2.trim()) {
                  toast.error("Please enter both styles to create a mashup!");
                  return;
                }
                setMashupGenerating(true);
                try {
                  // Generate mashup prompt by combining the two styles
                  const combined = `HYBRID MASHUP: Combine the energy and elements of [${mashupStyle1.trim()}] with the vibe and textures of [${mashupStyle2.trim()}]. Create a seamless fusion that balances both styles - take the rhythmic foundation and production techniques from the first style while incorporating the melodic elements and emotional tone from the second. The result should feel like a natural blend that highlights the best of both worlds.`;
                  setMashupResult(combined);
                  toast.success("Mashup prompt created!");
                } catch (error) {
                  toast.error("Failed to create mashup");
                }
                setMashupGenerating(false);
              }}
              disabled={mashupGenerating || !mashupStyle1.trim() || !mashupStyle2.trim()}
              style={{
                width: '100%',
                padding: '16px',
                background: (mashupStyle1.trim() && mashupStyle2.trim()) 
                  ? 'linear-gradient(135deg, #ff9500, #9F47FF)' 
                  : 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '12px',
                color: '#fff',
                fontSize: '1rem',
                fontWeight: 700,
                cursor: (mashupStyle1.trim() && mashupStyle2.trim()) ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                boxShadow: (mashupStyle1.trim() && mashupStyle2.trim()) 
                  ? '0 4px 20px rgba(255,153,0,0.4)' 
                  : 'none',
                opacity: (mashupStyle1.trim() && mashupStyle2.trim()) ? 1 : 0.5
              }}
              data-testid="generate-mashup-btn"
            >
              {mashupGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating Mashup...
                </>
              ) : (
                <>
                  <Layers className="w-5 h-5" />
                  Create Mashup Prompt
                </>
              )}
            </button>
            
            {/* Mashup Result */}
            {mashupResult && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  background: 'linear-gradient(135deg, rgba(255,153,0,0.1), rgba(255,0,102,0.1))',
                  border: '2px solid rgba(255,153,0,0.4)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginTop: '20px'
                }}
                data-testid="mashup-result"
              >
                <label style={{ 
                  color: '#ff9500', 
                  fontSize: '0.85rem', 
                  fontWeight: 600, 
                  marginBottom: '8px', 
                  display: 'block' 
                }}>
                  YOUR MASHUP PROMPT
                </label>
                <p style={{ 
                  color: '#fff', 
                  fontSize: '0.9rem', 
                  lineHeight: 1.6,
                  marginBottom: '12px' 
                }}>
                  {mashupResult}
                </p>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => universalCopy(mashupResult, "Mashup prompt copied! Tap & hold to paste")}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#9F47FF',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#000',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    data-testid="copy-mashup-btn"
                  >
                    <Copy className="w-4 h-4" />
                    Copy Mashup
                  </button>
                  <button
                    onClick={() => {
                      setSunoStyle(mashupResult);
                      setShowMashup(false);
                      toast.success("Mashup loaded to Song Creator!");
                    }}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#9F47FF',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    data-testid="use-mashup-btn"
                  >
                    <Music2 className="w-4 h-4" />
                    Use for Song
                  </button>
                  {/* Drum Pattern Button */}
                  <button
                    onClick={() => {
                      generateDrumFromStylePrompt(mashupResult);
                      toast.info("🥁 Generating drum pattern from mashup...");
                    }}
                    style={{
                      padding: '12px 16px',
                      background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    data-testid="drum-mashup-btn"
                  >
                    <Music className="w-4 h-4" />
                    🥁 Drums
                  </button>
                  {/* MIDI Download Button */}
                  <button
                    onClick={() => generateMidiFromPrompt(mashupResult)}
                    disabled={isGeneratingMidi}
                    style={{
                      padding: '12px 16px',
                      background: isGeneratingMidi 
                        ? 'rgba(16, 185, 129, 0.3)' 
                        : 'linear-gradient(135deg, #10b981, #059669)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: isGeneratingMidi ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                    data-testid="midi-mashup-btn"
                  >
                    <Download className="w-4 h-4" />
                    ⬇️ MIDI
                  </button>
                </div>
              </motion.div>
            )}
            
            {/* Quick Style Presets */}
            <div style={{ marginTop: '20px' }}>
              <p style={{ 
                color: 'rgba(255,255,255,0.5)', 
                fontSize: '0.8rem', 
                textAlign: 'center',
                marginBottom: '12px' 
              }}>
                Quick Presets - Click to auto-fill
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                {[
                  { label: 'Trap + R&B', s1: 'dark trap with heavy 808s, hard-hitting drums, aggressive energy', s2: 'smooth R&B vocals, emotional melodies, silky production' },
                  { label: 'Rock + EDM', s1: 'hard rock guitars, powerful drums, raw energy', s2: 'electronic drops, synth buildups, festival energy' },
                  { label: 'Hip Hop + Jazz', s1: 'boom bap drums, sampled loops, classic hip hop', s2: 'jazz piano, saxophone solos, smooth harmonies' },
                  { label: 'Pop + Lo-Fi', s1: 'catchy pop hooks, upbeat energy, radio-ready', s2: 'lo-fi beats, vinyl crackle, chill vibes' },
                ].map((preset, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setMashupStyle1(preset.s1);
                      setMashupStyle2(preset.s2);
                      toast.success(`${preset.label} preset loaded!`);
                    }}
                    style={{
                      padding: '6px 14px',
                      background: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '20px',
                      color: 'rgba(255,255,255,0.7)',
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    data-testid={`mashup-preset-${i}`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Suno Prompts Library Modal (10,000 Prompts) */}
      {showSunoLibrary && (
        <div className="modal-overlay" onClick={() => setShowSunoLibrary(false)}>
          <motion.div 
            onClick={e => e.stopPropagation()} 
            initial={{ scale: 0.9, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }}
            data-testid="suno-library-modal"
            style={{
              background: 'linear-gradient(135deg, rgba(15,15,26,0.98) 0%, rgba(26,26,46,0.98) 100%)',
              border: '2px solid rgba(159,71,255,0.4)',
              borderRadius: '20px',
              padding: '30px',
              maxWidth: '900px',
              width: '95%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative'
            }}
          >
            <button 
              className="modal-close" 
              onClick={() => setShowSunoLibrary(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'rgba(255,255,255,0.1)',
                border: 'none',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff'
              }}
            >
              <X />
            </button>
            
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: '10px',
                background: 'linear-gradient(135deg, rgba(159,71,255,0.2), rgba(0,255,136,0.1))',
                padding: '12px 24px',
                borderRadius: '30px',
                marginBottom: '12px'
              }}>
                <Library className="w-6 h-6" style={{ color: '#9F47FF' }} />
                <span style={{ color: '#9F47FF', fontWeight: 700, fontSize: '1.3rem' }}>70,000 MANNIS STYLE PROMPTS LIBRARY</span>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
                Browse, copy, or download professional style prompts - No upload required
              </p>
              <p style={{ color: '#9F47FF', fontSize: '0.85rem', marginTop: '8px' }}>
                {sunoLibraryTotal.toLocaleString()} prompts across {sunoLibraryCategories.length} genres • Click <Download className="w-3 h-3 inline" /> to download
              </p>
            </div>
            
            {/* Search Bar */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  placeholder="Search prompts (e.g., trap, emotional, 120 BPM...)"
                  value={sunoLibrarySearch}
                  onChange={(e) => setSunoLibrarySearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchSunoLibrary(sunoLibrarySearch)}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: 'rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.95rem'
                  }}
                  data-testid="suno-library-search"
                />
                <button
                  onClick={() => searchSunoLibrary(sunoLibrarySearch)}
                  style={{
                    padding: '12px 24px',
                    background: '#9F47FF',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#000',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  data-testid="suno-library-search-btn"
                >
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </div>
            
            {/* Category Pills with Download */}
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '8px', 
              marginBottom: '20px',
              justifyContent: 'center'
            }}>
              {sunoLibraryCategories.map((cat, i) => (
                <div key={cat.name} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                  <button
                    onClick={() => {
                      setSunoLibraryCategory(cat.name);
                      setSunoLibrarySearch('');
                      loadSunoLibraryPrompts(cat.name, 1);
                    }}
                    style={{
                      padding: '8px 12px',
                      background: sunoLibraryCategory === cat.name 
                        ? 'linear-gradient(135deg, #9F47FF, #9F47FF)' 
                        : 'rgba(255,255,255,0.1)',
                      border: sunoLibraryCategory === cat.name 
                        ? 'none' 
                        : '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '20px 0 0 20px',
                      color: sunoLibraryCategory === cat.name ? '#000' : 'rgba(255,255,255,0.8)',
                      fontWeight: sunoLibraryCategory === cat.name ? 700 : 500,
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    data-testid={`suno-category-${cat.name}`}
                  >
                    {mannisCat(cat.name)} ({cat.count})
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadCategoryPrompts(cat.name);
                    }}
                    title={`Download ${cat.name} prompts`}
                    style={{
                      padding: '8px 10px',
                      background: sunoLibraryCategory === cat.name 
                        ? '#9F47FF' 
                        : 'rgba(159,71,255,0.2)',
                      border: '1px solid rgba(159,71,255,0.4)',
                      borderLeft: 'none',
                      borderRadius: '0 20px 20px 0',
                      color: sunoLibraryCategory === cat.name ? '#000' : '#9F47FF',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'all 0.2s'
                    }}
                    data-testid={`download-${cat.name}`}
                  >
                    <Download className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            
            {/* Prompts Grid */}
            <div style={{ 
              display: 'grid', 
              gap: '12px',
              maxHeight: '400px',
              overflowY: 'auto',
              padding: '4px'
            }}>
              {sunoLibraryLoading ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  color: 'rgba(255,255,255,0.6)' 
                }}>
                  <Loader2 className="w-8 h-8 animate-spin" style={{ margin: '0 auto 12px' }} />
                  Loading prompts...
                </div>
              ) : sunoLibraryPrompts.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  color: 'rgba(255,255,255,0.5)' 
                }}>
                  {sunoLibraryCategory ? 'No prompts found' : 'Select a genre category above to browse prompts'}
                </div>
              ) : (
                sunoLibraryPrompts.map((prompt, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.02 }}
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '12px',
                      padding: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(159,71,255,0.1)';
                      e.currentTarget.style.borderColor = 'rgba(159,71,255,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                    }}
                    data-testid={`suno-prompt-${idx}`}
                  >
                    <p style={{ 
                      color: '#fff', 
                      fontSize: '0.9rem', 
                      lineHeight: 1.5,
                      marginBottom: '12px'
                    }}>
                      {prompt}
                    </p>
                    {sunoLibraryStructures[prompt] && (
                      <div
                        data-testid={`suno-prompt-structure-${idx}`}
                        style={{
                          background: 'rgba(47,141,255,0.08)',
                          border: '1px solid rgba(47,141,255,0.25)',
                          borderRadius: '8px',
                          padding: '10px 12px',
                          marginBottom: '12px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span style={{ color: '#4da3ff', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                            🎬 Song Structure
                          </span>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              data-testid={`copy-structure-btn-${idx}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                universalCopy(sunoLibraryStructures[prompt], '✓ Structure copied! Paste into Suno lyrics');
                              }}
                              style={{
                                padding: '4px 10px', background: 'rgba(47,141,255,0.15)',
                                border: '1px solid rgba(47,141,255,0.4)', borderRadius: '6px',
                                color: '#4da3ff', fontWeight: 600, fontSize: '0.7rem', cursor: 'pointer'
                              }}
                            >
                              📋 Copy Structure
                            </button>
                            <button
                              data-testid={`copy-both-btn-${idx}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                universalCopy(`STYLE:\n${prompt}\n\nSTRUCTURE:\n${sunoLibraryStructures[prompt]}`, '✓ Style + Structure copied!');
                              }}
                              style={{
                                padding: '4px 10px', background: 'rgba(16,185,129,0.15)',
                                border: '1px solid rgba(16,185,129,0.4)', borderRadius: '6px',
                                color: '#10b981', fontWeight: 600, fontSize: '0.7rem', cursor: 'pointer'
                              }}
                            >
                              🎼 Copy Both
                            </button>
                          </div>
                        </div>
                        <pre style={{
                          margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit',
                          color: 'rgba(255,255,255,0.75)', fontSize: '0.78rem', lineHeight: 1.5
                        }}>
                          {sunoLibraryStructures[prompt]}
                        </pre>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {/* Audio Preview Button - Generate actual music */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          generateAudioPreview(prompt, sunoLibraryCategory, idx);
                        }}
                        disabled={audioPreviewLoading === idx}
                        style={{
                          padding: '8px 12px',
                          background: audioPreviewPlaying === idx 
                            ? 'rgba(239, 68, 68, 0.3)' 
                            : audioPreviewLoading === idx 
                              ? 'rgba(234, 179, 8, 0.3)'
                              : 'rgba(16, 185, 129, 0.2)',
                          border: `1px solid ${audioPreviewPlaying === idx 
                            ? 'rgba(239, 68, 68, 0.5)' 
                            : audioPreviewLoading === idx
                              ? 'rgba(234, 179, 8, 0.5)'
                              : 'rgba(16, 185, 129, 0.4)'}`,
                          borderRadius: '6px',
                          color: audioPreviewPlaying === idx 
                            ? '#ef4444' 
                            : audioPreviewLoading === idx
                              ? '#eab308'
                              : '#10b981',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          cursor: audioPreviewLoading === idx ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          minWidth: '80px'
                        }}
                        data-testid={`audio-preview-${idx}`}
                      >
                        {audioPreviewLoading === idx ? (
                          <><Loader2 className="w-3 h-3 animate-spin" /> Gen...</>
                        ) : audioPreviewPlaying === idx ? (
                          <><VolumeX className="w-3 h-3" /> Stop</>
                        ) : (
                          <><Headphones className="w-3 h-3" /> Preview</>
                        )}
                      </button>
                      <button
                        onClick={() => copySunoPrompt(prompt)}
                        style={{
                          padding: '8px 12px',
                          background: '#9F47FF',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#000',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <Copy className="w-3 h-3" />
                        Copy
                      </button>
                      {/* 🥁 DRUM PATTERN BUTTON */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          generateDrumFromStylePrompt(prompt);
                          toast.info("🥁 Generating drum pattern...");
                        }}
                        style={{
                          padding: '8px 12px',
                          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(99, 102, 241, 0.3))',
                          border: '1px solid rgba(139, 92, 246, 0.5)',
                          borderRadius: '6px',
                          color: '#a78bfa',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                        data-testid={`40k-lib-drum-${idx}`}
                      >
                        <Music className="w-3 h-3" />
                        🥁 Drums
                      </button>
                      {/* ⬇️ MIDI DOWNLOAD BUTTON */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          generateMidiFromPrompt(prompt);
                        }}
                        disabled={isGeneratingMidi}
                        style={{
                          padding: '8px 12px',
                          background: isGeneratingMidi 
                            ? 'rgba(16, 185, 129, 0.2)'
                            : 'linear-gradient(135deg, #10b981, #059669)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          cursor: isGeneratingMidi ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          boxShadow: '0 2px 10px rgba(16, 185, 129, 0.3)'
                        }}
                        data-testid={`40k-lib-midi-${idx}`}
                      >
                        <Download className="w-3 h-3" />
                        ⬇️ MIDI
                      </button>
                      <button
                        onClick={() => {
                          setBeatPrompt(prompt);
                          updateWorkflow({
                            stylePrompt: prompt,
                            source: '40k-library'
                          });
                          setShowSunoLibrary(false);
                          toast.success("✓ Style loaded to workflow! Ready to export to Suno!");
                        }}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          boxShadow: '0 2px 10px rgba(0, 212, 255, 0.3)'
                        }}
                      >
                        <Zap className="w-3 h-3" />
                        USE THIS
                      </button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
            
            {/* Pagination */}
            {sunoLibraryTotalPages > 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                gap: '12px',
                marginTop: '20px'
              }}>
                <button
                  onClick={() => loadSunoLibraryPrompts(sunoLibraryCategory, sunoLibraryPage - 1)}
                  disabled={sunoLibraryPage <= 1}
                  style={{
                    padding: '8px 16px',
                    background: sunoLibraryPage <= 1 ? 'rgba(255,255,255,0.1)' : 'rgba(159,71,255,0.2)',
                    border: '1px solid rgba(159,71,255,0.4)',
                    borderRadius: '8px',
                    color: sunoLibraryPage <= 1 ? 'rgba(255,255,255,0.3)' : '#9F47FF',
                    cursor: sunoLibraryPage <= 1 ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                  data-testid="suno-prev-page"
                >
                  Previous
                </button>
                <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Page {sunoLibraryPage} of {sunoLibraryTotalPages}
                </span>
                <button
                  onClick={() => loadSunoLibraryPrompts(sunoLibraryCategory, sunoLibraryPage + 1)}
                  disabled={sunoLibraryPage >= sunoLibraryTotalPages}
                  style={{
                    padding: '8px 16px',
                    background: sunoLibraryPage >= sunoLibraryTotalPages ? 'rgba(255,255,255,0.1)' : 'rgba(159,71,255,0.2)',
                    border: '1px solid rgba(159,71,255,0.4)',
                    borderRadius: '8px',
                    color: sunoLibraryPage >= sunoLibraryTotalPages ? 'rgba(255,255,255,0.3)' : '#9F47FF',
                    cursor: sunoLibraryPage >= sunoLibraryTotalPages ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                  data-testid="suno-next-page"
                >
                  Next
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Beat Generator Style Library Modal */}
      {showBeatLibrary && (
        <div className="modal-overlay" onClick={() => setShowBeatLibrary(false)}>
          <motion.div 
            onClick={e => e.stopPropagation()} 
            initial={{ scale: 0.9, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }}
            style={{
              background: 'linear-gradient(135deg, rgba(10,10,30,0.98) 0%, rgba(20,20,50,0.98) 100%)',
              border: '2px solid rgba(159,71,255,0.4)',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '900px',
              width: '95%',
              maxHeight: '85vh',
              overflowY: 'auto',
              position: 'relative'
            }}
          >
            <button 
              onClick={() => setShowBeatLibrary(false)}
              style={{ 
                position: 'absolute', 
                top: '16px', 
                right: '16px', 
                background: 'rgba(255,255,255,0.1)', 
                border: 'none', 
                color: '#fff', 
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, #9F47FF, #8a2be2)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.8rem',
                margin: '0 auto 12px'
              }}>
                🎹
              </div>
              <h2 style={{ color: '#fff', margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>
                SELECT STYLE FOR BEAT GENERATOR
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.5)', margin: '8px 0 0', fontSize: '0.9rem' }}>
                Choose from 70,000+ style prompts to generate your beat
              </p>
            </div>
            
            {/* Search */}
            <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
              <input
                type="text"
                placeholder="Search styles (e.g., trap, R&B, lo-fi...)"
                value={sunoLibrarySearch}
                onChange={(e) => setSunoLibrarySearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchSunoLibrary(sunoLibrarySearch)}
                style={{
                  flex: 1,
                  padding: '14px 18px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(159,71,255,0.3)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem'
                }}
              />
              <button
                onClick={() => searchSunoLibrary(sunoLibrarySearch)}
                style={{
                  padding: '14px 24px',
                  background: 'linear-gradient(90deg, #9F47FF, #8a2be2)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Search className="w-4 h-4" />
                Search
              </button>
            </div>
            
            {/* Categories - Show ALL categories */}
            <div style={{ marginBottom: '20px' }}>
              <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', marginBottom: '10px' }}>
                {sunoLibraryCategories.length > 0 
                  ? `${sunoLibraryCategories.reduce((sum, c) => sum + c.count, 0).toLocaleString()} prompts across ${sunoLibraryCategories.length} genres • Click ↓ to download`
                  : 'Loading categories...'}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '300px', overflowY: 'auto', padding: '4px' }}>
                {sunoLibraryCategories.map((cat, i) => (
                  <div key={i} style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => {
                        setSunoLibraryCategory(cat.name);
                        setSunoLibrarySearch('');
                        loadSunoLibraryPrompts(cat.name, 1);
                      }}
                      style={{
                        padding: '10px 16px',
                        background: sunoLibraryCategory === cat.name 
                          ? 'linear-gradient(90deg, #9F47FF, #8a2be2)' 
                          : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${sunoLibraryCategory === cat.name ? '#9F47FF' : 'rgba(255,255,255,0.15)'}`,
                        borderRadius: '20px 0 0 20px',
                        color: '#fff',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      {mannisCat(cat.name)} ({cat.count})
                    </button>
                    <button
                      onClick={() => downloadCategoryPrompts(cat.name)}
                      style={{
                        padding: '10px 12px',
                        background: 'rgba(159,71,255,0.2)',
                        border: '1px solid rgba(159,71,255,0.4)',
                        borderRadius: '0 20px 20px 0',
                        color: '#9F47FF',
                        fontSize: '0.8rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      title={`Download ${cat.name} prompts`}
                    >
                      <Download className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Prompts Grid */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
              gap: '12px',
              maxHeight: '400px',
              overflowY: 'auto',
              padding: '4px'
            }}>
              {sunoLibraryLoading ? (
                <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
                  <Loader2 className="w-8 h-8 animate-spin" style={{ margin: '0 auto 12px' }} />
                  Loading styles...
                </div>
              ) : sunoLibraryPrompts.length === 0 ? (
                <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: 'rgba(255,255,255,0.5)' }}>
                  Select a category or search for styles
                </div>
              ) : (
                sunoLibraryPrompts.map((prompt, i) => (
                  <div
                    key={i}
                    style={{
                      padding: '14px',
                      background: 'rgba(0,0,0,0.3)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}
                  >
                    <p style={{ 
                      margin: 0, 
                      color: '#fff', 
                      fontSize: '0.85rem',
                      lineHeight: '1.4',
                      flex: 1
                    }}>
                      {prompt.length > 100 ? prompt.slice(0, 100) + '...' : prompt}
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => {
                          setBeatPrompt(prompt);
                          setShowBeatLibrary(false);
                          toast.success("Style loaded to Beat Generator!");
                        }}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: 'linear-gradient(90deg, #9F47FF, #8a2be2)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px'
                        }}
                      >
                        🎹 Use
                      </button>
                      {/* Drum Pattern */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          generateDrumFromStylePrompt(prompt);
                          toast.info("🥁 Generating drums...");
                        }}
                        style={{
                          padding: '10px',
                          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.4), rgba(99, 102, 241, 0.4))',
                          border: '1px solid rgba(139, 92, 246, 0.5)',
                          borderRadius: '8px',
                          color: '#a78bfa',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        🥁
                      </button>
                      {/* MIDI Download */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          generateMidiFromPrompt(prompt);
                        }}
                        disabled={isGeneratingMidi}
                        style={{
                          padding: '10px',
                          background: isGeneratingMidi 
                            ? 'rgba(16, 185, 129, 0.2)'
                            : 'linear-gradient(135deg, #10b981, #059669)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          cursor: isGeneratingMidi ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        ⬇️
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Pagination */}
            {sunoLibraryTotalPages > 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                gap: '12px',
                marginTop: '20px'
              }}>
                <button
                  onClick={() => loadSunoLibraryPrompts(sunoLibraryCategory, sunoLibraryPage - 1)}
                  disabled={sunoLibraryPage <= 1}
                  style={{
                    padding: '8px 16px',
                    background: sunoLibraryPage <= 1 ? 'rgba(255,255,255,0.1)' : 'rgba(159,71,255,0.2)',
                    border: '1px solid rgba(159,71,255,0.4)',
                    borderRadius: '8px',
                    color: sunoLibraryPage <= 1 ? 'rgba(255,255,255,0.3)' : '#9F47FF',
                    cursor: sunoLibraryPage <= 1 ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  Previous
                </button>
                <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                  Page {sunoLibraryPage} of {sunoLibraryTotalPages}
                </span>
                <button
                  onClick={() => loadSunoLibraryPrompts(sunoLibraryCategory, sunoLibraryPage + 1)}
                  disabled={sunoLibraryPage >= sunoLibraryTotalPages}
                  style={{
                    padding: '8px 16px',
                    background: sunoLibraryPage >= sunoLibraryTotalPages ? 'rgba(255,255,255,0.1)' : 'rgba(159,71,255,0.2)',
                    border: '1px solid rgba(159,71,255,0.4)',
                    borderRadius: '8px',
                    color: sunoLibraryPage >= sunoLibraryTotalPages ? 'rgba(255,255,255,0.3)' : '#9F47FF',
                    cursor: sunoLibraryPage >= sunoLibraryTotalPages ? 'not-allowed' : 'pointer',
                    fontWeight: 600
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}

      
      {/* Song Generated Modal */}
      {showSongModal && currentSong && (
        <div className="modal-overlay" onClick={() => setShowSongModal(false)}>
          <motion.div 
            className="song-modal" 
            onClick={e => e.stopPropagation()} 
            initial={{ scale: 0.9, opacity: 0 }} 
            animate={{ scale: 1, opacity: 1 }}
            data-testid="song-modal"
            style={{
              background: 'linear-gradient(135deg, rgba(15,15,26,0.98) 0%, rgba(26,26,46,0.98) 100%)',
              border: '1px solid rgba(204, 255, 0, 0.3)',
              borderRadius: '20px',
              padding: '30px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative'
            }}
          >
            <button 
              className="modal-close" 
              onClick={() => setShowSongModal(false)}
              style={{ position: 'absolute', top: '15px', right: '15px', background: 'none', border: 'none', color: '#fff', cursor: 'pointer', zIndex: 10 }}
            >
              <X />
            </button>
            
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <CheckCircle className="w-12 h-12" style={{ color: '#9F47FF', margin: '0 auto 16px' }} />
              <h2 style={{ color: '#fff', margin: 0, fontSize: '1.5rem' }}>🎉 Song Generated!</h2>
              <p style={{ color: 'rgba(255,255,255,0.6)', margin: '8px 0 0' }}>{currentSong.title}</p>
            </div>
            
            {currentSong.image_url && (
              <img 
                src={currentSong.image_url} 
                alt="Song artwork" 
                style={{ width: '180px', height: '180px', borderRadius: '12px', margin: '0 auto 20px', display: 'block', objectFit: 'cover' }}
              />
            )}
            
            {currentSong.audio_url && (
              <audio preload="metadata" 
                controls 
                src={currentSong.audio_url} 
                style={{ width: '100%', marginBottom: '20px' }}
                data-testid="song-audio-player"
              />
            )}
            
            {currentSong.duration && (
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', textAlign: 'center', marginBottom: '16px' }}>
                Duration: {Math.round(currentSong.duration)}s
              </p>
            )}

            {/* MUSIC AI ACTION GRID */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '10px',
              marginBottom: '16px'
            }}>
              {/* Download Options */}
              <button
                onClick={() => downloadSong(currentSong.audio_url, `${currentSong.title || 'song'}.wav`, 'wav')}
                style={{
                  padding: '12px',
                  background: 'linear-gradient(135deg, #9F47FF 0%, #ff3385 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.8rem'
                }}
              >
                <Download className="w-5 h-5" />
                <span>WAV (HQ)</span>
              </button>
              <button
                onClick={() => downloadStems(currentSong.audio_url, currentSong.title || 'song')}
                style={{
                  padding: '12px',
                  background: 'linear-gradient(135deg, #9F47FF 0%, #0099cc 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.8rem'
                }}
              >
                <Layers className="w-5 h-5" />
                <span>STEMS ZIP</span>
              </button>
            </div>

            {/* More Actions */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(4, 1fr)', 
              gap: '8px',
              marginBottom: '16px'
            }}>
              <button
                onClick={() => { updateWorkflow({ stylePrompt: currentSong.prompt || '', source: 'song-remix' }); setShowSongModal(false); toast.success("Prompt loaded - create a remix!"); }}
                style={{
                  padding: '10px 8px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.7rem'
                }}
              >
                <Zap className="w-4 h-4" style={{ color: '#9F47FF' }} />
                <span>Remix</span>
              </button>
              <button
                onClick={() => toast.info("Extend feature coming soon!")}
                style={{
                  padding: '10px 8px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.7rem'
                }}
              >
                <Sparkles className="w-4 h-4" style={{ color: '#9F47FF' }} />
                <span>Extend</span>
              </button>
              <button
                onClick={() => toast.info("Cover feature coming soon!")}
                style={{
                  padding: '10px 8px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.7rem'
                }}
              >
                <Mic className="w-4 h-4" style={{ color: '#9F47FF' }} />
                <span>Cover</span>
              </button>
              <button
                onClick={() => setShowMashup(true)}
                style={{
                  padding: '10px 8px',
                  background: 'rgba(255,153,0,0.2)',
                  border: '1px solid rgba(255,153,0,0.4)',
                  borderRadius: '8px',
                  color: '#ff9500',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.7rem'
                }}
                data-testid="mashup-btn"
              >
                <Layers className="w-4 h-4" style={{ color: '#ff9500' }} />
                <span>Mashup</span>
              </button>
            </div>

            {/* Share & Utility Actions */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '8px'
            }}>
              <button
                onClick={() => { universalCopy(currentSong.audio_url, "Link copied! Tap & hold to paste"); }}
                style={{
                  padding: '10px 8px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.7rem'
                }}
              >
                <Share2 className="w-4 h-4" style={{ color: '#9F47FF' }} />
                <span>Share</span>
              </button>
              <button
                onClick={() => { universalCopy(currentSong.prompt || '', "Style & lyrics copied! Tap & hold to paste"); }}
                style={{
                  padding: '10px 8px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.7rem'
                }}
              >
                <Copy className="w-4 h-4" style={{ color: '#9F47FF' }} />
                <span>Copy Style</span>
              </button>
              <button
                onClick={() => { 
                  const subject = encodeURIComponent(`Check out my song: ${currentSong.title}`);
                  const body = encodeURIComponent(`I created this song with MANNIS!\n\nListen here: ${currentSong.audio_url}`);
                  window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                }}
                style={{
                  padding: '10px 8px',
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.7rem'
                }}
              >
                <Mail className="w-4 h-4" style={{ color: '#9F47FF' }} />
                <span>Email</span>
              </button>
            </div>
          </motion.div>
        </div>
      )}

      
      {/* Prompts Popup */}
      {showPromptsPopup && filteredPrompts && filteredPrompts.length > 0 && (
        <div className="prompts-popup-overlay" onClick={() => setShowPromptsPopup(false)}>
          <div className="prompts-popup" onClick={e => e.stopPropagation()}>
            <div className="prompts-popup-header">
              <h2>Your Prompts ({filteredPrompts.length})</h2>
              <button className="close-popup" onClick={() => setShowPromptsPopup(false)}>✕</button>
            </div>
            <div className="prompts-popup-content">
              <textarea 
                readOnly 
                value={getPromptsText()} 
                className="prompts-textarea"
                onClick={(e) => e.target.select()}
              />
            </div>
            <div className="prompts-popup-footer">
              <button 
                className="popup-copy-btn"
                onClick={() => {
                  const textarea = document.querySelector('.prompts-textarea');
                  textarea.select();
                  document.execCommand('copy');
                  toast.success("Copied!");
                }}
              >
                Copy All Text
              </button>
              <button 
                className="popup-select-btn"
                onClick={() => {
                  const textarea = document.querySelector('.prompts-textarea');
                  textarea.focus();
                  textarea.select();
                  toast.info("Text selected! Tap Copy above");
                }}
              >
                Select All
              </button>
            </div>
          </div>
        </div>
      )}

          {/* CREATE VIEW - Main Content */}
          {activeView === 'create' && (
            <div className="create-section">


        {/* ===== DNA FUSION - HERO FEATURE ===== */}
        <style>{`
          @keyframes dnaFusionGlow {
            0%, 100% { box-shadow: 0 0 25px rgba(236,72,153,0.5), 0 0 50px rgba(0,212,255,0.25); }
            50% { box-shadow: 0 0 45px rgba(0,212,255,0.6), 0 0 80px rgba(236,72,153,0.35); }
          }
          @keyframes dnaFusionShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}</style>
        <div data-testid="dna-fusion-workbench" style={{
          background: 'linear-gradient(135deg, rgba(236,72,153,0.12), rgba(0,212,255,0.08))',
          border: '1px solid rgba(236,72,153,0.35)',
          borderRadius: '18px',
          padding: '18px 20px',
          marginBottom: '16px',
          animation: 'dnaFusionGlow 3s ease-in-out infinite'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <StepBadge n={1} />
            <span style={{ fontSize: '1.6rem' }}>🧬</span>
            <div>
              <div style={{ color: '#fff', fontWeight: 900, fontSize: '1.15rem', letterSpacing: '0.06em' }}>MANNIS FUSION WORKBENCH</div>
              <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.76rem' }}>Pick any two genres — chords from Genre A, rhythm from Genre B. 100% your combination.</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '160px' }}>
              <label style={{ display: 'block', color: '#ec4899', fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.1em', marginBottom: '4px' }}>GENRE A — CHORDS</label>
              <select
                data-testid="fusion-genre-a-select"
                value={fusionGenreA}
                onChange={(e) => setFusionGenreA(e.target.value)}
                style={{ width: '100%', padding: '11px 12px', background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(236,72,153,0.4)', borderRadius: '10px', color: '#fff', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer' }}
              >
                <option value="">Select Genre A...</option>
                {fusionGenres.map((g) => <option key={g} value={g} disabled={g === fusionGenreB}>{g}</option>)}
              </select>
            </div>
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.3rem', fontWeight: 800, marginTop: '16px' }}>✕</span>
            <div style={{ flex: 1, minWidth: '160px' }}>
              <label style={{ display: 'block', color: '#00d4ff', fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.1em', marginBottom: '4px' }}>GENRE B — RHYTHM & VOCALS</label>
              <select
                data-testid="fusion-genre-b-select"
                value={fusionGenreB}
                onChange={(e) => setFusionGenreB(e.target.value)}
                style={{ width: '100%', padding: '11px 12px', background: 'rgba(0,0,0,0.45)', border: '1px solid rgba(0,212,255,0.4)', borderRadius: '10px', color: '#fff', fontSize: '0.88rem', fontWeight: 600, cursor: 'pointer' }}
              >
                <option value="">Select Genre B...</option>
                {fusionGenres.map((g) => <option key={g} value={g} disabled={g === fusionGenreA}>{g}</option>)}
              </select>
            </div>
            <button
              data-testid="fuse-dna-btn"
              disabled={dnaFusionLoading || !fusionGenreA || !fusionGenreB}
              onClick={() => runDnaFusion(`?genre_a=${encodeURIComponent(fusionGenreA)}&genre_b=${encodeURIComponent(fusionGenreB)}`)}
              style={{
                padding: '12px 26px', marginTop: '16px',
                background: (!fusionGenreA || !fusionGenreB) ? 'rgba(255,255,255,0.08)' : 'linear-gradient(270deg, #ec4899, #8b5cf6, #00d4ff, #ec4899)',
                backgroundSize: '300% 300%',
                animation: (!fusionGenreA || !fusionGenreB) ? 'none' : 'dnaFusionShift 6s ease infinite',
                border: 'none', borderRadius: '12px',
                color: (!fusionGenreA || !fusionGenreB) ? 'rgba(255,255,255,0.35)' : '#fff',
                fontWeight: 900, fontSize: '0.95rem', letterSpacing: '0.06em',
                cursor: (dnaFusionLoading || !fusionGenreA || !fusionGenreB) ? 'not-allowed' : 'pointer'
              }}
            >
              {dnaFusionLoading ? '⏳ FUSING...' : '🧬 FUSE DNA'}
            </button>
            <button
              data-testid="dna-fusion-random-btn"
              disabled={dnaFusionLoading}
              onClick={() => runDnaFusion()}
              title="Random surprise fusion"
              style={{ padding: '12px 16px', marginTop: '16px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '12px', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
            >
              🎲 Surprise Me
            </button>
          </div>
        </div>
        
        {/* CURATED DNA BLENDS - Curation Menu */}
        <button
          data-testid="dna-blends-toggle"
          onClick={async () => {
            const next = !showBlendMenu;
            setShowBlendMenu(next);
            if (next && dnaBlends.length === 0) {
              try {
                const res = await axios.get(`${API}/dna-blends`);
                setDnaBlends(res.data.blends || []);
              } catch (e) { toast.error('Could not load blends'); }
            }
          }}
          style={{
            width: '100%', padding: '10px 16px', marginBottom: showBlendMenu ? '10px' : '16px',
            background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.35)',
            borderRadius: '12px', color: '#a78bfa', fontWeight: 700, fontSize: '0.82rem',
            cursor: 'pointer', letterSpacing: '0.05em'
          }}
        >
          🎛️ CURATED DNA BLENDS — choose your mix manually {showBlendMenu ? '▴' : '▾'}
        </button>
        {showBlendMenu && (
          <div data-testid="dna-blends-menu" style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '10px', marginBottom: '16px'
          }}>
            {dnaBlends.map((b) => (
              <button
                key={b.id}
                data-testid={`dna-blend-${b.id}`}
                disabled={dnaFusionLoading}
                onClick={async () => {
                  setDnaFusionLoading(true);
                  try {
                    const res = await axios.get(`${API}/dna-fusion?blend=${b.id}`);
                    setDnaFusionResult(res.data);
                    toast.success(`🧬 ${res.data.title} created!`);
                  } catch (e) { toast.error('Blend failed. Try again!'); }
                  setDnaFusionLoading(false);
                }}
                style={{
                  textAlign: 'left', padding: '12px 14px',
                  background: 'linear-gradient(135deg, rgba(139,92,246,0.12), rgba(236,72,153,0.08))',
                  border: '1px solid rgba(139,92,246,0.3)', borderRadius: '12px', cursor: 'pointer'
                }}
              >
                <div style={{ color: '#fff', fontWeight: 800, fontSize: '0.88rem', marginBottom: '4px' }}>🧬 {b.name}</div>
                <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.72rem', lineHeight: 1.4, marginBottom: '6px' }}>{b.tagline}</div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {[...new Set(b.genres)].map((g) => (
                    <span key={g} style={{ background: 'rgba(0,212,255,0.12)', color: '#00d4ff', padding: '2px 8px', borderRadius: '10px', fontSize: '0.62rem', fontWeight: 700 }}>{g}</span>
                  ))}
                </div>
              </button>
            ))}
          </div>
        )}
        
        {/* DNA Fusion Result */}
        {dnaFusionResult && (
          <div data-testid="dna-fusion-result" style={{
            background: 'linear-gradient(135deg, rgba(236,72,153,0.1), rgba(0,212,255,0.08))',
            border: '1px solid rgba(236,72,153,0.35)',
            borderRadius: '16px',
            padding: '18px 20px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <span style={{ fontSize: '1.2rem' }}>🧬</span>
              <span data-testid="dna-fusion-title" style={{ color: '#fff', fontWeight: 800, fontSize: '1.05rem' }}>{dnaFusionResult.title}</span>
              {dnaFusionResult.uniqueness_score != null && (
                <span style={{ background: 'rgba(16,185,129,0.2)', color: '#10b981', padding: '3px 10px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700 }}>
                  🛡️ {dnaFusionResult.uniqueness_score}% unique
                </span>
              )}
              <span style={{ background: 'rgba(0,212,255,0.15)', color: '#00d4ff', padding: '3px 10px', borderRadius: '12px', fontSize: '0.72rem', fontWeight: 700 }}>
                {dnaFusionResult.bpm} BPM
              </span>
              <button
                data-testid="dna-fusion-close-btn"
                onClick={() => setDnaFusionResult(null)}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '1rem' }}
              >✕</button>
            </div>
            <div data-testid="dna-fusion-prompt" style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px', padding: '12px 14px', color: '#fff', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '10px' }}>
              {dnaFusionResult.style_prompt}
            </div>
            <details style={{ marginBottom: '12px' }}>
              <summary style={{ color: '#4da3ff', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>🎬 View Six-Part Structure</summary>
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', lineHeight: 1.5, marginTop: '8px' }}>
                {dnaFusionResult.structure}
              </pre>
            </details>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                data-testid="dna-fusion-copy-btn"
                onClick={() => universalCopy(dnaFusionResult.style_prompt, '🧬 Fusion prompt copied!')}
                style={{ flex: 1, minWidth: '130px', padding: '11px', background: 'linear-gradient(90deg, #ec4899, #a855f7)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
              >📋 Copy Prompt</button>
              <button
                data-testid="dna-fusion-use-btn"
                onClick={() => {
                  updateWorkflow({ stylePrompt: dnaFusionResult.style_prompt, bpm: dnaFusionResult.bpm, source: 'dna-fusion' });
                  toast.success('🧬 Fusion loaded into workflow!');
                }}
                style={{ flex: 1, minWidth: '130px', padding: '11px', background: 'linear-gradient(90deg, #00d4ff, #6366f1)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
              >⚡ Use In Workflow</button>
              <button
                data-testid="dna-fusion-reroll-btn"
                onClick={async () => {
                  setDnaFusionLoading(true);
                  try {
                    const res = await axios.get(`${API}/dna-fusion`);
                    setDnaFusionResult(res.data);
                  } catch (e) { toast.error('Fusion failed'); }
                  setDnaFusionLoading(false);
                }}
                style={{ flex: 1, minWidth: '130px', padding: '11px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
              >🎲 Fuse Again</button>
            </div>
            
            {/* GENERATE FULL SONG */}
            <div style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
              <button
                data-testid="enhance-toggle"
                onClick={() => setEnhancePrompt(v => !v)}
                title="When ON, the Autonomous Prompt Engine adds studio-mastering boosters to your prompt. Turn OFF to send your raw prompt."
                style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 10px', padding: '7px 10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '8px', color: '#fff', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', width: '100%' }}
              >
                <span style={{ width: '34px', height: '18px', borderRadius: '999px', background: enhancePrompt ? '#22c55e' : 'rgba(255,255,255,0.2)', position: 'relative', transition: 'background-color 0.2s', flexShrink: 0 }}>
                  <span style={{ position: 'absolute', top: '2px', left: enhancePrompt ? '18px' : '2px', width: '14px', height: '14px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                </span>
                <span>Auto-Enhance Prompt: <strong style={{ color: enhancePrompt ? '#4ade80' : '#f87171' }}>{enhancePrompt ? 'ON' : 'OFF (raw prompt)'}</strong></span>
              </button>
              {(!fusionSong || fusionSong.status === 'failed') && (
                <button
                  data-testid="fusion-generate-song-btn"
                  onClick={generateFusionSong}
                  style={{ width: '100%', padding: '13px', background: 'linear-gradient(270deg, #ec4899, #8b5cf6, #00d4ff, #ec4899)', backgroundSize: '300% 300%', animation: 'dnaFusionShift 6s ease infinite', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.05em', cursor: 'pointer' }}
                >🎵 GENERATE FULL SONG (30s)</button>
              )}
              {fusionSong?.status === 'generating' && (
                <div data-testid="fusion-song-progress">
                  <div style={{ color: '#fff', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px' }}>
                    🎵 Suno V5.5 is producing your fusion... {fusionSong.progress}%
                  </div>
                  <div style={{ height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${fusionSong.progress}%`, height: '100%', background: 'linear-gradient(90deg, #ec4899, #00d4ff)', transition: 'width 0.5s' }} />
                  </div>
                  {fusionSong.note && (
                    <div data-testid="fusion-still-rendering-note" style={{ color: '#fbbf24', fontSize: '0.75rem', fontWeight: 600, marginTop: '8px' }}>
                      {fusionSong.note}
                    </div>
                  )}
                </div>
              )}
              {fusionSong?.status === 'completed' && fusionSong.url && (
                <div data-testid="fusion-song-player">
                  <div style={{ color: '#10b981', fontSize: '0.82rem', fontWeight: 700, marginBottom: '6px' }}>✅ Fusion song ready — built from your blueprint</div>
                  <audio controls src={fusionSong.url} style={{ width: '100%' }} data-testid="fusion-song-audio" />
                  <button
                    data-testid="fusion-song-new-btn"
                    onClick={() => setFusionSong(null)}
                    style={{ marginTop: '8px', padding: '8px 14px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', color: '#fff', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}
                  >🔄 Generate Another</button>
                </div>
              )}
            </div>
            
            {/* PRO EXPORT MENU */}
            <div data-testid="dna-fusion-pro-export" style={{ marginTop: '14px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <span style={{ color: '#fbbf24', fontWeight: 800, fontSize: '0.78rem', letterSpacing: '0.1em' }}>👑 PRO EXPORT</span>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  data-testid="fusion-export-pdf-btn"
                  disabled={fusionExporting === 'pdf'}
                  onClick={async () => {
                    setFusionExporting('pdf');
                    try {
                      const res = await axios.post(`${API}/dna-fusion/export-pdf`, dnaFusionResult, { responseType: 'blob' });
                      await shareOrDownloadBlob(res.data, `${dnaFusionResult.title.replace(/[^A-Za-z0-9_-]+/g, '_')}_blueprint.pdf`, 'application/pdf', { successMessage: '📄 Blueprint PDF downloaded!' });
                    } catch (e) { toast.error('PDF export failed. Try again.'); }
                    setFusionExporting(null);
                  }}
                  style={{ flex: 1, minWidth: '150px', padding: '11px', background: 'linear-gradient(90deg, #10b981, #0ea5e9)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: fusionExporting === 'pdf' ? 'wait' : 'pointer', opacity: fusionExporting === 'pdf' ? 0.7 : 1 }}
                >{fusionExporting === 'pdf' ? '⏳ Building PDF...' : '📄 Blueprint PDF'}</button>
                <button
                  data-testid="fusion-export-midi-btn"
                  disabled={fusionExporting === 'midi'}
                  onClick={async () => {
                    setFusionExporting('midi');
                    toast.info('🎹 Generating MIDI from your blueprint...');
                    try {
                      const res = await axios.post(`${API}/generate-midi`, {
                        style_prompt: dnaFusionResult.style_prompt,
                        bars: 32,
                        audio_url: fusionSong?.status === 'completed' && fusionSong.url ? fusionSong.url : null
                      });
                      const filename = res.data.filename;
                      if (!filename) throw new Error('No MIDI file returned');
                      setFusionLastMidi(filename);
                      const dl = await axios.get(`${API}/download-midi/${encodeURIComponent(filename)}`, { responseType: 'blob' });
                      await shareOrDownloadBlob(dl.data, `${dnaFusionResult.title.replace(/[^A-Za-z0-9_-]+/g, '_')}.mid`, 'audio/midi', {
                        successMessage: res.data.synced_to_audio
                          ? `🎹 MIDI downloaded — synced to your Suno track (${res.data.params?.tempo} BPM, ${res.data.params?.key})!`
                          : `🎹 MIDI downloaded! ${res.data.params?.tracks?.length || 10} tracks, ready for your DAW`,
                      });
                    } catch (e) { toast.error('MIDI export failed. Try again.'); }
                    setFusionExporting(null);
                  }}
                  style={{ flex: 1, minWidth: '150px', padding: '11px', background: 'linear-gradient(90deg, #8b5cf6, #6366f1)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: fusionExporting === 'midi' ? 'wait' : 'pointer', opacity: fusionExporting === 'midi' ? 0.7 : 1 }}
                >{fusionExporting === 'midi' ? '⏳ Generating MIDI...' : '🎹 Download MIDI'}</button>
                <button
                  data-testid="fusion-export-studio-wav-btn"
                  disabled={dnaStemsLoading === 'fusion' || !(fusionSong?.status === 'completed' && fusionSong?.url)}
                  onClick={() => saveWavFromUrl(fusionSong.url, `${dnaFusionResult.title.replace(/[^A-Za-z0-9_-]+/g, '_')}_song.wav`)}
                  style={{ flex: 1, minWidth: '150px', padding: '11px', background: 'linear-gradient(90deg, #f59e0b, #ec4899)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: dnaStemsLoading === 'fusion' ? 'wait' : 'pointer', opacity: (dnaStemsLoading === 'fusion' || !(fusionSong?.status === 'completed' && fusionSong?.url)) ? 0.7 : 1 }}
                >{dnaStemsLoading === 'fusion' ? '⏳ Rendering WAV...' : '🎧 Download Studio WAV'}</button>
              </div>
            </div>
          </div>
        )}

        {/* FUSION LIBRARY - audio, MIDI & style prompts */}
        <div style={{ marginTop: '12px' }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={saveFusionToLibrary}
              disabled={!dnaFusionResult}
              style={{
                flex: '1 1 160px', padding: '11px 14px',
                background: dnaFusionResult ? 'linear-gradient(90deg, #8b5cf6, #ec4899)' : 'rgba(100,100,100,0.3)',
                border: 'none', borderRadius: '8px', color: dnaFusionResult ? '#fff' : '#777',
                fontWeight: 700, fontSize: '0.78rem', cursor: dnaFusionResult ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
              }}
              data-testid="fusion-save-library-btn"
            >
              💾 SAVE TO LIBRARY
            </button>
            <button
              onClick={() => { const next = !showFusionLibrary; setShowFusionLibrary(next); if (next) loadFusionLibrary(); }}
              style={{
                flex: '1 1 160px', padding: '11px 14px',
                background: 'rgba(139, 92, 246, 0.15)', border: '1px solid rgba(139, 92, 246, 0.4)',
                borderRadius: '8px', color: '#a78bfa', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
              }}
              data-testid="fusion-toggle-library-btn"
            >
              📚 FUSION LIBRARY {fusionLibrary.length > 0 ? `(${fusionLibrary.length})` : ''}
            </button>
          </div>
          {showFusionLibrary && (
            <div style={{ marginTop: '10px', maxHeight: '280px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="fusion-library-list">
              <audio ref={fusionAudioRef} onEnded={() => setFusionPlayingId(null)} style={{ display: 'none' }} data-testid="fusion-library-audio" />
              {fusionLibrary.length === 0 && (
                <div style={{ color: '#9ca3af', fontSize: '0.72rem', textAlign: 'center', padding: '10px' }}>
                  Nothing saved yet — fuse two genres, then hit SAVE TO LIBRARY to keep the prompt, structure, audio & MIDI.
                </div>
              )}
              {fusionLibrary.map((item) => (
                <div key={item.id} style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: '8px', padding: '10px' }} data-testid={`fusion-lib-item-${item.id}`}>
                  <div style={{ color: '#e9d5ff', fontSize: '0.74rem', fontWeight: 700, marginBottom: '4px' }}>{item.name}</div>
                  {item.structure && (
                    <div style={{ color: '#9ca3af', fontSize: '0.64rem', marginBottom: '6px', whiteSpace: 'pre-line', maxHeight: '52px', overflow: 'hidden' }}>{item.structure}</div>
                  )}
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => universalCopy(item.style_prompt, '📋 Style prompt copied!')}
                      style={{ padding: '6px 10px', background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)', borderRadius: '6px', color: '#93c5fd', fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer' }}
                      data-testid={`fusion-lib-copy-${item.id}`}
                    >COPY PROMPT</button>
                    {item.audio_url && (
                      <button
                        onClick={() => toggleFusionPlay(item)}
                        style={{ padding: '6px 10px', background: 'rgba(139,92,246,0.2)', border: '1px solid rgba(139,92,246,0.45)', borderRadius: '6px', color: '#c4b5fd', fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer' }}
                        data-testid={`fusion-lib-play-${item.id}`}
                      >{fusionPlayingId === item.id ? '⏸ PAUSE' : '▶ PLAY'}</button>
                    )}
                    {item.audio_url && (
                      <button
                        onClick={() => saveWavFromUrl(item.audio_url, `${(item.name || 'fusion').replace(/[^A-Za-z0-9_-]+/g, '_').slice(0, 40)}.wav`)}
                        style={{ padding: '6px 10px', background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)', borderRadius: '6px', color: '#86efac', fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer' }}
                        data-testid={`fusion-lib-wav-${item.id}`}
                      >⬇ WAV</button>
                    )}
                    {!item.audio_url && (
                      <button
                        onClick={() => renderLibraryAudio(item, 'fusion')}
                        style={{ padding: '6px 10px', background: 'rgba(34,197,94,0.12)', border: '1px dashed rgba(34,197,94,0.45)', borderRadius: '6px', color: '#86efac', fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer' }}
                        data-testid={`fusion-lib-render-${item.id}`}
                      >🎧 RENDER WAV</button>
                    )}
                    {item.midi_filename && (
                      <a
                        href={`${API}/download-midi/${encodeURIComponent(item.midi_filename)}`}
                        download={item.midi_filename}
                        onClick={() => toast.success('🎹 Saving MIDI...', { duration: 4000 })}
                        style={{ padding: '6px 10px', background: 'rgba(245,158,11,0.2)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: '6px', color: '#fcd34d', fontSize: '0.64rem', fontWeight: 700, textDecoration: 'none' }}
                        data-testid={`fusion-lib-midi-${item.id}`}
                      >🎹 MIDI</a>
                    )}
                    <button
                      onClick={() => deleteFusionItem(item.id)}
                      style={{ padding: '6px 10px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '6px', color: '#fca5a5', fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer' }}
                      data-testid={`fusion-lib-delete-${item.id}`}
                    >✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* UNIFIED WORKFLOW BAR - Shows current prompt across all tools */}
        <div className="unified-workflow-container" style={{
          background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.08), rgba(139, 92, 246, 0.08))',
          border: '1px solid rgba(0, 212, 255, 0.2)',
          borderRadius: '16px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <div className="unified-workflow-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                background: workflowPrompt.stylePrompt ? '#10b981' : '#f97316',
                boxShadow: workflowPrompt.stylePrompt ? '0 0 10px #10b981' : '0 0 10px #f97316',
                animation: 'pulse 2s infinite',
                flexShrink: 0
              }} />
              <span style={{ color: '#00d4ff', fontWeight: 700, fontSize: '0.85rem' }}>UNIFIED WORKFLOW</span>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                {workflowPrompt.source ? `Source: ${workflowPrompt.source}` : 'No prompt loaded'}
              </span>
              {/* Drum indicator */}
              {workflowPrompt.drumPattern && (
                <span style={{ 
                  padding: '2px 8px', 
                  background: 'rgba(139, 92, 246, 0.2)', 
                  borderRadius: '10px', 
                  fontSize: '0.7rem',
                  color: '#8b5cf6',
                  fontWeight: 600
                }}>
                  🥁 Drums Added
                </span>
              )}
              {/* Mastered track indicator */}
              {workflowPrompt.masteredTrack && (
                <span
                  title={workflowPrompt.masteredTrack.presets}
                  data-testid="workflow-mastered-chip"
                  style={{
                    padding: '2px 8px',
                    background: 'rgba(34, 197, 94, 0.2)',
                    borderRadius: '10px',
                    fontSize: '0.7rem',
                    color: '#22c55e',
                    fontWeight: 600,
                    maxWidth: '220px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  🎛️ Mastered: {workflowPrompt.masteredTrack.filename}
                </span>
              )}
            </div>
            <div className="unified-workflow-buttons" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => { setAwTask(null); setShowAutoWorkflow(true); }}
                style={{
                  padding: '6px 14px',
                  background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
                data-testid="auto-workflow-btn"
              >
                ⚡ Auto Pipeline
              </button>
              <button
                onClick={() => {
                  const fullPrompt = `${workflowPrompt.stylePrompt || beatPrompt}\n\nBPM: ${workflowPrompt.bpm || selectedBPM || 120}\nKey: ${workflowPrompt.key || selectedKey || 'C'} ${workflowPrompt.mode || selectedMode || 'minor'}${workflowPrompt.voiceSample ? `\nVoice: ${workflowPrompt.voiceSample}` : ''}${workflowPrompt.masteredTrack ? `\nMastered Track: ${workflowPrompt.masteredTrack.filename} (${workflowPrompt.masteredTrack.presets})` : ''}${workflowPrompt.lyrics ? `\n\nLyrics:\n${workflowPrompt.lyrics}` : ''}`;
                  universalCopy(fullPrompt, 'Full workflow prompt copied!');
                }}
                style={{
                  padding: '6px 12px',
                  background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <Copy className="w-3 h-3" style={{ marginRight: '4px' }} />
                Copy All
              </button>
              {/* Copy Combined Suno Prompt button */}
              <button
                onClick={copyCombinedSunoPrompt}
                style={{
                  padding: '6px 12px',
                  background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
                title="Copy combined style + drums for Suno"
              >
                <Sparkles className="w-3 h-3" style={{ marginRight: '4px' }} />
                Suno Ready
              </button>
              <button
                onClick={() => {
                  setWorkflowPrompt({
                    stylePrompt: '',
                    bpm: 120,
                    key: 'C',
                    mode: 'minor',
                    genre: '',
                    mood: '',
                    voiceSample: '',
                    lyrics: '',
                    drumPattern: '',
                    drumSunoPrompt: '',
                    masteredTrack: null,
                    source: ''
                  });
                  setBeatPrompt('');
                  toast.info('Workflow cleared');
                }}
                style={{
                  padding: '6px 12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: 'var(--text-secondary)',
                  fontSize: '0.75rem',
                  cursor: 'pointer'
                }}
                data-testid="clear-workflow-btn"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Current Prompt Display - EDITABLE */}
          <div style={{
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '10px',
            padding: '12px',
            marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label 
                onClick={() => setFullscreenWorkflowSection('style')}
                style={{ color: '#00d4ff', fontSize: '0.7rem', fontWeight: 700, cursor: 'pointer' }}
              >
                1. STYLE PROMPT
              </label>
            </div>
            <textarea
              value={beatPrompt || workflowPrompt.stylePrompt || ''}
              onChange={(e) => {
                setBeatPrompt(e.target.value);
                updateWorkflow({ stylePrompt: e.target.value, source: 'manual' });
              }}
              placeholder="Enter or edit your style prompt here... (e.g., Modern trap, 140 BPM, hard 808s, dark melodies, aggressive hi-hats)"
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '10px',
                background: 'rgba(0, 212, 255, 0.05)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '0.85rem',
                lineHeight: 1.5,
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              data-testid="style-prompt-editor"
            />
          </div>

          {/* Quick Tool Access - All connected to same prompt */}
          <div className="tool-buttons-grid" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                // Scroll to Drop Your Tracks section
                const dropzone = document.querySelector('[data-testid="dropzone"]');
                if (dropzone) {
                  dropzone.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  toast.info('📤 Drop your MP3/WAV files below to analyze!');
                }
              }}
              style={{
                padding: '8px 14px',
                background: 'rgba(0, 212, 255, 0.15)',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '8px',
                color: '#00d4ff',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              data-testid="analyze-mp3-btn"
            >
              <Upload className="w-3 h-3" /> 1. Analyze MP3
            </button>
            <button
              onClick={() => { 
                setShowSunoLibrary(true);
                toast.info('🎹 Browse 70,000+ style prompts - click "USE THIS STYLE" to load');
              }}
              style={{
                padding: '8px 14px',
                background: 'rgba(139, 92, 246, 0.15)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                color: '#8b5cf6',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              data-testid="load-preset-btn"
            >
              <Star className="w-3 h-3" /> 2. 70K Prompts
            </button>
            <button
              onClick={() => {
                setActiveView('voices');
                toast.info('🎤 Tap a voice to add it to your workflow');
              }}
              style={{
                padding: '8px 14px',
                background: 'rgba(236, 72, 153, 0.15)',
                border: '1px solid rgba(236, 72, 153, 0.3)',
                borderRadius: '8px',
                color: '#ec4899',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              data-testid="add-voice-btn"
            >
              <Mic2 className="w-3 h-3" /> 3. Add Voice
            </button>
            <button
              onClick={() => {
                setActiveView('rhyme');
                toast.info('📝 Generate lyrics and add them to your workflow');
              }}
              style={{
                padding: '8px 14px',
                background: 'rgba(249, 115, 22, 0.15)',
                border: '1px solid rgba(249, 115, 22, 0.3)',
                borderRadius: '8px',
                color: '#f97316',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              data-testid="add-lyrics-btn"
            >
              <FileText className="w-3 h-3" /> 4. Add Lyrics
            </button>
            <button
              onClick={() => {
                const newState = !showWorldVoiceDNA;
                setShowWorldVoiceDNA(newState);
                setShowSingingGenerator(false);
                setShowDNALyrics(false);
                if (!worldVoiceData) loadWorldVoiceData();
                // Auto-scroll to World Voice DNA panel when opened
                if (newState) {
                  setTimeout(() => {
                    const panel = document.querySelector('[data-testid="world-voice-dna-panel"]');
                    if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 100);
                }
              }}
              style={{
                padding: '8px 14px',
                background: showWorldVoiceDNA 
                  ? 'linear-gradient(135deg, #06b6d4, #a855f7)'
                  : 'linear-gradient(135deg, rgba(6, 182, 212, 0.2), rgba(168, 85, 247, 0.2))',
                border: '1px solid rgba(6, 182, 212, 0.4)',
                borderRadius: '8px',
                color: showWorldVoiceDNA ? '#fff' : '#06b6d4',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: showWorldVoiceDNA 
                  ? '0 4px 15px rgba(6, 182, 212, 0.4)'
                  : '0 2px 8px rgba(6, 182, 212, 0.2)'
              }}
              data-testid="world-voice-btn"
            >
              <Globe className="w-3 h-3" /> 5. World Voices {showWorldVoiceDNA ? '▼' : '▶'}
            </button>
            <button
              onClick={() => { 
                setActiveView('marketplace'); 
                fetchMarketplaceDNAs(); 
              }}
              style={{
                padding: '8px 14px',
                background: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '8px',
                color: '#10b981',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              data-testid="presets-btn"
            >
              <Compass className="w-3 h-3" /> 650 Presets
            </button>
            
            {/* MASTERING STUDIO */}
            <button
              onClick={() => {
                setActiveView('mastering');
                toast.info('🎛️ Polish your track with studio presets, then add it to your workflow');
              }}
              style={{
                padding: '8px 14px',
                background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.2), rgba(168, 85, 247, 0.2))',
                border: '1px solid rgba(0, 212, 255, 0.4)',
                borderRadius: '8px',
                color: '#00d4ff',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 10px rgba(0, 212, 255, 0.2)'
              }}
              data-testid="mastering-workflow-btn"
            >
              <Headphones className="w-3 h-3" /> 🎛️ Mixing & Mastering
            </button>
            
            {/* SONG CREATOR - Combined Rhyme & Drums */}
            <button
              onClick={() => setFullscreenTool('song-creator')}
              style={{
                padding: '8px 14px',
                background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.3), rgba(139, 92, 246, 0.3))',
                border: '1px solid rgba(249, 115, 22, 0.5)',
                borderRadius: '8px',
                color: '#f97316',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 10px rgba(249, 115, 22, 0.2)'
              }}
              data-testid="song-creator-btn"
            >
              <Sparkles className="w-3 h-3" /> 🎵 Song Creator
            </button>
            
            {/* SUNO AI MUSIC BUTTON */}
            <button
              onClick={() => setFullscreenTool('suno-music')}
              style={{
                padding: '8px 14px',
                background: fullscreenTool === 'suno-music' 
                  ? 'linear-gradient(135deg, #ec4899, #8b5cf6)' 
                  : 'rgba(236, 72, 153, 0.15)',
                border: fullscreenTool === 'suno-music' ? 'none' : '1px solid rgba(236, 72, 153, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: fullscreenTool === 'suno-music' ? '0 2px 10px rgba(236, 72, 153, 0.4)' : 'none'
              }}
              data-testid="suno-music-btn"
            >
              <Music className="w-3 h-3" /> 🎤 Suno AI
            </button>
            
            {/* MY UPLOADS BUTTON */}
            <button
              onClick={() => setFullscreenTool('my-uploads')}
              style={{
                padding: '10px 16px',
                background: fullscreenTool === 'my-uploads' 
                  ? 'linear-gradient(135deg, #10b981, #059669)' 
                  : 'rgba(16, 185, 129, 0.1)',
                border: fullscreenTool === 'my-uploads' ? 'none' : '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: fullscreenTool === 'my-uploads' ? '0 2px 10px rgba(16, 185, 129, 0.4)' : 'none'
              }}
              data-testid="my-uploads-btn"
            >
              <Upload className="w-3 h-3" /> 📁 My Uploads
            </button>
            
            {/* SINGLE FULL PAGE BUTTON - Opens all 7 tools stacked */}
            <button
              onClick={() => setFullscreenTool('all-tools')}
              style={{
                padding: '10px 16px',
                background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '8px',
                width: '100%',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)'
              }}
              data-testid="full-page-all-tools-btn"
            >
              <Maximize2 className="w-4 h-4" />
              FULL PAGE (All 7 Tools)
            </button>
          </div>
        {/* CONSOLIDATED RHYME & FLOW ENGINE - FIRST SECTION */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.12) 0%, rgba(159, 71, 255, 0.08) 100%)',
          border: '2px solid rgba(249, 115, 22, 0.4)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px'
        }} data-testid="rhyme-flow-consolidated">
          {/* Header with Title and Fullscreen Button */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <StepBadge n={2} />
              <div style={{
                width: '36px',
                height: '36px',
                background: 'linear-gradient(135deg, #f97316, #ea580c)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Mic className="w-5 h-5" style={{ color: '#fff' }} />
              </div>
              <span style={{ color: '#f97316', fontWeight: 700, fontSize: '1.1rem' }}>RHYME & FLOW ENGINE</span>
              <span style={{ 
                background: 'rgba(249, 115, 22, 0.2)', 
                color: '#f97316', 
                padding: '3px 10px', 
                borderRadius: '12px', 
                fontSize: '0.7rem', 
                fontWeight: 600 
              }}>AI LYRICS</span>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* Tabs */}
              <button
                onClick={() => setRhymeEngineTab("generate")}
                style={{
                  padding: '6px 14px',
                  background: rhymeEngineTab === 'generate' ? 'rgba(249, 115, 22, 0.3)' : 'transparent',
                  border: `1px solid ${rhymeEngineTab === 'generate' ? '#f97316' : 'rgba(249, 115, 22, 0.3)'}`,
                  borderRadius: '8px',
                  color: rhymeEngineTab === 'generate' ? '#f97316' : 'rgba(255,255,255,0.6)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <Zap className="w-3 h-3" style={{ marginRight: '4px', display: 'inline' }} />
                AI Generated
              </button>
              <button
                onClick={() => setRhymeEngineTab("custom")}
                style={{
                  padding: '6px 14px',
                  background: rhymeEngineTab === 'custom' ? 'rgba(249, 115, 22, 0.3)' : 'transparent',
                  border: `1px solid ${rhymeEngineTab === 'custom' ? '#f97316' : 'rgba(249, 115, 22, 0.3)'}`,
                  borderRadius: '8px',
                  color: rhymeEngineTab === 'custom' ? '#f97316' : 'rgba(255,255,255,0.6)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                <Edit3 className="w-3 h-3" style={{ marginRight: '4px', display: 'inline' }} />
                My Own Lyrics (Original)
              </button>
              {/* Fullscreen Button */}
              <button
                onClick={() => setFullscreenTool('rhyme')}
                style={{
                  padding: '6px 14px',
                  background: 'linear-gradient(135deg, #f97316, #ea580c)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                data-testid="rhyme-fullscreen-consolidated-btn"
              >
                <Maximize2 className="w-3 h-3" />
                FULL SCREEN
              </button>
            </div>
          </div>

          {/* LYRICS OUTPUT - ALWAYS ON TOP */}
          {generatedBars && generatedBars.raw_lyrics && (
            <div style={{
              marginBottom: '16px',
              padding: '16px',
              background: 'rgba(0,0,0,0.4)',
              borderRadius: '12px',
              border: '1px solid rgba(249, 115, 22, 0.3)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#f97316', fontWeight: 700, fontSize: '0.9rem' }}>🔥 GENERATED LYRICS</span>
                  {generatedBars.total_bars && (
                    <span style={{ background: 'rgba(249, 115, 22, 0.2)', color: '#f97316', padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem' }}>
                      {generatedBars.total_bars} BARS
                    </span>
                  )}
                  {generatedBars.rhyme_scheme && (
                    <span style={{ background: 'rgba(159, 71, 255, 0.2)', color: '#9F47FF', padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem' }}>
                      {generatedBars.rhyme_scheme}
                    </span>
                  )}
                </div>
                {/* Syllable Count Toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>Syllables</span>
                  <button
                    onClick={() => setIncludeSyllables(!includeSyllables)}
                    style={{
                      padding: '4px 10px',
                      background: includeSyllables ? '#f97316' : 'rgba(255,255,255,0.1)',
                      border: 'none',
                      borderRadius: '12px',
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    {includeSyllables ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
              
              {/* Editable Lyrics Textarea */}
              <textarea
                value={generatedBars.raw_lyrics}
                onChange={(e) => setGeneratedBars(prev => ({ ...prev, raw_lyrics: e.target.value }))}
                style={{
                  width: '100%',
                  minHeight: '180px',
                  padding: '12px',
                  background: 'rgba(249, 115, 22, 0.05)',
                  border: '1px solid rgba(249, 115, 22, 0.3)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  lineHeight: 1.7,
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
                data-testid="lyrics-editor-consolidated"
              />
              
              {/* ACTION BUTTONS - Right Below Lyrics */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                {/* SUNO Button */}
                <button
                  onClick={() => openSunoExport(rhymeStyle, generatedBars.raw_lyrics)}
                  style={{
                    padding: '10px 18px',
                    background: 'linear-gradient(90deg, #9F47FF, #a855f7)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  data-testid="suno-consolidated-btn"
                >
                  <ExternalLink className="w-4 h-4" />
                  SUNO
                </button>
                
                {/* Generate With Vocals Button */}
                <button
                  onClick={() => {
                    setBeatLyrics(generatedBars.raw_lyrics);
                    setBeatPrompt(prev => prev ? `${prev}, ${rhymeStyle} vocals` : `${rhymeStyle} vocals, emotional delivery`);
                    toast.success("🎤 Lyrics loaded! Now click GENERATE MUSIC in the Demo Creator below", { duration: 4000 });
                    document.querySelector('[data-testid="beat-lyrics-input"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }}
                  style={{
                    padding: '10px 18px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  data-testid="generate-vocals-consolidated-btn"
                >
                  <Mic className="w-4 h-4" />
                  GENERATE WITH VOCALS
                </button>
                
                {/* Copy Button */}
                <button
                  onClick={() => {
                    universalCopy(generatedBars.raw_lyrics, 'Lyrics copied!');
                  }}
                  style={{
                    padding: '10px 14px',
                    background: 'rgba(249, 115, 22, 0.2)',
                    border: '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '8px',
                    color: '#f97316',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
                
                {/* Translate Button - Shows when language is not English */}
                {rhymeLanguage !== 'English' && (
                  <button
                    onClick={translateRhymeLyrics}
                    disabled={isTranslatingRhyme}
                    style={{
                      padding: '10px 14px',
                      background: isTranslatingRhyme ? 'rgba(99, 102, 241, 0.5)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: isTranslatingRhyme ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    {isTranslatingRhyme ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
                    Translate
                  </button>
                )}
                
                {/* Download Button */}
                <button
                  onClick={downloadBars}
                  style={{
                    padding: '10px 14px',
                    background: 'linear-gradient(135deg, #f97316 0%, #ffa500 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
                
                {/* Clear Button */}
                <button
                  onClick={() => {
                    setGeneratedBars(null);
                    setRhymeTopic('');
                    setSongTitle('');
                    toast.success('🧹 Cleared!');
                  }}
                  style={{
                    padding: '10px 14px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    color: '#ef4444',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                  Clear
                </button>
              </div>
            </div>
          )}

          {/* FORM CONTROLS - Below Lyrics Output */}
          {rhymeEngineTab === 'generate' && (
            <>
              {/* Song Title Input */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '6px', display: 'block' }}>SONG TITLE (optional)</label>
                <input
                  type="text"
                  value={songTitle}
                  onChange={(e) => setSongTitle(e.target.value)}
                  placeholder="Enter your song title..."
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem'
                  }}
                  data-testid="song-title-consolidated"
                />
              </div>
              
              {/* Controls Grid - Music Style, Rhyme Scheme, Flow Pattern, Language, Bars */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                {/* Music Style */}
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '6px', display: 'block' }}>MUSIC STYLE</label>
                  <select
                    value={rhymeStyle}
                    onChange={(e) => setRhymeStyle(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.8rem'
                    }}
                    data-testid="style-consolidated"
                  >
                    <optgroup label="Hip-Hop / Rap">
                      <option value="modern trap">Modern Trap (Atlanta)</option>
                      <option value="southern soul">Southern Soul</option>
                      <option value="pain">Pain / Emotional</option>
                      <option value="boom bap">Boom Bap (90s NY)</option>
                      <option value="drill">UK Drill</option>
                      <option value="west coast">West Coast G-Funk</option>
                      <option value="melodic">Melodic Rap</option>
                      <option value="conscious">Conscious/Lyrical</option>
                    </optgroup>
                    <optgroup label="R&B / Soul">
                      <option value="rnb">R&B / Soul</option>
                      <option value="neo soul">Neo Soul</option>
                      <option value="trapsoul">TrapSoul</option>
                    </optgroup>
                    <optgroup label="World / Latin">
                      <option value="afrobeats">Afrobeats</option>
                      <option value="amapiano">Amapiano</option>
                      <option value="reggaeton">Reggaeton</option>
                      <option value="dancehall">Dancehall</option>
                      <option value="latin trap">Latin Trap</option>
                    </optgroup>
                    <optgroup label="Other">
                      <option value="gospel">Gospel</option>
                      <option value="party">Party/Club</option>
                      <option value="pop">Pop</option>
                      <option value="rock">Rock</option>
                      <option value="country">Country</option>
                      <option value="baile funk">Baile Funk</option>
                      <option value="uk garage">UK Garage</option>
                      <option value="phonk">Phonk</option>
                    </optgroup>
                  </select>
                  <StyleBlueprintPreview styleKey={rhymeStyle} testId="style-blueprint-preview-consolidated" />
                </div>
                
                {/* Rhyme Scheme */}
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '6px', display: 'block' }}>RHYME SCHEME</label>
                  <select
                    value={rhymeScheme}
                    onChange={(e) => setRhymeScheme(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.8rem'
                    }}
                    data-testid="rhyme-scheme-consolidated"
                  >
                    <option value="AABB">AABB (Couplets)</option>
                    <option value="ABAB">ABAB (Alternating)</option>
                    <option value="ABBA">ABBA (Enclosed)</option>
                    <option value="AAAA">AAAA (Mono-rhyme)</option>
                    <option value="AAAB">AAAB (Triple + Twist)</option>
                    <option value="ABCB">ABCB (Ballad Stanza)</option>
                    <option value="ABCABC">ABCABC (Triple Rhyme)</option>
                    <option value="AABBA">AABBA (Limerick)</option>
                    <option value="AABCCB">AABCCB (Sestet)</option>
                    <option value="XAXA">XAXA (Alternate Blank)</option>
                    <option value="chain">Chain Rhyme (ABA BCB)</option>
                    <option value="internal">Internal Rhymes</option>
                    <option value="multisyllabic">Multi-Syllabic</option>
                    <option value="compound">Compound Rhymes</option>
                    <option value="slant">Slant / Near Rhymes</option>
                    <option value="assonance">Assonance (Vowel Rhymes)</option>
                    <option value="alliteration">Alliteration Driven</option>
                    <option value="internal-multi">Internal Multi-Syllabic</option>
                    <option value="cross">Cross Rhyme</option>
                    <option value="free">Free Form</option>
                  </select>
                </div>
                
                {/* Flow Pattern */}
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '6px', display: 'block' }}>FLOW PATTERN</label>
                  <select
                    value={flowPattern}
                    onChange={(e) => setFlowPattern(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.8rem'
                    }}
                    data-testid="flow-pattern-consolidated"
                  >
                    <option value="standard">Standard (4/4 Flow)</option>
                    <option value="boom-bap-cadence">Boom Bap Cadence (90s Pocket)</option>
                    <option value="triplet">Triplet Flow (Migos)</option>
                    <option value="double-time">Double-Time (Fast)</option>
                    <option value="chopper">Chopper (Tech N9ne Speed)</option>
                    <option value="slow-flow">Slow Flow (Deliberate)</option>
                    <option value="lazy-drawl">Lazy Drawl (Behind the Beat)</option>
                    <option value="syncopated">Syncopated (Off-Beat)</option>
                    <option value="halftime">Half-Time (Trap Beat)</option>
                    <option value="drill-slide">Drill Slide (UK Cadence)</option>
                    <option value="grime">Grime (140 Skippy)</option>
                    <option value="melodic-flow">Melodic Flow</option>
                    <option value="sing-song">Sing-Song (Playground Cadence)</option>
                    <option value="stutter">Stutter Flow (Word Repeats)</option>
                    <option value="start-stop">Start-Stop (Dramatic Pauses)</option>
                    <option value="pocket">Pocket Flow (Locked to Drums)</option>
                    <option value="bounce">Bounce (New Orleans)</option>
                    <option value="afro-bounce">Afro Bounce</option>
                    <option value="reggaeton-flow">Reggaeton Dembow</option>
                    <option value="syncopated-offbeat">Syncopated Off-Beat</option>
                    <option value="waterfall">Waterfall</option>
                    <option value="spoken-word">Spoken Word / Poetry</option>
                  </select>
                </div>
                
                {/* Language */}
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '6px', display: 'block' }}>LANGUAGE</label>
                  <select
                    value={rhymeLanguage}
                    onChange={(e) => setRhymeLanguage(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.8rem'
                    }}
                    data-testid="language-consolidated"
                  >
                    {OWN_LYRICS_LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                
                {/* Bars Count */}
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '6px', display: 'block' }}>BARS COUNT</label>
                  <select
                    value={barsCount}
                    onChange={(e) => setBarsCount(parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.8rem'
                    }}
                    data-testid="bars-consolidated"
                  >
                    <option value={8}>8 Bars (Half Verse)</option>
                    <option value={12}>12 Bars</option>
                    <option value={16}>16 Bars (Full Verse)</option>
                    <option value={24}>24 Bars</option>
                    <option value={32}>32 Bars (Double Verse)</option>
                    <option value={48}>48 Bars</option>
                    <option value={64}>64 Bars (Full Song)</option>
                  </select>
                </div>
                
                {/* Duration */}
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '6px', display: 'block' }}>DURATION (2-5 MIN)</label>
                  <input
                    type="number"
                    min={2}
                    max={5}
                    step={0.5}
                    value={rhymeDuration}
                    onChange={(e) => setRhymeDuration(e.target.value === '' ? '' : parseFloat(e.target.value))}
                    onBlur={() => setRhymeDuration(Math.min(5, Math.max(2, parseFloat(rhymeDuration) || 3)))}
                    data-testid="rhyme-duration-consolidated"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'rgba(0,0,0,0.4)',
                      border: '1px solid rgba(249, 115, 22, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.8rem'
                    }}
                  />
                </div>
              </div>
              
              {/* Topic Input + Generate Button */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={rhymeTopic}
                  onChange={(e) => setRhymeTopic(e.target.value)}
                  placeholder="What's your song about? (e.g., late night hustle, heartbreak, success, faith...)"
                  style={{
                    flex: 1,
                    minWidth: '200px',
                    padding: '12px 14px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem'
                  }}
                  data-testid="rhyme-topic-consolidated"
                />
                <button
                  onClick={generateBars}
                  disabled={isGeneratingBars || !rhymeTopic.trim()}
                  style={{
                    padding: '12px 24px',
                    background: isGeneratingBars ? 'rgba(249, 115, 22, 0.5)' : 'linear-gradient(135deg, #f97316, #ea580c)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    cursor: isGeneratingBars ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    opacity: !rhymeTopic.trim() ? 0.5 : 1,
                    boxShadow: '0 4px 15px rgba(249, 115, 22, 0.3)'
                  }}
                  data-testid="generate-bars-consolidated-btn"
                >
                  {isGeneratingBars ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Generating...</>
                  ) : (
                    <><Zap className="w-5 h-5" /> GENERATE {barsCount} BARS</>
                  )}
                </button>
              </div>
            </>
          )}

          {/* Custom Lyrics Tab Content */}
          {rhymeEngineTab === 'custom' && (
            <div>
              {/* Song Title */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '6px', display: 'block' }}>SONG TITLE</label>
                <input
                  type="text"
                  value={songTitle}
                  onChange={(e) => setSongTitle(e.target.value)}
                  placeholder="Enter your song title..."
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem'
                  }}
                />
              </div>
              
              {/* Custom Lyrics Textarea */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '6px', display: 'block' }}>YOUR LYRICS</label>
                <textarea
                  value={customLyrics}
                  onChange={(e) => setCustomLyrics(e.target.value)}
                  placeholder="Paste or write your own lyrics here..."
                  style={{
                    width: '100%',
                    minHeight: '150px',
                    padding: '12px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    lineHeight: 1.6,
                    resize: 'vertical'
                  }}
                  data-testid="custom-lyrics-consolidated"
                />
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', marginTop: '10px' }} data-testid="own-lyrics-language-row-consolidated">
                  <div style={{ flex: 1 }}>
                    <label style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem', marginBottom: '4px', display: 'block' }}>🌍 LANGUAGE</label>
                    <select value={rhymeLanguage} onChange={(e) => setRhymeLanguage(e.target.value)} style={{ width: '100%', padding: '10px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(249, 115, 22, 0.3)', borderRadius: '8px', color: '#fff', fontSize: '0.8rem' }} data-testid="own-lyrics-language-select-consolidated">
                      {OWN_LYRICS_LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <button onClick={translateCustomLyrics} disabled={lyricsAssistLoading} style={{ padding: '10px 14px', background: 'rgba(0, 212, 255, 0.12)', border: '1px solid rgba(0, 212, 255, 0.5)', borderRadius: '8px', color: '#00d4ff', fontSize: '0.8rem', fontWeight: 700, cursor: lyricsAssistLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap' }} data-testid="own-lyrics-translate-btn-consolidated">
                    {lyricsAssistLoading ? '⏳' : '🌍 Translate'}
                  </button>
                </div>
                <select
                  value=""
                  disabled={lyricsAssistLoading}
                  onChange={(e) => { if (e.target.value) runLyricsAssistant(e.target.value); }}
                  style={{
                    width: '100%',
                    marginTop: '10px',
                    padding: '12px',
                    background: 'rgba(139, 92, 246, 0.12)',
                    border: '1px solid rgba(139, 92, 246, 0.5)',
                    borderRadius: '10px',
                    color: '#8b5cf6',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: lyricsAssistLoading ? 'wait' : 'pointer'
                  }}
                  data-testid="lyric-assistant-dropdown-dashboard"
                >
                  <option value="">{lyricsAssistLoading ? '🪄 AI working on your lyrics...' : '🪄 AI Lyric Assistant...'}</option>
                  <option value="expand">➕ Expand — add the next 4 lines in your rhyme scheme</option>
                  <option value="polish">✨ Sonic Polish — better synonyms, flow & sound</option>
                  <option value="rewrite">🏆 Full Rewrite — professional finish of your ideas</option>
                </select>
              </div>
              
              {/* Action Buttons for Custom Lyrics */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => {
                    if (!customLyrics.trim()) {
                      toast.error('Please enter your lyrics first');
                      return;
                    }
                    openSunoExport(rhymeStyle, customLyrics);
                  }}
                  style={{
                    padding: '10px 18px',
                    background: 'linear-gradient(90deg, #9F47FF, #a855f7)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <ExternalLink className="w-4 h-4" />
                  SUNO
                </button>
                <button
                  onClick={() => {
                    if (!customLyrics.trim()) {
                      toast.error('Please enter your lyrics first');
                      return;
                    }
                    universalCopy(songTitle ? `🎵 ${songTitle}\n\n${customLyrics}` : customLyrics, 'Lyrics copied!');
                  }}
                  style={{
                    padding: '10px 14px',
                    background: 'rgba(249, 115, 22, 0.2)',
                    border: '1px solid rgba(249, 115, 22, 0.3)',
                    borderRadius: '8px',
                    color: '#f97316',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Copy className="w-4 h-4" />
                  Copy
                </button>
                <button
                  onClick={() => {
                    setCustomLyrics('');
                    setSongTitle('');
                    toast.success('🧹 Cleared!');
                  }}
                  style={{
                    padding: '10px 14px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    color: '#ef4444',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Trash2 className="w-4 h-4" />
                  Clear
                </button>
              </div>
            </div>
          )}
        </div>

          {/* DRUM MACHINE - QUANTUM COMPUTING INTERFACE (hidden/removed per request) */}
          <div className="quantum-panel" style={{
            display: 'none',
            marginTop: '16px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            overflow: 'hidden',
            position: 'relative'
          }} data-testid="drum-machine-section">
            
            {/* Quantum Trace Border Effect */}
            <div className="quantum-trace-border" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1 }}></div>
            
            {/* Top Display Bar - Quantum Style */}
            <div style={{
              background: 'var(--bg-base)',
              padding: 'clamp(10px, 2vw, 16px) clamp(12px, 3vw, 24px)',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
              zIndex: 2,
              flexWrap: 'wrap',
              gap: '10px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Quantum LED Indicator */}
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: generatedDrumPattern ? 'var(--quantum-cyan)' : 'var(--border)',
                  boxShadow: generatedDrumPattern ? '0 0 15px var(--quantum-cyan), 0 0 30px var(--quantum-cyan-glow)' : 'none',
                  transition: 'all 0.3s',
                  flexShrink: 0
                }} />
                <span style={{ 
                  color: 'var(--quantum-cyan)', 
                  fontFamily: 'Unbounded, sans-serif',
                  fontWeight: 700, 
                  fontSize: 'clamp(0.7rem, 2.5vw, 0.9rem)',
                  letterSpacing: '0.1em',
                  textShadow: '0 0 20px var(--quantum-cyan-glow)'
                }}>DRUM MACHINE</span>
                <span className="quantum-badge quantum-badge-purple" style={{
                  fontSize: '0.5rem',
                  padding: '2px 6px',
                  display: 'none'
                }}>AI</span>
              </div>
              
              {/* BPM Display - Quantum Data */}
              <div className="quantum-data-display" style={{
                padding: 'clamp(6px, 1.5vw, 10px) clamp(10px, 2vw, 20px)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{ 
                  color: 'var(--quantum-cyan)', 
                  fontFamily: 'JetBrains Mono, monospace', 
                  fontSize: 'clamp(1.2rem, 4vw, 1.8rem)', 
                  fontWeight: 600,
                  textShadow: '0 0 15px var(--quantum-cyan-glow)'
                }}>{drumTempo}</span>
                <span style={{ color: '#666', fontSize: '0.65rem' }}>BPM</span>
              </div>
            </div>

            {/* Main Control Panel - Quantum Style */}
            <div style={{ padding: 'clamp(12px, 3vw, 20px)', background: 'var(--bg-surface)', position: 'relative', zIndex: 2 }}>
              
              {/* Control Knobs Row */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
                gap: 'clamp(8px, 2vw, 16px)',
                marginBottom: '16px'
              }}>
                {/* Genre Selector */}
                <div style={{ textAlign: 'center' }}>
                  <label style={{ 
                    color: 'var(--quantum-cyan)', 
                    fontSize: '0.65rem', 
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'JetBrains Mono, monospace'
                  }}>GENRE</label>
                  <select
                    value={drumGenre}
                    onChange={(e) => setDrumGenre(e.target.value)}
                    className="quantum-select"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      color: 'var(--text-primary)',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                    data-testid="drum-genre-select"
                  >
                    {/* === TRAP & HIP HOP === */}
                    <optgroup label="🔥 TRAP">
                      <option value="trap">Trap</option>
                      <option value="rnb trap">R&B Trap</option>
                      <option value="melodic trap">Melodic Trap</option>
                      <option value="hard trap">Hard Trap</option>
                      <option value="dark trap">Dark Trap</option>
                      <option value="sad trap">Sad Trap</option>
                      <option value="emotional trap">Emotional Trap</option>
                      <option value="hybrid trap">Hybrid Trap</option>
                      <option value="festival trap">Festival Trap</option>
                      <option value="trap soul">Trap Soul</option>
                      <option value="cloud trap">Cloud Trap</option>
                      <option value="space trap">Space Trap</option>
                    </optgroup>
                    <optgroup label="🎤 HIP-HOP">
                      <option value="boom bap">Boom Bap</option>
                      <option value="east coast">East Coast</option>
                      <option value="west coast">West Coast</option>
                      <option value="dirty south">Dirty South</option>
                      <option value="southern hip hop">Southern Hip Hop</option>
                      <option value="chopped and screwed">Chopped & Screwed</option>
                      <option value="crunk">Crunk</option>
                      <option value="snap music">Snap Music</option>
                      <option value="hyphy">Hyphy (Bay Area)</option>
                      <option value="conscious hip hop">Conscious Hip Hop</option>
                      <option value="abstract hip hop">Abstract Hip Hop</option>
                      <option value="jazz rap">Jazz Rap</option>
                    </optgroup>
                    {/* === DRILL === */}
                    <optgroup label="🔫 DRILL">
                      <option value="drill">Drill</option>
                      <option value="uk drill">UK Drill</option>
                      <option value="ny drill">NY Drill</option>
                      <option value="brooklyn drill">Brooklyn Drill</option>
                      <option value="chicago drill">Chicago Drill</option>
                      <option value="detroit drill">Detroit Drill</option>
                      <option value="melodic drill">Melodic Drill</option>
                      <option value="afro drill">Afro Drill</option>
                    </optgroup>
                    {/* === R&B & SOUL === */}
                    <optgroup label="💜 R&B / SOUL">
                      <option value="rnb">R&B</option>
                      <option value="modern rnb">Modern R&B</option>
                      <option value="90s rnb">90s R&B</option>
                      <option value="neo soul">Neo Soul</option>
                      <option value="alternative rnb">Alternative R&B</option>
                      <option value="quiet storm">Quiet Storm</option>
                      <option value="new jack swing">New Jack Swing</option>
                      <option value="funk rnb">Funk R&B</option>
                      <option value="soul">Soul</option>
                      <option value="motown">Motown</option>
                    </optgroup>
                    {/* === HOUSE === */}
                    <optgroup label="🏠 HOUSE">
                      <option value="house">House</option>
                      <option value="deep house">Deep House</option>
                      <option value="tech house">Tech House</option>
                      <option value="afro house">Afro House</option>
                      <option value="progressive house">Progressive House</option>
                      <option value="future house">Future House</option>
                      <option value="bass house">Bass House</option>
                      <option value="electro house">Electro House</option>
                      <option value="tropical house">Tropical House</option>
                      <option value="chicago house">Chicago House</option>
                      <option value="acid house">Acid House</option>
                      <option value="minimal house">Minimal House</option>
                      <option value="soulful house">Soulful House</option>
                      <option value="ghetto house">Ghetto House</option>
                    </optgroup>
                    {/* === TECHNO === */}
                    <optgroup label="🤖 TECHNO">
                      <option value="techno">Techno</option>
                      <option value="hard techno">Hard Techno</option>
                      <option value="melodic techno">Melodic Techno</option>
                      <option value="detroit techno">Detroit Techno</option>
                      <option value="industrial techno">Industrial Techno</option>
                      <option value="minimal techno">Minimal Techno</option>
                      <option value="acid techno">Acid Techno</option>
                      <option value="peak time techno">Peak Time Techno</option>
                    </optgroup>
                    {/* === ELECTRONIC / DANCE === */}
                    <optgroup label="⚡ EDM / DANCE">
                      <option value="edm">EDM</option>
                      <option value="future bass">Future Bass</option>
                      <option value="dubstep">Dubstep</option>
                      <option value="brostep">Brostep</option>
                      <option value="riddim">Riddim</option>
                      <option value="tearout">Tearout</option>
                      <option value="color bass">Color Bass</option>
                      <option value="dnb">Drum & Bass</option>
                      <option value="liquid dnb">Liquid DnB</option>
                      <option value="jump up dnb">Jump Up DnB</option>
                      <option value="neuro dnb">Neuro DnB</option>
                      <option value="halftime dnb">Halftime DnB</option>
                      <option value="rollers dnb">Rollers DnB</option>
                      <option value="jungle">Jungle</option>
                      <option value="uk garage">UK Garage</option>
                      <option value="2-step">2-Step</option>
                      <option value="uk bass">UK Bass</option>
                    </optgroup>
                    {/* === TRANCE === */}
                    <optgroup label="🌀 TRANCE">
                      <option value="trance">Trance</option>
                      <option value="psytrance">Psytrance</option>
                      <option value="progressive trance">Progressive Trance</option>
                    </optgroup>
                    {/* === LO-FI & CHILL === */}
                    <optgroup label="☕ LO-FI / CHILL">
                      <option value="lo-fi">Lo-Fi</option>
                      <option value="lo-fi hip hop">Lo-Fi Hip Hop</option>
                      <option value="chillhop">Chillhop</option>
                      <option value="chillwave">Chillwave</option>
                      <option value="downtempo">Downtempo</option>
                      <option value="trip hop">Trip Hop</option>
                      <option value="ambient">Ambient</option>
                    </optgroup>
                    {/* === AFRICAN === */}
                    <optgroup label="🌍 AFRICAN">
                      <option value="afrobeats">Afrobeats</option>
                      <option value="amapiano">Amapiano</option>
                      <option value="afroswing">Afroswing</option>
                      <option value="gqom">Gqom</option>
                      <option value="kwaito">Kwaito</option>
                      <option value="highlife">Highlife</option>
                    </optgroup>
                    {/* === CARIBBEAN & LATIN === */}
                    <optgroup label="🌴 CARIBBEAN / LATIN">
                      <option value="reggaeton">Reggaeton</option>
                      <option value="latin trap">Latin Trap</option>
                      <option value="dembow">Dembow</option>
                      <option value="dancehall">Dancehall</option>
                      <option value="reggae">Reggae</option>
                      <option value="dub">Dub</option>
                      <option value="soca">Soca</option>
                      <option value="bachata">Bachata</option>
                      <option value="salsa">Salsa</option>
                      <option value="cumbia">Cumbia</option>
                      <option value="baile funk">Baile Funk</option>
                      <option value="bossa nova">Bossa Nova</option>
                    </optgroup>
                    {/* === PHONK === */}
                    <optgroup label="👻 PHONK">
                      <option value="phonk">Phonk</option>
                      <option value="drift phonk">Drift Phonk</option>
                      <option value="memphis rap">Memphis Rap</option>
                      <option value="house phonk">House Phonk</option>
                      <option value="cowbell phonk">Cowbell Phonk</option>
                      <option value="brazilian phonk">Brazilian Phonk</option>
                      <option value="memphis phonk">Memphis Phonk</option>
                    </optgroup>
                    {/* === CLUB === */}
                    <optgroup label="💃 CLUB">
                      <option value="jersey club">Jersey Club</option>
                      <option value="baltimore club">Baltimore Club</option>
                      <option value="philly club">Philly Club</option>
                      <option value="footwork">Footwork</option>
                      <option value="bounce">Bounce</option>
                    </optgroup>
                    {/* === MODERN SUBGENRES === */}
                    <optgroup label="🎮 MODERN">
                      <option value="plugg">Plugg</option>
                      <option value="rage">Rage</option>
                      <option value="hyperpop">Hyperpop</option>
                      <option value="digicore">Digicore</option>
                      <option value="grime">Grime</option>
                    </optgroup>
                    {/* === SYNTHWAVE / RETRO === */}
                    <optgroup label="🌃 SYNTHWAVE / RETRO">
                      <option value="synthwave">Synthwave</option>
                      <option value="retrowave">Retrowave</option>
                      <option value="nu disco">Nu Disco</option>
                      <option value="disco">Disco</option>
                    </optgroup>
                    {/* === ROCK / METAL === */}
                    <optgroup label="🎸 ROCK / METAL">
                      <option value="rock">Rock</option>
                      <option value="hard rock">Hard Rock</option>
                      <option value="punk rock">Punk Rock</option>
                      <option value="grunge">Grunge</option>
                      <option value="metal">Metal</option>
                      <option value="indie rock">Indie Rock</option>
                      <option value="post punk">Post Punk</option>
                      <option value="alternative">Alternative</option>
                    </optgroup>
                    {/* === FUNK / DISCO / JAZZ === */}
                    <optgroup label="🎷 FUNK / JAZZ">
                      <option value="funk">Funk</option>
                      <option value="jazz">Jazz</option>
                      <option value="blues">Blues</option>
                      <option value="gospel">Gospel</option>
                      <option value="country">Country</option>
                    </optgroup>
                    {/* === WORLD === */}
                    <optgroup label="🌏 WORLD">
                      <option value="bollywood">Bollywood</option>
                      <option value="indian kit">Indian</option>
                      <option value="arabic kit">Arabic</option>
                      <option value="j-pop">J-Pop</option>
                      <option value="k-pop">K-Pop</option>
                    </optgroup>
                    {/* === CINEMATIC / EXPERIMENTAL === */}
                    <optgroup label="🎬 CINEMATIC / EXPERIMENTAL">
                      <option value="cinematic">Cinematic</option>
                      <option value="orchestral">Orchestral</option>
                      <option value="experimental">Experimental</option>
                      <option value="idm">IDM</option>
                      <option value="glitch">Glitch</option>
                      <option value="breakbeat">Breakbeat</option>
                    </optgroup>
                  </select>
                </div>

                {/* Tempo Control */}
                <div style={{ textAlign: 'center' }}>
                  <label style={{ 
                    color: 'var(--quantum-cyan)', 
                    fontSize: '0.65rem', 
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'JetBrains Mono, monospace'
                  }}>TEMPO</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <button
                      onClick={() => setDrumTempo(prev => Math.max(60, prev - 5))}
                      style={{
                        width: '32px',
                        height: '38px',
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border)',
                        color: 'var(--quantum-cyan)',
                        fontSize: '1.2rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      data-testid="drum-tempo-minus"
                    >−</button>
                    <input
                      type="number"
                      value={drumTempo}
                      onChange={(e) => setDrumTempo(Math.max(60, Math.min(200, parseInt(e.target.value) || 120)))}
                      min="60"
                      max="200"
                      className="quantum-input"
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: 'var(--bg-base)',
                        border: '1px solid var(--border)',
                        color: 'var(--quantum-cyan)',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        fontFamily: 'JetBrains Mono, monospace',
                        textAlign: 'center',
                        outline: 'none',
                        minWidth: '60px'
                      }}
                      data-testid="drum-tempo-input"
                    />
                    <button
                      onClick={() => setDrumTempo(prev => Math.min(200, prev + 5))}
                      style={{
                        width: '32px',
                        height: '38px',
                        background: 'var(--bg-card)',
                        border: '1px solid var(--border)',
                        color: 'var(--quantum-cyan)',
                        fontSize: '1.2rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      data-testid="drum-tempo-plus"
                    >+</button>
                  </div>
                </div>

                {/* Kit Selector */}
                <div style={{ textAlign: 'center' }}>
                  <label style={{ 
                    color: 'var(--quantum-purple)', 
                    fontSize: '0.65rem', 
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'JetBrains Mono, monospace'
                  }}>KIT</label>
                  <select
                    value={drumKit}
                    onChange={(e) => setDrumKit(e.target.value)}
                    className="quantum-select"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      color: 'var(--quantum-purple)',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                    data-testid="drum-kit-select"
                  >
                    {/* === ROLAND CLASSICS === */}
                    <optgroup label="🎛️ ROLAND">
                      <option value="808">TR-808</option>
                      <option value="909">TR-909</option>
                      <option value="707">TR-707</option>
                      <option value="606">TR-606</option>
                      <option value="727">TR-727 (Latin)</option>
                      <option value="505">TR-505</option>
                      <option value="626">TR-626</option>
                      <option value="r8">Roland R-8</option>
                      <option value="cr-78">CR-78</option>
                    </optgroup>
                    {/* === OTHER MACHINES === */}
                    <optgroup label="🔊 DRUM MACHINES">
                      <option value="linn drum">LinnDrum</option>
                      <option value="lm-1">Linn LM-1</option>
                      <option value="dmx">Oberheim DMX</option>
                      <option value="alesis sr-16">Alesis SR-16</option>
                      <option value="simmons">Simmons SDS-V</option>
                      <option value="emu drumulator">E-mu Drumulator</option>
                      <option value="boss dr-110">Boss DR-110</option>
                    </optgroup>
                    {/* === SAMPLERS === */}
                    <optgroup label="📼 SAMPLERS">
                      <option value="sp1200">E-mu SP-1200</option>
                      <option value="sp-12">E-mu SP-12</option>
                      <option value="mpc60">MPC60</option>
                      <option value="mpc2000">MPC2000</option>
                      <option value="mpc3000">MPC3000</option>
                      <option value="mpc live">MPC Live</option>
                      <option value="maschine">Maschine</option>
                      <option value="s950">Akai S950</option>
                      <option value="s1000">Akai S1000</option>
                      <option value="electribe">Korg Electribe</option>
                    </optgroup>
                    {/* === ACOUSTIC === */}
                    <optgroup label="🥁 ACOUSTIC">
                      <option value="acoustic">Standard Acoustic</option>
                      <option value="acoustic jazz">Jazz Kit</option>
                      <option value="acoustic rock">Rock Kit</option>
                      <option value="acoustic metal">Metal Kit</option>
                      <option value="acoustic funk">Funk Kit</option>
                      <option value="acoustic soul">Soul Kit</option>
                      <option value="acoustic brush">Brush Kit</option>
                      <option value="acoustic vintage">Vintage 60s</option>
                      <option value="acoustic 70s">70s Kit</option>
                      <option value="acoustic 80s">80s Gated</option>
                      <option value="marching">Marching Drums</option>
                      <option value="orchestral drums">Orchestral</option>
                    </optgroup>
                    {/* === TRAP / HIP-HOP === */}
                    <optgroup label="🔥 TRAP / HIP-HOP">
                      <option value="trap kit">Trap Kit</option>
                      <option value="hard trap kit">Hard Trap Kit</option>
                      <option value="melodic trap kit">Melodic Trap Kit</option>
                      <option value="dark trap kit">Dark Trap Kit</option>
                      <option value="south side">Southside</option>
                      <option value="atlanta">Atlanta</option>
                      <option value="zaytoven">Zaytoven Style</option>
                      <option value="metro">Metro Boomin Style</option>
                      <option value="pierre bourne">Pi'erre Bourne Style</option>
                    </optgroup>
                    {/* === DRILL === */}
                    <optgroup label="🔫 DRILL">
                      <option value="drill kit">Drill Kit</option>
                      <option value="uk drill kit">UK Drill Kit</option>
                      <option value="ny drill kit">NY Drill Kit</option>
                      <option value="chicago drill kit">Chicago Drill Kit</option>
                      <option value="dark drill kit">Dark Drill Kit</option>
                    </optgroup>
                    {/* === PHONK === */}
                    <optgroup label="👻 PHONK">
                      <option value="phonk kit">Phonk Kit</option>
                      <option value="drift phonk kit">Drift Phonk Kit</option>
                      <option value="memphis kit">Memphis Kit</option>
                      <option value="cowbell kit">Cowbell Kit</option>
                      <option value="brazilian phonk kit">Brazilian Phonk Kit</option>
                    </optgroup>
                    {/* === CLUB === */}
                    <optgroup label="💃 CLUB">
                      <option value="jersey kit">Jersey Club Kit</option>
                      <option value="baltimore kit">Baltimore Kit</option>
                      <option value="footwork kit">Footwork Kit</option>
                      <option value="bounce kit">Bounce Kit</option>
                      <option value="vogue kit">Vogue Kit</option>
                    </optgroup>
                    {/* === HOUSE / TECHNO === */}
                    <optgroup label="🏠 HOUSE / TECHNO">
                      <option value="house kit">House Kit</option>
                      <option value="deep house kit">Deep House Kit</option>
                      <option value="tech house kit">Tech House Kit</option>
                      <option value="techno kit">Techno Kit</option>
                      <option value="minimal kit">Minimal Kit</option>
                      <option value="acid kit">Acid Kit</option>
                      <option value="garage kit">UK Garage Kit</option>
                    </optgroup>
                    {/* === WORLD === */}
                    <optgroup label="🌍 WORLD">
                      <option value="afro kit">Afro Kit</option>
                      <option value="amapiano kit">Amapiano Kit</option>
                      <option value="latin kit">Latin Kit</option>
                      <option value="reggaeton kit">Reggaeton Kit</option>
                      <option value="dancehall kit">Dancehall Kit</option>
                      <option value="reggae kit">Reggae Kit</option>
                      <option value="bossa kit">Bossa Nova Kit</option>
                      <option value="samba kit">Samba Kit</option>
                      <option value="indian kit">Indian Kit</option>
                      <option value="arabic kit">Arabic Kit</option>
                      <option value="asian kit">Asian Kit</option>
                    </optgroup>
                    {/* === ELECTRONIC === */}
                    <optgroup label="⚡ ELECTRONIC">
                      <option value="electronic">Modern Electronic</option>
                      <option value="edm kit">EDM Kit</option>
                      <option value="future bass kit">Future Bass Kit</option>
                      <option value="dubstep kit">Dubstep Kit</option>
                      <option value="riddim kit">Riddim Kit</option>
                      <option value="dnb kit">DnB Kit</option>
                      <option value="jungle kit">Jungle Kit</option>
                      <option value="breakbeat kit">Breakbeat Kit</option>
                      <option value="glitch kit">Glitch Kit</option>
                      <option value="idm kit">IDM Kit</option>
                    </optgroup>
                    {/* === LO-FI / CHILL === */}
                    <optgroup label="☕ LO-FI / CHILL">
                      <option value="lo-fi kit">Lo-Fi Kit</option>
                      <option value="vinyl kit">Vinyl Breaks Kit</option>
                      <option value="dusty kit">Dusty Samples Kit</option>
                      <option value="tape kit">Tape Saturated Kit</option>
                      <option value="chillhop kit">Chillhop Kit</option>
                    </optgroup>
                    {/* === ROCK / METAL === */}
                    <optgroup label="🎸 ROCK / METAL">
                      <option value="rock kit">Rock Kit</option>
                      <option value="punk kit">Punk Kit</option>
                      <option value="metal kit">Metal Kit</option>
                      <option value="indie kit">Indie Kit</option>
                      <option value="grunge kit">Grunge Kit</option>
                    </optgroup>
                    {/* === SYNTH / RETRO === */}
                    <optgroup label="🌃 SYNTH / RETRO">
                      <option value="synthwave kit">Synthwave Kit</option>
                      <option value="retrowave kit">Retrowave Kit</option>
                      <option value="80s kit">80s Kit</option>
                      <option value="vaporwave kit">Vaporwave Kit</option>
                      <option value="new wave kit">New Wave Kit</option>
                    </optgroup>
                    {/* === SPECIAL === */}
                    <optgroup label="🎬 SPECIAL">
                      <option value="cinematic kit">Cinematic Kit</option>
                      <option value="industrial kit">Industrial Kit</option>
                      <option value="foley kit">Foley Kit</option>
                      <option value="hybrid kit">Hybrid Kit</option>
                      <option value="layered kit">Layered Kit</option>
                    </optgroup>
                  </select>
                </div>

                {/* Swing Control */}
                <div style={{ textAlign: 'center' }}>
                  <label style={{ 
                    color: 'var(--quantum-crimson)', 
                    fontSize: '0.65rem', 
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    display: 'block',
                    marginBottom: '8px',
                    fontFamily: 'JetBrains Mono, monospace'
                  }}>SWING</label>
                  <select
                    value={drumSwing}
                    onChange={(e) => setDrumSwing(e.target.value)}
                    className="quantum-select"
                    style={{
                      width: '100%',
                      padding: '10px',
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      color: 'var(--quantum-crimson)',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                    data-testid="drum-swing-select"
                  >
                    {/* === BASIC TIMING === */}
                    <optgroup label="📊 BASIC">
                      <option value="none">None (Straight)</option>
                      <option value="light">Light (5%)</option>
                      <option value="medium-light">Medium-Light (10%)</option>
                      <option value="medium">Medium (15%)</option>
                      <option value="medium-heavy">Medium-Heavy (20%)</option>
                      <option value="heavy">Heavy (25%)</option>
                      <option value="extreme">Extreme (30%)</option>
                    </optgroup>
                    {/* === CLASSIC FEELS === */}
                    <optgroup label="🎵 CLASSIC">
                      <option value="shuffle">Shuffle</option>
                      <option value="half-shuffle">Half Shuffle</option>
                      <option value="triplet">Triplet</option>
                      <option value="dotted">Dotted</option>
                      <option value="bounce">Bounce</option>
                      <option value="pocket">Deep Pocket</option>
                      <option value="tight">Tight/Quantized</option>
                    </optgroup>
                    {/* === MPC / SAMPLER === */}
                    <optgroup label="📼 MPC / SAMPLER">
                      <option value="mpc60-54">MPC60 54%</option>
                      <option value="mpc60-58">MPC60 58%</option>
                      <option value="mpc60-62">MPC60 62%</option>
                      <option value="mpc60-66">MPC60 66%</option>
                      <option value="mpc60-70">MPC60 70%</option>
                      <option value="mpc3000">MPC3000</option>
                      <option value="sp1200">SP-1200</option>
                      <option value="maschine">Maschine</option>
                    </optgroup>
                    {/* === PRODUCER STYLES === */}
                    <optgroup label="🎤 PRODUCER STYLES">
                      <option value="j dilla">J Dilla</option>
                      <option value="madlib">Madlib</option>
                      <option value="pete rock">Pete Rock</option>
                      <option value="dj premier">DJ Premier</option>
                      <option value="9th wonder">9th Wonder</option>
                      <option value="kanye">Kanye</option>
                      <option value="timbaland">Timbaland</option>
                      <option value="neptunes">Neptunes</option>
                      <option value="metro">Metro Boomin</option>
                    </optgroup>
                    {/* === TIMING OFFSETS === */}
                    <optgroup label="⏱️ TIMING">
                      <option value="lazy">Lazy/Behind</option>
                      <option value="slightly-late">Slightly Late</option>
                      <option value="on-grid">On Grid</option>
                      <option value="slightly-early">Slightly Early</option>
                      <option value="pushed">Pushed/Ahead</option>
                      <option value="drunk">Drunk/Loose</option>
                      <option value="humanize-light">Humanize Light</option>
                      <option value="humanize-heavy">Humanize Heavy</option>
                    </optgroup>
                    {/* === GENRE GROOVES === */}
                    <optgroup label="🎸 GENRE GROOVES">
                      <option value="funk">Funk Pocket</option>
                      <option value="soul">Soul Groove</option>
                      <option value="motown">Motown</option>
                      <option value="disco">Disco</option>
                      <option value="jazz">Jazz Swing</option>
                      <option value="blues">Blues Shuffle</option>
                      <option value="rock">Rock Swing</option>
                      <option value="country">Country Shuffle</option>
                    </optgroup>
                    {/* === WORLD GROOVES === */}
                    <optgroup label="🌍 WORLD">
                      <option value="reggae">Reggae/One Drop</option>
                      <option value="dub">Dub</option>
                      <option value="latin">Latin Clave</option>
                      <option value="bossa">Bossa Nova</option>
                      <option value="samba">Samba</option>
                      <option value="cumbia">Cumbia</option>
                      <option value="afro">Afro Polyrhythm</option>
                      <option value="afrobeat">Afrobeat</option>
                      <option value="highlife">Highlife</option>
                      <option value="gospel">Gospel Shuffle</option>
                      <option value="new orleans">New Orleans</option>
                    </optgroup>
                    {/* === ELECTRONIC === */}
                    <optgroup label="⚡ ELECTRONIC">
                      <option value="house">House Groove</option>
                      <option value="garage">UK Garage</option>
                      <option value="breakbeat">Breakbeat</option>
                      <option value="jungle">Jungle</option>
                      <option value="dnb">DnB</option>
                      <option value="techno">Techno Grid</option>
                    </optgroup>
                    {/* === R&B FEELS === */}
                    <optgroup label="💜 R&B FEELS">
                      <option value="neo soul">Neo Soul</option>
                      <option value="slow jam">Slow Jam</option>
                      <option value="quiet storm">Quiet Storm</option>
                      <option value="new jack">New Jack Swing</option>
                      <option value="90s rnb">90s R&B</option>
                      <option value="modern rnb">Modern R&B</option>
                    </optgroup>
                  </select>
                </div>
              </div>

              {/* 40K STYLE PROMPTS - QUANTUM DROPDOWN SELECTOR */}
              <div style={{
                padding: '16px',
                background: drumStylePrompt ? 'linear-gradient(135deg, rgba(0, 240, 255, 0.08), rgba(139, 92, 246, 0.08))' : 'var(--bg-card)',
                border: drumStylePrompt ? '1px solid var(--quantum-cyan)' : '1px solid var(--border)',
                marginBottom: '16px',
                position: 'relative'
              }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      background: 'linear-gradient(135deg, var(--quantum-cyan), var(--quantum-purple))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1rem',
                      borderRadius: '4px'
                    }}>
                      <Star className="w-4 h-4" style={{ color: '#000' }} />
                    </div>
                    <div>
                      <span style={{ 
                        color: 'var(--quantum-cyan)', 
                        fontWeight: 700, 
                        fontSize: '0.85rem',
                        fontFamily: 'JetBrains Mono, monospace',
                        letterSpacing: '0.1em'
                      }}>
                        70K STYLE PROMPTS
                      </span>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.65rem', margin: '2px 0 0 0' }}>
                        71,206 professional prompts across 62 genres
                      </p>
                    </div>
                  </div>
                </div>

                {/* Two-Column Dropdown Selectors */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: drumStyleCategories.length > 0 && selectedDrumCategory ? '1fr 1fr' : '1fr',
                  gap: '12px',
                  marginBottom: drumStylePrompt ? '14px' : '0'
                }}>
                  {/* Category Dropdown */}
                  <div>
                    <label style={{ 
                      color: 'var(--quantum-cyan)', 
                      fontSize: '0.7rem', 
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                      display: 'block',
                      marginBottom: '8px',
                      fontFamily: 'JetBrains Mono, monospace'
                    }}>GENRE CATEGORY</label>
                    <CustomSelect
                      value={selectedDrumCategory}
                      onChange={(val) => loadDrumCategoryPrompts(val)}
                      accent="#00f0ff"
                      placeholder={`SELECT GENRE (${drumStyleCategories.length || 46})`}
                      testId="70k-category-dropdown"
                      options={[
                        { value: '', label: `SELECT GENRE (${drumStyleCategories.length || 46})` },
                        ...drumStyleCategories.map(cat => ({
                          value: cat.name || cat.genre,
                          label: `${cat.name || cat.genre} (${(cat.count || 0).toLocaleString()})`
                        }))
                      ]}
                      style={{
                        padding: '16px 12px',
                        background: '#1a1a2e',
                        border: '2px solid #00f0ff',
                        borderRadius: '8px',
                        color: selectedDrumCategory ? '#00f0ff' : '#888',
                        fontSize: '16px',
                        fontWeight: 600,
                        minHeight: '54px'
                      }}
                    />
                  </div>

                  {/* Prompts Dropdown - Shows when category selected */}
                  {drumStyleCategories.length > 0 && selectedDrumCategory && (
                    <div>
                      <label style={{ 
                        color: '#a78bfa', 
                        fontSize: '0.7rem', 
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        display: 'block',
                        marginBottom: '8px',
                        fontFamily: 'JetBrains Mono, monospace'
                      }}>SELECT PROMPT ({drumPromptTotal.toLocaleString()} total)</label>
                      <CustomSelect
                        value={drumStylePrompt}
                        onChange={(val) => { if (val) applyStyleToBoth(val); }}
                        accent="#a78bfa"
                        placeholder="SELECT STYLE PROMPT"
                        testId="70k-prompt-dropdown"
                        options={[
                          { value: '', label: 'SELECT STYLE PROMPT' },
                          ...drumCategoryPrompts.map((prompt) => ({
                            value: prompt,
                            label: prompt.length > 60 ? prompt.substring(0, 60) + '...' : prompt
                          }))
                        ]}
                        style={{
                          padding: '16px 12px',
                          background: '#1a1a2e',
                          border: '2px solid #a78bfa',
                          borderRadius: '8px',
                          color: drumStylePrompt ? '#a78bfa' : '#888',
                          fontSize: '16px',
                          fontWeight: 600,
                          minHeight: '50px'
                        }}
                      />
                      
                      {/* PAGINATION CONTROLS */}
                      {drumPromptTotalPages > 1 && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          marginTop: '12px',
                          padding: '10px',
                          background: 'rgba(167, 139, 250, 0.1)',
                          borderRadius: '8px',
                          border: '1px solid rgba(167, 139, 250, 0.2)'
                        }}>
                          <button
                            onClick={() => loadDrumCategoryPrompts(selectedDrumCategory, 1)}
                            disabled={drumPromptPage === 1}
                            style={{
                              padding: '6px 10px',
                              background: drumPromptPage === 1 ? 'rgba(100,100,100,0.3)' : 'rgba(167, 139, 250, 0.3)',
                              border: '1px solid rgba(167, 139, 250, 0.4)',
                              borderRadius: '4px',
                              color: drumPromptPage === 1 ? '#666' : '#a78bfa',
                              cursor: drumPromptPage === 1 ? 'not-allowed' : 'pointer',
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                            data-testid="70k-page-first"
                          >
                            ⏮ First
                          </button>
                          <button
                            onClick={() => loadDrumCategoryPrompts(selectedDrumCategory, drumPromptPage - 1)}
                            disabled={drumPromptPage === 1}
                            style={{
                              padding: '6px 12px',
                              background: drumPromptPage === 1 ? 'rgba(100,100,100,0.3)' : 'rgba(167, 139, 250, 0.3)',
                              border: '1px solid rgba(167, 139, 250, 0.4)',
                              borderRadius: '4px',
                              color: drumPromptPage === 1 ? '#666' : '#a78bfa',
                              cursor: drumPromptPage === 1 ? 'not-allowed' : 'pointer',
                              fontWeight: 600,
                              fontSize: '0.85rem'
                            }}
                            data-testid="70k-page-prev"
                          >
                            ◀ Prev
                          </button>
                          <span style={{
                            color: '#a78bfa',
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            padding: '0 12px',
                            fontFamily: 'JetBrains Mono, monospace'
                          }}>
                            Page {drumPromptPage} / {drumPromptTotalPages}
                          </span>
                          <button
                            onClick={() => loadDrumCategoryPrompts(selectedDrumCategory, drumPromptPage + 1)}
                            disabled={drumPromptPage >= drumPromptTotalPages}
                            style={{
                              padding: '6px 12px',
                              background: drumPromptPage >= drumPromptTotalPages ? 'rgba(100,100,100,0.3)' : 'rgba(167, 139, 250, 0.3)',
                              border: '1px solid rgba(167, 139, 250, 0.4)',
                              borderRadius: '4px',
                              color: drumPromptPage >= drumPromptTotalPages ? '#666' : '#a78bfa',
                              cursor: drumPromptPage >= drumPromptTotalPages ? 'not-allowed' : 'pointer',
                              fontWeight: 600,
                              fontSize: '0.85rem'
                            }}
                            data-testid="70k-page-next"
                          >
                            Next ▶
                          </button>
                          <button
                            onClick={() => loadDrumCategoryPrompts(selectedDrumCategory, drumPromptTotalPages)}
                            disabled={drumPromptPage >= drumPromptTotalPages}
                            style={{
                              padding: '6px 10px',
                              background: drumPromptPage >= drumPromptTotalPages ? 'rgba(100,100,100,0.3)' : 'rgba(167, 139, 250, 0.3)',
                              border: '1px solid rgba(167, 139, 250, 0.4)',
                              borderRadius: '4px',
                              color: drumPromptPage >= drumPromptTotalPages ? '#666' : '#a78bfa',
                              cursor: drumPromptPage >= drumPromptTotalPages ? 'not-allowed' : 'pointer',
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                            data-testid="70k-page-last"
                          >
                            Last ⏭
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected Style Preview with Full Action Buttons */}
                {drumStylePrompt && (
                  <div style={{
                    padding: '16px',
                    background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.08), rgba(139, 92, 246, 0.08))',
                    border: '2px solid rgba(0, 240, 255, 0.3)',
                    borderRadius: '12px',
                    position: 'relative'
                  }}>
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '12px'
                    }}>
                      <div style={{ 
                        fontSize: '0.7rem', 
                        color: 'var(--quantum-cyan)',
                        fontFamily: 'JetBrains Mono, monospace',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        fontWeight: 700
                      }}>
                        🎵 SELECTED STYLE PROMPT
                      </div>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setDrumStylePrompt("");
                          setSelectedDrumCategory("");
                          setDrumCategoryPrompts([]);
                          toast.success('Style cleared');
                        }}
                        style={{
                          background: 'rgba(239, 68, 68, 0.2)',
                          border: '1px solid rgba(239, 68, 68, 0.4)',
                          borderRadius: '4px',
                          color: '#ef4444',
                          padding: '4px 8px',
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        ✕ CLEAR
                      </button>
                    </div>
                    
                    {/* Style Prompt Text */}
                    <p style={{ 
                      color: '#e0e0e0', 
                      fontSize: '0.8rem', 
                      lineHeight: 1.6,
                      margin: '0 0 16px 0',
                      padding: '12px',
                      background: 'rgba(0,0,0,0.3)',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.1)',
                      fontStyle: 'italic'
                    }}>
                      "{drumStylePrompt}"
                    </p>
                    
                    {/* MO3 SONG STRUCTURE PANEL */}
                    {songStructure && showSongStructure && (
                      <div style={{
                        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(236, 72, 153, 0.1))',
                        border: '2px solid rgba(139, 92, 246, 0.4)',
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '16px'
                      }}>
                        {/* Header with Toggle */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '12px'
                        }}>
                          <div style={{
                            fontSize: '0.75rem',
                            color: '#a78bfa',
                            fontFamily: 'JetBrains Mono, monospace',
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            🎼 MO3 SONG STRUCTURE
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {/* Song Length Selector */}
                            <select
                              value={songLengthMinutes}
                              onChange={(e) => {
                                const newLength = parseFloat(e.target.value);
                                setSongLengthMinutes(newLength);
                                const structure = generateMo3Structure(drumStylePrompt, newLength, drumTempo);
                                setSongStructure(structure);
                              }}
                              style={{
                                background: 'rgba(139, 92, 246, 0.2)',
                                border: '1px solid rgba(139, 92, 246, 0.4)',
                                borderRadius: '4px',
                                color: '#a78bfa',
                                padding: '4px 8px',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              <option value={2}>2:00</option>
                              <option value={2.5}>2:30</option>
                              <option value={3}>3:00</option>
                              <option value={3.5}>3:30</option>
                              <option value={4}>4:00</option>
                              <option value={4.5}>4:30</option>
                              <option value={5}>5:00</option>
                            </select>
                            <button
                              onClick={() => {
                                const structure = generateMo3Structure(drumStylePrompt, songLengthMinutes, drumTempo);
                                setSongStructure(structure);
                                toast.success('🎼 Structure regenerated!');
                              }}
                              style={{
                                background: 'rgba(139, 92, 246, 0.2)',
                                border: '1px solid rgba(139, 92, 246, 0.4)',
                                borderRadius: '4px',
                                color: '#a78bfa',
                                padding: '4px 8px',
                                fontSize: '0.6rem',
                                fontWeight: 600,
                                cursor: 'pointer'
                              }}
                            >
                              🔄 REGEN
                            </button>
                          </div>
                        </div>
                        
                        {/* Quick Reference */}
                        <div style={{
                          background: 'rgba(0,0,0,0.3)',
                          borderRadius: '8px',
                          padding: '10px 12px',
                          marginBottom: '12px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          flexWrap: 'wrap',
                          gap: '8px'
                        }}>
                          <span style={{ color: '#f472b6', fontSize: '0.7rem', fontWeight: 700 }}>
                            ⏱️ {songStructure.quickRef}
                          </span>
                          <span style={{ color: '#a78bfa', fontSize: '0.7rem', fontWeight: 600 }}>
                            📊 {songStructure.metadata.totalBars} bars total
                          </span>
                        </div>
                        
                        {/* Section Breakdown */}
                        <div style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                          marginBottom: '12px'
                        }}>
                          {songStructure.metadata.sections.map((section, idx) => (
                            <div
                              key={idx}
                              style={{
                                background: section.modulate 
                                  ? 'linear-gradient(135deg, rgba(236, 72, 153, 0.3), rgba(139, 92, 246, 0.3))'
                                  : `rgba(139, 92, 246, ${0.1 + (section.complexity / 20)})`,
                                border: section.modulate 
                                  ? '2px solid #f472b6'
                                  : '1px solid rgba(139, 92, 246, 0.3)',
                                borderRadius: '6px',
                                padding: '8px 10px',
                                textAlign: 'center',
                                minWidth: '70px'
                              }}
                            >
                              <div style={{
                                fontSize: '0.65rem',
                                color: section.modulate ? '#f472b6' : '#a78bfa',
                                fontWeight: 700,
                                marginBottom: '4px'
                              }}>
                                {section.name}
                                {section.modulate && ' 🎹'}
                              </div>
                              <div style={{
                                fontSize: '0.6rem',
                                color: '#888',
                                marginBottom: '2px'
                              }}>
                                {section.bars} bars
                              </div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '2px'
                              }}>
                                <span style={{ fontSize: '0.55rem', color: '#666' }}>D:</span>
                                <div style={{
                                  width: '30px',
                                  height: '4px',
                                  background: 'rgba(255,255,255,0.1)',
                                  borderRadius: '2px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    width: `${(section.complexity / 9) * 100}%`,
                                    height: '100%',
                                    background: section.complexity >= 7 
                                      ? '#f472b6' 
                                      : section.complexity >= 5 
                                        ? '#a78bfa' 
                                        : '#6366f1',
                                    borderRadius: '2px'
                                  }} />
                                </div>
                                <span style={{ fontSize: '0.55rem', color: '#a78bfa' }}>{section.complexity}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Key Modulation Info */}
                        <div style={{
                          background: 'rgba(236, 72, 153, 0.1)',
                          border: '1px solid rgba(236, 72, 153, 0.3)',
                          borderRadius: '6px',
                          padding: '8px 12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <span style={{ fontSize: '0.7rem', color: '#f472b6', fontWeight: 700 }}>
                            🎹 Key Modulation:
                          </span>
                          <span style={{ fontSize: '0.7rem', color: '#e0e0e0' }}>
                            {songStructure.metadata.baseKey} → {songStructure.metadata.modulatedKey} 
                            <span style={{ color: '#888', marginLeft: '6px' }}>
                              ({songStructure.metadata.modulationStep} after Bridge)
                            </span>
                          </span>
                        </div>
                        
                        {/* Compact Format (Copyable) */}
                        <div 
                          onClick={() => {
                            universalCopy(songStructure.compact, '📋 Structure copied!');
                          }}
                          style={{
                            marginTop: '12px',
                            background: 'rgba(0,0,0,0.4)',
                            border: '1px dashed rgba(139, 92, 246, 0.3)',
                            borderRadius: '6px',
                            padding: '10px 12px',
                            cursor: 'pointer',
                            fontSize: '0.65rem',
                            color: '#888',
                            fontFamily: 'JetBrains Mono, monospace',
                            wordBreak: 'break-all'
                          }}
                          title="Click to copy"
                        >
                          📋 {songStructure.compact}
                        </div>

                      </div>
                    )}
                    
                    {/* Action Buttons Row - Adapts based on Quantum Mode */}
                    <div style={{ 
                      display: 'grid',
                      gridTemplateColumns: advancedDrumMode ? 'repeat(6, 1fr)' : 'repeat(5, 1fr)',
                      gap: '10px'
                    }}>
                      {/* FULL SONG Button - Only in Quantum Mode */}
                      {advancedDrumMode && (
                        <button
                          onClick={async () => {
                            toast.info('🧬 Generating quantum full song with style...');
                            await generateAdvancedDrumPattern();
                          }}
                          disabled={isGeneratingDrum}
                          style={{
                            padding: '12px 8px',
                            background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#fff',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            cursor: isGeneratingDrum ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            opacity: isGeneratingDrum ? 0.5 : 1,
                            boxShadow: '0 4px 15px rgba(139, 92, 246, 0.4)',
                            minHeight: '60px'
                          }}
                          data-testid="40k-fullsong-btn"
                        >
                          <span style={{ fontSize: '1.2rem' }}>🧬</span>
                          {isGeneratingDrum ? 'GENERATING...' : 'FULL SONG'}
                        </button>
                      )}
                      
                      {/* SUNO AI Button - Generate real music from 70K style prompt */}
                      <button
                        onClick={() => {
                          // Use the ACTUAL 70K style prompt directly
                          const stylePrompt = drumStylePrompt || 
                            (advancedDrumMode 
                              ? `${drumGenre} beat, ${drumTempo} BPM, ${drumKey} ${drumMode}`
                              : `${drumGenre} ${drumTempo} BPM`);
                          
                          // Pre-fill Suno form with the 70K style prompt
                          setSunoStyle(stylePrompt);
                          setSunoPrompt(`Create a professional track with this style`);
                          setFullscreenTool('suno-music');
                          toast.success('🎵 70K Style loaded into Suno AI!');
                        }}
                        style={{
                          padding: '12px 8px',
                          background: 'linear-gradient(135deg, #ec4899, #f43f5e)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          boxShadow: '0 4px 15px rgba(236, 72, 153, 0.4)',
                          minHeight: '60px'
                        }}
                        data-testid="drum-suno-btn"
                      >
                        <span style={{ fontSize: '1.2rem' }}>🎤</span>
                        USE IN SUNO
                      </button>
                      
                      {/* AUDIO PREVIEW Button - Uses same generateAudioPreview as Unified Workflow */}
                      <button
                        onClick={() => {
                          generateAudioPreview(drumStylePrompt, selectedDrumCategory || 'style', `drum-70k-preview`);
                        }}
                        disabled={audioPreviewLoading === `drum-70k-preview`}
                        style={{
                          padding: '12px 8px',
                          background: audioPreviewPlaying === `drum-70k-preview` 
                            ? 'linear-gradient(135deg, #ef4444, #dc2626)' 
                            : audioPreviewLoading === `drum-70k-preview` 
                              ? 'linear-gradient(135deg, #eab308, #ca8a04)'
                              : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          cursor: audioPreviewLoading === `drum-70k-preview` ? 'wait' : 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          boxShadow: audioPreviewPlaying === `drum-70k-preview`
                            ? '0 4px 15px rgba(239, 68, 68, 0.4)'
                            : '0 4px 15px rgba(139, 92, 246, 0.4)',
                          minHeight: '60px'
                        }}
                        data-testid="70k-audio-preview-btn"
                      >
                        {audioPreviewLoading === `drum-70k-preview` ? (
                          <><Loader2 className="w-5 h-5 animate-spin" />GENERATING...</>
                        ) : audioPreviewPlaying === `drum-70k-preview` ? (
                          <><VolumeX className="w-5 h-5" />STOP</>
                        ) : (
                          <><Headphones className="w-5 h-5" />🎧 AUDIO PREVIEW</>
                        )}
                      </button>

                      {/* COPY Button */}
                      <button
                        onClick={() => universalCopy(drumStylePrompt, '📋 Style prompt copied!')}
                        style={{
                          padding: '12px 8px',
                          background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                          minHeight: '60px'
                        }}
                        data-testid="40k-copy-btn"
                      >
                        <Copy className="w-5 h-5" />
                        COPY
                      </button>

                      {/* DRUMS Button */}
                      <button
                        onClick={async () => {
                          await generateDrumFromStylePrompt(drumStylePrompt);
                        }}
                        disabled={isGeneratingDrum}
                        style={{
                          padding: '12px 8px',
                          background: 'linear-gradient(135deg, #00f0ff, #0ea5e9)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#000',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          cursor: isGeneratingDrum ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          opacity: isGeneratingDrum ? 0.5 : 1,
                          boxShadow: '0 4px 15px rgba(0, 240, 255, 0.3)',
                          minHeight: '60px'
                        }}
                        data-testid="40k-drums-btn"
                      >
                        <Zap className="w-5 h-5" />
                        {isGeneratingDrum ? 'WAIT...' : 'DRUMS'}
                      </button>

                      {/* MIDI Button */}
                      <button
                        onClick={async () => {
                          toast.info('🎹 Generating MIDI file...');
                          await generateMidiFromPrompt(drumStylePrompt);
                        }}
                        disabled={isGeneratingMidi}
                        style={{
                          padding: '12px 8px',
                          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          cursor: isGeneratingMidi ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          opacity: isGeneratingMidi ? 0.5 : 1,
                          boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)',
                          minHeight: '60px'
                        }}
                        data-testid="40k-midi-btn"
                      >
                        <Music className="w-5 h-5" />
                        {isGeneratingMidi ? 'WAIT...' : 'MIDI'}
                      </button>
                      
                      {/* SUNO Button - Generate with Suno AI */}
                      <button
                        onClick={() => {
                          if (!drumStylePrompt) {
                            toast.error('Select a style prompt first');
                            return;
                          }
                          // Pre-fill Suno with selected style prompt
                          setSunoPrompt(drumStylePrompt);
                          setSunoStyle(selectedDrumCategory || drumGenre);
                          setFullscreenTool('suno-music');
                          toast.success('🎵 Opening Suno AI with selected style!');
                        }}
                        disabled={!drumStylePrompt}
                        style={{
                          padding: '10px 8px',
                          background: !drumStylePrompt 
                            ? 'rgba(100,100,100,0.3)' 
                            : 'linear-gradient(135deg, #ec4899, #f43f5e)',
                          border: 'none',
                          borderRadius: '8px',
                          color: !drumStylePrompt ? '#666' : '#fff',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          cursor: !drumStylePrompt ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          boxShadow: drumStylePrompt ? '0 4px 15px rgba(236, 72, 153, 0.3)' : 'none',
                          minHeight: '60px'
                        }}
                        data-testid="40k-suno-btn"
                      >
                        <span style={{ fontSize: '1rem' }}>🎵</span>
                        SUNO
                      </button>
                      
                      {/* DOWNLOAD WAV - renders on Suno, then arms a real save anchor */}
                      {drumWavReady ? (
                        <button
                          onClick={() => saveWavFromUrl(drumWavReady.url, drumWavReady.filename)}
                          style={{
                            padding: '10px 8px',
                            background: 'linear-gradient(135deg, #22c55e, #059669)',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#fff',
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            boxShadow: '0 4px 15px rgba(34, 197, 94, 0.35)',
                            minHeight: '60px'
                          }}
                          data-testid="drum-suno-save-wav-anchor"
                          title="Save the rendered WAV"
                        >
                          <Download className="w-5 h-5" />
                          SAVE WAV
                        </button>
                      ) : (
                      <button
                        onClick={downloadDrumWav}
                        disabled={!generatedDrumPattern && !drumStylePrompt}
                        style={{
                          padding: '10px 8px',
                          background: (!generatedDrumPattern && !drumStylePrompt)
                            ? 'rgba(100,100,100,0.3)'
                            : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                          border: 'none',
                          borderRadius: '8px',
                          color: (!generatedDrumPattern && !drumStylePrompt) ? '#666' : '#fff',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          cursor: (!generatedDrumPattern && !drumStylePrompt) ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          boxShadow: (generatedDrumPattern || drumStylePrompt) ? '0 4px 15px rgba(139, 92, 246, 0.3)' : 'none',
                          minHeight: '60px'
                        }}
                        data-testid="drum-suno-download-wav-btn"
                        title="Render & download a high-quality WAV of this style prompt"
                      >
                        <Download className="w-5 h-5" />
                        DOWNLOAD WAV
                      </button>
                      )}
                      
                      {/* DELETE/CLEAR Button */}
                      <button
                        onClick={() => {
                          setDrumStylePrompt("");
                          setSelectedDrumCategory("");
                          setDrumCategoryPrompts([]);
                          setGeneratedDrumPattern(null);
                          setDrumAudioUrl(null);
                          setDrumAudioSunoPrompt(null);
                          toast.success('🗑️ Drum machine cleared');
                        }}
                        style={{
                          padding: '10px 8px',
                          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
                          minHeight: '60px'
                        }}
                        data-testid="40k-clear-btn"
                      >
                        <Trash2 className="w-5 h-5" />
                        CLEAR
                      </button>
                    </div>

                        {/* MO3 LIBRARY - save audio, MIDI & style prompts */}
                        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                          <button
                            onClick={saveMo3ToLibrary}
                            style={{
                              flex: '1 1 140px', padding: '10px 12px',
                              background: 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                              border: 'none', borderRadius: '8px', color: '#fff',
                              fontWeight: 700, fontSize: '0.7rem', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                            }}
                            data-testid="mo3-save-library-btn"
                          >
                            💾 SAVE TO LIBRARY
                          </button>
                          <button
                            onClick={() => { const next = !showMo3Library; setShowMo3Library(next); if (next) loadMo3Library(); }}
                            style={{
                              flex: '1 1 140px', padding: '10px 12px',
                              background: 'rgba(139, 92, 246, 0.15)',
                              border: '1px solid rgba(139, 92, 246, 0.4)', borderRadius: '8px',
                              color: '#a78bfa', fontWeight: 700, fontSize: '0.7rem', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                            }}
                            data-testid="mo3-toggle-library-btn"
                          >
                            📚 MO3 LIBRARY {mo3Library.length > 0 ? `(${mo3Library.length})` : ''}
                          </button>
                        </div>
                        {showMo3Library && (
                          <div style={{ marginTop: '10px', maxHeight: '260px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="mo3-library-list">
                            {mo3Library.length === 0 && (
                              <div style={{ color: '#9ca3af', fontSize: '0.7rem', textAlign: 'center', padding: '10px' }}>
                                Nothing saved yet — hit SAVE TO LIBRARY to keep this prompt, structure, audio & MIDI.
                              </div>
                            )}
                            {mo3Library.map((item) => (
                              <div key={item.id} style={{
                                background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(139,92,246,0.25)',
                                borderRadius: '8px', padding: '10px'
                              }} data-testid={`mo3-lib-item-${item.id}`}>
                                <div style={{ color: '#e9d5ff', fontSize: '0.7rem', fontWeight: 700, marginBottom: '4px' }}>{item.name}</div>
                                {item.structure && (
                                  <div style={{ color: '#9ca3af', fontSize: '0.62rem', marginBottom: '6px' }}>📋 {item.structure}</div>
                                )}
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                  <button
                                    onClick={() => universalCopy(item.style_prompt, '📋 Style prompt copied!')}
                                    style={{ padding: '6px 10px', background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)', borderRadius: '6px', color: '#93c5fd', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer' }}
                                    data-testid={`mo3-lib-copy-${item.id}`}
                                  >COPY PROMPT</button>
                                  {item.audio_url && (
                                    <button
                                      onClick={() => saveWavFromUrl(item.audio_url, `${item.name.slice(0, 40)}.wav`)}
                                      style={{ padding: '6px 10px', background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)', borderRadius: '6px', color: '#86efac', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer' }}
                                      data-testid={`mo3-lib-wav-${item.id}`}
                                    >⬇ WAV</button>
                                  )}
                                  {!item.audio_url && (
                                    <button
                                      onClick={() => renderLibraryAudio(item, 'mo3')}
                                      style={{ padding: '6px 10px', background: 'rgba(34,197,94,0.12)', border: '1px dashed rgba(34,197,94,0.45)', borderRadius: '6px', color: '#86efac', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer' }}
                                      data-testid={`mo3-lib-render-${item.id}`}
                                    >🎧 RENDER WAV</button>
                                  )}
                                  {item.midi_filename && (
                                    <a
                                      href={`${API}/download-midi/${item.midi_filename}`}
                                      download={item.midi_filename}
                                      onClick={() => toast.success('🎹 Saving MIDI...', { duration: 4000 })}
                                      style={{ padding: '6px 10px', background: 'rgba(245,158,11,0.2)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: '6px', color: '#fcd34d', fontSize: '0.62rem', fontWeight: 700, textDecoration: 'none' }}
                                      data-testid={`mo3-lib-midi-${item.id}`}
                                    >🎹 MIDI</a>
                                  )}
                                  <button
                                    onClick={() => deleteMo3Item(item.id)}
                                    style={{ padding: '6px 10px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: '6px', color: '#fca5a5', fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer' }}
                                    data-testid={`mo3-lib-delete-${item.id}`}
                                  >✕</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                  </div>
                )}

                {/* Quick Load on Focus Helper */}
                {drumStyleCategories.length === 0 && (
                  <p style={{ 
                    color: 'var(--text-muted)', 
                    fontSize: '0.65rem', 
                    textAlign: 'center',
                    marginTop: '8px'
                  }}>
                    Click the dropdown to load 70,000+ style prompts
                  </p>
                )}
              </div>

              {/* ADVANCED MODE TOGGLE */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: advancedDrumMode ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(99, 102, 241, 0.2))' : 'rgba(255,255,255,0.03)',
                borderRadius: '10px',
                marginBottom: '16px',
                border: advancedDrumMode ? '1px solid rgba(139, 92, 246, 0.4)' : '1px solid rgba(255,255,255,0.05)',
                cursor: 'pointer'
              }}
              onClick={() => setAdvancedDrumMode(!advancedDrumMode)}
              data-testid="advanced-drum-toggle"
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '1.2rem' }}>🧬</span>
                  <div>
                    <span style={{ color: advancedDrumMode ? '#a78bfa' : '#888', fontWeight: 600, fontSize: '0.85rem' }}>
                      QUANTUM RHYTHMIC DESIGN
                    </span>
                    <p style={{ color: '#666', fontSize: '0.7rem', margin: '2px 0 0 0' }}>
                      Full song structure • Inversions • Cubic analysis
                    </p>
                  </div>
                </div>
                <div style={{
                  width: '44px',
                  height: '24px',
                  background: advancedDrumMode ? 'linear-gradient(135deg, #8b5cf6, #6366f1)' : '#333',
                  borderRadius: '12px',
                  position: 'relative',
                  transition: 'all 0.2s'
                }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    background: '#fff',
                    borderRadius: '50%',
                    position: 'absolute',
                    top: '2px',
                    left: advancedDrumMode ? '22px' : '2px',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }} />
                </div>
              </div>

              {/* ADVANCED CONTROLS - Only show when advanced mode is ON */}
              {advancedDrumMode && (
                <div style={{
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--quantum-purple)',
                  padding: '16px',
                  marginBottom: '16px',
                  boxShadow: '0 0 20px rgba(139, 92, 246, 0.15)'
                }}>
                  <h4 style={{ color: 'var(--quantum-purple)', fontSize: '0.8rem', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Unbounded, sans-serif', letterSpacing: '0.1em' }}>
                    <span>🎹</span> HARMONIC SETTINGS
                  </h4>
                  
                  {/* Key & Mode */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }}>
                    <div>
                      <label style={{ color: 'var(--quantum-purple)', fontSize: '0.65rem', display: 'block', marginBottom: '4px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em' }}>KEY</label>
                      <select
                        value={drumKey}
                        onChange={(e) => setDrumKey(e.target.value)}
                        data-testid="quantum-key-select"
                        style={{
                          width: '100%', 
                          padding: '8px', 
                          background: '#1a1a28', 
                          border: '1px solid #333',
                          color: '#8b5cf6', 
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="C" style={{ background: '#1a1a28', color: '#fff' }}>C</option>
                        <option value="C#" style={{ background: '#1a1a28', color: '#fff' }}>C#</option>
                        <option value="D" style={{ background: '#1a1a28', color: '#fff' }}>D</option>
                        <option value="D#" style={{ background: '#1a1a28', color: '#fff' }}>D#</option>
                        <option value="E" style={{ background: '#1a1a28', color: '#fff' }}>E</option>
                        <option value="F" style={{ background: '#1a1a28', color: '#fff' }}>F</option>
                        <option value="F#" style={{ background: '#1a1a28', color: '#fff' }}>F#</option>
                        <option value="G" style={{ background: '#1a1a28', color: '#fff' }}>G</option>
                        <option value="G#" style={{ background: '#1a1a28', color: '#fff' }}>G#</option>
                        <option value="A" style={{ background: '#1a1a28', color: '#fff' }}>A</option>
                        <option value="A#" style={{ background: '#1a1a28', color: '#fff' }}>A#</option>
                        <option value="B" style={{ background: '#1a1a28', color: '#fff' }}>B</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ color: 'var(--quantum-purple)', fontSize: '0.65rem', display: 'block', marginBottom: '4px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em' }}>MODE</label>
                      <select
                        value={drumMode}
                        onChange={(e) => setDrumMode(e.target.value)}
                        data-testid="quantum-mode-select"
                        style={{
                          width: '100%', 
                          padding: '8px', 
                          background: '#1a1a28', 
                          border: '1px solid #333',
                          color: '#8b5cf6', 
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="minor" style={{ background: '#1a1a28', color: '#fff' }}>Minor</option>
                        <option value="major" style={{ background: '#1a1a28', color: '#fff' }}>Major</option>
                        <option value="dorian" style={{ background: '#1a1a28', color: '#fff' }}>Dorian</option>
                        <option value="phrygian" style={{ background: '#1a1a28', color: '#fff' }}>Phrygian</option>
                        <option value="mixolydian" style={{ background: '#1a1a28', color: '#fff' }}>Mixolydian</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ color: '#ff2a4d', fontSize: '0.65rem', display: 'block', marginBottom: '4px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em' }}>ENERGY</label>
                      <select
                        value={drumEnergy}
                        onChange={(e) => setDrumEnergy(e.target.value)}
                        data-testid="quantum-energy-select"
                        style={{
                          width: '100%', 
                          padding: '8px', 
                          background: '#1a1a28', 
                          border: '1px solid #333',
                          color: '#ff2a4d', 
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="low" style={{ background: '#1a1a28', color: '#fff' }}>Low</option>
                        <option value="medium" style={{ background: '#1a1a28', color: '#fff' }}>Medium</option>
                        <option value="high" style={{ background: '#1a1a28', color: '#fff' }}>High</option>
                        <option value="extreme" style={{ background: '#1a1a28', color: '#fff' }}>Extreme</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ color: '#00f0ff', fontSize: '0.65rem', display: 'block', marginBottom: '4px', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em' }}>COMPLEXITY</label>
                      <select
                        value={drumComplexity}
                        onChange={(e) => setDrumComplexity(e.target.value)}
                        data-testid="quantum-complexity-select"
                        style={{
                          width: '100%', 
                          padding: '8px', 
                          background: '#1a1a28', 
                          border: '1px solid #333',
                          color: '#00f0ff', 
                          fontSize: '0.75rem',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="basic" style={{ background: '#1a1a28', color: '#fff' }}>Basic</option>
                        <option value="intermediate" style={{ background: '#1a1a28', color: '#fff' }}>Intermediate</option>
                        <option value="professional" style={{ background: '#1a1a28', color: '#fff' }}>Professional</option>
                        <option value="quantum" style={{ background: '#1a1a28', color: '#fff' }}>Quantum</option>
                      </select>
                    </div>
                  </div>

                  {/* Song Sections */}
                  <h4 style={{ color: 'var(--quantum-cyan)', fontSize: '0.8rem', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'Unbounded, sans-serif', letterSpacing: '0.1em' }}>
                    <span>📊</span> SONG STRUCTURE
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                    {['intro', 'verse', 'pre_chorus', 'chorus', 'bridge', 'outro'].map(section => (
                      <button
                        key={section}
                        onClick={() => toggleDrumSection(section)}
                        style={{
                          padding: '8px 14px',
                          background: drumSections.includes(section) 
                            ? 'var(--quantum-purple)' 
                            : 'var(--bg-card)',
                          border: drumSections.includes(section) ? 'none' : '1px solid var(--border)',
                          color: drumSections.includes(section) ? '#fff' : 'var(--text-muted)',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          textTransform: 'uppercase',
                          fontFamily: 'IBM Plex Sans, sans-serif',
                          boxShadow: drumSections.includes(section) ? '0 0 15px var(--quantum-purple-glow)' : 'none'
                        }}
                      >
                        {section.replace('_', '-')}
                      </button>
                    ))}
                  </div>

                  {/* Bars per section & Options */}
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <label style={{ color: 'var(--quantum-cyan)', fontSize: '0.65rem', fontFamily: 'JetBrains Mono, monospace' }}>BARS/SECTION:</label>
                      <select
                        value={drumBarsPerSection}
                        onChange={(e) => setDrumBarsPerSection(parseInt(e.target.value))}
                        className="quantum-select"
                        style={{
                          padding: '6px 10px', background: 'var(--bg-card)', border: '1px solid var(--border)',
                          color: 'var(--quantum-cyan)', fontSize: '0.75rem'
                        }}
                      >
                        <option value={4}>4 bars</option>
                        <option value={8}>8 bars</option>
                        <option value={16}>16 bars</option>
                      </select>
                    </div>
                    
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={drumIncludeInversions}
                        onChange={(e) => setDrumIncludeInversions(e.target.checked)}
                        style={{ accentColor: 'var(--quantum-purple)' }}
                      />
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>Inversions</span>
                    </label>
                    
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={drumIncludeFills}
                        onChange={(e) => setDrumIncludeFills(e.target.checked)}
                        style={{ accentColor: 'var(--quantum-purple)' }}
                      />
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem' }}>Fills</span>
                    </label>
                  </div>

                  {/* Total bars preview */}
                  <div style={{ 
                    marginTop: '12px', 
                    padding: '10px 14px', 
                    background: 'var(--bg-base)', 
                    border: '1px solid var(--quantum-cyan)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 0 15px rgba(0, 240, 255, 0.1)'
                  }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.7rem', fontFamily: 'JetBrains Mono, monospace' }}>TOTAL ARRANGEMENT:</span>
                    <span style={{ color: 'var(--quantum-cyan)', fontWeight: 700, fontSize: '0.85rem', fontFamily: 'JetBrains Mono, monospace', textShadow: '0 0 10px var(--quantum-cyan-glow)' }}>
                      {drumSections.length * drumBarsPerSection} BARS ({drumSections.length} sections)
                    </span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                marginBottom: '16px',
                flexWrap: 'wrap'
              }}>
                {/* Generate Pattern Button - Quantum Style */}
                <button
                  onClick={advancedDrumMode ? generateAdvancedDrumPattern : generateDrumPattern}
                  disabled={isGeneratingDrum}
                  className={isGeneratingDrum ? 'quantum-loading' : 'quantum-pulse'}
                  style={{
                    flex: '1 1 150px',
                    padding: '14px 20px',
                    background: isGeneratingDrum 
                      ? 'rgba(0, 240, 255, 0.2)' 
                      : advancedDrumMode 
                        ? 'linear-gradient(135deg, var(--quantum-purple) 0%, var(--quantum-crimson) 100%)'
                        : 'var(--quantum-cyan)',
                    border: 'none',
                    color: isGeneratingDrum ? 'var(--quantum-cyan)' : (advancedDrumMode ? '#fff' : '#030305'),
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    cursor: isGeneratingDrum ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: isGeneratingDrum ? 'none' : (advancedDrumMode ? '0 0 30px var(--quantum-purple-glow)' : '0 0 30px var(--quantum-cyan-glow)'),
                    transition: 'all 0.15s ease-out'
                  }}
                  data-testid="generate-drum-btn"
                >
                  {isGeneratingDrum ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {advancedDrumMode ? 'ANALYZING...' : 'GENERATING...'}
                    </>
                  ) : (
                    <>
                      {advancedDrumMode ? <span>🧬</span> : <Zap className="w-4 h-4" />}
                      {advancedDrumMode ? 'GENERATE FULL SONG' : 'GENERATE PATTERN'}
                    </>
                  )}
                </button>
                
                {/* Audio Preview Button */}
                <button
                  onClick={generateDrumAudio}
                  disabled={isGeneratingDrumAudio}
                  style={{
                    flex: '1 1 150px',
                    padding: '14px 20px',
                    background: isGeneratingDrumAudio 
                      ? 'rgba(16, 185, 129, 0.3)' 
                      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    letterSpacing: '0.5px',
                    cursor: isGeneratingDrumAudio ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: isGeneratingDrumAudio ? 'none' : '0 4px 20px rgba(16, 185, 129, 0.4)',
                    transition: 'all 0.2s'
                  }}
                  data-testid="generate-drum-audio-btn"
                >
                  {isGeneratingDrumAudio ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      30-90s...
                    </>
                  ) : (
                    <>
                      <Headphones className="w-4 h-4" />
                      PREVIEW AUDIO
                    </>
                  )}
                </button>

                {/* Clear Button */}
                <button
                  onClick={() => {
                    setGeneratedDrumPattern(null);
                    setDrumAudioUrl(null);
                    setDrumGenre('trap');
                    setDrumTempo(140);
                    setDrumKit('808');
                    toast.success('🧹 Drum Machine cleared!');
                  }}
                  style={{
                    padding: '14px 16px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '2px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '10px',
                    color: '#ef4444',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    transition: 'all 0.2s'
                  }}
                  data-testid="clear-drum-machine-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* Download Buttons Row - MIDI and WAV */}
              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                marginBottom: '16px',
                flexWrap: 'wrap'
              }}>
                {/* Download MIDI Button */}
                <button
                  onClick={async () => {
                    const sunoPrompt = generatedDrumPattern?.suno_prompt || `${drumGenre} drums, ${drumKit} kit, ${drumTempo} BPM`;
                    toast.info("🎹 Generating MIDI file for drums...");
                    try {
                      const response = await axios.post(`${API}/generate-midi`, {
                        style_prompt: sunoPrompt,
                        bars: 32,
                        preset_id: null
                      });
                      if (response.data.success) {
                        const { filename, params } = response.data;
        setMo3LastMidi(filename);
                        const downloadUrl = `${API}/download-midi/${filename}`;
                        const fetchResponse = await fetch(downloadUrl);
                        if (!fetchResponse.ok) throw new Error("Download failed");
                        const blob = await fetchResponse.blob();
                        await downloadFileWithPicker(
                          blob, 
                          filename, 
                          'audio/midi', 
                          `🎹 MIDI saved! ${params?.genre || drumGenre} @ ${params?.tempo || drumTempo} BPM`,
                          downloadUrl
                        );
                      }
                    } catch (error) {
                      console.error("MIDI generation error:", error);
                      toast.error("Failed to generate MIDI");
                    }
                  }}
                  style={{
                    flex: '1 1 150px',
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)',
                    transition: 'all 0.2s'
                  }}
                  data-testid="drum-download-midi-btn"
                >
                  <Music className="w-4 h-4" />
                  DOWNLOAD MIDI
                </button>

                {/* Download WAV Button */}
                {drumWavReady ? (
                <button
                  onClick={() => saveWavFromUrl(drumWavReady.url, drumWavReady.filename)}
                  style={{
                    flex: '1 1 150px',
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #22c55e 0%, #059669 100%)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 15px rgba(34, 197, 94, 0.35)',
                    transition: 'all 0.2s'
                  }}
                  data-testid="drum-save-wav-anchor"
                  title="Save the rendered WAV"
                >
                  <Download className="w-4 h-4" />
                  SAVE WAV
                </button>
                ) : (
                <button
                  onClick={downloadDrumWav}
                  disabled={!generatedDrumPattern}
                  style={{
                    flex: '1 1 150px',
                    padding: '12px 16px',
                    background: generatedDrumPattern 
                      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' 
                      : 'rgba(100,100,100,0.3)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    cursor: generatedDrumPattern ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: generatedDrumPattern ? '0 4px 15px rgba(245, 158, 11, 0.3)' : 'none',
                    transition: 'all 0.2s',
                    opacity: generatedDrumPattern ? 1 : 0.5
                  }}
                  data-testid="drum-download-wav-btn"
                >
                  <Download className="w-4 h-4" />
                  DOWNLOAD WAV
                </button>
                )}
                
                {/* LIBRARY Button */}
                <button
                  onClick={() => setShowDrumLibrary(!showDrumLibrary)}
                  style={{
                    flex: '1 1 120px',
                    padding: '12px 16px',
                    background: showDrumLibrary 
                      ? 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' 
                      : 'rgba(139, 92, 246, 0.15)',
                    border: '2px solid #8b5cf6',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    boxShadow: showDrumLibrary ? '0 4px 15px rgba(139, 92, 246, 0.4)' : 'none',
                    transition: 'all 0.2s'
                  }}
                  data-testid="drum-library-btn"
                >
                  <Archive className="w-4 h-4" />
                  LIBRARY ({drumLibrary.length})
                </button>
              </div>
              {drumAudioUrl && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(6, 182, 212, 0.1) 100%)',
                  border: '2px solid rgba(16, 185, 129, 0.3)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      onClick={toggleDrumAudio}
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        border: 'none',
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)',
                        transition: 'transform 0.2s'
                      }}
                      data-testid="drum-play-btn"
                    >
                      {isDrumPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                    </button>
                    {/* SAVE TO FILES Button */}
                    <button
                      onClick={async () => {
                        const filename = `drum_${drumGenre}_${drumTempo}bpm_${drumKit}.wav`;
                        toast.info('💾 Saving WAV to Files...');
                        try {
                          const response = await fetch(drumAudioUrl);
                          if (!response.ok) throw new Error("Save failed");
                          const blob = await response.blob();
                          await shareOrDownloadBlob(blob, filename, 'audio/wav', {
                            sourceUrl: drumAudioUrl, desktopPicker: true,
                            successMessage: '💾 WAV saved to Files!',
                          });
                        } catch (error) {
                          console.error("Save error:", error);
                          toast.error('Download failed — please try again');
                        }
                      }}
                      style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        border: 'none',
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 20px rgba(59, 130, 246, 0.4)',
                        transition: 'transform 0.2s'
                      }}
                      data-testid="drum-save-wav-btn"
                      title="Save WAV to Files"
                    >
                      <Save className="w-6 h-6" />
                    </button>
                    <div style={{ flex: 1 }}>
                      <p style={{ 
                        color: '#10b981', 
                        fontWeight: 700, 
                        fontSize: '1rem', 
                        margin: 0,
                        textShadow: '0 0 10px rgba(16, 185, 129, 0.3)'
                      }}>
                        🎧 {drumGenre.toUpperCase()} @ {drumTempo} BPM
                      </p>
                      <p style={{ color: '#666', fontSize: '0.8rem', margin: '4px 0 0' }}>
                        {drumKit.toUpperCase()} Kit • AI Generated • Suno Compatible
                      </p>
                    </div>
                    {/* Copy Matching Suno Prompt Button */}
                    <button
                      onClick={() => {
                        // Priority: drumAudioSunoPrompt (from audio generation) > generatedDrumPattern.suno_prompt > fallback
                        const prompt = drumAudioSunoPrompt || 
                                       generatedDrumPattern?.suno_prompt || 
                                       `${drumGenre} drums, ${drumKit} kit, ${drumTempo} BPM, instrumental drums only`;
                        universalCopy(prompt, '📋 Full Suno style prompt copied! Paste in Suno for matching sound');
                      }}
                      style={{
                        padding: '8px 12px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 10px rgba(139, 92, 246, 0.3)'
                      }}
                      data-testid="drum-copy-suno-prompt-btn"
                      title="Copy the exact Suno prompt that matches this WAV"
                    >
                      <Copy className="w-4 h-4" />
                      COPY SUNO PROMPT
                    </button>
                  </div>
                  {/* Show the Suno prompt below the player - always show when audio is available */}
                  {(drumAudioSunoPrompt || generatedDrumPattern?.suno_prompt || drumAudioUrl) && (
                    <div style={{
                      marginTop: '12px',
                      padding: '10px',
                      background: 'rgba(139, 92, 246, 0.1)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '8px'
                    }}>
                      <p style={{ 
                        color: '#a78bfa', 
                        fontSize: '0.7rem', 
                        fontWeight: 600, 
                        margin: '0 0 4px 0',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        🎵 Matching Suno/Udio Prompt (Copy this for same sound):
                      </p>
                      <p style={{ 
                        color: '#d4d4d4', 
                        fontSize: '0.75rem', 
                        margin: 0,
                        lineHeight: 1.4,
                        fontStyle: 'italic'
                      }}>
                        {drumAudioSunoPrompt || generatedDrumPattern?.suno_prompt || `${drumGenre} drums, ${drumKit} kit, ${drumTempo} BPM`}
                      </p>
                    </div>
                  )}
                  <audio 
                    ref={drumAudioRefFullscreen} 
                    src={drumAudioUrl} 
                    preload="metadata"
                    onEnded={handleDrumAudioEnded}
                    style={{ display: 'none' }}
                  />
                </div>
              )}

              {/* DRUM MACHINE LIBRARY PANEL */}
              {showDrumLibrary && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(59, 130, 246, 0.1))',
                  border: '2px solid rgba(139, 92, 246, 0.4)',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Archive className="w-5 h-5" style={{ color: '#a78bfa' }} />
                      <h3 style={{ 
                        color: '#a78bfa', 
                        fontSize: '1rem', 
                        fontWeight: 700,
                        margin: 0,
                        fontFamily: 'JetBrains Mono, monospace'
                      }}>
                        PATTERN LIBRARY ({drumLibrary.length})
                      </h3>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {/* Audio Preview ON/OFF Toggle */}
                      <button
                        onClick={() => {
                          setLibraryAudioPreviewEnabled(!libraryAudioPreviewEnabled);
                          toast.success(libraryAudioPreviewEnabled ? '🔇 Audio Preview OFF' : '🔊 Audio Preview ON');
                        }}
                        style={{
                          padding: '6px 12px',
                          background: libraryAudioPreviewEnabled 
                            ? 'linear-gradient(135deg, #10b981, #059669)'
                            : 'rgba(100,100,100,0.3)',
                          border: libraryAudioPreviewEnabled 
                            ? 'none'
                            : '1px solid rgba(100,100,100,0.4)',
                          borderRadius: '6px',
                          color: libraryAudioPreviewEnabled ? '#fff' : '#666',
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          transition: 'all 0.2s'
                        }}
                        data-testid="library-audio-preview-toggle"
                      >
                        {libraryAudioPreviewEnabled ? (
                          <><Volume2 className="w-3 h-3" /> AUDIO ON</>
                        ) : (
                          <><VolumeX className="w-3 h-3" /> AUDIO OFF</>
                        )}
                      </button>
                      {drumLibrary.length > 0 && (
                        <button
                          onClick={clearLibrary}
                          style={{
                            padding: '6px 12px',
                            background: 'rgba(239, 68, 68, 0.2)',
                            border: '1px solid rgba(239, 68, 68, 0.4)',
                            borderRadius: '6px',
                            color: '#ef4444',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                          data-testid="clear-library-btn"
                        >
                          CLEAR ALL
                        </button>
                      )}
                      <button
                        onClick={() => setShowDrumLibrary(false)}
                        style={{
                          padding: '6px 12px',
                          background: 'rgba(100,100,100,0.3)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#888',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        CLOSE
                      </button>
                    </div>
                  </div>

                  {drumLibrary.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '30px',
                      color: '#666'
                    }}>
                      <Archive className="w-12 h-12" style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                      <p style={{ margin: 0, fontSize: '0.9rem' }}>No patterns saved yet</p>
                      <p style={{ margin: '8px 0 0', fontSize: '0.75rem', color: '#555' }}>
                        Generate a pattern and it will auto-save here
                      </p>
                    </div>
                  ) : (
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '10px',
                      maxHeight: '400px',
                      overflowY: 'auto'
                    }}>
                      {drumLibrary.map((entry, idx) => (
                        <div 
                          key={entry.id}
                          style={{
                            background: 'rgba(0,0,0,0.3)',
                            border: '1px solid rgba(139, 92, 246, 0.2)',
                            borderRadius: '10px',
                            padding: '12px',
                            transition: 'all 0.15s'
                          }}
                          data-testid={`library-entry-${idx}`}
                        >
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            marginBottom: '10px'
                          }}>
                            <div>
                              <h4 style={{ 
                                color: '#00f0ff', 
                                fontSize: '0.9rem', 
                                fontWeight: 700,
                                margin: 0,
                                textTransform: 'uppercase'
                              }}>
                                {entry.genre} - {entry.tempo} BPM
                              </h4>
                              <p style={{ 
                                color: '#666', 
                                fontSize: '0.7rem', 
                                margin: '4px 0 0'
                              }}>
                                {entry.kit?.toUpperCase()} Kit • {new Date(entry.timestamp).toLocaleDateString()}
                              </p>
                              {entry.style_prompt && (
                                <p style={{ 
                                  color: '#a78bfa', 
                                  fontSize: '0.65rem', 
                                  margin: '6px 0 0',
                                  fontStyle: 'italic',
                                  maxWidth: '300px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  Style: {entry.style_prompt.substring(0, 50)}...
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => deleteFromLibrary(entry.id)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#666',
                                cursor: 'pointer',
                                padding: '4px'
                              }}
                              data-testid={`delete-entry-${idx}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>

                          {/* Action Buttons */}
                          <div style={{ 
                            display: 'flex', 
                            flexWrap: 'wrap',
                            gap: '8px' 
                          }}>
                            {/* Load Button */}
                            <button
                              onClick={() => loadFromLibrary(entry)}
                              style={{
                                flex: '1 1 80px',
                                padding: '8px 12px',
                                background: 'linear-gradient(135deg, #00f0ff, #0ea5e9)',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#000',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                              }}
                              data-testid={`load-entry-${idx}`}
                            >
                              <Upload className="w-3 h-3" /> LOAD
                            </button>

                            {/* Preview Audio Button - Respects ON/OFF toggle */}
                            {entry.audioUrl && libraryAudioPreviewEnabled && (
                              <button
                                onClick={() => {
                                  const audio = new Audio();
                                  audio.preload = 'metadata';
                                  audio.addEventListener('canplay', () => audio.play().catch(() => {}), { once: true });
                                  audio.src = entry.audioUrl;
                                  audio.load();
                                  toast.success('🎧 Playing preview...');
                                }}
                                style={{
                                  flex: '1 1 80px',
                                  padding: '8px 12px',
                                  background: 'linear-gradient(135deg, #10b981, #059669)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  color: '#fff',
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '4px'
                                }}
                                data-testid={`preview-entry-${idx}`}
                              >
                                <Play className="w-3 h-3" /> PREVIEW
                              </button>
                            )}
                            
                            {/* Generate Audio Preview Button - When no audio saved */}
                            {!entry.audioUrl && libraryAudioPreviewEnabled && (
                              <button
                                onClick={async () => {
                                  toast.info('🎧 Generating audio preview...');
                                  const prompt = entry.suno_prompt || entry.style_prompt || `${entry.genre} drums, ${entry.kit} kit, ${entry.tempo} BPM`;
                                  await generateAudioPreview(prompt, entry.genre, `library-${entry.id}`);
                                }}
                                disabled={audioPreviewLoading === `library-${entry.id}`}
                                style={{
                                  flex: '1 1 80px',
                                  padding: '8px 12px',
                                  background: audioPreviewLoading === `library-${entry.id}`
                                    ? 'rgba(139, 92, 246, 0.3)'
                                    : 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  color: '#fff',
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  cursor: audioPreviewLoading === `library-${entry.id}` ? 'wait' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '4px'
                                }}
                                data-testid={`gen-preview-entry-${idx}`}
                              >
                                {audioPreviewLoading === `library-${entry.id}` ? (
                                  <><Loader2 className="w-3 h-3 animate-spin" /> GEN...</>
                                ) : (
                                  <><Headphones className="w-3 h-3" /> 🎧 GEN</>
                                )}
                              </button>
                            )}

                            {/* Download MIDI Button */}
                            <button
                              onClick={async () => {
                                toast.info('🎹 Generating MIDI...');
                                await generateMidiFromPrompt(entry.suno_prompt || `${entry.genre} drums, ${entry.kit} kit, ${entry.tempo} BPM`);
                              }}
                              style={{
                                flex: '1 1 80px',
                                padding: '8px 12px',
                                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#fff',
                                fontSize: '0.7rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                              }}
                              data-testid={`midi-entry-${idx}`}
                            >
                              <Music className="w-3 h-3" /> MIDI
                            </button>

                            {/* Download WAV Button */}
                            {entry.audioUrl && (
                              <button
                                onClick={async () => {
                                  const filename = `SoundDNA_${entry.genre}_${entry.tempo}BPM.wav`;
                                  try {
                                    let src = entry.audioUrl;
                                    if (src.startsWith('/')) src = `${API_ORIGIN}${src}`;
                                    toast.info('⏳ Preparing WAV...', { duration: 1500 });
                                    const response = await fetch(src);
                                    const blob = await response.blob();
                                    // Unified helper → iOS gets the native share sheet (stays in the app,
                                    // no navigating away to a file-preview page); desktop unchanged.
                                    await shareOrDownloadBlob(blob, filename, 'audio/wav', {
                                      sourceUrl: src, desktopPicker: true, successMessage: '📥 WAV saved!',
                                    });
                                  } catch (e) {
                                    toast.error('Download failed — try again');
                                  }
                                }}
                                style={{
                                  flex: '1 1 80px',
                                  padding: '8px 12px',
                                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  color: '#fff',
                                  fontSize: '0.7rem',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '4px'
                                }}
                                data-testid={`wav-entry-${idx}`}
                              >
                                <Download className="w-3 h-3" /> WAV
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Generated Pattern Display */}
              {generatedDrumPattern && (
                <div style={{
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid #333',
                  borderRadius: '12px',
                  padding: '16px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '12px',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#00ff88',
                        boxShadow: '0 0 10px #00ff88'
                      }} />
                      <span style={{ 
                        color: '#00d4ff', 
                        fontWeight: 700, 
                        fontSize: '0.9rem',
                        letterSpacing: '1px'
                      }}>
                        {(generatedDrumPattern.genre || drumGenre).toUpperCase()} @ {generatedDrumPattern.tempo || drumTempo} BPM
                        {generatedDrumPattern.key && <span style={{ color: 'var(--quantum-purple, #8b5cf6)', marginLeft: '8px' }}>• {generatedDrumPattern.key}</span>}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={copySunoPromptFromDrum}
                        style={{
                          padding: '8px 14px',
                          background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: '0 2px 10px rgba(245, 158, 11, 0.3)'
                        }}
                        title="Copy Suno-ready prompt"
                      >
                        <Sparkles className="w-3 h-3" /> SUNO
                      </button>
                      <button
                        onClick={addDrumToWorkflow}
                        style={{
                          padding: '8px 14px',
                          background: 'linear-gradient(135deg, #00d4ff, #8b5cf6)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          boxShadow: '0 2px 10px rgba(0, 212, 255, 0.3)'
                        }}
                      >
                        <Plus className="w-3 h-3" /> WORKFLOW
                      </button>
                    </div>
                  </div>

                  {/* PROMPT CONTROL CENTER */}
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.1), rgba(139, 92, 246, 0.08))',
                    border: '1px solid rgba(0, 240, 255, 0.3)',
                    borderRadius: '10px',
                    padding: '16px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '1rem' }}>🎛️</span>
                        <span style={{ color: '#00f0ff', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.1em' }}>
                          PROMPT CONTROL CENTER
                        </span>
                      </div>
                      <button
                        onClick={() => setShowPromptEditor(!showPromptEditor)}
                        style={{
                          padding: '6px 12px',
                          background: showPromptEditor ? '#00f0ff' : 'transparent',
                          border: '1px solid #00f0ff',
                          borderRadius: '4px',
                          color: showPromptEditor ? '#000' : '#00f0ff',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        {showPromptEditor ? 'DONE EDITING' : 'EDIT PROMPT'}
                      </button>
                    </div>
                    
                    {/* Editable Prompt Area */}
                    {showPromptEditor ? (
                      <textarea
                        value={editableDrumPrompt || generatedDrumPattern.suno_prompt}
                        onChange={(e) => setEditableDrumPrompt(e.target.value)}
                        style={{
                          width: '100%',
                          minHeight: '100px',
                          padding: '12px',
                          background: '#0a0a12',
                          border: '1px solid #00f0ff',
                          borderRadius: '6px',
                          color: '#00f0ff',
                          fontSize: '0.8rem',
                          fontFamily: 'JetBrains Mono, monospace',
                          lineHeight: 1.5,
                          resize: 'vertical'
                        }}
                        placeholder="Edit your prompt here..."
                      />
                    ) : (
                      <div style={{
                        padding: '12px',
                        background: 'rgba(0,0,0,0.4)',
                        borderRadius: '6px',
                        border: '1px solid #333'
                      }}>
                        <p style={{ 
                          color: '#00f0ff', 
                          fontSize: '0.8rem', 
                          margin: 0, 
                          lineHeight: 1.5,
                          fontFamily: 'JetBrains Mono, monospace'
                        }}>
                          {editableDrumPrompt || generatedDrumPattern.suno_prompt}
                        </p>
                      </div>
                    )}
                    
                    {/* Quick Actions */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => {
                          const prompt = editableDrumPrompt || generatedDrumPattern.suno_prompt;
                          universalCopy(prompt, "📋 Prompt copied!");
                        }}
                        style={{
                          padding: '8px 14px',
                          background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Copy className="w-3 h-3" /> COPY
                      </button>
                      <button
                        onClick={() => {
                          setEditableDrumPrompt(generatedDrumPattern.suno_prompt);
                          toast.info("🔄 Reset to original");
                        }}
                        style={{
                          padding: '8px 14px',
                          background: 'transparent',
                          border: '1px solid #666',
                          borderRadius: '6px',
                          color: '#888',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        RESET
                      </button>
                    </div>
                  </div>

                  {/* DOWNLOAD CENTER */}
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(34, 197, 94, 0.08))',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    borderRadius: '10px',
                    padding: '16px',
                    marginBottom: '12px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                      <span style={{ fontSize: '1rem' }}>💾</span>
                      <span style={{ color: '#10b981', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.1em' }}>
                        DOWNLOAD CENTER
                      </span>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
                      {/* Download MIDI */}
                      <button
                        onClick={async () => {
                          const prompt = editableDrumPrompt || generatedDrumPattern?.suno_prompt;
                          toast.info("🎹 Generating MIDI...");
                          try {
                            const response = await axios.post(`${API}/generate-midi`, {
                              style_prompt: prompt,
                              bars: 32,
                              preset_id: null
                            });
                            if (response.data.success) {
                              const { filename } = response.data;
                              const downloadUrl = `${API}/download-midi/${filename}`;
                              const fetchResponse = await fetch(downloadUrl);
                              const blob = await fetchResponse.blob();
                              await downloadFileWithPicker(blob, filename, 'audio/midi', `🎹 MIDI saved!`, downloadUrl);
                            }
                          } catch (error) {
                            toast.error("MIDI generation failed");
                          }
                        }}
                        style={{
                          padding: '12px 16px',
                          background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <Music className="w-4 h-4" /> MIDI
                      </button>
                      
                      {/* Download WAV - render on tap, save via real anchor (iOS gesture-safe) */}
                      {drumWavReady ? (
                      <button
                        onClick={() => saveWavFromUrl(drumWavReady.url, drumWavReady.filename)}
                        style={{
                          padding: '12px 16px',
                          background: 'linear-gradient(135deg, #22c55e, #059669)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                        data-testid="drum-panel-save-wav-anchor"
                      >
                        <Download className="w-4 h-4" /> SAVE WAV
                      </button>
                      ) : (
                      <button
                        onClick={async () => {
                          const prompt = editableDrumPrompt || generatedDrumPattern?.suno_prompt;
                          // Reuse the already-rendered track for this exact pattern
                          if (drumAudioUrl && generatedDrumPattern?.suno_prompt && drumAudioRenderedFor.current === generatedDrumPattern.suno_prompt) {
                            setDrumWavReady({ url: drumAudioUrl, filename: `SoundDNA_${drumGenre}_${drumTempo}BPM.wav` });
                            toast.success('🎵 Your previewed track is ready — tap "SAVE WAV" to download it.', { duration: 7000 });
                            return;
                          }
                          toast.info("🎵 Generating WAV (30-90s)...");
                          try {
                            const response = await axios.post(`${API}/generate-drum-preview`, {
                              genre: generatedDrumPattern?.genre || drumGenre,
                              tempo: generatedDrumPattern?.tempo || drumTempo,
                              kit: generatedDrumPattern?.kit || drumKit,
                              swing: generatedDrumPattern?.swing || drumSwing,
                              style_prompt: prompt,
                              prompt: prompt,
                              email: currentUser?.email || getGuestEmail()
                            });
                            if (response.data.task_id) {
                              const taskId = response.data.task_id;
                              let attempts = 0;
                              let pollErrs = 0;
                              const maxAttempts = 100;
                              const poll = setInterval(async () => {
                                attempts++;
                                try {
                                  const status = await axios.get(`${API}/drum-preview-status/${taskId}`);
                                  pollErrs = 0;
                                  if (status.data.status === 'completed' && status.data.audio_url) {
                                    clearInterval(poll);
                                    // RENDER done — arm the SAVE anchor; downloading here would
                                    // not be a user gesture and iOS Safari blocks it silently.
                                    const resolvedUrl = status.data.audio_url.startsWith('/api')
                                      ? `${API_ORIGIN}${status.data.audio_url}`
                                      : status.data.audio_url;
                                    setDrumAudioUrl(resolvedUrl);
                                    drumAudioRenderedFor.current = generatedDrumPattern?.suno_prompt || null;
                                    setDrumWavReady({ url: resolvedUrl, filename: `SoundDNA_${drumGenre}_${drumTempo}BPM.wav` });
                                    toast.success('🎵 WAV rendered! Tap "SAVE WAV" to download it.', { duration: 8000 });
                                  } else if (status.data.status === 'failed' || attempts >= maxAttempts) {
                                    clearInterval(poll);
                                    toast.error(status.data.error || "WAV generation failed or timed out", { duration: 9000 });
                                  }
                                } catch (err) {
                                  console.error("Poll error:", err);
                                  pollErrs++;
                                  if (pollErrs >= 5 || attempts >= maxAttempts) {
                                    clearInterval(poll);
                                    toast.error("Lost connection while rendering the WAV — please try again.");
                                  }
                                }
                              }, 3000);
                            }
                          } catch (error) {
                            toast.error("WAV generation failed");
                          }
                        }}
                        style={{
                          padding: '12px 16px',
                          background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <Download className="w-4 h-4" /> WAV
                      </button>
                      )}
                      
                      {/* Full Song Package */}
                      <button
                        onClick={() => {
                          const prompt = editableDrumPrompt || generatedDrumPattern?.suno_prompt;
                          const pattern = generatedDrumPattern?.pattern || '';
                          const fullPackage = `=== MANNIS FULL SONG PACKAGE ===
                          
🎹 SUNO/UDIO PROMPT:
${prompt}

🥁 DRUM PATTERN:
${pattern}

📊 SETTINGS:
Genre: ${generatedDrumPattern?.genre || drumGenre}
Tempo: ${generatedDrumPattern?.tempo || drumTempo} BPM
Kit: ${generatedDrumPattern?.kit || drumKit}
Key: ${generatedDrumPattern?.key || 'C minor'}

Generated by MANNIS Pro - sounddna.pro`;
                          
                          const blob = new Blob([fullPackage], { type: 'text/plain' });
                          shareOrDownloadBlob(blob, `SoundDNA_${drumGenre}_${drumTempo}BPM_Package.txt`, 'text/plain', { successMessage: "📦 Full song package downloaded!" });
                        }}
                        style={{
                          padding: '12px 16px',
                          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                          border: 'none',
                          borderRadius: '8px',
                          color: '#fff',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        <FileText className="w-4 h-4" /> FULL PACKAGE
                      </button>
                    </div>
                  </div>

                  {/* Pattern Description */}
                  {generatedDrumPattern.description && (
                    <div style={{
                      background: 'rgba(139, 92, 246, 0.1)',
                      border: '1px solid rgba(139, 92, 246, 0.2)',
                      borderRadius: '8px',
                      padding: '12px'
                    }}>
                      <p style={{ 
                        color: '#888', 
                        fontSize: '0.65rem', 
                        margin: '0 0 6px 0',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}>PATTERN NOTES</p>
                      <p style={{ color: '#a78bfa', fontSize: '0.8rem', margin: 0, lineHeight: 1.5 }}>
                        {generatedDrumPattern.description}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        {/* ===== VOCAL DASHBOARD: My Vocals Library (top) + Singing Vocal Generator (bottom) ===== */}
        <div data-testid="vocal-dashboard" style={{ display: 'flex', flexDirection: 'column', marginTop: '24px' }}>
        <div style={{
          background: 'linear-gradient(90deg, rgba(236,72,153,0.25), rgba(168,85,247,0.25))',
          border: '1px solid rgba(236,72,153,0.3)',
          borderBottom: 'none',
          borderRadius: '16px 16px 0 0',
          padding: '14px 20px',
          order: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <StepBadge n={3} />
          <span style={{ fontSize: '1.3rem' }}>🎛️</span>
          <span style={{ color: '#fff', fontWeight: 800, fontSize: '1.05rem', letterSpacing: '0.05em' }}>VOCAL DASHBOARD</span>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>Your saved vocal catalog</span>
        </div>
        {/* SINGING VOCAL GENERATOR - MiniMax AI */}
        <div 
          data-testid="singing-vocal-generator"
          style={{
          display: 'none',
          background: 'linear-gradient(135deg, rgba(236,72,153,0.12) 0%, rgba(168,85,247,0.12) 100%)',
          border: '1px solid rgba(236,72,153,0.3)',
          borderTop: 'none',
          borderRadius: '0 0 16px 16px',
          padding: '24px',
          order: 2
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.5rem' }}>🎤</span>
              <span style={{ color: '#ec4899', fontWeight: 700, fontSize: '1.2rem' }}>SINGING VOCAL GENERATOR</span>
              <span style={{ 
                background: 'linear-gradient(90deg, #ec4899, #a855f7)', 
                color: '#fff', 
                padding: '4px 10px', 
                borderRadius: '12px', 
                fontSize: '0.7rem', 
                fontWeight: 700 
              }}>MINIMAX AI</span>
              <span style={{ 
                background: 'rgba(159,71,255,0.2)', 
                color: '#9F47FF', 
                padding: '4px 10px', 
                borderRadius: '12px', 
                fontSize: '0.7rem', 
                fontWeight: 700 
              }}>REAL SINGING</span>
            </div>
            {/* Clear Singing Vocals Button */}
            <button
              onClick={() => {
                setSingingLyrics('');
                setVocalStyle('pop');
                setVocalGender('female');
                setSingingAudioUrl(null);
                toast.success('🧹 Singing vocals cleared!');
              }}
              style={{
                padding: '8px 14px',
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              data-testid="clear-singing-vocals-btn"
            >
              <Trash2 className="w-4 h-4" />
              Clear
            </button>
          </div>
          
          <p style={{ 
            textAlign: 'center', 
            color: 'rgba(255,255,255,0.6)', 
            fontSize: '0.85rem', 
            marginBottom: '20px' 
          }}>
            Generate actual singing vocals from your lyrics • Download & use in Suno or any DAW
          </p>
          
          {/* Vocal Style & Gender */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: '6px', display: 'block' }}>
                Vocal Style
              </label>
              <select
                value={vocalStyle}
                onChange={(e) => setVocalStyle(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '2px solid rgba(236,72,153,0.3)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.9rem'
                }}
              >
                <option value="R&B">R&B / Soul</option>
                <option value="Pop">Pop</option>
                <option value="Trap">Trap / Hip-Hop</option>
                <option value="Rap">Rap / Spoken Word</option>
                <option value="Rock">Rock</option>
                <option value="Country">Country</option>
                <option value="Jazz">Jazz</option>
                <option value="Gospel">Gospel</option>
                <option value="EDM">EDM / Dance</option>
                <option value="Indie">Indie</option>
                <option value="Classical">Classical</option>
                <option value="Afrobeat">Afrobeat</option>
                <option value="Afropop">Afropop / African</option>
                <option value="Amapiano">Amapiano</option>
                <option value="Reggae">Reggae / Dancehall</option>
                <option value="Latin">Latin / Reggaeton</option>
                <option value="Metal">Metal / Hard Rock</option>
                <option value="Blues">Blues</option>
                <option value="Soul">Neo Soul</option>
                <option value="Funk">Funk</option>
                <option value="Alternative">Alternative</option>
                <option value="Drill">UK Drill</option>
                <option value="Ballad">Ballad / Slow Jam</option>
              </select>
            </div>
            
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: '6px', display: 'block' }}>
                Voice Gender
              </label>
              <select
                value={vocalGender}
                onChange={(e) => setVocalGender(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '2px solid rgba(236,72,153,0.3)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.9rem'
                }}
              >
                <option value="female">Female</option>
                <option value="male">Male</option>
              </select>
            </div>
            
            <div>
              <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', marginBottom: '6px', display: 'block' }}>
                Tempo
              </label>
              <select
                value={vocalTempo}
                onChange={(e) => setVocalTempo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '2px solid rgba(236,72,153,0.3)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.9rem'
                }}
              >
                <option value="slow">Slow (70 BPM)</option>
                <option value="medium">Medium (100 BPM)</option>
                <option value="fast">Fast (130 BPM)</option>
              </select>
            </div>
          </div>
          
          {/* ADD TO WORKFLOW BUTTON - Integrates with Suno Export */}
          <div style={{ 
            display: 'flex', 
            gap: '10px', 
            marginBottom: '16px',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => {
                // Build vocal style description for Suno
                const vocalStyleDesc = {
                  "R&B": "smooth R&B vocals, silky melismatic runs, sensual neo-soul delivery",
                  "Pop": "polished pop vocals, bright catchy melodic delivery, radio-ready",
                  "Trap": "melodic trap vocals, autotune, emotional hip-hop delivery",
                  "Rap": "confident rap flow, rhythmic spoken-word delivery",
                  "Rock": "powerful rock vocals, raw belting, gritty emotional delivery",
                  "Country": "country vocals with twang, heartfelt storytelling delivery",
                  "Jazz": "smooth jazz vocals, sophisticated phrasing, scat elements",
                  "Gospel": "powerful gospel vocals, spiritual belting, choir harmonies",
                  "EDM": "anthemic dance vocals, processed club-ready sound",
                  "Indie": "raw indie vocals, authentic understated emotional delivery",
                  "Classical": "operatic classical vocals, trained vibrato, refined technique",
                  "Afrobeat": "energetic Afrobeats vocals, rhythmic African delivery",
                  "Afropop": "melodic Afropop vocals, vibrant catchy hooks",
                  "Amapiano": "laid-back Amapiano vocals, South African house groove",
                  "Reggae": "reggae vocals, Caribbean offbeat delivery, conscious vibes",
                  "Latin": "passionate Latin vocals, reggaeton influenced delivery",
                  "Metal": "aggressive metal vocals, powerful screaming elements",
                  "Blues": "soulful blues vocals, raw emotional expression",
                  "Soul": "warm neo-soul vocals, rich emotional depth",
                  "Funk": "groovy funk vocals, playful James Brown energy",
                  "Alternative": "unique alternative vocals, artistic unconventional delivery",
                  "Drill": "aggressive UK drill vocals, dark roadman energy",
                  "Ballad": "emotional ballad vocals, intimate powerful expression"
                };
                
                const styleDesc = vocalStyleDesc[vocalStyle] || `${vocalStyle} style vocals`;
                const tempoDesc = vocalTempo === 'slow' ? '70 BPM slow ballad' : vocalTempo === 'fast' ? '130 BPM upbeat' : '100 BPM mid-tempo';
                
                // Update workflow with vocal style
                const vocalPrompt = `${vocalGender} ${styleDesc}, ${tempoDesc}`;
                
                updateWorkflow({ 
                  voiceSample: vocalPrompt,
                  genre: vocalStyle,
                  source: 'vocal-generator'
                });
                
                // Also add lyrics to workflow if present
                if (vocalLyrics.trim()) {
                  updateWorkflow({ lyrics: vocalLyrics });
                }
                
                toast.success(`✅ Added ${vocalStyle} vocals to workflow! Export to Suno above.`);
              }}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)'
              }}
              data-testid="add-vocal-to-workflow-btn"
            >
              <ArrowUp className="w-4 h-4" />
              ADD TO WORKFLOW (SUNO)
            </button>
            
            <button
              onClick={() => {
                // Copy vocal style directly
                const vocalStyleDesc = {
                  "R&B": "smooth R&B vocals, silky melismatic runs, sensual neo-soul delivery",
                  "Pop": "polished pop vocals, bright catchy melodic delivery",
                  "Trap": "melodic trap vocals with autotune, emotional hip-hop style",
                  "Rap": "confident rap delivery, rhythmic flow",
                  "Rock": "powerful rock vocals, raw belting",
                  "Country": "country vocals with twang, heartfelt",
                  "Jazz": "smooth jazz vocals, sophisticated phrasing",
                  "Gospel": "powerful gospel vocals, spiritual belting",
                  "EDM": "anthemic dance vocals, club-ready",
                  "Indie": "raw indie vocals, authentic delivery",
                  "Classical": "operatic classical vocals, trained vibrato",
                  "Afrobeat": "energetic Afrobeats vocals, rhythmic African style",
                  "Afropop": "melodic Afropop vocals, vibrant hooks",
                  "Amapiano": "laid-back Amapiano vocals, South African groove",
                  "Reggae": "reggae vocals, Caribbean style",
                  "Latin": "passionate Latin vocals, reggaeton style",
                  "Metal": "aggressive metal vocals, powerful",
                  "Blues": "soulful blues vocals, emotional",
                  "Soul": "warm neo-soul vocals",
                  "Funk": "groovy funk vocals, playful",
                  "Alternative": "unique alternative vocals",
                  "Drill": "aggressive UK drill vocals",
                  "Ballad": "emotional ballad vocals"
                };
                
                const styleDesc = vocalStyleDesc[vocalStyle] || `${vocalStyle} vocals`;
                const copyText = `${vocalGender} singer, ${styleDesc}`;
                
                universalCopy(copyText, `📋 Copied: "${copyText}"`);
              }}
              style={{
                padding: '12px 16px',
                background: 'rgba(236, 72, 153, 0.2)',
                border: '1px solid rgba(236, 72, 153, 0.4)',
                borderRadius: '8px',
                color: '#ec4899',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              data-testid="copy-vocal-style-btn"
            >
              <Copy className="w-4 h-4" />
              COPY STYLE
            </button>
          </div>
          
          {/* Lyrics Input */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>
                Lyrics to Sing:
              </label>
              <span style={{ 
                color: vocalLyrics.length > 3500 ? '#ef4444' : vocalLyrics.length > 3000 ? '#f59e0b' : 'rgba(255,255,255,0.5)', 
                fontSize: '0.8rem',
                fontWeight: vocalLyrics.length > 3500 ? 700 : 400
              }}>
                {vocalLyrics.length}/3500 characters
              </span>
            </div>
            <textarea
              value={vocalLyrics}
              onChange={(e) => {
                if (e.target.value.length <= 3500) {
                  setVocalLyrics(e.target.value);
                } else {
                  toast.error("Maximum 3500 characters for lyrics");
                }
              }}
              placeholder="Enter your lyrics here (max 3500 characters)...&#10;&#10;Example:&#10;Walking through the city lights&#10;Searching for you every night&#10;My heart beats to this melody&#10;You're the only one for me"
              style={{
                width: '100%',
                height: '150px',
                padding: '14px',
                background: 'rgba(0,0,0,0.4)',
                border: `2px solid ${vocalLyrics.length > 3500 ? 'rgba(239,68,68,0.5)' : 'rgba(236,72,153,0.3)'}`,
                borderRadius: '10px',
                color: '#fff',
                fontSize: '1rem',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
              data-testid="vocal-lyrics-input"
            />
            {vocalLyrics.length > 3500 && (
              <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '6px' }}>
                ⚠️ Lyrics too long! Please shorten to 3500 characters or less.
              </p>
            )}
          </div>
          
          {/* Progress Bar */}
          {vocalGenerating && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#ec4899', fontSize: '0.9rem', fontWeight: 600 }}>
                  🎤 Generating Vocals... {vocalProgress}%
                </span>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                  ~2 minutes
                </span>
              </div>
              <div style={{ 
                height: '8px', 
                background: 'rgba(255,255,255,0.1)', 
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  width: `${vocalProgress}%`, 
                  height: '100%', 
                  background: 'linear-gradient(90deg, #ec4899, #a855f7)',
                  borderRadius: '4px',
                  transition: 'width 0.3s'
                }} />
              </div>
            </div>
          )}
          
          {/* Error */}
          {vocalError && (
            <div style={{
              background: 'rgba(239,68,68,0.15)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '16px',
              color: '#ef4444',
              fontSize: '0.9rem',
              textAlign: 'center'
            }}>
              {vocalError}
            </div>
          )}
          
          {/* Generate Button */}
          <button
            onClick={generateVocals}
            disabled={vocalGenerating || !vocalLyrics.trim() || vocalLyrics.length > 3500}
            style={{
              width: '100%',
              padding: '16px 24px',
              background: vocalGenerating 
                ? 'rgba(236,72,153,0.3)' 
                : 'linear-gradient(90deg, #ec4899, #a855f7)',
              border: 'none',
              borderRadius: '10px',
              color: '#fff',
              fontSize: '1.1rem',
              fontWeight: 700,
              cursor: (vocalGenerating || !vocalLyrics.trim() || vocalLyrics.length > 3500) ? 'not-allowed' : 'pointer',
              opacity: (!vocalLyrics.trim() || vocalLyrics.length > 3500) ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px'
            }}
            data-testid="generate-vocals-btn"
          >
            {vocalGenerating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Generating Singing Vocals...
              </>
            ) : (
              <>
                <Mic className="w-5 h-5" />
                GENERATE SINGING VOCALS
              </>
            )}
          </button>
          
          {/* ========== DOWNLOAD YOUR VOCALS SECTION ========== */}
          <div style={{
            marginTop: '20px',
            background: 'linear-gradient(135deg, rgba(159,71,255,0.15), rgba(5,150,105,0.1))',
            border: '2px solid rgba(159,71,255,0.4)',
            borderRadius: '12px',
            padding: '20px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginBottom: '16px'
            }}>
              <Download className="w-6 h-6" style={{ color: '#9F47FF' }} />
              <span style={{ 
                color: '#9F47FF', 
                fontWeight: 700, 
                fontSize: '1.1rem',
                textTransform: 'uppercase',
                letterSpacing: '1px'
              }}>
                DOWNLOAD YOUR VOCALS
              </span>
            </div>
            
            {vocalAudioUrl ? (
              /* When vocals are ready - show player and download buttons */
              <div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginBottom: '12px',
                  color: '#9F47FF',
                  fontWeight: 600
                }}>
                  <CheckCircle className="w-5 h-5" />
                  Singing Vocals Ready!
                </div>
                <audio preload="metadata"
                  src={vocalAudioUrl}
                  controls
                  style={{ width: '100%', borderRadius: '8px', marginBottom: '12px' }}
                  data-testid="vocal-audio-player"
                />
                
                {/* Download Buttons */}
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {/* Download WAV for Suno */}
                  <button
                    onClick={async () => {
                      const taskId = vocalAudioUrl.split('/').pop();
                      const fileName = `sounddna-vocals-${vocalStyle}-${Date.now()}.wav`;
                      
                      try {
                        // Fetch the file as blob
                        const response = await fetch(`${API}/download-vocal/${taskId}`);
                        const blob = await response.blob();
                        await downloadFileWithPicker(blob, fileName, 'audio/wav', '🎤 Vocals saved!', `${API}/download-vocal/${taskId}`);
                      } catch (err) {
                        console.error('Download error:', err);
                        toast.error('Download failed. Please try again.');
                      }
                    }}
                    style={{
                      flex: 1,
                      minWidth: '140px',
                      padding: '14px',
                      background: 'linear-gradient(90deg, #9F47FF, #059669)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(159,71,255,0.3)'
                    }}
                    data-testid="download-vocal-btn"
                  >
                    <Download className="w-5 h-5" />
                    DOWNLOAD WAV
                  </button>
                  
                  {/* Download & Open Suno */}
                  <button
                    onClick={async () => {
                      const taskId = vocalAudioUrl.split('/').pop();
                      const fileName = `sounddna-vocals-${vocalStyle}-${Date.now()}.wav`;
                      
                      try {
                        // Fetch the file as blob
                        const response = await fetch(`${API}/download-vocal/${taskId}`);
                        const blob = await response.blob();
                        const saved = await downloadFileWithPicker(blob, fileName, 'audio/wav', '🎤 Vocals saved! Opening Suno...', `${API}/download-vocal/${taskId}`);
                        
                        // Open Suno after download
                        if (saved) {
                          setTimeout(() => {
                            window.open('https://suno.com/create', '_blank');
                          }, 1000);
                        }
                      } catch (err) {
                        console.error('Download error:', err);
                        toast.error('Download failed. Please try again.');
                      }
                    }}
                    style={{
                      flex: 1,
                      minWidth: '140px',
                      padding: '14px',
                      background: 'linear-gradient(90deg, #9F47FF, #6366f1)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(159,71,255,0.3)'
                    }}
                    data-testid="download-open-suno-btn"
                  >
                    <ExternalLink className="w-5 h-5" />
                    DOWNLOAD & OPEN SUNO
                  </button>
                  
                  {/* Copy WAV Link */}
                  <button
                    onClick={() => {
                      const fullUrl = API_ORIGIN + vocalAudioUrl;
                      universalCopy(fullUrl, '📋 WAV LINK COPIED! Paste this link anywhere to share your vocals!');
                    }}
                    style={{
                      flex: 1,
                      minWidth: '140px',
                      padding: '14px',
                      background: 'linear-gradient(90deg, #f59e0b, #d97706)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(245,158,11,0.3)'
                    }}
                    data-testid="copy-vocal-link-btn"
                  >
                    <Copy className="w-5 h-5" />
                    COPY MP3 LINK
                  </button>
                  
                  {/* Save to Catalog */}
                  <button
                    onClick={async () => {
                      try {
                        const libRes = await fetch(`${API}/vocal-library/${encodeURIComponent(userEmail)}`);
                        if (!libRes.ok) throw new Error('fetch failed');
                        const libData = await libRes.json();
                        setVocalLibrary(libData.vocals || []);
                        setVocalLibraryExpanded(true);
                        const taskId = vocalTaskId || vocalAudioUrl.split('/').pop();
                        const found = (libData.vocals || []).some(v => v.task_id === taskId);
                        toast.success(found ? '✅ Saved to your Vocal Catalog! See it in MY VOCALS LIBRARY above.' : '✅ Vocal Catalog refreshed!');
                        document.querySelector('[data-testid="my-vocals-library"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      } catch (err) {
                        toast.error('Could not confirm save. Please try again.');
                      }
                    }}
                    style={{
                      flex: 1,
                      minWidth: '140px',
                      padding: '14px',
                      background: 'linear-gradient(90deg, #ec4899, #a855f7)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '1rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: '0 4px 15px rgba(236,72,153,0.3)'
                    }}
                    data-testid="save-to-catalog-btn"
                  >
                    <CheckCircle className="w-5 h-5" />
                    SAVE TO CATALOG
                  </button>
                </div>
                
                {/* Instructions */}
                <div style={{
                  marginTop: '16px',
                  padding: '12px',
                  background: 'rgba(159,71,255,0.1)',
                  border: '1px solid rgba(159,71,255,0.3)',
                  borderRadius: '8px'
                }}>
                  <p style={{ color: '#a78bfa', fontWeight: 600, fontSize: '0.85rem', marginBottom: '8px' }}>
                    📋 How to use in Suno:
                  </p>
                  <ol style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', margin: 0, paddingLeft: '20px' }}>
                    <li>Click "DOWNLOAD WAV" above</li>
                    <li>Go to <strong>suno.com/create</strong></li>
                    <li>Click <strong>"+ Audio"</strong> → <strong>"Upload"</strong></li>
                    <li>Select your downloaded WAV file</li>
                    <li>Suno will create music with your vocals!</li>
                  </ol>
                </div>
              </div>
            ) : (
              /* When no vocals yet - show waiting state */
              <div style={{
                textAlign: 'center',
                padding: '20px',
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '8px'
              }}>
                <div style={{ 
                  color: 'rgba(255,255,255,0.5)', 
                  fontSize: '0.95rem',
                  marginBottom: '12px'
                }}>
                  {vocalGenerating ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" style={{ margin: '0 auto 10px', display: 'block' }} />
                      Generating your vocals... Download will appear here when ready!
                    </>
                  ) : (
                    <>
                      <Mic className="w-8 h-8" style={{ margin: '0 auto 10px', display: 'block', opacity: 0.5 }} />
                      Your WAV download will appear here after generation
                    </>
                  )}
                </div>
                <div style={{
                  display: 'flex',
                  gap: '10px',
                  justifyContent: 'center',
                  opacity: 0.4
                }}>
                  <div style={{
                    padding: '12px 24px',
                    background: 'rgba(159,71,255,0.3)',
                    borderRadius: '8px',
                    color: 'rgba(255,255,255,0.5)',
                    fontWeight: 600
                  }}>
                    <Download className="w-4 h-4" style={{ display: 'inline', marginRight: '6px' }} />
                    DOWNLOAD WAV
                  </div>
                  <div style={{
                    padding: '12px 24px',
                    background: 'rgba(159,71,255,0.3)',
                    borderRadius: '8px',
                    color: 'rgba(255,255,255,0.5)',
                    fontWeight: 600
                  }}>
                    <ExternalLink className="w-4 h-4" style={{ display: 'inline', marginRight: '6px' }} />
                    OPEN SUNO
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Voice Input Section - Record your own melody */}
        <div className="voice-input-section" data-testid="voice-input" style={{ marginTop: '24px', order: 3 }}>
          <div className="voice-record-box" style={{ marginTop: 0 }}>
            <div className="voice-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '1.2rem' }}>🎤</span>
                <span>Voice Sampler</span>
                <span style={{ 
                  background: 'rgba(159,71,255,0.2)', 
                  color: '#9F47FF', 
                  padding: '2px 8px', 
                  borderRadius: '4px', 
                  fontSize: '0.7rem', 
                  fontWeight: 600,
                  marginLeft: '8px'
                }}>30 SEC RECORDER</span>
              </div>
              {/* Clear Voice Sampler Button */}
              <button
                onClick={() => {
                  setRecordedAudioUrl(null);
                  setRecordingTime(0);
                  toast.success('🧹 Voice sampler cleared!');
                }}
                style={{
                  padding: '6px 12px',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '6px',
                  color: '#ef4444',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                data-testid="clear-voice-sampler-btn"
              >
                <Trash2 className="w-3 h-3" />
                Clear
              </button>
            </div>
            
            <div className="voice-record-area">
              <p style={{ 
                color: 'rgba(255,255,255,0.6)', 
                fontSize: '0.85rem', 
                marginBottom: '16px',
                textAlign: 'center'
              }}>
                Record your original vocals • Up to 30 seconds
              </p>
              
              {/* Record Button */}
              <button 
                className={`voice-record-btn ${isRecording ? 'recording' : ''}`}
                onClick={isRecording ? stopRecording : startRecording}
                disabled={false}
                data-testid="voice-record-btn"
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  border: isRecording ? '4px solid #9F47FF' : '4px solid #9F47FF',
                  background: isRecording 
                    ? 'linear-gradient(135deg, rgba(255,0,102,0.3) 0%, rgba(255,0,102,0.1) 100%)' 
                    : 'linear-gradient(135deg, rgba(159,71,255,0.2) 0%, rgba(159,71,255,0.05) 100%)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  transition: 'all 0.3s'
                }}
              >
                {isRecording ? (
                  <>
                    <MicOff className="w-8 h-8" style={{ color: '#9F47FF' }} />
                    <span className="recording-pulse" />
                  </>
                ) : (
                  <Mic className="w-8 h-8" style={{ color: '#9F47FF' }} />
                )}
              </button>
              
              {/* Recording Status */}
              {isRecording ? (
                <div className="recording-status" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}>
                  <span className="recording-dot" style={{
                    width: '10px',
                    height: '10px',
                    background: '#9F47FF',
                    borderRadius: '50%',
                    animation: 'pulse 1s infinite'
                  }} />
                  <span style={{ color: '#9F47FF', fontWeight: 600 }}>Recording: {recordingTime}s / 30s</span>
                  <button 
                    className="stop-btn" 
                    onClick={stopRecording}
                    style={{
                      padding: '8px 16px',
                      background: '#9F47FF',
                      border: 'none',
                      borderRadius: '20px',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Stop Recording
                  </button>
                </div>
              ) : !recordedAudioUrl ? (
                <p className="voice-hint" style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
                  Tap to record • Max 30 seconds
                </p>
              ) : null}
              
              {/* Playback Section - Shows after recording */}
              {recordedAudioUrl && !isRecording && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(159,71,255,0.15) 0%, rgba(159,71,255,0.1) 100%)',
                  border: '2px solid rgba(159,71,255,0.4)',
                  borderRadius: '16px',
                  padding: '20px',
                  marginTop: '16px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: '10px',
                    marginBottom: '16px'
                  }}>
                    <span style={{ color: '#9F47FF', fontWeight: 700, fontSize: '1rem' }}>
                      🎵 YOUR VOCAL SAMPLE
                    </span>
                    <span style={{ 
                      background: 'rgba(159,71,255,0.2)', 
                      color: '#9F47FF', 
                      padding: '4px 10px', 
                      borderRadius: '6px', 
                      fontSize: '0.75rem', 
                      fontWeight: 600 
                    }}>
                      {recordingTime}s
                    </span>
                  </div>
                  
                  {/* Audio Player */}
                  <audio preload="metadata" 
                    controls 
                    src={recordedAudioUrl}
                    style={{
                      width: '100%',
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}
                    data-testid="audio-player"
                  />
                  
                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={downloadRecording}
                      style={{
                        flex: 1,
                        padding: '12px 20px',
                        background: 'linear-gradient(135deg, #9F47FF 0%, #9acd00 100%)',
                        border: 'none',
                        borderRadius: '10px',
                        color: '#000',
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      data-testid="download-recording-btn"
                    >
                      <Download className="w-4 h-4" /> DOWNLOAD
                    </button>
                    <button
                      onClick={clearRecording}
                      style={{
                        flex: 1,
                        padding: '12px 20px',
                        background: 'rgba(255,255,255,0.1)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '10px',
                        color: '#fff',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      data-testid="record-again-btn"
                    >
                      <RotateCcw className="w-4 h-4" /> RECORD AGAIN
                    </button>
                  </div>
                  
                  <p style={{ 
                    textAlign: 'center', 
                    color: 'rgba(255,255,255,0.4)', 
                    fontSize: '0.75rem', 
                    marginTop: '12px',
                    marginBottom: 0
                  }}>
                    Use your original vocal sample in any AI music app
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* MY VOCALS LIBRARY - Cloud Saved Vocals */}
        <div data-testid="my-vocals-library" style={{
          background: 'linear-gradient(135deg, rgba(236,72,153,0.08), rgba(168,85,247,0.05))',
          border: '1px solid rgba(236,72,153,0.3)',
          borderTop: 'none',
          borderRadius: 0,
          padding: '20px',
          order: 1
        }}>
          {/* Header */}
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              cursor: 'pointer',
              marginBottom: vocalLibraryExpanded ? '16px' : 0
            }}
            onClick={() => setVocalLibraryExpanded(!vocalLibraryExpanded)}
            data-testid="vocal-library-header"
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '1.5rem' }}>🎤</span>
              <span style={{ color: '#ec4899', fontWeight: 700, fontSize: '1.1rem' }}>
                MY VOCALS LIBRARY
              </span>
              <span style={{ 
                background: 'rgba(236,72,153,0.2)', 
                color: '#ec4899', 
                padding: '2px 8px', 
                borderRadius: '10px', 
                fontSize: '0.75rem',
                fontWeight: 600
              }}>
                {vocalLibrary.length} vocals
              </span>
            </div>
            <ChevronDown 
              className={`w-5 h-5 transition-transform ${vocalLibraryExpanded ? 'rotate-180' : ''}`} 
              style={{ color: '#ec4899' }}
            />
          </div>
          
          {/* Expanded Content */}
          {vocalLibraryExpanded && (
            <div>
              {/* Clear All Vocals Button */}
              {vocalLibrary.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                  <button
                    onClick={() => {
                      setVocalLibrary([]);
                      toast.success('🧹 Vocals library cleared!');
                    }}
                    style={{
                      padding: '8px 14px',
                      background: 'rgba(239, 68, 68, 0.15)',
                      border: '1px solid rgba(239, 68, 68, 0.3)',
                      borderRadius: '8px',
                      color: '#ef4444',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    data-testid="clear-vocals-library-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Clear All Vocals
                  </button>
                </div>
              )}
              {vocalLibrary.length === 0 ? (
                <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '20px' }}>
                  No vocals saved yet. Generate singing vocals in the generator below!
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
                  {vocalLibrary.map((vocal, index) => {
                    let audioUrl = vocal.audio_url;
                    if (audioUrl && audioUrl.startsWith('/api/')) {
                      audioUrl = `${API_ORIGIN}${audioUrl}`;
                    }
                    const isPlaying = playingVocal === vocal.task_id;
                    
                    return (
                      <div
                        key={vocal.task_id || index}
                        style={{
                          background: 'rgba(0,0,0,0.3)',
                          borderRadius: '12px',
                          padding: '14px',
                          border: isPlaying ? '2px solid #ec4899' : '1px solid rgba(236,72,153,0.2)'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: '#ec4899', fontWeight: 600, fontSize: '0.9rem' }}>
                              {vocal.vocal_style} - {vocal.gender}
                            </div>
                            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginTop: '4px' }}>
                              {vocal.lyrics?.substring(0, 80)}...
                            </div>
                          </div>
                          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>
                            {new Date(vocal.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        
                        {/* Audio Player */}
                        {audioUrl && (
                          <audio preload="metadata"
                            src={audioUrl}
                            controls
                            style={{ width: '100%', height: '36px', borderRadius: '6px', marginBottom: '8px' }}
                          />
                        )}
                        
                        {/* Action Buttons */}
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            onClick={async () => {
                              const fileName = `vocals-${vocal.vocal_style}-${vocal.task_id?.substring(0,8)}.wav`;
                              
                              try {
                                const response = await fetch(`${API}/download-vocal/${vocal.task_id}`);
                                const blob = await response.blob();
                                await downloadFileWithPicker(blob, fileName, 'audio/wav', '🎤 Vocals saved!', `${API}/download-vocal/${vocal.task_id}`);
                              } catch (err) {
                                toast.error('Download failed');
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              background: 'linear-gradient(90deg, #9F47FF, #059669)',
                              border: 'none',
                              borderRadius: '6px',
                              color: '#fff',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px'
                            }}
                          >
                            <Download className="w-3 h-3" />
                            Download
                          </button>
                          <button
                            onClick={async () => {
                              const fileName = `vocals-${vocal.vocal_style}-${vocal.task_id?.substring(0,8)}.wav`;
                              
                              try {
                                const response = await fetch(`${API}/download-vocal/${vocal.task_id}`);
                                const blob = await response.blob();
                                const saved = await downloadFileWithPicker(blob, fileName, 'audio/wav', '🎤 Vocals saved! Opening Suno...', `${API}/download-vocal/${vocal.task_id}`);
                                if (saved) {
                                  setTimeout(() => window.open('https://suno.com/create', '_blank'), 1000);
                                }
                              } catch (err) {
                                toast.error('Download failed');
                              }
                            }}
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              background: 'linear-gradient(90deg, #9F47FF, #6366f1)',
                              border: 'none',
                              borderRadius: '6px',
                              color: '#fff',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px'
                            }}
                          >
                            <ExternalLink className="w-3 h-3" />
                            Suno
                          </button>
                          <button
                            onClick={() => {
                              const fullUrl = API_ORIGIN + audioUrl;
                              universalCopy(fullUrl, '📋 LINK COPIED!');
                            }}
                            style={{
                              padding: '8px 12px',
                              background: 'linear-gradient(90deg, #f59e0b, #d97706)',
                              border: 'none',
                              borderRadius: '6px',
                              color: '#fff',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px'
                            }}
                          >
                            <Copy className="w-3 h-3" />
                            Copy
                          </button>
                          <button
                            onClick={async () => {
                              if (window.confirm('Delete this vocal?')) {
                                try {
                                  await fetch(`${API}/vocal-library/${encodeURIComponent(userEmail)}/${vocal.task_id}`, {
                                    method: 'DELETE'
                                  });
                                  setVocalLibrary(prev => prev.filter(v => v.task_id !== vocal.task_id));
                                  toast.success('Vocal deleted');
                                } catch (err) {
                                  toast.error('Failed to delete');
                                }
                              }
                            }}
                            style={{
                              padding: '8px 12px',
                              background: 'rgba(239,68,68,0.2)',
                              border: '1px solid rgba(239,68,68,0.3)',
                              borderRadius: '6px',
                              color: '#ef4444',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              cursor: 'pointer'
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        </div>

          {/* SONG CREATION - Combined Suno + Vocal generators */}
          <div style={{
            marginTop: '16px',
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1), rgba(139, 92, 246, 0.1))',
            border: '2px solid rgba(236, 72, 153, 0.4)',
            borderRadius: '12px'
          }} data-testid="song-creation-box">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
              <StepBadge n={4} />
              <span style={{ color: '#ec4899', fontWeight: 800, fontSize: '1rem' }}>🎵 SONG CREATION</span>
              <div style={{ display: 'flex', gap: '6px', marginLeft: 'auto' }}>
                <button
                  onClick={() => setSongCreationTab('song')}
                  style={{
                    padding: '7px 16px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                    background: songCreationTab === 'song' ? 'linear-gradient(135deg, #ec4899, #f43f5e)' : 'transparent',
                    border: songCreationTab === 'song' ? 'none' : '1px solid rgba(236, 72, 153, 0.4)',
                    color: songCreationTab === 'song' ? '#fff' : 'rgba(255,255,255,0.6)'
                  }}
                  data-testid="song-creation-tab-song"
                >
                  🎵 Full Song
                </button>
                <button
                  onClick={() => setSongCreationTab('vocals')}
                  style={{
                    padding: '7px 16px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                    background: songCreationTab === 'vocals' ? 'linear-gradient(135deg, #ec4899, #a855f7)' : 'transparent',
                    border: songCreationTab === 'vocals' ? 'none' : '1px solid rgba(236, 72, 153, 0.4)',
                    color: songCreationTab === 'vocals' ? '#fff' : 'rgba(255,255,255,0.6)'
                  }}
                  data-testid="song-creation-tab-vocals"
                >
                  🎤 Vocals Only
                </button>
              </div>
            </div>

            {songCreationTab === 'song' && (
            <div data-testid="suno-workflow-section">
            
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  background: 'linear-gradient(135deg, #ec4899, #f43f5e)',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Zap className="w-4 h-4" style={{ color: '#fff' }} />
                </div>
                <span style={{ color: '#ec4899', fontWeight: 700, fontSize: 'clamp(0.8rem, 3vw, 1rem)' }}>MANNIS AI</span>
                <span style={{ 
                  padding: '3px 8px', 
                  background: 'rgba(34, 197, 94, 0.2)', 
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '10px',
                  color: '#22c55e',
                  fontSize: '0.55rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap'
                }}>
                  BEST TAKE
                </span>
              </div>
              <button
                onClick={() => setFullscreenTool('suno-music')}
                style={{
                  padding: '6px 12px',
                  background: 'linear-gradient(135deg, #ec4899, #f43f5e)',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                data-testid="suno-expand-btn"
              >
                <Maximize2 className="w-3 h-3" />
                Full Studio
              </button>
            </div>

            {/* QUICK STYLE CHIPS + OPTIONAL VOICES LIBRARY */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'6px' }}>
                <Sparkles className="w-3 h-3" style={{ color:'#ec4899' }} />
                <span style={{ color:'rgba(255,255,255,0.6)', fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.08em' }}>QUICK STYLES</span>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginBottom:'10px' }}>
                {QUICK_STYLES.map(st => (
                  <button
                    key={st}
                    data-testid={`quick-style-${st.replace(/\s+/g,'-').toLowerCase()}`}
                    onClick={() => {
                      setSunoPrompt(prev => {
                        const p = (prev || '').trim();
                        if (!p) return st;
                        if (new RegExp(`\\b${st.replace(/[-\/\\^$*+?.()|[\]{}]/g,'\\$&')}\\b`, 'i').test(p)) return p;
                        return `${p}, ${st}`;
                      });
                    }}
                    style={{ padding:'6px 12px', borderRadius:'999px', fontSize:'0.72rem', fontWeight:700, cursor:'pointer', background:'rgba(236,72,153,0.12)', border:'1px solid rgba(236,72,153,0.35)', color:'#f9a8d4' }}
                  >
                    + {st}
                  </button>
                ))}
              </div>

              {/* Optional Voices Library */}
              <button
                data-testid="toggle-voice-picker"
                onClick={() => setShowVoicePicker(v => !v)}
                style={{ display:'flex', alignItems:'center', gap:'8px', width:'100%', padding:'10px 12px', borderRadius:'10px', cursor:'pointer', background: showVoicePicker ? 'rgba(159,71,255,0.15)' : 'rgba(0,0,0,0.3)', border:'1px solid rgba(159,71,255,0.35)', color:'#fff', fontSize:'0.78rem', fontWeight:700 }}
              >
                <span>🎙️ Add a Voice <span style={{ color:'rgba(255,255,255,0.45)', fontWeight:500 }}>(optional)</span></span>
                {selectedVoiceId && (
                  <span style={{ marginLeft:'auto', marginRight:'8px', color:'#c4b5fd', fontWeight:600 }}>
                    {SONG_VOICES.find(v=>v.voice_id===selectedVoiceId)?.name}
                  </span>
                )}
                <span style={{ marginLeft: selectedVoiceId ? '0' : 'auto', padding:'4px 12px', borderRadius:'999px', background: showVoicePicker ? 'rgba(239,68,68,0.25)' : 'rgba(159,71,255,0.3)', border:`1px solid ${showVoicePicker ? 'rgba(239,68,68,0.5)' : 'rgba(159,71,255,0.55)'}`, color:'#fff', fontSize:'0.7rem', fontWeight:800, letterSpacing:'0.04em' }}>{showVoicePicker ? '✕ CLOSE' : '＋ OPEN'}</span>
              </button>

              {showVoicePicker && (
                <div data-testid="voices-library" style={{ marginTop:'10px', display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px, 1fr))', gap:'10px' }}>
                  {selectedVoiceId && (
                    <button
                      data-testid="voice-clear"
                      onClick={() => setSelectedVoiceId('')}
                      style={{ gridColumn:'1 / -1', padding:'7px', borderRadius:'8px', cursor:'pointer', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', color:'#ef4444', fontSize:'0.72rem', fontWeight:700 }}
                    >
                      ✕ Clear selected voice
                    </button>
                  )}
                  {SONG_VOICES.map(v => {
                    const selected = selectedVoiceId === v.voice_id;
                    const playing = voicePreviewId === v.voice_id;
                    return (
                      <div
                        key={v.voice_id}
                        data-testid={`voice-card-${v.voice_id}`}
                        onClick={() => setSelectedVoiceId(selected ? '' : v.voice_id)}
                        style={{ background: selected ? 'linear-gradient(135deg, rgba(236,72,153,0.25), rgba(159,71,255,0.2))' : 'rgba(0,0,0,0.35)', border: selected ? '2px solid #ec4899' : '1px solid rgba(255,255,255,0.14)', borderRadius:'12px', padding:'12px', cursor:'pointer', position:'relative' }}
                      >
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'6px' }}>
                          <span style={{ color:'#fff', fontWeight:700, fontSize:'0.82rem' }}>{v.name}</span>
                          <button
                            data-testid={`voice-preview-btn-${v.voice_id}`}
                            onClick={(e) => { e.stopPropagation(); playVoicePreview(v.voice_id); }}
                            title="Preview voice"
                            style={{ width:'28px', height:'28px', flexShrink:0, borderRadius:'50%', background: playing ? '#ec4899' : 'rgba(236,72,153,0.2)', border:'1px solid rgba(236,72,153,0.5)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
                          >
                            {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                          </button>
                        </div>
                        <div style={{ color:'rgba(255,255,255,0.65)', fontSize:'0.7rem', marginTop:'4px' }}>{v.style}</div>
                        <div style={{ color:'rgba(255,255,255,0.4)', fontSize:'0.65rem', marginTop:'2px' }}>{v.genre}</div>
                        {selected && <div style={{ position:'absolute', top:'6px', left:'8px', color:'#ec4899', fontSize:'0.7rem', fontWeight:900 }}>✓</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Quick Generate Row */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
              <input
                type="text"
                value={sunoPrompt}
                onChange={(e) => {
                  setSunoPrompt(e.target.value);
                  const celebrity = checkForCelebrityNames(e.target.value);
                  setSunoCelebrityWarning(celebrity);
                }}
                placeholder="Describe your song... (e.g., Upbeat pop song about summer love)"
                style={{
                  padding: 'clamp(10px, 2vw, 14px) clamp(12px, 2vw, 16px)',
                  background: '#0a0a0f',
                  border: sunoCelebrityWarning ? '2px solid #ef4444' : '2px solid rgba(236, 72, 153, 0.3)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: 'clamp(0.8rem, 2.5vw, 0.9rem)',
                  outline: 'none',
                  width: '100%'
                }}
                data-testid="suno-quick-input"
              />
              {/* Celebrity Warning - Inline */}
              {sunoCelebrityWarning && (
                <div style={{
                  padding: '8px 12px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  borderRadius: '8px',
                  color: '#ef4444',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  ⚠️ "{sunoCelebrityWarning}" - Artist names not allowed
                </div>
              )}
              <button
                onClick={() => {
                  if (!sunoPrompt.trim()) {
                    toast.error('Enter a song description');
                    return;
                  }
                  if (sunoCelebrityWarning) {
                    toast.error(`Remove artist name "${sunoCelebrityWarning}" - describe the style instead`);
                    return;
                  }
                  generateSunoMusic();
                }}
                disabled={isGeneratingSuno || !sunoPrompt.trim() || sunoCelebrityWarning}
                style={{
                  padding: '14px 24px',
                  background: isGeneratingSuno || !sunoPrompt.trim() || sunoCelebrityWarning
                    ? 'rgba(100,100,100,0.3)'
                    : 'linear-gradient(135deg, #ec4899, #f43f5e)',
                  border: 'none',
                  borderRadius: '10px',
                  color: isGeneratingSuno || !sunoPrompt.trim() || sunoCelebrityWarning ? '#666' : '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  cursor: isGeneratingSuno || !sunoPrompt.trim() || sunoCelebrityWarning ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap'
                }}
                data-testid="suno-quick-gen-btn"
              >
                {isGeneratingSuno ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    Generate with MANNIS AI
                  </>
                )}
              </button>
            </div>

            {/* Options Row - Responsive grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(70px, 1fr))', gap: '8px', marginBottom: '12px' }}>
              <div>
                <label style={{ color: '#888', fontSize: '0.6rem', display: 'block', marginBottom: '3px' }}>STYLE</label>
                <input
                  type="text"
                  value={sunoStyle}
                  onChange={(e) => setSunoStyle(e.target.value)}
                  placeholder="Pop..."
                  style={{
                    width: '100%',
                    padding: '8px 6px',
                    background: '#0a0a0f',
                    border: '1px solid rgba(236, 72, 153, 0.2)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.75rem'
                  }}
                />
              </div>
              <div>
                <label style={{ color: '#888', fontSize: '0.6rem', display: 'block', marginBottom: '3px' }}>KEY</label>
                <select
                  value={sunoKey}
                  onChange={(e) => setSunoKey(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 4px',
                    background: '#0a0a0f',
                    border: '1px solid rgba(236, 72, 153, 0.2)',
                    borderRadius: '6px',
                    color: '#ec4899',
                    fontSize: '0.7rem',
                    cursor: 'pointer'
                  }}
                  data-testid="suno-workflow-key"
                >
                  <optgroup label="Major">
                    {['C', 'D', 'E', 'F', 'G', 'A', 'B'].map(k => <option key={`${k}M`} value={`${k} major`}>{k}M</option>)}
                  </optgroup>
                  <optgroup label="Minor">
                    {['C', 'D', 'E', 'F', 'G', 'A', 'B'].map(k => <option key={`${k}m`} value={`${k} minor`}>{k}m</option>)}
                  </optgroup>
                  <optgroup label="Sharps">
                    {['C#', 'D#', 'F#', 'G#', 'A#'].map(k => <option key={k} value={`${k} major`}>{k}</option>)}
                  </optgroup>
                  <optgroup label="Flats">
                    {['Db', 'Eb', 'Gb', 'Ab', 'Bb'].map(k => <option key={k} value={`${k} major`}>{k}</option>)}
                  </optgroup>
                </select>
              </div>
              <div>
                <label style={{ color: '#888', fontSize: '0.6rem', display: 'block', marginBottom: '3px' }}>TITLE</label>
                <input
                  type="text"
                  value={sunoTitle}
                  onChange={(e) => setSunoTitle(e.target.value)}
                  placeholder="Title..."
                  style={{
                    width: '100%',
                    padding: '8px 6px',
                    background: '#0a0a0f',
                    border: '1px solid rgba(236, 72, 153, 0.2)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.75rem'
                  }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  padding: '8px 6px',
                  background: sunoInstrumental ? 'rgba(139, 92, 246, 0.2)' : 'rgba(0,0,0,0.2)',
                  border: `1px solid ${sunoInstrumental ? 'rgba(139, 92, 246, 0.4)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  width: '100%'
                }}>
                  <input
                    type="checkbox"
                    checked={sunoInstrumental}
                    onChange={(e) => setSunoInstrumental(e.target.checked)}
                    style={{ width: '14px', height: '14px' }}
                  />
                  <span style={{ color: sunoInstrumental ? '#a78bfa' : '#888', fontSize: '0.65rem', fontWeight: 600 }}>
                    🎹
                  </span>
                </label>
              </div>
            </div>

            {/* Action Buttons Row - PREVIEW, COPY, DRUMS, MIDI, SUNO */}
            {sunoPrompt.trim() && (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(5, 1fr)', 
                gap: '8px',
                marginBottom: '12px'
              }}>
                {/* PREVIEW Button */}
                <button
                  onClick={async () => {
                    toast.info('🎧 Generating audio preview (30-90s)...');
                    try {
                      const response = await axios.post(`${API}/generate-drum-preview`, {
                        prompt: sunoPrompt,
                        genre: sunoStyle || 'pop',
                        tempo: 120,
                        key: sunoKey?.split(' ')[0] || 'C'
                      });
                      if (response.data.task_id) {
                        const taskId = response.data.task_id;
                        let attempts = 0;
                        const maxAttempts = 50;
                        const poll = setInterval(async () => {
                          attempts++;
                          try {
                            const status = await axios.get(`${API}/drum-preview-status/${taskId}`);
                            if (status.data.status === 'completed' && status.data.audio_url) {
                              clearInterval(poll);
                              // Drum audio is rehosted internally (relative /api/drum-audio/...);
                              // resolve it to an absolute backend URL before playing.
                              const resolvedUrl = status.data.audio_url.startsWith('/api')
                                ? `${API_ORIGIN}${status.data.audio_url}`
                                : status.data.audio_url;
                              const audio = new Audio();
                              audio.preload = 'metadata';
                              audio.addEventListener('canplay', () => audio.play().catch(() => {}), { once: true });
                              audio.src = resolvedUrl;
                              audio.load();
                              toast.success('🎧 Playing preview!');
                            } else if (status.data.status === 'failed' || attempts >= maxAttempts) {
                              clearInterval(poll);
                              toast.error("Preview generation failed or timed out");
                            }
                          } catch (err) {
                            console.error("Poll error:", err);
                          }
                        }, 3000);
                      }
                    } catch (err) {
                      toast.error('Preview generation failed');
                    }
                  }}
                  style={{
                    padding: '12px 8px',
                    background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  data-testid="suno-preview-btn"
                >
                  <Headphones className="w-4 h-4" />
                  PREVIEW
                </button>

                {/* COPY Button */}
                <button
                  onClick={() => {
                    const fullPrompt = `${sunoStyle ? sunoStyle + ': ' : ''}${sunoPrompt}${sunoKey ? ', Key of ' + sunoKey : ''}`;
                    universalCopy(fullPrompt, '📋 Copied to clipboard!');
                  }}
                  style={{
                    padding: '12px 8px',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  data-testid="suno-copy-btn"
                >
                  <Copy className="w-4 h-4" />
                  COPY
                </button>

                {/* DRUMS Button */}
                <button
                  onClick={async () => {
                    toast.info('🥁 Generating drum pattern...');
                    setDrumStylePrompt(sunoPrompt);
                    setDrumGenre(sunoStyle || 'pop');
                    try {
                      await generateDrumPattern();
                      toast.success('🥁 Drum pattern generated!');
                    } catch (err) {
                      toast.error('Drum generation failed');
                    }
                  }}
                  style={{
                    padding: '12px 8px',
                    background: 'linear-gradient(135deg, #ec4899, #db2777)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  data-testid="suno-drums-btn"
                >
                  <span style={{ fontSize: '1rem' }}>🥁</span>
                  DRUMS
                </button>

                {/* MIDI Button */}
                <button
                  onClick={async () => {
                    toast.info('🎹 Generating MIDI file...');
                    try {
                      const response = await axios.post(`${API}/generate-midi`, {
                        prompt: sunoPrompt,
                        genre: sunoStyle || 'pop',
                        tempo: 120,
                        key: sunoKey?.split(' ')[0] || 'C',
                        mode: sunoKey?.includes('minor') ? 'minor' : 'major'
                      });
                      if (response.data.midi_url) {
                        const midiUrl = response.data.midi_url.startsWith('/')
                          ? `${API}${response.data.midi_url.replace('/api', '')}`
                          : response.data.midi_url;
                        await shareOrDownloadUrl(midiUrl, `${sunoTitle || 'suno_pattern'}.mid`, 'audio/midi', { successMessage: '🎹 MIDI downloaded!' });
                      }
                    } catch (err) {
                      toast.error('MIDI generation failed');
                    }
                  }}
                  style={{
                    padding: '12px 8px',
                    background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  data-testid="suno-midi-btn"
                >
                  <Music className="w-4 h-4" />
                  MIDI
                </button>

                {/* SUNO AI Button */}
                <button
                  onClick={() => {
                    if (!sunoPrompt.trim()) {
                      toast.error('Enter a description first');
                      return;
                    }
                    generateSunoMusic();
                  }}
                  disabled={isGeneratingSuno}
                  style={{
                    padding: '12px 8px',
                    background: isGeneratingSuno 
                      ? 'rgba(100,100,100,0.3)' 
                      : 'linear-gradient(135deg, #ec4899, #f43f5e)',
                    border: 'none',
                    borderRadius: '8px',
                    color: isGeneratingSuno ? '#666' : '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    cursor: isGeneratingSuno ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    boxShadow: isGeneratingSuno ? 'none' : '0 4px 15px rgba(236, 72, 153, 0.3)'
                  }}
                  data-testid="suno-generate-action-btn"
                >
                  <Zap className="w-4 h-4" />
                  {isGeneratingSuno ? 'WAIT...' : 'SUNO'}
                </button>
              </div>
            )}

            {/* Status & Progress */}
            {isGeneratingSuno && sunoStatus && (
              <div style={{
                padding: '12px 16px',
                background: 'rgba(236, 72, 153, 0.1)',
                border: '1px solid rgba(236, 72, 153, 0.2)',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#ec4899' }} />
                  <span style={{ color: '#ec4899', fontSize: '0.85rem' }}>{sunoStatus.message}</span>
                </div>
                {sunoStatus.progress && (
                  <div style={{ height: '4px', background: 'rgba(0,0,0,0.3)', borderRadius: '2px' }}>
                    <div style={{ 
                      height: '100%', 
                      width: `${sunoStatus.progress}%`, 
                      background: 'linear-gradient(90deg, #ec4899, #8b5cf6)',
                      borderRadius: '2px',
                      transition: 'width 0.3s'
                    }} />
                  </div>
                )}
              </div>
            )}

            {/* Recent Tracks Preview */}
            {sunoTracks.length > 0 && (
              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                overflowX: 'auto',
                paddingBottom: '8px'
              }}>
                {sunoTracks.slice(0, 4).map((track, idx) => {
                  const hasAudio = track.audio_url && track.status === 'completed';
                  const isPending = track.status === 'pending' || !track.audio_url;
                  
                  return (
                  <div 
                    key={track.task_id || idx}
                    style={{
                      minWidth: '200px',
                      padding: '12px',
                      background: currentSunoTrack?.task_id === track.task_id 
                        ? 'rgba(236, 72, 153, 0.2)' 
                        : isPending ? 'rgba(100, 100, 100, 0.2)' : 'rgba(0,0,0,0.3)',
                      border: `1px solid ${currentSunoTrack?.task_id === track.task_id ? '#ec4899' : isPending ? 'rgba(239, 68, 68, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                      borderRadius: '8px',
                      position: 'relative',
                      opacity: isPending ? 0.7 : 1
                    }}
                  >
                    <div style={{ cursor: hasAudio ? 'pointer' : 'default' }}>
                      <div style={{ 
                        color: isPending ? '#ef4444' : '#ec4899', 
                        fontSize: '0.8rem', 
                        fontWeight: 600, 
                        marginBottom: '4px', 
                        paddingRight: '30px' 
                      }}>
                        {track.title || 'Untitled'}
                        {isPending && <span style={{ marginLeft: '6px', fontSize: '0.6rem', color: '#ef4444' }}>⚠️ FAILED</span>}
                      </div>
                      <div style={{ color: '#666', fontSize: '0.7rem', marginBottom: '8px' }}>
                        {isPending ? 'Generation failed - delete this' : (track.track_label || track.tags?.slice(0, 30) || 'Ready to play')}
                      </div>
                    </div>
                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (hasAudio) {
                            playSunoTrack(track);
                          } else {
                            toast.error('No audio available - generation failed');
                          }
                        }}
                        disabled={!hasAudio}
                        style={{
                          flex: 1,
                          padding: '6px',
                          background: hasAudio ? 'rgba(139, 92, 246, 0.2)' : 'rgba(100,100,100,0.2)',
                          border: `1px solid ${hasAudio ? 'rgba(139, 92, 246, 0.3)' : 'rgba(100,100,100,0.3)'}`,
                          borderRadius: '4px',
                          color: hasAudio ? '#a78bfa' : '#666',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          cursor: hasAudio ? 'pointer' : 'not-allowed',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px'
                        }}
                      >
                        <Play className="w-3 h-3" /> {hasAudio ? 'Play' : 'N/A'}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (hasAudio) {
                            downloadSunoTrack(track);
                          } else {
                            toast.error('No audio to download');
                          }
                        }}
                        disabled={!hasAudio}
                        style={{
                          padding: '6px 8px',
                          background: hasAudio ? 'rgba(34, 197, 94, 0.2)' : 'rgba(100,100,100,0.2)',
                          border: `1px solid ${hasAudio ? 'rgba(34, 197, 94, 0.3)' : 'rgba(100,100,100,0.3)'}`,
                          borderRadius: '4px',
                          color: hasAudio ? '#22c55e' : '#666',
                          fontSize: '0.65rem',
                          cursor: hasAudio ? 'pointer' : 'not-allowed'
                        }}
                      >
                        <Download className="w-3 h-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSunoTrack(track);
                        }}
                        data-testid="suno-track-delete-btn"
                        style={{
                          padding: '6px 8px',
                          background: 'rgba(239, 68, 68, 0.2)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          borderRadius: '4px',
                          color: '#ef4444',
                          fontSize: '0.65rem',
                          cursor: 'pointer'
                        }}
                        title="Delete track"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  );
                })}
                {sunoTracks.length > 4 && (
                  <button
                    onClick={() => setFullscreenTool('suno-music')}
                    style={{
                      minWidth: '100px',
                      padding: '12px',
                      background: 'rgba(139, 92, 246, 0.2)',
                      border: '1px solid rgba(139, 92, 246, 0.3)',
                      borderRadius: '8px',
                      color: '#a78bfa',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    +{sunoTracks.length - 4} more
                  </button>
                )}
              </div>
            )}

            {/* Current Track Player */}
            {currentSunoTrack && fullscreenTool !== 'suno-music' && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: 'rgba(0,0,0,0.4)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{ color: '#ec4899', fontSize: '0.85rem', fontWeight: 600, minWidth: '120px' }}>
                  🎵 {currentSunoTrack.title?.slice(0, 20)}{currentSunoTrack.title?.length > 20 ? '...' : ''}
                </div>
                <audio preload="metadata"
                  ref={sunoAudioRef}
                  src={currentSunoTrack.audio_url?.startsWith('/') 
                    ? `${API}${currentSunoTrack.audio_url.replace('/api', '')}` 
                    : currentSunoTrack.audio_url}
                  controls
                  autoPlay
                  style={{ flex: 1, height: '32px' }}
                  onPlay={() => setIsSunoPlaying(true)}
                  onPause={() => setIsSunoPlaying(false)}
                />
                <button
                  onClick={() => downloadSunoTrack(currentSunoTrack)}
                  onTouchEnd={(e) => { e.preventDefault(); downloadSunoTrack(currentSunoTrack); }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(34, 197, 94, 0.2)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    borderRadius: '6px',
                    color: '#22c55e',
                    fontSize: '0.75rem',
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation'
                  }}
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </button>
                {/* STOP/CLOSE Button */}
                <button
                  onClick={() => {
                    setCurrentSunoTrack(null);
                    setIsSunoPlaying(false);
                    toast.info('🔇 Player closed');
                  }}
                  style={{
                    padding: '8px 12px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '6px',
                    color: '#ef4444',
                    fontSize: '0.75rem',
                    cursor: 'pointer'
                  }}
                  title="Stop & Close"
                  data-testid="suno-stop-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
            )}

            {songCreationTab === 'vocals' && (
            <div style={{ paddingTop: '4px' }} data-testid="singing-vocal-top">
            
            {/* Header */}
            <div className="singing-header" style={{ marginBottom: '16px' }}>
              <div className="singing-title" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Mic className="w-4 h-4" style={{ color: '#fff' }} />
                </div>
                <span style={{ color: '#ec4899', fontWeight: 700, fontSize: '0.85rem' }}>SINGING VOCAL GENERATOR</span>
                <button
                  onClick={() => setFullscreenTool('singing')}
                  style={{
                    padding: '6px 14px',
                    background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    marginLeft: 'auto'
                  }}
                  data-testid="singing-fullscreen-btn"
                >
                  <Maximize2 className="w-3 h-3" />
                  FULL SCREEN
                </button>
                <span className="singing-badge yue-badge" style={{ 
                  background: ['Chinese', 'Japanese', 'Korean'].includes(singingLanguage) 
                    ? 'rgba(16, 185, 129, 0.2)' 
                    : singingLanguage === 'English'
                    ? 'rgba(0, 212, 255, 0.2)'
                    : 'rgba(249, 115, 22, 0.2)', 
                  color: ['Chinese', 'Japanese', 'Korean'].includes(singingLanguage)
                    ? '#10b981'
                    : singingLanguage === 'English'
                    ? '#00d4ff'
                    : '#f97316'
                }}>{['Chinese', 'Japanese', 'Korean'].includes(singingLanguage) ? 'YuE MULTILINGUAL' : (singingLanguage === 'English' ? 'MINIMAX AI' : 'AUTO-TRANSLATE')}</span>
              </div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                {singingStyle} • {singingGender} • {singingKey} • {singingTempo}
              </span>
            </div>

            {/* Vocal Controls Grid */}
            <div className="vocal-controls-grid" style={{ marginBottom: '12px' }}>
              <div>
                <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>VOCAL STYLE</label>
                <select
                  value={singingStyle}
                  onChange={(e) => setSingingStyle(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.75rem'
                  }}
                  data-testid="vocal-style-select-top"
                >
                  <option value="pop ballad">Pop Ballad</option>
                  <option value="R&B">R&B</option>
                  <option value="Trap">Trap</option>
                  <option value="Hip Hop">Hip Hop</option>
                  <option value="Soul">Soul</option>
                  <option value="Rock">Rock</option>
                  <option value="Country">Country</option>
                  <option value="Afrobeats">Afrobeats</option>
                  <option value="Reggae">Reggae</option>
                  <option value="Gospel">Gospel</option>
                </select>
              </div>
              <div>
                <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>GENDER</label>
                <select
                  value={singingGender}
                  onChange={(e) => setSingingGender(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.75rem'
                  }}
                  data-testid="vocal-gender-select-top"
                >
                  <option value="female">Female</option>
                  <option value="male">Male</option>
                </select>
              </div>
              <div>
                <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>LANGUAGE</label>
                <select
                  value={singingLanguage}
                  onChange={(e) => setSingingLanguage(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.75rem'
                  }}
                  data-testid="vocal-language-select"
                >
                  <option value="English">English</option>
                  <option value="Spanish">Spanish (Español)</option>
                  <option value="French">French (Français)</option>
                  <option value="Portuguese">Portuguese (Português)</option>
                  <option value="German">German (Deutsch)</option>
                  <option value="Italian">Italian (Italiano)</option>
                  <option value="Japanese">Japanese (日本語)</option>
                  <option value="Korean">Korean (한국어)</option>
                  <option value="Chinese">Chinese (中文)</option>
                  <option value="Arabic">Arabic (العربية)</option>
                  <option value="Hindi">Hindi (हिन्दी)</option>
                  <option value="Punjabi">Punjabi (ਪੰਜਾਬੀ)</option>
                  <option value="Swahili">Swahili</option>
                  <option value="Yoruba">Yoruba</option>
                  <option value="Pidgin">Nigerian Pidgin</option>
                </select>
              </div>
              <div>
                <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>TEMPO</label>
                <select
                  value={singingTempo}
                  onChange={(e) => setSingingTempo(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.75rem'
                  }}
                  data-testid="vocal-tempo-select-top"
                >
                  <option value="slow">Slow (Ballad)</option>
                  <option value="medium">Medium</option>
                  <option value="fast">Fast (Upbeat)</option>
                </select>
              </div>
              <div>
                <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>MUSICAL KEY</label>
                <select
                  value={singingKey}
                  onChange={(e) => setSingingKey(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(236, 72, 153, 0.3)',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.75rem'
                  }}
                  data-testid="vocal-key-select"
                >
                  <optgroup label="Natural">
                    {['C', 'D', 'E', 'F', 'G', 'A', 'B'].map(k => <option key={k} value={k}>{k}</option>)}
                  </optgroup>
                  <optgroup label="Sharps">
                    {['C#', 'D#', 'F#', 'G#', 'A#'].map(k => <option key={k} value={k}>{k}</option>)}
                  </optgroup>
                  <optgroup label="Flats">
                    {['Db', 'Eb', 'Gb', 'Ab', 'Bb'].map(k => <option key={k} value={k}>{k}</option>)}
                  </optgroup>
                </select>
              </div>
            </div>

            {/* Drag & Drop Zone for Lyrics Files */}
            <div
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = '#ec4899';
                e.currentTarget.style.background = 'rgba(236, 72, 153, 0.15)';
              }}
              onDragLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(236, 72, 153, 0.3)';
                e.currentTarget.style.background = 'rgba(0,0,0,0.3)';
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = 'rgba(236, 72, 153, 0.3)';
                e.currentTarget.style.background = 'rgba(0,0,0,0.3)';
                
                const file = e.dataTransfer.files[0];
                if (file) {
                  if (file.type === 'text/plain' || file.name.endsWith('.txt') || file.name.endsWith('.lrc')) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      const content = event.target.result.slice(0, 3500);
                      setSingingLyrics(content);
                      toast.success(`🎤 Loaded lyrics from "${file.name}" (${content.length} chars)`);
                    };
                    reader.readAsText(file);
                  } else {
                    toast.error('Please drop a .txt or .lrc lyrics file');
                  }
                }
              }}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.txt,.lrc';
                input.onchange = (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      const content = event.target.result.slice(0, 3500);
                      setSingingLyrics(content);
                      toast.success(`🎤 Loaded lyrics from "${file.name}" (${content.length} chars)`);
                    };
                    reader.readAsText(file);
                  }
                };
                input.click();
              }}
              style={{
                marginBottom: '12px',
                padding: '16px',
                background: 'rgba(0,0,0,0.3)',
                border: '2px dashed rgba(236, 72, 153, 0.3)',
                borderRadius: '10px',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              data-testid="vocal-lyrics-dropzone"
            >
              <Upload className="w-6 h-6 mx-auto mb-2" style={{ color: '#ec4899', opacity: 0.7 }} />
              <div style={{ color: '#ec4899', fontWeight: 600, fontSize: '0.8rem', marginBottom: '2px' }}>
                Drop Lyrics File Here
              </div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>
                or click to browse • .txt, .lrc supported
              </div>
            </div>

            {/* Lyrics Input */}
            <div style={{ marginBottom: '12px' }}>
              <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', marginBottom: '4px', display: 'block' }}>
                LYRICS TO SING (max 3500 characters) • {singingLyrics.length}/3500
              </label>
              <textarea
                value={singingLyrics}
                onChange={(e) => setSingingLyrics(e.target.value.slice(0, 3500))}
                placeholder="Enter your lyrics here (max 3500 characters)... Example:
Walking through the city lights
Searching for you every night
My heart beats to this melody
You're the only one for me"
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '10px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(236, 72, 153, 0.3)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.85rem',
                  resize: 'vertical',
                  lineHeight: 1.5
                }}
                data-testid="vocal-lyrics-input-top"
              />
              
              {/* Translate Button - shows when language is not English */}
              {singingLanguage !== 'English' && singingLyrics.trim() && (
                <button
                  onClick={translateLyrics}
                  disabled={isTranslating}
                  style={{
                    marginTop: '8px',
                    padding: '8px 16px',
                    background: isTranslating ? 'rgba(99, 102, 241, 0.5)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: isTranslating ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  data-testid="translate-lyrics-btn"
                >
                  {isTranslating ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Translating...</>
                  ) : (
                    <><Globe className="w-4 h-4" /> Translate to {singingLanguage}</>
                  )}
                </button>
              )}
              
              {/* Clear Lyrics Button */}
              {singingLyrics.trim() && (
                <button
                  onClick={() => {
                    setSingingLyrics('');
                    toast.success('Lyrics cleared!');
                  }}
                  style={{
                    marginTop: '8px',
                    marginLeft: singingLanguage !== 'English' ? '8px' : '0',
                    padding: '8px 16px',
                    background: 'rgba(239, 68, 68, 0.2)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '6px',
                    color: '#ef4444',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  data-testid="clear-singing-lyrics-btn"
                >
                  <Trash2 className="w-4 h-4" /> Clear Lyrics
                </button>
              )}
            </div>

            {/* Generate Button */}
            <button
              onClick={generateSingingVocals}
              disabled={singingStatus === 'generating' || !singingLyrics.trim()}
              style={{
                width: '100%',
                padding: '12px 20px',
                background: singingStatus === 'generating' ? 'rgba(236, 72, 153, 0.5)' : 'linear-gradient(135deg, #ec4899, #a855f7)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '0.85rem',
                fontWeight: 700,
                cursor: singingStatus === 'generating' ? 'wait' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: !singingLyrics.trim() ? 0.5 : 1,
                boxShadow: '0 4px 15px rgba(236, 72, 153, 0.3)'
              }}
              data-testid="generate-vocals-top-btn"
            >
              {singingStatus === 'generating' ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Generating Vocals... {singingProgress}%</>
              ) : (
                <><Sparkles className="w-4 h-4" /> GENERATE SINGING VOCALS</>
              )}
            </button>

            {/* Vocal Generation Status */}
            {singingStatus === 'generating' && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: 'rgba(0,0,0,0.3)',
                borderRadius: '8px',
                border: '1px solid rgba(236, 72, 153, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#ec4899' }} />
                  <span style={{ color: '#ec4899', fontWeight: 600, fontSize: '0.8rem' }}>
                    Processing... {singingProgress}%
                  </span>
                </div>
                <div style={{
                  height: '4px',
                  background: 'rgba(236, 72, 153, 0.2)',
                  borderRadius: '2px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    width: `${singingProgress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #ec4899, #a855f7)',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </div>
            )}

            {/* Download Section */}
            {singingAudioUrl && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                background: 'rgba(16, 185, 129, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.8rem' }}>
                    ✓ Vocals Ready!
                  </span>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <audio preload="metadata" controls src={singingAudioUrl.startsWith('/api') ? `${API.replace('/api', '')}${singingAudioUrl}` : singingAudioUrl} style={{ height: '32px' }} />
                    <button
                      onClick={async () => {
                        try {
                          const filename = `sound_dna_vocals_${Date.now()}.wav`;
                          // Get the full URL
                          let audioUrl = singingAudioUrl;
                          if (audioUrl.startsWith('/api')) {
                            audioUrl = `${API.replace('/api', '')}${audioUrl}`;
                          }
                          
                          toast.info('Preparing download...');
                          
                          // Detect iOS for proxy download
                          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                                        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                          
                          if (isIOS) {
                            // iOS: Use server proxy for reliable download
                            await downloadFileWithPicker(null, filename, 'audio/wav', '✅ Vocals saved!', audioUrl);
                          } else {
                            // Desktop: Fetch blob and use file picker
                            const response = await fetch(audioUrl);
                            if (!response.ok) throw new Error('Failed to fetch audio');
                            const blob = await response.blob();
                            await downloadFileWithPicker(blob, filename, 'audio/wav', '✅ Vocals saved!', audioUrl);
                          }
                        } catch (error) {
                          console.error('Download error:', error);
                          // Fallback: open in new tab
                          const audioUrl = singingAudioUrl.startsWith('/api') 
                            ? `${API.replace('/api', '')}${singingAudioUrl}` 
                            : singingAudioUrl;
                          window.open(audioUrl, '_blank');
                          toast.info('Opening audio in new tab - right-click to save');
                        }
                      }}
                      style={{
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#fff',
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                      }}
                    >
                      <Download className="w-4 h-4" /> Download WAV
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
            )}
          </div>

        {/* MY BEAT LIBRARY - Cloud Saved Beats */}
        <div style={{
          background: 'rgba(30,30,36,0.8)',
          border: '1px solid rgba(159,71,255,0.2)',
          borderRadius: '12px',
          padding: '16px',
          marginTop: '20px'
        }}>
          {/* Header */}
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <div 
              onClick={() => {
                setBeatLibraryOpen(!beatLibraryOpen);
                if (!beatLibraryOpen && userEmail) {
                  fetchBeatLibrary();
                }
              }}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}
            >
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: '#9F47FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Library className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 style={{ margin: 0, color: '#fff', fontSize: '1rem', fontWeight: 600 }}>
                  MY BEAT LIBRARY 🎵
                </h3>
                <p style={{ margin: '2px 0 0', color: '#71717A', fontSize: '0.7rem' }}>
                  {beatLibrary.length} saved songs • All your generated music is here
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {/* Clear Beat Library Button */}
              {beatLibrary.length > 0 && beatLibraryOpen && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm('Clear all beats from your library?')) {
                      setBeatLibrary([]);
                      toast.success('🧹 Beat library cleared!');
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    background: 'rgba(239, 68, 68, 0.15)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '6px',
                    color: '#ef4444',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                  data-testid="clear-beat-library-btn"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear All
                </button>
              )}
              <ChevronDown 
                className="w-5 h-5" 
                onClick={() => {
                  setBeatLibraryOpen(!beatLibraryOpen);
                  if (!beatLibraryOpen && userEmail) {
                    fetchBeatLibrary();
                  }
                }}
                style={{ 
                  color: '#9F47FF',
                  transform: beatLibraryOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  cursor: 'pointer'
                }} 
              />
            </div>
          </div>
          
          {/* Library Content */}
          {beatLibraryOpen && (
            <div style={{ marginTop: '16px' }}>
              {!userEmail ? (
                <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '20px' }}>
                  Sign in to access your beat library
                </p>
              ) : beatLibraryLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#9F47FF', margin: '0 auto' }} />
                  <p style={{ color: 'rgba(255,255,255,0.5)', marginTop: '8px' }}>Loading your beats...</p>
                </div>
              ) : beatLibrary.length === 0 ? (
                <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: '20px' }}>
                  No saved beats yet. Generate a beat to add it to your library!
                </p>
              ) : (
                <>
                  {/* Migrate Beats to Permanent Storage Button */}
                  {beatLibrary.some(beat => {
                    const isPermanent = beat.permanent || (beat.audio_url || '').startsWith('/api/beat-audio/');
                    return !isPermanent;
                  }) && (
                    <button
                      onClick={async () => {
                        const nonPermanentCount = beatLibrary.filter(beat => {
                          const isPermanent = beat.permanent || (beat.audio_url || '').startsWith('/api/beat-audio/');
                          return !isPermanent;
                        }).length;
                        
                        if (window.confirm(`Attempt to save ${nonPermanentCount} beats to permanent storage? Only beats with active URLs can be migrated.`)) {
                          toast.loading('Migrating beats to permanent storage...');
                          try {
                            const response = await fetch(`${API}/beat-library/migrate/${encodeURIComponent(userEmail)}`, {
                              method: 'POST'
                            });
                            const result = await response.json();
                            toast.dismiss();
                            
                            if (result.success) {
                              toast.success(`Migration complete: ${result.migrated} saved, ${result.already_expired} expired`);
                              // Refresh library
                              const libResponse = await fetch(`${API}/beat-library/${encodeURIComponent(userEmail)}`);
                              const libData = await libResponse.json();
                              setBeatLibrary(libData.beats || []);
                            } else {
                              toast.error('Migration failed');
                            }
                          } catch (err) {
                            toast.dismiss();
                            toast.error('Migration failed: ' + err.message);
                          }
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        marginBottom: '12px',
                        background: 'rgba(159,71,255,0.15)',
                        border: '2px solid rgba(159,71,255,0.4)',
                        borderRadius: '10px',
                        color: '#9F47FF',
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      data-testid="migrate-beats-btn"
                    >
                      <Save className="w-4 h-4" />
                      SAVE BEATS TO PERMANENT STORAGE
                    </button>
                  )}

                  {/* Delete All Expired Button */}
                  {beatLibrary.some(beat => {
                    const isPermanent = beat.permanent || (beat.audio_url || '').startsWith('/api/beat-audio/');
                    const createdAt = new Date(beat.completed_at || beat.created_at);
                    return !isPermanent && (new Date() - createdAt) / (1000 * 60 * 60) > 48;
                  }) && (
                    <button
                      onClick={async () => {
                        if (window.confirm('Delete all expired beats? This cannot be undone.')) {
                          const expiredBeats = beatLibrary.filter(beat => {
                            const isPermanent = beat.permanent || (beat.audio_url || '').startsWith('/api/beat-audio/');
                            const createdAt = new Date(beat.completed_at || beat.created_at);
                            return !isPermanent && (new Date() - createdAt) / (1000 * 60 * 60) > 48;
                          });
                          
                          for (const beat of expiredBeats) {
                            await deleteBeatFromLibrary(beat.task_id);
                          }
                          
                          toast.success(`Deleted ${expiredBeats.length} expired beats`);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        marginBottom: '12px',
                        background: 'rgba(239,68,68,0.15)',
                        border: '2px solid rgba(239,68,68,0.4)',
                        borderRadius: '10px',
                        color: '#ef4444',
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                      data-testid="delete-all-expired-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                      DELETE ALL EXPIRED BEATS
                    </button>
                  )}
                  
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflowY: 'auto' }}>
                  {/* Hidden audio element for library playback - always present */}
                  <audio preload="metadata" 
                    ref={libraryAudioRef} 
                    onEnded={() => {
                      setPlayingLibraryBeat(null);
                      setLibraryBeatUrl(null);
                      setLibraryProgress(0);
                      setLibraryCurrentTime(0);
                    }}
                    onError={(e) => {
                      console.error('Audio load error:', e);
                      setPlayingLibraryBeat(null);
                      setLibraryBeatUrl(null);
                      setLibraryProgress(0);
                      toast.error('Audio expired or unavailable. Try downloading instead.');
                    }}
                    onTimeUpdate={(e) => {
                      const current = e.target.currentTime;
                      const duration = e.target.duration || 1;
                      setLibraryCurrentTime(current);
                      setLibraryProgress((current / duration) * 100);
                    }}
                    onLoadedMetadata={(e) => {
                      setLibraryDuration(e.target.duration || 0);
                    }}
                    playsInline
                    webkit-playsinline="true"
                    style={{ display: 'none' }}
                  />
                  
                  {beatLibrary
                    .filter(beat => beat.audio_url || beat.audio_url_a || beat.audio_url_b)
                    .map((beat) => {
                    let beatAudioUrl = beat.audio_url || beat.audio_url_a || beat.audio_url_b;
                    
                    // If it's a permanent URL (relative path), prepend the API base
                    if (beatAudioUrl && beatAudioUrl.startsWith('/api/')) {
                      beatAudioUrl = `${API_ORIGIN}${beatAudioUrl}`;
                    }
                    
                    const isPlaying = playingLibraryBeat === beat.task_id;
                    
                    // Check if beat is stored permanently (our server) vs Replicate (expires)
                    const isPermanent = beat.permanent || (beat.audio_url || '').startsWith('/api/beat-audio/');
                    
                    // Only check expiration for non-permanent beats
                    const createdAt = new Date(beat.completed_at || beat.created_at);
                    const now = new Date();
                    const hoursAgo = (now - createdAt) / (1000 * 60 * 60);
                    const isExpired = !isPermanent && hoursAgo > 48;
                    
                    return (
                    <div 
                      key={beat.task_id}
                      style={{
                        background: isExpired ? 'rgba(239,68,68,0.1)' : isPermanent ? 'rgba(159,71,255,0.05)' : 'rgba(0,0,0,0.3)',
                        borderRadius: '12px',
                        padding: '14px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        border: isPlaying ? '1px solid #9F47FF' : isExpired ? '1px solid rgba(239,68,68,0.3)' : isPermanent ? '1px solid rgba(159,71,255,0.2)' : '1px solid transparent',
                        opacity: isExpired ? 0.6 : 1
                      }}
                    >
                      {/* Status badge */}
                      {isPermanent ? (
                        <div style={{
                          background: 'rgba(159,71,255,0.2)',
                          color: '#9F47FF',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          alignSelf: 'flex-start'
                        }}>
                          ✓ Saved permanently
                        </div>
                      ) : isExpired && (
                        <div style={{
                          background: 'rgba(239,68,68,0.2)',
                          color: '#ef4444',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          alignSelf: 'flex-start'
                        }}>
                          ⚠️ Audio expired - Generate new beat
                        </div>
                      )}
                      
                      {/* Top row with controls */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {/* Play Button */}
                      <button
                        onClick={() => toggleLibraryBeat({...beat, audio_url: beatAudioUrl})}
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: '50%',
                          background: playingLibraryBeat === beat.task_id 
                            ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                            : '#9F47FF, #6366f1)',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                        data-testid={`library-play-${beat.task_id}`}
                      >
                        {playingLibraryBeat === beat.task_id ? (
                          <Pause className="w-5 h-5 text-white" fill="white" />
                        ) : (
                          <Play className="w-5 h-5 text-white" fill="white" style={{ marginLeft: '2px' }} />
                        )}
                      </button>
                      
                      {/* Beat Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ 
                          margin: 0, 
                          color: '#fff', 
                          fontSize: '0.9rem',
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {beat.prompt?.slice(0, 50)}{beat.prompt?.length > 50 ? '...' : ''}
                        </p>
                        <p style={{ margin: '4px 0 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                          {beat.model || 'MiniMax'} • {new Date(beat.completed_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => saveWavFromUrl(beatAudioUrl, `sounddna-beat-${beat.task_id.slice(0, 8)}.wav`)}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            background: 'rgba(159,71,255,0.2)',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          data-testid={`library-download-${beat.task_id}`}
                        >
                          <Download className="w-4 h-4" style={{ color: '#9F47FF' }} />
                        </button>
                        <button
                          onClick={() => {
                            if (window.confirm('Delete this beat from your library?')) {
                              deleteBeatFromLibrary(beat.task_id);
                            }
                          }}
                          style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            background: 'rgba(239,68,68,0.2)',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          data-testid={`library-delete-${beat.task_id}`}
                        >
                          <Trash2 className="w-4 h-4" style={{ color: '#ef4444' }} />
                        </button>
                      </div>
                      </div>
                      
                      {/* Progress bar - only show when this beat is playing */}
                      {isPlaying && (
                        <div style={{ width: '100%' }}>
                          {/* Progress bar container */}
                          <div 
                            style={{
                              width: '100%',
                              height: '6px',
                              background: 'rgba(255,255,255,0.1)',
                              borderRadius: '3px',
                              overflow: 'hidden',
                              cursor: 'pointer'
                            }}
                            onClick={(e) => {
                              if (libraryAudioRef.current && libraryDuration) {
                                const rect = e.currentTarget.getBoundingClientRect();
                                const clickX = e.clientX - rect.left;
                                const newTime = (clickX / rect.width) * libraryDuration;
                                libraryAudioRef.current.currentTime = newTime;
                              }
                            }}
                          >
                            <div 
                              style={{
                                width: `${libraryProgress}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #9F47FF, #a78bfa)',
                                borderRadius: '3px',
                                transition: 'width 0.1s linear'
                              }}
                            />
                          </div>
                          {/* Time display */}
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            marginTop: '4px',
                            fontSize: '0.7rem',
                            color: 'rgba(255,255,255,0.5)'
                          }}>
                            <span>{formatTime(libraryCurrentTime)}</span>
                            <span>{formatTime(libraryDuration)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                  })}
                </div>
                </>
              )}
            </div>
          )}
        </div>

          {/* WORLD VOICE DNA - Global Voices - Collapsible Panel */}
          {showWorldVoiceDNA && (
          <div 
            data-testid="world-voice-dna-panel"
            style={{
            marginTop: '16px',
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(168, 85, 247, 0.1))',
            border: '2px solid rgba(6, 182, 212, 0.3)',
            borderRadius: '16px',
            padding: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Globe className="w-5 h-5" style={{ color: '#06b6d4' }} />
                <div>
                  <span style={{ color: '#06b6d4', fontWeight: 700, fontSize: '0.9rem' }}>WORLD VOICE DNA</span>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', margin: 0 }}>Generate voice samples → Upload to Suno Voices</p>
                </div>
              </div>
              {/* Clear World Voice Button */}
              <button
                onClick={() => {
                  setWorldVoiceRegion('pop');
                  setWorldVoiceAudio(null);
                  setWorldVoiceWav(null);
                  setShowWorldVoiceDNA(false);
                  toast.success('🧹 World Voice DNA cleared!');
                }}
                style={{
                  padding: '6px 12px',
                  background: 'rgba(239, 68, 68, 0.15)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '6px',
                  color: '#ef4444',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
                data-testid="clear-world-voice-btn"
              >
                <Trash2 className="w-3 h-3" />
                Clear
              </button>
            </div>
            
            {/* Instructions Banner */}
            <div style={{
              background: 'rgba(245, 158, 11, 0.15)',
              border: '1px solid rgba(245, 158, 11, 0.4)',
              borderRadius: '8px',
              padding: '10px 12px',
              marginBottom: '12px'
            }}>
              <div style={{ marginBottom: '6px' }}>
                <span style={{ fontSize: '1rem', marginRight: '6px' }}>⚡</span>
                <span style={{ color: '#10b981', fontSize: '0.75rem', fontWeight: 700 }}>INSTANT: Copy Voice Prompt</span>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', marginLeft: '8px' }}>
                  Click "📋 Suno" → Paste in Suno's voice description
                </span>
              </div>
              <div style={{ marginBottom: '6px' }}>
                <span style={{ fontSize: '1rem', marginRight: '6px' }}>📥</span>
                <span style={{ color: '#00d4ff', fontSize: '0.75rem', fontWeight: 700 }}>QUICK: Download Sample WAV</span>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', marginLeft: '8px' }}>
                  Click "📥 WAV" → Instant download from cache
                </span>
              </div>
              <div>
                <span style={{ fontSize: '1rem', marginRight: '6px' }}>🎧</span>
                <span style={{ color: '#f59e0b', fontSize: '0.75rem', fontWeight: 700 }}>CUSTOM: Generate New Sample</span>
                <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', marginLeft: '8px' }}>
                  Click 🎧 WAV → Wait ~60s → Custom voice sample
                </span>
              </div>
            </div>
            
            {/* Region Tabs */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
              {[
                { id: 'pop', emoji: '🎤', name: 'Pop' },
                { id: 'rnb', emoji: '💜', name: 'R&B' },
                { id: 'rock', emoji: '🎸', name: 'Rock' },
                { id: 'hiphop', emoji: '🎤', name: 'Hip-Hop' },
                { id: 'country', emoji: '🤠', name: 'Country' },
                { id: 'gospel', emoji: '🙏', name: 'Gospel' },
                { id: 'jazz', emoji: '🎷', name: 'Jazz' },
                { id: 'electronic', emoji: '🎹', name: 'EDM' },
                { id: 'indie', emoji: '🌙', name: 'Indie' },
                { id: 'musical', emoji: '🎭', name: 'Musical' },
                { id: 'atl', emoji: '🔥', name: 'ATL Trap' },
                { id: 'nyc', emoji: '🗽', name: 'NYC' },
                { id: 'divas', emoji: '👑', name: 'Divas' },
                { id: 'africa', emoji: '🌍', name: 'Africa' },
                { id: 'latin', emoji: '🌎', name: 'Latin' },
                { id: 'asia', emoji: '🌏', name: 'Asia' },
                { id: 'caribbean', emoji: '🇯🇲', name: 'Caribbean' },
                { id: 'europe', emoji: '🇪🇺', name: 'Europe' },
                { id: 'afrobeats', emoji: '🥁', name: 'Afrobeats' },
                { id: 'kpop', emoji: '💫', name: 'K-Pop' },
                { id: 'reggae', emoji: '🇯🇲', name: 'Reggae' },
                { id: 'trap', emoji: '💎', name: 'Trap' },
              ].map(region => (
                <button
                  key={region.id}
                  onClick={() => {
                    setWorldVoiceRegion(region.id);
                    if (!worldVoiceData) loadWorldVoiceData();
                  }}
                  style={{
                    padding: '5px 8px',
                    background: worldVoiceRegion === region.id 
                      ? 'linear-gradient(135deg, #06b6d4, #a855f7)' 
                      : 'rgba(6, 182, 212, 0.1)',
                    border: worldVoiceRegion === region.id 
                      ? 'none' 
                      : '1px solid rgba(6, 182, 212, 0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.7rem',
                    fontWeight: worldVoiceRegion === region.id ? 700 : 500,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '3px'
                  }}
                >
                  <span>{region.emoji}</span>
                  <span>{region.name}</span>
                </button>
              ))}
            </div>
            
            {/* Voice Cards - Scrollable */}
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {worldVoiceData && worldVoiceData[worldVoiceRegion] ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {/* Global WAV Download Banner - shows when any voice sample is ready */}
                  {worldVoiceWav && (
                    <div style={{
                      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(239, 68, 68, 0.2))',
                      border: '2px solid #f59e0b',
                      borderRadius: '10px',
                      padding: '12px',
                      marginBottom: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        <div>
                          <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: '0.8rem' }}>
                            ✅ Voice Sample Ready!
                          </div>
                          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.65rem' }}>
                            {lastGeneratedVoice?.name || 'Voice'} - Play below, then download for Suno
                          </div>
                        </div>
                        <button
                          onClick={() => downloadWorldVoiceWav(lastGeneratedVoice?.name || 'voice_sample')}
                          style={{
                            padding: '10px 20px',
                            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#fff',
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                          data-testid="download-wav-main"
                        >
                          <Download className="w-4 h-4" /> Download WAV
                        </button>
                        {/* Direct link fallback */}
                        <a
                          href={(() => {
                            const audioSrc = worldVoiceWav;
                            if (audioSrc && audioSrc.startsWith('/api/')) {
                              return `${API}${audioSrc.replace('/api', '')}`;
                            }
                            return audioSrc;
                          })()}
                          download={`${lastGeneratedVoice?.name || 'voice_sample'}_for_suno.wav`}
                          style={{
                            padding: '6px 12px',
                            background: 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '6px',
                            color: 'rgba(255,255,255,0.8)',
                            fontSize: '0.65rem',
                            textDecoration: 'none',
                            cursor: 'pointer'
                          }}
                          title="Right-click and 'Save Link As' if button doesn't work"
                        >
                          💾 Save As
                        </a>
                      </div>
                      {/* Audio Player for preview */}
                      <audio preload="metadata" 
                        controls 
                        src={(() => {
                          const audioSrc = worldVoiceAudio || worldVoiceWav;
                          if (audioSrc && audioSrc.startsWith('/api/')) {
                            return `${API}${audioSrc.replace('/api', '')}`;
                          }
                          return audioSrc;
                        })()}
                        style={{ 
                          width: '100%', 
                          height: '40px',
                          borderRadius: '8px'
                        }}
                        data-testid="world-voice-audio-player"
                      >
                        Your browser does not support audio playback.
                      </audio>
                    </div>
                  )}
                  {/* VOICE STYLE PICKER - presets editable in backend/voice_styles.py */}
                  <div data-testid="voice-style-picker" style={{ background: 'rgba(236, 72, 153, 0.08)', border: '1px solid rgba(236, 72, 153, 0.3)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                    <p style={{ color: '#ec4899', fontSize: '0.8rem', fontWeight: 700, margin: '0 0 10px' }}>🎙️ VOICE STYLE — pick a preset, we generate it on Suno & isolate the vocal (44.1kHz WAV)</p>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <select
                        value={selectedVoiceStyle}
                        onChange={async (e) => {
                          setSelectedVoiceStyle(e.target.value);
                          if (voiceStylePresets.length === 0) {
                            try { const r = await axios.get(`${API}/voice-styles`); setVoiceStylePresets(r.data.styles || []); } catch { /* noop */ }
                          }
                        }}
                        onFocus={async () => {
                          if (voiceStylePresets.length === 0) {
                            try { const r = await axios.get(`${API}/voice-styles`); setVoiceStylePresets(r.data.styles || []); } catch { /* noop */ }
                          }
                        }}
                        data-testid="voice-style-select"
                        style={{ flex: 1, minWidth: '240px', padding: '10px', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(236,72,153,0.4)', borderRadius: '8px', color: '#fff', fontSize: '0.85rem' }}
                      >
                        <option value="">— Choose a voice style —</option>
                        {voiceStylePresets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                      <button
                        onClick={() => {
                          const p = voiceStylePresets.find(x => x.id === selectedVoiceStyle);
                          if (!p) { toast.error('Pick a voice style first'); return; }
                          generateWorldVoice({ voice_id: p.id, name: p.name, style: p.description, gender: p.gender, genre: p.genre }, p.region, false);
                        }}
                        disabled={!selectedVoiceStyle || !!worldVoiceGenerating}
                        data-testid="voice-style-generate-btn"
                        style={{ padding: '10px 20px', background: selectedVoiceStyle && !worldVoiceGenerating ? 'linear-gradient(135deg, #ec4899, #8b5cf6)' : 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: selectedVoiceStyle && !worldVoiceGenerating ? 'pointer' : 'not-allowed' }}
                      >
                        {worldVoiceGenerating ? '⏳ Generating…' : '🎤 Generate Voice'}
                      </button>
                    </div>
                    {selectedVoiceStyle && (
                      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.72rem', margin: '8px 0 0' }}>
                        {voiceStylePresets.find(x => x.id === selectedVoiceStyle)?.description}
                      </p>
                    )}
                  </div>
                  {worldVoiceData[worldVoiceRegion].voices.map((voice, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 10px',
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(6, 182, 212, 0.2)',
                        borderRadius: '8px'
                      }}
                    >
                      <button
                        onClick={() => generateWorldVoice(voice, worldVoiceRegion)}
                        disabled={worldVoiceGenerating === voice.voice_id}
                        data-testid={`generate-voice-${voice.voice_id}`}
                        style={{
                          padding: '6px 10px',
                          background: worldVoiceGenerating === voice.voice_id 
                            ? 'rgba(234, 179, 8, 0.4)' 
                            : 'rgba(6, 182, 212, 0.3)',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          cursor: worldVoiceGenerating === voice.voice_id ? 'wait' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="Generate WAV sample (~60 sec)"
                      >
                        {worldVoiceGenerating === voice.voice_id ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            <span style={{ fontSize: '0.6rem' }}>Generating...</span>
                          </>
                        ) : (
                          <>
                            <Headphones className="w-3 h-3" />
                            <span style={{ fontSize: '0.6rem' }}>🎧 WAV</span>
                          </>
                        )}
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ color: '#06b6d4', fontWeight: 600, fontSize: '0.75rem' }}>{voice.name}</span>
                          <span style={{
                            padding: '1px 5px',
                            background: 'rgba(168, 85, 247, 0.2)',
                            borderRadius: '6px',
                            color: '#a855f7',
                            fontSize: '0.6rem'
                          }}>
                            {voice.genre}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          // Add voice style to workflow - NO LYRICS
                          addWorldVoiceToWorkflow(voice, worldVoiceRegion);
                          setBeatPrompt(`${voice.genre} vocals, ${voice.style}, ${voice.name} style`);
                        }}
                        style={{
                          padding: '4px 8px',
                          background: 'linear-gradient(135deg, #10b981, #06b6d4)',
                          border: 'none',
                          borderRadius: '5px',
                          color: '#fff',
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                        title="Add voice style to Workflow"
                      >
                        + Workflow
                      </button>
                      <button
                        onClick={() => copyWorldVoiceToSuno(voice)}
                        style={{
                          padding: '4px 8px',
                          background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                          border: 'none',
                          borderRadius: '5px',
                          color: '#fff',
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                        title="Copy style for Suno"
                      >
                        📋 Suno
                      </button>
                      <button
                        onClick={() => downloadInstantSample(voice)}
                        style={{
                          padding: '4px 8px',
                          background: 'linear-gradient(135deg, #00d4ff, #06b6d4)',
                          border: 'none',
                          borderRadius: '5px',
                          color: '#fff',
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                        title="⚡ Instant download from cache"
                        data-testid={`instant-wav-${voice.voice_id || i}`}
                      >
                        📥 WAV
                      </button>
                      {worldVoiceWav && lastGeneratedVoice?.name === voice.name && (
                        <button
                          onClick={() => downloadWorldVoiceWav(voice.name)}
                          style={{
                            padding: '4px 8px',
                            background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
                            border: 'none',
                            borderRadius: '5px',
                            color: '#fff',
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            animation: 'pulse 1.5s infinite'
                          }}
                          title="Download WAV file to upload to Suno Voices"
                          data-testid="download-wav-suno"
                        >
                          ⬇️ WAV
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <button
                  onClick={loadWorldVoiceData}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'linear-gradient(135deg, #06b6d4, #a855f7)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Load World Voices
                </button>
              )}
            </div>
          </div>
          )}

        </div>

        {/* MASTER EXPORT TO SUNO BUTTON */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(139, 92, 246, 0.1))',
          border: '2px solid rgba(0, 212, 255, 0.3)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px',
          textAlign: 'center'
        }} data-testid="master-export-section">
          <h3 style={{ 
            color: '#00d4ff', 
            fontSize: '1.1rem', 
            fontWeight: 800, 
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px'
          }}>
            <span style={{ fontSize: '1.5rem' }}>🚀</span>
            EXPORT TO SUNO
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: '16px' }}>
            <strong>How to use:</strong> 1️⃣ Open suno.ai in a new tab → 2️⃣ Click COPY STYLE below → 3️⃣ Paste in Suno's Style field → 4️⃣ Click COPY LYRICS → 5️⃣ Paste in Lyrics field
          </p>
          
          {/* Two-Step Export for Suno */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '16px', 
            marginBottom: '16px',
            maxWidth: '600px',
            margin: '0 auto 16px'
          }}>
            {/* STEP 1: Copy Style Prompt */}
            <div style={{
              background: 'rgba(0, 212, 255, 0.1)',
              border: '2px solid rgba(0, 212, 255, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ color: '#00d4ff', fontSize: '0.7rem', fontWeight: 700, marginBottom: '8px' }}>
                STEP 1: STYLE PROMPT
              </div>
              <div style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '0.75rem', 
                marginBottom: '12px',
                minHeight: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {(beatPrompt || workflowPrompt.stylePrompt) 
                  ? `"${(beatPrompt || workflowPrompt.stylePrompt).slice(0, 50)}..."` 
                  : 'Load a preset or analyze a track first'}
              </div>
              <button
                onClick={async () => {
                  const currentStyle = beatPrompt || workflowPrompt.stylePrompt || '';
                  if (!currentStyle) {
                    toast.error('Load a preset first! Click "2. 70K Prompts" above.');
                    return;
                  }
                  const stylePrompt = `${currentStyle}, ${workflowPrompt.bpm || selectedBPM || 120} BPM, ${workflowPrompt.key || selectedKey || 'C'} ${workflowPrompt.mode || selectedMode || 'minor'}${workflowPrompt.voiceSample ? `, ${workflowPrompt.voiceSample} vocals` : ''}`;
                  const success = await copyTextToClipboard(stylePrompt, 'Style');
                  if (success) {
                    toast.success('🎹 Style copied! Paste in Suno\'s "Style of Music" field (⌘+V)');
                  } else {
                    toast.error('Copy failed - try selecting and copying manually');
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  background: (beatPrompt || workflowPrompt.stylePrompt) 
                    ? 'linear-gradient(135deg, #00d4ff, #0099cc)' 
                    : 'rgba(0, 212, 255, 0.3)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 800,
                  cursor: (beatPrompt || workflowPrompt.stylePrompt) ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: (beatPrompt || workflowPrompt.stylePrompt) ? '0 4px 15px rgba(0, 212, 255, 0.4)' : 'none',
                  opacity: (beatPrompt || workflowPrompt.stylePrompt) ? 1 : 0.6
                }}
                data-testid="copy-style-btn"
              >
                <Copy className="w-4 h-4" /> COPY STYLE
              </button>
            </div>

            {/* STEP 2: Copy Lyrics */}
            <div style={{
              background: 'rgba(249, 115, 22, 0.1)',
              border: '2px solid rgba(249, 115, 22, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ color: '#f97316', fontSize: '0.7rem', fontWeight: 700, marginBottom: '8px' }}>
                STEP 2: LYRICS
              </div>
              <div style={{ 
                color: 'var(--text-secondary)', 
                fontSize: '0.75rem', 
                marginBottom: '12px',
                minHeight: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {(workflowPrompt.lyrics || (generatedBars && generatedBars.raw_lyrics))
                  ? `"${(workflowPrompt.lyrics || generatedBars?.raw_lyrics || '').slice(0, 50)}..."` 
                  : 'Generate lyrics above first'}
              </div>
              <button
                onClick={async () => {
                  const lyrics = workflowPrompt.lyrics || generatedBars?.raw_lyrics || '';
                  if (!lyrics) {
                    toast.error('Generate lyrics first using the Rhyme Engine above!');
                    return;
                  }
                  const success = await copyTextToClipboard(lyrics, 'Lyrics');
                  if (success) {
                    toast.success('📝 Lyrics copied! Paste in Suno\'s "Lyrics" field (⌘+V)');
                  } else {
                    toast.error('Copy failed - try selecting and copying manually');
                  }
                }}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  background: (workflowPrompt.lyrics || (generatedBars && generatedBars.raw_lyrics)) 
                    ? 'linear-gradient(135deg, #f97316, #ea580c)' 
                    : 'rgba(249, 115, 22, 0.3)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 800,
                  cursor: (workflowPrompt.lyrics || (generatedBars && generatedBars.raw_lyrics)) ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: (workflowPrompt.lyrics || (generatedBars && generatedBars.raw_lyrics)) 
                    ? '0 4px 15px rgba(249, 115, 22, 0.4)' 
                    : 'none',
                  opacity: (workflowPrompt.lyrics || (generatedBars && generatedBars.raw_lyrics)) ? 1 : 0.6
                }}
                data-testid="copy-lyrics-btn"
              >
                <FileText className="w-4 h-4" /> COPY LYRICS
              </button>
            </div>
          </div>
          
          {/* Quick Actions Row */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
            <button
              onClick={async () => {
                const fullExport = `[Style of Music]
${workflowPrompt.stylePrompt || beatPrompt || 'Modern trap, 130 BPM, hard-hitting 808s'}${workflowPrompt.voiceSample ? `, ${workflowPrompt.voiceSample} vocals` : ''}, ${workflowPrompt.bpm || 120} BPM, ${workflowPrompt.key || 'C'} ${workflowPrompt.mode || 'minor'}

[Lyrics]
${workflowPrompt.lyrics || generatedBars?.raw_lyrics || '(No lyrics generated yet)'}

---
Generated by MANNIS • sounddna.pro`;
                const success = await copyTextToClipboard(fullExport, 'Full Export');
                if (success) {
                  toast.success('✅ Full export copied!');
                } else {
                  toast.error('Copy failed - try selecting and copying manually');
                }
              }}
              style={{
                padding: '10px 20px',
                background: 'rgba(139, 92, 246, 0.2)',
                border: '1px solid rgba(139, 92, 246, 0.4)',
                borderRadius: '10px',
                color: '#8b5cf6',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              data-testid="copy-all-btn"
            >
              <Copy className="w-4 h-4" /> Copy All
            </button>
            
            <button
              onClick={() => {
                toast.info('💡 Tip: If Suno blocks, type suno.ai directly in your browser address bar');
                window.open('https://suno.ai/create', '_blank');
              }}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
              }}
              data-testid="open-suno-btn"
            >
              <ExternalLink className="w-4 h-4" /> Open Suno
            </button>
            
            <button
              onClick={() => {
                window.open('https://www.udio.com/create', '_blank');
              }}
              style={{
                padding: '10px 20px',
                background: 'rgba(236, 72, 153, 0.2)',
                border: '1px solid rgba(236, 72, 153, 0.4)',
                borderRadius: '10px',
                color: '#ec4899',
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              data-testid="open-udio-btn"
            >
              <ExternalLink className="w-4 h-4" /> Open Udio
            </button>
          </div>

          {/* Current Workflow Summary */}
          <div style={{
            marginTop: '12px',
            padding: '12px',
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '10px',
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <span style={{ color: workflowPrompt.stylePrompt || beatPrompt ? '#10b981' : '#ef4444', fontSize: '0.75rem' }}>
              {workflowPrompt.stylePrompt || beatPrompt ? '✓' : '○'} Style Prompt
            </span>
            <span style={{ color: workflowPrompt.bpm || selectedBPM ? '#10b981' : '#ef4444', fontSize: '0.75rem' }}>
              {workflowPrompt.bpm || selectedBPM ? '✓' : '○'} BPM/Key
            </span>
            <span style={{ color: workflowPrompt.voiceSample ? '#10b981' : '#f97316', fontSize: '0.75rem' }}>
              {workflowPrompt.voiceSample ? '✓' : '○'} Voice
            </span>
            <span style={{ color: '#10b981', fontSize: '0.75rem' }}>
              ✓ Rhyme: {rhymeScheme}
            </span>
            <span style={{ color: '#10b981', fontSize: '0.75rem' }}>
              ✓ Flow: {flowPattern}
            </span>
            <span style={{ color: workflowPrompt.lyrics || (generatedBars && generatedBars.raw_lyrics) ? '#10b981' : '#f97316', fontSize: '0.75rem' }}>
              {workflowPrompt.lyrics || (generatedBars && generatedBars.raw_lyrics) ? '✓' : '○'} Lyrics
            </span>
          </div>

          {/* WORKS WITH ALL AI MUSIC APPS */}
          <div style={{
            marginTop: '16px',
            padding: '16px',
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.1))',
            borderRadius: '12px',
            border: '1px solid rgba(139, 92, 246, 0.2)'
          }}>
            <div style={{ 
              textAlign: 'center', 
              marginBottom: '12px',
              color: '#8b5cf6',
              fontSize: '0.8rem',
              fontWeight: 700
            }}>
              🎵 WORKS WITH ALL AI MUSIC APPS
            </div>
            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => window.open('https://suno.ai/create', '_blank')}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 10px rgba(255, 107, 53, 0.3)'
                }}
              >
                <Music2 className="w-4 h-4" /> SUNO
              </button>
              <button
                onClick={() => window.open('https://www.udio.com/create', '_blank')}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #7c3aed, #a855f7)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 10px rgba(124, 58, 237, 0.3)'
                }}
              >
                <Headphones className="w-4 h-4" /> UDIO
              </button>
              <button
                onClick={() => window.open('https://www.loudly.com', '_blank')}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #06b6d4, #0891b2)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 10px rgba(6, 182, 212, 0.3)'
                }}
              >
                <Volume2 className="w-4 h-4" /> LOUDLY
              </button>
              <button
                onClick={() => window.open('https://boomy.com', '_blank')}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #ec4899, #db2777)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 10px rgba(236, 72, 153, 0.3)'
                }}
              >
                <Sparkles className="w-4 h-4" /> BOOMY
              </button>
              <button
                onClick={() => window.open('https://www.beatoven.ai', '_blank')}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 10px rgba(245, 158, 11, 0.3)'
                }}
              >
                <Zap className="w-4 h-4" /> BEATOVEN
              </button>
              <button
                onClick={() => window.open('https://soundraw.io', '_blank')}
                style={{
                  padding: '10px 16px',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 2px 10px rgba(16, 185, 129, 0.3)'
                }}
              >
                <Waves className="w-4 h-4" /> SOUNDRAW
              </button>
            </div>
            <div style={{ 
              textAlign: 'center', 
              marginTop: '10px',
              color: 'var(--text-muted)',
              fontSize: '0.7rem'
            }}>
              Copy your style prompt and lyrics → Paste into any AI music generator
            </div>
          </div>
        </div>

        {/* MANNIS Hero with Frequency Bars */}
        <div style={{
          background: 'linear-gradient(180deg, rgba(10, 14, 26, 0.95) 0%, rgba(20, 28, 50, 0.9) 100%)',
          borderRadius: '24px',
          padding: '28px',
          marginBottom: '20px',
          border: '1px solid rgba(0, 212, 255, 0.15)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Glowing Background Effect */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(0, 212, 255, 0.15) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />
          
          {/* Title - removed, now in header */}
          <div style={{ textAlign: 'center', marginBottom: '20px', position: 'relative', zIndex: 1 }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              AI-Powered Style Prompt Generator for Suno & Udio
            </p>
          </div>

          {/* Animated Frequency Bars */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            gap: '4px',
            height: '80px',
            marginBottom: '20px',
            position: 'relative',
            zIndex: 1
          }}>
            {[40, 65, 45, 80, 55, 90, 70, 50, 85, 60, 75, 45, 95, 55, 70, 40, 60, 80, 50, 65].map((h, i) => (
              <div
                key={i}
                style={{
                  width: '8px',
                  height: `${h}%`,
                  background: `linear-gradient(180deg, 
                    ${i % 4 === 0 ? '#00d4ff' : i % 4 === 1 ? '#8b5cf6' : i % 4 === 2 ? '#ec4899' : '#f97316'} 0%, 
                    rgba(0, 0, 0, 0.3) 100%)`,
                  borderRadius: '4px',
                  boxShadow: `0 0 10px ${i % 4 === 0 ? 'rgba(0, 212, 255, 0.5)' : i % 4 === 1 ? 'rgba(139, 92, 246, 0.5)' : i % 4 === 2 ? 'rgba(236, 72, 153, 0.5)' : 'rgba(249, 115, 22, 0.5)'}`,
                  animation: `pulse ${1 + (i * 0.1)}s ease-in-out infinite alternate`
                }}
              />
            ))}
          </div>

          {/* Waveform Line */}
          <svg width="100%" height="40" style={{ marginBottom: '16px' }}>
            <defs>
              <linearGradient id="waveGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#00d4ff" />
                <stop offset="50%" stopColor="#8b5cf6" />
                <stop offset="100%" stopColor="#ec4899" />
              </linearGradient>
            </defs>
            <path
              d="M0,20 Q25,5 50,20 T100,20 T150,20 T200,20 T250,20 T300,20 T350,20 T400,20 T450,20 T500,20 T550,20 T600,20"
              fill="none"
              stroke="url(#waveGrad)"
              strokeWidth="2"
              style={{ filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.6))' }}
            />
          </svg>

          {/* Feature Tags */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {['VOLUME ANALYSIS', 'SOUND FINGERPRINT', 'GENRE DETECTION'].map((tag, i) => (
              <span key={tag} style={{
                padding: '6px 14px',
                background: i === 0 ? 'rgba(0, 212, 255, 0.15)' : i === 1 ? 'rgba(139, 92, 246, 0.15)' : 'rgba(236, 72, 153, 0.15)',
                border: `1px solid ${i === 0 ? 'rgba(0, 212, 255, 0.3)' : i === 1 ? 'rgba(139, 92, 246, 0.3)' : 'rgba(236, 72, 153, 0.3)'}`,
                borderRadius: '20px',
                fontSize: '0.65rem',
                fontWeight: 700,
                color: i === 0 ? '#00d4ff' : i === 1 ? '#8b5cf6' : '#ec4899',
                letterSpacing: '0.05em'
              }}>
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Quick Actions - 3 Column Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '20px' }}>
          {/* Discover Card */}
          <div 
            onClick={() => setActiveView('marketplace')}
            style={{
              background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(0, 212, 255, 0.05))',
              border: '1px solid rgba(0, 212, 255, 0.2)',
              borderRadius: '16px',
              padding: '16px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 12px',
              background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)'
            }}>
              <Star className="w-6 h-6" style={{ color: '#fff' }} />
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#fff' }}>500+ Presets</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '4px' }}>ATL Trap Library</div>
          </div>

          {/* Analyze Card */}
          <div 
            onClick={() => setActiveView('analyzer')}
            style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(139, 92, 246, 0.05))',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderRadius: '16px',
              padding: '16px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 12px',
              background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)'
            }}>
              <Activity className="w-6 h-6" style={{ color: '#fff' }} />
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#fff' }}>Music DNA</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '4px' }}>Extract BPM & Key</div>
          </div>

          {/* Rhyme Card */}
          <div 
            onClick={() => setActiveView('rhyme')}
            style={{
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1), rgba(236, 72, 153, 0.05))',
              border: '1px solid rgba(236, 72, 153, 0.2)',
              borderRadius: '16px',
              padding: '16px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'center'
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 12px',
              background: 'linear-gradient(135deg, #ec4899, #be185d)',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 15px rgba(236, 72, 153, 0.3)'
            }}>
              <Mic2 className="w-6 h-6" style={{ color: '#fff' }} />
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#fff' }}>Rhyme Flow</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '4px' }}>AI Lyrics Engine</div>
          </div>
        </div>

        {/* NEW FEATURES AUDIO TUTORIAL */}
        <div className="feature-section-mobile" style={{
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(236, 72, 153, 0.1))',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <div className="tutorial-header-mobile" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1 1 auto', minWidth: '200px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #f59e0b, #ec4899)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Headphones className="w-5 h-5" style={{ color: '#fff' }} />
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ color: '#f59e0b', fontWeight: 700, fontSize: '0.95rem' }}>NEW FEATURES TUTORIAL</span>
                  <span style={{ 
                    background: 'linear-gradient(135deg, #ec4899, #f59e0b)', 
                    color: '#fff', 
                    padding: '2px 8px', 
                    borderRadius: '4px', 
                    fontSize: '0.6rem', 
                    fontWeight: 700 
                  }}>AUDIO GUIDE</span>
                  <span style={{ 
                    background: 'rgba(16, 185, 129, 0.2)', 
                    color: '#10b981', 
                    padding: '2px 8px', 
                    borderRadius: '4px', 
                    fontSize: '0.6rem', 
                    fontWeight: 700 
                  }}>ALL SUBSCRIBERS</span>
                  <span style={{ 
                    background: 'rgba(59, 130, 246, 0.2)', 
                    color: '#3b82f6', 
                    padding: '2px 8px', 
                    borderRadius: '4px', 
                    fontSize: '0.6rem', 
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    🔒 SECURE
                  </span>
                </div>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', margin: '4px 0 0 0' }}>
                  DNA Lyrics, Singing Vocals, Storage & works with Suno, Udio, Loudly, Boomy & more
                </p>
              </div>
            </div>
            
            <div className="tutorial-controls" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              {/* Tutorial Language Dropdown */}
              <select
                value={tutorialLanguage}
                onChange={(e) => {
                  setTutorialLanguage(e.target.value);
                  setTutorialAudioUrl(null); // Clear audio when language changes
                }}
                style={{
                  padding: '8px 12px',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(245, 158, 11, 0.3)',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '0.8rem',
                  cursor: 'pointer'
                }}
                data-testid="tutorial-language-select"
              >
                <option value="English">🇺🇸 English</option>
                <option value="Spanish">🇪🇸 Spanish</option>
                <option value="French">🇫🇷 French</option>
                <option value="Portuguese">🇧🇷 Portuguese</option>
                <option value="German">🇩🇪 German</option>
                <option value="Italian">🇮🇹 Italian</option>
                <option value="Dutch">🇳🇱 Dutch</option>
                <option value="Polish">🇵🇱 Polish</option>
                <option value="Russian">🇷🇺 Russian</option>
                <option value="Greek">🇬🇷 Greek</option>
                <option value="Turkish">🇹🇷 Turkish</option>
                <option value="Hebrew">🇮🇱 Hebrew</option>
                <option value="Japanese">🇯🇵 Japanese</option>
                <option value="Korean">🇰🇷 Korean</option>
                <option value="Chinese">🇨🇳 Chinese</option>
                <option value="Thai">🇹🇭 Thai</option>
                <option value="Vietnamese">🇻🇳 Vietnamese</option>
                <option value="Indonesian">🇮🇩 Indonesian</option>
                <option value="Filipino">🇵🇭 Filipino (Tagalog)</option>
                <option value="Malay">🇲🇾 Malay</option>
                <option value="Arabic">🇸🇦 Arabic</option>
                <option value="Hindi">🇮🇳 Hindi</option>
                <option value="Punjabi">🇮🇳 Punjabi</option>
                <option value="Tamil">🇮🇳 Tamil</option>
                <option value="Bengali">🇧🇩 Bengali</option>
                <option value="Urdu">🇵🇰 Urdu</option>
                <option value="Swahili">🇰🇪 Swahili</option>
                <option value="Yoruba">🇳🇬 Yoruba</option>
                <option value="Amharic">🇪🇹 Amharic</option>
                <option value="Pidgin">🇳🇬 Pidgin</option>
                <option value="Patois">🇯🇲 Jamaican Patois</option>
                <option value="Creole">🇭🇹 Haitian Creole</option>
              </select>
              
              {tutorialAudioUrl ? (
                <>
                  <audio preload="metadata" 
                    ref={tutorialAudioRef}
                    src={tutorialAudioUrl} 
                    style={{ height: '36px', maxWidth: '200px' }}
                    controls
                  />
                  <button
                    onClick={() => setTutorialAudioUrl(null)}
                    style={{
                      padding: '8px',
                      background: 'rgba(255,255,255,0.1)',
                      border: 'none',
                      borderRadius: '6px',
                      color: 'rgba(255,255,255,0.6)',
                      cursor: 'pointer',
                      fontSize: '0.8rem'
                    }}
                  >
                    ×
                  </button>
                </>
              ) : (
                <button
                  onClick={generateAudioTutorial}
                  disabled={tutorialLoading}
                  style={{
                    padding: '10px 20px',
                    background: tutorialLoading ? 'rgba(100,100,100,0.3)' : 'linear-gradient(135deg, #f59e0b, #ec4899)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    cursor: tutorialLoading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)'
                  }}
                  data-testid="play-tutorial-btn"
                >
                  {tutorialLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Play Tutorial
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* AI MUSIC WORKFLOW HUB - Integrated Features */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.1))',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '20px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <h2 style={{ 
              fontSize: '1.3rem', 
              fontWeight: 800, 
              background: 'linear-gradient(135deg, #8b5cf6, #ec4899)', 
              WebkitBackgroundClip: 'text', 
              WebkitTextFillColor: 'transparent',
              marginBottom: '8px'
            }}>
              AI MUSIC WORKFLOW HUB
            </h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Integrate MP3 analysis with all features → Export to Suno, Udio & more
            </p>
          </div>

          {/* Workflow Steps */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
            {[
              { step: '1', title: 'Upload MP3', desc: 'Analyze your track', icon: '🎵', color: '#00d4ff' },
              { step: '2', title: 'Match Presets', desc: '550+ style prompts', icon: '⭐', color: '#8b5cf6' },
              { step: '3', title: 'Add Voice + Lyrics', desc: 'Complete your sound', icon: '🎤', color: '#ec4899' },
              { step: '4', title: 'Export to AI', desc: 'Suno, Udio & more', icon: '🚀', color: '#10b981' }
            ].map((item, i) => (
              <div key={i} style={{
                background: 'rgba(17, 24, 39, 0.6)',
                border: `1px solid ${item.color}33`,
                borderRadius: '12px',
                padding: '14px',
                textAlign: 'center',
                position: 'relative'
              }}>
                <div style={{ fontSize: '1.5rem', marginBottom: '8px' }}>{item.icon}</div>
                <div style={{ color: item.color, fontWeight: 700, fontSize: '0.8rem' }}>STEP {item.step}</div>
                <div style={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem', marginTop: '4px' }}>{item.title}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginTop: '2px' }}>{item.desc}</div>
                {i < 3 && (
                  <div style={{
                    position: 'absolute',
                    right: '-18px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-muted)',
                    fontSize: '1.2rem',
                    zIndex: 1
                  }}>→</div>
                )}
              </div>
            ))}
          </div>

          {/* Quick Export Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => {
                const prompt = beatPrompt || 'Generate a style prompt first';
                const sunoFormat = `[Style: ${prompt}]\n[Instruments: Based on analysis]\n[Mood: Dynamic]\n[Tempo: ${selectedBPM || 120} BPM]`;
                universalCopy(sunoFormat, 'Copied for Suno!');
              }}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)'
              }}
            >
              <span>🎹</span> Export to SUNO
            </button>
            <button
              onClick={() => {
                const prompt = beatPrompt || 'Generate a style prompt first';
                const udioFormat = `Style: ${prompt}\nTempo: ${selectedBPM || 120} BPM\nKey: ${selectedKey || 'C'} ${selectedMode || 'minor'}`;
                universalCopy(udioFormat, 'Copied for Udio!');
              }}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)'
              }}
            >
              <span>🎧</span> Export to UDIO
            </button>
            <button
              onClick={() => {
                const fullPrompt = `
Style Prompt: ${beatPrompt || 'Dark trap with heavy 808s'}
BPM: ${selectedBPM || 120}
Key: ${selectedKey || 'C'} ${selectedMode || 'minor'}
Voice: ${selectedVoiceSample || 'Not selected'}
---
Copy this entire prompt to any AI music generator!
                `.trim();
                universalCopy(fullPrompt, 'Universal prompt copied!');
              }}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #ec4899, #be185d)',
                border: 'none',
                borderRadius: '10px',
                color: '#fff',
                fontWeight: 700,
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 15px rgba(236, 72, 153, 0.3)'
              }}
            >
              <span>🌐</span> Universal Export
            </button>
          </div>

          {/* Supported Platforms */}
          <div style={{ 
            textAlign: 'center', 
            marginTop: '16px',
            padding: '12px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '10px'
          }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
              Works with: <span style={{ color: '#00d4ff' }}>Suno</span> • <span style={{ color: '#8b5cf6' }}>Udio</span> • <span style={{ color: '#ec4899' }}>Stable Audio</span> • <span style={{ color: '#f97316' }}>MusicGen</span> • <span style={{ color: '#10b981' }}>Riffusion</span> • <span style={{ color: '#eab308' }}>Any AI Music Platform</span>
            </span>
          </div>
        </div>

        {/* Currently Selected Options Indicator */}
        {(selectedGenre !== 'auto' || selectedArtist !== 'none') && (
          <div style={{
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            marginBottom: '12px',
            padding: '12px',
            background: 'rgba(159,71,255,0.1)',
            borderRadius: '10px',
            border: '1px solid rgba(159,71,255,0.3)'
          }}>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>Selected:</span>
            {selectedGenre !== 'auto' && (
              <span style={{
                background: 'rgba(255,0,102,0.2)',
                color: '#9F47FF',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: 600
              }}>
                🎵 {GENRES.find(g => g.value === selectedGenre)?.label || selectedGenre}
              </span>
            )}
            {selectedArtist !== 'none' && (
              <span style={{
                background: 'rgba(159,71,255,0.2)',
                color: '#9F47FF',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: 600
              }}>
                🎤 {ARTIST_STYLES.find(a => a.value === selectedArtist)?.label || selectedArtist}
              </span>
            )}
          </div>
        )}

        {/* Dropzone */}
        <div {...getRootProps()} className={`dropzone-portal ${isDragActive ? 'active' : ''} ${uploadedFile ? 'loaded' : ''}`} data-testid="dropzone">
          <input {...getInputProps()} />
          <div className="dropzone-content">
            {uploadedFile ? (
              <>
                <div className="dropzone-icon success"><FileAudio className="w-8 h-8" /></div>
                <span className="dropzone-title">{uploadedFile.name}</span>
                <span className="dropzone-sub">Tap to change</span>
              </>
            ) : (
              <>
                <div className="dropzone-icon"><Music2 className="w-8 h-8" /></div>
                <span className="dropzone-title">Drop Your Tracks</span>
                <span className="dropzone-sub">MP3, WAV, FLAC, OGG • Up to 10 files</span>
              </>
            )}
          </div>
        </div>

        {/* Discover Presets Button */}
        <button 
          onClick={() => setShowGenreGuide(true)}
          className="glow-btn"
          style={{
            width: '100%',
            padding: '16px 24px',
            background: 'linear-gradient(135deg, #ec4899, #8b5cf6)',
            borderRadius: '14px',
            fontSize: '1rem',
            fontWeight: 700,
            marginTop: '24px',
            boxShadow: '0 4px 20px rgba(236, 72, 153, 0.4)'
          }}
          data-testid="discover-presets-btn"
        >
          <Star className="w-5 h-5" /> Discover Presets
        </button>

        {/* LeShore Style Prompts Library Button */}
        <button 
          onClick={async () => {
            await loadSunoLibraryCategories();
            setShowSunoLibrary(true);
          }}
          className="glow-btn"
          style={{
            width: '100%',
            padding: '16px 24px',
            background: 'linear-gradient(135deg, #8b5cf6, #00d4ff)',
            borderRadius: '14px',
            color: '#fff',
            fontSize: '1rem',
            fontWeight: 700,
            marginTop: '12px',
            boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4)'
          }}
          data-testid="suno-library-btn"
        >
          <Library className="w-5 h-5" />
          70K Style Prompts Library
        </button>

        {/* MUSIC LINK ANALYZER */}
        <div className="glass-card" style={{ marginTop: '24px', padding: '20px' }} data-testid="music-analyzer">
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.2), rgba(139, 92, 246, 0.2))',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Radio className="w-6 h-6" style={{ color: 'var(--accent)' }} />
            </div>
            <div>
              <h2 style={{ margin: 0, color: 'var(--accent)', fontSize: '1.1rem', fontWeight: 700 }}>
                MUSIC LINK ANALYZER
              </h2>
              <p style={{ margin: '3px 0 0 0', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                Extract BPM, Key, Mood • Match Style Prompts • Generate "Sound Like" Prompts
              </p>
            </div>
          </div>
          
          {/* Source badges: YouTube recommended, Spotify under maintenance */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <span data-testid="analyzer-youtube-badge" style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '4px 10px', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700,
              background: 'rgba(34, 197, 94, 0.15)', border: '1px solid rgba(34, 197, 94, 0.4)', color: '#4ade80'
            }}>
              ▶ YouTube
              <span style={{
                background: '#22c55e', color: '#04120a', padding: '1px 7px',
                borderRadius: '999px', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.05em'
              }}>RECOMMENDED</span>
            </span>
            <span data-testid="analyzer-spotify-badge" style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '4px 10px', borderRadius: '999px', fontSize: '0.7rem', fontWeight: 700,
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.45)'
            }}>
              Spotify
              <span style={{
                background: 'rgba(245, 158, 11, 0.2)', color: '#fbbf24', padding: '1px 7px',
                borderRadius: '999px', fontSize: '0.6rem', fontWeight: 800, letterSpacing: '0.05em',
                border: '1px solid rgba(245, 158, 11, 0.35)'
              }}>UNDER MAINTENANCE</span>
            </span>
          </div>
          
          {/* Input */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '8px' }}>
            <input
              type="text"
              value={spotifyUrl}
              onChange={(e) => setSpotifyUrl(e.target.value)}
              placeholder="Paste YouTube track link (recommended) or style prompt..."
              className="modern-input"
              style={{ flex: 1 }}
              onKeyPress={(e) => e.key === 'Enter' && analyzeSpotifyTrack()}
              data-testid="music-url-input"
            />
            <button
              onClick={analyzeSpotifyTrack}
              disabled={spotifyAnalyzing}
              className="glow-btn"
              style={{
                padding: '12px 20px',
                opacity: spotifyAnalyzing ? 0.6 : 1,
                cursor: spotifyAnalyzing ? 'wait' : 'pointer',
                whiteSpace: 'nowrap'
              }}
              data-testid="music-analyze-btn"
            >
              {spotifyAnalyzing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Analyzing...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" /> Analyze
                </>
              )}
            </button>
          </div>
          <p style={{ margin: '0 0 8px 0', color: 'var(--text-muted)', fontSize: '0.7rem' }}>
            💡 Tip: Use YouTube track links (youtube.com/watch?v=... or youtu.be/...) for best results. Spotify analysis returns soon — we're finalizing API approval.
          </p>
          
          {/* OR Divider */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            marginBottom: '12px',
            color: 'var(--text-muted)',
            fontSize: '0.8rem'
          }}>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }} />
            <span>OR</span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.1)' }} />
          </div>
          
          {/* Drag-and-Drop Audio Upload */}
          <div
            onDrop={handleMusicAnalyzerDrop}
            onDragOver={handleMusicAnalyzerDragOver}
            onDragLeave={handleMusicAnalyzerDragLeave}
            onClick={() => document.getElementById('music-analyzer-file-input').click()}
            style={{
              border: `2px dashed ${musicAnalyzerDragActive ? '#00d4ff' : 'rgba(0, 212, 255, 0.4)'}`,
              borderRadius: '12px',
              padding: '24px',
              textAlign: 'center',
              cursor: spotifyAnalyzing ? 'wait' : 'pointer',
              background: musicAnalyzerDragActive ? 'rgba(0, 212, 255, 0.15)' : 'rgba(0, 212, 255, 0.05)',
              transition: 'all 0.2s ease',
              marginBottom: '12px'
            }}
            data-testid="music-analyzer-dropzone"
          >
            <input
              id="music-analyzer-file-input"
              type="file"
              accept=".mp3,.wav,.m4a,.flac,audio/*"
              onChange={handleMusicAnalyzerFileInput}
              style={{ display: 'none' }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              {spotifyAnalyzing ? (
                <>
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#00d4ff' }} />
                  <p style={{ color: '#00d4ff', fontWeight: 600, margin: 0 }}>
                    Analyzing {musicAnalyzerFile?.name || 'audio'}...
                  </p>
                </>
              ) : musicAnalyzerFile ? (
                <>
                  <Music className="w-8 h-8" style={{ color: '#10b981' }} />
                  <p style={{ color: '#10b981', fontWeight: 600, margin: 0 }}>
                    📁 {musicAnalyzerFile.name}
                  </p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', margin: 0 }}>
                    Click to upload a different file
                  </p>
                </>
              ) : (
                <>
                  <Upload className="w-8 h-8" style={{ color: '#00d4ff' }} />
                  <p style={{ color: '#00d4ff', fontWeight: 600, margin: 0 }}>
                    Drag & Drop Audio File
                  </p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', margin: 0 }}>
                    MP3, WAV, M4A, FLAC supported
                  </p>
                </>
              )}
            </div>
          </div>
          
          {/* Audio Preview Player + WAV Download */}
          {analyzerAudioUrl && (
            <div style={{
              background: 'rgba(0, 212, 255, 0.08)',
              border: '1px solid rgba(0, 212, 255, 0.3)',
              borderRadius: '12px',
              padding: '14px',
              marginBottom: '12px'
            }} data-testid="analyzer-preview-panel">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                <span style={{ color: '#00d4ff', fontSize: '0.8rem', fontWeight: 700 }}>
                  🎧 AUDIO PREVIEW {analyzerAudioSource === 'upload' ? `— ${musicAnalyzerFile?.name || ''}` : '— from URL'}
                </span>
                <button
                  onClick={() => {
                    if (analyzerAudioSource === 'upload') URL.revokeObjectURL(analyzerAudioUrl);
                    setAnalyzerAudioUrl(null);
                    setAnalyzerAudioSource(null);
                    setMusicAnalyzerFile(null);
                  }}
                  style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', fontSize: '1.1rem' }}
                  data-testid="analyzer-preview-clear-btn"
                >×</button>
              </div>
              <audio preload="metadata"
                controls
                src={analyzerAudioUrl}
                style={{ width: '100%', height: '40px', marginBottom: '10px' }}
                data-testid="analyzer-audio-preview"
              />
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {analyzerAudioSource === 'upload' && (
                  <button
                    onClick={() => analyzeAudioFile(musicAnalyzerFile)}
                    disabled={spotifyAnalyzing}
                    style={{
                      padding: '10px 18px',
                      background: spotifyAnalyzing ? 'rgba(0, 212, 255, 0.3)' : 'linear-gradient(135deg, #00d4ff, #0891b2)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      cursor: spotifyAnalyzing ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    data-testid="analyzer-run-analysis-btn"
                  >
                    {spotifyAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    {spotifyAnalyzing ? 'Analyzing...' : 'Analyze This Track'}
                  </button>
                )}
                <button
                  onClick={downloadAnalyzerWav}
                  disabled={isConvertingWav}
                  style={{
                    padding: '10px 18px',
                    background: isConvertingWav ? 'rgba(16, 185, 129, 0.3)' : 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    cursor: isConvertingWav ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  data-testid="analyzer-download-wav-btn"
                >
                  {isConvertingWav ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  {isConvertingWav ? 'Converting...' : 'Download WAV'}
                </button>
              </div>
            </div>
          )}
          
          {/* Analysis Results Summary (when file analyzed) */}
          {spotifyResult && musicAnalyzerFile && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(0, 212, 255, 0.1))',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <CheckCircle className="w-5 h-5" style={{ color: '#10b981' }} />
                <span style={{ color: '#10b981', fontWeight: 700 }}>
                  Analysis Complete: {spotifyResult.track?.name}
                </span>
                <button
                  onClick={() => setShowSpotifyModal(true)}
                  style={{
                    marginLeft: 'auto',
                    padding: '6px 12px',
                    background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  View Full Analysis
                </button>
              </div>
              {spotifyResult.note && (
                <div data-testid="analyzer-note-banner" style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '10px 14px', marginBottom: '12px', borderRadius: '8px',
                  background: /estimat/i.test(spotifyResult.note) ? 'rgba(251,191,36,0.12)' : 'rgba(16,185,129,0.12)',
                  border: `1px solid ${/estimat/i.test(spotifyResult.note) ? 'rgba(251,191,36,0.5)' : 'rgba(16,185,129,0.4)'}`
                }}>
                  <span style={{ fontSize: '1rem' }}>{/estimat/i.test(spotifyResult.note) ? '⚠️' : '🎧'}</span>
                  <span style={{
                    color: /estimat/i.test(spotifyResult.note) ? '#fbbf24' : '#10b981',
                    fontSize: '0.85rem', fontWeight: 700
                  }}>{spotifyResult.note}</span>
                </div>
              )}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {[
                  { label: 'BPM', value: spotifyResult.analysis_summary?.bpm },
                  { label: 'Key', value: spotifyResult.analysis_summary?.key },
                  { label: 'Mood', value: spotifyResult.analysis_summary?.mood },
                  { label: 'Genre', value: spotifyResult.analysis_summary?.genre }
                ].filter(item => item.value && item.value !== 'N/A').map((item, i) => (
                  <div key={i} style={{
                    padding: '8px 12px',
                    background: 'rgba(0,0,0,0.3)',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{item.label}:</span>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem' }}>{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Producer Report - structured analysis panels */}
          {spotifyResult?.producer_report && (
            <div style={{
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid rgba(139, 92, 246, 0.4)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '12px'
            }} data-testid="producer-report">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <span style={{ color: '#a855f7', fontWeight: 700, fontSize: '0.9rem' }}>🎛️ PRODUCER REPORT</span>
                <span style={{ background: 'rgba(168, 85, 247, 0.2)', color: '#a855f7', padding: '2px 8px', borderRadius: '10px', fontSize: '0.65rem', fontWeight: 600 }}>AI ANALYSIS</span>
              </div>
              
              {/* Stat cards row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                {[
                  { label: 'GENRE', value: spotifyResult.producer_report.genre, color: '#00d4ff' },
                  { label: 'SUBGENRE', value: spotifyResult.producer_report.subgenre, color: '#8b5cf6' },
                  { label: 'TEMPO (BPM)', value: spotifyResult.producer_report.tempo_bpm, color: '#f59e0b' },
                  { label: 'KEY / SCALE', value: spotifyResult.producer_report.key_scale, color: '#10b981' }
                ].filter(s => s.value).map((stat, i) => (
                  <div key={i} style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${stat.color}33`,
                    borderRadius: '10px',
                    padding: '10px 12px'
                  }} data-testid={`report-${stat.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                    <div style={{ color: stat.color, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', marginBottom: '4px' }}>{stat.label}</div>
                    <div style={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600 }}>{String(stat.value)}</div>
                  </div>
                ))}
              </div>
              
              {/* Mood / Vibe */}
              {spotifyResult.producer_report.mood_vibe && (
                <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px' }} data-testid="report-mood-vibe">
                  <div style={{ color: '#ec4899', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', marginBottom: '4px' }}>MOOD / VIBE</div>
                  <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem', lineHeight: 1.5 }}>{spotifyResult.producer_report.mood_vibe}</div>
                </div>
              )}
              
              {/* Chip lists */}
              {[
                { label: 'INSTRUMENTATION', items: spotifyResult.producer_report.instrumentation, color: '#00d4ff' },
                { label: 'PRODUCTION TECHNIQUES', items: spotifyResult.producer_report.production_techniques, color: '#f59e0b' },
                { label: 'COMPARABLE ARTISTS', items: spotifyResult.producer_report.comparable_artists, color: '#10b981' }
              ].filter(g => Array.isArray(g.items) && g.items.length > 0).map((group, gi) => (
                <div key={gi} style={{ marginBottom: '12px' }} data-testid={`report-${group.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <div style={{ color: group.color, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', marginBottom: '6px' }}>{group.label}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {group.items.map((item, ii) => (
                      <span key={ii} style={{
                        background: `${group.color}1a`,
                        border: `1px solid ${group.color}44`,
                        color: '#fff',
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '0.75rem'
                      }}>{item}</span>
                    ))}
                  </div>
                </div>
              ))}
              
              {/* Suno-Ready Style Prompt */}
              {spotifyResult.producer_report.suno_style_prompt && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15), rgba(0, 212, 255, 0.1))',
                  border: '1px solid rgba(168, 85, 247, 0.5)',
                  borderRadius: '10px',
                  padding: '12px'
                }} data-testid="report-suno-prompt">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ color: '#a855f7', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em' }}>🎵 SUNO-READY STYLE PROMPT</span>
                    <button
                      onClick={() => universalCopy(spotifyResult.producer_report.suno_style_prompt, '✅ Suno style prompt copied!')}
                      style={{
                        padding: '5px 12px',
                        background: 'linear-gradient(135deg, #a855f7, #8b5cf6)',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#fff',
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                      data-testid="report-copy-suno-prompt-btn"
                    >
                      📋 Copy
                    </button>
                  </div>
                  <div style={{ color: '#fff', fontSize: '0.85rem', lineHeight: 1.5 }}>{spotifyResult.producer_report.suno_style_prompt}</div>
                </div>
              )}
            </div>
          )}
          
          {/* Inline Generated Prompts Dropdown */}
          {inlinePrompts && (
            <div style={{
              marginTop: '12px',
              background: 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(139, 92, 246, 0.4)',
              borderRadius: '8px',
              padding: '12px',
              maxHeight: '300px',
              overflow: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <p style={{ margin: 0, color: '#a855f7', fontSize: '0.85rem', fontWeight: 700 }}>
                  ✨ 10 GENERATED STYLE PROMPTS
                </p>
                <button
                  onClick={() => setInlinePrompts(null)}
                  style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer', fontSize: '1.2rem' }}
                >×</button>
              </div>
              {inlinePrompts.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px 10px',
                    background: idx % 2 === 0 ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                    borderRadius: '6px',
                    marginBottom: '4px'
                  }}
                >
                  <span style={{
                    background: '#a855f7',
                    color: '#000',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    minWidth: '60px',
                    textAlign: 'center'
                  }}>{item.dna_code}</span>
                  <span style={{ flex: 1, color: '#fff', fontSize: '0.8rem' }}>{item.prompt}</span>
                  {/* Audio Preview Button */}
                  <button
                    onClick={() => generateAudioPreview(item.prompt, item.dna_code, `ma-inline-${idx}`)}
                    disabled={audioPreviewLoading === `ma-inline-${idx}`}
                    style={{
                      padding: '4px 8px',
                      background: audioPreviewLoading === `ma-inline-${idx}` ? 'rgba(139,92,246,0.3)' : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                      border: 'none',
                      borderRadius: '4px',
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      cursor: audioPreviewLoading === `ma-inline-${idx}` ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}
                    title="Preview Audio"
                    data-testid={`ma-preview-${idx}`}
                  >
                    {audioPreviewLoading === `ma-inline-${idx}` ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Headphones className="w-3 h-3" />
                    )}
                  </button>
                  {/* MIDI Download Button */}
                  <button
                    onClick={() => generateMidiFromPrompt(item.prompt)}
                    disabled={isGeneratingMidi}
                    style={{
                      padding: '4px 8px',
                      background: isGeneratingMidi ? 'rgba(16,185,129,0.3)' : 'linear-gradient(135deg, #10b981, #059669)',
                      border: 'none',
                      borderRadius: '4px',
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      cursor: isGeneratingMidi ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}
                    title="Download MIDI"
                    data-testid={`ma-midi-${idx}`}
                  >
                    {isGeneratingMidi ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Music className="w-3 h-3" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      universalCopy(item.prompt, `✅ Copied ${item.dna_code}!`);
                    }}
                    style={{
                      padding: '4px 10px',
                      background: '#9F47FF, #a855f7)',
                      border: 'none',
                      borderRadius: '4px',
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >Copy</button>
                </div>
              ))}
            </div>
          )}
          
          
        </div>

        {/* Music Results Modal */}
        {showSpotifyModal && spotifyResult && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            overflow: 'auto'
          }} onClick={() => { stopAudioPreview(); setShowSpotifyModal(false); }}>
            <div style={{
              background: 'linear-gradient(180deg, #1a1a2e, #0a0a0a)',
              borderRadius: '16px',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              border: '2px solid rgba(139, 92, 246, 0.3)'
            }} onClick={(e) => e.stopPropagation()}>
              {/* Track Header */}
              <div style={{
                display: 'flex',
                gap: '16px',
                padding: '20px',
                borderBottom: '1px solid rgba(255,255,255,0.1)',
                alignItems: 'center'
              }}>
                {spotifyResult.track.album_art && (
                  <img 
                    src={spotifyResult.track.album_art} 
                    alt={spotifyResult.track.album}
                    style={{ width: '100px', height: '100px', borderRadius: '8px' }}
                  />
                )}
                <div>
                  <h2 style={{ margin: 0, color: '#fff', fontSize: '1.4rem' }}>
                    {spotifyResult.track.name}
                  </h2>
                  <p style={{ margin: '4px 0', color: '#a855f7', fontSize: '1rem' }}>
                    {(spotifyResult.track?.artists || []).join(', ') || 'Unknown artist'}
                  </p>
                  <p style={{ margin: 0, color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                    {spotifyResult.track.album}
                  </p>
                  {spotifyResult.note && (
                    <div data-testid="analyzer-modal-note-banner" style={{
                      display: 'inline-flex', alignItems: 'center', gap: '8px',
                      marginTop: '10px', padding: '8px 14px', borderRadius: '8px',
                      background: /estimat/i.test(spotifyResult.note) ? 'rgba(251,191,36,0.12)' : 'rgba(16,185,129,0.12)',
                      border: `1px solid ${/estimat/i.test(spotifyResult.note) ? 'rgba(251,191,36,0.5)' : 'rgba(16,185,129,0.4)'}`
                    }}>
                      <span>{/estimat/i.test(spotifyResult.note) ? '⚠️' : '🎧'}</span>
                      <span style={{
                        color: /estimat/i.test(spotifyResult.note) ? '#fbbf24' : '#10b981',
                        fontSize: '0.8rem', fontWeight: 700
                      }}>{spotifyResult.note}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => { stopAudioPreview(); setShowSpotifyModal(false); }}
                  data-testid="analyzer-modal-close"
                  style={{
                    marginLeft: 'auto',
                    background: 'transparent',
                    border: 'none',
                    color: '#fff',
                    fontSize: '1.5rem',
                    cursor: 'pointer'
                  }}
                >×</button>
              </div>
              
              {/* Audio DNA */}
              <div style={{ padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <h3 style={{ margin: '0 0 12px 0', color: '#a855f7', fontSize: '1rem' }}>
                  🎵 AUDIO DNA
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px' }}>
                  {[
                    { label: 'BPM', value: spotifyResult.analysis_summary.bpm },
                    { label: 'Key', value: spotifyResult.analysis_summary.key },
                    { label: 'Energy', value: spotifyResult.analysis_summary.energy },
                    { label: 'Mood', value: spotifyResult.analysis_summary.mood },
                    { label: 'Genre', value: spotifyResult.analysis_summary.genre },
                    { label: 'Happiness', value: spotifyResult.analysis_summary.happiness }
                  ].map((item, i) => (
                    <div key={i} style={{
                      background: 'rgba(29, 185, 84, 0.1)',
                      padding: '12px',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', marginBottom: '4px' }}>
                        {item.label}
                      </div>
                      <div style={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>
                        {item.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Auto-generated audio preview */}
              {cardPreviews['analyzer-main'] && (
                <div style={{ padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }} data-testid="analyzer-auto-preview">
                  <h3 style={{ margin: '0 0 12px 0', color: '#00d4ff', fontSize: '1rem' }}>🎧 AUDIO PREVIEW</h3>
                  {cardPreviews['analyzer-main'].status === 'rendering' && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#00d4ff' }} />
                        <span style={{ color: '#00d4ff', fontSize: '0.8rem', fontWeight: 700 }}>Rendering Audio... (~30-60s)</span>
                      </div>
                      <div style={{ height: '5px', borderRadius: '3px', background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: '40%', borderRadius: '3px', background: 'linear-gradient(90deg, #00d4ff, #a855f7)', animation: 'previewSlide 1.2s ease-in-out infinite' }} />
                      </div>
                    </div>
                  )}
                  {cardPreviews['analyzer-main'].status === 'ready' && (
                    <audio controls preload="metadata" src={cardPreviews['analyzer-main'].url} style={{ width: '100%', height: '36px' }} data-testid="analyzer-preview-audio" />
                  )}
                  {cardPreviews['analyzer-main'].status === 'failed' && (
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>⚠️ Preview render failed</span>
                  )}
                </div>
              )}
              
              {/* Matching Style Prompts */}
              {spotifyResult.matching_styles?.length > 0 && (
                <div style={{ padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#9F47FF', fontSize: '1rem' }}>
                    🎯 MATCHING STYLE PROMPTS ({spotifyResult.matching_styles.length})
                  </h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {spotifyResult.matching_styles.slice(0, 10).map((style, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <button
                          onClick={() => {
                            setSelectedArtist(style.style_key);
                            setShowSpotifyModal(false);
                            toast.success(`Style selected: ${style.label}`);
                          }}
                          style={{
                            padding: '8px 16px',
                            background: 'rgba(0, 191, 255, 0.15)',
                            border: '1px solid rgba(0, 191, 255, 0.3)',
                            borderRadius: '20px 0 0 20px',
                            color: '#fff',
                            fontSize: '0.85rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          {style.label}
                        </button>
                        <button
                          onClick={() => generateAudioPreview(style.prompt || style.label, style.label, `match-${i}`)}
                          disabled={audioPreviewLoading === `match-${i}`}
                          style={{
                            padding: '8px 10px',
                            background: audioPreviewLoading === `match-${i}` ? 'rgba(139,92,246,0.3)' : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                            border: 'none',
                            borderRadius: '0 20px 20px 0',
                            color: '#fff',
                            cursor: audioPreviewLoading === `match-${i}` ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Preview Audio"
                          data-testid={`match-preview-${i}`}
                        >
                          {audioPreviewLoading === `match-${i}` ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Headphones className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Generated "Sound Like" Prompts */}
              {spotifyResult.generated_prompts?.length > 0 && (
                <div style={{ padding: '20px' }}>
                  <h3 style={{ margin: '0 0 12px 0', color: '#fbbf24', fontSize: '1rem' }}>
                    ✨ "SOUND LIKE" PROMPTS ({spotifyResult.generated_prompts.length})
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '300px', overflow: 'auto' }}>
                    {spotifyResult.generated_prompts.map((prompt, i) => (
                      <div key={i} style={{
                        background: 'rgba(251, 191, 36, 0.1)',
                        border: '1px solid rgba(251, 191, 36, 0.2)',
                        borderRadius: '8px',
                        padding: '12px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px'
                      }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: 0, color: '#fff', fontSize: '0.85rem', lineHeight: 1.4 }}>
                            {prompt.prompt}
                          </p>
                          <p style={{ margin: '6px 0 0 0', color: 'rgba(255,255,255,0.4)', fontSize: '0.7rem' }}>
                            DNA: {prompt.dna_code}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {/* Audio Preview Play/Stop Button */}
                          <button
                            onClick={() => generateAudioPreview(prompt.prompt, prompt.dna_code, `soundlike-${i}`)}
                            disabled={audioPreviewLoading === `soundlike-${i}`}
                            style={{
                              padding: '8px 10px',
                              background: audioPreviewPlaying === `soundlike-${i}`
                                ? 'linear-gradient(135deg, #ef4444, #b91c1c)'
                                : audioPreviewLoading === `soundlike-${i}` ? 'rgba(139,92,246,0.3)' : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                              border: 'none',
                              borderRadius: '6px',
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              cursor: audioPreviewLoading === `soundlike-${i}` ? 'wait' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title={audioPreviewPlaying === `soundlike-${i}` ? 'Stop' : 'Generate & Play Preview'}
                            data-testid={`soundlike-preview-${i}`}
                          >
                            {audioPreviewLoading === `soundlike-${i}` ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : audioPreviewPlaying === `soundlike-${i}` ? (
                              <>
                                <Square className="w-3 h-3" fill="#fff" />
                                <span>Stop</span>
                              </>
                            ) : (
                              <>
                                <Play className="w-3 h-3" fill="#fff" />
                                <span>Play</span>
                              </>
                            )}
                          </button>
                          {/* WAV Download Button */}
                          <button
                            onClick={async () => {
                              toast.info('🎵 Generating WAV...');
                              try {
                                // Generate audio preview first
                                const response = await fetch(`${API}/generate-audio-preview`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ prompt: prompt.prompt, category: 'MusicAnalyzer', email: currentUser?.email || userEmail || getGuestEmail() })
                                });
                                if (response.status === 402) {
                                  if (currentUser) { setShowTokenExhausted(true); }
                                  else { toast.error('WAV downloads are a paid feature — upgrade to unlock!'); setShowPricing(true); }
                                  return;
                                }
                                const data = await response.json();
                                if (data.task_id) {
                                  // Poll for completion
                                  let attempts = 0;
                                  const pollInterval = setInterval(async () => {
                                    attempts++;
                                    if (attempts > 60) {
                                      clearInterval(pollInterval);
                                      toast.error('WAV generation timed out');
                                      return;
                                    }
                                    try {
                                      const statusRes = await fetch(`${API}/audio-preview-status/${data.task_id}`);
                                      const status = await statusRes.json();
                                      if (status.status === 'completed' && status.audio_url) {
                                        clearInterval(pollInterval);
                                        // Download the WAV
                                        const wavUrl = status.audio_url.startsWith('/') ? `${API_ORIGIN}${status.audio_url}` : status.audio_url;
                                        const audioRes = await fetch(wavUrl);
                                        const blob = await audioRes.blob();
                                        const filename = `soundlike_${prompt.dna_code || 'track'}_${Date.now()}.wav`;
                                        await downloadFileWithPicker(blob, filename, 'audio/wav', '📥 WAV downloaded!', wavUrl);
                                      } else if (status.status === 'failed') {
                                        clearInterval(pollInterval);
                                        toast.error('WAV generation failed');
                                      }
                                    } catch (e) {
                                      console.error('Poll error:', e);
                                    }
                                  }, 2000);
                                } else if (data.audio_url) {
                                  // Cached - download directly
                                  const cachedUrl = data.audio_url.startsWith('/') ? `${API_ORIGIN}${data.audio_url}` : data.audio_url;
                                  const audioRes = await fetch(cachedUrl);
                                  const blob = await audioRes.blob();
                                  const filename = `soundlike_${prompt.dna_code || 'track'}_${Date.now()}.wav`;
                                  await downloadFileWithPicker(blob, filename, 'audio/wav', '📥 WAV downloaded!', cachedUrl);
                                } else {
                                  toast.error('Failed to start WAV generation');
                                }
                              } catch (error) {
                                console.error('WAV download error:', error);
                                toast.error('Failed to generate WAV');
                              }
                            }}
                            style={{
                              padding: '8px 10px',
                              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                              border: 'none',
                              borderRadius: '6px',
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Download WAV"
                            data-testid={`soundlike-wav-${i}`}
                          >
                            <Save className="w-3 h-3" />
                          </button>
                          {/* MIDI Download Button */}
                          <button
                            onClick={() => generateMidiFromPrompt(prompt.prompt)}
                            disabled={isGeneratingMidi}
                            style={{
                              padding: '8px 10px',
                              background: isGeneratingMidi ? 'rgba(16,185,129,0.3)' : 'linear-gradient(135deg, #10b981, #059669)',
                              border: 'none',
                              borderRadius: '6px',
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              cursor: isGeneratingMidi ? 'wait' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                            title="Download MIDI"
                            data-testid={`soundlike-midi-${i}`}
                          >
                            {isGeneratingMidi ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Music className="w-3 h-3" />
                            )}
                          </button>
                          <button
                            onClick={() => applySpotifyPromptToBeat(prompt.prompt)}
                            style={{
                              padding: '8px 12px',
                              background: '#fbbf24',
                              border: 'none',
                              borderRadius: '6px',
                              color: '#000',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              cursor: 'pointer'
                            }}
                          >
                            Use
                          </button>
                          <button
                            onClick={() => {
                              universalCopy(prompt.prompt, 'Copied!');
                            }}
                            style={{
                              padding: '8px 12px',
                              background: 'rgba(255,255,255,0.1)',
                              border: '1px solid rgba(255,255,255,0.2)',
                              borderRadius: '6px',
                              color: '#fff',
                              fontSize: '0.75rem',
                              cursor: 'pointer'
                            }}
                          >
                            Copy
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}





        {/* DNA STEMS SECTION */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(159,71,255,0.15) 0%, rgba(147,112,219,0.1) 100%)',
          border: '2px solid rgba(159,71,255,0.4)',
          borderRadius: '16px',
          padding: '24px',
          marginTop: '24px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            marginBottom: '20px' 
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <StepBadge n={5} />
              <span style={{ fontSize: '1.5rem' }}>🔊</span>
              <span style={{ color: '#9F47FF', fontWeight: 700, fontSize: '1.2rem' }}>AI STEM SEPARATION</span>
              <span style={{ 
                background: 'linear-gradient(90deg, #9F47FF, #16a34a)', 
                color: '#fff', 
                padding: '2px 10px', 
                borderRadius: '4px', 
                fontSize: '0.7rem', 
                fontWeight: 600 
              }}>6 STEMS • DEMUCS AI</span>
            </div>
            {/* Clear Stems Button */}
            <button
              onClick={() => {
                setStemsFile(null);
                setStemsAudioUrl('');
                setSeparatedStems(null);
                setStemsTaskId(null);
                toast.success('🧹 Stems cleared!');
              }}
              style={{
                padding: '8px 14px',
                background: 'rgba(239, 68, 68, 0.15)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '0.8rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              data-testid="clear-stems-btn"
            >
              <Trash2 className="w-4 h-4" />
              Clear
            </button>
          </div>
          
          <p style={{ 
            textAlign: 'center', 
            color: 'rgba(255,255,255,0.6)', 
            fontSize: '0.85rem', 
            marginBottom: '16px' 
          }}>
            Powered by Meta's Demucs AI • Separates: Vocals, Drums, Bass, Guitar, Piano, Other
          </p>
          
          {/* Drag & Drop Zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setStemsDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); setStemsDragActive(false); }}
            onDrop={handleStemsDrop}
            onClick={() => document.getElementById('stems-file-input').click()}
            style={{
              border: `2px dashed ${stemsDragActive ? '#9F47FF' : stemsFile ? '#9F47FF' : 'rgba(159,71,255,0.4)'}`,
              borderRadius: '16px',
              padding: '40px 24px',
              textAlign: 'center',
              cursor: 'pointer',
              background: stemsDragActive 
                ? 'rgba(159,71,255,0.15)' 
                : stemsFile 
                  ? 'rgba(159,71,255,0.1)' 
                  : 'rgba(0,0,0,0.3)',
              transition: 'all 0.2s',
              marginBottom: '16px'
            }}
            data-testid="stems-dropzone"
          >
            <input
              type="file"
              id="stems-file-input"
              accept=".mp3,.wav,.m4a,.flac,.ogg,.aac,.mp4,audio/*"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  if (file.size > 50 * 1024 * 1024) {
                    toast.error("File too large! Maximum size is 50MB");
                    return;
                  }
                  setStemsFile(file);
                  setStemsAudioUrl("");
                  toast.success(`File loaded: ${file.name}`);
                }
              }}
            />
            
            {stemsFile ? (
              <div>
                <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🎵</div>
                <div style={{ color: '#9F47FF', fontWeight: 600, fontSize: '1.1rem', marginBottom: '8px' }}>
                  {stemsFile.name}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                  {(stemsFile.size / 1024 / 1024).toFixed(2)} MB
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setStemsFile(null); }}
                  style={{
                    marginTop: '12px',
                    padding: '6px 16px',
                    background: 'rgba(255,68,68,0.2)',
                    border: '1px solid rgba(255,68,68,0.4)',
                    borderRadius: '8px',
                    color: '#ff4444',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  Remove File
                </button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '3rem', marginBottom: '12px' }}>
                  {stemsDragActive ? '📥' : '🎧'}
                </div>
                <div style={{ color: '#9F47FF', fontWeight: 600, fontSize: '1.1rem', marginBottom: '8px' }}>
                  {stemsDragActive ? 'Drop your audio file here!' : 'Drag & Drop Audio File'}
                </div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                  or click to browse • MP3, WAV, M4A, FLAC (max 50MB)
                </div>
              </div>
            )}
          </div>

          {/* OR Divider */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px', 
            marginBottom: '16px',
            color: 'rgba(255,255,255,0.3)'
          }}>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.2)' }} />
            <span style={{ fontSize: '0.85rem' }}>OR PASTE URL</span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.2)' }} />
          </div>
          
          {/* Audio URL Input */}
          <div style={{ marginBottom: '16px' }}>
            <input
              type="url"
              placeholder="Paste URL to audio file (MP3, WAV, etc.)"
              value={stemsAudioUrl}
              onChange={e => { setStemsAudioUrl(e.target.value); setStemsFile(null); }}
              style={{
                width: '100%',
                padding: '14px 18px',
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid rgba(159,71,255,0.3)',
                borderRadius: '12px',
                color: '#fff',
                fontSize: '0.95rem',
                outline: 'none'
              }}
              data-testid="stems-url-input"
            />
          </div>
          
          {/* Separate Button */}
          <button
            onClick={separateStems}
            disabled={isSeparatingStems || (!stemsFile && !stemsAudioUrl.trim())}
            style={{
              width: '100%',
              padding: '16px 24px',
              background: isSeparatingStems 
                ? 'rgba(159,71,255,0.3)' 
                : 'linear-gradient(135deg, #9F47FF 0%, #9370db 100%)',
              border: 'none',
              borderRadius: '12px',
              color: '#fff',
              fontSize: '1.1rem',
              fontWeight: 700,
              cursor: isSeparatingStems ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              transition: 'all 0.2s',
              opacity: (!stemsFile && !stemsAudioUrl.trim()) ? 0.5 : 1
            }}
            data-testid="separate-stems-btn"
          >
            {isSeparatingStems ? (
              <>
                <div style={{ 
                  width: '20px', 
                  height: '20px', 
                  border: '3px solid rgba(255,255,255,0.3)',
                  borderTop: '3px solid #fff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Separating Stems... (2-5 min)
              </>
            ) : (
              <>
                <span style={{ fontSize: '1.2rem' }}>🔊</span>
                Separate into Vocals, Drums, Bass & More
              </>
            )}
          </button>
          
          <p style={{ 
            textAlign: 'center', 
            color: 'rgba(255,255,255,0.4)', 
            fontSize: '0.75rem', 
            marginTop: '12px',
            marginBottom: 0
          }}>
            Powered by Demucs AI • ~$0.10 per separation
          </p>
          
          {/* Separated Stems Output */}
          {separatedStems && (
            <div style={{ marginTop: '20px' }}>
              {/* Download All as ZIP Button */}
              <div style={{ marginBottom: '16px', textAlign: 'center' }}>
                <a
                  href={`${API}/stems-download-zip/${stemsTaskId}`}
                  download
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '14px 28px',
                    background: 'linear-gradient(135deg, #9F47FF 0%, #9370db 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#fff',
                    fontSize: '1rem',
                    fontWeight: 700,
                    textDecoration: 'none',
                    cursor: 'pointer'
                  }}
                  data-testid="download-stems-zip"
                >
                  <Download className="w-5 h-5" />
                  Download All Stems as ZIP
                </a>
              </div>
              
              {/* Individual Stems Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '12px'
              }}>
              {Object.entries(separatedStems).map(([name, url]) => (
                <div key={name} style={{
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(159,71,255,0.2)',
                  borderRadius: '10px',
                  padding: '12px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '8px'
                  }}>
                    <span style={{ 
                      color: '#9F47FF', 
                      fontWeight: 600, 
                      fontSize: '0.85rem',
                      textTransform: 'capitalize'
                    }}>
                      {name === 'other' ? '🎸 Other' : name === 'vocals' ? '🎤 Vocals' : name === 'drums' ? '🥁 Drums' : '🎸 Bass'}
                    </span>
                    <button
                      onClick={async () => {
                        try {
                          toast.info(`Downloading ${name} stem...`);
                          const stemUrl = url.startsWith('/') ? `${API_ORIGIN}${url}` : url;
                          const resp = await fetch(stemUrl);
                          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                          const blob = await resp.blob();
                          await downloadFileWithPicker(blob, `sounddna_${name}.mp3`, 'audio/mpeg', `📥 ${name} stem saved!`, stemUrl);
                        } catch (err) {
                          console.error('Stem download failed:', err);
                          toast.error(`Failed to download ${name} stem. Try again!`);
                        }
                      }}
                      data-testid={`download-stem-${name}`}
                      style={{
                        padding: '4px 10px',
                        background: 'rgba(159,71,255,0.2)',
                        border: '1px solid rgba(159,71,255,0.4)',
                        borderRadius: '6px',
                        color: '#9F47FF',
                        fontSize: '0.7rem',
                        cursor: 'pointer'
                      }}
                    >
                      Download
                    </button>
                  </div>
                  <audio preload="metadata" 
                    controls 
                    src={url} 
                    style={{ width: '100%', height: '32px' }}
                  />
                </div>
              ))}
              </div>
            </div>
          )}
        </div>


        {/* Controls */}
        <div className="control-deck">
          <div className="control-section">
            <label className="control-label"><Layers className="w-4 h-4" /> Target Platforms {subscription.plan === 'free' && <span className="limit-tag">Pro: All 6</span>}</label>
            <div className="platform-buttons">
              <button className={`platform-btn ${selectedPlatforms.includes("all") ? 'active' : ''}`} onClick={() => togglePlatform("all")} disabled={subscription.plan === 'free'}><Sparkles className="w-4 h-4" /> ALL</button>
              {Object.entries(PLATFORMS).map(([key, p]) => (
                <button key={key} className={`platform-btn ${selectedPlatforms.includes(key) ? 'active' : ''} ${subscription.plan === 'free' && key !== 'suno' ? 'locked' : ''}`} onClick={() => togglePlatform(key)} disabled={subscription.plan === 'free' && key !== 'suno'}>
                  {p.icon} {p.name.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>

          {/* Genre Selector */}
          <div className="control-section">
            <div className="control-label-row">
              <label className="control-label"><Music2 className="w-4 h-4" /> Target Genre</label>
              <button 
                className="genre-guide-btn" 
                onClick={() => setShowGenreGuide(true)}
                data-testid="genre-guide-btn"
              >
                <HelpCircle className="w-4 h-4" /> Guide
              </button>
            </div>
            
            {/* ALL GENRES Special Button */}
            <button
              onClick={() => {
                setSelectedGenre("all");
                toast.success("🌈 ALL GENRES MODE! 1M+ unique style combinations!");
              }}
              style={{
                width: '100%',
                padding: '16px',
                marginBottom: '16px',
                background: selectedGenre === 'all' 
                  ? 'linear-gradient(135deg, #9F47FF, #ff9500, #9F47FF, #00f0ff, #a855f7, #9F47FF)' 
                  : 'linear-gradient(135deg, rgba(255,0,102,0.2), rgba(255,149,0,0.2), rgba(159,71,255,0.2))',
                backgroundSize: selectedGenre === 'all' ? '300% 300%' : '100% 100%',
                animation: selectedGenre === 'all' ? 'gradient-shift 3s ease infinite' : 'none',
                border: selectedGenre === 'all' ? '3px solid #fff' : '2px solid rgba(255,0,102,0.5)',
                borderRadius: '16px',
                color: '#fff',
                fontSize: '1.1rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                boxShadow: selectedGenre === 'all' 
                  ? '0 8px 30px rgba(255,0,102,0.5), 0 0 60px rgba(159,71,255,0.3)' 
                  : '0 4px 20px rgba(255,0,102,0.2)',
                transition: 'all 0.3s ease'
              }}
              data-testid="all-genres-btn"
            >
              <span style={{ fontSize: '1.5rem' }}>🌈</span>
              <span>ALL GENRES - 1 MILLION+ COMBINATIONS</span>
              {selectedGenre === 'all' && <span style={{ fontSize: '1.2rem' }}>✓</span>}
            </button>
            
            <div className="genre-grid" data-testid="genre-selector">
              {GENRES.filter(g => g.value !== 'all').map(g => (
                <button 
                  key={g.value} 
                  className={`genre-btn ${selectedGenre === g.value ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedGenre(g.value);
                    toast.success(`🎵 ${g.label} genre selected!`, { duration: 2000 });
                  }}
                  style={{ 
                    '--genre-color': g.color,
                    borderColor: selectedGenre === g.value ? g.color : 'transparent',
                    background: selectedGenre === g.value ? `${g.color}15` : 'rgba(255,255,255,0.05)'
                  }}
                  data-testid={`genre-${g.value}`}
                >
                  <span style={{ color: selectedGenre === g.value ? g.color : 'inherit' }}>{g.label}</span>
                  {g.bpm && selectedGenre === g.value && <small className="genre-bpm">{g.bpm} BPM</small>}
                </button>
              ))}
            </div>
          </div>

          {/* Artist Style Selector */}
          <div className="control-section">
            <label className="control-label"><Sparkles className="w-4 h-4" /> Artist Style <span className="style-hint">Sound inspired by</span></label>
            
            {/* Artist Name Input */}
            <div className="artist-name-input-container" style={{ marginBottom: '16px' }}>
              <input 
                type="text"
                placeholder="Type any artist name for style inspiration"
                value={artistName}
                onChange={e => setArtistName(e.target.value)}
                className="artist-name-input"
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '2px solid rgba(204, 255, 0, 0.3)',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                data-testid="artist-name-input"
              />
            </div>
            
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', marginBottom: '12px' }}>Or select a style:</p>
            <div className="artist-grid" data-testid="artist-selector">
              {ARTIST_STYLES.map(a => (
                <button 
                  key={a.value} 
                  className={`artist-btn ${selectedArtist === a.value ? 'active' : ''} ${a.genre || ''}`}
                  onClick={() => {
                    setSelectedArtist(a.value);
                    if (a.value !== 'none') {
                      toast.success(`🎤 ${a.label} style selected!`, { duration: 2000 });
                    }
                  }}
                  style={{ 
                    '--artist-color': a.color,
                    borderColor: selectedArtist === a.value ? a.color : 'transparent',
                    background: selectedArtist === a.value ? `${a.color}20` : 'rgba(255,255,255,0.03)'
                  }}
                  data-testid={`artist-${a.value}`}
                >
                  <span style={{ color: selectedArtist === a.value ? a.color : 'inherit' }}>{a.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 2026 Sounds Toggle */}
          <div className="toggle-2026-section">
            <button 
              className={`toggle-2026-btn ${enable2026Sounds ? 'active' : ''}`}
              onClick={() => setEnable2026Sounds(!enable2026Sounds)}
              data-testid="toggle-2026"
            >
              <Sparkles className="w-4 h-4" />
              <span>2026 Sounds</span>
              <span className={`toggle-indicator ${enable2026Sounds ? 'on' : 'off'}`}>
                {enable2026Sounds ? 'ON' : 'OFF'}
              </span>
            </button>
            <span className="toggle-hint">AI textures, neural synthesis, spatial audio & cutting-edge production</span>
          </div>

          <div className="control-row">
            <div className="style-mode-container">
              <label className="control-label">Style Mode</label>
              <div 
                className="style-dropdown-trigger"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowStyleDropdown(!showStyleDropdown); }}
                onTouchEnd={(e) => { e.preventDefault(); setShowStyleDropdown(!showStyleDropdown); }}
                data-testid="style-mode-trigger"
                style={{ cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}
              >
                <span>{STYLE_MODES.find(m => m.value === styleMode)?.label || 'Sound Clone'}</span>
                <span className="dropdown-arrow">{showStyleDropdown ? '▲' : '▼'}</span>
              </div>
              {showStyleDropdown && (
                <div className="style-dropdown-menu" data-testid="style-mode-menu">
                  {STYLE_MODES.map(m => (
                    <div 
                      key={m.value}
                      className={`style-dropdown-item ${styleMode === m.value ? 'active' : ''}`}
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setStyleMode(m.value); setShowStyleDropdown(false); toast.success(`Style: ${m.label}`); }}
                      onTouchEnd={(e) => { e.preventDefault(); setStyleMode(m.value); setShowStyleDropdown(false); toast.success(`Style: ${m.label}`); }}
                      style={{ cursor: 'pointer' }}
                    >
                      {m.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="count-box">
              <div className="count-value">{subscription.plan === 'free' ? Math.min(promptCount, 20) : promptCount}</div>
              <div className="count-label">Prompts {subscription.plan === 'free' ? <span>(max 20)</span> : <span style={{color: '#9F47FF'}}>(UNLIMITED)</span>}</div>
            </div>
          </div>

          <div className="slider-container">
            <Slider value={[promptCount]} onValueChange={v => setPromptCount(v[0])} min={20} max={subscription.plan === 'free' ? 20 : 1000} step={10} />
            <div className="slider-labels"><span>20</span><span>{subscription.plan === 'free' ? '20 (Free limit)' : '1000 (Unlimited)'}</span></div>
          </div>
        </div>

        {/* Generate */}
        <button className="generate-btn" onClick={handleGenerate} disabled={(!uploadedFile && !styleDescription.trim() && selectedGenre === 'auto' && selectedArtist === 'none' && !artistName.trim()) || isAnalyzing}>
          {isAnalyzing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Zap className="w-5 h-5" />}
          {isAnalyzing ? "Generating..." : `Generate Prompts`}
        </button>

        {/* Results */}
        {result && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="results-section">
            <div className="results-header">
              <h2 className="results-title"><Dna className="w-6 h-6" />{result.total_prompts} Style Prompts</h2>
              <div className="results-badges">
                {result.platforms.map(p => <span key={p} className="platform-badge" style={{ background: PLATFORMS[p]?.color + '20', color: PLATFORMS[p]?.color }}>{PLATFORMS[p]?.icon} {PLATFORMS[p]?.name}</span>)}
              </div>
            </div>

            <div className="filter-bar">
              <div className="filter-group">
                <Filter className="w-4 h-4" />
                <select className="filter-select" value={platformFilter} onChange={e => { setPlatformFilter(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All Platforms</option>
                  {result.platforms.map(p => <option key={p} value={p}>{PLATFORMS[p]?.name}</option>)}
                </select>
              </div>
              <div className="search-group">
                <Search className="w-4 h-4" />
                <input type="text" className="search-input" value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }} placeholder="Search..." />
              </div>
            </div>

            <div className="prompts-bento">{paginatedPrompts.map((p, i) => <PromptCard key={p.id} prompt={p} index={(currentPage - 1) * perPage + i} preview={cardPreviews[`style-${p.id}`]} onRenderPreview={() => startCardPreview(p.prompt, `style-${p.id}`)} onUseForSong={(promptText) => { universalCopy(promptText, "Prompt copied! Tap & hold to paste"); }} onGenerateDrumPattern={generateDrumFromStylePrompt} onGenerateMidi={generateMidiFromPrompt} isGenerating={false} isGeneratingMidi={isGeneratingMidi} shareOrDownloadBlob={shareOrDownloadBlob} />)}</div>

            {totalPages > 1 && (
              <div className="pagination">
                <button className="page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>←</button>
                <span className="page-info">{currentPage} / {totalPages}</span>
                <button className="page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>→</button>
              </div>
            )}

            {/* Audio DNA Panel - BELOW the prompts */}
            <div className="audio-dna-panel" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px', background: 'rgba(204, 255, 0, 0.05)', borderRadius: '16px', padding: '20px', marginTop: '24px', marginBottom: '20px', border: '1px solid rgba(204, 255, 0, 0.2)' }}>
              <div className="dna-stat" style={{ textAlign: 'center' }}>
                <span className="dna-stat-label" style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '4px' }}>TEMPO</span>
                <span className="dna-stat-value" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#9F47FF' }}>{Math.round(result.audio_dna.bpm)} BPM</span>
              </div>
              <div className="dna-stat" style={{ textAlign: 'center' }}>
                <span className="dna-stat-label" style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '4px' }}>KEY</span>
                <span className="dna-stat-value" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>{result.audio_dna.key} {result.audio_dna.mode}</span>
              </div>
              <div className="dna-stat" style={{ textAlign: 'center' }}>
                <span className="dna-stat-label" style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '4px' }}>GENRE</span>
                <span className="dna-stat-value" style={{ fontSize: '1.5rem', fontWeight: 700, color: '#9F47FF' }}>{result.audio_dna.detected_genre?.toUpperCase()}</span>
              </div>
              <div className="dna-stat" style={{ textAlign: 'center' }}>
                <span className="dna-stat-label" style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '4px' }}>ENERGY</span>
                <span className="dna-stat-value" style={{ fontSize: '1.2rem', fontWeight: 600, color: '#fff' }}>{result.audio_dna.energy?.toUpperCase()}</span>
              </div>
              <div className="dna-stat" style={{ textAlign: 'center' }}>
                <span className="dna-stat-label" style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: '4px' }}>MOOD</span>
                <span className="dna-stat-value" style={{ fontSize: '1.2rem', fontWeight: 600, color: '#fff' }}>{result.audio_dna.mood?.toUpperCase()}</span>
              </div>
            </div>

            {/* Genre Reference Guide - Compact & Attractive */}
            <div style={{ 
              background: 'linear-gradient(135deg, rgba(15,15,26,0.95) 0%, rgba(26,26,46,0.9) 100%)', 
              border: '1px solid rgba(204, 255, 0, 0.15)', 
              borderRadius: '16px', 
              padding: '20px', 
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
                <span style={{ fontSize: '1.2rem' }}>🎵</span>
                <span style={{ color: '#9F47FF', fontWeight: 600, fontSize: '0.9rem', letterSpacing: '1px' }}>GENRE REFERENCE</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                {GENRES.filter(g => g.value !== 'auto').map(g => (
                  <div 
                    key={g.value}
                    onClick={() => { setSelectedGenre(g.value); toast.success(`${g.icon} ${g.label} selected!`); }}
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: result.audio_dna.detected_genre?.toLowerCase() === g.value 
                        ? `linear-gradient(135deg, ${g.color}40, ${g.color}20)` 
                        : 'rgba(255,255,255,0.05)',
                      border: result.audio_dna.detected_genre?.toLowerCase() === g.value 
                        ? `2px solid ${g.color}` 
                        : '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '20px',
                      padding: '8px 14px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: result.audio_dna.detected_genre?.toLowerCase() === g.value 
                        ? `0 4px 15px ${g.color}30` 
                        : 'none'
                    }}
                  >
                    <span style={{ fontSize: '1.1rem' }}>{g.icon}</span>
                    <span style={{ 
                      fontWeight: 600, 
                      fontSize: '0.8rem', 
                      color: result.audio_dna.detected_genre?.toLowerCase() === g.value ? g.color : '#fff' 
                    }}>{g.label}</span>
                    <span style={{ 
                      fontSize: '0.65rem', 
                      color: 'rgba(255,255,255,0.4)',
                      background: 'rgba(0,0,0,0.3)',
                      padding: '2px 6px',
                      borderRadius: '8px'
                    }}>{g.bpm}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="export-bar">
              <button className="export-btn primary" onClick={copyAllPrompts}><Copy className="w-4 h-4" /> Copy All</button>
              <button className="export-btn" onClick={sharePrompts}><Share2 className="w-4 h-4" /> Share</button>
              <button className="export-btn" onClick={exportTxt}><Download className="w-4 h-4" /> Save As</button>
              <button className="export-btn" onClick={reset}><RotateCcw className="w-4 h-4" /> Reset</button>
            </div>
          </motion.div>
        )}


        {/* Generated Songs List */}
        {generatedSongs.length > 0 && (
          <div style={{
            background: 'rgba(159,71,255,0.05)',
            border: '1px solid rgba(159,71,255,0.2)',
            borderRadius: '16px',
            padding: '20px',
            marginTop: '16px'
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '8px', 
              marginBottom: '16px' 
            }}>
              <span style={{ fontSize: '1.2rem' }}>🎧</span>
              <span style={{ color: '#9F47FF', fontWeight: 600 }}>Your Generated Songs ({generatedSongs.length})</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {generatedSongs.map((song, i) => (
                <div 
                  key={song.id || i}
                  style={{
                    background: 'rgba(0,0,0,0.3)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}
                >
                  {song.image_url && (
                    <img 
                      src={song.image_url} 
                      alt={song.title} 
                      style={{ width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover' }}
                    />
                  )}
                  <div style={{ flex: 1 }}>
                    <p style={{ color: '#fff', fontWeight: 600, margin: 0 }}>{song.title}</p>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem', margin: '4px 0 0' }}>
                      {song.duration ? `${Math.round(song.duration)}s` : 'Generated'}
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => { setCurrentSong(song); setShowSongModal(true); }}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(159,71,255,0.2)',
                        border: '1px solid rgba(159,71,255,0.4)',
                        borderRadius: '8px',
                        color: '#9F47FF',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      ▶ Play
                    </button>
                    <button
                      onClick={() => downloadSong(song.audio_url, `${song.title || 'song'}.wav`)}
                      style={{
                        padding: '8px 16px',
                        background: 'rgba(255,0,102,0.2)',
                        border: '1px solid rgba(255,0,102,0.4)',
                        borderRadius: '8px',
                        color: '#9F47FF',
                        fontWeight: 600,
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      ↓ MP3
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Copy Fallback Modal - Shows when clipboard is blocked */}
        {/* Simple Copy Fallback for iPhone */}
        {showCopyFallback && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.97)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }} onClick={() => setShowCopyFallback(false)}>
            <div style={{
              background: '#111',
              borderRadius: '24px',
              padding: '28px',
              maxWidth: '100%',
              width: '100%',
              border: '4px solid #9F47FF'
            }} onClick={e => e.stopPropagation()}>
              
              {/* Simple 2-Step Instructions */}
              <div style={{
                background: '#9F47FF',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>
                <p style={{ color: '#000', fontSize: '1.4rem', fontWeight: 800, margin: '0 0 16px 0' }}>
                  📱 2 SIMPLE STEPS
                </p>
                <p style={{ color: '#000', fontSize: '1.2rem', fontWeight: 700, margin: '0 0 10px 0' }}>
                  ① TAP text below → Select All → Copy
                </p>
                <p style={{ color: '#000', fontSize: '1.2rem', fontWeight: 700, margin: '0' }}>
                  ② TAP TAP & HOLD in Music AI HOLD in Suno → Paste
                </p>
              </div>
              
              {/* Text area for copying */}
              <textarea
                value={copyFallbackText}
                readOnly
                onFocus={(e) => {
                  e.target.select();
                  e.target.setSelectionRange(0, e.target.value.length);
                }}
                onClick={(e) => {
                  e.target.focus();
                  e.target.select();
                  e.target.setSelectionRange(0, e.target.value.length);
                }}
                onTouchEnd={(e) => {
                  e.target.focus();
                  setTimeout(() => {
                    e.target.select();
                    e.target.setSelectionRange(0, e.target.value.length);
                  }, 100);
                }}
                style={{
                  width: '100%',
                  height: '150px',
                  padding: '16px',
                  background: '#000',
                  border: '3px solid #9F47FF',
                  borderRadius: '12px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontFamily: 'inherit',
                  resize: 'none',
                  WebkitUserSelect: 'text',
                  userSelect: 'text',
                  lineHeight: '1.5'
                }}
              />
              
              {/* Platform Selection Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', textAlign: 'center', margin: '0 0 8px 0' }}>
                  Open your favorite Music AI:
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={() => {
                      setShowCopyFallback(false);
                      toast.success('✅ Open Suno and paste!');
                    }}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'linear-gradient(90deg, #9F47FF, #059669)',
                      border: 'none',
                      borderRadius: '12px',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    SUNO
                  </button>
                  <button
                    onClick={() => {
                      setShowCopyFallback(false);
                      window.open('https://udio.com', '_blank');
                    }}
                    style={{
                      flex: 1,
                      padding: '14px',
                      background: 'linear-gradient(90deg, #9F47FF, #6366f1)',
                      border: 'none',
                      borderRadius: '12px',
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: '1rem',
                      cursor: 'pointer'
                    }}
                  >
                    UDIO
                  </button>
                </div>
                <button
                  onClick={() => setShowCopyFallback(false)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    color: 'rgba(255,255,255,0.7)',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  Close (I'll paste manually)
                </button>
              </div>
            </div>
          </div>
        )}

        {/* DNA Marketplace Modal */}
        {showDNAMarketplace && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }} onClick={() => setShowDNAMarketplace(false)}>
            <div style={{
              background: 'linear-gradient(180deg, #1a1a2e, #0a0a0a)',
              borderRadius: '16px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'auto',
              border: '2px solid rgba(245, 158, 11, 0.3)'
            }} onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '20px',
                borderBottom: '1px solid rgba(255,255,255,0.1)'
              }}>
                <h2 style={{ margin: 0, color: '#f59e0b', fontSize: '1.3rem' }}>
                  🏪 DNA MARKETPLACE
                </h2>
                <button
                  onClick={() => setShowDNAMarketplace(false)}
                  style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' }}
                >×</button>
              </div>
              
              {/* Tabs */}
              <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <button
                  onClick={() => { setDnaMarketplaceTab("browse"); fetchMarketplaceDNAs(); }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: dnaMarketplaceTab === "browse" ? 'rgba(245, 158, 11, 0.2)' : 'transparent',
                    border: 'none',
                    borderBottom: dnaMarketplaceTab === "browse" ? '2px solid #f59e0b' : '2px solid transparent',
                    color: dnaMarketplaceTab === "browse" ? '#f59e0b' : '#888',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  🌐 Browse Community
                </button>
                <button
                  onClick={() => { setDnaMarketplaceTab("my-library"); fetchMyDNAs(); }}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: dnaMarketplaceTab === "my-library" ? 'rgba(16, 185, 129, 0.2)' : 'transparent',
                    border: 'none',
                    borderBottom: dnaMarketplaceTab === "my-library" ? '2px solid #9F47FF' : '2px solid transparent',
                    color: dnaMarketplaceTab === "my-library" ? '#9F47FF' : '#888',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  📁 My Library
                </button>
              </div>
              
              {/* Content */}
              <div style={{ padding: '16px', maxHeight: '60vh', overflow: 'auto' }}>
                {dnaMarketplaceTab === "browse" ? (
                  marketplaceDNAs.length > 0 ? (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {marketplaceDNAs.map((dna, idx) => (
                        <div key={idx} style={{
                          background: 'rgba(255,255,255,0.05)',
                          borderRadius: '12px',
                          padding: '16px',
                          border: '1px solid rgba(255,255,255,0.1)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <h3 style={{ margin: '0 0 6px 0', color: '#fff', fontSize: '1rem' }}>{dna.name}</h3>
                              <p style={{ margin: '0 0 8px 0', color: '#888', fontSize: '0.8rem' }}>{dna.style_prompt}</p>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {dna.bpm && dna.bpm !== "auto" && (
                                  <span style={{ background: 'rgba(159,71,255,0.2)', color: '#9F47FF', padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem' }}>
                                    {dna.bpm} BPM
                                  </span>
                                )}
                                {dna.key && dna.key !== "auto" && (
                                  <span style={{ background: 'rgba(168, 85, 247, 0.2)', color: '#a855f7', padding: '2px 8px', borderRadius: '4px', fontSize: '0.7rem' }}>
                                    {dna.key} {dna.mode}
                                  </span>
                                )}
                                {dna.tags && dna.tags.map((tag, i) => (
                                  <span key={i} style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem' }}>
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                              <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '0.7rem' }}>
                                ⬇️ {dna.downloads || 0} downloads • ❤️ {dna.likes || 0} likes
                              </p>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              {/* Audio Preview Button */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  generateAudioPreview(dna.style_prompt, dna.category || 'General', `mp-${idx}`);
                                }}
                                disabled={audioPreviewLoading === `mp-${idx}`}
                                style={{
                                  padding: '8px 12px',
                                  background: audioPreviewPlaying === `mp-${idx}` 
                                    ? 'rgba(239, 68, 68, 0.3)' 
                                    : audioPreviewLoading === `mp-${idx}`
                                      ? 'rgba(234, 179, 8, 0.3)'
                                      : 'rgba(16, 185, 129, 0.2)',
                                  border: `1px solid ${audioPreviewPlaying === `mp-${idx}` 
                                    ? 'rgba(239, 68, 68, 0.5)' 
                                    : audioPreviewLoading === `mp-${idx}`
                                      ? 'rgba(234, 179, 8, 0.5)'
                                      : 'rgba(16, 185, 129, 0.4)'}`,
                                  borderRadius: '6px',
                                  color: audioPreviewPlaying === `mp-${idx}` 
                                    ? '#ef4444' 
                                    : audioPreviewLoading === `mp-${idx}`
                                      ? '#eab308'
                                      : '#10b981',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  cursor: audioPreviewLoading === `mp-${idx}` ? 'wait' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                {audioPreviewLoading === `mp-${idx}` ? (
                                  <><Loader2 className="w-3 h-3 animate-spin" /></>
                                ) : audioPreviewPlaying === `mp-${idx}` ? (
                                  <><VolumeX className="w-3 h-3" /></>
                                ) : (
                                  <><Headphones className="w-3 h-3" /></>
                                )}
                              </button>
                              <button
                                onClick={() => loadDNA(dna)}
                                style={{
                                  padding: '8px 16px',
                                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                                  border: 'none',
                                  borderRadius: '6px',
                                  color: '#000',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                USE
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <p style={{ fontSize: '2rem', margin: '0 0 12px 0' }}>🏪</p>
                      <p>No DNAs in marketplace yet. Be the first to share!</p>
                    </div>
                  )
                ) : (
                  myDNAs.length > 0 ? (
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {myDNAs.map((dna, idx) => (
                        <div key={idx} style={{
                          background: 'rgba(16, 185, 129, 0.1)',
                          borderRadius: '12px',
                          padding: '16px',
                          border: '1px solid rgba(16, 185, 129, 0.2)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <h3 style={{ margin: '0 0 6px 0', color: '#9F47FF', fontSize: '1rem' }}>{dna.name}</h3>
                              <p style={{ margin: '0 0 8px 0', color: '#888', fontSize: '0.8rem' }}>{dna.style_prompt}</p>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {dna.tags && dna.tags.map((tag, i) => (
                                  <span key={i} style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem' }}>
                                    #{tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <button
                              onClick={() => loadDNA(dna)}
                              style={{
                                padding: '8px 16px',
                                background: 'linear-gradient(135deg, #9F47FF, #059669)',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#fff',
                                fontWeight: 700,
                                cursor: 'pointer'
                              }}
                            >
                              LOAD
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <p style={{ fontSize: '2rem', margin: '0 0 12px 0' }}>📁</p>
                      <p>No saved DNAs yet. Create one and save it!</p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        )}

        {/* iPhone Tutorial Modal - VIDEO + LANGUAGE AUDIO */}
        {showIphoneTutorial && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.98)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}>
            <div style={{
              background: '#0a0a0a',
              borderRadius: '24px',
              padding: '20px',
              maxWidth: '420px',
              width: '100%',
              border: '2px solid rgba(16, 185, 129, 0.3)',
              textAlign: 'center'
            }}>
              {/* Header */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: '12px'
              }}>
                <span style={{ 
                  color: '#10b981', 
                  fontSize: '0.7rem', 
                  fontWeight: 700,
                  background: 'rgba(16, 185, 129, 0.2)',
                  padding: '4px 10px',
                  borderRadius: '20px'
                }}>
                  {tutorialStep}/14
                </span>
                {tutorialAudioPlaying && (
                  <span style={{ color: '#10b981', fontSize: '1rem' }}>🔊</span>
                )}
                <button 
                  onClick={() => {
                    if (tutorialAudioRef.current) tutorialAudioRef.current.pause();
                    completeTutorial();
                  }}
                  style={{ 
                    background: 'none', 
                    border: 'none', 
                    color: 'rgba(255,255,255,0.5)', 
                    cursor: 'pointer',
                    fontSize: '1.2rem'
                  }}
                >×</button>
              </div>

              {/* Video */}
              <div style={{
                borderRadius: '12px',
                overflow: 'hidden',
                marginBottom: '16px',
                background: '#000'
              }}>
                <video 
                  autoPlay
                  muted
                  loop
                  playsInline
                  style={{ 
                    width: '100%', 
                    height: 'auto',
                    maxHeight: '200px',
                    objectFit: 'contain'
                  }}
                >
                  <source src="https://customer-assets.emergentagent.com/job_dragdrop-prompts/artifacts/ksu7lqab_SOUND%20DNA.m4v" type="video/mp4" />
                </video>
              </div>

              {/* Current Language Display */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2), rgba(0, 212, 255, 0.1))',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '16px',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '8px' }}>
                  {tutorialLanguages[tutorialStep]?.flag || '🌍'}
                </div>
                <h2 style={{ 
                  color: '#fff', 
                  fontSize: '1.5rem', 
                  fontWeight: 800, 
                  margin: '0 0 8px 0'
                }}>
                  {tutorialLanguages[tutorialStep]?.name || 'MANNIS'}
                </h2>
                <p style={{ 
                  color: 'rgba(255,255,255,0.7)', 
                  fontSize: '0.9rem', 
                  margin: 0,
                  fontStyle: 'italic',
                  minHeight: '44px'
                }}>
                  "{tutorialNarrations[tutorialStep]}"
                </p>
              </div>

              {/* Progress Dots */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '16px' }}>
                {[1,2,3,4,5,6,7,8,9,10,11,12,13,14].map(step => (
                  <div key={step} style={{
                    width: tutorialStep === step ? '16px' : '6px',
                    height: '6px',
                    borderRadius: '3px',
                    background: tutorialStep >= step 
                      ? 'linear-gradient(90deg, #10b981, #00d4ff)' 
                      : 'rgba(255,255,255,0.2)',
                    transition: 'all 0.3s'
                  }} />
                ))}
              </div>

              {/* Controls */}
              {tutorialStep === 14 ? (
                <button
                  onClick={completeTutorial}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'linear-gradient(90deg, #10b981, #00d4ff)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '1rem',
                    cursor: 'pointer'
                  }}
                >
                  🚀 Start Creating
                </button>
              ) : (
                <button
                  onClick={() => setTutorialStep(14)}
                  style={{
                    padding: '10px 20px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '0.8rem',
                    cursor: 'pointer'
                  }}
                >
                  Skip →
                </button>
              )}
            </div>
          </div>
        )}

        {/* Song Structure Builder Modal */}
        {showStructureBuilder && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px',
            overflowY: 'auto'
          }} onClick={() => setShowStructureBuilder(false)}>
            <div style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '700px',
              width: '100%',
              border: '2px solid #f59e0b',
              maxHeight: '90vh',
              overflowY: 'auto'
            }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ marginBottom: '20px' }}>
                <h2 style={{ color: '#f59e0b', margin: '0 0 8px 0', fontSize: '1.4rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  🎼 Song Structure Builder
                </h2>
                <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: '0.85rem' }}>
                  Build complete Suno prompts with vocal harmonies and song sections
                </p>
              </div>

              {/* Base Style Input with Library Browser */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <label style={{ color: '#9F47FF', fontWeight: 700, fontSize: '0.9rem' }}>
                    Base Style Prompt
                  </label>
                  <button
                    onClick={async () => {
                      if (!sunoLibraryCategories.length) await loadSunoLibraryCategories();
                      setStructureLibraryOpen(!structureLibraryOpen);
                    }}
                    onTouchEnd={async (e) => {
                      e.preventDefault();
                      if (!sunoLibraryCategories.length) await loadSunoLibraryCategories();
                      setStructureLibraryOpen(!structureLibraryOpen);
                    }}
                    style={{
                      padding: '6px 12px',
                      background: structureLibraryOpen ? 'linear-gradient(90deg, #f59e0b, #d97706)' : 'rgba(245,158,11,0.15)',
                      border: '1px solid #f59e0b',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    📚 {structureLibraryOpen ? 'Close Library' : 'Browse 70K+ Prompts'}
                  </button>
                </div>

                {/* Quick Genre Presets with Audio Preview */}
                <div style={{ marginBottom: '12px' }}>
                  <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', marginBottom: '8px' }}>
                    🎧 Quick presets - click headphone to preview:
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {[
                      { name: "Dark Trap", prompt: "dark trap, heavy 808s, haunting melodies, 140 BPM", color: "#ef4444" },
                      { name: "Melodic Trap", prompt: "melodic trap, emotional synths, hard 808s, 145 BPM", color: "#ef4444" },
                      { name: "Boom Bap", prompt: "boom bap, dusty drums, vinyl samples, 90 BPM", color: "#f97316" },
                      { name: "R&B", prompt: "90s r&b, slow jam, smooth vocals, romantic, 70 BPM", color: "#a855f7" },
                      { name: "TrapSoul", prompt: "trapsoul, emotional r&b, 808s with soul, 75 BPM", color: "#a855f7" },
                      { name: "Afrobeats", prompt: "afrobeats, african drums, danceable groove, 105 BPM", color: "#06b6d4" },
                      { name: "Gospel", prompt: "contemporary gospel, powerful vocals, choir harmonies, 80 BPM", color: "#9F47FF" },
                      { name: "UK Drill", prompt: "uk drill, sliding 808s, dark melodies, 140 BPM", color: "#64748b" },
                    ].map((style, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '4px 6px 4px 10px',
                          background: `${style.color}22`,
                          border: `1px solid ${style.color}55`,
                          borderRadius: '14px'
                        }}
                      >
                        <span
                          onClick={() => {
                            setStructureBaseStyle(style.prompt);
                            toast.success(`✅ Loaded: ${style.name}`);
                          }}
                          style={{
                            color: '#fff',
                            fontSize: '0.7rem',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {style.name}
                        </span>
                        <button
                          onClick={() => generateAudioPreview(style.prompt, style.name, `struct-quick-${i}`)}
                          disabled={audioPreviewLoading === `struct-quick-${i}`}
                          style={{
                            padding: '4px 6px',
                            background: audioPreviewPlaying === `struct-quick-${i}` 
                              ? 'rgba(239, 68, 68, 0.5)' 
                              : audioPreviewLoading === `struct-quick-${i}`
                                ? 'rgba(234, 179, 8, 0.5)'
                                : 'rgba(16, 185, 129, 0.4)',
                            border: 'none',
                            borderRadius: '10px',
                            color: '#fff',
                            cursor: audioPreviewLoading === `struct-quick-${i}` ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                        >
                          {audioPreviewLoading === `struct-quick-${i}` ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : audioPreviewPlaying === `struct-quick-${i}` ? (
                            <VolumeX className="w-3 h-3" />
                          ) : (
                            <Headphones className="w-3 h-3" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Library Browser Panel */}
                {structureLibraryOpen && (
                  <div style={{
                    background: '#0d0d14',
                    border: '2px solid #f59e0b',
                    borderRadius: '10px',
                    padding: '12px',
                    marginBottom: '12px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    position: 'relative',
                    zIndex: 10
                  }}>
                    {/* Category Selector */}
                    <div style={{ marginBottom: '10px', position: 'relative', zIndex: 11 }}>
                      <select
                        data-testid="structure-category-select"
                        value={structureLibraryCategory}
                        onChange={async (e) => {
                          setStructureLibraryCategory(e.target.value);
                          if (e.target.value) {
                            setStructureLibraryLoading(true);
                            try {
                              const res = await fetch(`${API}/suno-prompts/${encodeURIComponent(e.target.value)}?page=1&limit=50`);
                              const data = await res.json();
                              setStructureLibraryPrompts(data.prompts || []);
                            } catch (err) {
                              console.error('Failed to load prompts:', err);
                            }
                            setStructureLibraryLoading(false);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: '#1a1a2e',
                          border: '1px solid rgba(245,158,11,0.3)',
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.85rem',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">-- Select Category ({sunoLibraryCategories.length} available) --</option>
                        {sunoLibraryCategories.map(cat => (
                          <option key={cat.name} value={cat.name}>
                            {mannisCat(cat.name)} ({cat.count} prompts)
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Prompts List */}
                    {structureLibraryLoading ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.5)' }}>
                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                        Loading prompts...
                      </div>
                    ) : structureLibraryPrompts.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {structureLibraryPrompts.map((prompt, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '10px 12px',
                              background: 'rgba(245,158,11,0.08)',
                              border: '1px solid rgba(245,158,11,0.2)',
                              borderRadius: '6px'
                            }}
                          >
                            <span
                              data-testid={`prompt-item-${idx}`}
                              onClick={() => {
                                setStructureBaseStyle(prompt);
                                setStructureLibraryOpen(false);
                                toast.success('✅ Style prompt selected!');
                              }}
                              style={{
                                flex: 1,
                                color: '#fff',
                                fontSize: '0.8rem',
                                cursor: 'pointer'
                              }}
                            >
                              {prompt}
                            </span>
                            {/* Audio Preview */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                generateAudioPreview(prompt, 'Song Structure', `structure-${idx}`);
                              }}
                              disabled={audioPreviewLoading === `structure-${idx}`}
                              style={{
                                padding: '6px 10px',
                                background: audioPreviewPlaying === `structure-${idx}` 
                                  ? 'rgba(239, 68, 68, 0.4)' 
                                  : audioPreviewLoading === `structure-${idx}`
                                    ? 'rgba(234, 179, 8, 0.4)'
                                    : 'rgba(16, 185, 129, 0.3)',
                                border: 'none',
                                borderRadius: '6px',
                                color: audioPreviewPlaying === `structure-${idx}` 
                                  ? '#ef4444' 
                                  : audioPreviewLoading === `structure-${idx}`
                                    ? '#eab308'
                                    : '#10b981',
                                cursor: audioPreviewLoading === `structure-${idx}` ? 'wait' : 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Preview audio"
                            >
                              {audioPreviewLoading === `structure-${idx}` ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : audioPreviewPlaying === `structure-${idx}` ? (
                                <VolumeX className="w-4 h-4" />
                              ) : (
                                <Headphones className="w-4 h-4" />
                              )}
                            </button>
                            {/* Generate Drum Pattern */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                generateDrumFromStylePrompt(prompt);
                                toast.info("🥁 Generating drum pattern...");
                              }}
                              style={{
                                padding: '6px 10px',
                                background: 'rgba(139, 92, 246, 0.3)',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#a78bfa',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Generate drum pattern"
                              data-testid={`40k-drum-${idx}`}
                            >
                              <Music className="w-4 h-4" />
                            </button>
                            {/* Download MIDI */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                generateMidiFromPrompt(prompt);
                              }}
                              disabled={isGeneratingMidi}
                              style={{
                                padding: '6px 10px',
                                background: isGeneratingMidi 
                                  ? 'rgba(16, 185, 129, 0.2)' 
                                  : 'rgba(16, 185, 129, 0.3)',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#34d399',
                                cursor: isGeneratingMidi ? 'wait' : 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Download MIDI (32 bars)"
                              data-testid={`40k-midi-${idx}`}
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : structureLibraryCategory ? (
                      <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                        No prompts found in this category
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '20px', color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                        👆 Select a category to browse prompts
                      </div>
                    )}
                  </div>
                )}
                
                <textarea
                  value={structureBaseStyle}
                  onChange={(e) => setStructureBaseStyle(e.target.value)}
                  placeholder="e.g., dark trap, 808 bass, emotional vocals, 140 BPM"
                  style={{
                    width: '100%',
                    height: '60px',
                    padding: '12px',
                    background: '#0d0d14',
                    border: '1px solid rgba(159,71,255,0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    resize: 'none'
                  }}
                />
              </div>

              {/* Vocal Harmonies */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#a855f7', fontWeight: 700, fontSize: '0.9rem', display: 'block', marginBottom: '10px' }}>
                  🎤 Six-Part Vocal Harmony
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {[
                    { key: 'soprano', label: 'Soprano' },
                    { key: 'alto', label: 'Alto' },
                    { key: 'tenor', label: 'Tenor' },
                    { key: 'bass', label: 'Bass' },
                    { key: 'twoOctavesDown', label: '2 Octaves Down' },
                    { key: 'harmony', label: 'Full Harmony' }
                  ].map(vocal => (
                    <button
                      key={vocal.key}
                      onClick={() => setStructureVocals(prev => ({...prev, [vocal.key]: !prev[vocal.key]}))}
                      style={{
                        padding: '10px 16px',
                        background: structureVocals[vocal.key] ? 'linear-gradient(90deg, #9F47FF, #16a34a)' : 'rgba(239,68,68,0.15)',
                        border: `2px solid ${structureVocals[vocal.key] ? '#9F47FF' : '#ef4444'}`,
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '120px',
                        justifyContent: 'center'
                      }}
                    >
                      <span style={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                        background: structureVocals[vocal.key] ? '#9F47FF' : '#ef4444',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.7rem',
                        fontWeight: 800
                      }}>
                        {structureVocals[vocal.key] ? 'ON' : 'OFF'}
                      </span>
                      {vocal.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Chord Progressions */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <button
                    onClick={() => setStructureChords(prev => ({...prev, enabled: !prev.enabled}))}
                    style={{
                      padding: '8px 16px',
                      background: structureChords.enabled ? 'linear-gradient(90deg, #9F47FF, #16a34a)' : 'rgba(239,68,68,0.15)',
                      border: `2px solid ${structureChords.enabled ? '#9F47FF' : '#ef4444'}`,
                      borderRadius: '8px',
                      color: '#fff',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: structureChords.enabled ? '#9F47FF' : '#ef4444',
                      fontSize: '0.7rem',
                      fontWeight: 800
                    }}>
                      {structureChords.enabled ? 'ON' : 'OFF'}
                    </span>
                    🎹 Chord Changes
                  </button>
                </div>
                
                {structureChords.enabled && (
                  <div style={{
                    padding: '16px',
                    background: 'rgba(159,71,255,0.1)',
                    border: '2px solid #9F47FF',
                    borderRadius: '10px'
                  }}>
                    {/* Key Selection */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                      <div>
                        <label style={{ color: '#9F47FF', fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Key</label>
                        <select
                          value={structureChords.key}
                          onChange={(e) => setStructureChords(prev => ({...prev, key: e.target.value}))}
                          style={{
                            padding: '8px 12px',
                            background: '#0d0d14',
                            border: '1px solid #9F47FF',
                            borderRadius: '6px',
                            color: '#fff',
                            fontSize: '0.9rem'
                          }}
                        >
                          <optgroup label="Natural">
                            {['C', 'D', 'E', 'F', 'G', 'A', 'B'].map(k => (
                              <option key={k} value={k}>{k}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Sharps">
                            {['C#', 'D#', 'F#', 'G#', 'A#'].map(k => (
                              <option key={k} value={k}>{k}</option>
                            ))}
                          </optgroup>
                          <optgroup label="Flats">
                            {['Db', 'Eb', 'Gb', 'Ab', 'Bb'].map(k => (
                              <option key={k} value={k}>{k}</option>
                            ))}
                          </optgroup>
                        </select>
                      </div>
                      <div>
                        <label style={{ color: '#9F47FF', fontSize: '0.8rem', fontWeight: 600, display: 'block', marginBottom: '4px' }}>Mode</label>
                        <select
                          value={structureChords.mode}
                          onChange={(e) => setStructureChords(prev => ({...prev, mode: e.target.value}))}
                          style={{
                            padding: '8px 12px',
                            background: '#0d0d14',
                            border: '1px solid #9F47FF',
                            borderRadius: '6px',
                            color: '#fff',
                            fontSize: '0.9rem'
                          }}
                        >
                          <option value="major">Major</option>
                          <option value="minor">Minor</option>
                          <option value="dorian">Dorian</option>
                          <option value="mixolydian">Mixolydian</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Chord progressions for each section */}
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {[
                        { key: 'intro', label: 'Intro' },
                        { key: 'verse', label: 'Verse' },
                        { key: 'preChorus', label: 'Pre-Chorus' },
                        { key: 'chorus', label: 'Chorus' },
                        { key: 'bridge', label: 'Bridge' },
                        { key: 'outro', label: 'Outro' }
                      ].map(section => (
                        <div key={section.key} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ color: '#9F47FF', fontSize: '0.8rem', fontWeight: 600, minWidth: '80px' }}>{section.label}:</span>
                          <input
                            value={structureChords[section.key]}
                            onChange={(e) => setStructureChords(prev => ({...prev, [section.key]: e.target.value}))}
                            placeholder="e.g., i - VI - III - VII"
                            style={{
                              flex: 1,
                              padding: '6px 10px',
                              background: '#0d0d14',
                              border: '1px solid rgba(159,71,255,0.3)',
                              borderRadius: '6px',
                              color: '#fff',
                              fontSize: '0.85rem'
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    
                    {/* Common progressions quick select */}
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', display: 'block', marginBottom: '6px' }}>Quick Presets:</label>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {[
                          { label: 'Pop', verse: 'I - V - vi - IV', chorus: 'IV - V - I' },
                          { label: 'Trap', verse: 'i - VI - III - VII', chorus: 'VI - VII - i' },
                          { label: 'R&B', verse: 'ii - V - I - vi', chorus: 'IV - V - iii - vi' },
                          { label: 'Gospel', verse: 'I - IV - V - I', chorus: 'IV - I - V - I' }
                        ].map(preset => (
                          <button
                            key={preset.label}
                            onClick={() => setStructureChords(prev => ({
                              ...prev,
                              verse: preset.verse,
                              chorus: preset.chorus
                            }))}
                            style={{
                              padding: '4px 10px',
                              background: 'rgba(159,71,255,0.2)',
                              border: '1px solid rgba(159,71,255,0.4)',
                              borderRadius: '4px',
                              color: '#9F47FF',
                              fontSize: '0.75rem',
                              cursor: 'pointer'
                            }}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Song Sections */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ color: '#f59e0b', fontWeight: 700, fontSize: '0.9rem', display: 'block', marginBottom: '10px' }}>
                  🎵 Song Sections & Transitions
                </label>
                {[
                  { key: 'intro', label: 'Intro', color: '#3b82f6' },
                  { key: 'verse', label: 'Verses', color: '#9F47FF' },
                  { key: 'preChorus', label: 'Pre-Chorus', color: '#eab308' },
                  { key: 'chorus', label: 'Chorus', color: '#ef4444' },
                  { key: 'bridge', label: 'Bridge', color: '#a855f7' },
                  { key: 'outro', label: 'Outro', color: '#6366f1' }
                ].map(section => (
                  <div key={section.key} style={{
                    marginBottom: '12px',
                    padding: '12px',
                    background: structureSections[section.key].enabled ? 'rgba(159,71,255,0.1)' : 'rgba(239,68,68,0.05)',
                    border: `2px solid ${structureSections[section.key].enabled ? '#9F47FF' : '#ef4444'}`,
                    borderRadius: '10px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: structureSections[section.key].enabled ? '10px' : '0' }}>
                      <button
                        onClick={() => setStructureSections(prev => ({
                          ...prev,
                          [section.key]: {...prev[section.key], enabled: !prev[section.key].enabled}
                        }))}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: 'none',
                          background: structureSections[section.key].enabled ? '#9F47FF' : '#ef4444',
                          color: '#fff',
                          cursor: 'pointer',
                          fontWeight: 800,
                          fontSize: '0.7rem'
                        }}
                      >
                        {structureSections[section.key].enabled ? 'ON' : 'OFF'}
                      </button>
                      <span style={{ color: section.color, fontWeight: 700, fontSize: '0.95rem' }}>[{section.label}]</span>
                    </div>
                    {structureSections[section.key].enabled && (
                      <input
                        value={structureSections[section.key].instruction}
                        onChange={(e) => setStructureSections(prev => ({
                          ...prev,
                          [section.key]: {...prev[section.key], instruction: e.target.value}
                        }))}
                        placeholder={`Instructions for ${section.label.toLowerCase()}...`}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          background: '#0d0d14',
                          border: `1px solid ${section.color}40`,
                          borderRadius: '6px',
                          color: '#fff',
                          fontSize: '0.85rem'
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Generate & Export Buttons */}
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const { style, lyrics } = generateStructuredPrompt();
                    const fullContent = `STYLE:\n${style}\n\nLYRICS:\n${lyrics}`;
                    universalCopy(fullContent, '✅ COPIED! TAP & HOLD in Suno to paste').then((ok) => {
                      setShowStructureBuilder(false);
                      if (ok) {
                        setTimeout(() => {
                          window.open('https://suno.com/create', '_blank');
                        }, 500);
                      }
                    });
                  }}
                  onClick={() => {
                    const { style, lyrics } = generateStructuredPrompt();
                    const fullContent = `STYLE:\n${style}\n\nLYRICS:\n${lyrics}`;
                    universalCopy(fullContent, '✅ COPIED! TAP & HOLD in Suno to paste').then((ok) => {
                      setShowStructureBuilder(false);
                      if (ok) {
                        setTimeout(() => {
                          window.open('https://suno.com/create', '_blank');
                        }, 500);
                      }
                    });
                  }}
                  style={{
                    flex: 1,
                    minWidth: '200px',
                    padding: '18px',
                    background: 'linear-gradient(90deg, #9F47FF, #059669)',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#fff',
                    fontWeight: 800,
                    cursor: 'pointer',
                    fontSize: '1.1rem',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                    minHeight: '60px',
                    boxShadow: '0 4px 20px rgba(159,71,255,0.4)'
                  }}
                >
                  🚀 SEND TO SUNO
                </button>
                <button
                  onClick={() => {
                    const { style, lyrics } = generateStructuredPrompt();
                    const fullContent = `MANNIS - SONG STRUCTURE PROMPT\n${'='.repeat(40)}\n\nSTYLE:\n${style}\n\nLYRICS:\n${lyrics}\n\nGenerated: ${new Date().toLocaleString()}`;
                    shareOrDownloadText(fullContent, `song-structure-prompt-${Date.now()}.txt`, 'text/plain', { successMessage: '📥 Prompt downloaded!' });
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    const { style, lyrics } = generateStructuredPrompt();
                    const fullContent = `MANNIS - SONG STRUCTURE PROMPT\n${'='.repeat(40)}\n\nSTYLE:\n${style}\n\nLYRICS:\n${lyrics}\n\nGenerated: ${new Date().toLocaleString()}`;
                    shareOrDownloadText(fullContent, `song-structure-prompt-${Date.now()}.txt`, 'text/plain', { successMessage: '📥 Prompt downloaded!' });
                  }}
                  style={{
                    padding: '16px 24px',
                    background: 'linear-gradient(90deg, #f59e0b, #d97706)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Download className="w-5 h-5" />
                  Download Prompt
                </button>
                <button
                  type="button"
                  onTouchStart={(e) => e.stopPropagation()}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const { style, lyrics } = generateStructuredPrompt();
                    openSunoExport(style, lyrics);
                    setShowStructureBuilder(false);
                  }}
                  onClick={() => {
                    const { style, lyrics } = generateStructuredPrompt();
                    openSunoExport(style, lyrics);
                    setShowStructureBuilder(false);
                  }}
                  style={{
                    padding: '16px 24px',
                    background: 'linear-gradient(90deg, #9F47FF, #16a34a)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation',
                    minHeight: '56px'
                  }}
                >
                  Copy Style & Lyrics Separately
                </button>
                <button
                  onClick={() => setShowStructureBuilder(false)}
                  style={{
                    padding: '16px 24px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    color: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Music AI Export Modal - TWO SEPARATE WINDOWS for Style and Lyrics */}
        {showSunoExport && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }} onClick={() => setShowSunoExport(false)}>
            <div style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: '20px',
              padding: '28px',
              maxWidth: '650px',
              width: '100%',
              border: '2px solid #a855f7',
              maxHeight: '90vh',
              overflow: 'auto'
            }} onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h2 style={{ color: '#a855f7', margin: 0, fontSize: '1.4rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <ExternalLink className="w-6 h-6" />
                  Export to Music AI
                </h2>
                <button
                  onClick={() => {
                    setShowSunoExport(false);
                    
                  }}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(90deg, #9F47FF, #a855f7)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  Open Music AI
                </button>
              </div>

              {/* Instructions */}
              <div style={{
                background: 'rgba(168,85,247,0.1)',
                border: '1px solid rgba(168,85,247,0.3)',
                borderRadius: '10px',
                padding: '12px 16px',
                marginBottom: '20px'
              }}>
                <p style={{ color: '#fff', margin: 0, fontSize: '0.9rem' }}>
                  Suno has <strong style={{ color: '#a855f7' }}>2 separate fields</strong>. Copy each and paste into the matching field:
                </p>
              </div>

              {/* WINDOW 1: Style */}
              <div style={{
                background: 'rgba(159,71,255,0.05)',
                border: '2px solid rgba(159,71,255,0.3)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <label style={{ 
                    color: '#9F47FF', 
                    fontWeight: 700, 
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ 
                      background: '#9F47FF', 
                      color: '#000', 
                      padding: '3px 10px', 
                      borderRadius: '6px', 
                      fontSize: '0.8rem',
                      fontWeight: 800
                    }}>1</span>
                    STYLE → Paste in Suno's "Style" field
                  </label>
                  <button
                    onClick={() => {
                      universalCopy(sunoExportStyle, '✅ STYLE COPIED!');
                      setSunoStyleCopied(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: sunoStyleCopied ? '#9F47FF' : 'linear-gradient(90deg, #9F47FF, #16a34a)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {sunoStyleCopied ? '✓ Copied!' : 'Copy Style'}
                  </button>
                </div>
                <textarea
                  value={sunoExportStyle}
                  readOnly
                  onClick={(e) => e.target.select()}
                  style={{
                    width: '100%',
                    height: '80px',
                    padding: '12px',
                    background: '#0d0d14',
                    border: sunoStyleCopied ? '2px solid #9F47FF' : '1px solid rgba(159,71,255,0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontFamily: 'monospace',
                    resize: 'none'
                  }}
                />
              </div>

              {/* WINDOW 2: Lyrics */}
              <div style={{
                background: 'rgba(245,158,11,0.05)',
                border: '2px solid rgba(245,158,11,0.3)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <label style={{ 
                    color: '#f59e0b', 
                    fontWeight: 700, 
                    fontSize: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{ 
                      background: '#f59e0b', 
                      color: '#000', 
                      padding: '3px 10px', 
                      borderRadius: '6px', 
                      fontSize: '0.8rem',
                      fontWeight: 800
                    }}>2</span>
                    LYRICS → Paste in Suno's "Lyrics" field
                  </label>
                  <button
                    onClick={() => {
                      universalCopy(sunoExportLyrics, '✅ LYRICS COPIED!');
                      setSunoLyricsCopied(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      background: sunoLyricsCopied ? '#f59e0b' : 'linear-gradient(90deg, #f59e0b, #d97706)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {sunoLyricsCopied ? '✓ Copied!' : 'Copy Lyrics'}
                  </button>
                </div>
                <textarea
                  value={sunoExportLyrics}
                  readOnly
                  onClick={(e) => e.target.select()}
                  style={{
                    width: '100%',
                    height: '120px',
                    padding: '12px',
                    background: '#0d0d14',
                    border: sunoLyricsCopied ? '2px solid #f59e0b' : '1px solid rgba(245,158,11,0.3)',
                    borderRadius: '8px',
                    color: '#fff',
                    fontSize: '0.9rem',
                    fontFamily: 'monospace',
                    resize: 'none',
                    whiteSpace: 'pre-wrap'
                  }}
                />
              </div>

              {/* Progress & Actions */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginBottom: '16px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  background: sunoStyleCopied ? 'rgba(159,71,255,0.2)' : 'rgba(255,255,255,0.05)',
                  borderRadius: '20px',
                  border: `1px solid ${sunoStyleCopied ? '#9F47FF' : 'rgba(255,255,255,0.1)'}`
                }}>
                  <span style={{ color: sunoStyleCopied ? '#9F47FF' : 'rgba(255,255,255,0.5)' }}>
                    {sunoStyleCopied ? '✓' : '○'} Style
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  background: sunoLyricsCopied ? 'rgba(245,158,11,0.2)' : 'rgba(255,255,255,0.05)',
                  borderRadius: '20px',
                  border: `1px solid ${sunoLyricsCopied ? '#f59e0b' : 'rgba(255,255,255,0.1)'}`
                }}>
                  <span style={{ color: sunoLyricsCopied ? '#f59e0b' : 'rgba(255,255,255,0.5)' }}>
                    {sunoLyricsCopied ? '✓' : '○'} Lyrics
                  </span>
                </div>
              </div>

              {/* Done button */}
              <button
                onClick={() => {
                  setShowSunoExport(false);
                  
                }}
                style={{
                  width: '100%',
                  padding: '16px',
                  background: (sunoStyleCopied && sunoLyricsCopied) 
                    ? 'linear-gradient(90deg, #9F47FF, #a855f7)' 
                    : 'rgba(159,71,255,0.3)',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontSize: '1.1rem',
                  marginBottom: '10px'
                }}
              >
                {(sunoStyleCopied && sunoLyricsCopied) ? '🎵 Open Music AI & Create!' : 'Open Music AI'}
              </button>
              
              <button
                onClick={() => setShowSunoExport(false)}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'transparent',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '8px',
                  color: 'rgba(255,255,255,0.5)',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

            </div> 
          )} {/* End CREATE VIEW */}

          {/* LIBRARY VIEW */}
          {activeView === 'library' && (
            <div className="library-section" style={{ maxWidth: '900px', margin: '0 auto' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }} data-testid="library-tabs">
                {[['beats', '🎵 Beats'], ['prompts', '✨ Style Prompts'], ['analyses', '🔍 Analyses']].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setLibraryTab(key);
                      if (key === 'prompts') fetchSavedPrompts();
                      if (key === 'analyses') fetchSavedAnalyses();
                    }}
                    data-testid={`library-tab-${key}`}
                    style={{
                      padding: '8px 18px', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem',
                      background: libraryTab === key ? 'rgba(0, 212, 255, 0.15)' : 'transparent',
                      border: libraryTab === key ? '1px solid rgba(0, 212, 255, 0.45)' : '1px solid var(--border)',
                      color: libraryTab === key ? '#00d4ff' : 'var(--text-muted)'
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {libraryTab === 'prompts' && (
                <div data-testid="library-prompts-panel">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div>
                      <h2 style={{ color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>My Style Prompts</h2>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>{savedPrompts.length} saved prompts — auto-saved from the Analyzer & generators</p>
                    </div>
                    <button onClick={fetchSavedPrompts} className="btn btn-secondary"><RotateCcw className="w-4 h-4" /> Refresh</button>
                  </div>
                  {!userEmail ? (
                    <div style={{ textAlign: 'center', padding: '50px 20px', background: 'var(--bg-surface)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                      <p style={{ color: 'var(--text-muted)' }}>Sign in to view your saved style prompts</p>
                    </div>
                  ) : savedPrompts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '50px 20px', background: 'var(--bg-surface)', borderRadius: '12px', border: '1px solid var(--border)' }} data-testid="library-prompts-empty">
                      <p style={{ color: 'var(--text-muted)' }}>No saved prompts yet — analyze a track or generate prompt variations and they'll appear here automatically</p>
                    </div>
                  ) : (
                    savedPrompts.map((p, i) => (
                      <div key={p.id || i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '12px', marginBottom: '8px' }} data-testid={`saved-prompt-row-${i}`}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: 'var(--text-primary)', fontSize: '0.88rem', lineHeight: 1.4 }}>{p.prompt}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', marginTop: '4px' }}>
                            {p.dna_code && <span style={{ color: '#00d4ff', marginRight: '10px' }}>{p.dna_code}</span>}
                            {p.source} • {p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}
                          </div>
                        </div>
                        <button className="song-action-btn primary" title="Use in Beat Generator" onClick={() => applySpotifyPromptToBeat(p.prompt)} data-testid={`saved-prompt-use-${i}`}><Play className="w-4 h-4" /></button>
                        <button className="song-action-btn" title="Copy" onClick={() => { navigator.clipboard.writeText(p.prompt); toast.success('Copied!'); }} data-testid={`saved-prompt-copy-${i}`}><Copy className="w-4 h-4" /></button>
                        <button className="song-action-btn" title="Delete" onClick={() => deleteSavedPrompt(p.id)} data-testid={`saved-prompt-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {libraryTab === 'analyses' && (
                <div data-testid="library-analyses-panel">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div>
                      <h2 style={{ color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>My Analyses</h2>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>{savedAnalyses.length} analyzed tracks — every Music Link Analyzer run is saved here</p>
                    </div>
                    <button onClick={fetchSavedAnalyses} className="btn btn-secondary"><RotateCcw className="w-4 h-4" /> Refresh</button>
                  </div>
                  {!userEmail ? (
                    <div style={{ textAlign: 'center', padding: '50px 20px', background: 'var(--bg-surface)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                      <p style={{ color: 'var(--text-muted)' }}>Sign in to view your analysis history</p>
                    </div>
                  ) : savedAnalyses.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '50px 20px', background: 'var(--bg-surface)', borderRadius: '12px', border: '1px solid var(--border)' }} data-testid="library-analyses-empty">
                      <p style={{ color: 'var(--text-muted)' }}>No analyses yet — paste a Spotify or YouTube link in the Music Analyzer and it'll be saved here</p>
                    </div>
                  ) : (
                    savedAnalyses.map((a, i) => (
                      <div key={a.id || i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '12px', marginBottom: '8px' }} data-testid={`saved-analysis-row-${i}`}>
                        <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'rgba(0, 212, 255, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          {a.source === 'youtube' ? '▶️' : '🎧'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: 'var(--text-primary)', fontSize: '0.9rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {a.track?.name || 'Unknown track'}
                          </div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '2px' }}>
                            {(a.track?.artists || []).join(', ')} • {a.source} • {a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}
                          </div>
                        </div>
                        <button className="song-action-btn primary" title="View analysis" onClick={() => { setSpotifyResult(a.result); setShowSpotifyModal(true); }} data-testid={`saved-analysis-view-${i}`}><Eye className="w-4 h-4" /></button>
                        <button className="song-action-btn" title="Delete" onClick={() => deleteSavedAnalysis(a.id)} data-testid={`saved-analysis-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))
                  )}
                </div>
              )}

              {libraryTab === 'beats' && (<>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: '24px'
              }}>
                <div>
                  <h2 style={{ color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
                    My Songs
                  </h2>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>
                    {beatLibrary.length} saved songs
                  </p>
                </div>
                <button
                  onClick={() => fetchBeatLibrary()}
                  className="btn btn-secondary"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <RotateCcw className="w-4 h-4" />
                  Refresh
                </button>
              </div>

              {!userEmail ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  background: 'var(--bg-surface)',
                  borderRadius: '12px',
                  border: '1px solid var(--border)'
                }}>
                  <User className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                  <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>Sign in to view your library</h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                    Enter your email in the sidebar to access your saved beats
                  </p>
                </div>
              ) : beatLibrary.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  background: 'var(--bg-surface)',
                  borderRadius: '12px',
                  border: '1px solid var(--border)'
                }}>
                  <Music2 className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                  <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>No beats yet</h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '16px' }}>
                    Generate your first beat to start building your library
                  </p>
                  <button
                    onClick={() => setActiveView('create')}
                    className="btn btn-primary"
                  >
                    <PlusCircle className="w-4 h-4" />
                    Create Style Prompt
                  </button>
                </div>
              ) : (
                <div className="songs-list">
                  {beatLibrary.map((beat, i) => (
                    <div key={beat._id || i} className="song-card">
                      <div className="song-thumbnail">
                        <Music2 className="w-6 h-6 song-thumbnail-icon" />
                      </div>
                      <div className="song-info">
                        <div className="song-title">{beat.prompt?.slice(0, 50) || 'Generated Beat'}...</div>
                        <div className="song-meta">
                          <span className="song-meta-item">
                            {new Date(beat.created_at).toLocaleDateString()}
                          </span>
                          <span className="song-meta-item">
                            {beat.model || 'MiniMax'}
                          </span>
                        </div>
                      </div>
                      <div className="song-actions" style={{ opacity: 1 }}>
                        {beat.audio_url && (
                          <>
                            <button
                              className="song-action-btn primary"
                              onClick={() => {
                                setBeatAudioUrl(beat.audio_url);
                                setBeatPrompt(beat.prompt);
                              }}
                            >
                              <Play className="w-4 h-4" />
                            </button>
                            <button
                              className="song-action-btn"
                              onClick={() => saveWavFromUrl(beat.audio_url, `sounddna-beat-${(beat.task_id || 'track').slice(0, 8)}.wav`)}
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              </>)}
            </div>
          )}

          {/* VOICES VIEW */}
          {activeView === 'voices' && (
            <div className="voices-section" style={{ maxWidth: '900px', margin: '0 auto' }}>
              {/* BACK TO DASHBOARD BUTTON */}
              <button
                onClick={() => setActiveView('create')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  marginBottom: '20px',
                  boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)'
                }}
                data-testid="voices-back-to-dashboard-btn"
              >
                <RotateCcw className="w-4 h-4" />
                ← BACK TO DASHBOARD
              </button>

              <div style={{ marginBottom: '24px' }}>
                <h2 style={{ color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
                  Voice Samples
                </h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>
                  Click any voice to add to workflow • Then export to Suno
                </p>
              </div>

              {/* Male Voices */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ 
                  color: 'var(--accent)', 
                  fontSize: '0.85rem', 
                  fontWeight: 600, 
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>👨</span> MALE VOCALS
                </h3>
                <div className="voice-grid">
                  {VOICE_SAMPLES.male.map((sample, i) => (
                    <div
                      key={i}
                      className="voice-card"
                      onClick={() => {
                        updateWorkflow({ voiceSample: sample.name });
                        setSelectedVoiceSample(sample.name);
                        toast.success(`✓ Added "${sample.name}" to workflow! Click BACK TO DASHBOARD to export.`);
                      }}
                    >
                      <div className="voice-card-title">{sample.name}</div>
                      <div className="voice-card-desc">{sample.prompt.slice(0, 60)}...</div>
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '0.65rem', 
                        color: '#10b981',
                        fontWeight: 600
                      }}>
                        TAP TO ADD TO WORKFLOW
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Female Voices */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ 
                  color: 'var(--accent)', 
                  fontSize: '0.85rem', 
                  fontWeight: 600, 
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>👩</span> FEMALE VOCALS
                </h3>
                <div className="voice-grid">
                  {VOICE_SAMPLES.female.map((sample, i) => (
                    <div
                      key={i}
                      className="voice-card"
                      onClick={() => {
                        updateWorkflow({ voiceSample: sample.name });
                        setSelectedVoiceSample(sample.name);
                        toast.success(`✓ Added "${sample.name}" to workflow! Click BACK TO DASHBOARD to export.`);
                      }}
                    >
                      <div className="voice-card-title">{sample.name}</div>
                      <div className="voice-card-desc">{sample.prompt.slice(0, 60)}...</div>
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '0.65rem', 
                        color: '#10b981',
                        fontWeight: 600
                      }}>
                        TAP TO ADD TO WORKFLOW
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vocal Styles */}
              <div>
                <h3 style={{ 
                  color: 'var(--accent)', 
                  fontSize: '0.85rem', 
                  fontWeight: 600, 
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>🎚️</span> VOCAL STYLES & EFFECTS
                </h3>
                <div className="voice-grid">
                  {VOICE_SAMPLES.styles.map((sample, i) => (
                    <div
                      key={i}
                      className="voice-card"
                      onClick={async () => {
                        await copyTextToClipboard(sample.prompt, sample.name);
                        toast.success(`✓ Copied: ${sample.name}`);
                      }}
                    >
                      <div className="voice-card-title">{sample.name}</div>
                      <div className="voice-card-desc">{sample.prompt.slice(0, 60)}...</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ANALYZER VIEW */}
          {activeView === 'analyzer' && (
            <div className="analyzer-section" style={{ maxWidth: '700px', margin: '0 auto' }}>
              <div style={{ marginBottom: '24px' }}>
                <h2 style={{ color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
                  Music Link Analyzer
                </h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>
                  Extract BPM, Key, Mood • Generate "Sound Like" style prompts
                </p>
              </div>

              <div className="create-input-card">
                <div className="form-group">
                  <label className="form-label">Paste Music Link</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={spotifyUrl}
                      onChange={(e) => setSpotifyUrl(e.target.value)}
                      placeholder="Paste any music track link..."
                      className="form-input"
                      style={{ flex: 1 }}
                      onKeyPress={(e) => e.key === 'Enter' && analyzeSpotifyTrack()}
                    />
                    <button
                      onClick={analyzeSpotifyTrack}
                      disabled={spotifyAnalyzing || !spotifyUrl}
                      className="btn btn-primary"
                    >
                      {spotifyAnalyzing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Search className="w-4 h-4" />
                          Analyze
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Genre Style Prompts with Audio Preview */}
                <div style={{ marginTop: '20px' }}>
                  <h4 style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '10px' }}>
                    🎵 Genre Style Prompts - Click 🎧 to preview audio
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', maxHeight: '200px', overflow: 'auto', padding: '4px' }}>
                    {[
                      { name: "Dark Trap", prompt: "dark trap, heavy 808s, haunting melodies, aggressive hi-hats, minor key, 140 BPM", color: "#ef4444" },
                      { name: "Melodic Trap", prompt: "melodic trap, emotional synths, hard 808s, ambient pads, 145 BPM", color: "#ef4444" },
                      { name: "Boom Bap", prompt: "boom bap, dusty drums, vinyl samples, jazzy loops, old school, 90 BPM", color: "#f97316" },
                      { name: "G-Funk", prompt: "g-funk, smooth synths, funky bassline, talk box, laid back, 95 BPM", color: "#f97316" },
                      { name: "90s R&B", prompt: "90s r&b, slow jam, smooth vocals, new jack swing, romantic, 70 BPM", color: "#a855f7" },
                      { name: "TrapSoul", prompt: "trapsoul, emotional r&b, 808s with soul, vulnerable, late night, 75 BPM", color: "#a855f7" },
                      { name: "Neo Soul", prompt: "neo soul, jazzy chords, organic drums, conscious, warm, 85 BPM", color: "#fbbf24" },
                      { name: "UK Drill", prompt: "uk drill, sliding 808s, dark melodies, aggressive, menacing, 140 BPM", color: "#64748b" },
                      { name: "Afrobeats", prompt: "afrobeats, african drums, danceable groove, infectious rhythm, 105 BPM", color: "#06b6d4" },
                      { name: "Amapiano", prompt: "amapiano, log drums, jazzy keys, deep house influence, 115 BPM", color: "#06b6d4" },
                      { name: "Gospel", prompt: "contemporary gospel, powerful vocals, choir harmonies, uplifting, 80 BPM", color: "#9F47FF" },
                      { name: "Reggae", prompt: "reggae, offbeat rhythm, bass-heavy, island vibes, conscious, 80 BPM", color: "#9F47FF" },
                    ].map((style, i) => (
                      <div
                        key={i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 8px 6px 12px',
                          background: `${style.color}22`,
                          border: `1px solid ${style.color}55`,
                          borderRadius: '20px'
                        }}
                      >
                        <span
                          onClick={() => {
                            setSpotifyUrl(style.prompt);
                            toast.success(`✅ Loaded: ${style.name}`);
                          }}
                          style={{
                            color: '#fff',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {style.name}
                        </span>
                        <button
                          onClick={() => generateAudioPreview(style.prompt, style.name, `analyzer-genre-${i}`)}
                          disabled={audioPreviewLoading === `analyzer-genre-${i}`}
                          style={{
                            padding: '5px 8px',
                            background: audioPreviewPlaying === `analyzer-genre-${i}` 
                              ? 'rgba(239, 68, 68, 0.5)' 
                              : audioPreviewLoading === `analyzer-genre-${i}`
                                ? 'rgba(234, 179, 8, 0.5)'
                                : 'rgba(16, 185, 129, 0.4)',
                            border: 'none',
                            borderRadius: '12px',
                            color: audioPreviewPlaying === `analyzer-genre-${i}` 
                              ? '#fff' 
                              : audioPreviewLoading === `analyzer-genre-${i}`
                                ? '#fff'
                                : '#10b981',
                            cursor: audioPreviewLoading === `analyzer-genre-${i}` ? 'wait' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          {audioPreviewLoading === `analyzer-genre-${i}` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : audioPreviewPlaying === `analyzer-genre-${i}` ? (
                            <VolumeX className="w-4 h-4" />
                          ) : (
                            <Headphones className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* HIT SONG FORMULA ANALYZER */}
                <div style={{ 
                  marginTop: '24px',
                  padding: '20px',
                  background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.1), rgba(249, 115, 22, 0.1))',
                  border: '2px solid rgba(234, 179, 8, 0.3)',
                  borderRadius: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <span style={{ fontSize: '1.5rem' }}>🔬</span>
                    <div>
                      <h4 style={{ color: '#eab308', fontSize: '1rem', fontWeight: 700, margin: 0 }}>
                        HIT SONG FORMULA ANALYZER
                      </h4>
                      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', margin: 0 }}>
                        Discover WHY hit songs work • Get matching style prompts with audio previews
                      </p>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                    <input
                      type="text"
                      value={hitSongInput}
                      onChange={(e) => setHitSongInput(e.target.value)}
                      placeholder='Enter a hit song (e.g., "Blinding Lights by The Weeknd" or "Trap Queen")'
                      style={{
                        flex: 1,
                        padding: '12px 16px',
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid rgba(234, 179, 8, 0.3)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.9rem'
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && analyzeHitSong()}
                    />
                    <button
                      onClick={analyzeHitSong}
                      disabled={isAnalyzingHitSong || !hitSongInput.trim()}
                      style={{
                        padding: '12px 20px',
                        background: isAnalyzingHitSong 
                          ? 'rgba(234, 179, 8, 0.3)' 
                          : 'linear-gradient(135deg, #eab308, #f97316)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff',
                        fontWeight: 700,
                        cursor: isAnalyzingHitSong ? 'wait' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {isAnalyzingHitSong ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</>
                      ) : (
                        <><Zap className="w-4 h-4" /> Analyze Formula</>
                      )}
                    </button>
                  </div>
                  
                  {/* Hit Song Analysis Results */}
                  {hitSongAnalysis && (
                    <div style={{ marginTop: '16px' }}>
                      {/* Song Info Header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        background: 'rgba(234, 179, 8, 0.15)',
                        borderRadius: '8px',
                        marginBottom: '16px'
                      }}>
                        <div>
                          <h5 style={{ color: '#fff', fontSize: '1rem', fontWeight: 700, margin: 0 }}>
                            {hitSongAnalysis.song_name}
                          </h5>
                          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem', margin: 0 }}>
                            {hitSongAnalysis.artist} • {hitSongAnalysis.genre}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#eab308', fontSize: '1.2rem', fontWeight: 700 }}>{hitSongAnalysis.bpm}</div>
                            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem' }}>BPM</div>
                          </div>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ color: '#eab308', fontSize: '1rem', fontWeight: 700 }}>{hitSongAnalysis.key}</div>
                            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem' }}>KEY</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Formula Breakdown */}
                      {hitSongAnalysis.formula_breakdown && (
                        <div style={{
                          padding: '16px',
                          background: 'rgba(0,0,0,0.3)',
                          borderRadius: '8px',
                          marginBottom: '16px'
                        }}>
                          <h6 style={{ color: '#f97316', fontSize: '0.85rem', fontWeight: 700, marginBottom: '12px' }}>
                            🎯 WHY THIS SONG WORKS
                          </h6>
                          <p style={{ color: '#fff', fontSize: '0.85rem', marginBottom: '12px', fontStyle: 'italic' }}>
                            "{hitSongAnalysis.formula_breakdown.why_it_works}"
                          </p>
                          
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                            <div>
                              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', marginBottom: '4px' }}>HOOK STRENGTH</div>
                              <div style={{ color: '#fff', fontSize: '0.8rem' }}>{hitSongAnalysis.formula_breakdown.hook_strength}</div>
                            </div>
                            <div>
                              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', marginBottom: '4px' }}>ARRANGEMENT</div>
                              <div style={{ color: '#fff', fontSize: '0.8rem' }}>{hitSongAnalysis.formula_breakdown.arrangement}</div>
                            </div>
                          </div>
                          
                          {hitSongAnalysis.formula_breakdown.production_techniques && (
                            <div style={{ marginTop: '12px' }}>
                              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', marginBottom: '6px' }}>PRODUCTION TECHNIQUES</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {hitSongAnalysis.formula_breakdown.production_techniques.map((tech, i) => (
                                  <span key={i} style={{
                                    padding: '4px 10px',
                                    background: 'rgba(234, 179, 8, 0.2)',
                                    border: '1px solid rgba(234, 179, 8, 0.3)',
                                    borderRadius: '12px',
                                    color: '#eab308',
                                    fontSize: '0.7rem'
                                  }}>
                                    {tech}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Style Prompts with Audio Preview */}
                      {hitSongAnalysis.style_prompts && (
                        <div>
                          <h6 style={{ color: '#10b981', fontSize: '0.85rem', fontWeight: 700, marginBottom: '12px' }}>
                            🎧 STYLE PROMPTS WITH AUDIO PREVIEW
                          </h6>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {hitSongAnalysis.style_prompts.map((stylePrompt, i) => (
                              <div
                                key={i}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  padding: '12px',
                                  background: 'rgba(16, 185, 129, 0.08)',
                                  border: '1px solid rgba(16, 185, 129, 0.2)',
                                  borderRadius: '8px'
                                }}
                              >
                                <button
                                  onClick={() => generateAudioPreview(stylePrompt.prompt, stylePrompt.name, `hit-${i}`)}
                                  disabled={audioPreviewLoading === `hit-${i}`}
                                  style={{
                                    padding: '10px 14px',
                                    background: audioPreviewPlaying === `hit-${i}` 
                                      ? 'rgba(239, 68, 68, 0.4)' 
                                      : audioPreviewLoading === `hit-${i}`
                                        ? 'rgba(234, 179, 8, 0.4)'
                                        : 'rgba(16, 185, 129, 0.3)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: '#fff',
                                    cursor: audioPreviewLoading === `hit-${i}` ? 'wait' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  {audioPreviewLoading === `hit-${i}` ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                  ) : audioPreviewPlaying === `hit-${i}` ? (
                                    <VolumeX className="w-5 h-5" />
                                  ) : (
                                    <Headphones className="w-5 h-5" />
                                  )}
                                </button>
                                <div style={{ flex: 1 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                    <span style={{ color: '#10b981', fontWeight: 700, fontSize: '0.85rem' }}>{stylePrompt.name}</span>
                                    <span style={{
                                      padding: '2px 8px',
                                      background: 'rgba(255,255,255,0.1)',
                                      borderRadius: '10px',
                                      color: 'rgba(255,255,255,0.6)',
                                      fontSize: '0.65rem'
                                    }}>
                                      {stylePrompt.vibe}
                                    </span>
                                  </div>
                                  <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', margin: 0 }}>
                                    {stylePrompt.prompt.length > 100 ? stylePrompt.prompt.substring(0, 100) + '...' : stylePrompt.prompt}
                                  </p>
                                </div>
                                <button
                                  onClick={() => {
                                    setBeatPrompt(stylePrompt.prompt);
                                    toast.success(`Loaded "${stylePrompt.name}" to your prompt!`);
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    background: 'linear-gradient(135deg, #9F47FF, #6366f1)',
                                    border: 'none',
                                    borderRadius: '6px',
                                    color: '#fff',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                  }}
                                >
                                  Use
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Similar Hits & Producer Tips */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginTop: '16px' }}>
                        {hitSongAnalysis.similar_hits && hitSongAnalysis.similar_hits.length > 0 && (
                          <div style={{
                            padding: '12px',
                            background: 'rgba(99, 102, 241, 0.1)',
                            border: '1px solid rgba(99, 102, 241, 0.2)',
                            borderRadius: '8px'
                          }}>
                            <div style={{ color: '#6366f1', fontSize: '0.75rem', fontWeight: 700, marginBottom: '8px' }}>
                              🎵 SIMILAR HITS
                            </div>
                            {hitSongAnalysis.similar_hits.map((song, i) => (
                              <div key={i} style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', marginBottom: '4px' }}>
                                • {song}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {hitSongAnalysis.producer_tips && hitSongAnalysis.producer_tips.length > 0 && (
                          <div style={{
                            padding: '12px',
                            background: 'rgba(236, 72, 153, 0.1)',
                            border: '1px solid rgba(236, 72, 153, 0.2)',
                            borderRadius: '8px'
                          }}>
                            <div style={{ color: '#ec4899', fontSize: '0.75rem', fontWeight: 700, marginBottom: '8px' }}>
                              💡 PRODUCER TIPS
                            </div>
                            {hitSongAnalysis.producer_tips.map((tip, i) => (
                              <div key={i} style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem', marginBottom: '4px' }}>
                                • {tip}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Clear Button */}
                      <button
                        onClick={() => {
                          setHitSongAnalysis(null);
                          setHitSongInput('');
                        }}
                        style={{
                          marginTop: '16px',
                          padding: '8px 16px',
                          background: 'rgba(239, 68, 68, 0.2)',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          borderRadius: '6px',
                          color: '#ef4444',
                          fontSize: '0.8rem',
                          cursor: 'pointer'
                        }}
                      >
                        Clear Analysis
                      </button>
                    </div>
                  )}
                </div>

                {/* Analysis Results */}
                {spotifyResult && (
                  <div style={{ marginTop: '20px' }}>
                    <h3 style={{ color: 'var(--text-primary)', fontSize: '1rem', marginBottom: '12px' }}>
                      Analysis Results
                    </h3>
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(3, 1fr)', 
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      <div style={{ 
                        background: 'var(--bg-card)', 
                        padding: '12px', 
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginBottom: '4px' }}>BPM</div>
                        <div style={{ color: 'var(--accent)', fontSize: '1.2rem', fontWeight: 700 }}>
                          {spotifyResult.bpm || 'N/A'}
                        </div>
                      </div>
                      <div style={{ 
                        background: 'var(--bg-card)', 
                        padding: '12px', 
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginBottom: '4px' }}>KEY</div>
                        <div style={{ color: 'var(--accent)', fontSize: '1.2rem', fontWeight: 700 }}>
                          {spotifyResult.key || 'N/A'}
                        </div>
                      </div>
                      <div style={{ 
                        background: 'var(--bg-card)', 
                        padding: '12px', 
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem', marginBottom: '4px' }}>MOOD</div>
                        <div style={{ color: 'var(--accent)', fontSize: '1.2rem', fontWeight: 700 }}>
                          {spotifyResult.mood || 'N/A'}
                        </div>
                      </div>
                    </div>

                    {/* Generated Prompts */}
                    {spotifyResult.prompts && spotifyResult.prompts.length > 0 && (
                      <div>
                        <h4 style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '8px' }}>
                          Generated Style Prompts
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {spotifyResult.prompts.slice(0, 5).map((prompt, i) => (
                            <div
                              key={i}
                              style={{
                                padding: '12px',
                                background: 'var(--bg-card)',
                                border: '1px solid var(--border)',
                                borderRadius: '8px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '12px'
                              }}
                            >
                              <div 
                                onClick={() => {
                                  universalCopy(prompt, 'Prompt copied!');
                                }}
                                style={{
                                  flex: 1,
                                  color: 'var(--text-primary)',
                                  fontSize: '0.85rem',
                                  cursor: 'pointer'
                                }}
                              >
                                {prompt}
                              </div>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                {/* Audio Preview */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    generateAudioPreview(prompt, 'Music Analyzer', `ma-${i}`);
                                  }}
                                  disabled={audioPreviewLoading === `ma-${i}`}
                                  style={{
                                    padding: '6px 10px',
                                    background: audioPreviewPlaying === `ma-${i}` 
                                      ? 'rgba(239, 68, 68, 0.3)' 
                                      : audioPreviewLoading === `ma-${i}`
                                        ? 'rgba(234, 179, 8, 0.3)'
                                        : 'rgba(16, 185, 129, 0.2)',
                                    border: 'none',
                                    borderRadius: '6px',
                                    color: audioPreviewPlaying === `ma-${i}` 
                                      ? '#ef4444' 
                                      : audioPreviewLoading === `ma-${i}`
                                        ? '#eab308'
                                        : '#10b981',
                                    cursor: audioPreviewLoading === `ma-${i}` ? 'wait' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center'
                                  }}
                                >
                                  {audioPreviewLoading === `ma-${i}` ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : audioPreviewPlaying === `ma-${i}` ? (
                                    <VolumeX className="w-4 h-4" />
                                  ) : (
                                    <Headphones className="w-4 h-4" />
                                  )}
                                </button>
                                {/* Copy */}
                                <button
                                  onClick={() => universalCopy(prompt, 'Prompt copied!')}
                                  style={{
                                    padding: '6px 10px',
                                    background: 'rgba(159, 71, 255, 0.2)',
                                    border: 'none',
                                    borderRadius: '6px',
                                    color: '#9F47FF',
                                    cursor: 'pointer'
                                  }}
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                                {/* Drum Pattern */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    generateDrumFromStylePrompt(prompt);
                                    toast.info("🥁 Generating drum pattern...");
                                  }}
                                  style={{
                                    padding: '6px 10px',
                                    background: 'rgba(139, 92, 246, 0.2)',
                                    border: 'none',
                                    borderRadius: '6px',
                                    color: '#a78bfa',
                                    cursor: 'pointer'
                                  }}
                                  title="Generate Drum Pattern"
                                >
                                  <Music className="w-4 h-4" />
                                </button>
                                {/* MIDI Download */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    generateMidiFromPrompt(prompt);
                                  }}
                                  disabled={isGeneratingMidi}
                                  style={{
                                    padding: '6px 10px',
                                    background: isGeneratingMidi ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.2)',
                                    border: 'none',
                                    borderRadius: '6px',
                                    color: '#34d399',
                                    cursor: isGeneratingMidi ? 'wait' : 'pointer'
                                  }}
                                  title="Download MIDI (32 bars)"
                                >
                                  <Download className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* RHYME ENGINE VIEW */}
          {activeView === 'rhyme' && (
            <div className="rhyme-section" style={{ maxWidth: '700px', margin: '0 auto' }}>
              {/* BACK TO DASHBOARD BUTTON */}
              <button
                onClick={() => setActiveView('create')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  marginBottom: '20px',
                  boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)'
                }}
                data-testid="rhyme-back-to-dashboard-btn"
              >
                <RotateCcw className="w-4 h-4" />
                ← BACK TO DASHBOARD
              </button>

              <div style={{ marginBottom: '24px' }}>
                <h2 style={{ color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
                  Rhyme & Flow Engine
                </h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>
                  AI-powered lyrics generation with rhyme schemes and flow patterns
                </p>
              </div>

              <div className="create-input-card">
                <div className="form-group">
                  <label className="form-label">Topic or Theme</label>
                  <input
                    type="text"
                    value={rhymeTopic}
                    onChange={(e) => setRhymeTopic(e.target.value)}
                    placeholder="e.g., late night in the city, heartbreak, success story..."
                    className="form-input"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Style</label>
                    <select
                      value={rhymeStyle}
                      onChange={(e) => setRhymeStyle(e.target.value)}
                      className="form-select"
                      data-testid="rhyme-view-style"
                    >
                      <optgroup label="Hip-Hop / Rap">
                        <option value="modern trap">Modern Trap (Atlanta)</option>
                        <option value="southern soul">Southern Soul</option>
                        <option value="pain">Pain / Emotional</option>
                        <option value="boom bap">Boom Bap (90s NY)</option>
                        <option value="drill">UK Drill</option>
                        <option value="west coast">West Coast G-Funk</option>
                        <option value="melodic">Melodic Rap</option>
                        <option value="conscious">Conscious/Lyrical</option>
                      </optgroup>
                      <optgroup label="R&B / Soul">
                        <option value="rnb">R&B / Soul</option>
                        <option value="neo soul">Neo Soul</option>
                        <option value="trapsoul">TrapSoul</option>
                      </optgroup>
                      <optgroup label="World / Latin">
                        <option value="afrobeats">Afrobeats</option>
                        <option value="amapiano">Amapiano</option>
                        <option value="reggaeton">Reggaeton</option>
                        <option value="dancehall">Dancehall</option>
                        <option value="latin trap">Latin Trap</option>
                      </optgroup>
                      <optgroup label="Other">
                        <option value="gospel">Gospel</option>
                        <option value="party">Party/Club</option>
                        <option value="pop">Pop</option>
                        <option value="rock">Rock</option>
                        <option value="country">Country</option>
                        <option value="baile funk">Baile Funk</option>
                        <option value="uk garage">UK Garage</option>
                        <option value="phonk">Phonk</option>
                      </optgroup>
                    </select>
                    <StyleBlueprintPreview styleKey={rhymeStyle} testId="style-blueprint-preview-view" />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Rhyme Scheme</label>
                    <select
                      value={rhymeScheme}
                      onChange={(e) => setRhymeScheme(e.target.value)}
                      className="form-select"
                      data-testid="rhyme-view-scheme"
                    >
                      <option value="AABB">AABB (Couplets)</option>
                      <option value="ABAB">ABAB (Alternating)</option>
                      <option value="ABBA">ABBA (Enclosed)</option>
                      <option value="AAAA">AAAA (Mono-rhyme)</option>
                      <option value="AAAB">AAAB (Triple + Twist)</option>
                      <option value="ABCB">ABCB (Ballad Stanza)</option>
                      <option value="ABCABC">ABCABC (Triple Rhyme)</option>
                      <option value="AABBA">AABBA (Limerick)</option>
                      <option value="AABCCB">AABCCB (Sestet)</option>
                      <option value="XAXA">XAXA (Alternate Blank)</option>
                      <option value="chain">Chain Rhyme (ABA BCB)</option>
                      <option value="internal">Internal Rhymes</option>
                      <option value="multisyllabic">Multi-Syllabic</option>
                      <option value="compound">Compound Rhymes</option>
                      <option value="slant">Slant / Near Rhymes</option>
                      <option value="assonance">Assonance (Vowel Rhymes)</option>
                      <option value="alliteration">Alliteration Driven</option>
                      <option value="internal-multi">Internal Multi-Syllabic</option>
                      <option value="cross">Cross Rhyme</option>
                      <option value="free">Free Form</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Flow Pattern</label>
                    <select
                      value={flowPattern}
                      onChange={(e) => setFlowPattern(e.target.value)}
                      className="form-select"
                      data-testid="rhyme-view-flow"
                    >
                      <option value="standard">Standard (4/4 Flow)</option>
                      <option value="boom-bap-cadence">Boom Bap Cadence (90s Pocket)</option>
                      <option value="triplet">Triplet Flow (Migos)</option>
                      <option value="double-time">Double-Time (Fast)</option>
                      <option value="chopper">Chopper (Tech N9ne Speed)</option>
                      <option value="slow-flow">Slow Flow (Deliberate)</option>
                      <option value="lazy-drawl">Lazy Drawl (Behind the Beat)</option>
                      <option value="syncopated">Syncopated (Off-Beat)</option>
                      <option value="halftime">Half-Time (Trap Beat)</option>
                      <option value="drill-slide">Drill Slide (UK Cadence)</option>
                      <option value="grime">Grime (140 Skippy)</option>
                      <option value="melodic-flow">Melodic Flow</option>
                      <option value="sing-song">Sing-Song (Playground Cadence)</option>
                      <option value="stutter">Stutter Flow (Word Repeats)</option>
                      <option value="start-stop">Start-Stop (Dramatic Pauses)</option>
                      <option value="pocket">Pocket Flow (Locked to Drums)</option>
                      <option value="bounce">Bounce (New Orleans)</option>
                      <option value="afro-bounce">Afro Bounce</option>
                      <option value="reggaeton-flow">Reggaeton Dembow</option>
                      <option value="syncopated-offbeat">Syncopated Off-Beat</option>
                      <option value="waterfall">Waterfall</option>
                      <option value="spoken-word">Spoken Word / Poetry</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Language</label>
                    <select
                      value={rhymeLanguage}
                      onChange={(e) => setRhymeLanguage(e.target.value)}
                      className="form-select"
                      data-testid="rhyme-view-language"
                    >
                      {OWN_LYRICS_LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Bars Count</label>
                    <select
                      value={barsCount}
                      onChange={(e) => setBarsCount(parseInt(e.target.value))}
                      className="form-select"
                      data-testid="rhyme-view-bars"
                    >
                      <option value={8}>8 Bars (Half Verse)</option>
                      <option value={12}>12 Bars</option>
                      <option value={16}>16 Bars (Full Verse)</option>
                      <option value={24}>24 Bars</option>
                      <option value={32}>32 Bars (Double Verse)</option>
                      <option value={48}>48 Bars</option>
                      <option value={64}>64 Bars (Full Song)</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Duration (2-5 min)</label>
                    <input
                      type="number"
                      min={2}
                      max={5}
                      step={0.5}
                      value={rhymeDuration}
                      onChange={(e) => setRhymeDuration(e.target.value === '' ? '' : parseFloat(e.target.value))}
                      onBlur={() => setRhymeDuration(Math.min(5, Math.max(2, parseFloat(rhymeDuration) || 3)))}
                      className="form-input"
                      data-testid="rhyme-view-duration"
                    />
                  </div>
                </div>

                <button
                  onClick={generateBars}
                  disabled={isGeneratingBars || !rhymeTopic}
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                >
                  {isGeneratingBars ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      Generate Lyrics
                    </>
                  )}
                </button>

                {/* Generated Lyrics */}
                {generatedBars && generatedBars.raw_lyrics && (
                  <div style={{ marginTop: '20px' }}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '12px'
                    }}>
                      <h3 style={{ color: 'var(--text-primary)', fontSize: '1rem', margin: 0 }}>
                        Generated Lyrics
                      </h3>
                      <button
                        onClick={() => universalCopy(generatedBars.raw_lyrics, 'Lyrics copied!')}
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                      >
                        <Copy className="w-3 h-3" />
                        Copy
                      </button>
                    </div>
                    <div style={{
                      padding: '16px',
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      color: 'var(--text-primary)',
                      fontSize: '0.9rem',
                      lineHeight: 1.8,
                      whiteSpace: 'pre-wrap'
                    }}>
                      {generatedBars.raw_lyrics}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MARKETPLACE VIEW */}

          {activeView === 'voiceshowcase' && (
            <VoiceShowcase
              onBack={() => setActiveView('create')}
              onAddToWorkflow={(voice, region) => addWorldVoiceToWorkflow(voice, region)}
            />
          )}

          {activeView === 'marketplace' && (
            <div className="marketplace-section" style={{ maxWidth: '900px', margin: '0 auto' }}>
              {/* BACK TO DASHBOARD BUTTON */}
              <button
                onClick={() => setActiveView('create')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  marginBottom: '20px',
                  boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)'
                }}
                data-testid="back-to-dashboard-btn"
              >
                <RotateCcw className="w-4 h-4" />
                ← BACK TO DASHBOARD
              </button>

              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: '24px'
              }}>
                <div>
                  <h2 style={{ color: 'var(--text-primary)', fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
                    DNA Presets
                  </h2>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '4px' }}>
                    Click any preset to load it into your workflow
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => { setDnaMarketplaceTab('browse'); fetchMarketplaceDNAs(); }}
                    className={`btn ${dnaMarketplaceTab === 'browse' ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    Browse All
                  </button>
                  <button
                    onClick={() => { setDnaMarketplaceTab('my-library'); fetchMyDNAs(); }}
                    className={`btn ${dnaMarketplaceTab === 'my-library' ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    My Presets
                  </button>
                </div>
              </div>

              {/* Genre Filter Buttons */}
              <div style={{ 
                display: 'flex', 
                gap: '8px', 
                flexWrap: 'wrap', 
                marginBottom: '12px',
                padding: '12px',
                background: 'rgba(0, 212, 255, 0.05)',
                borderRadius: '12px',
                border: '1px solid rgba(0, 212, 255, 0.1)'
              }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginRight: '8px', alignSelf: 'center' }}>Filter:</span>
                {['All', 'Pop', 'Hip Hop', 'R&B', 'Country', 'Latin', 'Electronic', 'Rock', 'Afrobeats', 'ATL Trap'].map(genre => (
                  <button
                    key={genre}
                    onClick={() => {
                      setDnaFilterGenre(genre);
                      setDnaCurrentPage(1);
                    }}
                    style={{
                      padding: '6px 12px',
                      background: dnaFilterGenre === genre ? 'linear-gradient(135deg, #00d4ff, #0099cc)' : 'rgba(0, 212, 255, 0.1)',
                      border: dnaFilterGenre === genre ? 'none' : '1px solid rgba(0, 212, 255, 0.2)',
                      borderRadius: '20px',
                      color: dnaFilterGenre === genre ? '#fff' : '#00d4ff',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {genre}
                  </button>
                ))}
              </div>

              {/* PAGINATION - Pages 1-10 */}
              {(() => {
                const { presets, totalPages, totalCount } = getPaginatedPresets();
                return (
                  <>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '16px',
                      padding: '12px 16px',
                      background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(236, 72, 153, 0.1))',
                      borderRadius: '12px',
                      border: '1px solid rgba(139, 92, 246, 0.2)'
                    }}>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        <span style={{ color: '#8b5cf6', fontWeight: 700 }}>{totalCount}</span> presets found
                        {dnaFilterGenre !== 'All' && <span> in <span style={{ color: '#00d4ff' }}>{dnaFilterGenre}</span></span>}
                      </div>
                      
                      {/* Page Numbers 1-10 */}
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginRight: '8px' }}>Page:</span>
                        {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(pageNum => (
                          <button
                            key={pageNum}
                            onClick={() => setDnaCurrentPage(pageNum)}
                            style={{
                              width: '32px',
                              height: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: dnaCurrentPage === pageNum 
                                ? 'linear-gradient(135deg, #00d4ff, #8b5cf6)' 
                                : 'rgba(255, 255, 255, 0.05)',
                              border: dnaCurrentPage === pageNum 
                                ? 'none' 
                                : '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '8px',
                              color: dnaCurrentPage === pageNum ? '#fff' : 'var(--text-muted)',
                              fontSize: '0.85rem',
                              fontWeight: dnaCurrentPage === pageNum ? 700 : 500,
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            data-testid={`page-${pageNum}-btn`}
                          >
                            {pageNum}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Favorites Strip */}
                    {presetFavorites.length > 0 && (
                      <div data-testid="preset-favorites-strip" style={{ marginBottom: '16px' }}>
                        <div style={{ color: '#fbbf24', fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', marginBottom: '8px' }}>
                          ⭐ YOUR FAVORITES — one tap to load
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                          {presetFavorites.map(fav => (
                            <div key={fav.id} style={{
                              display: 'flex', alignItems: 'center', gap: '4px',
                              background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.4)',
                              borderRadius: '999px', padding: '6px 6px 6px 12px'
                            }}>
                              <button
                                onClick={() => {
                                  setBeatPrompt(fav.prompt);
                                  updateWorkflow({
                                    stylePrompt: fav.prompt, bpm: fav.bpm || 120, key: fav.key || '',
                                    mode: '', genre: fav.genre || '', source: 'preset'
                                  });
                                  setActiveView('create');
                                  toast.success(`⭐ Loaded "${fav.name}" → Ready to export!`);
                                }}
                                data-testid={`fav-chip-${fav.id}`}
                                style={{ background: 'none', border: 'none', color: '#fbbf24', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', padding: 0 }}
                              >⭐ {fav.name}</button>
                              <button
                                onClick={() => togglePresetFavorite(fav)}
                                data-testid={`fav-remove-${fav.id}`}
                                title="Remove favorite"
                                style={{
                                  background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '50%',
                                  width: '20px', height: '20px', color: 'rgba(255,255,255,0.6)',
                                  fontSize: '0.8rem', cursor: 'pointer', lineHeight: 1
                                }}
                              >×</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Preset Cards Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                      {(dnaMarketplaceTab === 'browse' ? presets : myDNAs).map((dna, i) => (
                        <div
                          key={dna.id || i}
                          onClick={() => {
                            const prompt = dna.style_prompt || dna.prompt || '';
                            setPresetDetail({
                              id: `dna:${dna.id || dna.name}`,
                              name: dna.name,
                              prompt,
                              bpm: dna.bpm || 120,
                              key: `${dna.key || 'C'} ${dna.mode || 'minor'}`,
                              genre: dna.categories?.[0] || '—',
                              desc: dna.description,
                              apply: () => {
                                setBeatPrompt(prompt);
                                updateWorkflow({
                                  stylePrompt: prompt,
                                  bpm: dna.bpm || 120,
                                  key: dna.key || 'C',
                                  mode: dna.mode || 'minor',
                                  genre: dna.categories?.[0] || '',
                                  source: 'preset'
                                });
                                setActiveView('create');
                                toast.success(`✓ Loaded "${dna.name}" → Ready to export to Suno!`);
                              }
                            });
                          }}
                          style={{
                            background: 'var(--bg-surface)',
                            border: '1px solid var(--border)',
                            borderRadius: '12px',
                            padding: '16px',
                            transition: 'all 0.15s ease',
                            cursor: 'pointer'
                          }}
                          data-testid={`preset-card-${i}`}
                        >
                          <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'flex-start',
                            marginBottom: '8px'
                          }}>
                            <div>
                              <h3 style={{ 
                                color: 'var(--text-primary)', 
                                fontSize: '1rem', 
                                fontWeight: 600,
                                margin: 0
                              }}>
                                {dna.name}
                              </h3>
                              {dna.categories && dna.categories[0] && (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  background: 'rgba(0, 212, 255, 0.15)',
                                  borderRadius: '10px',
                                  fontSize: '0.65rem',
                                  color: '#00d4ff',
                                  marginTop: '4px'
                                }}>
                                  {dna.categories[0]}
                                </span>
                              )}
                            </div>
                            <span style={{ 
                              color: 'var(--text-muted)', 
                              fontSize: '0.7rem'
                            }}>
                              {dna.downloads || 0} uses
                            </span>
                          </div>
                          <p style={{ 
                            color: 'var(--text-secondary)', 
                            fontSize: '0.85rem',
                            marginBottom: '12px',
                            lineHeight: 1.5
                          }}>
                            {(dna.style_prompt || dna.prompt || '')?.slice(0, 100)}...
                          </p>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {/* Audio Preview Button - plays the exact mixdown WAV */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                previewDnaMixdown(dna, `preset-${i}`);
                              }}
                              disabled={audioPreviewLoading === `preset-${i}`}
                              className="btn"
                              title={audioPreviewPlaying === `preset-${i}` ? 'Stop preview' : 'Play preview (same as WAV export)'}
                              data-testid={`preset-preview-${i}`}
                              style={{
                                padding: '8px',
                                background: audioPreviewPlaying === `preset-${i}` ? 'rgba(239,68,68,0.25)' : 'rgba(139,92,246,0.15)',
                                border: 'none',
                                color: audioPreviewPlaying === `preset-${i}` ? '#ef4444' : '#8b5cf6'
                              }}
                            >
                              {audioPreviewLoading === `preset-${i}` ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : audioPreviewPlaying === `preset-${i}` ? (
                                <Square className="w-4 h-4" fill="#ef4444" />
                              ) : (
                                <Play className="w-4 h-4" fill="#8b5cf6" />
                              )}
                            </button>
                            {/* WAV Stems Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadDnaStems(dna, `preset-${i}`);
                              }}
                              disabled={dnaStemsLoading === `preset-${i}`}
                              className="btn"
                              title="Render & download stereo WAV mixdown"
                              data-testid={`preset-stems-${i}`}
                              style={{
                                padding: '8px',
                                background: dnaStemsLoading === `preset-${i}` ? 'rgba(234,179,8,0.3)' : 'rgba(0,212,255,0.15)',
                                border: 'none',
                                color: dnaStemsLoading === `preset-${i}` ? '#eab308' : '#00d4ff'
                              }}
                            >
                              {dnaStemsLoading === `preset-${i}` ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <FileAudio className="w-4 h-4" />
                              )}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const prompt = dna.style_prompt || dna.prompt || '';
                                setBeatPrompt(prompt);
                                updateWorkflow({
                                  stylePrompt: prompt,
                                  bpm: dna.bpm || 120,
                                  key: dna.key || 'C',
                                  mode: dna.mode || 'minor',
                                  genre: dna.categories?.[0] || '',
                                  source: 'preset'
                                });
                                setShowDNAMarketplace(false);
                                setActiveView('create');
                                toast.success(`✓ Loaded "${dna.name}" → Ready to export to Suno!`);
                              }}
                              className="btn btn-primary"
                              style={{ flex: 1, padding: '8px' }}
                            >
                              Use Preset
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const promptToCopy = dna.style_prompt || dna.prompt || '';
                                if (promptToCopy) {
                                  universalCopy(promptToCopy, '📋 Preset copied to clipboard!');
                                } else {
                                  toast.error('No prompt to copy');
                                }
                              }}
                              className="btn btn-secondary"
                              style={{ padding: '8px' }}
                              data-testid={`copy-preset-${i}`}
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            {/* Generate Drum Pattern Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const prompt = dna.style_prompt || dna.prompt || '';
                                if (prompt) {
                                  generateDrumFromStylePrompt(prompt);
                                  toast.info("🥁 Generating drum pattern from style...");
                                }
                              }}
                              className="btn"
                              style={{ 
                                padding: '8px',
                                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(99, 102, 241, 0.3))',
                                border: '1px solid rgba(139, 92, 246, 0.4)',
                                color: '#a78bfa'
                              }}
                              data-testid={`drum-preset-${i}`}
                              title="Generate drum pattern from this style"
                            >
                              <Music className="w-4 h-4" />
                            </button>
                            {/* Download MIDI Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const prompt = dna.style_prompt || dna.prompt || '';
                                if (prompt) {
                                  generateMidiFromPrompt(prompt, dna._id || dna.id);
                                }
                              }}
                              disabled={isGeneratingMidi}
                              className="btn"
                              style={{ 
                                padding: '8px',
                                background: isGeneratingMidi 
                                  ? 'rgba(16, 185, 129, 0.2)' 
                                  : 'linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(5, 150, 105, 0.3))',
                                border: '1px solid rgba(16, 185, 129, 0.4)',
                                color: '#34d399'
                              }}
                              data-testid={`midi-preset-${i}`}
                              title="Download MIDI file (32 bars: drums, bass, chords, melody)"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Bottom Pagination */}
                    {totalPages > 1 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '8px',
                        marginTop: '24px',
                        padding: '16px',
                        background: 'rgba(0, 0, 0, 0.2)',
                        borderRadius: '12px'
                      }}>
                        <button
                          onClick={() => setDnaCurrentPage(Math.max(1, dnaCurrentPage - 1))}
                          disabled={dnaCurrentPage === 1}
                          style={{
                            padding: '8px 16px',
                            background: dnaCurrentPage === 1 ? 'rgba(255,255,255,0.05)' : 'rgba(0, 212, 255, 0.2)',
                            border: 'none',
                            borderRadius: '8px',
                            color: dnaCurrentPage === 1 ? 'var(--text-muted)' : '#00d4ff',
                            cursor: dnaCurrentPage === 1 ? 'not-allowed' : 'pointer',
                            fontWeight: 600
                          }}
                        >
                          ← Previous
                        </button>
                        
                        <span style={{ 
                          padding: '8px 16px', 
                          color: '#fff',
                          fontWeight: 700 
                        }}>
                          Page {dnaCurrentPage} of {totalPages}
                        </span>
                        
                        <button
                          onClick={() => setDnaCurrentPage(Math.min(totalPages, dnaCurrentPage + 1))}
                          disabled={dnaCurrentPage === totalPages}
                          style={{
                            padding: '8px 16px',
                            background: dnaCurrentPage === totalPages ? 'rgba(255,255,255,0.05)' : 'rgba(0, 212, 255, 0.2)',
                            border: 'none',
                            borderRadius: '8px',
                            color: dnaCurrentPage === totalPages ? 'var(--text-muted)' : '#00d4ff',
                            cursor: dnaCurrentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontWeight: 600
                          }}
                        >
                          Next →
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}

              {(dnaMarketplaceTab === 'browse' ? marketplaceDNAs : myDNAs).length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '60px 20px',
                  background: 'var(--bg-surface)',
                  borderRadius: '12px',
                  border: '1px solid var(--border)'
                }}>
                  <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: '#00d4ff' }} />
                  <h3 style={{ color: 'var(--text-primary)', marginBottom: '8px' }}>
                    Loading Presets...
                  </h3>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                    Fetching 650+ style prompts from database
                  </p>
                  <button
                    onClick={() => fetchMarketplaceDNAs()}
                    style={{
                      marginTop: '16px',
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg, #00d4ff, #0099cc)',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#fff',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    Retry Loading
                  </button>
                </div>
              )}
            </div>
          )}

          {activeView === 'dna-toolkit' && (
            <DNAToolkit
              onSendToWorkflow={(prompt, dna) => {
                setBeatPrompt(prompt);
                updateWorkflow({
                  stylePrompt: prompt, bpm: 120, key: '', mode: '',
                  genre: dna.genre || '', source: 'toolkit'
                });
                setActiveView('create');
                toast.success('✓ Toolkit prompt loaded → Ready to export to Suno!');
              }}
            />
          )}

          {activeView === 'mastering' && (
            <MasteringStudio
              onBack={() => setActiveView('create')}
              userEmail={currentUser?.email || userEmail || getGuestEmail()}
              onAddToWorkflow={(processedAudio) => {
                toast.success('Mastered track added to workflow!');
                setActiveView('create');
              }}
            />
          )}

          {activeView === 'mysounddna' && (
            <MySoundDnaLibrary
              onBack={() => setActiveView('create')}
              drumLibrary={drumLibrary}
              sunoTracks={sunoTracks}
              beatLibrary={beatLibrary}
              separatedStems={separatedStems}
              saveWavFromUrl={saveWavFromUrl}
              downloadSongWav={downloadSong}
              onGenerateMidi={generateMidiFromPrompt}
              onErase={eraseLibraryItem}
            />
          )}

          {activeView === 'promptlab' && (
            <AutonomousPromptLab
              onBack={() => setActiveView('create')}
              apiBase={API}
              genreOptions={fusionGenres}
              saveWavFromUrl={saveWavFromUrl}
              onGenerateMidi={generateMidiFromPrompt}
              email={currentUser?.email || userEmail || getGuestEmail()}
              onSaveToLibrary={(row, audioUrl) => {
                const entry = {
                  id: Date.now().toString(),
                  timestamp: new Date().toISOString(),
                  genre: row.genre,
                  tempo: null, kit: '', swing: 0, pattern: '',
                  suno_prompt: row.prompt,
                  style_prompt: row.prompt,
                  audioUrl: audioUrl || null,
                  midiData: null,
                  source: 'prompt-lab',
                  name: `${row.genre} — Prompt Lab v${row.variation_index}`
                };
                setDrumLibrary(prev => [entry, ...prev]);
                toast.success('💾 Saved to your MANNIS library!');
              }}
              onUsePrompt={(p) => {
                setBeatPrompt(p);
                setWorkflowPrompt(prev => ({ ...prev, stylePrompt: p, source: 'prompt-lab' }));
                setActiveView('create');
                toast.success('Loaded into the Workflow — generate audio & download WAV / MIDI / Studio WAV below', { duration: 5000 });
                setTimeout(() => {
                  const el = document.querySelector('[data-testid="style-prompt-editor"]');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 350);
              }}
            />
          )}

        </div> {/* End suno-content */}
        
        {/* Bottom Player Bar */}
        {beatAudioUrl && (
          <div className="suno-player">
            {/* Real audio element driving the bottom player */}
            <audio
              ref={beatAudioRef}
              src={beatAudioUrl.startsWith('/api/') ? `${API_ORIGIN}${beatAudioUrl}` : beatAudioUrl}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleAudioEnded}
              onPlay={() => setPlayerIsPlaying(true)}
              onPause={() => setPlayerIsPlaying(false)}
              preload="metadata"
              data-testid="beat-audio-player"
            />
            <div className="player-song-info">
              <div className="player-thumbnail">
                <Music2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="player-song-title">{beatPrompt?.slice(0, 40) || 'Generated Beat'}...</div>
                <div className="player-song-artist">MANNIS Demo</div>
              </div>
            </div>

            <div className="player-controls">
              <div className="player-buttons">
                <button
                  className="player-btn play"
                  onClick={togglePlayPause}
                  data-testid="beat-player-play-btn"
                  aria-label={playerIsPlaying ? 'Pause' : 'Play'}
                >
                  {playerIsPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </button>
              </div>
              <div className="player-progress">
                <span className="player-time" data-testid="beat-player-current-time">{formatTime(playerProgress)}</span>
                <div className="player-progress-bar" onClick={handleSeek} style={{ cursor: 'pointer' }}>
                  <div
                    className="player-progress-fill"
                    style={{ width: `${playerDuration > 0 ? (playerProgress / playerDuration) * 100 : 0}%` }}
                  />
                </div>
                <span className="player-time" data-testid="beat-player-duration">{formatTime(playerDuration)}</span>
              </div>
            </div>

            <div className="player-volume">
              <button
                onClick={() => {
                  if (beatAudioUrl) {
                    downloadBeat(beatAudioUrl, 'sound_dna_beat.mp3');
                  } else {
                    toast.error('No beat to download');
                  }
                }}
                className="btn btn-primary"
                style={{ fontSize: '0.8rem', padding: '8px 12px' }}
              >
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          </div>
        )}
      </main>
      
      {/* GLOBAL GENERATION STATUS WIDGET - Optimistic UI (non-blocking) */}
      {genWidget && (
        <div
          data-testid="generation-widget"
          style={{
            position: 'fixed', bottom: '90px', right: '20px', zIndex: 9998, width: '300px',
            background: 'rgba(10, 14, 25, 0.96)', backdropFilter: 'blur(16px)',
            border: genWidget.status === 'ready' ? '1px solid rgba(34,197,94,0.5)' : '1px solid rgba(0,212,255,0.35)',
            borderRadius: '14px', padding: '14px 16px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
          }}
        >
          {genWidget.status === 'working' ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: '#00d4ff' }} />
                <span style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 700 }}>{genWidget.label}</span>
              </div>
              <div data-testid="generation-widget-stage" style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.78rem', marginBottom: '8px' }}>
                {genStageFor(genWidget.kind, genWidget.progress)}
              </div>
              <div style={{ height: '6px', background: 'rgba(255,255,255,0.1)', borderRadius: '99px', overflow: 'hidden', marginBottom: '6px' }}>
                <div style={{ height: '100%', width: `${genWidget.progress}%`, background: 'linear-gradient(90deg, #00d4ff, #8b5cf6)', borderRadius: '99px', transition: 'width 0.8s ease' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>
                <span>{genWidget.progress}%</span>
                <span data-testid="generation-widget-eta">~{genWidget.eta}s remaining</span>
              </div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem', marginTop: '6px' }}>
                💡 Keep browsing — we'll notify you here when it's ready
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#4ade80', fontSize: '0.85rem', fontWeight: 800 }}>✅ {genWidget.label} ready!</span>
                <button
                  data-testid="generation-widget-dismiss"
                  onClick={() => {
                    if (genWidgetAudioRef.current) genWidgetAudioRef.current.pause();
                    setGenWidget(null);
                  }}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 }}
                >×</button>
              </div>
              <button
                data-testid="generation-widget-play"
                onClick={() => {
                  const a = genWidgetAudioRef.current;
                  if (!a || !genWidget.resultUrl) return;
                  if (a.paused) {
                    if (a.src !== genWidget.resultUrl) a.src = genWidget.resultUrl;
                    a.play().catch(() => {});
                  } else {
                    a.pause();
                  }
                }}
                style={{ width: '100%', padding: '9px', background: 'linear-gradient(90deg, #22c55e, #16a34a)', border: 'none', borderRadius: '8px', color: '#04120a', fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer' }}
              >
                ▶ Play / Pause
              </button>
            </>
          )}
        </div>
      )}
      <audio preload="metadata" ref={genWidgetAudioRef} style={{ display: 'none' }} />
      
      {/* STYLE PROMPT MINI - Floating Widget */}
      {/* Toggle Button */}
      <button
        onClick={() => setShowStyleMini(!showStyleMini)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: showStyleMini 
            ? 'linear-gradient(135deg, #ef4444, #dc2626)'
            : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
          border: 'none',
          color: '#fff',
          fontSize: '1.5rem',
          cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(139, 92, 246, 0.4)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s'
        }}
        data-testid="style-mini-toggle"
      >
        {showStyleMini ? <X className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
      </button>
      
      {/* Style Prompt Mini Panel */}
      {showStyleMini && (
        <div style={{
          position: 'fixed',
          bottom: '90px',
          right: '20px',
          width: 'min(360px, calc(100vw - 40px))',
          maxHeight: '70vh',
          background: 'linear-gradient(135deg, #0d0d1a 0%, #1a1a2e 100%)',
          border: '1px solid rgba(139, 92, 246, 0.3)',
          borderRadius: '16px',
          boxShadow: '0 8px 40px rgba(0, 0, 0, 0.5)',
          zIndex: 9998,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }} data-testid="style-mini-panel">
          {/* Header */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid rgba(139, 92, 246, 0.2)',
            background: 'rgba(139, 92, 246, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <Sparkles className="w-5 h-5" style={{ color: '#a78bfa' }} />
              <span style={{ color: '#fff', fontWeight: 700, fontSize: '1rem' }}>Style Prompt Mini</span>
            </div>
            
            {/* Tab Buttons */}
            <div style={{ display: 'flex', gap: '6px' }}>
              {[
                { id: 'generate', label: '🎲 Random', icon: null },
                { id: 'audio', label: '🎧 Audio', icon: null },
                { id: 'presets', label: '⚡ Presets', icon: null }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setStyleMiniTab(tab.id)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    background: styleMiniTab === tab.id 
                      ? 'linear-gradient(135deg, #8b5cf6, #6366f1)' 
                      : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${styleMiniTab === tab.id ? '#8b5cf6' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '8px',
                    color: styleMiniTab === tab.id ? '#fff' : '#888',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Content Area */}
          <div style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
            
            {/* Random Generator Tab */}
            {styleMiniTab === 'generate' && (
              <div>
                <p style={{ color: '#888', fontSize: '0.8rem', marginBottom: '16px' }}>
                  Generate a random style prompt with one click. Perfect for creative exploration!
                </p>
                
                <button
                  onClick={generateRandomStyle}
                  disabled={isGeneratingStyle}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: isGeneratingStyle 
                      ? 'rgba(100,100,100,0.3)' 
                      : 'linear-gradient(135deg, #8b5cf6, #ec4899)',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#fff',
                    fontSize: '1rem',
                    fontWeight: 700,
                    cursor: isGeneratingStyle ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    marginBottom: '16px'
                  }}
                  data-testid="style-mini-random-btn"
                >
                  {isGeneratingStyle ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-5 h-5" /> Generate Random Style</>
                  )}
                </button>
                
                {/* Current Style Display */}
                {beatPrompt && (
                  <div style={{
                    padding: '12px',
                    background: 'rgba(139, 92, 246, 0.1)',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    borderRadius: '10px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <span style={{ color: '#a78bfa', fontSize: '0.7rem', fontWeight: 600 }}>CURRENT STYLE</span>
                      <button
                        onClick={() => {
                          universalCopy(beatPrompt, 'Copied!');
                        }}
                        style={{
                          padding: '4px 8px',
                          background: 'rgba(139, 92, 246, 0.2)',
                          border: 'none',
                          borderRadius: '4px',
                          color: '#a78bfa',
                          fontSize: '0.65rem',
                          cursor: 'pointer'
                        }}
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    <p style={{ color: '#fff', fontSize: '0.85rem', margin: 0, lineHeight: 1.5 }}>{beatPrompt}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Audio Analysis Tab */}
            {styleMiniTab === 'audio' && (
              <div>
                <p style={{ color: '#888', fontSize: '0.8rem', marginBottom: '16px' }}>
                  Upload a reference track and AI will generate a matching style prompt.
                </p>
                
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => setStyleMiniAudio(e.target.files?.[0] || null)}
                  style={{ display: 'none' }}
                  id="style-mini-audio-input"
                  ref={styleMiniAudioRef}
                />
                
                <label
                  htmlFor="style-mini-audio-input"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                    background: 'rgba(139, 92, 246, 0.05)',
                    border: '2px dashed rgba(139, 92, 246, 0.3)',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    marginBottom: '12px'
                  }}
                >
                  <Upload className="w-8 h-8" style={{ color: '#8b5cf6', marginBottom: '8px' }} />
                  <span style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}>
                    {styleMiniAudio ? styleMiniAudio.name : 'Drop audio or click to upload'}
                  </span>
                  <span style={{ color: '#666', fontSize: '0.7rem', marginTop: '4px' }}>
                    MP3, WAV, OGG, FLAC (max 10MB)
                  </span>
                </label>
                
                {styleMiniAudio && (
                  <button
                    onClick={() => analyzeAudioForStyle(styleMiniAudio)}
                    disabled={isAnalyzingAudio}
                    style={{
                      width: '100%',
                      padding: '14px',
                      background: isAnalyzingAudio 
                        ? 'rgba(100,100,100,0.3)' 
                        : 'linear-gradient(135deg, #22c55e, #16a34a)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      cursor: isAnalyzingAudio ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                    data-testid="style-mini-analyze-btn"
                  >
                    {isAnalyzingAudio ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Analyzing...</>
                    ) : (
                      <><Headphones className="w-5 h-5" /> Analyze & Generate Style</>
                    )}
                  </button>
                )}
              </div>
            )}
            
            {/* Presets Tab */}
            {styleMiniTab === 'presets' && (
              <div>
                <p style={{ color: '#888', fontSize: '0.8rem', marginBottom: '12px' }}>
                  Quick style presets for instant inspiration.
                </p>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(2, 1fr)', 
                  gap: '8px',
                  maxHeight: '280px',
                  overflowY: 'auto'
                }}>
                  {STYLE_PRESETS.map((preset, idx) => (
                    <button
                      key={idx}
                      onClick={() => applyStylePreset(preset)}
                      style={{
                        padding: '10px 8px',
                        background: 'rgba(139, 92, 246, 0.1)',
                        border: '1px solid rgba(139, 92, 246, 0.2)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.2s'
                      }}
                      data-testid={`style-preset-${idx}`}
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
// Build timestamp: 1775453009
